/* Croc Sentinel Console — bundled from dashboard/src/ (npm run build) */
"use strict";
import { LS, OFFLINE_MS, DEFAULT_REMOTE_SIREN_MS, DEFAULT_PANIC_FANOUT_MS, DEFAULT_LOCAL_SIREN_MS, NAV_GROUPS, ROLE_WEIGHT, PUBLIC_ROUTE_IDS, ROUTE_ALIASES, ROUTES } from "./lib/constants.js";
import { resolveHashRoute as resolveHashRouteFromLib } from "./lib/resolve-hash-route.js";
import { $, $$, escapeHtml, hx, mountView, parseHtmlToFragment, setChildMarkup, prependChildMarkup, appendChildMarkup, setHtmlIfChanged, setTextIfChanged } from "./lib/dom.js";
import { parseSseFields, pumpSseBody, SSE_PARSE_BUF_MAX } from "./lib/sse.js";
import { fmtTs, fmtRel, maskPlatform, humanizeAlarmTitle, humanizeZoneLabel, humanizeProofReason, softDurationSeconds, softDeviceLabel, rememberDeviceLabels, softIncidentPickerLabel, softFleetDeviceTitle, isHarnessDeviceId, isHarnessUsername, softFirmwareChip, softApiError, softOperatorNote, softOpsLogLine, auditActionPrefix, auditActionDisplay, auditDetailDedupedRows, eventDetailDedupedRows, messagePayloadRows, auditChipClass, fmtDeviceCount, fmtDevicesOnlineOfTotal } from "./lib/format.js";
import { softConfirm } from "./lib/soft-confirm.js";
import { decisionSurfaceEscalateSoftMessage, decisionSurfaceMoreEvidenceSoftMessage } from "./lib/decision-surface.js";
import { buildAiDecisionRecordHtml, matchAiRecordForEvent, wireAiDecisionRecordClicks } from "./lib/ai-decision-record.js";
import { incidentWorkspaceSpineHtml } from "./lib/incident-workspace-chrome.js";
import { incidentEngineHostHtml } from "./lib/incident-engine-host.js";
import { capabilityRegistrySoftHtml, softLanesMapChipHtml, softLanesPerceiveHtml } from "./lib/capability-registry-facade.js";
import { evidenceGraphSoftHtml } from "./lib/evidence-graph-soft.js";
import { responseLoopWorkbenchHtml, responseLoopSteps, responseAiInnovHtml, incidentOsWorkflowWorkbenchHtml, incidentOsWorkflowSteps } from "./lib/response-loop-workbench.js";
import { buildTonightStageStripHtml } from "./lib/tonight-stage-strip.js";
import {
  buildAutoSentinelSettingsWorkbenchHtml,
  buildAutoPlanDetailHtml,
  softAutoPlanLabel,
} from "./lib/auto-sentinel-settings-workbench.js";
import { DEFAULT_API_TIMEOUT_MS, ROUTE_RENDER_TIMEOUT_MS, apiBase, fetchWithDeadline, _sleep, _isTransientFetchError, _isRetryableHttpStatus, _isWriteMethod } from "./lib/api.js";
import { bindContractApi } from "./lib/api/contract-client.js";
import { CSRF_COOKIE_NAME, CSRF_HEADER_NAME, _readCsrfCookie, getCsrfToken, setCsrfToken, refreshCsrfToken, _isCsrfRejection } from "./lib/csrf.js";
import {
  authSiteFooterHtml,
  authAsideHtml,
  activateCommandCenterAuthLayout,
  deactivateCommandCenterAuthLayout,
  buildCommandCenterAuthSurfaceHtml,
  ccAuthFooterHtml,
  authAlreadySignedInHtml,
} from "./lib/auth-chrome.js";
import {
  BRAND_PRODUCT,
  BRAND_SERIES,
  BRAND_SERIES_ROLE,
  BRAND_HARDWARE_MANUFACTURER,
  BRAND_OTA_ZONE_EYEBROW,
  BRAND_OTA_UNLOCK_PLACEHOLDER,
} from "./lib/brand.js";
import {
  OPS_HIST_LEN,
  OPS_SPARK_SPECS,
  createOpsHist,
  pushOpsHistFromPayload,
  redrawOpsSparkGrid,
  ensureOpsSparkHover,
  opsMetricsStale,
  opsSparkSpecsForPrincipal,
  opsMetricsQueryScopeForSparkSpecs,
  unwrapOpsMetricsEnvelope,
  opsSparkSpecsFromAllowedKeys,
} from "./lib/ops-metrics-viz.js";
import { aiIncidentObservabilityHtml } from "./lib/ai-incident-observability-panel.js";
import { oncrIncidentUxHtml } from "./lib/oncr-ux-panel.js";
import { canUseOpsMap } from "./lib/ops-map-policy.js";
import {
  getHomeLandingPreference,
  setHomeLandingPreference,
  applyHomeLandingChrome,
  wireHomeLandingPick,
} from "./lib/home-priority.js";
import { resolveOpsMapVendorScriptUrl } from "./lib/ops-map-loader.js";
import { runGlobalOpsMapTeardownIfAny } from "./lib/ops-map-teardown.js";
import { geocodeOpenMeteo, parseLatLngPair } from "./lib/ops-map-geocode.js";
import { clampOpsMapZoom } from "./lib/ops-map-limits.js";
import { startAuthResendCooldown, startEmailCodeExpiryCountdown } from "./lib/email-otp-countdown.js";
import { formatHttpApiDetail } from "./lib/http-error-detail.js";
import { triggerDownloadFromBlob } from "./lib/download-blob.js";
import {
  DEVICES_API_LIVE,
  OVERVIEW_API_LIVE,
  FLEET_PAGE_LIMIT,
  pickDevicesPollPath,
  pickOverviewPollPath,
  deviceReachable,
  devicePresenceStatus,
  deviceStatusLine,
  devicePresenceBadgeText,
} from "./lib/device-presence.js";
import {
  bindFleetListApi,
  fetchFleetDevicesList,
  fetchFleetDevicesPage,
  fetchFleetDevicesPoll,
  fetchGroupDevicesPoll,
  FLEET_DEVICES_INITIAL_PAGES,
  FLEET_POLL_MAX_PAGES,
  GROUP_DEVICES_POLL_MAX_PAGES,
  fleetVisibleTotalFrom,
  fleetPollTruncatedHtml,
  syncFleetPollTruncBanner,
} from "./lib/fleet-list-paginated.js";
import {
  debounceDevicesSearch,
  devicesSearchQueryReady,
  devicesSearchTooShort,
  devicesServerSearchActive,
  syncDevicesSearchMinLenBanner,
  syncDevicesServerSearchBanner,
} from "./lib/devices-fleet-search.js";
import {
  mountDeviceGridVirtualList,
  DEVICES_GRID_VIRTUAL_MIN,
  DEVICES_GRID_VIRTUAL_ROW_PX,
  GROUP_PAGE_GRID_VIRTUAL_ROW_PX,
  OVERVIEW_GROUP_CARD_VIRTUAL_ROW_PX,
} from "./lib/device-grid-virtual-list.js";
import {
  fetchOverviewAndDevicesBundle,
  fetchOverviewLivePair,
  buildOverviewKpiPatch,
  overviewFleetPollTruncatedHtml,
  syncOverviewFleetPollTruncBanner,
  OVERVIEW_LIVE_MS,
} from "./lib/overview-fleet-poll.js";
import {
  assessOverviewLiveTick,
  shouldToastOverviewLivePollFail,
  syncOverviewLivePollDegradedBanner,
} from "./lib/overview-live-poll-status.js";
import {
  buildOverviewSlotsFromApiResponse,
  coalesceOverviewGroupSlots,
  enrichOverviewSlotsForDisplay,
  filterOverviewSlotsByOwner,
  mergeSettingsOnlyOverviewSlots,
  mergeTenantOwnerDirectory,
  overviewGroupSlotsTruncatedHtml,
  overviewTenantOwnerIndexTruncatedHtml,
  resolveGroupAlarmOwnerAdmin,
  resolveOverviewGroupCardTitle,
  syncOverviewGroupSlotsTruncBanner,
} from "./lib/overview-group-slots.js";
import { fetchOverviewGroupSettingsList } from "./lib/overview-group-settings-load.js";
import {
  collectSiteGroupSlots,
  collectSiteOwnerUsernames,
  fetchSiteOverviewBundle,
  filterSiteDevicesByOwner,
  siteFleetBannerHtml,
  siteOwnersIndexNote,
  siteOwnerServerFilterActive,
  siteOwnerServerFilterBannerHtml,
  appendSiteFleetPage,
  buildSiteDeviceTableRowHtml,
  siteFleetCursorState,
  SITE_FLEET_INITIAL_PAGES,
} from "./lib/site-fleet-data.js";
import {
  findGroupPageOverviewSlot,
  groupPageFleetTruncatedHtml,
  groupPagePresenceCounts,
  groupPageAccessPartial,
  groupPageSampleMismatch,
  syncGroupPageFleetTruncBanner,
} from "./lib/group-page-overview-slot.js";
import {
  appendGroupPageFleetPage,
  groupPageFleetCursorState,
  groupPageLegacyFilterBannerHtml,
  GROUP_PAGE_DEVICES_INITIAL_PAGES,
  GROUP_FLEET_ALL_KEY,
  GROUP_HUB_DIRECTORY_KEY,
  isGroupFleetAllView,
  isGroupHubDirectoryView,
  loadGroupPageFleet,
} from "./lib/group-page-fleet-load.js";
import {
  buildGroupAllDevicesBulkToolbarHtml,
  createGroupAllDevicesFleetPanel,
  mountGroupAllDevicesBulkToolbar,
} from "./lib/group-fleet-all-devices-panel.js";
import {
  groupDeviceIdsFromList as overviewGroupDeviceIdsFromList,
  inferUniqueOwnerForGroup as overviewInferUniqueOwnerForGroup,
  groupDeviceIdsFromSlot as overviewGroupDeviceIdsFromSlot,
  sirenTargetIdsForSlot as overviewSirenTargetIdsForSlot,
  canGroupSirenForSlot as overviewCanGroupSirenForSlot,
  groupSharedBySlot as overviewGroupSharedBySlot,
  groupSharedByNotificationKey as overviewGroupSharedByNotificationKey,
  overviewSlotDeviceSampleIncomplete,
  overviewSlotSampleIncompleteAlarmTitle,
} from "./lib/overview-group-slot-helpers.js";
import {
  buildOverviewGroupDeviceRowHtml,
  buildOverviewGroupCardHtml,
} from "./lib/overview-group-card-html.js";
import { createOverviewShareChrome } from "./lib/overview-share-modal.js";
import {
  buildShareModalDevicePickerRowsHtml,
  filterSharePickerOwnDevices,
  loadShareModalDevicePickerPage,
  mergeSharePickerDevicePages,
  shareModalDevicePickerTruncatedHtml,
} from "./lib/share-modal-device-picker.js";
import {
  createOverviewGroupModalsController,
  readOverviewGroupSlotFromBtn,
} from "./lib/overview-group-modals.js";
import {
  buildGroupsHubCardsSectionHtml,
  buildGroupsHubCardsModalsHtml,
  buildGroupDetailEditModalHtml,
  wireGroupsHubCardsSurface,
} from "./lib/overview-groups-hub-surface.js";
import {
  buildOverviewCommandCenterShellHtml,
  buildCommandCenterPageShellHtml,
  mountCommandCenterPageShell,
  clearCommandCenterPageShell,
  isCommandCenterImmersiveRoute,
  wireOverviewCommandCenterChrome,
  hashFromCcGo,
  ensureCcGoDocumentWire,
  patchOverviewCommandCenterMetrics,
  patchOverviewIncidentFeed,
  activateOverviewCommandCenterLayout,
  deactivateOverviewCommandCenterLayout,
  mapEventToIncidentCard,
  buildAutoSentinelHomeHtml,
  paintHomePhotoThumbsHtml,
  paintHomeProofStatus,
  applyHomeProofPanel,
  paintHomePeopleStripHtml,
  paintSoftLanesMapChip,
  openHomeAssignWorkbench,
} from "./lib/overview-command-center-ui.js";
import {
  buildOtaCommandCenterHtml,
  wireOtaCommandCenter,
  patchOtaCommandCenterMetrics,
  patchOtaCommandCenterLive,
} from "./lib/ota-command-center-ui.js";
import {
  buildAccountSettingsShellHtml,
  buildAccountSettingsNavHtml,
} from "./lib/dashboard/pages/AccountSettingsShell.template.js";
import { ensureAccountSettingsStyles, removeAccountSettingsStyles } from "./lib/dashboard/pages/account-settings-styles.js";
import { buildCapabilityOverviewHtml } from "./lib/capability-overview.js";
import {
  catalogEnabled,
  catalogAnyEnabled,
  catalogEntry,
  capabilityUnavailableHtml,
  workspaceBlockedHtml,
} from "./lib/capability-gate.js";
// workspaceBlockedHtml: use in routes for admin-gate / incomplete desks (no dead-ends)
import {
  buildHealthStatusCardsHtml,
  buildHealthAlertsTableHtml,
  severityBadgeClass,
} from "./lib/readiness-health-cards.js";
import {
  buildIncidentTimelineCardsHtml,
  buildSnapshotEvidenceHtml,
} from "./lib/incident-timeline-cards.js";
import {
  registerPwaServiceWorker,
  applyFcmDeepLink,
  purgePwaCachesOnLogout,
} from "./lib/pwa-register.js";
import {
  INTELLIGENCE_TIMEOUT_MS,
  buildGetAiIncidentsPath,
  buildGetAiIncidentPath,
  buildPostAiAnalyzePath,
  buildPostAiApprovePath,
  buildGetAiSettingsPath,
  buildPatchAiSettingsPath,
  buildGetPredictiveMaintenancePath,
} from "./lib/intelligence-console-api.js";
import {
  AUTOMATION_TIMEOUT_MS,
  buildGetAutomationsPath,
  buildPostAutomationsPath,
  buildPostAutomationTriggerPath,
  buildGetAutomationRunsPath,
} from "./lib/automation-console-api.js";
import {
  ORCHESTRATION_TIMEOUT_MS,
  buildGetIncidentCellPath,
  buildPostIncidentCellActivatePath,
  buildPostIncidentCellResolvePath,
  buildGetIncidentClusterPath,
  buildPostIncidentClusterJoinPath,
  buildPostIncidentClusterLeavePath,
  buildPostIncidentClusterReallocatePath,
  buildPatchIncidentClusterPriorityPath,
  buildGetIncidentClusterAffinityPath,
  buildGetIncidentStigmergyPath,
  buildPostIncidentStigmergyPath,
  buildGetIncidentSwarmAuditPath,
  buildGetActuatorResourcesPath,
  buildPostActuatorSelectPath,
} from "./lib/orchestration-console-api.js";
import {
  SRF_CONCEPTS_TIMEOUT_MS,
  buildGetPhysicalInhibitStatusPath,
  buildPostPhysicalInhibitPath,
  buildPostPhysicalInhibitClearPath,
  buildGetAdapterCertificationsPath,
  buildGetExternalRobotsCatalogPath,
  buildGetExternalRobotsIntegrationsPath,
  buildPostExternalRobotsIntegrationsPath,
  buildGetExternalRobotsUnitsPath,
  buildPostExternalRobotsUnitsPath,
  softAdapterKindLabel,
  softCertStateLabel,
  softRobotVendorLabel,
} from "./lib/srf-concepts-console-api.js";
import {
  PLATFORM_CONTROL_TIMEOUT_MS,
  buildGetFederationMetricsPath,
  buildGetFederationTenantsPath,
  buildPostFederationConsentPath,
  buildGetSaPlatformDomainsPath,
  buildPostSaDevicesCustodyPath,
  buildGetSaNotificationGroupSharesPath,
  buildGetSharesPath,
  buildPostSharesKeysPath,
  buildPostSharesRedeemPath,
  buildDeleteSharePath,
  buildGetAdminSecurityAuditPath,
  buildPostAdminSecurityRotateCmdKeyPath,
  buildGetSaMacDriftPath,
  buildGetAdminBackupSchedulePath,
  buildPostAdminBackupRestoreDrillPath,
  buildGetHealthStatusPath,
  buildGetHealthAlertsPath,
  buildGetSopPath,
  buildPostSopPath,
  buildPostSopFeedbackPath,
  buildGetSopRecommendationsPath,
  buildGetDevicesCapabilitiesPath,
  buildPostDeviceCapabilitiesPath,
  buildPostExportControlJobPath,
  buildGetExportControlJobPath,
  buildPostExportControlDownloadTokenPath,
  buildGetExportControlDownloadPath,
  buildGetExportControlDownloadPresignedPath,
} from "./lib/platform-control-console-api.js";
import { buildPlatformDomainsTablesHtml } from "./lib/platform-control-domains-table.js";
import { buildFederationTablesHtml } from "./lib/platform-control-federation-table.js";
import {
  buildSecurityTablesHtml,
  buildBackupScheduleTablesHtml,
} from "./lib/platform-control-security-backup-table.js";
import {
  buildAccountNavItems,
  buildAccountSettingsSubrouteHtml,
  buildSettingsHubNavItems,
  settingsNavItemVisible,
  wireAccountSettingsNav,
  wireSettingsHubNav,
} from "./lib/dashboard/pages/account-settings-chrome.js";
import { buildGroupDetailShellHtml } from "./lib/dashboard/pages/GroupDetailShell.template.js";
import {
  buildGroupSidebarListHtml,
  buildAllDevicesSidebarCardHtml,
  paintGroupSidebarAllDevicesCard,
  wireGroupDetailTabs,
  wireCreateGroupModal,
} from "./lib/dashboard/pages/group-detail-chrome.js";
import { ensureGroupDetailStyles } from "./lib/dashboard/pages/group-detail-styles.js";
import { ensureAuditLogStyles, removeAuditLogStyles } from "./lib/dashboard/pages/audit-log-styles.js";
import { ensureCityObservationStyles, removeCityObservationStyles } from "./lib/dashboard/pages/city-observation-styles.js";
import { createGroupDetailMapController } from "./lib/dashboard/pages/group-detail-map.js";
import {
  createOverviewMapStageController,
  runGlobalOverviewMapTeardownIfAny,
} from "./lib/dashboard/pages/overview-map-stage.js";
import {
  fetchGroupDetailEvents,
  renderGroupActivityListHtml,
  renderGroupAiRecommendation,
} from "./lib/dashboard/pages/group-detail-activity.js";
import {
  ensureActiveGroupInSidebarSlots,
  mergeHubRowsIntoSidebarSlots,
  buildGranteesMembersHtml,
  granteesForActiveSlot,
  paintModeBMembersPanels,
  loadModeBHubOperatorRows,
  wireGroupSidebarShareActions,
} from "./lib/dashboard/pages/group-sidebar-share.js";

import { buildGroupSharingContextLineHtml } from "./lib/group-page-share-context.js";
import {
  buildGroupPageDeviceCardHtml,
  createGroupPageDeviceGrid,
} from "./lib/group-page-device-grid.js";
import { createGroupPageShareChrome } from "./lib/group-page-share-modal.js";
import {
  formatGroupShareApplyError,
  isGroupShareDbBusyError,
} from "./lib/group-share-busy-ux.js";
import { bindAdminSharesApi, fetchAllAdminShares } from "./lib/admin-shares-paginated.js";
import {
  buildGroupsHubSummaryPath,
  buildNotificationGroupSharesFallbackPath,
} from "./lib/groups-hub-api.js";
import {
  fetchGroupsHubGranteeFleet,
  fetchGroupsHubOperatorBundle,
  probeGroupsHubAdminHasOwnedDevices,
} from "./lib/groups-hub-load.js";
import { revokeGroupsHubBulkRows } from "./lib/groups-hub-bulk-revoke.js";
import {
  ADMIN_CONSOLE_TIMEOUT_MS,
  ADMIN_CONSOLE_WRITE_TIMEOUT_MS,
  ADMIN_BACKUP_FETCH_TIMEOUT_MS,
  buildAuthAdminsPath,
  buildAuthUsersPath,
  buildPostAuthUsersPath,
  buildAuthUsersPolicyPath,
  buildPutAuthUsersPolicyPath,
  buildDeleteAuthUserPath,
  buildPostAuthAdminClosePath,
  buildAdminDeviceSharesPath,
  buildAdminDeviceSharePath,
  buildAdminDeviceShareRevokePath,
  buildAdminSmtpStatusPath,
  buildPostAdminSmtpTestPath,
  buildAdminAlertRecipientsPath,
  buildPostAdminAlertRecipientsRequestVerifyPath,
  buildPostAdminAlertRecipientsPath,
  buildDeleteAdminAlertRecipientPath,
  buildPatchAdminAlertRecipientPath,
  buildAdminGranteeAlertRecipientsPath,
  buildPostAdminGranteeAlertRecipientsRequestVerifyPath,
  buildPostAdminGranteeAlertRecipientsPath,
  buildDeleteAdminGranteeAlertRecipientPath,
  buildAdminTelegramStatusPath,
  buildAdminTelegramBindingsPath,
  buildPostAdminTelegramTestPath,
  buildPostAdminTelegramBindSelfPath,
  buildDeleteAdminTelegramBindingPath,
  buildPatchAdminTelegramBindingEnabledPath,
  buildPostTelegramLinkTokenPath,
  buildAdminBackupExportPath,
  buildAdminBackupImportPath,
  buildAuthSignupPendingPath,
  buildPostAuthSignupApprovePath,
  buildPostAuthSignupRejectPath,
} from "./lib/admin-console-api.js";
import {
  ACCOUNT_AVATAR_UPLOAD_TIMEOUT_MS,
  ACCOUNT_CONSOLE_TIMEOUT_MS,
  ACCOUNT_CONSOLE_WRITE_TIMEOUT_MS,
  buildAdminUserScopesMePath,
  buildPostAuthMeAvatarUploadPath,
  buildPatchAuthMeProfilePath,
  buildPatchAuthMePasswordPath,
  buildPostAuthMeDeletePath,
  buildAuthMeAlertRecipientsPath,
  buildPostAuthMeAlertRecipientsRequestVerifyPath,
  buildPostAuthMeAlertRecipientsPath,
  buildDeleteAuthMeAlertRecipientPath,
  buildPatchAuthMeAlertRecipientPath,
} from "./lib/account-console-api.js";
import {
  DEVICES_CONSOLE_TIMEOUT_MS,
  DEVICES_FIRMWARE_HINTS_TIMEOUT_MS,
  buildGetDevicesPath,
  buildGetDevicesMapSnapshotPath,
  buildPostDevicesMapResolvePath,
  buildPostDevicesBulkProfilePath,
  buildDevicesFirmwareHintsPath,
  buildGetDevicePath,
  buildDeviceAlarmFeedPath,
  buildDeviceMessagesPath,
  buildPatchDeviceProfilePath,
  buildPostDeviceAlertOnPath,
  buildPostDeviceAlertOffPath,
  buildPostDeviceSelfTestPath,
  buildPostDeviceScheduleRebootPath,
  buildPostDeviceUnrevokePath,
  buildPostDeviceCommandsPath,
  buildPostDeviceDeleteResetPath,
  buildGetDeviceProvisionWifiTaskPath,
  buildPostDeviceProvisionWifiTaskPath,
  buildGetDeviceTriggerPolicyPath,
  buildPutDeviceTriggerPolicyPath,
} from "./lib/devices-console-api.js";
import {
  deviceCamerasPanelHtml,
  wireDeviceCamerasPanel,
} from "./lib/device-cameras-panel.js";
import {
  camerasFleetHubShellHtml,
  wireCamerasFleetHub,
} from "./lib/cameras-fleet-hub.js";
import {
  devicePresenceDiagnosePanelHtml,
  wireDevicePresenceDiagnosePanel,
} from "./lib/device-presence-panel.js";
import {
  activateQrScanSupported,
  activateQrScanUnavailableReason,
  createActivateQrScanner,
} from "./lib/activate-qr-scan.js";
import {
  dispatchComposerHtml,
  wireDispatchComposer,
} from "./lib/dispatch-composer.js";
import {
  deviceCapabilitiesPanelHtml,
  wireDeviceCapabilitiesPanel,
  mountDeviceCapabilitiesFleet,
} from "./lib/device-capabilities-panel.js";
import {
  deviceTfStoragePanelHtml,
  wireDeviceTfStoragePanel,
} from "./lib/device-tf-storage-panel.js";
import {
  HOME_CONSOLE_TIMEOUT_MS,
  buildPostAlertsPath,
  buildBulkAlertBody,
  buildAdminNotificationGroupSharePreviewPath,
  buildPostAdminNotificationGroupShareLabelPath,
} from "./lib/home-console-api.js";
import {
  OTA_CONSOLE_TIMEOUT_MS,
  OTA_DEVICES_LIST_TIMEOUT_MS,
  OTA_VERIFY_UNLOCK_TIMEOUT_MS,
  OTA_FIRMWARE_UPLOAD_TIMEOUT_MS,
  buildPostAuthMeVerifyOtaUnlockPath,
  buildOtaFirmwaresPath,
  buildPostOtaFirmwareUploadPath,
  buildPostOtaCampaignsFromStoredPath,
  buildGetOtaCampaignsPath,
  buildPostOtaCampaignAcceptPath,
  buildPostOtaCampaignDeclinePath,
  buildPostOtaCampaignRollbackPath,
  buildOtaFirmwareReachabilityPath,
} from "./lib/ota-console-api.js";
import {
  AUTH_LOGOUT_TIMEOUT_MS,
  AUTH_ME_TIMEOUT_MS,
  AUTH_LOGIN_TIMEOUT_MS,
  AUTH_HEALTH_TIMEOUT_MS,
  buildPostAuthLogoutPath,
  buildGetAuthMePath,
  buildGetHealthPath,
  buildPostAuthLoginPath,
  buildPostAuthLoginEmailSecondPath,
  buildPostAuthCodeResendPath,
  buildGetAuthMeAvatarPath,
  buildPostAuthSignupStartPath,
  buildPostAuthSignupVerifyPath,
  buildPostAuthActivatePath,
  buildGetAuthForgotEmailEnabledPath,
  buildPostAuthForgotEmailCheckPath,
  buildPostAuthForgotEmailStartPath,
  buildPostAuthForgotEmailCompletePath,
} from "./lib/auth-console-api.js";
import {
  TENANT_CONSOLE_TIMEOUT_MS,
  buildGetAdminUserScopesChildrenPath,
  buildGetAdminUserScopesPath,
  buildPostAdminUserScopePath,
  buildDeleteAdminUserScopePath,
  buildGetAdminTenantDelegationsPath,
  buildPostAdminTenantDelegationsPath,
  buildDeleteAdminTenantDelegationPath,
} from "./lib/tenant-console-api.js";
import {
  SA_CONSOLE_TIMEOUT_MS,
  buildGetSaTeamPath,
  buildPostSaTeamProvisionPath,
  buildPostSaTeamTtlPath,
} from "./lib/sa-console-api.js";
import {
  PROVISION_CONSOLE_TIMEOUT_MS,
  buildPostProvisionClaimPath,
  buildPostProvisionIdentifyPath,
} from "./lib/provision-console-api.js";
import {
  BILLING_CONSOLE_TIMEOUT_MS,
  buildBillingInvoicesPath,
  buildBillingUsagePath,
} from "./lib/billing-console-api.js";
import {
  VOICE_CONSOLE_TIMEOUT_MS,
  buildGetAdminVoiceTemplatesPath,
  buildPutAdminVoiceTemplatePath,
  buildPostAdminVoiceTestCallPath,
} from "./lib/voice-console-api.js";
import {
  LOCATIONS_CONSOLE_TIMEOUT_MS,
  buildLocationsLatestPath,
  buildLocationsStreamPath,
  buildLocationsConsentPostPath,
  buildLocationsConsentDeletePath,
  buildLocationsConsentsPath,
  buildLocationsBatchPath,
  buildLocationsOnCallGetPath,
  buildLocationsOnCallPutPath,
  buildPhoneLocationBatchBody,
} from "./lib/locations-console-api.js";
import { locationsSoftDeskHtml, wireLocationsSoftDesk } from "./lib/locations-soft-desk.js";
import { renderTeamLocationTreeHtml } from "./lib/team-location-tree.js";
import {
  OPS_METRICS_CONSOLE_TIMEOUT_MS,
  buildAdminOpsGlobalMetricsPath,
  buildAdminOpsGlobalMetricsStreamPath,
  buildGetLogsFilePath,
  buildGetAdminFcmTopicMigrationHealthPath,
} from "./lib/ops-console-api.js";
import {
  CAMERAS_CONSOLE_TIMEOUT_MS,
  CAMERAS_SNAPSHOT_IMAGE_TIMEOUT_MS,
  cameraApiPathWithOwner,
  buildGetAdminCameraSnapshotsPath,
  buildGetAdminCameraSnapshotImagePath,
  buildPostAdminCameraSnapshotSendEmailPath,
  buildGetAdminCamerasPath,
  buildPostAdminCamerasPath,
  buildDeleteAdminCamerasPath,
  buildPostAdminCamerasProbeTestPath,
  buildPostAdminCamerasTestCapturePath,
  buildGetAdminDevicesCameraBindingsPath,
  buildPostAdminDevicesCameraBindingsPath,
  buildDeleteAdminDevicesCameraBindingsPath,
  buildGetAdminDevicesCameraDiscoveryPath,
  buildPostAdminDevicesCameraDiscoveryPath,
} from "./lib/cameras-console-api.js";
import { animateCcPageEnter } from "./lib/cc-motion.js";
import {
  TELEMETRY_EVENTS_TIMEOUT_MS,
  TELEMETRY_AUDIT_TIMEOUT_MS,
  TELEMETRY_ACTIVITY_TIMEOUT_MS,
  TELEMETRY_SIGNALS_TIMEOUT_MS,
  TELEMETRY_ALARMS_SUMMARY_TIMEOUT_MS,
  queryFromSearchParams,
  buildEventsPath,
  buildEventsStatsByDevicePath,
  buildEventsStreamPath,
  buildEventsExportCsvPath,
  buildAuditPath,
  buildAuditExportCsvPath,
  buildAuditExportPdfPath,
  buildAuditExportCoverPdfPath,
  buildEventsExportCoverPdfPath,
  buildActivityRecordsPath,
  buildActivitySignalsPath,
  buildAlarmsSummaryPath,
  buildPostActivityScopeTokenMintPath,
} from "./lib/telemetry-console-api.js";
import {
  EVENTS_SSE_POLL_INTERVAL_MS,
  EVENTS_SSE_FAIL_THRESHOLD,
  shouldEnterPollFallback,
  parseRetryAfterSeconds,
  pickEventsStreamPath,
  preferSseGatewayV2Stream,
  pollFallbackBadgeLabel,
  pollFallbackBannerMessage,
  openEventsLiveStream,
} from "./lib/events-stream-live.js";
import {
  buildGetGroupCardsSettingsPath,
  buildPutGroupCardsSettingsPath,
  buildPostGroupCardsApplyPath,
  buildDeleteGroupCardsPath,
  buildPostGroupCardsDeletePath,
  buildDeleteNotificationGroupsPath,
  buildPostNotificationGroupsDeletePath,
  buildGetGroupsV1SettingsPath,
  buildPutGroupsV1SettingsPath,
  buildPostGroupsV1ApplyPath,
  buildDeleteGroupsV1Path,
  buildGetGroupsV1SharesPath,
  buildPutGroupsV1SharesPath,
} from "./lib/group-console-api.js";
import {
  tryContractApiWithProbe,
  tryGroupDeleteWithProbe,
} from "./lib/group-api-compat.js";
import {
  bindGroupSharesApi,
  fetchGroupShareGrantees,
  syncGroupShareGrantees,
  bulkRevokeGroupShareHub,
  granteePermissionSummary,
} from "./lib/group-shares-v1.js";
import {
  applyGroupCardSettingsV1,
  bindGroupCardsApi,
  deleteGroupCardV1,
  getGroupCardSettingsV1,
  putGroupCardSettingsV1,
} from "./lib/group-cards-v1.js";

/* Croc Sentinel Console - SPA
 * Markup safety: escapeHtml(str) or hx`...${str}...` for any API/user text; mountView(el, html) for route shells (DOMParser + replaceChildren, no innerHTML).
 * Live stream: Events use fetch()+stream+Authorization (not EventSource URL+?token=) for reverse-proxy reliability. */
  // LS / OFFLINE_MS / DEFAULT_REMOTE_SIREN_MS / DEFAULT_PANIC_FANOUT_MS /
  // NAV_GROUPS / ROLE_WEIGHT / one-time JWT migration → src/lib/constants.js.

  // authSiteFooterHtml / authAsideHtml → src/lib/auth-chrome.js
  // (imported by build-dashboard.mjs HEADER).

  // state object + group-meta storage + lifecycle timers (route redirect /
  // health poll / events live badge) → src/shell/00-state.shell.js.

  // ------------------------------------------------------------------ utils
  // $ / $$ → src/lib/dom.js.

  // apiBase / fetchWithDeadline / _sleep / _isTransientFetchError /
  // _isRetryableHttpStatus / DEFAULT_API_TIMEOUT_MS / ROUTE_RENDER_TIMEOUT_MS
  // → src/lib/api.js (imported by build-dashboard.mjs HEADER).

  // getToken / setToken → src/shell/40-glue.shell.js.

  // ------------------------------------------------------------------ csrf
  // CSRF_COOKIE_NAME / CSRF_HEADER_NAME / _readCsrfCookie / getCsrfToken /
  // setCsrfToken / refreshCsrfToken / _isCsrfRejection → src/lib/csrf.js.
  // _isWriteMethod (HTTP method classifier) → src/lib/api.js.

  // escapeHtml / parseHtmlToFragment / setChildMarkup / prependChildMarkup /
  // appendChildMarkup / setHtmlIfChanged / setTextIfChanged / hx / mountView
  // → src/lib/dom.js.

  // parseSseFields / SSE_PARSE_BUF_MAX / pumpSseBody → src/lib/sse.js.

  // MY_TZ / MY_OFFSET_HINT / fmtTs / fmtRel / maskPlatform / auditActionPrefix /
  // auditDetailDedupedRows / eventDetailDedupedRows / messagePayloadRows /
  // auditChipClass → src/lib/format.js.

  // roleWeight / hasRole / can / isOnline / toast → src/shell/40-glue.shell.js.

  // api / apiOr / apiGetCached / FW hint dialog / login / loadMe / loadHealth
  // and the group apply/delete + share matrix helpers →
  // src/shell/10-api.shell.js.

  // renderAuthState / renderNav / renderHealthPills / renderMqttDot /
  // setCrumb / setTheme / initTheme / toggleNav / applySidebarRail /
  // toggleSidebarRail / syncNavForViewport → src/shell/20-layout.shell.js.

  // registerRoute / renderRoute / clearRouteTickers / scheduleRouteTicker /
  // hashchange listener → src/shell/30-router.shell.js.

  // ------------------------------------------------------------------ pages
  //
  // Auth pages have been peeled off into src/routes/*.route.js (concatenated
  // back into this same scope by scripts/build-dashboard.mjs):
  //   • login.route.js              (#/login)
  //   • forgot-password.route.js    (#/forgot-password)
  //   • register.route.js           (#/register)
  //   • account-activate.route.js   (#/account-activate)
  //
  // Adding more routes? Drop a `<id>.route.js` in src/routes/ that calls
  // `registerRoute("<id>", async (view, args, routeSeq) => {...})` at the top
  // level — do NOT use ES `import`/`export`, the file is concatenated as raw
  // text so it shares scope with this monolith. See src/README.md.

  // route "account" extracted to src/routes/account.route.js

  // Overview
  // route "overview" extracted to src/routes/overview.route.js

  // route "site" extracted to src/routes/site.route.js

  // route "group" extracted to src/routes/group.route.js

  // Device list (no id) + device detail
  // route "devices" extracted to src/routes/devices.route.js

  // Siren channel — `#/response?tab=siren` (ZD-DEAD: alerts.route.js deleted tip559).

  // Activate
  // route "activate" extracted to src/routes/activate.route.js

  // Event Center / soft Audit Log hashes — F18 tip581 purge (was soft → audit-log)
  // route "events"|"activity"|"signals"|"audit" deleted; canonical `#/audit-log`

  // Telegram self-service (user/admin/superadmin)
  // route "telegram" extracted to src/routes/telegram.route.js

  // Admin
  // route "admin" extracted to src/routes/admin.route.js

  // route "billing" extracted to src/routes/billing.route.js

  // route "ota" extracted to src/routes/ota.route.js

  // ------------------------------------------------------------------ boot
  async function boot() {
    initTheme();
    try { ensureCcGoDocumentWire(); } catch (_) {}

    $("#menuBtn").addEventListener("click", () => toggleNav());
    $("#sidebarBackdrop").addEventListener("click", () => toggleNav(false));
    const railT = document.getElementById("sidebarRailToggle");
    if (railT) railT.addEventListener("click", () => toggleSidebarRail());
    window.addEventListener("resize", syncNavForViewport);
    window.addEventListener("orientationchange", syncNavForViewport);
    $("#themeBtn").addEventListener("click", () => {
      setTheme(document.documentElement.dataset.theme === "dark" ? "light" : "dark");
    });
    $("#logoutBtn").addEventListener("click", async () => {
      try {
        await fetchWithDeadline(apiBase() + buildPostAuthLogoutPath(), { method: "POST" }, 12000);
      } catch (_) {}
      try { await purgePwaCachesOnLogout(); } catch (_) {}
      setToken("");
      state.me = null;
      clearHealthPollTimer();
      location.hash = "#/login";
      renderAuthState();
    });
    $("#refreshBtn").addEventListener("click", () => renderRoute());

    document.addEventListener("click", (ev) => {
      const b = ev.target.closest(".btn-tap");
      if (!b || b.disabled) return;
      try {
        if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
        if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(10);
      } catch (_) {}
    }, true);

    syncNavForViewport();
    try { applySidebarRail(); } catch (_) {}
    if (!location.hash) location.hash = "#/login";
    state.bootReady = false;
    try {
      await loadMe();
      if (state.me) {
        const pub =
          typeof PUBLIC_ROUTE_IDS !== "undefined" && PUBLIC_ROUTE_IDS
            ? PUBLIC_ROUTE_IDS
            : new Set(["login", "forgot-password", "register", "account-activate"]);
        const rid = ((location.hash || "#/login").split("/")[1] || "login").split("?")[0];
        if (pub.has(rid)) location.hash = defaultHomeHash();
      }
    } catch (_) {}
    state.bootReady = true;
    await renderRoute();
    void loadHealth().catch(() => {});
    window.addEventListener("online", () => {
      loadHealth().catch(() => {});
      tickHealthIfVisible();
      if (typeof window.__eventsStreamResume === "function") {
        try {
          if (!window.__evSSE || window.__evSSE.readyState === EventSource.CLOSED) {
            window.__eventsStreamResume();
          } else {
            syncEventsLiveBadge();
          }
        } catch (_) {}
      }
    });
    document.addEventListener("visibilitychange", () => {
      document.documentElement.classList.toggle("tab-hidden", document.visibilityState === "hidden");
      if (document.visibilityState === "hidden") {
        /* Keep SSE open through tab switches — closing forced reconnect storms through proxies. */
        const live = document.getElementById("evLive");
        if (live && window.__evSSE && window.__evSSE.readyState === EventSource.OPEN) {
          live.textContent = "Background";
          live.className = "badge neutral";
          live.title = "Stream stays connected in background";
        }
        return;
      }
      tickHealthIfVisible();
      syncEventsLiveBadge();
      if (typeof window.__eventsStreamResume === "function") {
        try {
          if (!window.__evSSE || window.__evSSE.readyState === EventSource.CLOSED) {
            window.__eventsStreamResume();
          }
        } catch (_) {}
      }
    });
    document.documentElement.classList.toggle("tab-hidden", document.visibilityState === "hidden");
  }

  document.addEventListener("DOMContentLoaded", boot);
// ============================================================
// === Shell spliced from src/shell/*.shell.js ================
// === (state / api / layout / router; raw concat, shared) ====
// ============================================================

// ── 00-state.shell.js ─────────────────────────────────────
/**
 * SPA shared state + group-meta storage + lifecycle timers.
 *
 * Concatenated as raw text by scripts/build-dashboard.mjs (NOT an ESM
 * module). Lives in the same IIFE scope as the monolith body and the
 * route files, which is why every other module can still reference
 * `state`, `_groupMetaSyncChain`, `healthPollTimer`, etc. without imports.
 *
 * Numeric prefix `00-` forces this to load first inside src/shell/ so the
 * mutable state declarations exist before any function that touches them
 * runs (function declarations in later shell files hoist; let/const here
 * does not).
 */
// ------------------------------------------------------------------ state
const state = {
  me: null,
  mqttConnected: false,
  health: null,
  overviewCache: null,
  routeSeq: 0,
  /** False until the first loadMe() in boot() completes — gates renderRoute auth. */
  bootReady: false,
  /** Bumped after avatar URL changes so sidebar <img> bypasses browser cache */
  avatarImgRev: 0,
};

const GROUP_CARD_TENANT_SEP = "\u001e";
function normalizeGroupKeyStr(v) {
  return String(v == null ? "" : v).trim();
}
/**
 * Display-friendly normalization of a notification_group string.
 * Trims, NFC-normalizes, collapses internal whitespace — but preserves
 * the user's original casing. Use this for human-readable strings
 * (group card titles, breadcrumbs, edit-form prefills).
 */
function displayGroupName(v) {
  let s = normalizeGroupKeyStr(v);
  if (!s) return "";
  try {
    s = s.normalize("NFC");
  } catch (_) {}
  return s.replace(/\s+/g, " ");
}
/**
 * Sibling-matching key for a notification_group string.
 *
 * **Must mirror the backend ``helpers._sibling_group_norm`` exactly**
 * so the dashboard groups devices the same way the MQTT fan-out
 * resolves siblings. The backend does:
 *
 *     strip → NFC → collapse whitespace → casefold
 *
 * If this drifts, the dashboard will show phantom duplicate cards
 * (e.g. "Warehouse A" and "warehouse a" rendered separately) while
 * the server still treats them as one sibling group — operators
 * see two cards but only one set of devices reacts to "Alarm ON".
 *
 * We use ``toLocaleLowerCase()`` as the JS-side equivalent of Python's
 * ``str.casefold()``: case-folding is locale-aware and handles e.g.
 * the German ß → ss collision the simple ``toLowerCase()`` misses.
 * For the ASCII / Latin-1 names that 99% of group keys use these
 * are equivalent.
 */
function canonicalGroupKey(v) {
  const s = displayGroupName(v);
  if (!s) return "";
  try {
    return s.toLocaleLowerCase();
  } catch (_) {
    return s.toLowerCase();
  }
}
/** Stable localStorage / UI key for a group card.
 * Superadmin prefixes owning admin. Grantee prefixes ``shared_by`` when set so two
 * owners cannot merge into one card if they reuse the same notification group name.
 */
function groupCardMetaKey(groupKey, tenantOwner) {
  const gk = canonicalGroupKey(groupKey);
  if (!gk) return "";
  const o = String(tenantOwner || "").trim();
  if (state.me && state.me.role === "superadmin") {
    return `${o || "__unowned__"}${GROUP_CARD_TENANT_SEP}${gk}`;
  }
  if (o) {
    return `${o}${GROUP_CARD_TENANT_SEP}${gk}`;
  }
  return gk;
}
function parseGroupMetaKey(metaKey) {
  const mk = String(metaKey || "");
  if (mk.includes(GROUP_CARD_TENANT_SEP)) {
    const i = mk.indexOf(GROUP_CARD_TENANT_SEP);
    let tenantOwner = mk.slice(0, i).trim();
    if (tenantOwner === "__unowned__") tenantOwner = "";
    return { tenantOwner, groupKey: canonicalGroupKey(mk.slice(i + 1)) };
  }
  return { tenantOwner: "", groupKey: canonicalGroupKey(mk) };
}
/** Group card slots from a device list (Overview / Site). */
function buildGroupSlotsFromDeviceList(devList) {
  const acc = new Map();
  const isSuper = state.me && state.me.role === "superadmin";
  for (const d of (Array.isArray(devList) ? devList : [])) {
    const sgk = String((d && d.shared_group_key) || "").trim();
    const gk = sgk ? canonicalGroupKey(sgk) : canonicalGroupKey(d && d.notification_group);
    if (!gk) continue;
    const tenant = isSuper
      ? String(d.owner_admin || "").trim()
      : (d.is_shared ? String(d.shared_by || "").trim() : "");
    const mk = groupCardMetaKey(gk, tenant);
    if (!acc.has(mk)) acc.set(mk, { metaKey: mk, groupKey: gk, tenantOwner: tenant });
  }
  return Array.from(acc.values()).sort((a, b) => {
    const c = a.groupKey.localeCompare(b.groupKey);
    return c !== 0 ? c : a.tenantOwner.localeCompare(b.tenantOwner);
  });
}
function groupApiQueryOwner(tenantOwner) {
  const o = String(tenantOwner || "").trim();
  if (!o) return "";
  // Superadmin and grantees pass owning tenant; server validates ACL / role.
  return `owner_admin=${encodeURIComponent(o)}`;
}
function groupApiSuffixWithOwner(pathSuffix, tenantOwner) {
  const q = groupApiQueryOwner(tenantOwner);
  if (!q) return pathSuffix;
  const join = pathSuffix.includes("?") ? "&" : "?";
  return `${pathSuffix}${join}${q}`;
}

/** Group cards (Overview) are stored in localStorage; keep in sync when device group changes in profile. */
function groupMetaStorageKey() {
  return (state.me && state.me.username) ? `croc.group.meta.v2.${state.me.username}` : "croc.group.meta.v2.anon";
}
function reconcileGroupMetaForDevice(deviceId, newGroupKey, ownerHint) {
  try {
    const k = groupMetaStorageKey();
    const raw = localStorage.getItem(k);
    const meta = raw ? JSON.parse(raw) : {};
    if (!meta || typeof meta !== "object") return;
    const id = String(deviceId || "");
    for (const gk of Object.keys(meta)) {
      const m = meta[gk];
      if (!m || !Array.isArray(m.device_ids)) continue;
      const fil = m.device_ids.filter((x) => String(x) !== id);
      if (fil.length !== m.device_ids.length) {
        if (fil.length) meta[gk] = { ...m, device_ids: fil };
        else delete meta[gk];
      }
    }
    const ng = canonicalGroupKey(newGroupKey);
    if (ng) {
      const ck = groupCardMetaKey(ng, ownerHint);
      if (!ck) return;
      if (!meta[ck] || typeof meta[ck] !== "object") {
        // Prefer the user-supplied (case-preserved) string for the title
        // — case-folded ``ng`` would render as "warehouse a" instead of
        // the "Warehouse A" the user just typed.
        const human = displayGroupName(newGroupKey) || ng;
        meta[ck] = { display_name: human, owner_name: "", phone: "", email: "", device_ids: [] };
      }
      const s = new Set((meta[ck].device_ids || []).map(String));
      s.add(id);
      meta[ck].device_ids = Array.from(s);
    }
    localStorage.setItem(k, JSON.stringify(meta));
  } catch (_) {}
}
function removeDeviceIdFromAllGroupMeta(deviceId) {
  reconcileGroupMetaForDevice(deviceId, "");
}

/**
 * Single source of truth for group membership: device_state.notification_group from /devices.
 * - Overwrites each group's device_ids from the API.
 * - Keeps display_name / owner / contact for groups that still exist.
 * - Drops any local-only/stale group keys to avoid residual data after edits.
 */
function syncGroupMetaWithDevices(meta, devices) {
  if (!meta || typeof meta !== "object") return meta;
  const list = Array.isArray(devices) ? devices : [];
  const isSuper = state.me && state.me.role === "superadmin";
  const notifMap = new Map();
  const sharedCardKeys = new Set();
  // Pick a representative human-readable name for each canonical group.
  // Prefer the first device's original (case-preserved) notification_group
  // string so the card title stays "Warehouse A" even though the match
  // key is folded to "warehouse a". Without this fallback, cards
  // re-rendered after a fresh load would show the lowercase key.
  const displayMap = new Map();
  for (const d of list) {
    const g = canonicalGroupKey(d && d.notification_group);
    if (!g) continue;
    const ck = groupCardMetaKey(g, isSuper ? d.owner_admin : (d.is_shared ? d.shared_by : ""));
    if (!notifMap.has(ck)) notifMap.set(ck, []);
    notifMap.get(ck).push(String(d.device_id));
    if (!isSuper && d && d.is_shared) sharedCardKeys.add(ck);
    if (!displayMap.has(ck)) {
      const human = displayGroupName(d && d.notification_group);
      if (human) displayMap.set(ck, human);
    }
  }
  // Migrate any pre-Phase-90 entries that were keyed by the old
  // case-preserving canonicalGroupKey: when the new (case-folded) key
  // collides with an old (cased) one for the same tenant, copy the
  // user-editable fields forward so we don't drop display_name /
  // owner_name / phone / email metadata silently.
  for (const oldKey of Object.keys(meta)) {
    if (notifMap.has(oldKey)) continue; // already canonical
    const oldEntry = meta[oldKey];
    if (!oldEntry || typeof oldEntry !== "object") continue;
    const { tenantOwner: oldTenant, groupKey: oldGroup } = parseGroupMetaKey(oldKey);
    if (!oldGroup) continue;
    const folded = groupCardMetaKey(canonicalGroupKey(oldGroup), oldTenant);
    if (!folded || !notifMap.has(folded) || folded === oldKey) continue;
    const dest = (meta[folded] && typeof meta[folded] === "object") ? meta[folded] : {};
    if (!dest.display_name && oldEntry.display_name) dest.display_name = oldEntry.display_name;
    if (!dest.owner_name && oldEntry.owner_name) dest.owner_name = oldEntry.owner_name;
    if (!dest.phone && oldEntry.phone) dest.phone = oldEntry.phone;
    if (!dest.email && oldEntry.email) dest.email = oldEntry.email;
    meta[folded] = dest;
  }
  for (const [ck, ids] of notifMap.entries()) {
    const prev = meta[ck] && typeof meta[ck] === "object" ? meta[ck] : {};
    const isSharedCard = sharedCardKeys.has(ck);
    // Shared cards should follow owner-side server data to avoid stale local
    // names for grantees. Owner cards keep local override precedence.
    let dn = isSharedCard
      ? (displayMap.get(ck) || "")
      : ((prev.display_name && String(prev.display_name).trim()) || "");
    if (!dn) {
      dn = isSharedCard
        ? ((prev.display_name && String(prev.display_name).trim()) || "")
        : (displayMap.get(ck) || "");
    }
    if (!dn) {
      const gOnly = ck.includes(GROUP_CARD_TENANT_SEP)
        ? ck.slice(ck.indexOf(GROUP_CARD_TENANT_SEP) + 1)
        : ck;
      dn = gOnly;
    }
    meta[ck] = {
      display_name: dn,
      owner_name: prev.owner_name != null ? String(prev.owner_name) : "",
      phone: prev.phone != null ? String(prev.phone) : "",
      email: prev.email != null ? String(prev.email) : "",
      device_ids: ids,
    };
  }
  for (const g of Object.keys(meta)) {
    if (!notifMap.has(g)) delete meta[g];
  }
  return meta;
}

/** Re-fetch /devices and reconcile group-card localStorage (after profile/delete cache bust). */
let _groupMetaSyncTimer = null;
let _groupMetaSyncChain = Promise.resolve();
function syncGroupMetaFromServer() {
  if (!state.me) {
    return _groupMetaSyncChain;
  }
  _groupMetaSyncChain = _groupMetaSyncChain
    .then(async () => {
      if (!state.me) return;
      const r = await api(buildGetDevicesPath(), { timeoutMs: DEVICES_CONSOLE_TIMEOUT_MS, retries: 1 });
      let meta = {};
      try {
        const raw = localStorage.getItem(groupMetaStorageKey());
        meta = raw ? JSON.parse(raw) : {};
      } catch (_) { meta = {}; }
      if (!meta || typeof meta !== "object") meta = {};
      syncGroupMetaWithDevices(meta, (r && r.items) || []);
      localStorage.setItem(groupMetaStorageKey(), JSON.stringify(meta));
    })
    .catch(() => {});
  return _groupMetaSyncChain;
}
function scheduleSyncGroupMetaFromServer() {
  if (!state.me) return;
  if (_groupMetaSyncTimer) clearTimeout(_groupMetaSyncTimer);
  _groupMetaSyncTimer = setTimeout(() => {
    _groupMetaSyncTimer = null;
    void syncGroupMetaFromServer();
  }, 400);
}

/** Route redirect timer (signup / activate → login); cleared on navigation. */
let routeRedirectTimer = null;
function clearRouteRedirectTimer() {
  if (routeRedirectTimer) {
    clearTimeout(routeRedirectTimer);
    routeRedirectTimer = null;
  }
}
function scheduleRouteRedirect(ms, hash) {
  clearRouteRedirectTimer();
  routeRedirectTimer = setTimeout(() => {
    routeRedirectTimer = null;
    location.hash = hash;
  }, ms);
}

/** Cleared when leaving Events; used to resume SSE when tab visible again. */
window.__eventsStreamResume = null;

let healthPollTimer = null;
/** Overview device search debounce. */
let overviewFilterDebounce = null;
function clearHealthPollTimer() {
  if (healthPollTimer) {
    clearInterval(healthPollTimer);
    healthPollTimer = null;
  }
}
function tickHealthIfVisible() {
  if (document.visibilityState !== "visible") return;
  loadHealth();
}

/** When MQTT is down, poll faster so the green dot tracks reality (not a 30s stale snapshot). */
const HEALTH_POLL_SLOW_MS = 12000;
const HEALTH_POLL_FAST_MS = 3500;

function armHealthPoll() {
  clearHealthPollTimer();
  if (!state.me) return;
  const fast = state.mqttConnected === false;
  const ms = fast ? HEALTH_POLL_FAST_MS : HEALTH_POLL_SLOW_MS;
  healthPollTimer = setInterval(tickHealthIfVisible, ms);
}

/** Events page: align Live badge with EventSource state (no reconnect). */
function syncEventsLiveBadge() {
  const live = document.getElementById("evLive");
  if (!live || !window.__evSSE) return;
  const es = window.__evSSE;
  if (es.readyState === EventSource.OPEN) {
    live.textContent = "Live";
    live.className = "badge online";
    live.title = "Live stream connected";
  } else if (es.readyState === EventSource.CONNECTING) {
    live.textContent = "Reconnecting…";
    live.className = "badge offline";
    live.title = "Live stream reconnecting";
  }
}


// ── 10-api.shell.js ─────────────────────────────────────
/**
 * Authenticated API layer (api / apiOr / apiGetCached + caching), CSRF-aware
 * write retry, group apply/delete route negotiation (no simulated group delete),
 * dialog, and the auth lifecycle calls (login / loadMe / loadHealth).
 *
 * Concatenated as raw text by scripts/build-dashboard.mjs after
 * 00-state.shell.js so it can mutate state.me / state.health and call into
 * the timer helpers declared there. The pure HTTP helpers it depends on
 * (apiBase, fetchWithDeadline, _isWriteMethod, ...) come from
 * src/lib/api.js + src/lib/csrf.js via the bundle HEADER imports.
 */
// ------------------------------------------------------------------ api
/** Single-flight session teardown when parallel requests all return 401. */
let _authExpiredSweep = null;

async function api(path, opts) {
  opts = opts || {};
  const token = getToken();
  const cookieSession = _hasCookieSessionHint();
  // HttpOnly-cookie sessions must win over stale legacy localStorage tokens.
  // The API intentionally prefers Authorization over cookies, so attaching an
  // old Bearer here would turn a valid cookie session into a false 401.
  const bearerToken = token && !cookieSession ? token : "";
  const headers = Object.assign({}, opts.headers || {});
  if (bearerToken) headers.Authorization = "Bearer " + bearerToken;
  let body = opts.body;
  if (body && typeof body === "object" && !(body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(body);
  }
  const method = String(opts.method || "GET").toUpperCase();
  // Attach CSRF header on cookie-authenticated writes; Bearer requests are
  // exempt server-side but harmless to skip.
  if (_isWriteMethod(method) && !bearerToken && !headers[CSRF_HEADER_NAME]) {
    const ctok = getCsrfToken();
    if (ctok) headers[CSRF_HEADER_NAME] = ctok;
  }
  const retryable = opts.retryable != null ? !!opts.retryable : (method === "GET" || method === "HEAD");
  const retries = Number.isFinite(Number(opts.retries)) ? Math.max(0, Number(opts.retries)) : (retryable ? 2 : 0);
  let csrfRetry = 0;
  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const r = await fetchWithDeadline(
        apiBase() + path,
        { method, headers, body, signal: opts.signal },
        opts.timeoutMs,
      );
      if (r.status === 401) {
        if (!_authExpiredSweep) {
          _authExpiredSweep = (async () => {
            try {
              setToken("");
              setCsrfToken("");
              state.me = null;
              try {
                await fetchWithDeadline(
                  apiBase() + buildPostAuthLogoutPath(),
                  { method: "POST" },
                  AUTH_LOGOUT_TIMEOUT_MS,
                );
              } catch (_) {}
              if (location.hash !== "#/login") location.hash = "#/login";
            } finally {
              _authExpiredSweep = null;
            }
          })();
        }
        await _authExpiredSweep;
        throw new Error("401 Unauthorized or session expired");
      }
      if (!r.ok) {
        // CSRF token may have rotated / never bootstrapped — refresh once
        // and retry the same write before bubbling the error.
        if (_isWriteMethod(method) && csrfRetry === 0 && Number(r.status) === 403) {
          const t403 = await r.clone().text().catch(() => "");
          if (_isCsrfRejection(403, t403)) {
            csrfRetry = 1;
            const fresh = await refreshCsrfToken();
            if (fresh) {
              headers[CSRF_HEADER_NAME] = fresh;
              // Don't let this iteration consume a retry budget — writes
              // default to retries=0, so without attempt-- we'd exit the
              // loop immediately and the refreshed token would never be
              // used. csrfRetry=1 guards against infinite loops.
              attempt--;
              continue;
            }
          }
        }
        if (retryable && attempt < retries && _isRetryableHttpStatus(Number(r.status))) {
          await _sleep(250 * (2 ** attempt));
          continue;
        }
        const t = await r.text().catch(() => "");
        let msg;
        try {
          const j = JSON.parse(t);
          msg = formatHttpApiDetail(j.detail);
          if (!msg) msg = t;
        } catch {
          msg = t;
        }
        throw new Error(`${r.status} ${msg || r.statusText}`);
      }
      const ct = r.headers.get("content-type") || "";
      if (ct.includes("application/json")) return r.json();
      if (opts.raw) return r;
      return r.text();
    } catch (e) {
      lastErr = e;
      if (retryable && attempt < retries && _isTransientFetchError(e)) {
        await _sleep(250 * (2 ** attempt));
        continue;
      }
      throw e;
    }
  }
  throw lastErr || new Error("request failed");
}

bindFleetListApi(api);
bindContractApi(api);
bindAdminSharesApi(api);
bindGroupSharesApi(api);
bindGroupCardsApi(api);

/**
 * §12-B: parallel GETs via ``Promise.allSettled``. Each entry is
 * ``[path, optionalPerCallOpts]`` merged onto ``defaultOpts``.
 */
async function apiBundleAllSettled(entries, defaultOpts) {
  const d = defaultOpts || {};
  return Promise.allSettled(
    entries.map(([path, extra]) => api(path, Object.assign({}, d, extra || {}))),
  );
}

/**
 * §12-B: parallel GETs via ``Promise.all`` (fail-fast). Same entry shape as
 * ``apiBundleAllSettled``.
 */
async function apiBundleAll(entries, defaultOpts) {
  const d = defaultOpts || {};
  return Promise.all(
    entries.map(([path, extra]) => api(path, Object.assign({}, d, extra || {}))),
  );
}

/**
 * When ``GET /auth/me`` sets ``export_csv_require_scope_token``, mint a P5
 * token for CSV export only. The token is returned to callers for query
 * strings / fetch — never assign it to ``innerHTML`` or a visible ``href``.
 */
async function mintExportCsvScopeTokenIfRequired() {
  const me = state.me || {};
  if (!me.export_csv_require_scope_token) return "";
  const j = await api(buildPostActivityScopeTokenMintPath(), { method: "POST", body: {} });
  const tok = j && j.token != null ? String(j.token).trim() : "";
  return tok;
}

async function apiOr(path, fallback, opts) {
  try {
    return await api(path, opts);
  } catch (e) {
    return (typeof fallback === "function") ? fallback(e) : fallback;
  }
}
function isGroupRouteMissingError(err) {
  const msg = String((err && err.message) || err || "");
  if (msg.includes("405") || msg.includes("501")) return true;
  const m = msg.match(/^404\s*(.*)$/i);
  if (!m) return false;
  const rest = String(m[1] || "").trim().toLowerCase();
  // api() formats failures as "404 <JSON detail>". Those are application errors,
  // not "this HTTP route is missing". Treating them as route-missing used to
  // run a client-side per-device PATCH(notification_group:"") "delete", which
  // diverges shares / ACL from server truth — that path is no longer used.
  if (
    rest.includes("group not found") ||
    rest.includes("not found in your scope") ||
    rest.includes("not yours") ||
    rest.includes("cannot be deleted") ||
    rest.includes("shared group") ||
    rest.includes("unowned group") ||
    rest.includes("forbidden")
  ) {
    return false;
  }
  return true;
}
function groupTriggerPayloadFromSettings(gs) {
  const s = gs || {};
  const delay_seconds = Number(s.delay_seconds || 0);
  const trigger_duration_ms = Number(s.trigger_duration_ms || DEFAULT_REMOTE_SIREN_MS);
  return {
    trigger_mode: delay_seconds > 0 ? "delay" : "continuous",
    trigger_duration_ms,
    delay_seconds,
    reboot_self_check: !!s.reboot_self_check,
  };
}
async function runGroupApplyOnAction(ctx) {
  const { groupKey, ownerAdmin, payload, apiCaps, saveApiCaps, tryApplyV1, tryApplyRoute, applyFallback } = ctx;
  if (typeof tryApplyV1 === "function") {
    try {
      return await tryApplyV1(groupKey, ownerAdmin);
    } catch (e) {
      if (!isGroupRouteMissingError(e)) throw e;
    }
  }
  if (apiCaps && apiCaps.apply && typeof tryApplyRoute === "function") {
    try {
      return await tryApplyRoute(groupKey, ownerAdmin);
    } catch (e) {
      if (isGroupRouteMissingError(e)) {
        apiCaps.apply = false;
        if (typeof saveApiCaps === "function") saveApiCaps(apiCaps);
        return await applyFallback(groupKey, ownerAdmin, payload);
      }
      throw e;
    }
  }
  return await applyFallback(groupKey, ownerAdmin, payload);
}
async function runGroupDeleteAction(ctx) {
  const { groupKey, ownerAdmin, apiCaps, saveApiCaps, tryDeleteV1, tryDeletePostRoute, tryDeleteRoute } = ctx;
  // Never skip the real delete route when caps say delete:false. That flag only means
  // "last time we thought HTTP DELETE/POST was absent" — a poisoned cap + zero devices
  // would otherwise no-op and leave group_card_settings / NGS ghosts.
  if (typeof tryDeleteV1 === "function") {
    try {
      return await tryDeleteV1(groupKey, ownerAdmin);
    } catch (e) {
      if (!isGroupRouteMissingError(e)) throw e;
    }
  }
  try {
    return await tryDeletePostRoute(groupKey, ownerAdmin);
  } catch (e) {
    if (!isGroupRouteMissingError(e)) throw e;
    try {
      return await tryDeleteRoute(groupKey, ownerAdmin);
    } catch (e2) {
      if (isGroupRouteMissingError(e2)) {
        if (apiCaps) apiCaps.delete = false;
        if (typeof saveApiCaps === "function" && apiCaps) saveApiCaps(apiCaps);
        throw new Error(
          "Group delete requires the API delete routes (POST or DELETE on " +
            "/notification-groups/{key} or /group-cards/{key}, including /api/ mirrors). " +
            "Per-device PATCH with notification_group cleared is **leave group** only, " +
            "not a supported group-delete simulation — fix reverse-proxy routing or upgrade the API.",
        );
      }
      throw e2;
    }
  }
}

async function grantShareMatrix(deviceIds, usernames, perms, onProgress) {
  const dids = (Array.isArray(deviceIds) ? deviceIds : []).map((x) => String(x || "").trim()).filter(Boolean);
  const users = (Array.isArray(usernames) ? usernames : []).map((x) => String(x || "").trim()).filter(Boolean);
  const canView = !!(perms && perms.can_view);
  const canOperate = !!(perms && perms.can_operate);
  if (!dids.length) throw new Error("No boards selected");
  if (!users.length) throw new Error("No users selected");
  if (!canView && !canOperate) throw new Error("No sharing permission selected");
  const total = dids.length * users.length;
  let ok = 0;
  let fail = 0;
  let idx = 0;
  for (const did of dids) {
    for (const user of users) {
      idx += 1;
      let lastError = "";
      try {
        await api(buildAdminDeviceSharePath(did), {
          method: "POST",
          body: { grantee_username: user, can_view: canView, can_operate: canOperate },
        });
        ok += 1;
      } catch (e) {
        fail += 1;
        lastError = String((e && e.message) || e || "error");
      }
      if (typeof onProgress === "function") {
        onProgress({ idx, total, ok, fail, device_id: did, username: user, lastError });
      }
    }
  }
  return { total, ok, fail };
}

/**
 * Short-lived GET cache to coalesce identical in-flight requests only (ttlMs > 0 adds a brief stale window).
 * Overview / device list: prefer `api()` + server-side CACHE_TTL (see .env) so truth stays on the server.
 */
const _apiGetCache = new Map();
const _apiGetInflight = new Map();
const _API_GET_CACHE_MAX_KEYS = 48;
function _apiGetCacheSet(path, data) {
  const p = String(path || "");
  _apiGetCache.set(p, { t: Date.now(), data });
  while (_apiGetCache.size > _API_GET_CACHE_MAX_KEYS) {
    let oldestK = null;
    let oldestT = Infinity;
    for (const [k, v] of _apiGetCache.entries()) {
      if (v && v.t < oldestT) {
        oldestT = v.t;
        oldestK = k;
      }
    }
    if (oldestK != null) _apiGetCache.delete(oldestK);
    else break;
  }
}
async function apiGetCached(path, opts, ttlMs) {
  const ttl = ttlMs != null ? ttlMs : 4500;
  const ent = _apiGetCache.get(path);
  const now = Date.now();
  if (ent && (now - ent.t) < ttl) return ent.data;
  if (_apiGetInflight.has(path)) return _apiGetInflight.get(path);
  const p = (async () => {
    const data = await api(path, opts);
    _apiGetCacheSet(path, data);
    return data;
  })();
  _apiGetInflight.set(path, p);
  let data;
  try {
    data = await p;
  } finally {
    if (_apiGetInflight.get(path) === p) _apiGetInflight.delete(path);
  }
  return data;
}

/** Clear short-lived GET cache entries (server also invalidates on write). */
function bustApiGetCachedPrefix(prefix) {
  const p = String(prefix || "");
  for (const k of _apiGetCache.keys()) {
    if (!p || k.startsWith(p)) _apiGetCache.delete(k);
  }
}
function bustDeviceListCaches() {
  bustApiGetCachedPrefix("/devices");
  bustApiGetCachedPrefix("/dashboard/overview");
  scheduleSyncGroupMetaFromServer();
}

function normalizeFwLabel(s) {
  return String(s == null ? "" : s)
    .trim()
    .toLowerCase()
    .replace(/^v+/, "");
}
function firmwareHintStillValid(devFw, hint) {
  if (!hint || !hint.update_available) return false;
  const t = normalizeFwLabel(hint.to_version);
  const c = normalizeFwLabel(devFw);
  if (c && t && c === t) return false;
  return true;
}

/** Backend verb matrix: only superadmin or sa_staff + sa_ota_admin may publish OTA. */
function principalMayPublishOta(me) {
  if (!me) return false;
  if (me.role === "superadmin") return true;
  if (me.role !== "sa_staff") return false;
  const caps = new Set((me.sa_capabilities || []).map(String));
  return caps.has("sa_ota_admin") || caps.has("sa_full_access");
}

const FW_HINT_DLG_VER = "4";
async function openGlobalFwHintDialog(hint, ctx) {
  ctx = ctx || {};
  if (!hint || !hint.update_available) return;
  if (ctx.deviceId) {
    try {
      const row = await api(buildGetDevicePath(ctx.deviceId), { timeoutMs: DEVICES_CONSOLE_TIMEOUT_MS });
      if (row && row.fw != null) ctx = Object.assign({}, ctx, { currentFw: String(row.fw) });
      const h2 = row && row.firmware_hint;
      if (!h2 || !h2.update_available || !firmwareHintStillValid(row && row.fw, h2)) {
        toast("Firmware is up to date.", "ok");
        try { bustDeviceListCaches(); } catch (_) {}
        return;
      }
      hint = h2;
    } catch (_) {
      if (!firmwareHintStillValid(ctx.currentFw, hint)) {
        toast("Firmware is up to date.", "ok");
        return;
      }
    }
  } else if (!firmwareHintStillValid(ctx.currentFw, hint)) {
    toast("Firmware is up to date.", "ok");
    return;
  }

  let dlg = document.getElementById("crocFwHintDialog");
  if (!dlg || dlg.dataset.crocFwDlgVer !== FW_HINT_DLG_VER) {
    if (dlg) dlg.remove();
    dlg = document.createElement("dialog");
    dlg.id = "crocFwHintDialog";
    dlg.dataset.crocFwDlgVer = FW_HINT_DLG_VER;
    dlg.className = "croc-fw-hint-dlg";
    dlg.setAttribute("aria-label", "Firmware update");
    dlg.innerHTML = `
      <div class="croc-fw-hint-dlg__form">
        <h3 class="croc-fw-hint-dlg__title">Firmware update</h3>
        <div class="croc-fw-hint-dlg__compare" id="crocFwHintCompare" aria-live="polite"></div>
        <label class="field" id="crocFwHintPickWrap" style="margin-top:10px;display:none">
          <span>Target firmware (explicit selection)</span>
          <select id="crocFwHintPick"></select>
        </label>
        <p class="muted" id="crocFwHintPickNote" style="margin:6px 0 0;display:none"></p>
        <p class="croc-fw-hint-dlg__release-label" id="crocFwHintRelLabel" style="display:none">Package notes (from .bin / sidecar)</p>
        <pre class="croc-fw-hint-dlg__release" id="crocFwHintRelease" style="display:none"></pre>
        <p class="croc-fw-hint-dlg__preflight muted" id="crocFwHintPreflight" style="margin:10px 0 0;min-height:1.2em"></p>
        <div class="row" style="justify-content:flex-end;margin-top:14px;gap:8px;flex-wrap:wrap">
          <button type="button" class="btn secondary btn-tap" id="crocFwHintClose">Close</button>
          <button type="button" class="btn btn-tap" id="crocFwHintDoOta" style="display:none">Send OTA</button>
        </div>
      </div>`;
    document.body.appendChild(dlg);
  }

  const curFw = String(ctx.currentFw != null ? ctx.currentFw : "").trim() || "—";
  const newFw = String(hint.to_version || "—").trim() || "—";
  const toFile = String(hint.to_file || "").trim();
  const serverNotes = String(hint.release_notes || "").trim();
  const relEl = document.getElementById("crocFwHintRelease");
  const relLab = document.getElementById("crocFwHintRelLabel");
  if (relEl) {
    if (serverNotes) {
      relEl.textContent = serverNotes;
      relEl.style.display = "block";
    } else {
      relEl.textContent = "";
      relEl.style.display = "none";
    }
  }
  if (relLab) relLab.style.display = serverNotes ? "block" : "none";

  const cmp = document.getElementById("crocFwHintCompare");
  if (cmp) {
    cmp.innerHTML =
      `<span class="croc-fw-hint-dlg__ver mono" title="Current">${escapeHtml(curFw)}</span>` +
      `<span class="croc-fw-hint-dlg__ver-arrow" aria-hidden="true">→</span>` +
      `<span class="croc-fw-hint-dlg__ver mono croc-fw-hint-dlg__ver--new" title="New">${escapeHtml(newFw)}</span>`;
  }

  const pre = document.getElementById("crocFwHintPreflight");
  const pickWrap = document.getElementById("crocFwHintPickWrap");
  const pickSel = document.getElementById("crocFwHintPick");
  const pickNote = document.getElementById("crocFwHintPickNote");
  let picked = {
    file: toFile,
    fw: newFw,
    url: String(hint.download_url || "").trim(),
  };
  if (pickWrap && pickSel && pickNote) {
    pickWrap.style.display = "none";
    pickNote.style.display = "none";
    pickNote.textContent = "";
    pickSel.innerHTML = "";
    try {
      const inv = await api(buildOtaFirmwaresPath(), { timeoutMs: OTA_CONSOLE_TIMEOUT_MS });
      const items = Array.isArray(inv && inv.items) ? inv.items : [];
      if (items.length > 0) {
        const opts = items.map((it) => {
          const name = String(it && it.name || "").trim();
          const fv = String(it && it.fw_version || "").trim();
          const dl = String(it && it.download_url || "").trim();
          if (!name || !dl) return "";
          const selected = (name === toFile) ? " selected" : "";
          const label = fv ? `${name} · v${fv}` : name;
          return `<option value="${escapeHtml(name)}" data-fw="${escapeHtml(fv)}" data-url="${escapeHtml(dl)}"${selected}>${escapeHtml(label)}</option>`;
        }).filter(Boolean).join("");
        if (opts) {
          pickSel.innerHTML = opts;
          pickWrap.style.display = "block";
          pickNote.style.display = "block";
          pickNote.textContent = "Send OTA will use exactly the selected file/version.";
          const applyPick = () => {
            const op = pickSel.selectedOptions && pickSel.selectedOptions[0];
            if (!op) return;
            picked.file = String(op.value || "").trim();
            picked.fw = String(op.dataset.fw || "").trim() || newFw;
            picked.url = String(op.dataset.url || "").trim();
          };
          pickSel.onchange = applyPick;
          applyPick();
        }
      }
    } catch (_) {
      // Non-superadmin or old route: keep legacy hint path, but surface this clearly.
      pickNote.style.display = "block";
      pickNote.textContent = "Firmware list unavailable; using the suggested file.";
    }
  }
  if (pre) {
    if (!ctx.deviceId || !can("can_send_command")) {
      pre.textContent = "Open a board with command permission to send OTA in one step.";
    } else if (ctx.canOperateThisDevice === false) {
      pre.textContent = "No operate permission on this board — OTA disabled.";
    } else if (!principalMayPublishOta(state.me)) {
      pre.textContent = "Firmware notes are read-only for your seat — OTA publish requires SA OTA admin.";
    } else {
      pre.textContent = "Send OTA verifies your session, the firmware file, and operate access.";
    }
  }

  const closeBtn = document.getElementById("crocFwHintClose");
  if (closeBtn) {
    closeBtn.onclick = () => { try { dlg.close(); } catch (_) {} };
  }

  const did = String(ctx.deviceId || "").trim();
  const knownNoOperate = ctx.canOperateThisDevice === false;
  const otaBtn = document.getElementById("crocFwHintDoOta");
  if (otaBtn) {
    const show = !!(did && can("can_send_command") && !knownNoOperate && principalMayPublishOta(state.me));
    otaBtn.style.display = show ? "inline-flex" : "none";
    otaBtn.disabled = false;
    otaBtn.onclick = async () => {
      const url = String(picked.url || "").trim();
      const fw = String(picked.fw || "").trim();
      const file = String(picked.file || toFile || "").trim();
      if (!did || !url || !fw) {
        toast("Missing device or download information.", "err");
        return;
      }
      let fresh = null;
      try {
        fresh = await api(buildGetDevicePath(did), { timeoutMs: DEVICES_CONSOLE_TIMEOUT_MS });
      } catch (_) { /* use hint */ }
      if (fresh) {
        const h3 = fresh.firmware_hint;
        if (!firmwareHintStillValid(fresh.fw, h3) || !h3 || !h3.update_available) {
          toast("Firmware is already up to date.", "ok");
          try { bustDeviceListCaches(); } catch (_) {}
          try { dlg.close(); } catch (_) {}
          return;
        }
      }
      if (!confirm(`Send OTA to this board?\n\n${did}\n\n${curFw} → ${fw}\nFile: ${file || "(unknown)"}`)) return;
      otaBtn.disabled = true;
      if (pre) pre.textContent = "Checking…";
      try {
        if (!state.me) {
          throw new Error("Not signed in or session expired");
        }
        let canOp = true;
        if (ctx.canOperateThisDevice !== true) {
          const row = await api(buildGetDevicePath(did), { timeoutMs: DEVICES_CONSOLE_TIMEOUT_MS });
          canOp = !!(row && row.can_operate);
        } else {
          canOp = true;
        }
        if (!canOp) {
          throw new Error("No operate permission on this board");
        }
        // Safety guard: if an explicit OTA campaign exists and points to a different
        // target version than this auto hint, block one-click send to avoid
        // accidentally pushing an unintended package (e.g. highest semver vs staged rollout).
        try {
          const campRes = await api(buildGetOtaCampaignsPath(), { timeoutMs: OTA_CONSOLE_TIMEOUT_MS });
          const camps = Array.isArray(campRes && campRes.items) ? campRes.items : [];
          const latest = camps.find((c) => c && c.fw_version && c.url);
          if (latest) {
            const campFw = String(latest.fw_version || "").trim();
            const hintFw = String(fw || "").trim();
            if (normalizeFwLabel(campFw) && normalizeFwLabel(hintFw) &&
                normalizeFwLabel(campFw) !== normalizeFwLabel(hintFw)) {
              throw new Error(
                `Blocked: latest OTA campaign is v${campFw}, but auto hint is v${hintFw}. ` +
                `Use OTA Campaign rollout to avoid sending the wrong firmware.`
              );
            }
          }
        } catch (guardErr) {
          const m = String(guardErr && guardErr.message || guardErr || "");
          if (m.startsWith("Blocked:")) throw guardErr;
          // Non-fatal: if campaign listing is unavailable, keep legacy path.
        }

        const probe = await api(buildOtaFirmwareReachabilityPath(file || toFile), { timeoutMs: OTA_CONSOLE_TIMEOUT_MS });
        if (!probe || !probe.ok) {
          const det = probe && probe.detail ? String(probe.detail) : "probe failed";
          throw new Error(`Firmware URL probe failed: ${det}`);
        }
        if (pre) pre.textContent = "Sending OTA command…";
        await api(buildPostDeviceCommandsPath(did), {
          method: "POST",
          body: { cmd: "ota", params: { url, fw } },
        });
        toast("OTA command sent", "ok");
        try { bustDeviceListCaches(); } catch (_) {}
        dlg.close();
      } catch (e) {
        const msg = e && e.message ? String(e.message) : String(e);
        if (pre) pre.textContent = msg;
        toast(msg, "err");
      } finally {
        otaBtn.disabled = false;
      }
    };
  }

  if (typeof dlg.showModal === "function") dlg.showModal();
}

/** All-devices list + device detail: sync Firmware row version + OTA-hint control from API model. */
function syncDevicePageFirmwareHint(view, dev, deviceIdForOta) {
  const hasUpd = !!(dev && dev.firmware_hint && dev.firmware_hint.update_available && firmwareHintStillValid(dev && dev.fw, dev.firmware_hint));
  const stEl = $("#devFwStatus", view);
  if (stEl) {
    stEl.textContent = hasUpd ? "Update available · 有更新" : "Up to date · 已是最新";
    stEl.className = hasUpd ? "device-fw-state device-fw-state--update" : "device-fw-state device-fw-state--ok";
  }
  const hBtn = $("#devFwHintBtn", view);
  if (hBtn) {
    if (hasUpd) {
      hBtn.style.display = "inline-flex";
      hBtn.setAttribute("aria-pressed", "true");
      const h = dev.firmware_hint;
      const did = String(deviceIdForOta || (dev && dev.device_id) || "");
      const operate = !!(dev && dev.can_operate);
      hBtn.onclick = () => openGlobalFwHintDialog(h, {
        currentFw: String(dev && dev.fw != null ? dev.fw : ""),
        deviceId: did,
        canOperateThisDevice: operate,
      });
    } else {
      hBtn.style.display = "none";
      hBtn.removeAttribute("aria-pressed");
      hBtn.onclick = null;
    }
  }
  const vEl = $("#devFwVer", view);
  if (vEl) vEl.textContent = String(dev && dev.fw != null && dev.fw !== "" ? dev.fw : "—");
}

async function login(username, password) {
  const r = await fetchWithDeadline(
    apiBase() + buildPostAuthLoginPath(),
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    },
    AUTH_LOGIN_TIMEOUT_MS,
  );
  const text = await r.text();
  if (!r.ok) {
    if (r.status === 429) {
      const ra = r.headers.get("Retry-After");
      let detail = "Too many sign-in attempts. Please wait and try again.";
      try {
        const j = JSON.parse(text);
        if (j && j.detail) detail = String(j.detail);
      } catch {
        if (text) detail = text;
      }
      if (ra && /^\d+$/.test(ra)) detail = `${detail} (retry after ${ra}s)`;
      throw new Error(detail);
    }
    if (r.status === 503) {
      let detail = "Server busy (database locked). Wait a few seconds and try again.";
      try {
        const j = JSON.parse(text);
        if (j && j.detail) detail = String(j.detail);
      } catch {
        if (text) detail = text;
      }
      throw new Error(detail);
    }
    throw new Error(`${r.status} ${text}`);
  }
  const j = JSON.parse(text);
  if (j.requires_email_second) {
    return j;
  }
  if (j.access_token) setToken(j.access_token);
  else setToken("");
  if (j.csrf_token) setCsrfToken(String(j.csrf_token));
  else setCsrfToken(_readCsrfCookie());
  localStorage.setItem(LS.user, username);
  localStorage.setItem(LS.role, j.role || "");
  localStorage.setItem(LS.zones, JSON.stringify(j.zones || []));
  return j;
}

async function loginEmailSecond(username, emailCode) {
  const r = await fetchWithDeadline(
    apiBase() + buildPostAuthLoginEmailSecondPath(),
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username: String(username || "").trim(), email_code: String(emailCode || "").trim() }),
    },
    DEFAULT_API_TIMEOUT_MS,
  );
  const text = await r.text();
  if (!r.ok) {
    if (r.status === 429) {
      const ra = r.headers.get("Retry-After");
      let detail = "Too many attempts. Please wait and try again.";
      try {
        const j = JSON.parse(text);
        if (j && j.detail) detail = String(j.detail);
      } catch {
        if (text) detail = text;
      }
      if (ra && /^\d+$/.test(ra)) detail = `${detail} (retry after ${ra}s)`;
      throw new Error(detail);
    }
    if (r.status === 503) {
      let detail = "Server busy (database locked). Wait a few seconds and try again.";
      try {
        const j = JSON.parse(text);
        if (j && j.detail) detail = String(j.detail);
      } catch {
        if (text) detail = text;
      }
      throw new Error(detail);
    }
    throw new Error(`${r.status} ${text}`);
  }
  const j = JSON.parse(text);
  if (j.access_token) setToken(j.access_token);
  else setToken("");
  if (j.csrf_token) setCsrfToken(String(j.csrf_token));
  else setCsrfToken(_readCsrfCookie());
  localStorage.setItem(LS.user, String(username || "").trim());
  localStorage.setItem(LS.role, j.role || "");
  localStorage.setItem(LS.zones, JSON.stringify(j.zones || []));
  return j;
}

async function resendLoginSecondEmail(username) {
  const r = await fetchWithDeadline(
    apiBase() + buildPostAuthCodeResendPath(),
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        username: String(username || "").trim(),
        channel: "email",
        purpose: "login_second",
      }),
    },
    DEFAULT_API_TIMEOUT_MS,
  );
  const text = await r.text();
  if (!r.ok) throw new Error(`${r.status} ${text}`);
  return JSON.parse(text);
}

function _hasCookieSessionHint() {
  return !!(getCsrfToken() || _readCsrfCookie());
}

async function loadMe() {
  // HttpOnly JWT: bearer empty; CSRF (memory or cookie) means we should call /auth/me.
  if (!getToken() && !_hasCookieSessionHint()) {
    state.me = null;
    setCsrfToken("");
    renderAuthState();
    return;
  }
  try {
    // Session discovery is a probe, not a logout command. Calling api() here
    // would let one boot-time 401 invoke the global teardown and POST /logout,
    // deleting an otherwise valid cookie before the operator can recover.
    const token = getToken();
    const cookieSession = _hasCookieSessionHint();
    const headers = {};
    if (token && !cookieSession) headers.Authorization = "Bearer " + token;
    const r = await fetchWithDeadline(
      apiBase() + buildGetAuthMePath(),
      { method: "GET", headers },
      AUTH_ME_TIMEOUT_MS,
    );
    if (!r.ok) {
      if (r.status === 401) setToken("");
      throw new Error(String(r.status));
    }
    state.me = await r.json();
  } catch (_) {
    state.me = null;
  }
  // Reuse the (still-valid) cookie-issued CSRF token across reloads, and
  // proactively refresh before any write happens — avoids first-write 403.
  if (state.me) {
    const ck = _readCsrfCookie();
    if (ck) setCsrfToken(ck);
    if (!getCsrfToken()) {
      try { await refreshCsrfToken(); } catch (_) {}
    }
  } else {
    setCsrfToken("");
  }
  renderAuthState();
}

async function loadHealth() {
  try {
    // Public endpoint — do not use api() (no Authorization) so bad/expired JWT
    // never affects probes and we never trip the global 401 handler here.
    const r = await fetchWithDeadline(apiBase() + buildGetHealthPath(), { method: "GET" }, AUTH_HEALTH_TIMEOUT_MS);
    if (!r.ok) throw new Error(String(r.status));
    const h = await r.json();
    const mc = h.mqtt_connected;
    state.mqttConnected = mc === true ? true : mc === false ? false : null;
    state.health = h;
  } catch {
    state.mqttConnected = false;
    state.health = null;
  }
  renderMqttDot();
  renderHealthPills();
  armHealthPoll();
}


// ── 20-layout.shell.js ─────────────────────────────────────
/**
 * App chrome: who-am-I card, sidebar nav, health pills, MQTT dot, theme
 * toggle, mobile drawer + desktop rail collapse logic.
 *
 * Concatenated as raw text by scripts/build-dashboard.mjs after
 * 10-api.shell.js. Layout reads state directly (state.me, state.health,
 * state.mqttConnected) and writes to the live DOM via setHtmlIfChanged /
 * setChildMarkup from src/lib/dom.js (HEADER import).
 */
// ------------------------------------------------------------------ layout
/** True when hostnames match ignoring leading ``www.`` (apex vs www). */
function avatarHostnameSameSite(a, b) {
  const na = String(a || "")
    .replace(/^www\./i, "")
    .toLowerCase();
  const nb = String(b || "")
    .replace(/^www\./i, "")
    .toLowerCase();
  return na.length > 0 && na === nb;
}

/**
 * Reverse-proxy only forwards ``/api/*`` to uvicorn. Legacy DB values used
 * ``https://host/avatars/{file}`` (no ``/api``) → browser got 404 and sidebar
 * fell back to initials. Rewrite to ``…/api/avatars/{file}`` for our domain.
 */
function canonicalizeHostedAvatarUrl(absUrl) {
  let s = String(absUrl || "").trim();
  if (!s) return "";
  try {
    const u = new URL(s, window.location.href);
    let path = String(u.pathname || "").replace(/\\/g, "/");
    if (!path.startsWith("/avatars/") || path.indexOf("/api/avatars/") >= 0) {
      return s;
    }
    if (!avatarHostnameSameSite(u.hostname, window.location.hostname)) {
      return s;
    }
    path = "/api" + path;
    if (u.host === window.location.host) {
      return path + (u.search || "") + (u.hash || "");
    }
    return `${u.protocol}//${u.host}${path}${u.search || ""}${u.hash || ""}`;
  } catch (_) {
    return s;
  }
}

/** Same-origin avatar URLs as path-only + optional cache-bust (fixes www/host quirks). */
function avatarUrlForDisplay(absUrl, rev) {
  let s = canonicalizeHostedAvatarUrl(String(absUrl || "").trim());
  if (!s) return "";
  let out = s;
  try {
    const u = new URL(s, window.location.href);
    if (u.host === window.location.host) {
      out = u.pathname + (u.search || "") + (u.hash || "");
    } else {
      out = u.href;
    }
  } catch (_) {}
  const r = Number(rev) > 0 ? (out.includes("?") ? "&" : "?") + "v=" + rev : "";
  return out + r;
}

/** Authenticated avatar bytes (opaque path; does not expose stored filename). */
function meAvatarImgSrc(rev) {
  return apiBase() + buildGetAuthMeAvatarPath(rev);
}

/** Warm MapLibre vendor on Command Center (Home map). */
function maybePreloadOpsMapVendor() {
  if (!state.me || typeof canUseOpsMap !== "function" || !canUseOpsMap(state.me)) return;
  const route = String(location.hash || "").split("?")[0];
  if (route !== "#/overview") return;
  if (document.querySelector('link[data-ops-map-vendor-preload="1"]')) return;
  let href = "";
  try {
    href = resolveOpsMapVendorScriptUrl();
  } catch (_) {
    return;
  }
  if (!href) return;
  const link = document.createElement("link");
  link.rel = "preload";
  link.as = "script";
  link.href = href;
  link.setAttribute("data-ops-map-vendor-preload", "1");
  document.head.appendChild(link);
}

function renderAuthState() {
  if (!state.me) clearHealthPollTimer();
  document.body.dataset.auth = state.me ? "ok" : "none";
  const who = $("#who");
  if (state.me) {
    const u = String(state.me.username || "").trim() || "—";
    const role = String(state.me.role || "").trim() || "—";
    const zt = (state.me.zones || []).map((z) => String(z)).filter(Boolean).join(", ") || "—";
    const regEm = String(state.me.email || "").trim();
    const emailLine = regEm
      ? `<div class="who-card__line who-card__line--email muted" title="${escapeHtml(regEm)}"><span class="who-card__email mono">${escapeHtml(regEm)}</span></div>`
      : `<div class="who-card__line who-card__line--email muted" title="No email on file"><span class="who-card__email-none">No email</span></div>`;
    const initial = (u[0] || "?").toUpperCase();
    const hasAv = !!(state.me && state.me.has_avatar);
    const rev = Number(state.me && state.me.avatar_rev) || 0;
    const srcDisp = hasAv ? meAvatarImgSrc(rev) : "";
    const avatarEl = hasAv
      ? `<div class="who-card__avatar who-card__avatar--photo" aria-hidden="true"><img src="${escapeHtml(srcDisp)}" alt="" width="40" height="40" loading="eager" decoding="async" referrerpolicy="no-referrer" /></div>`
      : `<div class="who-card__avatar" aria-hidden="true">${escapeHtml(initial)}</div>`;
    setChildMarkup(
      who,
      `<div class="who-card" title="${escapeHtml(u)}">` +
        avatarEl +
        `<div class="who-card__body">` +
          `<div class="who-card__name">${escapeHtml(u)}</div>` +
          emailLine +
          `<div class="who-card__meta">` +
          `<span class="who-card__role">${escapeHtml(role)}</span>` +
          `<span class="who-card__meta-sep" aria-hidden="true"></span>` +
          `<span class="who-card__zones" title="${escapeHtml(zt)}">${escapeHtml(zt)}</span>` +
          `</div>` +
        `</div>` +
      `</div>`,
    );
    const im = who && who.querySelector(".who-card__avatar--photo img");
    if (im) {
      im.addEventListener(
        "error",
        function onAvErr() {
          im.removeEventListener("error", onAvErr);
          const w = im.closest(".who-card__avatar");
          if (w) {
            w.classList.remove("who-card__avatar--photo");
            w.textContent = initial;
          }
        },
        { once: true },
      );
    }
    if (who) {
      who.className = "who who--authed";
      who.setAttribute("role", "group");
      who.setAttribute("aria-label", "Account");
    }
    try { maybePreloadOpsMapVendor(); } catch (_) {}
    try { activateOverviewCommandCenterLayout(); } catch (_) {}
  } else {
    try { deactivateOverviewCommandCenterLayout(); } catch (_) {}
    const rmPre = document.querySelector('link[data-ops-map-vendor-preload="1"]');
    if (rmPre) rmPre.remove();
    if (who) {
      who.className = "who who--guest";
      who.textContent = "Signed out";
      who.removeAttribute("role");
      who.removeAttribute("aria-label");
      who.removeAttribute("title");
    }
  }
  renderNav();
  renderHealthPills();
  try { applySidebarRail(); } catch (_) {}
}

function navItemVisible(n) {
  if (!state.me) return false;
  if (n.mapNavEligible) return !!canUseOpsMap(state.me);
  if (Array.isArray(n.navCatalogAny) && n.navCatalogAny.length) {
    if (!catalogAnyEnabled(state.me, n.navCatalogAny)) return false;
  } else if (n.navCatalogId && !catalogEnabled(state.me, n.navCatalogId)) {
    return false;
  }
  if (n.navGate && !state.me[n.navGate]) return false;
  if (n.navRequiresCap && typeof can === "function" && !can(n.navRequiresCap)) return false;
  if (n.hideChildAdmin && String(state.me.manager_admin || "").trim()) return false;
  const r = String(state.me.role || "").trim();
  if (r === "sa_staff") {
    if (n.hideSaStaff) return false;
    const caps = new Set((state.me.sa_capabilities || []).map(String));
    const capOk = (need) => caps.has("sa_full_access") || caps.has(need);
    if (n.navCap) {
      if (!capOk(n.navCap)) return false;
      return true;
    }
    if (String(n.min || "") === "superadmin" && n.saStaffNav) {
      return Boolean(state.me.ops_dashboard_eligible);
    }
    return roleWeight(r) >= roleWeight(n.min || "user");
  }
  return hasRole(n.min);
}

function navPaintLabel(n) {
  if (n.id === "devices" || n.label === "All devices") return "All boards";
  if (n.id === "readiness" || n.label === "Readiness") return "Before tonight";
  if (n.id === "activate" || n.label === "Activate device") return "Activate board";
  return n.label;
}

function renderNav() {
  const nav = $("#nav");
  if (!nav) return;
  if (!state.me) { setHtmlIfChanged(nav, ""); return; }
  const prevCore = nav.querySelector(".nav-core");
  const savedScroll = prevCore ? prevCore.scrollTop : 0;
  const hash = location.hash || defaultHomeHash();
  const hashNoQuery = hash.split("?")[0];
  const coreParts = [];
  for (const g of NAV_GROUPS) {
    const items = g.items.filter((n) => navItemVisible(n));
    if (items.length === 0) continue;
    coreParts.push(`<div class="nav-section">${escapeHtml(g.title)}</div>`);
    for (const n of items) {
      const homeHubActive = hashNoQuery === "#/overview";
      const active = (n.path === "#/group/_all" || n.path === "#/devices"
        // Highlight "All devices" on list (#/group/_all), legacy #/devices redirect, and device detail.
        ? (hashNoQuery === "#/group/_all"
          || hashNoQuery === "#/devices"
          || hashNoQuery.startsWith("#/devices/"))
        : n.path === "#/site"
          ? hashNoQuery === "#/site"
          : n.id === "overview"
            ? homeHubActive
            : n.id === "account"
              ? (hashNoQuery === "#/account" || hashNoQuery.startsWith("#/account/"))
              : n.id === "settings"
                ? (hashNoQuery === "#/settings"
                  || hashNoQuery.startsWith("#/admin")
                  || hashNoQuery === "#/billing"
                  || hashNoQuery.startsWith("#/telegram"))
                : hash.startsWith(n.path))
        ? ` aria-current="page"`
        : "";
      const paint = navPaintLabel(n);
      coreParts.push(
        `<a href="${n.path}"${active} title="${escapeHtml(paint)}"><span class="nav-ico" aria-hidden="true">${n.ico}</span><span class="nav-label">${escapeHtml(paint)}</span></a>`,
      );
    }
  }
  const html = `<div class="nav-core">${coreParts.join("")}</div>`;
  const changed = setHtmlIfChanged(nav, html);
  if (changed && savedScroll > 0) {
    const nextCore = nav.querySelector(".nav-core");
    if (nextCore) {
      nextCore.scrollTop = savedScroll;
      requestAnimationFrame(() => { nextCore.scrollTop = savedScroll; });
    }
  }
}

function renderHealthPills() {
  const el = $("#healthPills");
  if (!el) return;
  if (!state.me || !state.health) {
    setHtmlIfChanged(el, "");
    return;
  }
  const sm = state.health.smtp || {};
  const tg = state.health.telegram || {};
  const mailOk = !!sm.configured && !!sm.worker_running;
  const tgOn = !!tg.enabled;
  const tgOk = tgOn && !!tg.worker_running;
  const tgErr = String(tg.last_error || "").trim();
  const mc = state.health.mqtt_connected;
  const mqConn = mc === true;
  const mqUnknown = mc !== true && mc !== false;
  const mqQ = Number(state.health.mqtt_ingest_queue_depth || 0);
  const mqDrop = Number(state.health.mqtt_ingest_dropped || 0);
  const _mqLastUp = String(state.health.mqtt_last_connect_at || "");
  const _mqLastDown = String(state.health.mqtt_last_disconnect_at || "");
  const _mqLastReason = String(state.health.mqtt_last_disconnect_reason || "");
  const mailTitle = sm.configured
    ? (mailOk ? "Mail worker running — verification email can be sent" : "Mail channel configured but delivery is not running — ask platform staff")
    : "Mail channel not configured on server";
  const tgTitle = tgOn
    ? (tgOk
      ? (tgErr ? `Telegram running — last delivery issue: ${tgErr}` : "Telegram running — qualifying events are queued")
      : "Telegram is on but delivery is not running — ask platform staff")
    : "Telegram is off for this environment — ask platform staff to enable it.";
  const mqttTitle = mqUnknown
    ? "Device link status unknown — live snapshot not available on this server yet."
    : mqConn
      ? (mqDrop > 0
        ? `Device link up — ${mqDrop} message(s) dropped; queue ${mqQ}.`
        : `Device link up — queue ${mqQ}.`)
      : "Device link down — check network path to the field.";
  const mqttPillClass = mqUnknown ? "warn" : mqConn ? (mqDrop > 0 ? "warn" : "ok") : "off";
  setHtmlIfChanged(el, `
    <span class="health-pill ${mqttPillClass}" title="${escapeHtml(mqttTitle)}">LINK</span>
    <span class="health-pill ${mailOk ? "ok" : sm.configured ? "warn" : "off"}" title="${escapeHtml(mailTitle)}">MAIL</span>
    <span class="health-pill ${tgOk ? "ok" : tgOn ? "warn" : "off"}" title="${escapeHtml(tgTitle)}">Chat</span>`);
}

function renderMqttDot() {
  const dot = $("#mqttDot");
  if (!dot) return;
  const mc = state.mqttConnected;
  dot.className = "dot-status " + (mc === true ? "ok" : mc === false ? "bad" : "warn");
  dot.title = mc === true ? "Device link up" : mc === false ? "Device link down" : "Device link status unknown";
}

function setCrumb(text) { const el = $("#crumb"); if (el) el.textContent = text; }

function setTheme(t) {
  document.documentElement.dataset.theme = t;
  localStorage.setItem(LS.theme, t);
}

function initTheme() {
  const saved = localStorage.getItem(LS.theme);
  const prefersDark = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
  setTheme(saved || (prefersDark ? "dark" : "light"));
}

function toggleNav(open) {
  if (open == null) open = document.body.dataset.nav !== "open";
  document.body.dataset.nav = open ? "open" : "";
  // Keep backdrop visibility in sync with nav state across desktop/mobile.
  const bd = document.getElementById("sidebarBackdrop");
  if (!bd) return;
  const mobile = !!(window.matchMedia && window.matchMedia("(max-width: 900px)").matches);
  const show = !!open && mobile;
  if (show) {
    bd.removeAttribute("hidden");
    bd.style.display = "";
  } else {
    bd.setAttribute("hidden", "");
    bd.style.display = "none";
  }
}

function applySidebarRail() {
  const m = window.matchMedia && window.matchMedia("(min-width: 901px)");
  if (!m || !m.matches) {
    try { document.body.removeAttribute("data-sidebar"); } catch (_) {}
  } else {
    try {
      const c = localStorage.getItem(LS.sidebarCollapsed) === "1";
      if (c) document.body.setAttribute("data-sidebar", "collapsed");
      else document.body.removeAttribute("data-sidebar");
    } catch (_) {
      try { document.body.removeAttribute("data-sidebar"); } catch (_) {}
    }
  }
  const btn = document.getElementById("sidebarRailToggle");
  if (btn) {
    const isCol = document.body.getAttribute("data-sidebar") === "collapsed";
    btn.setAttribute("aria-label", isCol ? "Expand sidebar" : "Collapse sidebar");
    btn.setAttribute("aria-expanded", isCol ? "false" : "true");
    btn.setAttribute("title", isCol ? "Expand sidebar" : "Collapse sidebar");
    const svgL = "<svg class=\"sidebar-rail-toggle__icon\" viewBox=\"0 0 24 24\" width=\"18\" height=\"18\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\" aria-hidden=\"true\"><path d=\"M15 6l-6 6 6 6\"/></svg>";
    const svgR = "<svg class=\"sidebar-rail-toggle__icon\" viewBox=\"0 0 24 24\" width=\"18\" height=\"18\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\" aria-hidden=\"true\"><path d=\"M9 6l6 6-6 6\"/></svg>";
    btn.innerHTML = isCol ? svgR : svgL;
  }
}

function toggleSidebarRail() {
  if (!window.matchMedia("(min-width: 901px)").matches) return;
  const cur = localStorage.getItem(LS.sidebarCollapsed) === "1";
  try { localStorage.setItem(LS.sidebarCollapsed, cur ? "0" : "1"); } catch (_) {}
  applySidebarRail();
}

/** Collapse drawer when viewport is desktop width (e.g. rotate phone / resize). */
function syncNavForViewport() {
  try {
    if (window.matchMedia && window.matchMedia("(min-width: 901px)").matches) toggleNav(false);
  } catch (_) {}
  try { applySidebarRail(); } catch (_) {}
}


// ── 30-router.shell.js ─────────────────────────────────────
/**
 * Hash router + per-route ticker registry. Owns the `routes` registry that
 * src/routes/*.route.js populates via registerRoute(id, handler) at load
 * time, plus the renderRoute() pipeline (auth gating, view-loading skeleton,
 * timeout race, post-render nav refresh). Listens on hashchange.
 *
 * Concatenated AFTER state/api/layout but BEFORE the route files, so the
 * registry is empty at concat-time but every routes/*.route.js sees
 * registerRoute() in scope when its top-level call runs.
 */
// ------------------------------------------------------------------ router
const routes = {};

function registerRoute(id, handler) { routes[id] = handler; }
async function invokeRoute(id, view, args, routeSeq) {
  const h = routes[id];
  if (!h) return;
  return h(view, args, routeSeq);
}
function isRouteCurrent(seq) { return seq === state.routeSeq; }
function resolveHashRoute(hashFull) {
  // Bound at bundle time from lib/resolve-hash-route.js + ROUTES / ROUTE_ALIASES.
  return resolveHashRouteFromLib(hashFull, { routes: ROUTES, aliases: ROUTE_ALIASES });
}
function clearRouteTickers() {
  const ticks = window.__routeTickers;
  if (!ticks) return;
  for (const t of ticks.values()) {
    try { clearTimeout(t); } catch (_) {}
  }
  ticks.clear();
}
function scheduleRouteTicker(routeSeq, key, fn, intervalMs) {
  window.__routeTickers = window.__routeTickers || new Map();
  const ticks = window.__routeTickers;
  const k = String(key || "");
  let running = false;
  const run = async () => {
    if (!isRouteCurrent(routeSeq)) return;
    if (document.visibilityState !== "visible") {
      const tid = setTimeout(run, intervalMs);
      ticks.set(k, tid);
      return;
    }
    if (running) {
      const tid = setTimeout(run, intervalMs);
      ticks.set(k, tid);
      return;
    }
    running = true;
    try { await fn(); } catch (_) {}
    running = false;
    if (!isRouteCurrent(routeSeq)) return;
    const tid = setTimeout(run, intervalMs);
    ticks.set(k, tid);
  };
  const old = ticks.get(k);
  if (old) { try { clearTimeout(old); } catch (_) {} }
  const first = setTimeout(run, intervalMs);
  ticks.set(k, first);
}

async function renderRoute() {
  const view = $("#view");
  if (!view) return;
  // Tear down heavy map work before painting the next route (MapLibre + fleet poll).
  try {
    runGlobalOpsMapTeardownIfAny();
    runGlobalGroupDetailMapTeardownIfAny();
    runGlobalOverviewMapTeardownIfAny();
    removeGroupDetailStyles();
    removeAccountSettingsStyles();
  } catch (_) {}
  if (typeof window.__overviewLandingPickDispose === "function") {
    try {
      window.__overviewLandingPickDispose();
    } catch (_) {}
    window.__overviewLandingPickDispose = null;
  }
  if (typeof window.__overviewCcDispose === "function") {
    try {
      window.__overviewCcDispose();
    } catch (_) {}
    window.__overviewCcDispose = null;
  }
  // Orphan portaled nav menus on body must not leave a click shield after route change.
  try {
    document.querySelectorAll(".croc-cc-nav-dd__menu--portaled").forEach((menu) => {
      try {
        menu.classList.remove("croc-cc-nav-dd__menu--portaled", "croc-cc-nav-dd__menu--anim-in", "is-open");
        menu.hidden = true;
        menu.remove();
      } catch (_) {}
    });
  } catch (_) {}
  try {
    const soft = document.getElementById("dsSoftConfirm");
    if (soft) soft.remove();
  } catch (_) {}
  try {
    deactivateOverviewCommandCenterLayout();
  } catch (_) {}
  try {
    deactivateCommandCenterAuthLayout();
  } catch (_) {}
  try {
    view.scrollTop = 0;
  } catch (_) {}
  let hashFull = location.hash || defaultHomeHash();
  let routeQuery = new URLSearchParams("");
  const qm = hashFull.indexOf("?");
  if (qm >= 0) {
    try {
      routeQuery = new URLSearchParams(hashFull.slice(qm + 1));
    } catch (_) {}
    hashFull = hashFull.slice(0, qm);
  }
  window.__routeQuery = routeQuery;
  // Longest-hash match so #/account/cameras does not fall through to #/account
  // (first-segment only). Unlisted paths (e.g. #/devices/:id) still use segment 1.
  const resolved = resolveHashRoute(hashFull);
  const id = resolved.id;
  const args = resolved.args;
  const routeSeq = ++state.routeSeq;

  clearRouteRedirectTimer();
  if (overviewFilterDebounce) {
    clearTimeout(overviewFilterDebounce);
    overviewFilterDebounce = null;
  }
  if (window.__pendingEvListRaf) {
    try { cancelAnimationFrame(window.__pendingEvListRaf); } catch (_) {}
    window.__pendingEvListRaf = 0;
  }
  clearRouteTickers();
  if (window.__fpCooldownTimer) {
    // Forgot-password resend countdown is a setInterval (clearTimeout won't
    // touch it), so clear it here when the user navigates mid-cooldown.
    try { clearInterval(window.__fpCooldownTimer); } catch (_) {}
    window.__fpCooldownTimer = 0;
  }
  if (window.__authEmailOtpInterval) {
    try { clearInterval(window.__authEmailOtpInterval); } catch (_) {}
    window.__authEmailOtpInterval = 0;
  }
  if (window.__authResendCooldownInterval) {
    try { clearInterval(window.__authResendCooldownInterval); } catch (_) {}
    window.__authResendCooldownInterval = 0;
  }
  if (window.__evReconnectTimer) {
    try { clearTimeout(window.__evReconnectTimer); } catch (_) {}
    window.__evReconnectTimer = 0;
  }
  if (window.__evFetchAbort) {
    try { window.__evFetchAbort.abort(); } catch (_) {}
    window.__evFetchAbort = null;
  }
  if (window.__opsDashAbort) {
    try { window.__opsDashAbort.abort(); } catch (_) {}
    window.__opsDashAbort = null;
  }
  if (window.__incListAbort) {
    try { window.__incListAbort.abort(); } catch (_) {}
    window.__incListAbort = null;
  }
  if (window.__opsDashPoll) {
    try { clearInterval(window.__opsDashPoll); } catch (_) {}
    window.__opsDashPoll = 0;
  }
  window.__eventsStreamResume = null;
  toggleNav(false);
  if (window.__evSSE) { try { window.__evSSE.close(); } catch {} window.__evSSE = null; }

  // Public-route ids and alias mapping come from src/routes/manifest.js
  // (the bundle splice rewrites these references so non-bundled local runs
  // still see the legacy literal sets). Edit the manifest, not this file.
  const publicRoutes = PUBLIC_ROUTE_IDS;
  const routeId = id;
  if (!state.bootReady && !publicRoutes.has(routeId)) {
    mountView(view, `<div class="route-loading card" aria-busy="true" role="status">
      <span class="sr-only">Loading session</span>
      <div class="route-loading__head"></div>
      <div class="route-loading__lines">
        <span class="route-loading__bar route-loading__bar--90"></span>
        <span class="route-loading__bar route-loading__bar--72"></span>
        <span class="route-loading__bar route-loading__bar--84"></span>
      </div>
    </div>`);
    return;
  }
  if (!state.me && !publicRoutes.has(routeId)) {
    location.hash = "#/login";
    return;
  }
  if (state.me && publicRoutes.has(routeId)) {
    location.hash = defaultHomeHash();
    return;
  }
  if (state.me && !publicRoutes.has(routeId)) {
    void registerPwaServiceWorker();
  }
  const isAuthRoute = publicRoutes.has(routeId);
  document.body.dataset.layout = isAuthRoute ? "auth" : "app";
  document.body.dataset.route = routeId;
  // Keep an explicit class for auth-shell CSS guards and legacy selectors.
  document.body.classList.toggle("auth-route-active", isAuthRoute);
  // Runtime hard guard: enforce auth/app chrome visibility directly so
  // late-loaded styles or specificity collisions cannot re-show app chrome.
  try {
    const topbar = document.querySelector(".topbar");
    const sidebar = document.querySelector(".sidebar");
    const sidebarBackdrop = document.querySelector(".sidebar-backdrop");
    for (const el of [topbar, sidebar]) {
      if (!el) continue;
      if (isAuthRoute) {
        el.setAttribute("hidden", "");
        el.style.display = "none";
      } else {
        el.removeAttribute("hidden");
        el.style.display = "";
      }
    }
    if (sidebarBackdrop) {
      if (isAuthRoute) {
        sidebarBackdrop.setAttribute("hidden", "");
        sidebarBackdrop.style.display = "none";
      } else {
        // Backdrop should only appear when mobile nav is opened.
        const navOpen = document.body.dataset.nav === "open";
        if (navOpen) {
          sidebarBackdrop.removeAttribute("hidden");
          sidebarBackdrop.style.display = "";
        } else {
          sidebarBackdrop.setAttribute("hidden", "");
          sidebarBackdrop.style.display = "none";
        }
      }
    }
  } catch (_) {}
  try { applySidebarRail(); } catch (_) {}
  const handler = routes[routeId] || routes["overview"];
  const useCcPageShell = !!state.me && !isAuthRoute && !isCommandCenterImmersiveRoute(routeId);
  if (useCcPageShell) {
    try {
      mountCommandCenterPageShell(view, {
        me: state.me,
        hasRole,
        can,
        currentHash: location.hash || defaultHomeHash(),
      });
    } catch (_) {}
  }
  try {
    mountView(view, `<div class="route-loading card glass-panel glass-panel--panel" aria-busy="true" role="status">
      <span class="route-loading__label muted">Loading…</span>
      <div class="route-loading__head"></div>
      <div class="route-loading__lines">
        <span class="route-loading__bar route-loading__bar--90"></span>
        <span class="route-loading__bar route-loading__bar--72"></span>
        <span class="route-loading__bar route-loading__bar--84"></span>
      </div>
    </div>`);
    const swap = async () => {
      await handler(view, args, routeSeq);
    };
    // Do not wrap `swap()` in `document.startViewTransition`: handlers often await
    // network I/O before finishing; the View Transition API then hits a DOM-update
    // timeout and rejects. (Also avoids races where a later route replaces #view while
    // an older handler is still awaiting.)
    await Promise.race([
      swap(),
      new Promise((_, reject) => {
        setTimeout(() => reject(new Error("Page render timed out. Please retry.")), ROUTE_RENDER_TIMEOUT_MS);
      }),
    ]);
    // Async handlers replace #view after the initial scroll reset; keep top aligned
    // so long pages (e.g. Groups hub) do not inherit a stale scroll offset.
    try {
      view.scrollTop = 0;
    } catch (_) {}
    if (useCcPageShell) {
      try {
        wireOverviewCommandCenterChrome(view, { devices: [] });
      } catch (_) {}
      try {
        if (typeof window.__ccMotionRevert === "function") {
          window.__ccMotionRevert();
          window.__ccMotionRevert = null;
        }
        if (typeof animateCcPageEnter === "function") {
          window.__ccMotionRevert = animateCcPageEnter(view);
        }
      } catch (_) {}
    }
    renderNav();
    renderHealthPills();
  } catch (e) {
    mountView(view, hx`<div class="card glass-panel glass-panel--panel"><h2>Could not open this page</h2><p class="muted">${typeof softApiError === "function" ? softApiError(e) : (e.message || e)}</p></div>`);
    if (useCcPageShell) {
      try {
        wireOverviewCommandCenterChrome(view, { devices: [] });
      } catch (_) {}
    }
  }
}

window.addEventListener("hashchange", renderRoute);


// ── 35-settings-overview.shell.js ─────────────────────────────────────
/**
 * Settings overview + admin/billing/telegram panels (concat scope — uses mountView, api, state).
 * Loaded before src/routes/*.route.js so all settings routes share these helpers.
 */

/**
 * Settings — Admin & users panel (extracted from admin.route.js).
 */

function isAdminPanelVisible(me, hasRoleFn, canFn) {
  if (!hasRoleFn("admin")) return false;
  if (me.role === "sa_staff") {
    const c = new Set((me.sa_capabilities || []).map(String));
    if (!c.has("sa_full_access") && !c.has("sa_user_admin")) return false;
  }
  return settingsNavItemVisible({ min: "admin", navCap: "sa_user_admin" }, me, hasRoleFn, canFn);
}

async function buildAdminPanelHtml(_me) {
  const isSuper = state.me.role === "superadmin";
  const selfEmail = String((state.me && state.me.email) || "").trim();
  const selfEmailBanner = `<p class="settings-users-email-strip__text"><strong>Your login email:</strong> ${
    selfEmail
      ? `<span class="mono">${escapeHtml(selfEmail)}</span>`
      : '<span>(none on file)</span> — ask platform staff if your profile should include an email.'
  }</p>`;
  /** @type {{username:string,email:string}[]} */
  let admins = [];
  const normalizeAdminPickerItems = (raw) => {
    const arr = Array.isArray(raw) ? raw : [];
    return arr
      .map((a) =>
        typeof a === "string"
          ? { username: String(a || "").trim(), email: "" }
          : {
              username: String((a && a.username) || "").trim(),
              email: String((a && a.email) || "").trim(),
            },
      )
      .filter((x) => x.username);
  };
  if (isSuper) {
    try {
      admins = normalizeAdminPickerItems((await api(buildAuthAdminsPath(), { timeoutMs: ADMIN_CONSOLE_TIMEOUT_MS })).items);
    } catch {
      admins = [];
    }
  }
  return `
    <div class="glass-panel glass-panel--panel acc-settings-panel settings-users-panel">
      <div class="ui-section-head">
        <div class="settings-users-panel__intro">
          <p class="acc-settings-panel__title">Users</p>
          <h3 class="acc-settings-panel__head" style="margin:0">User directory</h3>
          <p class="muted settings-users-desc">${isSuper
            ? "Create admin or end-user accounts, assign a manager admin."
            : "Manage users under your account and toggle their capabilities."}</p>
        </div>
        <div class="ui-section-actions">
        <button class="btn btn-tap" id="showCreate" type="button">New user</button>
        <button class="btn secondary btn-tap" id="reloadUsers" type="button">Refresh</button>
      </div>
      </div>
      <div class="settings-users-email-strip glass-panel glass-panel--inset">${selfEmailBanner}</div>
      <div id="userTable" class="settings-users-table-host"><p class="muted">Loading users…</p></div>
    </div>

    <div class="glass-panel glass-panel--panel acc-settings-panel settings-admin-panel" id="teamLocPanel">
      <div class="ui-section-head">
        <div>
          <p class="acc-settings-panel__title">Team</p>
          <h3 class="acc-settings-panel__head" style="margin:0">Relationship + locations</h3>
          <p class="muted settings-admin-desc">Manager tree with App GPS pin, consent roster, and accuracy when reported. Home map is the live picture — browser pin on this page stays off.${"" /* tip551 */}</p>
        </div>
        <div class="ui-section-actions">
          <button type="button" class="btn secondary btn-tap" data-cc-go="overview">Open Home map</button>
        </div>
      </div>
      <div id="teamLocTree" class="settings-admin-list-host"><p class="muted">Loading team pins…</p></div>
      ${typeof locationsSoftDeskHtml === "function" ? locationsSoftDeskHtml() : ""}
    </div>

    ${isSuper ? `<div class="glass-panel glass-panel--panel acc-settings-panel settings-admin-panel">
      <div class="ui-section-head">
        <div>
          <p class="acc-settings-panel__title">Sharing</p>
          <h3 class="acc-settings-panel__head" style="margin:0">Board sharing</h3>
          <p class="muted settings-admin-desc">Find who can see or operate a board, then grant or revoke access.</p>
        </div>
        <div class="ui-section-actions">
          <button class="btn secondary btn-tap" id="gs_query" type="button">Search</button>
        </div>
      </div>
      <div class="settings-admin-form glass-panel glass-panel--inset">
        <div class="inline-form settings-admin-inline-form">
          <label class="field"><span>Board serial</span><input id="gs_device" placeholder="SN-…" /></label>
          <label class="field"><span>Shared with</span><input id="gs_user" placeholder="username" /></label>
          <label class="field settings-admin-check"><span>View</span><input id="gs_view" type="checkbox" checked /></label>
          <label class="field settings-admin-check"><span>Operate</span><input id="gs_operate" type="checkbox" /></label>
          <label class="field settings-admin-check"><span>Include revoked</span><input id="gs_inc_rev" type="checkbox" /></label>
          <div class="row wide settings-admin-form-actions">
            <button class="btn btn-tap" id="gs_grant" type="button">Grant / Update</button>
          </div>
        </div>
      </div>
      <div id="gsList" class="settings-admin-list-host"><p class="muted settings-admin-empty">Search to list who has access.</p></div>
    </div>` : ""}

    <div class="glass-panel glass-panel--panel acc-settings-panel settings-admin-panel settings-admin-panel--create" id="createPanel" style="display:none">
      <div class="ui-section-head">
        <div>
          <p class="acc-settings-panel__title">Users</p>
          <h3 class="acc-settings-panel__head" style="margin:0">New user</h3>
          <p class="muted settings-admin-desc">Account starts as pending until phone + email activation completes.</p>
        </div>
        <div class="ui-section-actions">
          <button class="btn ghost btn-tap" id="u_cancel" type="button">Cancel</button>
          <button class="btn btn-tap" id="u_submit" type="button">Create &amp; send activation</button>
        </div>
      </div>
      <div class="settings-admin-form glass-panel glass-panel--inset">
        <div class="acc-settings-meta-grid settings-admin-create-grid">
          <label class="field"><span>Username</span><input id="u_name" autocomplete="off" /></label>
          <label class="field"><span>Email (required)</span><input id="u_email" type="email" autocomplete="off"/></label>
          <label class="field"><span>Phone (required)</span><input id="u_phone" type="tel" autocomplete="off" placeholder="+60123456789" /></label>
          <label class="field" style="grid-column:1/-1"><span>Password (min 12)</span><input id="u_pass" type="password" autocomplete="new-password" minlength="12" /></label>
          <p class="muted settings-admin-hint" style="grid-column:1/-1">Uppercase, lowercase, digit, and symbol (e.g. <span class="mono">Aa1!aaaaaaaa</span>).</p>
          <label class="field"><span>Role</span><select id="u_role">
            ${isSuper
              ? `<option value="user">End user</option><option value="admin">Admin</option>`
              : `<option value="user">End user</option><option value="admin">Child admin (staff)</option>`}
          </select></label>
          <label class="field"><span>Organization (optional)</span><input id="u_tenant" /></label>
          <label class="field" id="u_mgr_wrap" style="grid-column:1/-1${isSuper ? "" : ";display:none"}">
            <span>Manager admin</span>
            <select id="u_mgr">${admins.map((a) => {
              const u = escapeHtml(a.username);
              const lab = a.email ? `${u} · ${escapeHtml(a.email)}` : u;
              return `<option value="${u}">${lab}</option>`;
            }).join("")}</select>
          </label>
          ${isSuper ? "" : `<p class="muted settings-admin-hint" style="grid-column:1/-1">Child admin logs in under your account — configure whitelist under <button type="button" class="btn sm ghost btn-tap" data-cc-go="admin-user-scopes">Child admin access</button>.</p>`}
        </div>
        <p class="muted settings-admin-footnote">Finish at <button type="button" class="btn sm ghost btn-tap" data-cc-go="account-activate">Activate account</button>: answer the phone call, then enter the email code.</p>
      </div>
    </div>

    ${isSuper ? `<div class="glass-panel glass-panel--panel acc-settings-panel settings-admin-panel">
      <div class="ui-section-head">
        <div>
          <p class="acc-settings-panel__title">Signups</p>
          <h3 class="acc-settings-panel__head" style="margin:0">Pending admin signups</h3>
          <p class="muted settings-admin-desc">Public registration with verified email — awaiting approval.</p>
        </div>
      </div>
      <div id="pendAdmins" class="settings-admin-list-host"><p class="muted">Loading…</p></div>
    </div>` : ""}

    <div class="glass-panel glass-panel--panel acc-settings-panel settings-admin-panel">
      <p class="acc-settings-panel__title">Notifications</p>
      <h3 class="acc-settings-panel__head">Alert email recipients</h3>
      <p class="muted settings-admin-desc">Account-wide alarm addresses. Your account email is included automatically.</p>
      ${isSuper ? `<div id="smtpStatus" class="settings-admin-status-host"></div>` : ""}
      <div class="settings-admin-subsec">
        <h4 class="settings-admin-subsec__title">Account-wide recipients</h4>
        <div class="settings-admin-form glass-panel glass-panel--inset">
          <div class="inline-form settings-admin-inline-form">
            <label class="field wide"><span>Email</span><input id="r_email" type="email" autocomplete="off" placeholder="you@company.com"/></label>
            <label class="field"><span>Label</span><input id="r_label" autocomplete="off" placeholder="on-call"/></label>
            <label class="field"><span>6-digit code</span><input id="r_code" inputmode="numeric" autocomplete="one-time-code" maxlength="8" placeholder="from email"/></label>
            <div class="row wide settings-admin-form-actions">
              <button class="btn secondary btn-tap" type="button" id="r_send_code">Send code</button>
              <button class="btn btn-tap" type="button" id="r_add">Verify &amp; add</button>
              <button class="btn ghost btn-tap" type="button" id="r_test">Test mail</button>
            </div>
          </div>
          <p class="muted settings-admin-otp" id="r_otp_expiry"></p>
        </div>
        <div id="recipientList" class="settings-admin-list-host"><p class="muted">Loading…</p></div>
      </div>
      <div class="settings-admin-subsec">
        <h4 class="settings-admin-subsec__title">User alarm emails</h4>
        <p class="muted settings-admin-desc" style="margin-top:0">Only for boards shared with that user — same account boundary as mobile alerts.</p>
        <div class="settings-admin-form glass-panel glass-panel--inset">
          <div class="inline-form settings-admin-inline-form">
            <label class="field"><span>User</span><select id="gr_user"><option value="">— select user —</option></select></label>
            <label class="field wide"><span>Email</span><input id="gr_email" type="email" autocomplete="off" placeholder="user@example.com"/></label>
            <label class="field"><span>Label</span><input id="gr_label" autocomplete="off" placeholder="mobile"/></label>
            <label class="field"><span>6-digit code</span><input id="gr_code" inputmode="numeric" autocomplete="one-time-code" maxlength="8"/></label>
            <div class="row wide settings-admin-form-actions">
              <button class="btn secondary btn-tap" type="button" id="gr_send_code">Send code</button>
              <button class="btn btn-tap" type="button" id="gr_add">Verify &amp; add</button>
            </div>
          </div>
          <p class="muted settings-admin-otp" id="gr_otp_expiry"></p>
        </div>
        <div id="granteeRecipientList" class="settings-admin-list-host"><p class="muted">Select a user to see their alarm emails.</p></div>
      </div>
    </div>

    <div class="glass-panel glass-panel--panel acc-settings-panel settings-admin-panel">
      <div class="ui-section-head">
        <div>
          <p class="acc-settings-panel__title">Integrations</p>
          <h3 class="acc-settings-panel__head" style="margin:0">Telegram (account)</h3>
          <p class="muted settings-admin-desc">Bot status, test message, and chat bindings for this account.</p>
        </div>
        <div class="ui-section-actions">
          <button class="btn secondary btn-tap" id="tgTest" type="button">Send test</button>
          <button class="btn ghost btn-tap" id="tgReloadBindings" type="button">Refresh</button>
        </div>
      </div>
      <div id="tgStatus" class="settings-admin-status-host"></div>
      <div class="settings-admin-subsec">
        <h4 class="settings-admin-subsec__title">Command chat binding</h4>
        <p class="muted settings-admin-desc" style="margin-top:0">Open the bot, send Start, then paste the chat number here — no password in Telegram.</p>
        <div class="settings-admin-form glass-panel glass-panel--inset">
          <div class="inline-form settings-admin-inline-form">
            <label class="field wide"><span>Telegram chat number</span><input id="tgBindChatId" placeholder="e.g. 2082431201" /></label>
            <label class="field settings-admin-check"><span>Enabled</span><input id="tgBindEnabled" type="checkbox" checked /></label>
            <div class="row wide settings-admin-form-actions">
              <button class="btn btn-tap" id="tgBindSelf" type="button">Bind this chat</button>
            </div>
          </div>
        </div>
        <div id="tgBindings" class="settings-admin-list-host"><p class="muted">Loading bindings…</p></div>
      </div>
    </div>

    ${isSuper ? `<div class="glass-panel glass-panel--panel acc-settings-panel settings-admin-panel settings-admin-panel--ops">
      <p class="acc-settings-panel__title">Operations</p>
      <h3 class="acc-settings-panel__head">Backup / restore</h3>
      <p class="muted settings-admin-desc">Encrypted backup for platform staff. Import writes a restored copy — follow the operations checklist before swapping live data.</p>
      <div class="settings-admin-form glass-panel glass-panel--inset">
        <label class="field settings-admin-backup-key">
          <span>Backup passphrase</span>
          <input id="bk_key" type="password" autocomplete="off" />
        </label>
        <div class="settings-admin-backup-actions">
          <button class="btn btn-tap" id="bk_export" type="button">Export backup</button>
          <label class="settings-admin-file-btn btn secondary btn-tap">
            Choose file
            <input type="file" id="bk_file" accept=".enc,application/octet-stream" hidden />
          </label>
          <button class="btn secondary btn-tap" id="bk_import" type="button">Upload &amp; restore</button>
        </div>
      </div>
    </div>` : ""}`;
}

function renderAdminUsersTableHtml(users, isSuper, meUsername) {
  if (!users || !users.length) {
    return `<p class="muted settings-users-empty">No users yet — create one with <strong>New user</strong>.</p>`;
  }
  const counts = { total: users.length, superadmin: 0, admin: 0, user: 0 };
  for (const u of users) {
    const r = String(u.role || "").trim().toLowerCase();
    if (r === "superadmin") counts.superadmin += 1;
    else if (r === "admin") counts.admin += 1;
    else if (r === "user") counts.user += 1;
  }
  const stat = (k, v) =>
    `<div class="stat glass-panel glass-panel--inset"><span class="k">${k}</span><strong class="v">${v}</strong></div>`;
  const statsHtml = `<div class="stats settings-users-stats">${stat("Total", counts.total)}${stat("Admins", counts.admin + counts.superadmin)}${stat("End users", counts.user)}</div>`;
  const bodyRows = users
    .map((u) => {
      const isUser = u.role === "user";
      const isAdminRow = u.role === "admin";
      const self = u.username === meUsername;
      const closeTenantBtn =
        isSuper && isAdminRow && !self
          ? `<button type="button" class="btn sm danger js-close-admin" data-u="${escapeHtml(u.username)}">Close account</button>`
          : "";
      const em = String(u.email || "").trim();
      const evOk = !!(u.email_verified_at && String(u.email_verified_at).trim());
      const initial = (String(u.username || "?").trim()[0] || "?").toUpperCase();
      const mgr = String(u.manager_admin || "").trim();
      const tenant = String(u.tenant || "").trim();
      const createdFull = fmtTs(u.created_at);
      const createdShort = createdFull ? createdFull.replace(/\s+\([^)]+\)$/, "") : "—";
      const actionBtns = [
        isUser
          ? `<button type="button" class="btn sm secondary js-pol" data-u="${escapeHtml(u.username)}">Policy</button>`
          : "",
        closeTenantBtn,
        self ? "" : isAdminRow ? "" : `<button type="button" class="btn sm danger js-del" data-u="${escapeHtml(u.username)}">Delete</button>`,
      ]
        .filter(Boolean)
        .join("");
      return `<tr class="settings-users-row${self ? " settings-users-row--self" : ""}">
                <td>
                  <div class="settings-users-usercell">
                    <span class="settings-users-avatar" aria-hidden="true">${escapeHtml(initial)}</span>
                    <div class="settings-users-usercell__meta">
                      <strong class="settings-users-usercell__name">${escapeHtml(u.username)}</strong>
                      ${self ? '<span class="settings-users-you badge online">You</span>' : ""}
                    </div>
                  </div>
                </td>
                <td class="settings-users-col-email"><span class="mono settings-users-email" title="${escapeHtml(em || "")}">${escapeHtml(em || "—")}</span></td>
                <td class="settings-users-col-ok">${evOk ? '<span class="mono">yes</span>' : '<span class="muted">—</span>'}</td>
                <td class="mono settings-users-col-role">${escapeHtml(u.role)}</td>
                <td class="mono settings-users-col-mgr">${escapeHtml(mgr || "—")}</td>
                <td class="mono settings-users-col-tenant">${escapeHtml(tenant || "—")}</td>
                <td class="settings-users-col-created"><span class="settings-users-ts" title="${escapeHtml(createdFull || "")}">${escapeHtml(createdShort)}</span></td>
                <td class="settings-users-col-actions">
                  <div class="settings-users-actions">${actionBtns || '<span class="muted">—</span>'}</div>
                </td>
              </tr><tr class="sub settings-users-pol-row" style="display:none" data-pol-row="${escapeHtml(u.username)}"><td colspan="8"></td></tr>`;
    })
    .join("");
  return `${statsHtml}
    <div class="settings-users-table-wrap glass-panel glass-panel--inset">
      <div class="table-wrap settings-users-table-scroll">
        <table class="t settings-users-table">
          <thead><tr>
            <th scope="col">User</th>
            <th scope="col">Email</th>
            <th scope="col" class="settings-users-col-ok">Verified</th>
            <th scope="col" class="settings-users-col-role">Role</th>
            <th scope="col" class="settings-users-col-mgr">Manager</th>
            <th scope="col" class="settings-users-col-tenant">Organization</th>
            <th scope="col" class="settings-users-col-created">Created</th>
            <th scope="col" class="settings-users-col-actions"><span class="sr-only">Actions</span></th>
          </tr></thead>
          <tbody>${bodyRows}</tbody>
        </table>
      </div>
    </div>`;
}

function settingsAdminEmpty(msg) {
  return `<p class="muted settings-admin-empty">${msg}</p>`;
}

function settingsAdminTableShell(headHtml, bodyHtml, opts = {}) {
  if (!bodyHtml) return settingsAdminEmpty(opts.emptyMsg || "Nothing to show.");
  const cls = opts.tableClass ? ` settings-admin-table ${opts.tableClass}` : " settings-admin-table";
  const maxH = opts.scroll ? " settings-admin-table-scroll" : "";
  return `<div class="settings-admin-table-wrap glass-panel glass-panel--inset">
    <div class="table-wrap${maxH}">
      <table class="t${cls}">
        <thead>${headHtml}</thead>
        <tbody>${bodyHtml}</tbody>
      </table>
    </div>
  </div>`;
}

function settingsAdminStatusStrip(innerHtml, extraClass) {
  if (!innerHtml) return "";
  const cls = extraClass ? ` ${extraClass}` : "";
  return `<div class="settings-admin-status-strip glass-panel glass-panel--inset${cls}">${innerHtml}</div>`;
}

function settingsAdminKvGrid(items) {
  const rows = (items || [])
    .filter((it) => it && it.k != null)
    .map(
      (it) => `<div class="settings-admin-kv">
        <dt class="settings-admin-kv__k">${it.k}</dt>
        <dd class="settings-admin-kv__v">${it.v}</dd>
      </div>`,
    )
    .join("");
  return `<dl class="settings-admin-kv-grid">${rows}</dl>`;
}

function settingsAdminMonoStatus(on, onLabel, offLabel) {
  return on
    ? `<span class="mono">${escapeHtml(onLabel || "on")}</span>`
    : `<span class="muted mono">${escapeHtml(offLabel || "off")}</span>`;
}

function settingsAdminPermLabel(on, label) {
  if (!on) return '<span class="muted">—</span>';
  return `<span class="mono">${escapeHtml(label || "yes")}</span>`;
}

function renderGlobalSharesTableHtml(items) {
  if (!items || !items.length) return settingsAdminEmpty("No matching share grants.");
  const active = items.filter((it) => !it.revoked_at).length;
  const stats = `<div class="stats settings-admin-mini-stats">
    <div class="stat glass-panel glass-panel--inset"><span class="k">Grants</span><strong class="v">${items.length}</strong></div>
    <div class="stat glass-panel glass-panel--inset"><span class="k">Active</span><strong class="v">${active}</strong></div>
  </div>`;
  const rows = items
    .map((it) => {
      const revoked = !!it.revoked_at;
      return `<tr class="${revoked ? "settings-admin-row--muted" : ""}">
        <td class="mono settings-admin-col-device">${escapeHtml(it.device_id || "")}</td>
        <td class="mono">${escapeHtml(it.owner_admin || "—")}</td>
        <td><strong>${escapeHtml(it.grantee_username || "")}</strong></td>
        <td class="mono settings-users-col-role">${escapeHtml(it.grantee_role || "—")}</td>
        <td>${settingsAdminPermLabel(!!it.can_view, "view")}</td>
        <td>${settingsAdminPermLabel(!!it.can_operate, "operate")}</td>
        <td class="mono settings-admin-col-by">${escapeHtml(it.granted_by || "—")}</td>
        <td>${revoked ? '<span class="muted mono">revoked</span>' : '<span class="mono">active</span>'}</td>
        <td class="settings-users-col-actions"><div class="settings-users-actions">${revoked ? "" : `<button class="btn sm danger js-gs-revoke" data-device="${escapeHtml(it.device_id || "")}" data-user="${escapeHtml(it.grantee_username || "")}">Revoke</button>`}</div></td>
      </tr>`;
    })
    .join("");
  return `${stats}${settingsAdminTableShell(
    "<tr><th>Board</th><th>Owner</th><th>Shared with</th><th>Role</th><th>View</th><th>Operate</th><th>Granted by</th><th>Status</th><th></th></tr>",
    rows,
    { scroll: true },
  )}`;
}

function renderPendingSignupsHtml(items) {
  if (!items || !items.length) return settingsAdminEmpty("No pending signups.");
  const rows = items
    .map((u) => {
      const initial = (String(u.username || "?").trim()[0] || "?").toUpperCase();
      const evOk = !!u.email_verified_at;
      return `<tr>
        <td><div class="settings-users-usercell">
          <span class="settings-users-avatar" aria-hidden="true">${escapeHtml(initial)}</span>
          <strong class="settings-users-usercell__name">${escapeHtml(u.username)}</strong>
        </div></td>
        <td class="mono">${escapeHtml(u.email || "—")}</td>
        <td class="settings-users-col-created"><span class="settings-users-ts">${escapeHtml(fmtTs(u.created_at))}</span></td>
        <td>${evOk ? '<span class="mono">yes</span>' : '<span class="muted">—</span>'}</td>
        <td class="settings-users-col-actions"><div class="settings-users-actions">
          <button class="btn sm btn-tap js-ok" data-u="${escapeHtml(u.username)}">Approve</button>
          <button class="btn sm danger js-reject" data-u="${escapeHtml(u.username)}">Reject</button>
        </div></td>
      </tr>`;
    })
    .join("");
  return settingsAdminTableShell(
    "<tr><th>Username</th><th>Email</th><th>Submitted</th><th>Verified</th><th></th></tr>",
    rows,
  );
}

function renderAlertRecipientsHtml(items) {
  if (!items || !items.length) return settingsAdminEmpty("No account-wide recipients yet.");
  const rows = items
    .map((r) => `<tr>
      <td class="mono settings-admin-col-email">${escapeHtml(r.email)}</td>
      <td>${escapeHtml(r.label || "—")}</td>
      <td>${settingsAdminMonoStatus(!!r.enabled, "on", "off")}</td>
      <td class="mono">${escapeHtml(r.owner_admin || "—")}</td>
      <td class="settings-users-col-actions"><div class="settings-users-actions">
        <button class="btn sm secondary js-rtoggle" data-id="${r.id}" data-en="${r.enabled ? 1 : 0}">${r.enabled ? "Disable" : "Enable"}</button>
        <button class="btn sm danger js-rdel" data-id="${r.id}">Delete</button>
      </div></td>
    </tr>`)
    .join("");
  return settingsAdminTableShell(
    "<tr><th>Email</th><th>Label</th><th>Status</th><th>Organization</th><th></th></tr>",
    rows,
  );
}

function renderGranteeRecipientsHtml(grantee, items) {
  if (!grantee) return settingsAdminEmpty("Select a user to see their alarm emails.");
  if (!items || !items.length) {
    return settingsAdminEmpty(`No extra emails for <strong>${escapeHtml(grantee)}</strong>.`);
  }
  const rows = items
    .map(
      (r) => `<tr>
      <td class="mono settings-admin-col-email">${escapeHtml(r.email)}</td>
      <td>${escapeHtml(r.label || "—")}</td>
      <td>${settingsAdminMonoStatus(!!r.enabled, "on", "off")}</td>
      <td class="settings-users-col-actions"><div class="settings-users-actions">
        <button class="btn sm danger js-gr-del" type="button" data-id="${r.id}">Delete</button>
      </div></td>
    </tr>`,
    )
    .join("");
  return settingsAdminTableShell(
    "<tr><th>Email</th><th>Label</th><th>Status</th><th></th></tr>",
    rows,
  );
}

function renderTgBindingsHtml(items) {
  if (!items || !items.length) return settingsAdminEmpty("No Telegram bindings yet.");
  const rows = items
    .map(
      (it) => `<tr>
      <td class="mono">${escapeHtml(it.chat_id || "")}</td>
      <td>${escapeHtml(it.username || "—")}</td>
      <td>${settingsAdminMonoStatus(!!it.enabled, "on", "off")}</td>
      <td class="settings-users-col-created"><span class="settings-users-ts">${escapeHtml(fmtTs(it.updated_at || it.created_at))}</span></td>
      <td class="settings-users-col-actions"><div class="settings-users-actions">
        <button class="btn sm danger js-tg-unbind" data-chat="${escapeHtml(String(it.chat_id || ""))}">Unbind</button>
      </div></td>
    </tr>`,
    )
    .join("");
  return settingsAdminTableShell(
    "<tr><th>Chat number</th><th>Username</th><th>Status</th><th>Updated</th><th></th></tr>",
    rows,
  );
}

function renderBillingTableHtml(items) {
  if (!items || !items.length) return settingsAdminEmpty("Nothing to show yet.");
  const rows = items
    .map((it) => {
      const minor = Number(it.amount_minor) || 0;
      const amt = (minor / 100).toFixed(2);
      const cur = escapeHtml(String(it.currency || "MYR"));
      const ts = it.created_at ? escapeHtml(fmtTs(it.created_at)) : "—";
      const st = escapeHtml(String(it.status || ""));
      const ref = escapeHtml(String(it.provider_bill_id || "").slice(0, 28));
      return `<tr>
        <td class="settings-users-col-created"><span class="settings-users-ts">${ts}</span></td>
        <td class="mono">${cur} ${amt}</td>
        <td class="mono settings-users-col-role">${st}</td>
        <td class="mono muted">${ref}</td>
      </tr>`;
    })
    .join("");
  return settingsAdminTableShell(
    "<tr><th>Date</th><th>Amount</th><th>Status</th><th>Reference</th></tr>",
    rows,
    { scroll: true },
  );
}

function renderTgMineBindingsHtml(items) {
  if (!items || !items.length) return settingsAdminEmpty("No bindings yet.");
  const rows = items
    .map(
      (it) => `<tr>
        <td class="mono">${escapeHtml(it.chat_id || "")}</td>
        <td>${settingsAdminMonoStatus(!!it.enabled, "on", "off")}</td>
        <td class="settings-users-col-created"><span class="settings-users-ts">${escapeHtml(fmtTs(it.updated_at || it.created_at))}</span></td>
        <td class="settings-users-col-actions"><div class="settings-users-actions">
          <button class="btn sm secondary js-tg-toggle" data-chat="${escapeHtml(String(it.chat_id || ""))}" data-en="${it.enabled ? "1" : "0"}">${it.enabled ? "Disable" : "Enable"}</button>
          <button class="btn sm danger js-tg-unbind" data-chat="${escapeHtml(String(it.chat_id || ""))}">Unbind</button>
        </div></td>
      </tr>`,
    )
    .join("");
  return settingsAdminTableShell(
    "<tr><th>Chat number</th><th>Status</th><th>Updated</th><th></th></tr>",
    rows,
  );
}

function settingsSoftErrLine(err, fallback) {
  const soft = typeof softApiError === "function" ? softApiError(err, fallback) : String((err && err.message) || err || fallback || "Something went wrong");
  return `<p class="muted settings-admin-empty">${escapeHtml(soft)}</p>`;
}

function wireAdminPanel(view) {
  const $v = (sel) => $(sel, view);
  const isSuper = state.me.role === "superadmin";
  const isDbBusyMsg = (e) => {
    const msg = String((e && e.message) || e || "");
    return msg.includes("503")
      || msg.includes("database contended")
      || msg.includes("System busy")
      || msg.includes("account store temporarily unavailable");
  };

  // users + team location tree (L2)
  let teamLocPollTimer = null;
  const paintTeamLocTree = (users, locationItems, consentItems) => {
    const treeEl = $v("#teamLocTree");
    if (!treeEl) return;
    if (typeof renderTeamLocationTreeHtml !== "function") {
      setChildMarkup(treeEl, `<p class="muted">Team location panel unavailable in this build.</p>`);
      return;
    }
    setChildMarkup(
      treeEl,
      renderTeamLocationTreeHtml(users || [], locationItems || [], {
        escapeHtml,
        fmtRel,
        consentItems: consentItems || [],
      }),
    );
  };
  const loadUsers = async () => {
    const userTableEl = $v("#userTable");
    const treeEl = $v("#teamLocTree");
    if (userTableEl) setChildMarkup(userTableEl, `<p class="muted">Loading users…</p>`);
    if (treeEl) setChildMarkup(treeEl, `<p class="muted">Loading team pins…</p>`);
    try {
      const locPath = typeof buildLocationsLatestPath === "function"
        ? buildLocationsLatestPath({ limit: 64 })
        : "/locations/latest?limit=64";
      const consentPath = typeof buildLocationsConsentsPath === "function"
        ? buildLocationsConsentsPath({ include_revoked: 1, limit: 200 })
        : "/locations/consents?include_revoked=1&limit=200";
      const [usersRes, locRes, consentRes] = await Promise.allSettled([
        api(buildAuthUsersPath(), { timeoutMs: ADMIN_CONSOLE_TIMEOUT_MS }),
        api(locPath, {
          timeoutMs: typeof LOCATIONS_CONSOLE_TIMEOUT_MS === "number" ? LOCATIONS_CONSOLE_TIMEOUT_MS : 8000,
        }),
        api(consentPath, {
          timeoutMs: typeof LOCATIONS_CONSOLE_TIMEOUT_MS === "number" ? LOCATIONS_CONSOLE_TIMEOUT_MS : 8000,
        }),
      ]);
      if (usersRes.status !== "fulfilled") throw usersRes.reason;
      const users = (usersRes.value && usersRes.value.items) || [];
      const locationItems =
        locRes.status === "fulfilled" && Array.isArray(locRes.value && locRes.value.items)
          ? locRes.value.items
          : [];
      const consentItems =
        consentRes.status === "fulfilled" && Array.isArray(consentRes.value && consentRes.value.items)
          ? consentRes.value.items
          : [];
      const grSel = $v("#gr_user");
      if (grSel) {
        const opts = users
          .filter((u) => u.role === "user")
          .map((u) => `<option value="${escapeHtml(u.username)}">${escapeHtml(u.username)}</option>`)
          .join("");
        grSel.innerHTML = `<option value="">— select user —</option>${opts}`;
      }
      if (userTableEl) {
        setChildMarkup(
          userTableEl,
          renderAdminUsersTableHtml(users, isSuper, state.me && state.me.username),
        );
      }
      paintTeamLocTree(users, locationItems, consentItems);
      if ((locRes.status !== "fulfilled" || consentRes.status !== "fulfilled") && treeEl) {
        const softLoc = locRes.status !== "fulfilled"
          ? softApiError(locRes.reason, "Location share is off or slow for this account.")
          : "";
        const softCons = consentRes.status !== "fulfilled"
          ? softApiError(consentRes.reason, "Consent roster not open yet.")
          : "";
        const soft = [softLoc, softCons].filter(Boolean).join(" · ");
        if (soft) {
          setChildMarkup(
            treeEl,
            `${renderTeamLocationTreeHtml(users, locationItems, {
              escapeHtml,
              fmtRel,
              consentItems,
            })}
             <p class="muted" style="margin-top:8px;font-size:12px">${escapeHtml(soft)}</p>`,
          );
        }
      }
    } catch (e) {
      if (userTableEl) {
        setChildMarkup(
          userTableEl,
          isDbBusyMsg(e)
            ? `<p class="badge revoked">Server busy (database contended). Wait a few seconds and click <strong>Refresh</strong>.</p>`
            : `<p class="badge revoked">${escapeHtml(e.message || e)}</p>`,
        );
      }
      if (treeEl) {
        setChildMarkup(treeEl, `<p class="muted">${escapeHtml(softApiError(e, "Could not load team directory."))}</p>`);
      }
    }
  };
  // L3: soft freshness poll while Users settings stays open
  if (teamLocPollTimer) clearInterval(teamLocPollTimer);
  teamLocPollTimer = setInterval(() => {
    if (!view.isConnected) {
      clearInterval(teamLocPollTimer);
      teamLocPollTimer = null;
      return;
    }
    if (!$v("#teamLocTree")) return;
    loadUsers().catch(() => {});
  }, 15000);

  let softDeskDispose = () => {};
  if (typeof wireLocationsSoftDesk === "function") {
    softDeskDispose = wireLocationsSoftDesk(view, { api, toast, escapeHtml }) || (() => {});
  }
  const prevUnload = view.__settingsSoftDeskDispose;
  view.__settingsSoftDeskDispose = () => {
    try { softDeskDispose(); } catch (_) {}
    try { if (typeof prevUnload === "function") prevUnload(); } catch (_) {}
  };

  const loadGlobalShares = async () => {
    if (!isSuper) return;
    const listEl = $v("#gsList");
    if (!listEl) return;
    const query = {};
    const device = ($v("#gs_device").value || "").trim();
    const user = ($v("#gs_user").value || "").trim();
    if (device) query.device_id = device;
    if (user) query.grantee_username = user;
    const includeRevoked = !!($v("#gs_inc_rev") && $v("#gs_inc_rev").checked);
    if (includeRevoked) query.include_revoked = "true";
    setChildMarkup(listEl, `<p class="muted">Loading shares…</p>`);
    try {
      const pack = await fetchAllAdminShares({
        query,
        timeoutMs: 16000,
        activeOnly: !includeRevoked,
      });
      const items = pack.items || [];
      setChildMarkup(
        listEl,
        renderGlobalSharesTableHtml(items),
      );
    } catch (e) {
      setChildMarkup(listEl, settingsSoftErrLine(e, "Could not load shares — try again."));
    }
  };

  $v("#reloadUsers").addEventListener("click", loadUsers);
  $v("#showCreate").addEventListener("click", () => {
    $v("#createPanel").style.display = "";
    $v("#createPanel").scrollIntoView({ behavior: "smooth", block: "start" });
  });
  $v("#u_cancel").addEventListener("click", () => { $v("#createPanel").style.display = "none"; });
  $v("#u_submit").addEventListener("click", async () => {
    const body = {
      username: $v("#u_name").value.trim(),
      password: $v("#u_pass").value,
      role: $v("#u_role").value,
    };
    if (!body.username || !body.password) { toast("Username and password required", "err"); return; }
    const email = $v("#u_email").value.trim();
    if (!email) { toast("Email required for activation", "err"); return; }
    body.email = email;
    const phone = ($v("#u_phone") && $v("#u_phone").value.trim()) || "";
    if (!phone) { toast("Phone required", "err"); return; }
    body.phone = phone;
    const tenant = $v("#u_tenant").value.trim(); if (tenant) body.tenant = tenant;
    if (isSuper && body.role === "user") {
      const mgr = String(($v("#u_mgr") && $v("#u_mgr").value) || "").trim();
      if (!mgr) {
        toast("Select a manager admin for this end user (or create an admin account first).", "err");
        return;
      }
      body.manager_admin = mgr;
    }
    try {
      const resp = await api(buildPostAuthUsersPath(), { method: "POST", body });
      const roleNote = body.role === "admin" && !isSuper
        ? " Child admin (staff) created — configure whitelist under Child admin access."
        : "";
      toast(`Created: ${resp.message || "activation email sent"}${roleNote}`, "ok");
      $v("#createPanel").style.display = "none";
      $v("#u_name").value = ""; $v("#u_pass").value = ""; $v("#u_tenant").value = "";
      $v("#u_email").value = "";
      if ($v("#u_phone")) $v("#u_phone").value = "";
      loadUsers();
    } catch (e) {
      toast(
        isDbBusyMsg(e)
          ? "Account store busy — retry shortly or ask an operator to finish the account-store cutover."
          : softApiError(e, "Could not create user — try again shortly."),
        "err",
      );
    }
  });

  if (isSuper) {
    $v("#gs_query").addEventListener("click", loadGlobalShares);
    $v("#gs_grant").addEventListener("click", async () => {
      const device = ($v("#gs_device").value || "").trim();
      const user = ($v("#gs_user").value || "").trim();
      const canView = !!$v("#gs_view").checked;
      const canOperate = !!$v("#gs_operate").checked;
      if (!device || !user) { toast("Board serial and username required", "err"); return; }
      if (!canView && !canOperate) { toast("Select view and/or operate", "err"); return; }
      try {
        await api(buildAdminDeviceSharePath(device), {
          method: "POST",
          body: { grantee_username: user, can_view: canView, can_operate: canOperate },
        });
        toast("Share updated", "ok");
        loadGlobalShares();
      } catch (e) { toast(softApiError(e), "err"); }
    });
    $v("#gsList").addEventListener("click", async (ev) => {
      const btn = ev.target.closest(".js-gs-revoke");
      if (!btn) return;
      const device = btn.dataset.device || "";
      const user = btn.dataset.user || "";
      if (!device || !user) return;
      if (!confirm(`Revoke ${user} from ${device}?`)) return;
      try {
        await api(buildAdminDeviceShareRevokePath(device, user), { method: "DELETE" });
        toast("Share revoked", "ok");
        loadGlobalShares();
      } catch (e) { toast(softApiError(e), "err"); }
    });
  }

  const openPolicy = async (username, trRow) => {
    const cell = trRow.querySelector("td");
    setChildMarkup(cell, `<span class="muted">Loading…</span>`);
    trRow.style.display = "";
    try {
        const p = await api(buildAuthUsersPolicyPath(username), { timeoutMs: ADMIN_CONSOLE_TIMEOUT_MS });
      setChildMarkup(cell, renderPolicyPanel(username, p));
      cell.querySelector(".js-save").addEventListener("click", async () => {
        const body = {};
        cell.querySelectorAll("input[type=checkbox][data-k]").forEach((i) => body[i.dataset.k] = !!i.checked);
        try {
          const r = await api(buildPutAuthUsersPolicyPath(username), { method: "PUT", body });
          toast(`Policy updated for ${username}`, "ok");
          setChildMarkup(cell, renderPolicyPanel(username, r.policy || r));
          cell.querySelector(".js-save").addEventListener("click", () => openPolicy(username, trRow));
        } catch (e) {
          toast(isDbBusyMsg(e) ? "Server busy — wait and retry saving policy" : (e.message || e), "err");
        }
      });
    } catch (e) {
      setChildMarkup(
        cell,
        isDbBusyMsg(e)
          ? `<span class="badge revoked">Server busy — wait a few seconds and open Policy again.</span>`
          : `<span class="badge revoked">${escapeHtml(e.message || e)}</span>`,
      );
    }
  };

  $v("#userTable").addEventListener("click", async (ev) => {
    const t = ev.target.closest("button");
    if (!t) return;
    const u = t.dataset.u;
    if (t.classList.contains("js-del")) {
      if (!confirm(`Delete user ${u}?`)) return;
      try { await api(buildDeleteAuthUserPath(u), { method: "DELETE" }); toast("Deleted", "ok"); loadUsers(); }
      catch (e) { toast(e.message || e, "err"); }
    }
    if (t.classList.contains("js-pol")) {
      const row = view.querySelector(`tr[data-pol-row="${CSS.escape(u)}"]`);
      if (!row) return;
      if (row.style.display === "") { row.style.display = "none"; return; }
      openPolicy(u, row);
    }
    if (t.classList.contains("js-close-admin")) {
      if (!isSuper) return;
      if (!u) return;
      if (!confirm(
        `Close organization account "${u}"?\n\n` +
          "· Boards: release all, OR transfer to another admin in the next prompt.\n" +
          "· All team users under this admin will be removed.\n" +
          "· That username and email are released for new signups."
      )) return;
      const transfer = window.prompt(
        "Optional: admin username to receive ALL this account’s boards (leave empty to release every board):"
      );
      if (transfer === null) return;
      const transferTo = String(transfer).trim() || null;
      const confirmText = window.prompt(
        "Security phrase required.\nType exactly: CLOSE TENANT\n(to confirm closing this organization account)",
      );
      if (confirmText === null) return;
      if (String(confirmText).trim() !== "CLOSE TENANT") {
        toast("Type the security phrase exactly as shown", "err");
        return;
      }
      try {
        const r = await api(buildPostAuthAdminClosePath(u), {
          method: "POST",
          body: { confirm_text: "CLOSE TENANT", transfer_devices_to: transferTo },
        });
        toast(
          `Account closed — unclaimed ${Number(r.devices_unclaimed || 0)} boards, transferred ${Number(r.devices_transferred || 0)} boards, removed ${Number(r.subordinate_users_deleted || 0)} user(s).`,
          "ok",
        );
        loadUsers();
      } catch (e) {
        const msg = String(e.message || e);
        if (msg.includes("503") || msg.includes("database contended") || msg.includes("System busy")) {
          toast(
            "Database busy — close may be partial. Wait ~30s, verify user in Admin list, retry if still present.",
            "err",
          );
        } else {
          toast(msg, "err");
        }
      }
    }
  });

  // backup
  if (isSuper) {
    $v("#bk_export").addEventListener("click", async () => {
      const key = ($v("#bk_key").value || "").trim();
      if (!key) { toast("Enter backup passphrase", "err"); return; }
      const btn = $v("#bk_export");
      const orig = btn ? btn.textContent : "";
      if (btn) { btn.disabled = true; btn.textContent = "Exporting…"; }
      try {
        const _h = { "X-Backup-Encryption-Key": key };
        const _tb = getToken();
        if (_tb) _h.Authorization = "Bearer " + _tb;
        // Backup files can be large; allow up to 5 minutes before aborting.
        const r = await fetchWithDeadline(apiBase() + buildAdminBackupExportPath(), {
          method: "GET",
          credentials: "include",
          headers: _h,
        }, ADMIN_BACKUP_FETCH_TIMEOUT_MS);
        if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
        const blob = new Blob([await r.arrayBuffer()], { type: "application/octet-stream" });
        triggerDownloadFromBlob(blob, "sentinel-backup.enc");
        toast("Downloaded", "ok");
      } catch (e) { toast(e.message || e, "err"); }
      finally { if (btn) { btn.disabled = false; btn.textContent = orig || "Export"; } }
    });
    $v("#bk_import").addEventListener("click", async () => {
      const key = ($v("#bk_key").value || "").trim();
      const f = $v("#bk_file").files[0];
      if (!key || !f) { toast("Pick a file and enter the backup passphrase", "err"); return; }
      const fd = new FormData(); fd.append("file", f, f.name || "sentinel-backup.enc");
      const btn = $v("#bk_import");
      const orig = btn ? btn.textContent : "";
      if (btn) { btn.disabled = true; btn.textContent = "Importing…"; }
      try {
        const _hi = { "X-Backup-Encryption-Key": key };
        const _ti = getToken();
        if (_ti) _hi.Authorization = "Bearer " + _ti;
        else {
          // Cookie session: backup is a write op → must echo CSRF token.
          let _ctok = getCsrfToken();
          if (!_ctok) _ctok = await refreshCsrfToken();
          if (_ctok) _hi[CSRF_HEADER_NAME] = _ctok;
        }
        const r = await fetchWithDeadline(apiBase() + buildAdminBackupImportPath(), {
          method: "POST",
          credentials: "include",
          headers: _hi,
          body: fd,
        }, ADMIN_BACKUP_FETCH_TIMEOUT_MS);
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(`${r.status} ${j.detail || ""}`);
        toast("Restore file written — follow the checklist before swapping live data", "ok");
      } catch (e) { toast(softApiError(e), "err"); }
      finally { if (btn) { btn.disabled = false; btn.textContent = orig || "Import"; } }
    });
  }

  // SMTP status + recipients
  const loadSmtpStatus = async () => {
    if (!isSuper) return;
    try {
      const s = await api(buildAdminSmtpStatusPath(), { timeoutMs: ADMIN_CONSOLE_TIMEOUT_MS });
      const smtpEl = $v("#smtpStatus");
      if (!smtpEl) return;
      const okBadge = s.enabled
        ? `<span class="mono">on</span>`
        : `<span class="muted mono">off</span>`;
      const last = s.last_error
        ? `<p class="settings-admin-status-note muted">${escapeHtml(softApiError(s.last_error, "Mail reported a recent issue."))}</p>`
        : "";
      setChildMarkup(
        smtpEl,
        settingsAdminStatusStrip(`${settingsAdminKvGrid([
          { k: "Mail", v: okBadge },
          { k: "From", v: `<span class="mono">${escapeHtml(s.sender || "—")}</span>` },
          { k: "Sent", v: `<span class="mono">${s.sent_count || 0}</span>` },
          { k: "Failed", v: `<span class="mono">${s.failed_count || 0}</span>` },
        ])}${last}`),
      );
    } catch (e) {
      const smtpEl = $v("#smtpStatus");
      if (!smtpEl) return;
      setChildMarkup(smtpEl, settingsAdminStatusStrip(settingsSoftErrLine(e, "Could not load mail status."), "settings-admin-status-strip--err"));
    }
  };
  const loadRecipients = async () => {
    try {
      const d = await api(buildAdminAlertRecipientsPath(), { timeoutMs: ADMIN_CONSOLE_TIMEOUT_MS });
      const items = d.items || [];
      const listEl = $v("#recipientList");
      if (!listEl) return;
      setChildMarkup(listEl, renderAlertRecipientsHtml(items));
    } catch (e) {
      const listEl = $v("#recipientList");
      if (!listEl) return;
      setChildMarkup(listEl, settingsSoftErrLine(e, "Could not load recipients."));
    }
  };
  let disposeROtp = () => {};
  let disposeGrOtp = () => {};
  const expiredOtpMsg = "Verification failed — request a new code";
  let rSendBusy = false;
  $v("#r_send_code")?.addEventListener("click", async () => {
    if (rSendBusy) return;
    const email = ($v("#r_email").value || "").trim();
    if (!email) { toast("Enter email", "err"); return; }
    rSendBusy = true;
    try {
      const j = await api(buildPostAdminAlertRecipientsRequestVerifyPath(), {
        method: "POST",
        body: { email },
        timeoutMs: ADMIN_CONSOLE_WRITE_TIMEOUT_MS,
        retries: 0,
      });
      disposeROtp();
      const d1 = startEmailCodeExpiryCountdown($v("#r_otp_expiry"), j.ttl_seconds, { expiredMessage: expiredOtpMsg });
      const d2 = startAuthResendCooldown($v("#r_send_code"), Number(j.resend_after_seconds || 60), "Send code");
      disposeROtp = () => { try { d1(); } catch (_) {} try { d2(); } catch (_) {} };
      toast("Code sent — check inbox (5 min)", "ok");
    } catch (e) {
      const msg = String(e.message || e);
      if (msg.includes("timed out") || msg.includes("Request timed out")) {
        toast("Send code timed out — too many background requests; wait and retry", "err");
      } else if (msg.includes("503") || msg.includes("database contended")) {
        toast("Server database busy — wait a moment and retry", "err");
      } else {
        toast(msg, "err");
      }
    } finally {
      rSendBusy = false;
    }
  });
  $v("#r_add")?.addEventListener("click", async () => {
    const email = ($v("#r_email").value || "").trim();
    const label = ($v("#r_label").value || "").trim();
    const code = ($v("#r_code").value || "").trim();
    if (!email || !code) { toast("Enter email and verification code", "err"); return; }
    try {
      await api(buildPostAdminAlertRecipientsPath(), {
        method: "POST",
        body: { email, label, code },
        timeoutMs: ADMIN_CONSOLE_WRITE_TIMEOUT_MS,
        retries: 0,
      });
      $v("#r_email").value = ""; $v("#r_label").value = ""; $v("#r_code").value = "";
      disposeROtp();
      if ($v("#r_otp_expiry")) $v("#r_otp_expiry").textContent = "";
      toast("Added", "ok");
      void loadRecipients().catch(() => {});
    } catch (e) { toast(e.message || e, "err"); }
  });
  const loadGranteeRecipients = async () => {
    const grantee = ($v("#gr_user")?.value || "").trim();
    const listEl = $v("#granteeRecipientList");
    if (!listEl) return;
    if (!grantee) {
      setChildMarkup(listEl, renderGranteeRecipientsHtml("", []));
      return;
    }
    try {
      const d = await api(buildAdminGranteeAlertRecipientsPath(grantee), { timeoutMs: ADMIN_CONSOLE_TIMEOUT_MS });
      const items = d.items || [];
      setChildMarkup(listEl, renderGranteeRecipientsHtml(grantee, items));
    } catch (e) {
      setChildMarkup(listEl, settingsSoftErrLine(e, "Could not load alarm emails for this user."));
    }
  };
  let grListBusy = false;
  $v("#granteeRecipientList")?.addEventListener("click", async (ev) => {
    const btn = ev.target.closest(".js-gr-del");
    if (!btn || btn.disabled || grListBusy) return;
    const id = String(btn.dataset.id || "").trim();
    const grantee = ($v("#gr_user")?.value || "").trim();
    if (!id || !grantee) return;
    ev.preventDefault();
    ev.stopPropagation();
    if (!confirm("Remove this alarm email for the user?")) return;
    const tr = btn.closest("tr");
    const prevLabel = btn.textContent;
    grListBusy = true;
    btn.disabled = true;
    btn.textContent = "Deleting…";
    try {
      await api(buildDeleteAdminGranteeAlertRecipientPath(grantee, id), {
        method: "DELETE",
        timeoutMs: ADMIN_CONSOLE_WRITE_TIMEOUT_MS,
        retries: 0,
      });
      toast("Removed", "ok");
      if (tr && tr.parentElement) tr.remove();
    } catch (e) {
      const msg = String(e.message || e);
      if (msg.includes("timed out") || msg.includes("Request timed out")) {
        toast("Delete timed out — check Network or restart the API service", "err");
      } else {
        toast(msg, "err");
      }
    } finally {
      grListBusy = false;
      btn.disabled = false;
      btn.textContent = prevLabel;
    }
  });
  $v("#gr_user")?.addEventListener("change", loadGranteeRecipients);
  let grSendBusy = false;
  $v("#gr_send_code")?.addEventListener("click", async () => {
    if (grSendBusy) return;
    const grantee_username = ($v("#gr_user")?.value || "").trim();
    const email = ($v("#gr_email").value || "").trim();
    if (!grantee_username) { toast("Select user", "err"); return; }
    if (!email) { toast("Enter email", "err"); return; }
    grSendBusy = true;
    try {
      const j = await api(buildPostAdminGranteeAlertRecipientsRequestVerifyPath(), {
        method: "POST",
        body: { grantee_username, email },
        timeoutMs: ADMIN_CONSOLE_WRITE_TIMEOUT_MS,
        retries: 0,
      });
      disposeGrOtp();
      const d1 = startEmailCodeExpiryCountdown($v("#gr_otp_expiry"), j.ttl_seconds, { expiredMessage: expiredOtpMsg });
      const d2 = startAuthResendCooldown($v("#gr_send_code"), Number(j.resend_after_seconds || 60), "Send code");
      disposeGrOtp = () => { try { d1(); } catch (_) {} try { d2(); } catch (_) {} };
      toast("Code sent — check inbox (5 min)", "ok");
    } catch (e) {
      const msg = String(e.message || e);
      if (msg.includes("timed out") || msg.includes("Request timed out")) {
        toast("Send code timed out — too many background requests; wait and retry", "err");
      } else {
        toast(msg, "err");
      }
    } finally {
      grSendBusy = false;
    }
  });
  $v("#gr_add")?.addEventListener("click", async () => {
    const grantee_username = ($v("#gr_user")?.value || "").trim();
    const email = ($v("#gr_email").value || "").trim();
    const label = ($v("#gr_label").value || "").trim();
    const code = ($v("#gr_code").value || "").trim();
    if (!grantee_username || !email || !code) {
      toast("Select user, enter email and code", "err");
      return;
    }
    try {
      await api(buildPostAdminGranteeAlertRecipientsPath(), {
        method: "POST",
        body: { grantee_username, email, label, code },
        timeoutMs: ADMIN_CONSOLE_WRITE_TIMEOUT_MS,
        retries: 0,
      });
      $v("#gr_email").value = ""; $v("#gr_label").value = ""; $v("#gr_code").value = "";
      disposeGrOtp();
      if ($v("#gr_otp_expiry")) $v("#gr_otp_expiry").textContent = "";
      toast("Added for user", "ok");
      void loadGranteeRecipients().catch(() => {});
    } catch (e) { toast(e.message || e, "err"); }
  });
  $v("#r_test").addEventListener("click", async () => {
    const email = ($v("#r_email").value || "").trim();
    if (!email) { toast("Enter recipient email first", "err"); return; }
    try {
      await api(buildPostAdminSmtpTestPath(), { method: "POST", body: { to: email } });
      toast("Mail test sent", "ok");
      loadSmtpStatus();
    } catch (e) { toast(e.message || e, "err"); }
  });
  const loadTgStatus = async () => {
    try {
      const t = await api(buildAdminTelegramStatusPath(), { timeoutMs: ADMIN_CONSOLE_TIMEOUT_MS });
      const tgEl = $v("#tgStatus");
      if (!tgEl) return;
      const badge = t.enabled
        ? `<span class="mono">on</span>`
        : `<span class="muted mono">off</span>`;
      const softLast = (t.last_error || "").trim()
        ? `<p class="settings-admin-status-note muted">${escapeHtml(softApiError(t.last_error, "Bot reported a recent issue — try Refresh."))}</p>`
        : "";
      const modNote = t.status_module_error
        ? `<p class="settings-admin-status-note muted">Telegram service needs attention — ask platform staff.</p>`
        : "";
      setChildMarkup(
        tgEl,
        settingsAdminStatusStrip(`${settingsAdminKvGrid([
          { k: "Bot", v: badge },
          { k: "Chats", v: `<span class="mono">${t.chats ?? 0}</span>` },
        ])}${modNote}${softLast}`),
      );
    } catch (e) {
      const tgEl = $v("#tgStatus");
      if (!tgEl) return;
      setChildMarkup(tgEl, settingsAdminStatusStrip(settingsSoftErrLine(e, "Could not load Telegram status."), "settings-admin-status-strip--err"));
    }
  };
  const loadTgBindings = async () => {
    const el = $v("#tgBindings");
    if (!el) return;
    setChildMarkup(el, `<p class="muted">Loading bindings…</p>`);
    try {
      const d = await api(buildAdminTelegramBindingsPath(), { timeoutMs: ADMIN_CONSOLE_TIMEOUT_MS });
      const items = d.items || [];
      setChildMarkup(el, renderTgBindingsHtml(items));
    } catch (e) {
      setChildMarkup(el, settingsSoftErrLine(e, "Could not load chat bindings — try Refresh."));
    }
  };
  $v("#tgTest").addEventListener("click", async () => {
    try {
      const r = await api(buildPostAdminTelegramTestPath(), { method: "POST", body: { text: "Croc Sentinel UI test" } });
      toast(r.detail || "Test sent", "ok");
      loadTgStatus();
    } catch (e) { toast(softApiError(e), "err"); }
  });
  $v("#tgBindSelf").addEventListener("click", async () => {
    const chat_id = ($v("#tgBindChatId").value || "").trim();
    const enabled = !!$v("#tgBindEnabled").checked;
    if (!chat_id) { toast("Enter the Telegram chat number", "err"); return; }
    try {
      await api(buildPostAdminTelegramBindSelfPath(), { method: "POST", body: { chat_id, enabled } });
      toast("Chat bound", "ok");
      loadTgBindings();
    } catch (e) { toast(softApiError(e), "err"); }
  });
  $v("#tgReloadBindings").addEventListener("click", loadTgBindings);
  $v("#tgBindings").addEventListener("click", async (ev) => {
    const btn = ev.target.closest(".js-tg-unbind");
    if (!btn) return;
    const chat = btn.dataset.chat || "";
    if (!chat) return;
    if (!confirm(`Unbind chat ${chat}?`)) return;
    try {
      await api(buildDeleteAdminTelegramBindingPath(chat), { method: "DELETE" });
      toast("Unbound", "ok");
      loadTgBindings();
    } catch (e) { toast(softApiError(e), "err"); }
  });
  let recipientListBusy = false;
  const reloadRecipientsQuiet = () => {
    void loadRecipients().catch((e) => {
      toast(softApiError(e), "warn");
    });
  };
  $v("#recipientList").addEventListener("click", async (ev) => {
    const b = ev.target.closest("button");
    if (!b || b.disabled) return;
    const id = String(b.dataset.id || "").trim();
    if (!id) return;
    if (b.classList.contains("js-rdel")) {
      if (recipientListBusy) return;
      ev.preventDefault();
      ev.stopPropagation();
      if (!confirm("Remove this recipient?")) return;
      const tr = b.closest("tr");
      const prevLabel = b.textContent;
      recipientListBusy = true;
      b.disabled = true;
      b.textContent = "Deleting…";
      try {
        await api(buildDeleteAdminAlertRecipientPath(id), {
          method: "DELETE",
          timeoutMs: ADMIN_CONSOLE_WRITE_TIMEOUT_MS,
          retries: 0,
        });
        toast("Removed", "ok");
        if (tr && tr.parentElement) tr.remove();
      } catch (e) {
        const msg = String(e.message || e);
        if (msg.includes("timed out") || msg.includes("Request timed out")) {
          toast("Delete timed out — API may be stuck; check Network or restart the API service", "err");
        } else if (msg.includes("503") || msg.includes("database contended")) {
          toast("Server database busy — wait a moment and retry", "err");
        } else if (msg.includes("401")) {
          toast("Session expired — please sign in again", "err");
        } else {
          toast(msg, "err");
        }
      } finally {
        recipientListBusy = false;
        b.disabled = false;
        b.textContent = prevLabel;
      }
      return;
    }
    if (b.classList.contains("js-rtoggle")) {
      if (recipientListBusy) return;
      ev.preventDefault();
      ev.stopPropagation();
      const en = b.dataset.en === "1" ? 0 : 1;
      const prevLabel = b.textContent;
      recipientListBusy = true;
      b.disabled = true;
      b.textContent = "…";
      try {
        await api(buildPatchAdminAlertRecipientPath(id), {
          method: "PATCH",
          body: { enabled: !!en },
          timeoutMs: ADMIN_CONSOLE_WRITE_TIMEOUT_MS,
          retries: 0,
        });
        reloadRecipientsQuiet();
      } catch (e) {
        const msg = String(e.message || e);
        if (msg.includes("401")) toast("Session expired — please sign in again", "err");
        else toast(msg, "err");
      } finally {
        recipientListBusy = false;
        b.disabled = false;
        b.textContent = prevLabel;
      }
    }
  });

  // Pending admin signups (superadmin approval queue)
  const loadPendAdmins = async () => {
    if (!isSuper) return;
    try {
      const d = await api(buildAuthSignupPendingPath(), { timeoutMs: ADMIN_CONSOLE_TIMEOUT_MS });
      const items = d.items || [];
      const pendEl = $v("#pendAdmins");
      if (!pendEl) return;
      setChildMarkup(pendEl, renderPendingSignupsHtml(items));
    } catch (e) {
      const pendEl = $v("#pendAdmins");
      if (!pendEl) return;
      setChildMarkup(pendEl, `<p class="badge revoked">${escapeHtml(e.message || e)}</p>`);
    }
  };
  if (isSuper) {
    $v("#pendAdmins").addEventListener("click", async (ev) => {
      const b = ev.target.closest("button"); if (!b) return;
      const u = b.dataset.u;
      if (b.classList.contains("js-ok")) {
        try { await api(buildPostAuthSignupApprovePath(u), { method: "POST" }); toast("Approved", "ok"); loadPendAdmins(); loadUsers(); }
        catch (e) { toast(e.message || e, "err"); }
      } else if (b.classList.contains("js-reject")) {
        if (!confirm(`Reject and delete signup for ${u}?`)) return;
        try { await api(buildPostAuthSignupRejectPath(u), { method: "POST" }); toast("Rejected", "ok"); loadPendAdmins(); }
        catch (e) { toast(e.message || e, "err"); }
      }
    });
  }

  void (async () => {
    await loadUsers().catch(() => {});
    if (isSuper) {
      await loadPendAdmins().catch(() => {});
      await loadSmtpStatus().catch(() => {});
    }
    void loadRecipients().catch(() => {});
    void loadGranteeRecipients().catch(() => {});
    void loadTgStatus().catch(() => {});
    void loadTgBindings().catch(() => {});
  })();
}

/**
 * Settings — Billing panel.
 */


function isBillingPanelVisible(me, hasRoleFn, canFn) {
  if (!me || !me.billing_enabled) return false;
  return settingsNavItemVisible({ min: "admin", navCap: "sa_billing_admin" }, me, hasRoleFn, canFn);
}

function buildBillingPanelHtml() {
  return `<div class="settings-overview-sec" id="settings-sec-billing" data-settings-section="billing">
    <div class="glass-panel glass-panel--panel acc-settings-panel settings-admin-panel">
      <p class="acc-settings-panel__title">Billing</p>
      <h3 class="acc-settings-panel__head">Usage</h3>
      <p class="muted settings-admin-desc">Current plan and board quota for your account.</p>
      <div id="billingUsageWrap" class="settings-admin-list-host"><p class="muted">Loading usage…</p></div>
      <div class="divider"></div>
      <h3 class="acc-settings-panel__head">Invoices</h3>
      <p class="muted settings-admin-desc">Payment history and invoice status for your account.</p>
      <div class="settings-filter-bar" role="toolbar" aria-label="Invoice filters">
        <button type="button" class="btn btn-tap sm" data-bill-filter="all">All</button>
        <button type="button" class="btn secondary btn-tap sm" data-bill-filter="paid">Paid</button>
        <button type="button" class="btn secondary btn-tap sm" data-bill-filter="unpaid">Unpaid</button>
      </div>
      <div id="billingTableWrap" class="settings-admin-list-host"><p class="muted">Loading invoices…</p></div>
    </div>
  </div>`;
}

/** @param {ParentNode} view @param {number} routeSeq */
function wireBillingPanel(view, routeSeq) {
  const setFilterButtons = (active) => {
    const nodes = view.querySelectorAll("[data-bill-filter]");
    for (let i = 0; i < nodes.length; i++) {
      const b = nodes[i];
      const v = b.getAttribute("data-bill-filter");
      const on = v === active;
      b.className = on ? "btn btn-tap sm" : "btn secondary btn-tap sm";
    }
  };

  const renderRows = (items) => renderBillingTableHtml(items);

  const renderUsage = (data) => {
    if (!data || typeof data !== "object") {
      return '<p class="muted">Usage unavailable.</p>';
    }
    const usage = data.usage && typeof data.usage === "object" ? data.usage : {};
    const devices = usage.devices != null ? usage.devices : "—";
    const plan = escapeHtml(String(data.plan_code || "—"));
    const status = escapeHtml(String(data.status || "—"));
    const maxDev = usage.max_devices != null ? usage.max_devices : usage.device_limit;
    const quotaLine =
      maxDev != null && maxDev !== ""
        ? `<div><span class="muted">Board quota</span><strong class="mono">${escapeHtml(String(devices))} / ${escapeHtml(String(maxDev))}</strong></div>`
        : `<div><span class="muted">Boards</span><strong class="mono">${escapeHtml(String(devices))}</strong></div>`;
    return `<div class="settings-usage-grid">
      <div><span class="muted">Plan</span><strong class="mono">${plan}</strong></div>
      <div><span class="muted">Status</span><strong>${status}</strong></div>
      ${quotaLine}
    </div>`;
  };

  const loadUsage = async () => {
    if (!isRouteCurrent(routeSeq)) return;
    let data = null;
    try {
      data = await api(buildBillingUsagePath(), { timeoutMs: BILLING_CONSOLE_TIMEOUT_MS });
    } catch (_) {
      data = null;
    }
    if (!isRouteCurrent(routeSeq)) return;
    const wrap = $("#billingUsageWrap", view);
    if (wrap) setHtmlIfChanged(wrap, renderUsage(data));
  };

  const load = async (flt) => {
    if (!isRouteCurrent(routeSeq)) return;
    setFilterButtons(flt);
    let items = [];
    try {
      const data = await api(buildBillingInvoicesPath(flt), { timeoutMs: BILLING_CONSOLE_TIMEOUT_MS });
      items = (data && data.items) || [];
    } catch (_) {
      items = [];
    }
    if (!isRouteCurrent(routeSeq)) return;
    const wrap = $("#billingTableWrap", view);
    if (wrap) setHtmlIfChanged(wrap, renderRows(items));
  };

  view.addEventListener(
    "click",
    (ev) => {
      const btn = ev.target && ev.target.closest && ev.target.closest("[data-bill-filter]");
      if (!btn || !view.contains(btn)) return;
      const flt = btn.getAttribute("data-bill-filter");
      if (flt) void load(flt);
    },
    true,
  );

  void load("all");
  void loadUsage();
}

/**
 * Settings — Voice alert panel (admin).
 */

function isVoicePanelVisible(me, hasRoleFn, canFn) {
  if (!me || !me.voice_enabled) return false;
  return settingsNavItemVisible({ min: "admin" }, me, hasRoleFn, canFn);
}

function buildVoicePanelHtml() {
  return `<div class="settings-overview-sec" id="settings-sec-voice" data-settings-section="voice">
    <div class="glass-panel glass-panel--panel acc-settings-panel settings-admin-panel">
      <p class="acc-settings-panel__title">Voice alerts</p>
      <h3 class="acc-settings-panel__head">Templates</h3>
      <p class="muted settings-admin-desc">Phone message templates for emergency voice alerts. Placeholders like <span class="mono">{device}</span> are filled with the board serial when a call is placed.</p>
      <div id="voiceTemplatesWrap" class="settings-admin-list-host"><p class="muted">Loading templates…</p></div>
      <div class="divider"></div>
      <h3 class="acc-settings-panel__head">Test call</h3>
      <p class="muted settings-admin-desc">Place a manual test call to a user in your account (voice calling must be enabled).</p>
      <div class="settings-filter-bar" style="flex-direction:column;align-items:stretch;gap:10px">
        <label class="field"><span>Username</span><input id="voiceTestUser" type="text" autocomplete="off" placeholder="account username" /></label>
        <label class="field"><span>Message</span><textarea id="voiceTestMsg" rows="3" placeholder="Test message"></textarea></label>
        <button type="button" class="btn btn-tap sm" id="voiceTestBtn">Send test call</button>
        <p class="muted" id="voiceTestStatus" style="margin:0"></p>
      </div>
    </div>
  </div>`;
}

/** @param {ParentNode} view @param {number} routeSeq */
function wireVoicePanel(view, routeSeq) {
  const tplWrap = $("#voiceTemplatesWrap", view);
  const loadTemplates = async () => {
    if (!isRouteCurrent(routeSeq)) return;
    try {
      const data = await api(buildGetAdminVoiceTemplatesPath(), { timeoutMs: VOICE_CONSOLE_TIMEOUT_MS });
      const templates = (data && data.templates) || [];
      if (!templates.length) {
        if (tplWrap) setHtmlIfChanged(tplWrap, '<p class="muted">No templates.</p>');
        return;
      }
      const html = templates
        .map((t, i) => {
          const name = escapeHtml(String(t.name || `template-${i}`));
          const rawText = String(t.text || "");
          const text = escapeHtml(rawText.replace(/\{device_id\}/g, "{device}"));
          const lang = escapeHtml(String(t.language || ""));
          return `<article class="settings-voice-tpl glass-panel glass-panel--inset">
            <div class="row between" style="gap:8px;flex-wrap:wrap">
              <strong class="mono">${name}</strong>
              <span class="muted mono">${lang}</span>
            </div>
            <label class="field" style="margin-top:8px"><span>Text</span>
              <textarea class="voice-tpl-text" data-voice-name="${name}" rows="2">${text}</textarea>
            </label>
            <button type="button" class="btn secondary btn-tap sm voice-tpl-save" data-voice-name="${name}">Save template</button>
          </article>`;
        })
        .join("");
      if (tplWrap) setHtmlIfChanged(tplWrap, html);
    } catch (e) {
      if (tplWrap) setHtmlIfChanged(tplWrap, `<p class="muted">${escapeHtml(e.message || String(e))}</p>`);
    }
  };

  view.addEventListener(
    "click",
    (ev) => {
      const btn = ev.target && ev.target.closest && ev.target.closest(".voice-tpl-save");
      if (!btn || !view.contains(btn)) return;
      const article = btn.closest("article");
      const ta = article ? article.querySelector("textarea.voice-tpl-text") : null;
      const name = (ta && ta.getAttribute("data-voice-name")) || btn.getAttribute("data-voice-name") || "default";
      const textRaw = ta ? String(ta.value || "").trim() : "";
      if (!textRaw) {
        toast("Template text required", "err");
        return;
      }
      const text = textRaw.replace(/\{device\}/g, "{device_id}");
      void (async () => {
        try {
          await api(buildPutAdminVoiceTemplatePath(name), {
            method: "PUT",
            body: { text, language: "zh-CN" },
            timeoutMs: VOICE_CONSOLE_TIMEOUT_MS,
          });
          toast("Template saved", "ok");
        } catch (e) {
          toast(typeof softApiError === "function" ? softApiError(e) : (e.message || e), "err");
        }
      })();
    },
    true,
  );

  const testBtn = $("#voiceTestBtn", view);
  const statusEl = $("#voiceTestStatus", view);
  if (testBtn) {
    testBtn.addEventListener("click", () => {
      const username = String($("#voiceTestUser", view)?.value || "").trim();
      const message = String($("#voiceTestMsg", view)?.value || "").trim();
      if (!username || !message) {
        toast("Username and message required", "err");
        return;
      }
      testBtn.disabled = true;
      if (statusEl) statusEl.textContent = "Calling…";
      void (async () => {
        try {
          const res = await api(buildPostAdminVoiceTestCallPath(), {
            method: "POST",
            body: { username, message },
            timeoutMs: VOICE_CONSOLE_TIMEOUT_MS,
          });
          if (statusEl) statusEl.textContent = `Queued · ${String((res && res.status) || "ok")}`;
          toast("Test call queued", "ok");
        } catch (e) {
          if (statusEl) statusEl.textContent = "";
          toast(e.message || e, "err");
        } finally {
          testBtn.disabled = false;
        }
      })();
    });
  }

  void loadTemplates();
}

/**
 * Settings — Telegram connect panel (user bindings).
 */

function buildTelegramPanelHtml() {
  return `<div class="settings-overview-sec" id="settings-sec-telegram" data-settings-section="telegram">
    <div class="glass-panel glass-panel--panel acc-settings-panel settings-admin-panel">
      <div class="ui-section-head">
        <div>
          <p class="acc-settings-panel__title">Telegram</p>
          <h3 class="acc-settings-panel__head" style="margin:0">Telegram connect</h3>
          <p class="muted settings-admin-desc">No password in Telegram — generate a link, open the bot, send <span class="mono">/start</span>.</p>
        </div>
        <div class="ui-section-actions">
          <button class="btn btn-tap" id="tgGenLink" type="button">Generate link</button>
          <button class="btn secondary btn-tap" id="tgReloadMine" type="button">Refresh</button>
        </div>
      </div>
      <div id="tgLinkBox" class="settings-admin-list-host settings-tg-link-host"><p class="muted settings-admin-empty">Generate a connect link to bind your chat.</p></div>
    </div>
    <div class="glass-panel glass-panel--panel acc-settings-panel settings-admin-panel">
      <h4 class="settings-admin-subsec__title">My chat bindings</h4>
      <p class="muted settings-admin-desc" style="margin-top:0">Enable, disable, or unbind your own Telegram chats.</p>
      <div id="tgMineList" class="settings-admin-list-host"><p class="muted">Loading…</p></div>
    </div>
    <div class="glass-panel glass-panel--panel acc-settings-panel settings-admin-panel">
      <h4 class="settings-admin-subsec__title">Manual bind (fallback)</h4>
      <p class="muted settings-admin-desc" style="margin-top:0">If the connect link fails, paste the chat number from the bot and bind here.</p>
      <div class="settings-admin-form glass-panel glass-panel--inset">
        <div class="inline-form settings-admin-inline-form">
          <label class="field wide"><span>Telegram chat number</span><input id="tgManualChatId" placeholder="e.g. 2082431201" /></label>
          <label class="field settings-admin-check"><span>Enabled</span><input id="tgManualEnabled" type="checkbox" checked /></label>
          <div class="row wide settings-admin-form-actions">
            <button class="btn btn-tap" id="tgManualBind" type="button">Bind manually</button>
          </div>
        </div>
      </div>
    </div>
  </div>`;
}

/** @param {ParentNode} view */
function wireTelegramPanel(view) {
  const mineEl = $("#tgMineList", view);
  const linkEl = $("#tgLinkBox", view);
  const loadMine = async () => {
    if (!mineEl) return;
    setChildMarkup(mineEl, `<p class="muted">Loading…</p>`);
    try {
      const d = await api(buildAdminTelegramBindingsPath(), { timeoutMs: ADMIN_CONSOLE_TIMEOUT_MS });
      const items = d.items || [];
    setChildMarkup(mineEl, renderTgMineBindingsHtml(items));
    } catch (e) {
      setChildMarkup(mineEl, settingsSoftErrLine(e, "Could not load your Telegram chats — try Refresh."));
    }
  };
  const genBtn = $("#tgGenLink", view);
  if (genBtn) {
    genBtn.addEventListener("click", async () => {
      if (!linkEl) return;
      try {
        const r = await api(buildPostTelegramLinkTokenPath(), { method: "POST", body: { enabled_on_bind: true } });
        const deep = r.deep_link || "";
        const openChat = r.open_chat_url || "";
        setChildMarkup(
          linkEl,
          deep
            ? `<div class="settings-admin-form glass-panel glass-panel--inset settings-tg-link-steps">
             <div class="settings-admin-kv-grid settings-admin-kv-grid--steps">
               <div class="settings-admin-kv"><dt class="settings-admin-kv__k">Step 1</dt><dd class="settings-admin-kv__v"><a class="btn btn-tap sm" href="${escapeHtml(openChat || deep)}" target="_blank" rel="noopener">Open bot chat</a></dd></div>
               <div class="settings-admin-kv"><dt class="settings-admin-kv__k">Step 2</dt><dd class="settings-admin-kv__v"><a class="btn secondary btn-tap sm" href="${escapeHtml(deep)}" target="_blank" rel="noopener">One-click bind</a></dd></div>
             </div>
           </div>`
            : `<p class="muted settings-admin-empty">Telegram bot is not configured yet — ask platform staff.<br/>Or open your bot chat and send Start with the one-time code they provide.</p>`,
        );
      } catch (e) {
        setChildMarkup(linkEl, settingsSoftErrLine(e, "Could not create a connect link — try again."));
      }
    });
  }
  const manualBtn = $("#tgManualBind", view);
  if (manualBtn) {
    manualBtn.addEventListener("click", async () => {
      const chatId = ($("#tgManualChatId", view).value || "").trim();
      const enabled = !!$("#tgManualEnabled", view).checked;
      if (!chatId) { toast("Enter the Telegram chat number", "err"); return; }
      try {
        await api(buildPostAdminTelegramBindSelfPath(), { method: "POST", body: { chat_id: chatId, enabled } });
        toast("Bound", "ok");
        loadMine();
      } catch (e) { toast(softApiError(e), "err"); }
    });
  }
  const reloadBtn = $("#tgReloadMine", view);
  if (reloadBtn) reloadBtn.addEventListener("click", loadMine);
  if (mineEl) {
    mineEl.addEventListener("click", async (ev) => {
      const tgl = ev.target.closest(".js-tg-toggle");
      if (tgl) {
        const chat = tgl.dataset.chat || "";
        const enabled = !(tgl.dataset.en === "1");
        try {
          await api(buildPatchAdminTelegramBindingEnabledPath(chat, enabled), { method: "PATCH" });
          toast(enabled ? "Enabled" : "Disabled", "ok");
          loadMine();
        } catch (e) { toast(e.message || e, "err"); }
        return;
      }
      const del = ev.target.closest(".js-tg-unbind");
      if (del) {
        const chat = del.dataset.chat || "";
        if (!chat) return;
        if (!confirm(`Unbind chat ${chat}?`)) return;
        try {
          await api(buildDeleteAdminTelegramBindingPath(chat), { method: "DELETE" });
          toast("Unbound", "ok");
          loadMine();
        } catch (e) { toast(e.message || e, "err"); }
      }
    });
  }
  loadMine();
}

/**
 * Settings overview — single page with admin, billing, telegram sections.
 */

/**
 * @param {ParentNode} view
 * @param {{ activeNavId?: string, routeSeq?: number, me?: object, hasRole?: (min: string) => boolean, can?: (cap: string) => boolean }} ctx
 */
async function renderSettingsOverviewPage(view, ctx = {}) {
  const me = ctx.me || state.me || {};
  const hasRoleFn = ctx.hasRole || hasRole;
  const canFn = ctx.can || can;
  const activeNavId = ctx.activeNavId || "hub";
  const routeSeq = ctx.routeSeq != null ? ctx.routeSeq : state.routeSeq;

  if (!hasRoleFn("user")) {
    mountView(view, `<div class="card"><p class="muted">Sign in required.</p></div>`);
    return;
  }

  ensureAccountSettingsStyles();
  const roleNorm = String(me.role || "").trim().toLowerCase();
  const roleLabels = { superadmin: "Super Admin", admin: "Admin", user: "User", sa_staff: "SA Staff" };
  const roleLabel = escapeHtml(roleLabels[roleNorm] || me.role || "—");
  const username = escapeHtml(me.username || "—");

  const sections = [];
  sections.push(buildCapabilityOverviewHtml(me.capability_catalog || []));
  const showAdmin = isAdminPanelVisible(me, hasRoleFn, canFn);
  const showBilling = isBillingPanelVisible(me, hasRoleFn, canFn);
  const showVoice = isVoicePanelVisible(me, hasRoleFn, canFn);
  const showTelegram = hasRoleFn("user");

  if (showAdmin) {
    const adminHtml = await buildAdminPanelHtml(me);
    sections.push(`<div class="settings-overview-sec" id="settings-sec-admin" data-settings-section="admin">
      <div class="glass-panel glass-panel--panel acc-settings-panel settings-overview-sec__head">
        <p class="acc-settings-panel__title">Administration</p>
        <h3 class="acc-settings-panel__head">Admin &amp; users</h3>
      </div>
      ${adminHtml}
    </div>`);
  }
  if (showBilling) {
    sections.push(buildBillingPanelHtml());
  }
  if (showVoice) {
    sections.push(buildVoicePanelHtml());
  }
  if (showTelegram) {
    sections.push(buildTelegramPanelHtml());
  }

  if (!sections.length) {
    sections.push(`<div class="glass-panel glass-panel--panel acc-settings-panel"><p class="muted" style="margin:0">No settings modules available for your role.</p></div>`);
  }

  const navItems = buildSettingsHubNavItems({ me, hasRole: hasRoleFn, can: canFn });
  const navHtml = buildAccountSettingsNavHtml(navItems, activeNavId, true);

  mountView(
    view,
    buildAccountSettingsSubrouteHtml({
      activeNavId,
      me,
      hasRole: hasRoleFn,
      can: canFn,
      roleLabel,
      username,
      contentHtml: `<div class="settings-overview-stack" id="settings-sec-hub">${sections.join("")}</div>`,
      navHtml,
    }),
  );

  const root = view.querySelector(".acc-settings-page") || view;
  wireSettingsHubNav(root, activeNavId);

  if (showAdmin) wireAdminPanel(view);
  if (showBilling) wireBillingPanel(view, routeSeq);
  if (showVoice) wireVoicePanel(view, routeSeq);
  if (showTelegram) wireTelegramPanel(view);

  const scrollId = activeNavId === "hub" ? null : `settings-sec-${activeNavId}`;
  if (scrollId) {
    requestAnimationFrame(() => {
      const el = view.querySelector(`#${scrollId}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }
}



// ── 40-glue.shell.js ─────────────────────────────────────
/**
 * Cross-cutting glue helpers used by every other shell/route file.
 *
 * Concatenated as raw text by scripts/build-dashboard.mjs (NOT an ESM module).
 * Lives in the same IIFE scope as the monolith body, the rest of src/shell/,
 * and src/routes/*.route.js.
 *
 *   • getToken / setToken      Legacy localStorage JWT (now mostly unused;
 *                              session is HttpOnly cookie). setToken("")
 *                              also resets the group-meta sync chain so a
 *                              hard logout cannot replay queued writes.
 *   • roleWeight / hasRole     Numeric role comparison; reads state.me.role.
 *   • can(cap)                 Capability check against state.me.policy[cap].
 *   • isOnline(d)              Device online heuristic (boolean field or
 *                              freshness against OFFLINE_MS).
 *   • toast(msg, kind)         Tiny non-blocking toast surfaced at #toast.
 *
 * Numeric prefix `40-` keeps these after state/api/layout/router so they can
 * reference state without forward-declaring it.
 */

function getToken() { return localStorage.getItem(LS.token) || ""; }
function setToken(t) {
  t ? localStorage.setItem(LS.token, t) : localStorage.removeItem(LS.token);
  if (!t) {
    _groupMetaSyncChain = Promise.resolve();
  }
}

function roleWeight(r) { return ROLE_WEIGHT[r] || 0; }
function hasRole(min) { return state.me && roleWeight(state.me.role) >= roleWeight(min); }
/** Default SPA landing: Home (map + App GPS SoT). ops-live stays SA nav deep-link only. */
function defaultHomeHash() {
  if (!state.me) return "#/login";
  try {
    if (getHomeLandingPreference() === "map" && canUseOpsMap(state.me)) return "#/overview";
  } catch (_) {}
  return "#/overview";
}
function can(cap) { return !!(state.me && state.me.policy && state.me.policy[cap]); }
function isOnline(d) {
  if (typeof d.is_online === "boolean") return d.is_online;
  return Date.now() - Date.parse(d.updated_at || 0) < OFFLINE_MS;
}

function toast(msg, kind) {
  const el = $("#toast");
  if (!el) return;
  el.textContent = String(msg);
  el.className = "toast show " + (kind || "");
  clearTimeout(el._t);
  el._t = setTimeout(() => { el.className = "toast"; }, 3200);
}


// ── 45-groups-hub.shell.js ─────────────────────────────────────
/**
 * #/groups hub (P1+P2) — pure table models for operator / grantee views.
 * Depends on ``canonicalGroupKey``, ``buildGroupSlotsFromDeviceList``, ``groupCardMetaKey``, ``displayGroupName``.
 */
/**
 * Mirrors ``authz.principal_sees_device_custody`` (server) for SPA gating.
 * Keep in sync: ``superadmin``; ``sa_staff`` + ``sa_device_admin`` (via ``has_sa_capability`` → includes
 * ``sa_full_access``); or ``sa_staff`` + ``sa_ota_admin``. If server authz changes, update here + smoke.
 */
function principalSeesCustodyDashboard(me) {
  if (!me) return false;
  if (me.role === "superadmin") return true;
  if (me.role !== "sa_staff") return false;
  const c = new Set((me.sa_capabilities || []).map(String));
  return c.has("sa_full_access") || c.has("sa_device_admin") || c.has("sa_ota_admin");
}

function custodyPairMapFromSettings(settingsItems) {
  const m = new Map();
  for (const it of Array.isArray(settingsItems) ? settingsItems : []) {
    const oa = String(it.owner_admin || "").trim();
    const gk = canonicalGroupKey(it.group_key || "");
    if (!oa || !gk || !it.custody || typeof it.custody !== "object") continue;
    const gr = it.custody.grantees;
    if (!Array.isArray(gr) || !gr.length) continue;
    m.set(`${oa}\t${gk}`, it.custody);
  }
  return m;
}

function granteesRichForHubRow(_mapKey, ngGrantees, custodyObj) {
  if (custodyObj && Array.isArray(custodyObj.grantees) && custodyObj.grantees.length) {
    return custodyObj.grantees
      .map((x) => ({
        username: String(x.username || x.grantee_username || "").trim(),
        can_view: !!x.can_view,
        can_operate: !!x.can_operate,
        via: String(x.via || "device_acl"),
      }))
      .filter((x) => x.username);
  }
  return (Array.isArray(ngGrantees) ? ngGrantees : [])
    .map((x) => ({
      username: String(x.grantee_username || "").trim(),
      can_view: !!x.can_view,
      can_operate: !!x.can_operate,
      via: "group_share",
    }))
    .filter((x) => x.username);
}
function clusterNgSharesByOwnerGroup(ngItems) {
  const by = new Map();
  for (const it of Array.isArray(ngItems) ? ngItems : []) {
    if (!it || it.revoked_at) continue;
    const oa = String(it.owner_admin || "").trim();
    const gk = String(it.group_key || "").trim();
    if (!oa || !gk) continue;
    const k = `${oa}\t${gk}`;
    const g = String(it.grantee_username || "").trim();
    if (!g) continue;
    if (!by.has(k)) by.set(k, []);
    by.get(k).push({ grantee_username: g, can_view: !!it.can_view, can_operate: !!it.can_operate });
  }
  for (const [k, arr] of by.entries()) {
    const merged = new Map();
    for (const r of arr) {
      const ex = merged.get(r.grantee_username) || { grantee_username: r.grantee_username, can_view: false, can_operate: false };
      ex.can_view = ex.can_view || r.can_view;
      ex.can_operate = ex.can_operate || r.can_operate;
      merged.set(r.grantee_username, ex);
    }
    by.set(k, [...merged.values()].sort((a, b) => a.grantee_username.localeCompare(b.grantee_username)));
  }
  return by;
}

function deviceHubGroupKey(d) {
  const sgk = String((d && d.shared_group_key) || "").trim();
  if (sgk) return canonicalGroupKey(sgk);
  return canonicalGroupKey(d && d.notification_group);
}

function countDevicesInHubSlot(slot, devices) {
  const gk = slot.groupKey;
  const isSuper = state.me && state.me.role === "superadmin";
  const wantOa = String(slot.tenantOwner || "").trim();
  let n = 0;
  for (const d of Array.isArray(devices) ? devices : []) {
    if (deviceHubGroupKey(d) !== gk) continue;
    if (isSuper) {
      if (String(d.owner_admin || "").trim() === wantOa) n++;
    } else if (d.is_shared) {
      if (String(d.shared_by || "").trim() === wantOa) n++;
    } else if (!wantOa) n++;
  }
  return n;
}

function firstRawGroupLabelForSlot(slot, devices) {
  const gk = slot.groupKey;
  const isSuper = state.me && state.me.role === "superadmin";
  const wantOa = String(slot.tenantOwner || "").trim();
  for (const d of Array.isArray(devices) ? devices : []) {
    if (deviceHubGroupKey(d) !== gk) continue;
    if (isSuper) {
      if (String(d.owner_admin || "").trim() !== wantOa) continue;
    } else if (d.is_shared) {
      if (String(d.shared_by || "").trim() !== wantOa) continue;
    } else if (wantOa) continue;
    const raw = String(d.notification_group || "").trim();
    if (raw) return raw;
  }
  return gk;
}

/** Owner admin key aligned with ``notification_group_shares`` for lookup (admin own-card slots use ``tenantOwner`` ""). */
function ownerAdminForNgLookup(slot, devices) {
  const isSuper = state.me && state.me.role === "superadmin";
  if (isSuper) return String(slot.tenantOwner || "").trim();
  const t = String(slot.tenantOwner || "").trim();
  if (t) return t;
  const gk = slot.groupKey;
  for (const d of Array.isArray(devices) ? devices : []) {
    if (canonicalGroupKey(d.notification_group) !== gk) continue;
    if (!d.is_shared) return String(d.owner_admin || "").trim();
  }
  return "";
}

/**
 * P3: operator rows from ``GET /admin/groups-hub/summary`` (snake_case items). ``owner_scope`` is server-side;
 * no client-side ``ownerQuery`` filter here.
 */
function buildGroupsHubOperatorRowsFromSummary(summaryItems, meta, settingsItems, filter) {
  const isSuper = state.me && state.me.role === "superadmin";
  const custodyMap = custodyPairMapFromSettings(settingsItems);
  const filt = filter === "shared" || filter === "unshared" ? filter : "all";
  const metaObj = meta && typeof meta === "object" ? meta : {};
  const rows = [];
  for (const it of Array.isArray(summaryItems) ? summaryItems : []) {
    const oa = String(it.owner_admin || "").trim();
    const gk = canonicalGroupKey(it.group_key || "");
    if (!gk) continue;
    const hasNg = !!it.has_group_share;
    if (filt === "shared" && !hasNg) continue;
    if (filt === "unshared" && hasNg) continue;
    const grantees = Array.isArray(it.grantees) ? it.grantees : [];
    const mapKey = `${oa}\t${gk}`;
    const rawLabel = String(it.group_display_sample || "").trim() || gk;
    const mk = groupCardMetaKey(gk, isSuper ? oa : "");
    const disp = (metaObj[mk] || metaObj[gk] || {}).display_name || displayGroupName(rawLabel) || gk;
    const custodyObj = custodyMap.get(mapKey);
    const granteesRich = granteesRichForHubRow(mapKey, grantees, custodyObj);
    const nDev = Number(it.device_count) || 0;
    rows.push({
      metaKey: mk,
      groupKey: gk,
      tenantOwner: isSuper ? oa : "",
      ownerAdminForApi: oa,
      displayName: disp,
      groupDisplayForApi: rawLabel,
      deviceCount: nDev,
      grantees,
      granteesRich,
      hasGroupShare: hasNg,
    });
  }
  return rows.sort((a, b) => a.displayName.localeCompare(b.displayName));
}

function buildGroupsHubOperatorRows(devices, meta, ngItems, filter, ownerQuery, settingsItems) {
  const isSuper = state.me && state.me.role === "superadmin";
  let devIn = Array.isArray(devices) ? devices : [];
  const oq = String(ownerQuery || "").trim();
  const saFleetScope = isSuper || (state.me && state.me.role === "sa_staff" && principalSeesCustodyDashboard(state.me));
  if (saFleetScope && oq) devIn = devIn.filter((d) => String(d.owner_admin || "").trim() === oq);
  const slots = buildGroupSlotsFromDeviceList(devIn);
  const ngMap = clusterNgSharesByOwnerGroup(ngItems);
  const custodyMap = custodyPairMapFromSettings(settingsItems);
  const filt = filter === "shared" || filter === "unshared" ? filter : "all";
  const metaObj = meta && typeof meta === "object" ? meta : {};
  const rows = [];
  for (const slot of slots) {
    const gk = slot.groupKey;
    const mk = slot.metaKey;
    const oaNg = ownerAdminForNgLookup(slot, devIn);
    const mapKey = `${oaNg}\t${gk}`;
    const grantees = ngMap.get(mapKey) || [];
    const hasNg = grantees.length > 0;
    if (filt === "shared" && !hasNg) continue;
    if (filt === "unshared" && hasNg) continue;
    const nDev = countDevicesInHubSlot(slot, devIn);
    const rawLabel = firstRawGroupLabelForSlot(slot, devIn);
    const disp = (metaObj[mk] || metaObj[gk] || {}).display_name || displayGroupName(rawLabel) || gk;
    const custodyObj = custodyMap.get(mapKey);
    const granteesRich = granteesRichForHubRow(mapKey, grantees, custodyObj);
    rows.push({
      metaKey: mk,
      groupKey: gk,
      tenantOwner: String(slot.tenantOwner || "").trim(),
      ownerAdminForApi: oaNg,
      displayName: disp,
      groupDisplayForApi: rawLabel,
      deviceCount: nDev,
      grantees,
      granteesRich,
      hasGroupShare: hasNg,
    });
  }
  return rows.sort((a, b) => a.displayName.localeCompare(b.displayName));
}

function buildGroupsHubGranteeRows(devices, meta) {
  const devIn = (Array.isArray(devices) ? devices : []).filter((d) => d && d.is_shared);
  const slots = buildGroupSlotsFromDeviceList(devIn);
  const metaObj = meta && typeof meta === "object" ? meta : {};
  const rows = [];
  for (const slot of slots) {
    const gk = slot.groupKey;
    const mk = slot.metaKey;
    const owner = String(slot.tenantOwner || "").trim();
    const nDev = countDevicesInHubSlot(slot, devIn);
    const rawLabel = firstRawGroupLabelForSlot(slot, devIn);
    const disp = (metaObj[mk] || metaObj[gk] || {}).display_name || displayGroupName(rawLabel) || gk;
    rows.push({
      metaKey: mk,
      groupKey: gk,
      sharedByOwner: owner,
      displayName: disp,
      groupUrlRaw: rawLabel,
      deviceCount: nDev,
    });
  }
  return rows.sort((a, b) => a.displayName.localeCompare(b.displayName));
}



// ============================================================
// === Routes spliced from src/routes/*.route.js ==============
// ============================================================

// ── account-activate.route.js ─────────────────────────────────────
/**
 * Route: #/account-activate — admin-issued account activation flow.
 *
 * Build mechanism: this file is *not* an ES module. It's read as raw text by
 * `scripts/build-dashboard.mjs` and concatenated after the spliced
 * `console.raw.js` body, so it shares scope with the monolith and can call
 * `registerRoute`, `mountView`, `setCrumb`, `apiBase`, `$`, etc. directly.
 * Do NOT add `import` or `export` here — esbuild would treat it as a separate
 * module and lose the shared scope.
 *
 * Smoke + tests:
 *   - `scripts/smoke-routes.mjs` confirms `registerRoute("account-activate")`
 *     still appears in the bundle and matches the route manifest.
 *   - `tests/test_spa_api_contract.py` confirms `/auth/activate` and
 *     `/auth/code/resend` still hit a real FastAPI route.
 */

registerRoute("account-activate", async (view) => {
  setCrumb("Activate account");
  document.body.dataset.auth = "none";
  activateCommandCenterAuthLayout();
  mountView(view, buildCommandCenterAuthSurfaceHtml("activate", `
          <div class="auth-card auth-card--panel auth-card--auth-main auth-card--wide" data-auth-card>
            <header class="auth-card__head">
              <h1 class="auth-card__title">Activate account</h1>
              <p class="auth-card__lead">Confirm your phone, then enter the email code</p>
            </header>
            <div class="auth-card__body">
              <ol class="auth-card__note muted" style="margin:0 0 14px;padding-left:1.2em;line-height:1.45">
                <li>Enter your <strong>username</strong>.</li>
                <li>Tap <strong>Call my phone</strong> and answer — that confirms the number on file.</li>
                <li>Enter the <strong>email code</strong>, then Activate.</li>
              </ol>
              <label class="field"><span>Username</span><input id="a_user" autocomplete="username" placeholder="Your username"/></label>
              <label class="field field--spaced"><span>Email code</span><input id="a_email_code" inputmode="numeric" maxlength="12" autocomplete="one-time-code" placeholder="From email"/></label>
              <p class="muted" id="a_otp_expiry" style="margin:6px 0 0" aria-live="polite"></p>
              <div class="auth-card__submit">
                <button class="btn btn-tap btn-block auth-btn-primary" type="button" id="a_submit">Activate</button>
                <button class="btn secondary btn-tap btn-block" type="button" id="a_phone">Call my phone</button>
                <button class="btn secondary btn-tap btn-block" type="button" id="a_resend">Resend email code</button>
                <a class="auth-link auth-link--center" href="#/login">Back to sign in</a>
              </div>
              <p class="auth-card__msg muted" id="a_msg" aria-live="polite"></p>
            </div>
          </div>`));
  const msg = $("#a_msg");
  let disposeActivateOtpUi = () => {};
  const soft = (e) => (typeof softApiError === "function" ? softApiError(e) : String(e.message || e));

  $("#a_submit").addEventListener("click", async () => {
    const body = {
      username: $("#a_user").value.trim(),
      email_code: $("#a_email_code").value.trim(),
    };
    msg.textContent = "";
    try {
      const r = await fetch(apiBase() + buildPostAuthActivatePath(), {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || `${r.status}`);
      setChildMarkup(msg, `<span class="badge online">Activated</span> Redirecting to sign in…`);
      scheduleRouteRedirect(1500, "#/login");
    } catch (e) { msg.textContent = soft(e); }
  });

  $("#a_phone").addEventListener("click", async () => {
    msg.textContent = "";
    const username = $("#a_user").value.trim();
    if (!username) { msg.textContent = "Enter username first"; return; }
    try {
      const r = await fetch(apiBase() + buildPostAuthCodeResendPath(), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, channel: "phone", purpose: "activate" }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || `${r.status}`);
      msg.textContent = "Calling the phone on file — answer to confirm, then Activate with your email code.";
    } catch (e) { msg.textContent = soft(e); }
  });

  $("#a_resend").addEventListener("click", async () => {
    msg.textContent = "";
    const username = $("#a_user").value.trim();
    if (!username) { msg.textContent = "Enter username first"; return; }
    try {
      const r = await fetch(apiBase() + buildPostAuthCodeResendPath(), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, channel: "email", purpose: "activate" }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || `${r.status}`);
      disposeActivateOtpUi();
      const d1 = startEmailCodeExpiryCountdown($("#a_otp_expiry"), j.ttl_seconds);
      const d2 = startAuthResendCooldown($("#a_resend"), Number(j.resend_after_seconds || 60), "Resend email code");
      disposeActivateOtpUi = () => {
        try { d1(); } catch (_) {}
        try { d2(); } catch (_) {}
      };
      msg.textContent = "New email code requested — check inbox and spam.";
    } catch (e) { msg.textContent = soft(e); }
  });
});


// ── account-cameras.route.js ─────────────────────────────────────
/**
 * Route: #/account/cameras — CAM-S4 / FE-A4 account camera fleet hub.
 * Catalog CRUD + device bind inspector. ONVIF discovery stays on #/devices/:id.
 */

registerRoute("account-cameras", async (view, _args, routeSeq) => {
  setCrumb("Cameras");
  if (!state.me) {
    location.hash = "#/login";
    return;
  }
  if (!hasRole("admin")) {
    mountView(view, `<div class="card glass-panel glass-panel--panel"><p class="muted">Admins only.</p></div>`);
    return;
  }
  if (!state.me.camera_snapshot_enabled) {
    mountView(
      view,
      capabilityUnavailableHtml({
        title: "Cameras",
        detail: "Camera tools are not opened for this account yet.",
        backHref: "#/settings",
      }),
    );
    return;
  }
  if (!isRouteCurrent(routeSeq)) return;

  const isSuper = !!(state.me && state.me.role === "superadmin");
  const ownerAdmin = isSuper ? String((window.__routeQuery && window.__routeQuery.get("owner_admin")) || "").trim() : "";

  if (isSuper && !ownerAdmin) {
    mountView(view, `<div class="card glass-panel glass-panel--panel">
      <h2 style="margin:0">Cameras</h2>
      <p class="muted" style="margin:8px 0 0;line-height:1.45;max-width:40rem">
        Platform staff manage cameras under an account owner. Enter the owner username to open that account.
      </p>
      <div class="inline-form" style="margin-top:14px;max-width:28rem">
        <label class="field wide"><span>Account owner</span>
          <input id="camFleetOwnerPick" class="mono" placeholder="account owner username" maxlength="64" autocomplete="off" />
        </label>
      </div>
      <div class="row" style="margin-top:10px;gap:8px">
        <button type="button" class="btn btn-tap" id="camFleetOwnerGo">Open cameras</button>
        <button type="button" class="btn secondary btn-tap" data-cc-go="snapshots">Evidence</button>
      </div>
      <p id="camFleetOwnerMsg" class="muted" style="margin-top:8px" aria-live="polite"></p>
    </div>`);
    const go = $("#camFleetOwnerGo", view);
    const inp = $("#camFleetOwnerPick", view);
    const msg = $("#camFleetOwnerMsg", view);
    const run = () => {
      const o = String((inp && inp.value) || "").trim();
      if (!o) {
        if (msg) msg.textContent = "Account owner is required.";
        return;
      }
      location.hash = `#/account/cameras?owner_admin=${encodeURIComponent(o)}`;
    };
    if (go) go.onclick = run;
    if (inp) inp.addEventListener("keydown", (ev) => { if (ev.key === "Enter") { ev.preventDefault(); run(); } });
    return;
  }

  mountView(view, camerasFleetHubShellHtml({ escapeHtml }));
  wireCamerasFleetHub(view, {
    ownerAdmin,
    routeSeq,
    isRouteCurrent,
    api,
    toast,
    escapeHtml,
  });
});


// ── account.route.js ─────────────────────────────────────
/**
 * Route: #/account — Logged-in user account & sessions panel.
 *
 * Build: split out of src/console.raw.js and concatenated as raw text by
 * scripts/build-dashboard.mjs after the monolith body. Shares scope with
 * helpers like $, mountView, api, registerRoute, state, toast, can, setCrumb
 * (defined in console.raw.js and the lib/ modules spliced at the top).
 */

registerRoute("account", async (view) => {
  setCrumb("Account");
  if (!hasRole("user")) { mountView(view, `<div class="card"><p class="muted">Sign in required.</p></div>`); return; }
  ensureAccountSettingsStyles();
  let disposeAccNav = () => {};
  const me = state.me || { username: "", role: "" };
  const regEmail = String(me.email || "").trim();
  const hasAv = !!me.has_avatar;
  const rev = Number(me.avatar_rev) || 0;
  const uname0 = (String(me.username || "?").trim() || "?")[0].toUpperCase();
  const roleNorm = String(me.role || "").trim().toLowerCase();
  const roleLabels = { superadmin: "Super Admin", admin: "Admin", user: "User", sa_staff: "SA Staff" };
  const roleLabel = escapeHtml(roleLabels[roleNorm] || me.role || "—");
  const deleteSection = (() => {
    if (roleNorm === "superadmin") {
      return `
    <div class="glass-panel glass-panel--panel acc-settings-panel acc-settings-danger" id="acc-sec-danger">
      <p class="acc-settings-panel__title">Danger zone</p>
      <h3 class="acc-settings-panel__head">Delete account</h3>
      <p class="muted">Superadmin accounts cannot be removed through this console (API blocks self-deletion).</p>
    </div>`;
    }
    if (roleNorm === "admin") {
      return `
    <details class="glass-panel glass-panel--panel acc-settings-panel acc-settings-danger" id="acc-sec-danger">
      <summary>Danger zone · Close organization</summary>
      <div style="margin-top:12px">
      <h3 class="acc-settings-panel__head">Close organization account</h3>
      <p class="muted" style="margin:0 0 10px">If you close this admin organization:</p>
      <ul class="muted" style="margin:0 0 12px;padding-left:1.25em;line-height:1.55">
        <li><strong>Boards</strong> you own are <strong>released</strong> (unclaimed).</li>
        <li><strong>Team users</strong> under your account are <strong>removed</strong>.</li>
        <li>Your <strong>username</strong> and <strong>email</strong> become available for new registration.</li>
      </ul>
      <label class="checkbox" style="margin-bottom:12px;display:flex;gap:8px;align-items:flex-start">
        <input id="accAckTenant" type="checkbox" />
        <span>I understand all boards under this organization will be released and team users removed.</span>
      </label>
      <label class="field"><span>Current password</span><input id="accDelPw" type="password" autocomplete="current-password"/></label>
      <label class="field field--spaced"><span>Type DELETE</span><input id="accDelText" placeholder="DELETE"/></label>
      <div class="row" style="justify-content:flex-end;margin-top:10px">
        <button class="btn danger" id="accDeleteSelf">Close organization permanently</button>
      </div>
      </div>
    </details>`;
    }
    return `
    <details class="glass-panel glass-panel--panel acc-settings-panel acc-settings-danger" id="acc-sec-danger">
      <summary>Danger zone · Delete account</summary>
      <div style="margin-top:12px">
      <h3 class="acc-settings-panel__head">Delete account</h3>
      <p class="muted">This action is irreversible. Type <span class="mono">DELETE</span> and confirm your password.</p>
      <label class="field"><span>Current password</span><input id="accDelPw" type="password" autocomplete="current-password"/></label>
      <label class="field field--spaced"><span>Type DELETE</span><input id="accDelText" placeholder="DELETE"/></label>
      <div class="row" style="justify-content:flex-end;margin-top:10px">
        <button class="btn danger" id="accDeleteSelf">Delete my account</button>
      </div>
      </div>
    </details>`;
  })();
  const previewInner = hasAv
    ? `<img src="${escapeHtml(meAvatarImgSrc(rev))}" alt="" width="88" height="88" loading="lazy" decoding="async" referrerpolicy="no-referrer" />`
    : `<span class="account-avatar-fallback" aria-hidden="true">${escapeHtml(uname0)}</span>`;
  const showTenantDeleg =
    roleNorm === "admin" && can("can_manage_users") && me.tenant_acl_enabled;
  const showSubAdminScopes =
    showTenantDeleg && !String(me.manager_admin || "").trim();
  const isChildAdmin =
    roleNorm === "admin" && !!String(me.is_child_admin || me.manager_admin || "").trim();
  const parentAdmin = String(me.parent_admin_username || me.manager_admin || "").trim();
  const childScopesCard = isChildAdmin
    ? `<div id="accChildScopesCard" class="glass-panel glass-panel--panel acc-settings-panel">
        <p class="acc-settings-panel__title">Access scope</p>
        <h3 class="acc-settings-panel__head">Allowed end-users</h3>
        <p class="muted" style="margin:0 0 8px;font-size:13px">Parent <span class="mono">${escapeHtml(parentAdmin)}</span> granted you access to manage these users and their organization boards.</p>
        <div id="accChildScopesList"><p class="muted">Loading…</p></div>
      </div>`
    : "";
  const childAdminBanner = isChildAdmin
    ? `<div class="glass-panel glass-panel--panel acc-settings-panel">
        <p class="muted" style="margin:0;font-size:13px;line-height:1.5">
          <strong>Child admin</strong> under parent <span class="mono">${escapeHtml(parentAdmin)}</span>.
          Boards appear only when shared to a whitelisted end-user.
        </p>
      </div>`
    : "";
  const tenantLinksHtml = showTenantDeleg && !isChildAdmin
    ? `<div class="glass-panel glass-panel--panel acc-settings-panel">
        <p class="acc-settings-panel__title">Organization admin</p>
        <h3 class="acc-settings-panel__head">Quick links</h3>
        <div class="acc-settings-links">
          <button type="button" data-cc-go="tenant-delegations">Staff delegations <span aria-hidden="true">›</span></button>
          ${showSubAdminScopes ? `<button type="button" data-cc-go="admin-user-scopes">Child admin access <span aria-hidden="true">›</span></button>` : ""}
        </div>
      </div>`
    : "";

  const profileSection = `
    <div class="glass-panel glass-panel--panel acc-settings-panel" id="acc-sec-profile">
      <p class="acc-settings-panel__title">Profile</p>
      <h3 class="acc-settings-panel__head">My account</h3>
      <div class="acc-settings-profile-layout">
        <div class="acc-settings-avatar-block">
          <div class="account-avatar-preview" id="accAvatarPreview" aria-hidden="true">${previewInner}</div>
          <input type="file" id="accAvatarFile" accept="image/jpeg,image/png,.jpg,.jpeg,.png" style="display:none" />
          <div class="row" style="justify-content:center;gap:6px;flex-wrap:wrap;width:100%">
            <button class="btn secondary btn-tap sm" type="button" id="accAvatarPick">Photo</button>
            <button class="btn ghost btn-tap sm" type="button" id="accAvatarClear">Initial</button>
          </div>
        </div>
        <div class="acc-settings-meta-grid">
          <label class="field"><span>Username</span><input class="acc-settings-readonly" value="${escapeHtml(me.username)}" disabled /></label>
          <label class="field"><span>Role</span><input class="acc-settings-readonly" value="${roleLabel}" disabled /></label>
          <label class="field" style="grid-column:1/-1"><span>Email</span><input class="acc-settings-readonly" value="${escapeHtml(regEmail || "(none on file)")}" disabled /></label>
          ${regEmail && me.email_verified_at ? `<p class="muted" style="margin:0;grid-column:1/-1;font-size:12px"><span class="badge online">Email verified</span></p>` : ""}
        </div>
      </div>
      <p class="muted" style="margin:14px 0 0;font-size:13px;line-height:1.45">Upload <strong>JPEG</strong> or <strong>PNG</strong> (max ~256&nbsp;KB). Server re-encodes before publish.</p>
      ${me.avatar_upload_pending ? `<p class="muted" style="margin:8px 0 0;font-size:13px">Queued for verification — typically ready within ~5&nbsp;minutes.</p>` : ""}
      ${me.avatar_upload_notice ? `<p class="badge revoked" style="margin:8px 0 0;display:inline-block">${escapeHtml(String(me.avatar_upload_notice))}</p>` : ""}
    </div>`;
  const notificationsSection = `
    <div class="glass-panel glass-panel--panel acc-settings-panel acc-settings-panel--compact" id="acc-sec-notifications">
      <p class="acc-settings-panel__title">Notifications</p>
      <h3 class="acc-settings-panel__head">Alarm emails</h3>
      <p class="muted" style="margin:0 0 12px;font-size:13px;line-height:1.45">Optional extra addresses for board alarms. Your registered email is always included when set.</p>
      <div class="inline-form">
        <label class="field wide"><span>Email</span><input id="uar_email" type="email" autocomplete="off" placeholder="you@example.com"/></label>
        <label class="field"><span>Label</span><input id="uar_label" autocomplete="off" placeholder="phone"/></label>
        <label class="field"><span>6-digit code</span><input id="uar_code" inputmode="numeric" autocomplete="one-time-code" maxlength="8" placeholder="from email"/></label>
        <div class="row wide" style="justify-content:flex-end;flex-wrap:wrap;gap:8px">
          <button class="btn secondary btn-tap" type="button" id="uar_send_code">Send code</button>
          <button class="btn btn-tap" type="button" id="uar_add">Verify &amp; add</button>
        </div>
      </div>
      <p class="muted" id="uar_otp_expiry" style="margin:8px 0 0;font-size:13px"></p>
      <div id="uarList" style="margin-top:12px"><p class="muted">Loading…</p></div>
    </div>`;
  const securitySection = `
    <div class="glass-panel glass-panel--panel acc-settings-panel" id="acc-sec-security">
      <p class="acc-settings-panel__title">Security</p>
      <h3 class="acc-settings-panel__head">Change password</h3>
      <div class="acc-settings-meta-grid" style="max-width:480px">
        <label class="field" style="grid-column:1/-1"><span>Current password</span><input id="acc_old" type="password" autocomplete="current-password"/></label>
        <label class="field"><span>New password</span><input id="acc_new1" type="password" autocomplete="new-password"/></label>
        <label class="field"><span>Confirm</span><input id="acc_new2" type="password" autocomplete="new-password"/></label>
      </div>
      <div class="row" style="justify-content:flex-end;margin-top:12px">
        <button class="btn btn-tap" id="accChangePw">Update password</button>
      </div>
    </div>`;
  const profileColHtml = `
    ${childAdminBanner}
    ${childScopesCard}
    ${profileSection}
    ${tenantLinksHtml}`;
  const midColHtml = `
    ${notificationsSection}
    ${securitySection}
    ${deleteSection}`;
  mountView(view, buildAccountSettingsShellHtml({
    roleLabel,
    username: escapeHtml(me.username),
    noSidebar: true,
    navHtml: "",
    profileColHtml,
    midColHtml,
  }));
  try { disposeAccNav(); } catch (_) {}
  disposeAccNav = wireAccountSettingsNav(view.querySelector(".acc-settings-page") || view);
  if (isChildAdmin) {
    const listEl = $("#accChildScopesList", view);
    if (listEl) {
      try {
        const scopeData = await api(buildAdminUserScopesMePath());
        const rows = (scopeData && scopeData.items) || [];
        if (!rows.length) {
          setChildMarkup(
            listEl,
            '<p class="muted">No end-users on your whitelist yet. Ask the parent admin under <strong>Child admin access</strong>.</p>',
          );
        } else {
          const html =
            '<ul style="margin:0;padding-left:1.25em;line-height:1.6">' +
            rows
              .map((r) => {
                const u = escapeHtml(String(r.user_username || ""));
                const em = r.email
                  ? ` <span class="muted">(${escapeHtml(String(r.email))})</span>`
                  : "";
                return `<li><span class="mono">${u}</span>${em}</li>`;
              })
              .join("") +
            "</ul>";
          setChildMarkup(listEl, html);
        }
      } catch (e) {
        setChildMarkup(listEl, `<p class="badge revoked">${escapeHtml(e.message || String(e))}</p>`);
      }
    }
  }
  const accPre = $("#accAvatarPreview", view);
  const setAccPreview = () => {
    if (!accPre) return;
    const m = state.me || {};
    const hv = !!m.has_avatar;
    const r = Number(m.avatar_rev) || 0;
    if (!hv) {
      setChildMarkup(accPre, `<span class="account-avatar-fallback" aria-hidden="true">${escapeHtml(uname0)}</span>`);
      return;
    }
    setChildMarkup(
      accPre,
      `<img src="${escapeHtml(meAvatarImgSrc(r))}" alt="" width="48" height="48" loading="lazy" decoding="async" referrerpolicy="no-referrer" />`,
    );
    const g = accPre.querySelector("img");
    if (g) {
      g.addEventListener(
        "error",
        () => {
          setChildMarkup(
            accPre,
            `<span class="account-avatar-fallback account-avatar-fallback--err" title="Image failed to load" aria-hidden="true">${escapeHtml(uname0)}</span>`,
          );
        },
        { once: true },
      );
    }
  };
  const accFile = $("#accAvatarFile", view);
  const AVATAR_MAX = 262144;
  const accAvatarPick = $("#accAvatarPick", view);
  if (accAvatarPick) {
    accAvatarPick.addEventListener("click", () => {
      if (accFile) accFile.click();
    });
  }
  if (accFile) {
    accFile.addEventListener("change", async () => {
      const f = accFile.files && accFile.files[0];
      if (!f) return;
      if (f.size > AVATAR_MAX) {
        toast(`File too large (max ${Math.round(AVATAR_MAX / 1024)} KB)`, "err");
        accFile.value = "";
        return;
      }
      const okType = /^image\/(jpeg|png)$/i.test(f.type || "");
      const okName = /\.(jpe?g|png)$/i.test(f.name || "");
      if (!okType && !okName) {
        toast("Use JPEG or PNG only", "err");
        accFile.value = "";
        return;
      }
      try {
        const fd = new FormData();
        fd.append("file", f, f.name || "avatar");
        const r = await api(buildPostAuthMeAvatarUploadPath(), {
          method: "POST",
          body: fd,
          timeoutMs: ACCOUNT_AVATAR_UPLOAD_TIMEOUT_MS,
        });
        await loadMe();
        renderAuthState();
        setAccPreview();
        const pend = !!(state.me && state.me.avatar_upload_pending);
        const esc = Math.ceil(Number(r && r.estimated_sec) / 60);
        const mins = Number.isFinite(esc) && esc > 0 ? esc : 5;
        toast(
          pend
            ? `Queued — avatar publishes after server verification (~${mins} min if accepted).`
            : "Photo uploaded",
          "ok",
        );
      } catch (e) {
        toast(e.message || e, "err");
      } finally {
        accFile.value = "";
      }
    });
  }
  const accAvatarClear = $("#accAvatarClear", view);
  if (accAvatarClear) {
    accAvatarClear.addEventListener("click", async () => {
      try {
        const r = await api(buildPatchAuthMeProfilePath(), { method: "PATCH", body: { avatar_url: "" } });
      if (state.me) {
        state.me.has_avatar = !!(r && r.has_avatar);
        state.me.avatar_rev = r && r.avatar_rev != null ? Number(r.avatar_rev) : 0;
      }
      renderAuthState();
      setAccPreview();
      toast("Using initial letter", "ok");
    } catch (e) {
      toast(e.message || e, "err");
    }
  });
  }
  const accChangePwBtn = $("#accChangePw", view);
  if (accChangePwBtn) {
    accChangePwBtn.addEventListener("click", async () => {
    try {
      await api(buildPatchAuthMePasswordPath(), {
        method: "PATCH",
        body: {
          current_password: ($("#acc_old", view).value || ""),
          new_password: ($("#acc_new1", view).value || ""),
          new_password_confirm: ($("#acc_new2", view).value || ""),
        },
      });
      toast("Password updated — please sign in again.", "ok");
      setToken("");
      state.me = null;
      clearHealthPollTimer();
      renderAuthState();
      location.hash = "#/login";
    } catch (e) { toast(e.message || e, "err"); }
    });
  }
  const accDeleteSelfBtn = $("#accDeleteSelf", view);
  if (accDeleteSelfBtn) {
    accDeleteSelfBtn.addEventListener("click", async () => {
    if (roleNorm === "superadmin") return;
    const msg = roleNorm === "admin"
      ? "Close this admin tenant permanently? All owned boards will be factory-unclaimed and sub-users deleted."
      : "Delete your account permanently?";
    if (!confirm(msg)) return;
    if (roleNorm === "admin") {
      const ack = $("#accAckTenant", view);
      if (!ack || !ack.checked) {
        toast("Confirm the checklist: boards will be released and sub-users removed.", "err");
        return;
      }
    }
    try {
      const body = {
        password: ($("#accDelPw", view).value || ""),
        confirm_text: ($("#accDelText", view).value || "").trim(),
        acknowledge_admin_tenant_closure: roleNorm === "admin",
      };
      await api(buildPostAuthMeDeletePath(), { method: "POST", body });
      toast(roleNorm === "admin" ? "Organization account closed" : "Account deleted", "ok");
      setToken("");
      state.me = null;
      clearHealthPollTimer();
      location.hash = "#/login";
      renderAuthState();
    } catch (e) {
      const msg = String(e.message || e);
      if (msg.includes("503") || msg.includes("database contended") || msg.includes("System busy")) {
        toast(
          roleNorm === "admin"
            ? "Server busy — close may be incomplete. Wait ~30s, then retry once."
            : "Server busy — wait ~30s and retry once.",
          "err",
        );
      } else {
        toast(msg, "err");
      }
    }
    });
  }
  const loadUar = async () => {
    const listEl = $("#uarList", view);
    if (!listEl) return;
    try {
      const d = await api(buildAuthMeAlertRecipientsPath(), { timeoutMs: ACCOUNT_CONSOLE_TIMEOUT_MS });
      const items = (d && d.items) || [];
      setChildMarkup(
        listEl,
        items.length === 0
          ? '<p class="muted">No extra addresses — registered email still receives alarms when set.</p>'
          : `<div class="table-wrap"><table class="t"><thead><tr><th>Email</th><th>Label</th><th>Enabled</th><th></th></tr></thead><tbody>${items.map((r) => `
              <tr>
                <td class="mono">${escapeHtml(r.email)}</td>
                <td>${escapeHtml(r.label || "")}</td>
                <td>${r.enabled ? "On" : "Off"}</td>
                <td class="row" style="gap:6px;justify-content:flex-end">
                  <button class="btn sm ghost js-uar-toggle" data-id="${r.id}" data-en="${r.enabled ? "0" : "1"}">${r.enabled ? "Disable" : "Enable"}</button>
                  <button class="btn sm danger js-uar-del" data-id="${r.id}">Delete</button>
                </td>
              </tr>`).join("")}</tbody></table></div>`,
      );
    } catch (e) {
      setChildMarkup(listEl, `<p class="badge revoked">${escapeHtml(e.message || String(e))}</p>`);
    }
  };
  let disposeUarOtp = () => {};
  const uarExpiredMsg = "Verification failed — request a new code";
  $("#uar_send_code", view)?.addEventListener("click", async () => {
    const email = ($("#uar_email", view)?.value || "").trim();
    if (!email) { toast("Enter email", "err"); return; }
    try {
      const j = await api(buildPostAuthMeAlertRecipientsRequestVerifyPath(), {
        method: "POST",
        body: { email },
        timeoutMs: ACCOUNT_CONSOLE_WRITE_TIMEOUT_MS,
        retries: 0,
      });
      disposeUarOtp();
      const d1 = startEmailCodeExpiryCountdown($("#uar_otp_expiry", view), j.ttl_seconds, { expiredMessage: uarExpiredMsg });
      const d2 = startAuthResendCooldown($("#uar_send_code", view), Number(j.resend_after_seconds || 60), "Send code");
      disposeUarOtp = () => { try { d1(); } catch (_) {} try { d2(); } catch (_) {} };
      toast("Code sent — check inbox (5 min)", "ok");
    } catch (e) { toast(e.message || e, "err"); }
  });
  $("#uar_add", view)?.addEventListener("click", async () => {
    const email = ($("#uar_email", view)?.value || "").trim();
    const label = ($("#uar_label", view)?.value || "").trim();
    const code = ($("#uar_code", view)?.value || "").trim();
    if (!email || !code) { toast("Enter email and verification code", "err"); return; }
    try {
      await api(buildPostAuthMeAlertRecipientsPath(), {
        method: "POST",
        body: { email, label, code },
        timeoutMs: ACCOUNT_CONSOLE_WRITE_TIMEOUT_MS,
        retries: 0,
      });
      if ($("#uar_email", view)) $("#uar_email", view).value = "";
      if ($("#uar_label", view)) $("#uar_label", view).value = "";
      if ($("#uar_code", view)) $("#uar_code", view).value = "";
      disposeUarOtp();
      const ex = $("#uar_otp_expiry", view);
      if (ex) ex.textContent = "";
      toast("Added", "ok");
      void loadUar().catch(() => {});
    } catch (e) { toast(e.message || e, "err"); }
  });
  let uarListBusy = false;
  const reloadUarQuiet = () => {
    void loadUar().catch((e) => {
      toast(String(e.message || e), "warn");
    });
  };
  $("#uarList", view)?.addEventListener("click", async (ev) => {
    const del = ev.target.closest(".js-uar-del");
    const tog = ev.target.closest(".js-uar-toggle");
    const btn = del || tog;
    if (!btn || btn.disabled) return;
    const id = String(btn.dataset.id || "").trim();
    if (!id) return;
    if (del) {
      if (uarListBusy) return;
      ev.preventDefault();
      ev.stopPropagation();
      if (!confirm("Remove this alarm email?")) return;
      const tr = btn.closest("tr");
      const prevLabel = btn.textContent;
      uarListBusy = true;
      btn.disabled = true;
      btn.textContent = "Deleting…";
      try {
        await api(buildDeleteAuthMeAlertRecipientPath(id), {
          method: "DELETE",
          timeoutMs: ACCOUNT_CONSOLE_WRITE_TIMEOUT_MS,
          retries: 0,
        });
        toast("Removed", "ok");
        if (tr && tr.parentElement) tr.remove();
      } catch (e) {
        const msg = String(e.message || e);
        if (msg.includes("timed out") || msg.includes("Request timed out")) {
          toast("Delete timed out — API may be stuck; check Network or restart the API service", "err");
        } else if (msg.includes("401")) toast("Session expired — please sign in again", "err");
        else toast(msg, "err");
      } finally {
        uarListBusy = false;
        btn.disabled = false;
        btn.textContent = prevLabel;
      }
    } else if (tog) {
      if (uarListBusy) return;
      ev.preventDefault();
      ev.stopPropagation();
      const en = tog.dataset.en === "1";
      const prevLabel = btn.textContent;
      uarListBusy = true;
      btn.disabled = true;
      btn.textContent = "…";
      try {
        await api(buildPatchAuthMeAlertRecipientPath(id), {
          method: "PATCH",
          body: { enabled: en },
          timeoutMs: ACCOUNT_CONSOLE_WRITE_TIMEOUT_MS,
          retries: 0,
        });
        reloadUarQuiet();
      } catch (e) {
        const msg = String(e.message || e);
        if (msg.includes("401")) toast("Session expired — please sign in again", "err");
        else toast(msg, "err");
      } finally {
        uarListBusy = false;
        btn.disabled = false;
        btn.textContent = prevLabel;
      }
    }
  });
  loadUar();
});


// ── activate.route.js ─────────────────────────────────────
/**
 * Route: #/activate — Admin: activate/claim a device into the tenant.
 *
 * Build: split out of src/console.raw.js and concatenated as raw text by
 * scripts/build-dashboard.mjs after the monolith body. Shares scope with
 * helpers like $, mountView, api, registerRoute, state, toast, can, setCrumb
 * (defined in console.raw.js and the lib/ modules spliced at the top).
 */

registerRoute("activate", async (view) => {
  setCrumb("Activate board");
  if (!hasRole("admin")) { mountView(view, `<div class="card"><p class="muted">Admins only.</p></div>`); return; }
  const me = state.me || {};
  if (me.role === "sa_staff") {
    const c = new Set((me.sa_capabilities || []).map(String));
    if (!c.has("sa_full_access") && !c.has("sa_device_admin")) {
      mountView(view, `<div class="card"><p class="muted">Missing board-admin access for staff accounts.</p></div>`);
      return;
    }
  }
  const canClaim = can("can_claim_device");

  mountView(view, `
    <div class="activate-shell">
      <section class="card glass-panel glass-panel--panel activate-hero">
        <h2 class="activate-title">Claim board</h2>
        <p class="muted activate-lead">
          Enter the factory serial to add this unit to your account — it does not need to be online yet.
          When it comes online, setup completes automatically.
          Optional: save a Wi‑Fi draft in this browser, then send it from the board page after claim.
        </p>
        <ol class="activate-steps">
          <li><span class="n">1</span>Enter sticker serial or scan QR</li>
          <li><span class="n">2</span>Identify → confirm claim</li>
          <li><span class="n">3</span>Board comes online after Wi‑Fi</li>
        </ol>
        ${canClaim ? "" : `<p class="badge revoked" style="margin-top:12px">Your account cannot claim boards yet — ask an administrator.</p>`}
      </section>

      <section class="card glass-panel glass-panel--panel activate-main">
        <div class="activate-wifi-row">
          <button type="button" class="btn secondary btn-tap activate-wifi-btn" id="activateWifiOpenBtn">Wi‑Fi draft</button>
          <p class="muted activate-wifi-status" id="activateWifiStatus"></p>
        </div>
        <div class="inline-form activate-serial-block">
          <label class="field wide"><span>Serial / QR</span>
            <input id="idn_input" class="activate-serial-input" placeholder="SN-… or CROC|…" autocomplete="off"/>
          </label>
          <div class="row wide activate-actions" style="gap:8px;flex-wrap:wrap">
            <button class="btn btn-tap activate-id-btn" id="idn_go" ${canClaim ? "" : "disabled"}>Identify</button>
            <button class="btn secondary btn-tap" type="button" id="idn_scan_qr" ${canClaim ? "" : "disabled"}>Scan QR</button>
            <button class="btn ghost btn-tap" type="button" id="idn_scan_stop" hidden>Stop camera</button>
          </div>
          <p class="muted" id="idn_scan_hint" style="margin:8px 0 0">Optional camera scan (Chrome or Edge recommended). Paste always works.</p>
          <div id="idn_scan_wrap" hidden style="margin-top:10px">
            <video id="idn_scan_video" playsinline muted style="width:100%;max-width:360px;border-radius:8px;background:#111"></video>
          </div>
        </div>
        <div id="idnResult" class="activate-result"></div>
      </section>

      <dialog id="activateWifiDialog" class="activate-wifi-dlg">
        <form class="activate-wifi-dlg__inner" onsubmit="return false">
          <h3 class="activate-wifi-dlg__title">Wi‑Fi draft</h3>
          <p class="muted activate-wifi-dlg__lead">
            Stored in <strong>this browser</strong> only. After claim, use the board page to push to the unit when it is online. Open network: leave password empty.
          </p>
          <label class="field wide"><span>Wi‑Fi name</span>
            <input type="text" id="activateDlgSsid" maxlength="32" autocomplete="off" placeholder="2.4 GHz network name" />
          </label>
          <label class="field wide"><span>Password</span>
            <input type="password" id="activateDlgPass" maxlength="64" autocomplete="new-password" placeholder="Empty if open network" />
          </label>
          <label class="field" style="margin-bottom:0"><span></span>
            <span><input type="checkbox" id="activateDlgShowPass" /> Show password</span>
          </label>
          <div class="activate-wifi-dlg__actions">
            <button type="button" class="btn ghost" id="activateWifiDlgClose">Close</button>
            <button type="button" class="btn" id="activateWifiDlgSave">Save to this browser</button>
            <button type="button" class="btn secondary" id="activateWifiDlgClear">Clear draft</button>
          </div>
        </form>
      </dialog>

      <section class="card glass-panel glass-panel--panel activate-pending-card">
        <div class="row between activate-pending-head" style="flex-wrap:wrap;gap:8px;align-items:center">
          <h3 class="activate-pending-title">Waiting to come online</h3>
          <button class="btn secondary btn-tap" id="reload">Refresh</button>
        </div>
        <div class="divider"></div>
        <div id="pendList"></div>
      </section>
    </div>`);

  const ACTIVATE_WIFI_STORE = "croc.activateWifiDraft.v1";
  const DEVICE_WIFI_PREFILL_KEY = "croc.deviceWifiPrefill.v1";
  const readWifiDraft = () => {
    try {
      const raw = sessionStorage.getItem(ACTIVATE_WIFI_STORE);
      const o = raw ? JSON.parse(raw) : null;
      if (o && typeof o.ssid === "string" && o.ssid.trim()) {
        return { ssid: o.ssid.trim() };
      }
    } catch (_) {}
    return null;
  };
  const refreshWifiBanner = () => {
    const el = $("#activateWifiStatus", view);
    const d = readWifiDraft();
    if (!el) return;
    el.textContent = d
      ? `Draft: “${d.ssid}” — prefill after claim (push when board online).`
      : "Optional Wi‑Fi draft for this browser.";
  };
  const dlgWifi = $("#activateWifiDialog", view);
  const openActivateWifiDialog = () => {
    const d = readWifiDraft();
    const s = $("#activateDlgSsid", view);
    const p = $("#activateDlgPass", view);
    if (s) s.value = d ? d.ssid : "";
    if (p) p.value = "";
    if (dlgWifi && typeof dlgWifi.showModal === "function") dlgWifi.showModal();
  };
  const closeActivateWifiDialog = () => {
    if (dlgWifi && typeof dlgWifi.close === "function") dlgWifi.close();
  };

  const wifiOpenBtn = $("#activateWifiOpenBtn", view);
  if (wifiOpenBtn) wifiOpenBtn.addEventListener("click", openActivateWifiDialog);
  const wifiSaveBtn = $("#activateWifiDlgSave", view);
  if (wifiSaveBtn) {
    wifiSaveBtn.addEventListener("click", () => {
      const ssid = ($("#activateDlgSsid", view).value || "").trim();
      if (!ssid) { toast("Enter Wi‑Fi name", "err"); return; }
      sessionStorage.setItem(ACTIVATE_WIFI_STORE, JSON.stringify({ ssid }));
      refreshWifiBanner();
      closeActivateWifiDialog();
      toast("Saved (this browser only)", "ok");
    });
  }
  const wifiClrDlg = $("#activateWifiDlgClear", view);
  if (wifiClrDlg) {
    wifiClrDlg.addEventListener("click", () => {
      sessionStorage.removeItem(ACTIVATE_WIFI_STORE);
      refreshWifiBanner();
      closeActivateWifiDialog();
      toast("Wi‑Fi draft cleared", "ok");
    });
  }
  const wifiClsDlg = $("#activateWifiDlgClose", view);
  if (wifiClsDlg) wifiClsDlg.addEventListener("click", closeActivateWifiDialog);
  const showPassEl = $("#activateDlgShowPass", view);
  if (showPassEl) {
    showPassEl.addEventListener("change", () => {
      const p = $("#activateDlgPass", view);
      if (p) p.type = showPassEl.checked ? "text" : "password";
    });
  }
  refreshWifiBanner();

  try {
    if (typeof window.__activateQrStop === "function") window.__activateQrStop();
  } catch (_) {}
  window.__activateQrStop = null;

  const scanHint = $("#idn_scan_hint", view);
  const scanWrap = $("#idn_scan_wrap", view);
  const scanVideo = $("#idn_scan_video", view);
  const scanBtn = $("#idn_scan_qr", view);
  const scanStopBtn = $("#idn_scan_stop", view);
  let qrScanner = null;

  const stopQrScan = () => {
    try {
      if (qrScanner) qrScanner.stop();
    } catch (_) {}
    qrScanner = null;
    if (scanWrap) scanWrap.hidden = true;
    if (scanStopBtn) scanStopBtn.hidden = true;
    if (scanBtn) scanBtn.disabled = !canClaim;
  };
  window.__activateQrStop = stopQrScan;

  if (scanBtn) {
    scanBtn.onclick = async () => {
      if (!canClaim) return;
      if (!activateQrScanSupported()) {
        const msg = activateQrScanUnavailableReason();
        if (scanHint) scanHint.textContent = msg;
        toast(msg, "err");
        return;
      }
      stopQrScan();
      qrScanner = createActivateQrScanner({
        videoEl: scanVideo,
        onCode: (text) => {
          const el = $("#idn_input", view);
          if (el) el.value = String(text || "").trim();
          stopQrScan();
          toast("QR captured", "ok");
          const go = $("#idn_go", view);
          if (go && !go.disabled) go.click();
        },
        onError: (message) => {
          if (scanHint) scanHint.textContent = String(message || "");
        },
      });
      if (scanWrap) scanWrap.hidden = false;
      if (scanStopBtn) scanStopBtn.hidden = false;
      scanBtn.disabled = true;
      if (scanHint) scanHint.textContent = "Point camera at board QR…";
      try {
        await qrScanner.start();
      } catch (e) {
        stopQrScan();
        const msg = String((e && e.message) || e || activateQrScanUnavailableReason());
        if (scanHint) scanHint.textContent = msg;
        toast(msg, "err");
      }
    };
  }
  if (scanStopBtn) scanStopBtn.onclick = () => stopQrScan();

  const resultBox = $("#idnResult");
  const drawBadge = (kind, label) =>
    `<span class="badge ${kind === "ok" ? "online" : (kind === "err" ? "offline" : "")}">${escapeHtml(label)}</span>`;

  const showClaimForm = (serial, mac, qr) => {
    const draft = readWifiDraft();
    const draftNote = draft
      ? `<p class="muted" style="margin:0 0 12px">Saved target Wi‑Fi <span class="mono">${escapeHtml(draft.ssid)}</span> — after claim we jump to the board page with Wi‑Fi (board) prefilled.</p>`
      : "";
    appendChildMarkup(
      resultBox,
      `
      <div class="card glass-panel glass-panel--panel" style="margin-top:10px">
        <h4 style="margin-top:0">Confirm claim</h4>
        ${draftNote}
        <div class="inline-form">
          <label class="field"><span>Board serial</span><input id="c_id" value="${escapeHtml(serial)}"/></label>
          <label class="field"><span>Network ID</span><input id="c_mac" value="${escapeHtml(mac)}"/></label>
          <label class="field"><span>Zone</span><input id="c_zone" value="all" placeholder="e.g. Gate A"/></label>
          <label class="field wide"><span>QR code (optional)</span><input id="c_qr" value="${escapeHtml(qr || "")}"/></label>
          <div class="row wide" style="justify-content:flex-end">
            <button class="btn btn-tap" id="c_submit">Confirm claim</button>
          </div>
        </div>
      </div>`,
    );
    $("#c_submit").addEventListener("click", async () => {
      const body = {
        mac_nocolon: ($("#c_mac").value || "").trim().toUpperCase(),
        device_id: ($("#c_id").value || "").trim().toUpperCase(),
        zone: ($("#c_zone").value || "all").trim(),
      };
      const q = ($("#c_qr").value || "").trim();
      if (q) body.qr_code = q;
      const preWifi = readWifiDraft();
      try {
        await api(buildPostProvisionClaimPath(), { method: "POST", body, timeoutMs: PROVISION_CONSOLE_TIMEOUT_MS });
        const did = String(body.device_id || "").toUpperCase();
        if (preWifi && preWifi.ssid) {
          sessionStorage.setItem(
            DEVICE_WIFI_PREFILL_KEY,
            JSON.stringify({ device_id: did, ssid: preWifi.ssid }),
          );
        }
        sessionStorage.removeItem(ACTIVATE_WIFI_STORE);
        refreshWifiBanner();
        toast("Claim completed", "ok");
        location.hash = `#/devices/${encodeURIComponent(did)}`;
      } catch (e) { toast(typeof softApiError === "function" ? softApiError(e) : (e.message || e), "err"); }
    });
  };

  const renderOperatorHints = (r) => {
    const zhH = r.operator_hints && Array.isArray(r.operator_hints.zh) ? r.operator_hints.zh : [];
    const enH = r.operator_hints && Array.isArray(r.operator_hints.en) ? r.operator_hints.en : [];
    const hintLines = zhH.length ? zhH : enH;
    if (!hintLines.length) return "";
    return `<ol class="activate-offline-hints muted">${hintLines
      .slice(0, 5)
      .map((t) => `<li>${escapeHtml(String(t))}</li>`)
      .join("")}</ol>`;
  };

  $("#idn_go").addEventListener("click", async () => {
    setChildMarkup(resultBox, `<p class="muted">Identifying…</p>`);
    const raw = ($("#idn_input").value || "").trim();
    if (!raw) { setChildMarkup(resultBox, `<p class="muted">Enter serial or QR code</p>`); return; }
    const body = raw.startsWith("CROC|") ? { qr_code: raw } : { serial: raw.toUpperCase() };
    try {
      const r = await api(buildPostProvisionIdentifyPath(), { method: "POST", body, timeoutMs: PROVISION_CONSOLE_TIMEOUT_MS });
      const kv = (k, v) => `<dt>${escapeHtml(k)}</dt><dd class="mono">${escapeHtml(v)}</dd>`;
      switch (r.status) {
        case "ready":
        case "ready_factory": {
          const isFactory = r.status === "ready_factory";
          setChildMarkup(
            resultBox,
            `${drawBadge("ok", isFactory ? "Ready to claim" : "Ready to claim")}
            <dl class="kv">${kv("Serial", r.serial)}${kv("Network ID", r.mac_nocolon)}${kv("Firmware", r.fw || "—")}${r.last_seen_at ? kv("Last seen", fmtTs(r.last_seen_at)) : ""}</dl>
            <p>${escapeHtml(r.message)}</p>
            ${renderOperatorHints(r)}`,
          );
          showClaimForm(r.serial, r.mac_nocolon, raw.startsWith("CROC|") ? raw : (r.qr_code || ""));
          break;
        }
        case "already_registered":
          const canSeeOwner = !!(state.me && state.me.role === "superadmin");
          const ownerKv = canSeeOwner ? kv("Account owner", r.owner_admin || "—") : "";
          const byYou = !!r.by_you;
          setChildMarkup(
            resultBox,
            `${drawBadge("err", byYou ? "Already yours" : "Already registered")}
            <dl class="kv">${kv("Serial", r.serial)}${kv("Board", r.device_id)}${ownerKv}${kv("Claimed at", r.claimed_at ? fmtTs(r.claimed_at) : "—")}</dl>
            <p class="muted">${escapeHtml(r.message)}</p>
            ${byYou ? `<button type="button" class="btn secondary" data-cc-go="devices" data-cc-id="${escapeHtml(String(r.device_id || ""))}">Open board</button>` : ""}`,
          );
          break;
        case "offline": {
          const dw = readWifiDraft();
          const hints = renderOperatorHints(r);
          const draftNote = dw
            ? `<p class="muted" style="margin-top:8px">Wi‑Fi draft: <strong>${escapeHtml(dw.ssid)}</strong></p>`
            : "";
          setChildMarkup(
            resultBox,
            `${drawBadge("", r.factory_registered ? "Waiting for board" : "Not claimed yet")}
            <dl class="kv">${kv("Serial", r.serial)}${r.mac_hint ? kv("Factory network ID", r.mac_hint) : ""}</dl>
            <p class="activate-offline-msg">${escapeHtml(r.message)}</p>
            ${hints}
            ${draftNote}
            <div class="activate-offline-actions">
              <button type="button" class="btn secondary btn-tap" id="activateOfflineWifiBtn">Edit Wi‑Fi draft</button>
              <button type="button" class="btn btn-tap" id="idn_retry_offline">Retry identify</button>
            </div>`,
          );
          break;
        }
        case "blocked":
          setChildMarkup(resultBox, `${drawBadge("err", "Factory blocked")}<p>${escapeHtml(r.message)}</p>`);
          break;
        case "unknown_serial":
          setChildMarkup(resultBox, `${drawBadge("err", "Unknown serial")}<p>${escapeHtml(r.message)}</p>`);
          break;
        default:
          setChildMarkup(resultBox, `<p class="muted">Could not identify this unit — try again or ask platform staff.</p>`);
      }
    } catch (e) {
      const soft = typeof softApiError === "function" ? softApiError(e) : String(e.message || e);
      setChildMarkup(resultBox, `<p class="badge revoked">${escapeHtml(soft)}</p>`);
    }
  });

  view.addEventListener("click", (ev) => {
    if (ev.target.closest("#activateOfflineWifiBtn")) {
      openActivateWifiDialog();
      return;
    }
    if (ev.target.closest("#idn_retry_offline")) {
      const go = $("#idn_go", view);
      if (go) go.click();
    }
  });

  $("#reload").addEventListener("click", () => renderRoute());

  try {
    const rq = window.__routeQuery || new URLSearchParams("");
    const pre = (rq.get("q") || rq.get("serial") || "").trim();
    if (pre) {
      const el = $("#idn_input");
      if (el) el.value = pre;
    }
  } catch (_) {}

  let pendingErr = "";
  const data = await apiOr("/provision/pending", (e) => {
    pendingErr = String((e && e.message) || e || "load failed");
    return { items: [] };
  }, { timeoutMs: 16000 });
  const items = data.items || [];
  const pendListEl = view.querySelector("#pendList");
  if (!pendListEl) return;
  setChildMarkup(
    pendListEl,
    `
    <div class="table-wrap"><table class="t">
      <thead><tr><th>Network ID</th><th>Proposed serial</th><th>Has QR</th><th>Firmware</th><th>Last seen</th></tr></thead>
      <tbody>${items.length === 0 ? `<tr><td colspan="5" class="muted">${pendingErr ? "Could not load — try Refresh." : "None waiting"}</td></tr>` :
        items.map((p) => `<tr>
          <td class="mono">${escapeHtml(p.mac_nocolon || p.mac || "")}</td>
          <td class="mono">${escapeHtml(p.proposed_device_id || "—")}</td>
          <td>${p.qr_code ? "Yes" : "—"}</td>
          <td>${escapeHtml(p.fw || "—")}</td>
          <td>${escapeHtml(fmtTs(p.last_seen_at))}</td>
        </tr>`).join("")}</tbody>
    </table></div>`,
  );
});


// ── admin-user-scopes.route.js ─────────────────────────────────────
/**
 * Route: #/admin-user-scopes — parent tenant admin grants per-user scopes to child admins.
 */

registerRoute("admin-user-scopes", async (view) => {
  setCrumb("Child admin access");
  if (!hasRole("admin")) {
    mountView(view, `<div class="card"><p class="muted">Admins only.</p></div>`);
    return;
  }
  const me = state.me || {};
  if (!can("can_manage_users")) {
    mountView(
      view,
      `<div class="card glass-panel glass-panel--panel"><p class="muted">You need user-management access to open this page.</p></div>`,
    );
    return;
  }
  if (!me.tenant_acl_enabled) {
    mountView(
      view,
      `<div class="card glass-panel glass-panel--panel"><p class="muted">Child-admin access scopes are not opened on this server yet.</p></div>`,
    );
    return;
  }
  if (String(me.manager_admin || "").trim()) {
    mountView(
      view,
      `<div class="card glass-panel glass-panel--panel"><p class="muted">Only the parent account admin can use this page.</p><p style="margin-top:10px"><button type="button" class="btn secondary btn-tap" data-cc-go="account">Account</button></p></div>`,
    );
    return;
  }

  const scopeEmptyRow = (text) =>
    `<tr class="tenant-deleg-table__row-empty"><td colspan="3"><div class="tenant-deleg-table__empty-state">${escapeHtml(text)}</div></td></tr>`;

  mountView(
    view,
    `
    <div class="tenant-admin-page">
      <header class="card tenant-admin-page__top">
        <div>
          <h2>Child admin access</h2>
          <p class="muted">Choose which end-users each staff admin may manage. They only see devices shared to those users.</p>
        </div>
        <button type="button" class="tenant-admin-page__link" data-cc-go="tenant-delegations">Staff delegations →</button>
      </header>

      <section class="card tenant-admin-page__card">
        <h3 class="tenant-admin-page__card-title">Add to whitelist</h3>
        <p class="tenant-admin-page__hint">
          Create staff in <button type="button" class="tenant-admin-page__link" data-cc-go="admin">Users</button> (role Child admin). Share devices to end-users first.
        </p>
        <p class="tenant-tool-callout--warn" id="aus_empty_child" style="display:none">
          No staff admins yet — create one in <button type="button" class="tenant-admin-page__link" data-cc-go="admin">Users</button> first.
        </p>
        <div class="tenant-admin-page__form-row tenant-admin-page__form-row--2">
          <label class="field"><span>Staff admin</span><select id="aus_sub" autocomplete="off"></select></label>
          <label class="field"><span>End-user</span><select id="aus_user_one" autocomplete="off"></select></label>
        </div>
        <div class="tenant-admin-page__form-actions tenant-admin-page__form-actions--tight" style="border:0;padding-top:0;margin-top:12px">
          <button class="btn btn-tap" id="aus_add_one" type="button">Add to whitelist</button>
        </div>
        <details class="tenant-tool-bulk">
          <summary>Add several users at once</summary>
          <label class="field" style="margin-top:10px"><span>Select users</span>
            <select id="aus_user" multiple autocomplete="off"></select>
          </label>
          <div class="tenant-admin-page__form-actions tenant-admin-page__form-actions--tight" style="border:0;padding-top:10px;margin-top:8px">
            <button class="btn secondary btn-tap" id="aus_grant" type="button">Add selected</button>
          </div>
        </details>
        <div class="tenant-admin-page__form-actions">
          <button class="btn secondary btn-tap" id="aus_reload" type="button">Refresh lists</button>
        </div>
        <p class="muted" id="aus_msg" style="margin:8px 0 0;font-size:12px"></p>
      </section>

      <section class="card tenant-admin-page__card">
        <div class="tenant-admin-page__table-head">
          <h3 class="tenant-admin-page__card-title">Current whitelist</h3>
          <button class="btn secondary btn-tap sm" id="aus_reload_table" type="button">Refresh</button>
        </div>
        <p class="tenant-admin-page__status" id="aus_badge"></p>
        <div class="tenant-admin-page__table-wrap">
          <table class="table compact tenant-deleg-table tenant-deleg-table--scopes">
            <colgroup>
              <col class="tenant-deleg-table__w-user" />
              <col class="tenant-deleg-table__w-added" />
              <col class="tenant-deleg-table__w-actions" />
            </colgroup>
            <thead>
              <tr>
                <th scope="col">End-user</th>
                <th scope="col">Added</th>
                <th scope="col" class="tenant-deleg-table__th-actions">Actions</th>
              </tr>
            </thead>
            <tbody id="aus_rows">${scopeEmptyRow("Select a staff admin above.")}</tbody>
          </table>
        </div>
      </section>
    </div>`,
  );

  const userSel = $("#aus_user", view);
  const userOneSel = $("#aus_user_one", view);
  const subSel = $("#aus_sub", view);
  const msg = $("#aus_msg", view);
  const rows = $("#aus_rows", view);
  const badge = $("#aus_badge", view);
  const emptyChild = $("#aus_empty_child", view);

  /** @type {{username:string,email:string}[]} */
  let tenantUsers = [];

  const fillUserPickers = () => {
    const multiOpts =
      tenantUsers.length === 0
        ? '<option value="">(no end-users)</option>'
        : tenantUsers
            .map((x) => {
              const u = escapeHtml(x.username);
              const em = x.email ? ` · ${escapeHtml(x.email)}` : "";
              return `<option value="${u}">${u}${em}</option>`;
            })
            .join("");
    if (userSel) userSel.innerHTML = multiOpts;
    if (userOneSel) {
      userOneSel.innerHTML =
        tenantUsers.length === 0
          ? '<option value="">(no end-users)</option>'
          : `<option value="">— Choose —</option>${tenantUsers
              .map((x) => {
                const u = escapeHtml(x.username);
                const em = x.email ? ` · ${escapeHtml(x.email)}` : "";
                return `<option value="${u}">${u}${em}</option>`;
              })
              .join("")}`;
    }
  };

  const loadUsers = async () => {
    try {
      const d = await api(buildAuthUsersPath(), { timeoutMs: TENANT_CONSOLE_TIMEOUT_MS });
      tenantUsers = (Array.isArray(d.items) ? d.items : [])
        .filter((x) => String(x.role || "") === "user")
        .map((x) => ({
          username: String(x.username || "").trim(),
          email: String(x.email || "").trim(),
        }))
        .filter((x) => x.username);
      fillUserPickers();
    } catch (e) {
      if (msg) msg.textContent = softOperatorNote(softApiError(e));
    }
  };

  const grantUsers = async (usernames, grantBtn) => {
    const sub = String((subSel && subSel.value) || "").trim();
    if (!sub) {
      toast("Select a staff admin.", "err");
      return;
    }
    const selected = usernames.filter(Boolean);
    if (!selected.length) {
      toast("Select an end-user.", "err");
      return;
    }
    if (grantBtn) grantBtn.disabled = true;
    let ok = 0;
    try {
      for (const user of selected) {
        await api(buildPostAdminUserScopePath(sub, user), {
          method: "POST",
          timeoutMs: TENANT_CONSOLE_TIMEOUT_MS,
        });
        ok += 1;
      }
      toast(`Added ${ok} user(s)`, "ok");
      if (userOneSel) userOneSel.value = "";
      await loadChildAdmins();
    } catch (e) {
      toast(softOperatorNote(softApiError(e)), "err");
      if (ok) await loadChildAdmins();
    } finally {
      if (grantBtn) grantBtn.disabled = false;
    }
  };

  const loadScopes = async () => {
    const sub = String((subSel && subSel.value) || "").trim();
    if (!sub) {
      if (rows) rows.innerHTML = scopeEmptyRow("Select a staff admin above.");
      if (badge) badge.textContent = "";
      return;
    }
    try {
      const d = await api(buildGetAdminUserScopesPath(sub), { timeoutMs: TENANT_CONSOLE_TIMEOUT_MS });
      const items = Array.isArray(d.items) ? d.items : [];
      if (!items.length) {
        if (rows) rows.innerHTML = scopeEmptyRow("No users yet — add one above.");
        if (badge) badge.textContent = `${sub} · 0 users`;
        if (msg) msg.textContent = "";
        return;
      }
      if (rows) {
        rows.innerHTML = items
          .map((x) => {
            const u = escapeHtml(String(x.user_username || ""));
            const em = x.email ? `<span class="muted"> ${escapeHtml(String(x.email))}</span>` : "";
            const ts = x.created_at ? fmtTs(x.created_at) : "—";
            return `<tr>
            <td class="tenant-deleg-table__cell-staff"><span class="mono">${u}</span>${em}</td>
            <td class="muted tenant-deleg-table__cell-until">${ts}</td>
            <td class="tenant-deleg-table__cell-actions"><button class="btn secondary btn-tap aus_revoke" data-sub="${escapeHtml(sub)}" data-user="${u}" type="button">Remove</button></td>
          </tr>`;
          })
          .join("");
      }
      if (msg) msg.textContent = "";
      if (badge) badge.textContent = `${sub} · ${items.length} user(s)`;
    } catch (e) {
      if (rows) rows.innerHTML = scopeEmptyRow(softOperatorNote(softApiError(e)));
      if (badge) badge.textContent = "";
    }
  };

  const loadChildAdmins = async () => {
    try {
      const d = await api(buildGetAdminUserScopesChildrenPath(), { timeoutMs: TENANT_CONSOLE_TIMEOUT_MS });
      const items = Array.isArray(d.items) ? d.items : [];
      if (!subSel) return;
      const prev = String(subSel.value || "").trim();
      if (!items.length) {
        subSel.innerHTML = `<option value="">(none — create in Users)</option>`;
        if (rows) rows.innerHTML = scopeEmptyRow("Create a staff admin in Users first.");
        if (badge) badge.textContent = "";
        if (emptyChild) emptyChild.style.display = "";
        return;
      }
      if (emptyChild) emptyChild.style.display = "none";
      subSel.innerHTML = items
        .map((x) => {
          const raw = String(x.sub_admin_username || "");
          const u = escapeHtml(raw);
          const em = String(x.email || "").trim();
          const c = Number(x.active_scope_count || 0);
          const lab = em ? `${u} · ${escapeHtml(em)} (${c})` : `${u} (${c})`;
          return `<option value="${u}">${lab}</option>`;
        })
        .join("");
      const still = items.some((x) => String(x.sub_admin_username || "").trim() === prev);
      if (prev && still) subSel.value = prev;
      await loadScopes();
    } catch (e) {
      if (msg) msg.textContent = softOperatorNote(softApiError(e));
    }
  };

  const grantBtn = $("#aus_grant", view);
  if (grantBtn) {
    grantBtn.addEventListener("click", async () => {
      const selected = userSel
        ? Array.from(userSel.selectedOptions || [])
            .map((o) => String(o.value || "").trim())
            .filter(Boolean)
        : [];
      await grantUsers(selected, grantBtn);
    });
  }

  const addOneBtn = $("#aus_add_one", view);
  if (addOneBtn) {
    addOneBtn.addEventListener("click", async () => {
      const one = userOneSel ? String(userOneSel.value || "").trim() : "";
      await grantUsers(one ? [one] : [], addOneBtn);
    });
  }

  const reloadBtn = $("#aus_reload", view);
  if (reloadBtn) {
    reloadBtn.addEventListener("click", async () => {
      await loadUsers();
      await loadChildAdmins();
    });
  }
  const reloadTableBtn = $("#aus_reload_table", view);
  if (reloadTableBtn) reloadTableBtn.addEventListener("click", () => loadScopes());

  if (subSel) subSel.addEventListener("change", () => loadScopes());

  view.addEventListener("click", async (ev) => {
    const t = ev.target;
    if (!t || !t.classList || !t.classList.contains("aus_revoke")) return;
    const sub = String(t.getAttribute("data-sub") || "").trim();
    const user = String(t.getAttribute("data-user") || "").trim();
    if (!sub || !user) return;
    if (!confirm(`Remove ${user} from ${sub}?`)) return;
    try {
      await api(buildDeleteAdminUserScopePath(sub, user), {
        method: "DELETE",
        timeoutMs: TENANT_CONSOLE_TIMEOUT_MS,
      });
      toast("Removed", "ok");
      await loadChildAdmins();
    } catch (e) {
      toast(softOperatorNote(softApiError(e)), "err");
    }
  });

  await loadUsers();
  await loadChildAdmins();
});


// ── admin.route.js ─────────────────────────────────────
/**
 * Route: #/admin — deep link → Settings overview (admin section).
 */

registerRoute("admin", async (view, _args, routeSeq) => {
  setCrumb("Admin");
  await renderSettingsOverviewPage(view, {
    activeNavId: "admin",
    routeSeq,
    me: state.me,
    hasRole,
    can,
  });
});


// ── audit-log.route.js ─────────────────────────────────────
/**
 * Route: #/audit-log — Audit Log Center.
 *
 * Consolidates the former Activity / Channels / Events monitoring pages into a
 * single command-center surface. The three sources keep their own deep-link
 * routes (#/activity, #/signals, #/events) and APIs; this page only re-presents
 * them behind one mockup-aligned layout (left source/filter rail, center
 * timeline, right analytics column, bottom charts + export strip).
 *
 * Build: split out of src/console.raw.js and concatenated as raw text by
 * scripts/build-dashboard.mjs after the monolith body. Shares scope with
 * helpers like $, mountView, api, registerRoute, state, toast, setCrumb.
 */

registerRoute("audit-log", async (view, _args, routeSeq) => {
  setCrumb("Audit Log");
  ensureAuditLogStyles();

  const me = state.me || {};
  const canAuditTenantTrail = (() => {
    if (!hasRole("admin")) return false;
    if (me.role === "sa_staff") {
      const caps = new Set((me.sa_capabilities || []).map(String));
      return caps.has("sa_full_access") || caps.has("sa_audit_read");
    }
    return me.role === "admin" || me.role === "superadmin";
  })();
  const canAuditExport = canAuditTenantTrail;
  const exportsOn = hasRole("admin") && catalogEnabled(me, "exports");
  const auditPdfOn = canAuditExport && !!me.audit_export_pdf_enabled;
  const auditCoverOn = canAuditExport && me.audit_export_cover_pdf_enabled !== false;
  const eventsCoverOn = hasRole("admin");

  /** @type {"activity"|"channels"|"events"|"audit"|"exports"} */
  let source = "activity";
  try {
    const rq = window.__routeQuery;
    const srcParam = rq && typeof rq.get === "function" ? String(rq.get("source") || "").trim() : "";
    if (srcParam === "channels" || srcParam === "events" || srcParam === "activity") {
      source = srcParam;
    } else if (srcParam === "audit" && canAuditTenantTrail) {
      source = "audit";
    } else if (srcParam === "exports") {
      if (!exportsOn) {
        mountView(view, capabilityUnavailableHtml({
          title: "Export jobs",
          detail: "Background export jobs are not opened for this account yet.",
          backHref: "#/audit-log",
          backLabel: "Audit Log",
        }));
        return;
      }
      source = "exports";
    }
  } catch (_) {}

  // ---- Async export control plane (catalog exports → this deep link) ----
  if (source === "exports") {
    const otherTabs = [
      { id: "activity", label: "Activity" },
      { id: "channels", label: "Channels" },
      { id: "events", label: "Events" },
      ...(canAuditTenantTrail ? [{ id: "audit", label: "Audit trail" }] : []),
      { id: "exports", label: "Export jobs" },
    ];
    const tabsNav = otherTabs.map((s) =>
      `<button type="button" class="btn sm ${s.id === "exports" ? "" : "secondary"} btn-tap" data-cc-go="audit-log?source=${encodeURIComponent(s.id)}">${escapeHtml(s.label)}</button>`,
    ).join(" ");
    mountView(view, `<section class="card glass-panel glass-panel--panel" aria-label="Export jobs">
      <p class="muted" style="margin:0 0 8px">Audit Log · Background downloads</p>
      <h2>Export jobs</h2>
      <div class="row" style="gap:8px;flex-wrap:wrap;margin:10px 0 14px">${tabsNav}</div>
      <p class="muted">Create a download job (audit sheet or events sheet). When the file is ready, create a download link. Quick PDF export stays on the main Audit Log strip when enabled.</p>
      <div class="glass-panel glass-panel--inset" style="padding:12px;margin-top:12px">
        <strong>Create job</strong>
        <div class="inline-form" style="margin-top:8px">
          <label class="field"><span>Export type</span>
            <select id="expKind">
              <option value="audit_csv">Audit spreadsheet</option>
              <option value="events_csv">Events spreadsheet</option>
            </select>
          </label>
          <label class="field"><span>Your reference (optional)</span><input id="expClientRef" maxlength="64" placeholder="optional note" /></label>
        </div>
        <div class="row" style="margin-top:8px"><button class="btn btn-tap" type="button" id="expCreateBtn">Create export job</button></div>
        <p id="expCreateMsg" class="muted" aria-live="polite"></p>
      </div>
      <div class="glass-panel glass-panel--inset" style="padding:12px;margin-top:12px">
        <strong>Job status</strong>
        <div class="inline-form" style="margin-top:8px">
          <label class="field grow"><span>Job reference</span><input id="expJobId" class="mono" placeholder="from create" /></label>
          <button class="btn secondary btn-tap" type="button" id="expRefreshBtn">Refresh</button>
        </div>
        <p id="expJobOut" class="muted" style="margin-top:8px;white-space:pre-wrap;font-size:13px;line-height:1.45"></p>
        <div class="row" style="margin-top:8px;gap:8px;flex-wrap:wrap">
          <button class="btn secondary btn-tap" type="button" id="expMintBtn" disabled>Prepare download</button>
          <button class="btn secondary btn-tap" type="button" id="expDownloadBtn" disabled>Download file</button>
          <button class="btn secondary btn-tap" type="button" id="expPresignBtn" disabled>Temporary link</button>
        </div>
        <p id="expDlMsg" class="muted" aria-live="polite"></p>
      </div>
      <p class="muted" style="margin-top:12px">Quick spreadsheet shortcuts remain on the other Audit Log tabs.</p>
    </section>`);

    let lastToken = "";
    const setJobOut = (pack) => {
      const out = $("#expJobOut", view);
      if (out) {
        const stateLabel = String((pack && pack.state) || "—");
        const kindRaw = String((pack && pack.kind) || "");
        const kind = kindRaw === "audit_csv" ? "Audit spreadsheet"
          : kindRaw === "events_csv" ? "Events spreadsheet"
          : (kindRaw || "—");
        const jobId = String((pack && (pack.job_id || pack.id)) || "—");
        const hasKey = !!(pack && pack.has_object_key);
        out.textContent = [
          `Status: ${stateLabel}`,
          `Type: ${kind}`,
          `Job: ${jobId}`,
          hasKey ? "File: ready" : "File: not ready yet",
        ].join("\n");
      }
      const ready = !!(pack && pack.state === "ready" && pack.has_object_key);
      const mint = $("#expMintBtn", view);
      const dl = $("#expDownloadBtn", view);
      const ps = $("#expPresignBtn", view);
      if (mint) mint.disabled = !ready;
      if (dl) dl.disabled = !(ready && lastToken);
      if (ps) ps.disabled = !(ready && lastToken);
    };

    const refreshJob = async () => {
      const jobId = String(($("#expJobId", view) && $("#expJobId", view).value) || "").trim();
      if (!jobId) return;
      const pack = await api(buildGetExportControlJobPath(jobId), { timeoutMs: PLATFORM_CONTROL_TIMEOUT_MS });
      if (!isRouteCurrent(routeSeq)) return;
      setJobOut(pack);
      return pack;
    };

    const createBtn = $("#expCreateBtn", view);
    if (createBtn) {
      createBtn.onclick = async () => {
        const kind = String(($("#expKind", view) && $("#expKind", view).value) || "audit_csv").trim();
        const clientRef = String(($("#expClientRef", view) && $("#expClientRef", view).value) || "").trim();
        if (!window.confirm(`Create durable export job kind=${kind}?`)) return;
        createBtn.disabled = true;
        ($("#expCreateMsg", view) || {}).textContent = "Creating…";
        try {
          const body = { kind, claims: {} };
          if (clientRef) body.claims.client_ref = clientRef;
          const created = await api(buildPostExportControlJobPath(), {
            method: "POST",
            body,
            timeoutMs: PLATFORM_CONTROL_TIMEOUT_MS,
          });
          if (!isRouteCurrent(routeSeq)) return;
          const jid = String((created && created.job_id) || "");
          ($("#expCreateMsg", view) || {}).textContent = jid ? `Export started · ${jid}` : "Export started.";
          if (jid && $("#expJobId", view)) $("#expJobId", view).value = jid;
          lastToken = "";
          await refreshJob();
          toast("Export job created", "ok");
        } catch (e) {
          ($("#expCreateMsg", view) || {}).textContent = String(e.message || e);
          toast(String(e.message || e), "err");
        } finally {
          createBtn.disabled = false;
        }
      };
    }
    const refreshBtn = $("#expRefreshBtn", view);
    if (refreshBtn) {
      refreshBtn.onclick = async () => {
        refreshBtn.disabled = true;
        try {
          await refreshJob();
        } catch (e) {
          ($("#expDlMsg", view) || {}).textContent = String(e.message || e);
          toast(String(e.message || e), "err");
        } finally {
          refreshBtn.disabled = false;
        }
      };
    }
    const mintBtn = $("#expMintBtn", view);
    if (mintBtn) {
      mintBtn.onclick = async () => {
        const jobId = String(($("#expJobId", view) && $("#expJobId", view).value) || "").trim();
        if (!jobId) return;
        if (!window.confirm(`Create a download link for this export job?`)) return;
        mintBtn.disabled = true;
        try {
          const pack = await api(buildPostExportControlDownloadTokenPath(jobId), {
            method: "POST",
            body: {},
            timeoutMs: PLATFORM_CONTROL_TIMEOUT_MS,
          });
          if (!isRouteCurrent(routeSeq)) return;
          lastToken = String((pack && pack.token) || "");
          ($("#expDlMsg", view) || {}).textContent = lastToken
            ? `Download ready · expires ${pack.expires_at || "soon"} (link stays in this browser only).`
            : "Download link was not created.";
          await refreshJob();
          toast("Download link ready", "ok");
        } catch (e) {
          ($("#expDlMsg", view) || {}).textContent = String(e.message || e);
          toast(String(e.message || e), "err");
        } finally {
          mintBtn.disabled = false;
        }
      };
    }
    const downloadBtn = $("#expDownloadBtn", view);
    if (downloadBtn) {
      downloadBtn.onclick = async () => {
        const jobId = String(($("#expJobId", view) && $("#expJobId", view).value) || "").trim();
        if (!jobId || !lastToken) return;
        downloadBtn.disabled = true;
        try {
          const path = buildGetExportControlDownloadPath(jobId);
          const qs = path.includes("?") ? "&" : "?";
          const url = apiBase() + path + qs + "token=" + encodeURIComponent(lastToken);
          const hdrs = {};
          const t = getToken();
          if (t) hdrs.Authorization = "Bearer " + t;
          const r = await fetch(url, { credentials: "include", headers: hdrs });
          if (!r.ok) throw new Error((await r.text()) || r.statusText);
          const blob = await r.blob();
          triggerDownloadFromBlob(blob, `export-${jobId}.bin`);
          toast("Export downloaded", "ok");
        } catch (e) {
          ($("#expDlMsg", view) || {}).textContent = String(e.message || e);
          toast(String(e.message || e), "err");
        } finally {
          downloadBtn.disabled = false;
        }
      };
    }
    const presignBtn = $("#expPresignBtn", view);
    if (presignBtn) {
      presignBtn.onclick = async () => {
        const jobId = String(($("#expJobId", view) && $("#expJobId", view).value) || "").trim();
        if (!jobId || !lastToken) return;
        presignBtn.disabled = true;
        try {
          const path = buildGetExportControlDownloadPresignedPath(jobId);
          const qs = path.includes("?") ? "&" : "?";
          const pack = await api(path + qs + "token=" + encodeURIComponent(lastToken), {
            timeoutMs: PLATFORM_CONTROL_TIMEOUT_MS,
          });
          if (!isRouteCurrent(routeSeq)) return;
          const url = pack && (pack.url || pack.presigned_url || pack.download_url);
          ($("#expDlMsg", view) || {}).textContent = url
            ? "Secure download link opened in a new tab."
            : "Download link was not returned.";
          if (url) window.open(String(url), "_blank", "noopener,noreferrer");
          toast(url ? "Download link opened" : "No download link returned", "ok");
        } catch (e) {
          ($("#expDlMsg", view) || {}).textContent = String(e.message || e);
          toast(String(e.message || e), "err");
        } finally {
          presignBtn.disabled = false;
        }
      };
    }
    return;
  }

  let rangeHours = 24;
  /** @type {Array<{ts:any, level:string, category:string, title:string, actor:string, target:string, deviceId:string}>} */
  let rows = [];

  const SOURCES = [
    { id: "activity", label: "Activity", ico: "≋", sub: "Device and runtime facts" },
    { id: "channels", label: "Channels", ico: "◉", sub: "Delivery, alerts, and alarms" },
    { id: "events", label: "Events", ico: "≈", sub: "Live system event stream" },
    ...(canAuditTenantTrail
      ? [{ id: "audit", label: "Audit trail", ico: "⊞", sub: "Who changed what" }]
      : []),
    ...(exportsOn
      ? [{ id: "exports", label: "Export jobs", ico: "⇩", sub: "Background download jobs" }]
      : []),
  ];
  const RANGES = [
    { h: 24, label: "Today" },
    { h: 168, label: "7 Days" },
    { h: 720, label: "30 Days" },
    { h: 2160, label: "90 Days" },
  ];

  const tabsHtml = SOURCES.map(
    (s) => `
      <button type="button" class="alog-tab${s.id === source ? " alog-tab--on" : ""}" data-alog-source="${s.id}">
        <span class="alog-tab__ico" aria-hidden="true">${s.ico}</span>
        <span>
          <span style="display:block">${s.label}</span>
          <span class="muted" style="font-size:10.5px;font-weight:500">${escapeHtml(s.sub)}</span>
        </span>
      </button>`,
  ).join("");

  const rangeHtml = RANGES.map(
    (r) => `<button type="button" class="alog-pill${r.h === rangeHours ? " alog-pill--on" : ""}" data-alog-range="${r.h}">${escapeHtml(r.label)}</button>`,
  ).join("");

  mountView(
    view,
    `
    <section class="alog-page" aria-label="Audit Log Center">
      <div class="alog-grid">

        <aside class="alog-side glass-panel glass-panel--panel alog-panel" aria-label="Sources and filters">
          <div class="alog-side__brand">
            <p class="alog-eyebrow">Audit Log Center</p>
            <h2 class="alog-title">Unified timeline</h2>
          </div>
          <div class="alog-tabs" id="alogTabs">${tabsHtml}</div>
          <div class="alog-filters">
            <p class="alog-eyebrow" style="margin-top:4px">Filters</p>
            <label class="alog-field"><span>Search</span><input id="alogSearch" type="search" autocomplete="off" placeholder="summary / actor / type" /></label>
            <label class="alog-field"><span>Device / Entity</span><input id="alogDevice" type="search" autocomplete="off" placeholder="SN-… or id" /></label>
            <label class="alog-field"><span>User / Actor</span><input id="alogActor" type="search" autocomplete="off" placeholder="username" /></label>
            <label class="alog-field"><span>Event Level</span>
              <select id="alogLevel">
                <option value="">All levels</option>
                <option value="critical">Critical</option>
                <option value="error">High / Error</option>
                <option value="warn">Medium / Warn</option>
                <option value="info">Info</option>
                <option value="debug">Debug / Low</option>
              </select>
            </label>
            <button type="button" class="btn primary btn-tap alog-apply" id="alogApply">Apply Filters</button>
          </div>
        </aside>

        <div class="alog-topbar glass-panel glass-panel--panel">
          <div class="alog-range" id="alogRange">${rangeHtml}</div>
          <div class="alog-topbar__right">
            <span class="badge offline" id="evLive">${source === "events" ? "Connecting" : "Polling"}</span>
            <span class="muted mono" id="alogCount">—</span>
            <button type="button" class="btn sm secondary btn-tap" id="alogRefresh">Refresh</button>
          </div>
        </div>

        <section class="alog-center glass-panel glass-panel--panel alog-panel" aria-label="Audit timeline">
          <div class="alog-panel__head">
            <div>
              <p class="alog-eyebrow">Timeline</p>
              <h3 class="alog-title" id="alogTimelineTitle">Audit Timeline</h3>
            </div>
          </div>
          <div class="alog-feed-wrap" id="alogFeed"><p class="alog-empty">Loading…</p></div>
        </section>

        <div class="alog-right" aria-label="Analytics">
          <section class="glass-panel glass-panel--panel alog-panel">
            <div class="alog-panel__head"><h3 class="alog-title">Channel Analytics</h3></div>
            <div class="alog-donut-wrap">
              <div class="alog-donut" id="alogDonut">
                <div class="alog-donut__center">
                  <span class="alog-donut__total" id="alogDonutTotal">0</span>
                  <span class="alog-donut__label">Total</span>
                </div>
              </div>
              <div class="alog-legend" id="alogLegend"></div>
            </div>
          </section>

          <section class="glass-panel glass-panel--panel alog-panel">
            <div class="alog-panel__head"><h3 class="alog-title">Event Breakdown</h3></div>
            <div class="alog-break" id="alogBreak"></div>
          </section>

          <section class="glass-panel glass-panel--panel alog-panel">
            <div class="alog-panel__head"><h3 class="alog-title">Top Active Users</h3></div>
            <div class="alog-rank" id="alogTopUsers"></div>
          </section>

          <section class="glass-panel glass-panel--panel alog-panel">
            <div class="alog-panel__head"><h3 class="alog-title">Top Event Sources</h3></div>
            <div class="alog-rank" id="alogTopSources"></div>
          </section>

          <section class="glass-panel glass-panel--panel alog-panel alog-insight">
            <div class="alog-panel__head"><h3 class="alog-title">Heuristic insight</h3></div>
            <p class="alog-insight__title" id="alogInsightTitle">Analyzing…</p>
            <p class="alog-insight__body" id="alogInsightBody">Local pattern summary · not an AI API.</p>
          </section>
        </div>

        <div class="alog-bottom">
          <section class="glass-panel glass-panel--panel alog-panel">
            <div class="alog-panel__head"><h3 class="alog-title">Activity Over Time</h3></div>
            <canvas class="alog-chart-canvas" id="alogChart"></canvas>
          </section>

          <section class="glass-panel glass-panel--panel alog-panel">
            <div class="alog-panel__head"><h3 class="alog-title">Event Heatmap</h3></div>
            <div class="alog-heatmap" id="alogHeatmap"></div>
          </section>

          <section class="glass-panel glass-panel--panel alog-panel">
            <div class="alog-panel__head"><h3 class="alog-title">Export &amp; Report</h3></div>
            <div class="alog-export">
              <p class="alog-export__hint">Download spreadsheet / PDF from this timeline${exportsOn ? ` · or open <button type="button" class="tenant-admin-page__link" data-cc-go="audit-log?source=exports">background export jobs</button>` : ""}.</p>
              <div class="row" style="gap:8px;flex-wrap:wrap">
                <button type="button" class="btn sm secondary btn-tap" id="alogExportEvents">Export Events CSV</button>
                ${canAuditExport ? `<button type="button" class="btn sm secondary btn-tap" id="alogExportAudit">Export Audit CSV</button>` : ""}
                ${eventsCoverOn ? `<button type="button" class="btn sm secondary btn-tap" id="alogExportEventsCover">Events cover PDF</button>` : ""}
                ${auditCoverOn ? `<button type="button" class="btn sm secondary btn-tap" id="alogExportAuditCover">Audit cover PDF</button>` : ""}
                ${auditPdfOn ? `<button type="button" class="btn sm btn-tap" id="alogExportAuditPdf">Full audit PDF</button>` : ""}
              </div>
              ${!auditPdfOn && canAuditExport ? `<p class="muted" style="margin:8px 0 0;font-size:12px">Full audit PDF is not opened for this deployment yet.</p>` : ""}
            </div>
          </section>
        </div>

      </div>
    </section>`,
  );

  // ---------------------------------------------------------------- helpers
  const tsToEpochSec = (v) => {
    if (v == null) return NaN;
    if (typeof v === "number") return v > 1e12 ? v / 1000 : v;
    const s = String(v).trim();
    if (/^\d+(\.\d+)?$/.test(s)) {
      const n = Number(s);
      return n > 1e12 ? n / 1000 : n;
    }
    const p = Date.parse(s);
    return Number.isFinite(p) ? p / 1000 : NaN;
  };

  const levelBucket = (lvl) => {
    const s = String(lvl || "").toLowerCase();
    if (/(crit|fatal|panic|alarm)/.test(s)) return "critical";
    if (/(error|fail|denied|reject)/.test(s)) return "high";
    if (/(warn|degrad|partial|medium)/.test(s)) return "medium";
    if (/(debug|trace|ok|success|low)/.test(s)) return "low";
    return "info";
  };
  const BUCKETS = [
    { id: "critical", label: "Critical" },
    { id: "high", label: "High" },
    { id: "medium", label: "Medium" },
    { id: "low", label: "Low" },
    { id: "info", label: "Info" },
  ];
  const DONUT_COLORS = ["#38bdf8", "#6366f1", "#f97316", "#22c55e", "#eab308", "#ec4899", "#14b8a6", "#94a3b8"];

  // ---------------------------------------------------------------- normalize
  const normActivity = (d) =>
    (d.items || []).map((e) => ({
      ts: e.ts_malaysia || e.ts,
      level: e.level || "info",
      category: e.category || "activity",
      title: (e.summary && String(e.summary).trim()) || (e.event_type ? auditActionDisplay(e.event_type) : "—"),
      actor: e.actor || "",
      target: e.target || "",
      deviceId: e.device_id || "",
    }));

  const normChannels = (d) =>
    (d.items || []).map((a) => ({
      ts: a.ts,
      level: a.what || a.kind || "info",
      category: a.kind || a.zone || "channel",
      title: a.display_label || a.what || a.kind || "—",
      actor: a.who || "",
      target: a.notification_group || "",
      deviceId: a.device_id && a.device_id !== "*" ? a.device_id : "",
    }));

  const normEvents = (d) =>
    (d.items || []).map((e) => ({
      ts: e.ts_malaysia || e.ts,
      level: e.level || "info",
      category: e.category || "system",
      title: (e.summary && String(e.summary).trim()) || (e.event_type ? auditActionDisplay(e.event_type) : "—"),
      actor: e.actor || "",
      target: (e.target && e.target !== e.device_id ? e.target : "") || "",
      deviceId: e.device_id || "",
    }));

  const normAudit = (d) =>
    (d.items || []).map((e) => ({
      ts: e.occurred_at || e.created_at,
      level: e.action || "info",
      category: "audit",
      title: auditActionDisplay(e.action || "") || String(e.action || "—"),
      actor: e.actor || "",
      target: e.target || "",
      deviceId: "",
    }));

  // ---------------------------------------------------------------- fetch
  const currentFilters = () => ({
    q: ($("#alogSearch", view)?.value || "").trim(),
    device: ($("#alogDevice", view)?.value || "").trim(),
    actor: ($("#alogActor", view)?.value || "").trim(),
    level: ($("#alogLevel", view)?.value || "").trim(),
  });

  const fetchRows = async (f) => {
    if (source === "audit") {
      const qs = new URLSearchParams({ limit: "200" });
      if (f.actor) qs.set("actor", f.actor);
      if (f.device) qs.set("target", f.device);
      if (f.q) qs.set("action", f.q);
      const d = await api(buildAuditPath(queryFromSearchParams(qs)), { timeoutMs: TELEMETRY_AUDIT_TIMEOUT_MS });
      return normAudit(d);
    }
    if (source === "channels") {
      const qs = new URLSearchParams({ limit: "200", since_hours: String(rangeHours) });
      const d = await api(buildActivitySignalsPath(queryFromSearchParams(qs)), { timeoutMs: TELEMETRY_SIGNALS_TIMEOUT_MS });
      return normChannels(d);
    }
    if (source === "events") {
      const qs = new URLSearchParams({ limit: "200" });
      if (f.level) qs.set("min_level", f.level);
      if (f.device) qs.set("device_id", f.device);
      if (f.q) qs.set("q", f.q);
      const d = await api(buildEventsPath(queryFromSearchParams(qs)), { timeoutMs: TELEMETRY_EVENTS_TIMEOUT_MS });
      return normEvents(d);
    }
    const qs = new URLSearchParams({ source: "log", limit: "200" });
    if (f.device) qs.set("device_id", f.device);
    if (f.q) qs.set("q", f.q);
    const d = await api(buildActivityRecordsPath(queryFromSearchParams(qs)), { timeoutMs: TELEMETRY_ACTIVITY_TIMEOUT_MS });
    return normActivity(d);
  };

  const applyClientFilters = (list, f) => {
    const cutoff = Date.now() / 1000 - rangeHours * 3600;
    return list.filter((r) => {
      const ep = tsToEpochSec(r.ts);
      if (Number.isFinite(ep) && ep < cutoff) return false;
      if (f.actor && !String(r.actor).toLowerCase().includes(f.actor.toLowerCase())) return false;
      if (f.q && source === "channels") {
        const hay = `${r.title} ${r.actor} ${r.category} ${r.target}`.toLowerCase();
        if (!hay.includes(f.q.toLowerCase())) return false;
      }
      if (f.device && source === "channels" && !String(r.deviceId).toLowerCase().includes(f.device.toLowerCase())) return false;
      if (f.level && source !== "events" && source !== "audit") {
        const want = levelBucket(f.level);
        if (levelBucket(r.level) !== want) return false;
      }
      if (f.q && source === "audit") {
        const hay = `${r.title} ${r.actor} ${r.target} ${r.level}`.toLowerCase();
        if (!hay.includes(f.q.toLowerCase())) return false;
      }
      return true;
    });
  };

  // ---------------------------------------------------------------- renderers
  const renderTimeline = () => {
    const el = $("#alogFeed", view);
    if (!el) return;
    if (rows.length === 0) {
      setHtmlIfChanged(el, `<p class="alog-empty">No entries in this window.</p>`);
      return;
    }
    const html = rows
      .map((r) => {
        const b = levelBucket(r.level);
        const dev = r.deviceId
          ? `<button type="button" class="mono tenant-admin-page__link" data-cc-go="devices" data-cc-id="${escapeHtml(String(r.deviceId || ""))}">${escapeHtml(r.deviceId)}</button>`
          : "";
        const metaBits = [];
        if (r.actor) metaBits.push(`<span>${escapeHtml(r.actor)}</span>`);
        if (r.target) metaBits.push(`<span class="mono">${escapeHtml(r.target)}</span>`);
        if (dev) metaBits.push(dev);
        return `<article class="alog-item">
          <div class="alog-item__time">
            <span class="alog-item__ts">${escapeHtml(fmtTs(r.ts))}</span>
            <span class="alog-item__rel">${escapeHtml(fmtRel(r.ts))}</span>
          </div>
          <div class="alog-item__ico alog-dot--${b}" aria-hidden="true">${escapeHtml(String(r.category || "").slice(0, 1).toUpperCase() || "·")}</div>
          <div class="alog-item__body">
            <div class="alog-item__title">${escapeHtml(r.title)}</div>
            <div class="alog-item__meta">${metaBits.join('<span aria-hidden="true">·</span>') || '<span class="muted">—</span>'}</div>
          </div>
          <div class="alog-item__tags">
            <span class="alog-chip alog-chip--${b}">${escapeHtml(String(r.level || b))}</span>
            <span class="alog-chip">${escapeHtml(String(r.category || "—"))}</span>
          </div>
        </article>`;
      })
      .join("");
    setHtmlIfChanged(el, html);
  };

  const renderDonut = () => {
    const counts = new Map();
    for (const r of rows) {
      const k = String(r.category || "other");
      counts.set(k, (counts.get(k) || 0) + 1);
    }
    const total = rows.length;
    const totalEl = $("#alogDonutTotal", view);
    if (totalEl) setTextIfChanged(totalEl, String(total));
    const sorted = [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 7);
    const donut = $("#alogDonut", view);
    const legend = $("#alogLegend", view);
    if (!donut || !legend) return;
    if (total === 0) {
      donut.style.background = "conic-gradient(rgba(148,163,184,0.25) 0 100%)";
      legend.replaceChildren();
      legend.innerHTML = `<p class="muted" style="font-size:12px;margin:0">No data.</p>`;
      return;
    }
    let acc = 0;
    const stops = [];
    const legendRows = [];
    sorted.forEach(([name, c], i) => {
      const col = DONUT_COLORS[i % DONUT_COLORS.length];
      const from = (acc / total) * 100;
      acc += c;
      const to = (acc / total) * 100;
      stops.push(`${col} ${from.toFixed(2)}% ${to.toFixed(2)}%`);
      const pct = Math.round((c / total) * 100);
      legendRows.push(
        `<div class="alog-legend__row"><span class="alog-legend__sw" style="background:${col}"></span><span class="alog-legend__name">${escapeHtml(name)}</span><span class="alog-legend__pct">${pct}%</span></div>`,
      );
    });
    donut.style.background = `conic-gradient(${stops.join(", ")})`;
    setHtmlIfChanged(legend, legendRows.join(""));
  };

  const renderBreakdown = () => {
    const counts = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
    for (const r of rows) counts[levelBucket(r.level)] += 1;
    const el = $("#alogBreak", view);
    if (!el) return;
    setHtmlIfChanged(
      el,
      BUCKETS.map(
        (b) => `<div class="alog-break__cell"><div class="alog-break__v alog-chip--${b.id}" style="background:none;padding:0">${counts[b.id]}</div><div class="alog-break__k">${b.label}</div></div>`,
      ).join(""),
    );
  };

  const renderRank = (selId, keyFn, emptyLabel) => {
    const counts = new Map();
    for (const r of rows) {
      const k = keyFn(r);
      if (!k) continue;
      counts.set(k, (counts.get(k) || 0) + 1);
    }
    const el = $(selId, view);
    if (!el) return;
    const sorted = [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5);
    if (sorted.length === 0) {
      setHtmlIfChanged(el, `<p class="muted" style="font-size:12px;margin:0">${escapeHtml(emptyLabel)}</p>`);
      return;
    }
    setHtmlIfChanged(
      el,
      sorted
        .map(
          ([name, c], i) =>
            `<div class="alog-rank__row"><span class="alog-rank__n">${i + 1}</span><span class="alog-rank__name">${escapeHtml(name)}</span><span class="alog-rank__c">${c}</span></div>`,
        )
        .join(""),
    );
  };

  const renderInsight = () => {
    const titleEl = $("#alogInsightTitle", view);
    const bodyEl = $("#alogInsightBody", view);
    if (!titleEl || !bodyEl) return;
    if (rows.length === 0) {
      setTextIfChanged(titleEl, "No recent activity");
      setTextIfChanged(bodyEl, "Nothing recorded in the selected window.");
      return;
    }
    const crit = rows.filter((r) => levelBucket(r.level) === "critical").length;
    const high = rows.filter((r) => levelBucket(r.level) === "high").length;
    const actorCounts = new Map();
    for (const r of rows) if (r.actor) actorCounts.set(r.actor, (actorCounts.get(r.actor) || 0) + 1);
    const topActor = [...actorCounts.entries()].sort((a, b) => b[1] - a[1])[0];
    if (crit > 0) {
      setTextIfChanged(titleEl, "Elevated critical activity");
      setTextIfChanged(bodyEl, `${crit} critical and ${high} high-severity entries detected in the last ${rangeHours >= 24 ? rangeHours / 24 + "d" : rangeHours + "h"}. Review the timeline above.`);
    } else if (topActor && topActor[1] >= 5) {
      setTextIfChanged(titleEl, "Concentrated activity");
      setTextIfChanged(bodyEl, `${escapeHtml(topActor[0])} accounts for ${topActor[1]} of ${rows.length} entries — the most active actor in this window.`);
    } else {
      setTextIfChanged(titleEl, "Nominal activity");
      setTextIfChanged(bodyEl, `${rows.length} entries across ${new Set(rows.map((r) => r.category)).size} categories. No severity spikes detected.`);
    }
  };

  const renderChart = () => {
    const canvas = $("#alogChart", view);
    if (!canvas) return;
    const buckets = Math.min(48, Math.max(12, Math.round(rangeHours / (rangeHours <= 24 ? 1 : rangeHours <= 168 ? 6 : 48))));
    const now = Date.now() / 1000;
    const span = rangeHours * 3600;
    const series = new Array(buckets).fill(0);
    for (const r of rows) {
      const ep = tsToEpochSec(r.ts);
      if (!Number.isFinite(ep)) continue;
      const age = now - ep;
      if (age < 0 || age > span) continue;
      const idx = Math.min(buckets - 1, Math.floor(((span - age) / span) * buckets));
      series[idx] += 1;
    }
    if (typeof drawOpsSparkline === "function") drawOpsSparkline(canvas, series.length ? series : [0, 0], "#6366f1", null);
  };

  const renderHeatmap = () => {
    const el = $("#alogHeatmap", view);
    if (!el) return;
    const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const grid = Array.from({ length: 7 }, () => new Array(24).fill(0));
    let max = 0;
    for (const r of rows) {
      const ep = tsToEpochSec(r.ts);
      if (!Number.isFinite(ep)) continue;
      const dt = new Date(ep * 1000);
      const d = dt.getDay();
      const h = dt.getHours();
      grid[d][h] += 1;
      if (grid[d][h] > max) max = grid[d][h];
    }
    const cell = (v) => {
      if (!v || max === 0) return "rgba(148, 163, 184, 0.12)";
      const t = v / max;
      const a = 0.18 + t * 0.72;
      return `rgba(99, 102, 241, ${a.toFixed(2)})`;
    };
    setHtmlIfChanged(
      el,
      grid
        .map(
          (rowArr, d) =>
            `<div class="alog-heatmap__row"><span class="alog-heatmap__day">${DAYS[d]}</span>${rowArr
              .map((v) => `<span class="alog-heatmap__cell" style="background:${cell(v)}" title="${v}"></span>`)
              .join("")}</div>`,
        )
        .join(""),
    );
  };

  const renderAll = () => {
    renderTimeline();
    renderDonut();
    renderBreakdown();
    renderRank("#alogTopUsers", (r) => r.actor, "No actors recorded.");
    renderRank("#alogTopSources", (r) => r.deviceId || r.category, "No sources recorded.");
    renderInsight();
    renderChart();
    renderHeatmap();
    const countEl = $("#alogCount", view);
    if (countEl) setTextIfChanged(countEl, `${rows.length} entries`);
    const titleEl = $("#alogTimelineTitle", view);
    const srcLabel = (SOURCES.find((s) => s.id === source) || {}).label || "Audit";
    if (titleEl) setTextIfChanged(titleEl, `${srcLabel} Timeline`);
  };

  // ---------------------------------------------------------------- load
  const load = async () => {
    const f = currentFilters();
    const feed = $("#alogFeed", view);
    if (feed && rows.length === 0) setHtmlIfChanged(feed, `<p class="alog-empty">Loading…</p>`);
    let list;
    try {
      list = await fetchRows(f);
    } catch (e) {
      if (!isRouteCurrent(routeSeq)) return;
      toast(e.message || String(e), "err");
      if (feed) setHtmlIfChanged(feed, `<p class="alog-empty">Failed to load.</p>`);
      return;
    }
    if (!isRouteCurrent(routeSeq)) return;
    rows = applyClientFilters(list, f);
    renderAll();
  };

  const stopEventsLive = () => {
    if (window.__evSSE) window.__evSSE.close();
    window.__evSSE = null;
    setTextIfChanged($("#evLive", view), "Polling");
  };
  const startEventsLive = () => {
    stopEventsLive();
    if (source !== "events") return;
    setTextIfChanged($("#evLive", view), "Connecting");
    const filters = currentFilters();
    const query = {};
    if (filters.level) query.min_level = filters.level;
    if (filters.device) query.device_id = filters.device;
    if (filters.q) query.q = filters.q;
    const path = pickEventsStreamPath(query, preferSseGatewayV2Stream(state.health));
    window.__evSSE = openEventsLiveStream({
      url: apiBase() + path,
      token: getToken(),
      onOpen: () => setTextIfChanged($("#evLive", view), "Live"),
      onFallback: () => setTextIfChanged($("#evLive", view), pollFallbackBadgeLabel()),
      onEvent: (event) => {
        if (!isRouteCurrent(routeSeq) || source !== "events") return;
        rows = applyClientFilters([...normEvents({ items: [event] }), ...rows], currentFilters()).slice(0, 200);
        renderAll();
      },
    });
  };
  window.__eventsStreamResume = startEventsLive;

  // ---------------------------------------------------------------- export
  const exportHttpErrorMessage = async (r) => {
    const status = Number(r.status) || 0;
    let bodyText = "";
    try {
      bodyText = await r.text();
    } catch (_e) {
      bodyText = "";
    }
    let detail = "";
    try {
      const j = JSON.parse(bodyText);
      detail = String((j && (j.detail || j.message)) || "");
    } catch (_e) {
      detail = String(bodyText || "").slice(0, 240);
    }
    if (status === 404) {
      return detail || "Exports are not enabled on this server.";
    }
    if (status === 429) {
      return detail || "Export rate-limited — try again shortly.";
    }
    if (status === 403) {
      return detail || "Export denied for this account.";
    }
    if (status === 503) {
      return detail || "Server busy — retry export shortly.";
    }
    return (typeof softApiError === "function"
      ? softApiError(detail || `${status}`)
      : (detail || `${status} ${r.statusText || "export failed"}`));
  };

  const exportCsv = async (kind) => {
    if (!isRouteCurrent(routeSeq)) return;
    let scopeTok = "";
    try {
      scopeTok = await mintExportCsvScopeTokenIfRequired();
    } catch (e) {
      toast(e.message || e, "err");
      return;
    }
    if (!isRouteCurrent(routeSeq)) return;
    const f = currentFilters();
    const p = new URLSearchParams();
    p.set("limit", "8000");
    if (kind === "events") {
      if (f.level) p.set("min_level", f.level);
      if (f.device) p.set("device_id", f.device);
      if (f.q) p.set("q", f.q);
    } else {
      if (f.actor) p.set("actor", f.actor);
      if (f.device) p.set("target", f.device);
    }
    if ((state.me || {}).export_csv_require_scope_token) {
      if (!scopeTok) {
        toast("Could not authorize this download — exports may be turned off. Ask platform staff.", "err");
        return;
      }
      p.set("scope_token", scopeTok);
    }
    const builder =
      kind === "events" ? buildEventsExportCsvPath : buildAuditExportCsvPath;
    const url = apiBase() + builder(queryFromSearchParams(p));
    const hdrs = {};
    const t = getToken();
    if (t) hdrs.Authorization = "Bearer " + t;
    try {
      const r = await fetch(url, { credentials: "include", headers: hdrs });
      if (!r.ok) throw new Error(await exportHttpErrorMessage(r));
      const blob = await r.blob();
      triggerDownloadFromBlob(blob, kind === "events" ? "croc_sentinel_events.csv" : "croc_sentinel_audit.csv");
      toast("CSV downloaded", "ok");
    } catch (e) {
      toast(e.message || e, "err");
    }
  };

  /**
   * @param {"audit_pdf"|"audit_cover"|"events_cover"} kind
   */
  const exportPdf = async (kind) => {
    if (!isRouteCurrent(routeSeq)) return;
    let scopeTok = "";
    try {
      scopeTok = await mintExportCsvScopeTokenIfRequired();
    } catch (e) {
      toast(e.message || e, "err");
      return;
    }
    if (!isRouteCurrent(routeSeq)) return;
    const f = currentFilters();
    const p = new URLSearchParams();
    if (kind === "events_cover") {
      if (f.level) p.set("min_level", f.level);
      if (f.device) p.set("device_id", f.device);
      if (f.q) p.set("q", f.q);
    } else {
      p.set("limit", "5000");
      if (f.actor) p.set("actor", f.actor);
      if (f.device) p.set("target", f.device);
    }
    if ((state.me || {}).export_csv_require_scope_token) {
      if (!scopeTok) {
        toast("Could not authorize this download — exports may be turned off. Ask platform staff.", "err");
        return;
      }
      p.set("scope_token", scopeTok);
    }
    const builder =
      kind === "audit_pdf"
        ? buildAuditExportPdfPath
        : kind === "audit_cover"
          ? buildAuditExportCoverPdfPath
          : buildEventsExportCoverPdfPath;
    const filename =
      kind === "audit_pdf"
        ? "croc_sentinel_audit.pdf"
        : kind === "audit_cover"
          ? "croc_sentinel_audit_cover.pdf"
          : "croc_sentinel_events_cover.pdf";
    const url = apiBase() + builder(queryFromSearchParams(p));
    const hdrs = {};
    const t = getToken();
    if (t) hdrs.Authorization = "Bearer " + t;
    try {
      const r = await fetch(url, { credentials: "include", headers: hdrs });
      if (!r.ok) throw new Error(await exportHttpErrorMessage(r));
      const blob = await r.blob();
      triggerDownloadFromBlob(blob, filename);
      toast("PDF downloaded", "ok");
    } catch (e) {
      toast(e.message || e, "err");
    }
  };

  // ---------------------------------------------------------------- wiring
  const tabsEl = $("#alogTabs", view);
  if (tabsEl) {
    tabsEl.addEventListener("click", (ev) => {
      const btn = ev.target.closest("[data-alog-source]");
      if (!btn) return;
      const next = btn.getAttribute("data-alog-source");
      if (!next || next === source) return;
      location.hash = `#/audit-log?source=${encodeURIComponent(next)}`;
    });
  }

  const rangeEl = $("#alogRange", view);
  if (rangeEl) {
    rangeEl.addEventListener("click", (ev) => {
      const btn = ev.target.closest("[data-alog-range]");
      if (!btn) return;
      const h = parseInt(btn.getAttribute("data-alog-range"), 10);
      if (!h || h === rangeHours) return;
      rangeHours = h;
      for (const b of rangeEl.querySelectorAll(".alog-pill")) b.classList.toggle("alog-pill--on", b === btn);
      void load();
    });
  }

  $("#alogApply", view)?.addEventListener("click", () => void load().then(startEventsLive));
  $("#alogRefresh", view)?.addEventListener("click", () => void load());
  $("#alogSearch", view)?.addEventListener("keydown", (e) => {
    if (e.key === "Enter") void load();
  });
  $("#alogActor", view)?.addEventListener("keydown", (e) => {
    if (e.key === "Enter") void load();
  });
  $("#alogExportEvents", view)?.addEventListener("click", () => void exportCsv("events"));
  if (canAuditExport) $("#alogExportAudit", view)?.addEventListener("click", () => void exportCsv("audit"));
  if (eventsCoverOn) $("#alogExportEventsCover", view)?.addEventListener("click", () => void exportPdf("events_cover"));
  if (auditCoverOn) $("#alogExportAuditCover", view)?.addEventListener("click", () => void exportPdf("audit_cover"));
  if (auditPdfOn) $("#alogExportAuditPdf", view)?.addEventListener("click", () => void exportPdf("audit_pdf"));

  await load();
  startEventsLive();
  scheduleRouteTicker(
    routeSeq,
    "audit-log-live-reload",
    () => (source === "events" && window.__evSSE?.readyState === 1 ? undefined : load()),
    15000,
  );
});


// ── auto-sentinel.route.js ─────────────────────────────────────
/**
 * Route: #/auto-sentinel — Administration · AI Policy (CONV-3 / tip550).
 * Soft redirect target historically for #/ai / #/governance (F18 alias purge tip581).
 * Live Allow/Hold Decision Surface stays on Home `#/overview` — this page is policy & history only.
 * expiration_tip: 550 (CTA rename wave; route hash kept)
 * `#/intelligence` **DELETED** tip559 — do not document as a live redirect.
 */

registerRoute("auto-sentinel", async (view, _args, routeSeq) => {
  setCrumb("AI Policy");

  if (!hasRole("user")) {
    mountView(view, workspaceBlockedHtml({
      title: "AI Policy",
      detail: "Sign in to open AI Policy.",
      backHref: "#/settings",
      backLabel: "Settings",
    }));
    return;
  }

  if (!hasRole("admin")) {
    mountView(view, workspaceBlockedHtml({
      title: "AI Policy",
      detail: "AI Policy is for admin accounts. Your field steps stay on Assignments and Home — Accept → On the way → On site.",
      backHref: "#/overview",
      backLabel: "Home workbench",
    }));
    return;
  }

  ensureAccountSettingsStyles();
  const me = state.me || {};
  const roleNorm = String(me.role || "").trim().toLowerCase();
  const roleLabels = { superadmin: "Platform Admin", admin: "Admin", user: "User", sa_staff: "Staff" };
  const roleLabel = escapeHtml(roleLabels[roleNorm] || me.role || "—");
  const username = escapeHtml(me.username || "—");
  const navItems = buildSettingsHubNavItems({ me, hasRole, can });
  const navHtml = buildAccountSettingsNavHtml(navItems, "auto-sentinel", false);

  const params = new URLSearchParams((location.hash.split("?")[1] || ""));
  let selectedId = String(params.get("id") || "").trim();

  const aiOn = catalogEnabled(me, "ai_incident");

  const mountShell = (contentHtml, maicLive = null) => {
    const live = maicLive && typeof maicLive === "object" ? maicLive : {};
    mountView(
      view,
      buildAccountSettingsSubrouteHtml({
        activeNavId: "auto-sentinel",
        me,
        hasRole,
        can,
        roleLabel,
        username,
        navHtml,
        contentHtml,
        navTitle: "Settings",
        hideRail: true,
        waitingN: live.waitingN,
        category: live.category,
        riskScore: live.riskScore,
        reasonLine: live.reasonLine,
        responderHint: live.responderHint,
      }),
    );
  };

  mountShell(`<div class="croc-as-workbench" data-as-workbench="1">
    <header class="croc-as-workbench__hero glass-panel glass-panel--panel">
      <h2 class="croc-as-workbench__title">AI Policy</h2>
      <p class="muted croc-as-workbench__lede">Tonight's next step stays on Home. This page never Holds or Takes over. Scene text is preview.</p>
      <div class="croc-as-pulse" data-as-pulse="watching" aria-live="polite">
        <div class="croc-as-pulse__stages" role="list">
          <span class="croc-as-pulse__stage is-lit" role="listitem"><i aria-hidden="true"></i>Watching</span>
          <span class="croc-as-pulse__stage" role="listitem"><i aria-hidden="true"></i>Acting</span>
          <span class="croc-as-pulse__stage" role="listitem"><i aria-hidden="true"></i>Needs you</span>
        </div>
        <p class="croc-as-pulse__line">The Watch is on the loop — Continue or Needs You, not a user rule</p>
      </div>
    </header>
    <div class="croc-as-workbench__grid">
      <section class="croc-as-panel glass-panel glass-panel--panel" aria-label="Interrupt queue">
        <div class="croc-as-panel__head">
          <div>
            <p class="incident-workspace__eyebrow">Live</p>
            <h3 class="croc-as-panel__title">Interrupt queue</h3>
          </div>
        </div>
        <p class="muted croc-as-empty">No plans waiting. The Watch plans new alarms automatically.</p>
      </section>
    </div>
  </div>`);

  const quietScanRecent = async (limit = 3, { force = false } = {}) => {
    if (!aiOn) return { ok: 0 };
    const key = "croc_auto_sentinel_settings_scan_at";
    try {
      const last = Number(sessionStorage.getItem(key) || 0);
      if (!force && Date.now() - last < 5 * 60 * 1000) return { ok: 0, skipped: true };
    } catch (_) {}
    let recent = [];
    try {
      const pack = await api("/incidents?limit=12", { timeoutMs: INTELLIGENCE_TIMEOUT_MS });
      recent = (Array.isArray(pack && pack.items) ? pack.items : [])
        .filter((it) => it && (it.event_id || it.incident_id) && it.device_id)
        .slice(0, Math.max(1, Number(limit) || 3));
    } catch (_) {
      return { ok: 0, fail: 1 };
    }
    if (!recent.length) return { ok: 0, empty: true };
    try { sessionStorage.setItem(key, String(Date.now())); } catch (_) {}
    let ok = 0;
    for (const it of recent) {
      if (!isRouteCurrent(routeSeq)) return { ok, aborted: true };
      const eventId = Number(it.event_id != null ? it.event_id : it.incident_id);
      const deviceId = String(it.device_id || "").trim();
      if (!(eventId > 0) || !deviceId) continue;
      try {
        await api(buildPostAiAnalyzePath(), {
          method: "POST",
          body: {
            alarm_id: `alarm-${eventId}`,
            device_id: deviceId,
            event_id: eventId,
            event_type: String(it.event_type || "alarm.trigger"),
          },
          timeoutMs: INTELLIGENCE_TIMEOUT_MS,
        });
        ok += 1;
      } catch (_) {}
    }
    return { ok };
  };

  const buildRecentOpts = async () => {
    let recent = [];
    try {
      const pack = await api("/incidents?limit=12", { timeoutMs: INTELLIGENCE_TIMEOUT_MS });
      recent = (Array.isArray(pack && pack.items) ? pack.items : [])
        .filter((it) => it && (it.event_id || it.incident_id) && it.device_id)
        .filter((it) => String(it.event_type || "").includes("alarm") || String(it.event_type || "").includes("panic") || !it.event_type);
    } catch (_) { recent = []; }
    if (!recent.length) return `<option value="">No recent alarms yet</option>`;
    return recent.map((it) => {
      const eid = String(it.event_id != null ? it.event_id : it.incident_id);
      const label = typeof softIncidentPickerLabel === "function"
        ? softIncidentPickerLabel(it)
        : softOperatorNote(String(it.summary || eid));
      return `<option value="${escapeHtml(eid)}" data-device="${escapeHtml(String(it.device_id || ""))}" data-type="${escapeHtml(String(it.event_type || "alarm.trigger"))}">${escapeHtml(label)}</option>`;
    }).join("");
  };

  const refreshDesk = async ({ forceScan = false, keepId = true } = {}) => {
    if (!isRouteCurrent(routeSeq)) return;
    if (!keepId) selectedId = "";

    if (!aiOn) {
      mountShell(buildAutoSentinelSettingsWorkbenchHtml({ aiOn: false }));
      return;
    }

    let items = [];
    let settings = {};
    try {
      const [listRes, setRes] = await Promise.allSettled([
        api(buildGetAiIncidentsPath(), { timeoutMs: INTELLIGENCE_TIMEOUT_MS }),
        api(buildGetAiSettingsPath(), { timeoutMs: INTELLIGENCE_TIMEOUT_MS }),
      ]);
      if (!isRouteCurrent(routeSeq)) return;
      if (listRes.status === "fulfilled") {
        items = Array.isArray(listRes.value && listRes.value.items) ? listRes.value.items : [];
      }
      if (setRes.status === "fulfilled") settings = setRes.value || {};
    } catch (_) {}

    // CONV-3: Policy desk must NOT auto-approve. Live Continue/Hold = Home Decision Surface only.
    const waiting = items.filter((it) => {
      const st = String(it.status || "").toLowerCase();
      return !st || st === "pending" || st === "open";
    });
    if (waiting.length === 0) {
      const scanned = await quietScanRecent(2, { force: !!forceScan });
      if (scanned && scanned.ok > 0) {
        try {
          const again = await api(buildGetAiIncidentsPath(), { timeoutMs: INTELLIGENCE_TIMEOUT_MS });
          items = Array.isArray(again && again.items) ? again.items : items;
        } catch (_) {}
      }
    }

    try {
      if (typeof rememberDeviceLabels === "function") {
        rememberDeviceLabels(items.map((it) => ({
          device_id: it.device_id,
          display_label: it.display_label || it.device_label || "",
        })));
      }
    } catch (_) {}

    let detailHtml = "";
    if (selectedId) {
      try {
        const detail = await api(buildGetAiIncidentPath(selectedId), { timeoutMs: INTELLIGENCE_TIMEOUT_MS });
        if (!isRouteCurrent(routeSeq)) return;
        detailHtml = buildAutoPlanDetailHtml(detail, selectedId);
      } catch (e) {
        detailHtml = `<p class="muted ops-inset">${escapeHtml(typeof softApiError === "function" ? softApiError(e) : "Could not load plan")}</p>`;
      }
    }

    const waitingN = items.filter((it) => {
      const st = String(it.status || "").toLowerCase();
      return !st || st === "pending" || st === "open";
    }).length;
    const pulse = {
      state: waitingN ? "interrupt" : "watching",
      line: waitingN
        ? (waitingN === 1 ? "1 plan needs your interrupt" : `${waitingN} plans need your interrupt`)
        : "The Watch is on the loop — Continue or Needs You, not a user rule",
    };
    const maicLive = typeof maicFieldsFromAiItems === "function"
      ? maicFieldsFromAiItems(items)
      : { waitingN };

    const recentOptsHtml = await buildRecentOpts();
    if (!isRouteCurrent(routeSeq)) return;

    let tipsHtml = "";
    try {
      if (catalogEnabled(me, "predictive")) {
        const tipPack = await api(buildGetPredictiveMaintenancePath(), { timeoutMs: INTELLIGENCE_TIMEOUT_MS });
        const tips = Array.isArray(tipPack && tipPack.items)
          ? tipPack.items
          : (Array.isArray(tipPack && tipPack.tips) ? tipPack.tips : []);
        if (tips.length) {
          tipsHtml = `<ul class="croc-as-tips" style="margin:0;padding-left:1.1em;line-height:1.5">
            ${tips.slice(0, 6).map((t) => {
              const text = softOperatorNote(String(
                (t && (t.message || t.tip || t.summary || t.title || t.detail)) || t || "",
              ));
              return text ? `<li>${escapeHtml(text)}</li>` : "";
            }).filter(Boolean).join("") || "<li class='muted'>No tips right now.</li>"}
          </ul>`;
        } else {
          tipsHtml = `<p class="muted" style="margin:0;font-size:13px">Devices look quiet — no maintenance tips right now.</p>`;
        }
      }
    } catch (_) {
      tipsHtml = `<p class="muted" style="margin:0;font-size:13px">Tips unavailable right now — readiness and devices stay open.</p>`;
    }

    mountShell(buildAutoSentinelSettingsWorkbenchHtml({
      aiOn: true,
      items,
      settings,
      selectedId,
      detailHtml,
      recentOptsHtml,
      tipsHtml,
      pulse,
    }), maicLive);
    wireDesk();
  };

  const wireDesk = () => {
    const root = view.querySelector("[data-as-workbench]") || view;
    if (!root || root.dataset.asWired === "1") return;
    root.dataset.asWired = "1";

    root.addEventListener("click", async (ev) => {
      const t = ev.target && ev.target.closest
        ? ev.target.closest("[data-as-open],#asQuietScanBtn,#asRefreshBtn,#asSetSaveBtn,#asSetRollbackBtn,#asForceScanBtn")
        : null;
      if (!t || !root.contains(t)) return;

      if (t.id === "asRefreshBtn") {
        t.disabled = true;
        await refreshDesk({ forceScan: false });
        return;
      }

      if (t.id === "asQuietScanBtn") {
        t.disabled = true;
        const line = $("#asPulseLine", view);
        if (line) line.textContent = "Sentinel scanning recent alarms…";
        const res = await quietScanRecent(3, { force: true });
        if (!isRouteCurrent(routeSeq)) return;
        if (res && res.ok > 0) {
          toast(res.ok === 1 ? "Auto plan ready — decide on Home map" : `${res.ok} auto plans ready — decide on Home map`, "ok");
        } else if (res && res.empty) {
          toast("No recent alarms to scan yet", "warn");
        } else {
          toast("Scan finished — Sentinel keeps watching", "ok");
        }
        await refreshDesk({ forceScan: false });
        return;
      }

      if (t.id === "asForceScanBtn") {
        const sel = $("#asIncidentPick", view);
        const opt = sel && sel.selectedOptions && sel.selectedOptions[0];
        const eventId = Number((sel && sel.value) || 0);
        const deviceId = String((opt && opt.getAttribute("data-device")) || "").trim();
        const eventType = String((opt && opt.getAttribute("data-type")) || "alarm.trigger").trim();
        const msg = $("#asScanMsg", view);
        if (!(eventId > 0) || !deviceId) {
          if (msg) msg.textContent = "Choose a recent alarm first.";
          return;
        }
        if (!(await softConfirm("Let Sentinel scan this alarm now?"))) return;
        t.disabled = true;
        if (msg) msg.textContent = "Sentinel scanning…";
        try {
          const pack = await api(buildPostAiAnalyzePath(), {
            method: "POST",
            body: {
              alarm_id: `alarm-${eventId}`,
              device_id: deviceId,
              event_id: eventId,
              event_type: eventType,
            },
            timeoutMs: INTELLIGENCE_TIMEOUT_MS,
          });
          if (!isRouteCurrent(routeSeq)) return;
          const rec = pack && pack.record ? pack.record : pack;
          selectedId = String((rec && (rec.incident_id || rec.id)) || "");
          toast("Auto plan ready — decide on Home map", "ok");
          await refreshDesk({ forceScan: false });
        } catch (e) {
          if (msg) msg.textContent = typeof softApiError === "function" ? softApiError(e) : "Scan failed";
          toast(typeof softApiError === "function" ? softApiError(e) : "Scan failed", "err");
          t.disabled = false;
        }
        return;
      }

      if (t.id === "asSetSaveBtn") {
        const msg = $("#asSetMsg", view);
        if (msg) msg.textContent = "Watch brain is constitution — thinking style and auto-continue are not tenant rules.";
        toast("Watch brain is constitution — not a tenant rule desk", "ok");
        return;
      }

      if (t.id === "asSetRollbackBtn") {
        if (!(await softConfirm("Reset stored overlay to deployment defaults?"))) return;
        t.disabled = true;
        try {
          await api(buildPatchAiSettingsPath(), {
            method: "PATCH",
            body: { rollback: true },
            timeoutMs: INTELLIGENCE_TIMEOUT_MS,
          });
          toast("Policy reset", "ok");
          await refreshDesk({ forceScan: false });
        } catch (e) {
          toast(typeof softApiError === "function" ? softApiError(e) : "Could not reset", "err");
          t.disabled = false;
        }
        return;
      }

      const openId = t.getAttribute("data-as-open");
      if (openId) {
        selectedId = openId;
        await refreshDesk({ forceScan: false });
        if (!isRouteCurrent(routeSeq)) return;
        try {
          const slot = view.querySelector("#asDetailSlot");
          if (slot) slot.scrollIntoView({ behavior: "smooth", block: "nearest" });
        } catch (_) {}
      }
    });
  };

  try {
    await refreshDesk({ forceScan: false });
  } catch (e) {
    if (!isRouteCurrent(routeSeq)) return;
    mountShell(`<div class="glass-panel glass-panel--panel"><p class="muted">${escapeHtml(typeof softApiError === "function" ? softApiError(e) : "Could not open AI Policy")}</p></div>`);
  }
});


// ── automation.route.js ─────────────────────────────────────
/**
 * Route: #/automation — Automation engine rules / trigger / runs.
 */

function buildAutomationRunStatusHtml(run) {
  const r = run || {};
  const status = String(r.status || r.ok || r.result || "—");
  const when = String(r.created_at || r.ts || r.started_at || "—");
  const dryRun = r.dry_run === true || r.mode === "dry_run";
  const detail = String(r.detail || r.message || r.error || "");
  const badge = dryRun ? '<span class="badge neutral">Dry run</span>' : '<span class="badge ok">Live</span>';
  return `<tr>
    <td class="mono">${escapeHtml(when)}</td>
    <td>${badge} ${escapeHtml(status)}</td>
    <td>${detail ? escapeHtml(detail) : '<span class="muted">—</span>'}</td>
  </tr>`;
}

function buildAutomationRuleCardHtml(it) {
  const id = String(it.id || it.automation_id || "");
  const enabled = it.enabled !== false;
  return `<tr>
    <td class="mono"><button type="button" class="tenant-admin-page__link mono" data-cc-go="automation?id=${encodeURIComponent(id)}">${escapeHtml(id || "—")}</button></td>
    <td>${escapeHtml(String(it.name || "—"))}</td>
    <td>${enabled ? '<span class="badge online">on</span>' : '<span class="badge offline">off</span>'}</td>
    <td class="mono">${escapeHtml(String(it.trigger_kind || "—"))}</td>
    <td class="mono">${escapeHtml(String(it.action_type || "—"))}</td>
  </tr>`;
}

registerRoute("automation", async (view, _args, routeSeq) => {
  setCrumb("Automation");
  if (!hasRole("admin")) {
    mountView(view, `<div class="card"><p class="muted">Admin access required.</p></div>`);
    return;
  }
  if (!catalogEnabled(state.me, "automation")) {
    mountView(view, `<div class="card glass-panel glass-panel--panel">
      <h2>Auto rules</h2>
      <span class="badge neutral">Verify</span>
      <p class="muted" style="margin-top:10px;line-height:1.45">Rules that act during incidents — notify, escalate, or hand off — appear here when auto rules are opened for verification.</p>
      <p style="margin-top:12px"><button type="button" class="btn secondary btn-tap" data-cc-go="overview">Back to Home</button></p>
    </div>`);
    return;
  }

  const params = new URLSearchParams((location.hash.split("?")[1] || ""));
  const selectedId = String(params.get("id") || "").trim();
  mountView(view, `<div class="card"><p class="muted">Loading automations…</p></div>`);

  const renderError = (msg) => {
    if (/503|disabled|unavailable|not.?enabled/i.test(msg)) {
      mountView(view, capabilityUnavailableHtml({
        title: "Automation",
        detail: "Automation is not opened for this account yet.",
      }));
      return;
    }
    mountView(view, `<div class="card"><h2>Automation unavailable</h2><p class="muted">${escapeHtml(msg)}</p></div>`);
  };

  try {
    const pack = await api(buildGetAutomationsPath(), { timeoutMs: AUTOMATION_TIMEOUT_MS });
    if (!isRouteCurrent(routeSeq)) return;
    const items = Array.isArray(pack.items) ? pack.items : [];
    let runsHtml = "";
    if (selectedId) {
      try {
        const runs = await api(buildGetAutomationRunsPath(selectedId), { timeoutMs: AUTOMATION_TIMEOUT_MS });
        if (!isRouteCurrent(routeSeq)) return;
        const runItems = Array.isArray(runs.items) ? runs.items : (Array.isArray(runs.runs) ? runs.runs : []);
        runsHtml = `<div class="glass-panel glass-panel--inset automation-status-card" style="margin-top:14px;padding:12px">
          <div class="row" style="justify-content:space-between;gap:8px;align-items:center;flex-wrap:wrap">
            <strong>Runs · ${escapeHtml(selectedId)}</strong>
            <button class="btn sm btn-tap" type="button" id="autoTriggerBtn">Trigger now (live)</button>
          </div>
          <p class="muted" style="margin-top:6px">Manual trigger executes against the live automation engine.</p>
          <table class="table" style="margin-top:8px"><thead><tr><th>When</th><th>Status</th><th>Detail</th></tr></thead>
            <tbody>${runItems.map((r) => buildAutomationRunStatusHtml(r)).join("") || `<tr><td colspan="3" class="muted">No runs yet.</td></tr>`}</tbody></table>
        </div>`;
      } catch (e) {
        runsHtml = `<p class="muted" style="margin-top:12px">${escapeHtml(String(e.message || e))}</p>`;
      }
    }

    const rows = items.map((it) => buildAutomationRuleCardHtml(it)).join("");

    mountView(view, `<div class="card glass-panel glass-panel--panel">
      <h2>Automation</h2>
      <p class="muted">${escapeHtml(String(pack.count != null ? pack.count : items.length))} rules for your account.</p>
      <div class="glass-panel glass-panel--inset automation-status-card" style="padding:12px;margin:12px 0">
        <strong>Create rule</strong>
        <p class="muted" style="margin-top:4px">New rules start enabled and run live when triggered.</p>
        <div class="inline-form" style="margin-top:8px">
          <label class="field"><span>Name</span><input id="autoName" placeholder="Night alarm escalate" /></label>
          <label class="field"><span>When</span><input id="autoTriggerKind" value="alarm" placeholder="alarm" /></label>
          <label class="field"><span>Then do</span><input id="autoActionType" value="notify" placeholder="notify" /></label>
        </div>
        <div class="row" style="margin-top:8px"><button class="btn btn-tap" type="button" id="autoCreateBtn">Create</button></div>
        <p id="autoCreateMsg" class="muted" aria-live="polite"></p>
      </div>
      <table class="table"><thead><tr><th>ID</th><th>Name</th><th>Enabled</th><th>Trigger</th><th>Action</th></tr></thead>
        <tbody>${rows || `<tr><td colspan="5" class="muted">No automations.</td></tr>`}</tbody></table>
      ${runsHtml}
    </div>`);

    const createBtn = $("#autoCreateBtn", view);
    if (createBtn) {
      createBtn.onclick = async () => {
        const name = String(($("#autoName", view) && $("#autoName", view).value) || "").trim();
        const triggerKind = String(($("#autoTriggerKind", view) && $("#autoTriggerKind", view).value) || "").trim();
        const actionType = String(($("#autoActionType", view) && $("#autoActionType", view).value) || "").trim();
        const msg = $("#autoCreateMsg", view);
        if (!name) {
          if (msg) msg.textContent = "Name is required.";
          return;
        }
        if (!window.confirm(`Create automation rule "${name}"?`)) return;
        createBtn.disabled = true;
        if (msg) msg.textContent = "Creating…";
        try {
          const created = await api(buildPostAutomationsPath(), {
            method: "POST",
            body: { name, trigger_kind: triggerKind || undefined, action_type: actionType || undefined },
            timeoutMs: AUTOMATION_TIMEOUT_MS,
          });
          if (!isRouteCurrent(routeSeq)) return;
          const newId = String((created && (created.id || created.automation_id || (created.item && created.item.id))) || "");
          toast("Automation created", "ok");
          location.hash = newId ? `#/automation?id=${encodeURIComponent(newId)}` : "#/automation";
        } catch (e) {
          if (!isRouteCurrent(routeSeq)) return;
          if (msg) msg.textContent = String(e.message || e);
          toast(softApiError(e), "err");
          createBtn.disabled = false;
        }
      };
    }

    const triggerBtn = $("#autoTriggerBtn", view);
    if (triggerBtn && selectedId) {
      triggerBtn.onclick = async () => {
        if (!window.confirm(`Trigger automation ${selectedId} now (live)?`)) return;
        triggerBtn.disabled = true;
        try {
          await api(buildPostAutomationTriggerPath(selectedId), {
            method: "POST",
            body: {},
            timeoutMs: AUTOMATION_TIMEOUT_MS,
          });
          toast("Triggered", "ok");
          location.hash = `#/automation?id=${encodeURIComponent(selectedId)}`;
        } catch (e) {
          toast(softApiError(e), "err");
          triggerBtn.disabled = false;
        }
      };
    }
  } catch (e) {
    if (!isRouteCurrent(routeSeq)) return;
    renderError(String(e.message || e));
  }
});


// ── billing.route.js ─────────────────────────────────────
/**
 * Route: #/billing — deep link → Settings overview (billing section).
 */

registerRoute("billing", async (view, _args, routeSeq) => {
  if (!(state.me && state.me.billing_enabled)) {
    location.hash = "#/settings";
    return;
  }
  setCrumb("Billing");
  await renderSettingsOverviewPage(view, {
    activeNavId: "billing",
    routeSeq,
    me: state.me,
    hasRole,
    can,
  });
});


// ── croc-fde.route.js ─────────────────────────────────────
/**
 * Route: #/croc-fde — Sentinel SA creates single-use CROC-FDE register links.
 * FDE belongs to Croc Sentinel; link opens CTA /invite once only.
 */

registerRoute("croc-fde", async (view) => {
  setCrumb("CROC-FDE");
  const me = state.me || {};
  const role = String(me.role || "");
  const caps = new Set((me.sa_capabilities || []).map(String));
  const canIssue =
    role === "superadmin" ||
    (role === "sa_staff" && (caps.has("sa_full_access") || caps.has("sa_user_admin")));
  if (!canIssue) {
    mountView(
      view,
      `<div class="croc-desk-stack">
        ${typeof incidentOsWorkflowWorkbenchHtml === "function"
          ? incidentOsWorkflowWorkbenchHtml({
              compact: true,
              activeId: "observe",
              title: "Incident OS · platform invite",
              lede: "CROC-FDE invites sit under platform ops — Home still runs the living alarm loop.",
              showMaic: false,
            })
          : ""}
        <div class="card glass-panel glass-panel--panel">
        <h2 style="margin:0 0 8px">CROC-FDE invite</h2>
        <p class="muted" style="margin:0;line-height:1.45">
          Platform SA only. Create a <b>single-use</b> register link for CROC-FDE
          (product of Croc Sentinel). Recipients open the link once — second use is rejected.
        </p>
        <div class="row" style="gap:8px;margin-top:14px;flex-wrap:wrap">
          <button type="button" class="btn sm btn-tap" data-cc-go="overview">Home</button>
          <button type="button" class="btn sm secondary btn-tap" data-cc-go="platform">Platform</button>
        </div>
      </div>
      </div>`,
    );
    return;
  }

  const defaultTenant = String(me.tenant_key || me.account_id || me.username || "ops").trim() || "ops";
  const fdeSay = (raw, fallback) => {
    const s = String(raw || "").trim();
    if (!s) return fallback || "—";
    if (typeof softOperatorNote === "function") {
      const out = String(softOperatorNote(s) || "").trim();
      if (out && out !== "—") return out;
    }
    return s;
  };
  const fdeTierSay = (raw) => {
    const t = String(raw || "").trim().toLowerCase();
    if (t === "partner") return "Partner · cannot publish";
    if (t === "tenant_fde") return "Site · customer self-train";
    return "Staff · employee";
  };
  const fdeSpaceSay = (raw) => {
    const s = String(raw || "").trim();
    if (!s) return "—";
    if (typeof looksLikeCode === "function" && looksLikeCode(s)) return "This Space";
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s)) return "This Space";
    if (/^[0-9a-f]{16,}$/i.test(s)) return "This Space";
    return s;
  };

  mountView(
    view,
    `<div class="croc-desk-stack">
      ${typeof incidentOsWorkflowWorkbenchHtml === "function"
        ? incidentOsWorkflowWorkbenchHtml({
            compact: true,
            activeId: "observe",
            title: "Incident OS · CROC-FDE",
            lede: "Issue a CROC-FDE register link — opens in a new tab.",
            showMaic: false,
          })
        : ""}
      ${typeof responseAiInnovHtml === "function" ? responseAiInnovHtml("during") : ""}
      <div class="card glass-panel glass-panel--panel">
      <h2 style="margin:0 0 8px">Create CROC-FDE register link</h2>
      <p class="muted" style="margin:0 0 12px;line-height:1.45;max-width:62ch">
        FDE belongs to <b>Croc Sentinel</b>. Create a <b>single-use</b> register link here.
        The recipient opens it in a <b>new tab</b> to register for this Space.
        Twin must use the <b>live store</b> — a Soft stand-in is blocked.
      </p>
      <div class="acc-settings-meta-grid" style="gap:12px;max-width:560px">
        <label class="field wide"><span>Recipient email</span>
          <input id="fdeEmail" type="email" placeholder="fde@company.com" autocomplete="off" />
        </label>
        <label class="field"><span>Site key</span>
          <input id="fdeTenant" value="${escapeHtml(defaultTenant)}" maxlength="80" />
        </label>
        <label class="field"><span>Space name</span>
          <input id="fdeSpace" value="Lobby" maxlength="120" />
        </label>
        <label class="field wide"><span>Existing Space (optional)</span>
          <input id="fdeSpaceId" placeholder="Leave empty to create a new Space" maxlength="80" autocomplete="off" />
        </label>
        <label class="field"><span>How long (hours)</span>
          <input id="fdeTtl" type="number" min="1" max="168" value="48" />
        </label>
        <label class="field"><span>Invite level</span>
          <select id="fdeTier">
            <option value="staff" selected>Staff · employee</option>
            <option value="partner">Partner · cannot publish</option>
            <option value="tenant_fde">Site · customer self-train</option>
          </select>
        </label>
      </div>
      <p class="honest muted" style="margin:10px 0 0;font-size:12px;line-height:1.4;max-width:62ch">
        Invite level is sealed by the server. Soft claims are not verify.
      </p>
      <div class="row" style="gap:8px;margin-top:14px;flex-wrap:wrap">
        <button type="button" class="btn btn-tap" id="fdeCreate">Create single-use link</button>
        <button type="button" class="btn secondary btn-tap" id="fdeHealth">Check Twin</button>
        <button type="button" class="btn ghost btn-tap" id="fdeRefresh">Refresh invites</button>
        <a class="btn ghost btn-tap" href="https://cta.crocnexus.com/" target="_blank" rel="noopener">Open Croc Twin Assets</a>
      </div>
      <p class="muted" id="fdeStatus" style="margin:12px 0 0"></p>
      <div id="fdeResult" class="glass-panel glass-panel--inset" style="margin-top:14px;padding:12px;display:none">
        <p style="margin:0 0 8px"><b>Copy now — shown once</b></p>
        <p class="mono" id="fdeLink" style="margin:0 0 10px;word-break:break-all;font-size:13px"></p>
        <div class="row" style="gap:8px;flex-wrap:wrap">
          <button type="button" class="btn btn-tap" id="fdeCopy">Copy link</button>
          <a class="btn secondary btn-tap" id="fdeOpen" href="#" target="_blank" rel="noopener">Open link</a>
        </div>
        <p class="muted" id="fdeMeta" style="margin:10px 0 0;font-size:12px;line-height:1.4"></p>
      </div>
      <h3 style="margin:18px 0 8px;font-size:1rem">Recent CROC-FDE invites</h3>
      <p class="muted" style="margin:0 0 8px;font-size:12px;line-height:1.4">
        Status only — the link is never shown again. Revoke a pending invite to block accept.
      </p>
      <div id="fdeList" class="settings-admin-list-host"><p class="muted">Loading…</p></div>
    </div>
    </div>`,
  );

  const statusEl = $("#fdeStatus", view);
  const resultEl = $("#fdeResult", view);
  const linkEl = $("#fdeLink", view);
  const metaEl = $("#fdeMeta", view);
  const openEl = $("#fdeOpen", view);
  const listEl = $("#fdeList", view);
  let lastUrl = "";

  const setStatus = (msg, kind) => {
    if (!statusEl) return;
    statusEl.textContent = msg || "";
    statusEl.className = kind === "err" ? "badge revoked" : "muted";
  };

  const fmtExp = (unix) => {
    const n = Number(unix || 0);
    if (!n) return "—";
    try {
      return new Date(n * 1000).toLocaleString();
    } catch (_) {
      return String(unix);
    }
  };

  const refreshList = async () => {
    if (!listEl) return;
    listEl.innerHTML = `<p class="muted">Loading…</p>`;
    try {
      const tenant = ($("#fdeTenant", view)?.value || defaultTenant).trim();
      const pack = await api(
        `/twin-bridge/fde-provision-grants?tenant_key=${encodeURIComponent(tenant)}&limit=40`,
        { timeoutMs: 20000, retries: 0 },
      );
      const items = Array.isArray(pack.items) ? pack.items : [];
      if (!items.length) {
        listEl.innerHTML = `<p class="muted">No CROC-FDE invites for this site yet.</p>`;
        return;
      }
      listEl.innerHTML = `<div class="table-wrap"><table class="table">
        <thead><tr><th>Email</th><th>Level</th><th>Status</th><th>Space</th><th>Expires</th><th></th></tr></thead>
        <tbody>${items
          .map((it) => {
            const st = String(it.status || "—");
            const canRevoke = st === "pending";
            return `<tr>
              <td>${escapeHtml(it.email || "—")}</td>
              <td style="font-size:12px">${escapeHtml(fdeTierSay(it.fde_tier))}</td>
              <td><span class="badge ${st === "pending" ? "ok" : "neutral"}">${escapeHtml(st)}</span></td>
              <td style="font-size:12px">${escapeHtml(fdeSpaceSay(it.space_id))}</td>
              <td style="font-size:12px">${escapeHtml(fmtExp(it.expires_at))}</td>
              <td>${
                canRevoke
                  ? `<button type="button" class="btn sm secondary btn-tap" data-revoke="${escapeHtml(
                      it.grant_id || it.id || "",
                    )}">Revoke</button>`
                  : "—"
              }</td>
            </tr>`;
          })
          .join("")}</tbody></table></div>`;
      listEl.querySelectorAll("[data-revoke]").forEach((btn) => {
        btn.addEventListener("click", async () => {
          const id = btn.getAttribute("data-revoke") || "";
          if (!id) return;
          if (!confirm("Revoke this pending invite? The recipient will no longer be able to accept.")) return;
          try {
            await api(`/twin-bridge/fde-provision-grants/${encodeURIComponent(id)}/revoke`, {
              method: "POST",
              body: {},
              timeoutMs: 20000,
              retries: 0,
            });
            toast("Invite revoked", "ok");
            await refreshList();
          } catch (e) {
            toast(fdeSay(e.message || e, "Could not revoke that invite."), "err");
          }
        });
      });
    } catch (e) {
      listEl.innerHTML = `<p class="badge revoked">${escapeHtml(fdeSay(e.message || e, "Could not load invites."))}</p>`;
    }
  };

  $("#fdeHealth", view)?.addEventListener("click", async () => {
    setStatus("Checking Twin…");
    try {
      const h = await api("/twin-bridge/fde-provision-grants/health", { timeoutMs: 15000, retries: 0 });
      if (!h.ok) {
        setStatus(fdeSay(h.error, "Twin is not reachable."), "err");
        return;
      }
      if (h.production_ready === false) {
        setStatus("Issuance is blocked until Twin live store is ready.", "err");
        return;
      }
      setStatus("Twin live store is ready.");
    } catch (e) {
      setStatus(fdeSay(e.message || e, "Could not reach Twin."), "err");
    }
  });

  $("#fdeRefresh", view)?.addEventListener("click", () => void refreshList());

  $("#fdeCreate", view)?.addEventListener("click", async () => {
    setStatus("");
    if (resultEl) resultEl.style.display = "none";
    const email = ($("#fdeEmail", view)?.value || "").trim();
    const tenant = ($("#fdeTenant", view)?.value || "ops").trim();
    const space = ($("#fdeSpace", view)?.value || "Lobby").trim();
    const reuseSpace = ($("#fdeSpaceId", view)?.value || "").trim();
    const hours = Number($("#fdeTtl", view)?.value || 48);
    const fdeTier = ($("#fdeTier", view)?.value || "staff").trim() || "staff";
    if (!email.includes("@")) {
      setStatus("Valid email required", "err");
      return;
    }
    const btn = $("#fdeCreate", view);
    if (btn) btn.disabled = true;
    try {
      const pack = await api("/twin-bridge/fde-provision-grants", {
        method: "POST",
        body: {
          email,
          tenant_key: tenant,
          space_name: space,
          space_id: reuseSpace || null,
          ttl_sec: Math.max(3600, Math.min(7 * 24 * 3600, Math.round(hours * 3600))),
          fde_tier: fdeTier,
        },
        timeoutMs: 30000,
        retries: 0,
      });
      lastUrl = String(pack.register_url || "");
      if (!lastUrl) throw new Error("The server did not return a register link.");
      if (linkEl) linkEl.textContent = lastUrl;
      if (openEl) openEl.href = lastUrl;
      if (metaEl) {
        metaEl.textContent = [
          pack.single_use !== false ? "One-time link" : "This link may be reused",
          fdeTierSay(pack.fde_tier),
          pack.space_name ? `Space ${pack.space_name}` : "",
        ]
          .filter(Boolean)
          .join(" · ");
      }
      if (resultEl) resultEl.style.display = "";
      setStatus("Grant link created — copy now. Second accept will fail.");
      toast("CROC-FDE invite ready", "ok");
      await refreshList();
    } catch (e) {
      setStatus(fdeSay(e.message || e, "Could not create that invite."), "err");
      toast(fdeSay(e.message || e, "Could not create that invite."), "err");
    } finally {
      if (btn) btn.disabled = false;
    }
  });

  $("#fdeCopy", view)?.addEventListener("click", async () => {
    if (!lastUrl) return;
    try {
      await navigator.clipboard.writeText(lastUrl);
      toast("Link copied", "ok");
    } catch (_) {
      prompt("Copy CROC-FDE link", lastUrl);
    }
  });

  void api("/twin-bridge/fde-provision-grants/health", { timeoutMs: 12000, retries: 0 })
    .then((h) => {
      if (!h.ok) {
        setStatus(fdeSay(h.error, "Twin is not reachable."), "err");
        return;
      }
      if (h.production_ready === false) {
        setStatus("Issuance is blocked until Twin live store and signing are ready.", "err");
        const createBtn = $("#fdeCreate", view);
        if (createBtn) createBtn.disabled = true;
        return;
      }
      setStatus("Twin live store is ready.");
    })
    .catch(() => {});
  await refreshList();
});


// ── devices.route.js ─────────────────────────────────────
/**
 * Route: #/devices — ZD-FLEET-ENTRY one-list · F18 bare soft purged tip583 (was expireTip **585**).
 *   Bare #/devices → Home (no soft redirect). Nav "All devices" uses navPath #/group/_all.
 *   #/devices?view=capabilities → Soft capability registry (not the list).
 *   #/devices/:id → device DETAIL (keep).
 * Do NOT bind devices.hash to #/group/_all (tip563 hijack).
 *
 * Build: split out of src/console.raw.js and concatenated as raw text by
 * scripts/build-dashboard.mjs after the monolith body. Shares scope with
 * helpers like $, mountView, api, registerRoute, state, toast, can, setCrumb
 * (defined in console.raw.js and the lib/ modules spliced at the top).
 */

function crocFmtHwModelPrimary(d) {
  const sku = String((d && d.hw_code_resolved) || "").trim();
  if (sku) return sku;
  return maskPlatform(`${(d && d.chip_target) || ""}/${(d && d.board_profile) || ""}`);
}

function crocFmtMacDisplay(d) {
  const disp = String((d && d.mac_display) || "").trim();
  if (disp) return disp;
  const raw = String((d && d.mac_nocolon) || "").replace(/[^0-9A-Fa-f]/g, "").toUpperCase();
  if (raw.length !== 12) return raw || "";
  return raw.match(/.{1,2}/g).join(":");
}

function crocFmtHwModelHtml(d) {
  const sku = String((d && d.hw_code_resolved) || "").trim();
  const main = sku
    ? escapeHtml(sku)
    : escapeHtml(maskPlatform(`${(d && d.chip_target) || ""}/${(d && d.board_profile) || ""}`));
  const dbg = String((d && d.hw_debug_chip_board) || "").trim();
  if (!dbg) return main;
  return `${main}<span class="muted mono" style="font-size:10.5px;font-weight:400;opacity:0.88" title="Internal chip/board"> (${escapeHtml(dbg)})</span>`;
}

/** Fly-to hash for Command Center map (``#/overview`` glass slot). */
function crocHashOpsMapFlyTo(lat, lng, zoom) {
  const zz = clampOpsMapZoom(Number.isFinite(Number(zoom)) ? Number(zoom) : 16);
  return `#/overview?lat=${encodeURIComponent(String(lat))}&lng=${encodeURIComponent(String(lng))}&zoom=${encodeURIComponent(String(zz))}`;
}

registerRoute("devices", async (view, args, routeSeq) => {
  const id = decodeURIComponent(args[0] || "");
  if (!id) {
    // Capability registry fleet is NOT the All-devices list — keep it on an
    // explicit query so #/group/_all stays the real device grid.
    const capsView = String((window.__routeQuery && window.__routeQuery.get("view")) || "")
      .trim()
      .toLowerCase() === "capabilities";
    if (capsView && hasRole("admin") && catalogEnabled(state.me, "device_capabilities")) {
      setCrumb("Board features");
      await mountDeviceCapabilitiesFleet(view, {
        routeSeq,
        isRouteCurrent,
        api,
        toast,
        escapeHtml,
        mountView,
      });
      return;
    }
    // tip583 F18 expire-purge: bare #/devices soft → #/group/_all deleted (was expireTip 585).
    location.hash = typeof defaultHomeHash === "function" ? defaultHomeHash() : "#/overview";
    return;
  }
  const isSuperViewer = !!(state.me && state.me.role === "superadmin");

  let d = await api(buildGetDevicePath(id));
  const canOperateThisDevice = d.is_shared
    ? !!d.can_operate
    : !!(d.can_operate ?? (state.me && (state.me.role === "superadmin" || state.me.role === "admin")));
  const canSirenThisDevice = can("can_alert") || canOperateThisDevice;
  const canEditDeviceCameras =
    hasRole("admin") && (isSuperViewer || !d.is_shared);
  const cameraOwnerAdmin = isSuperViewer ? String(d.owner_admin || "").trim() : "";
  window.__devicePollLocks = window.__devicePollLocks || new Map();
  const runPollDedup = (key, worker) => {
    const k = String(key || "");
    if (!k) return worker();
    const locks = window.__devicePollLocks;
    if (locks.has(k)) return locks.get(k);
    const p = (async () => {
      try {
        return await worker();
      } finally {
        if (locks.get(k) === p) locks.delete(k);
      }
    })();
    locks.set(k, p);
    return p;
  };
  setCrumb(d.display_label ? `Board · ${d.display_label}` : `Board · ${id}`);
  const mapLatStr =
    d.map_lat != null && Number.isFinite(Number(d.map_lat)) ? String(Number(d.map_lat)) : "";
  const mapLngStr =
    d.map_lng != null && Number.isFinite(Number(d.map_lng)) ? String(Number(d.map_lng)) : "";
  const bps = (v) => {
    v = Number(v || 0);
    if (v < 1024) return v.toFixed(0) + " B/s";
    if (v < 1024 * 1024) return (v / 1024).toFixed(1) + " KB/s";
    return (v / 1024 / 1024).toFixed(2) + " MB/s";
  };
  const reasonEn = {
    none: "OK",
    power_low: "Power low",
    network_lost: "Network lost",
    signal_weak: "Weak signal",
  };
  const deviceLiveModel = (dev) => {
    const pres = devicePresenceStatus(dev);
    const on = deviceReachable(dev);
    const s = dev.last_status_json || {};
    const reason = s.disconnect_reason || (on ? "none" : "network_lost");
    const outV = (s.vbat == null || s.vbat < 0) ? "—" : `${Number(s.vbat).toFixed(2)} V`;
    const rssi = (s.rssi == null || s.rssi === -127) ? "—" : `${s.rssi} dBm`;
    const netT = String(s.net_type || dev.net_type || "");
    const wifiSsidDd = netT === "wifi"
      ? ((s.wifi_ssid != null && String(s.wifi_ssid).length > 0)
        ? escapeHtml(String(s.wifi_ssid))
        : `<span class="muted">Not associated</span>`)
      : `<span class="muted">N/A (${escapeHtml(netT || "—")})</span>`;
    const wifiChDd = (netT === "wifi" && s.wifi_channel != null && Number(s.wifi_channel) > 0)
      ? escapeHtml(String(s.wifi_channel))
      : "—";
    return { pres, on, s, reason, outV, rssi, wifiSsidDd, wifiChDd };
  };
  const dm = deviceLiveModel(d);
  const rawCommandDrawer = (state.me && state.me.role === "superadmin") ? `
    <details class="card device-drawer">
      <summary class="device-drawer__summary">
        <span class="device-drawer__title">Staff command</span>
        <span class="device-drawer__hint muted">Advanced · rare</span>
      </summary>
      <div class="device-drawer__body">
        <p class="muted" style="margin:0 0 8px;font-size:12px">Platform staff only. Prefer the buttons above for everyday work.</p>
        <label class="field"><span>Command</span><input id="cmdName" placeholder="status / firmware update" ${can("can_send_command") ? "" : "disabled"} /></label>
        <label class="field" style="margin-top:8px"><span>Extra options</span><textarea id="cmdParams" placeholder="Leave empty unless platform staff asked you to fill this" ${can("can_send_command") ? "" : "disabled"}></textarea></label>
        <div class="row" style="margin-top:8px;justify-content:flex-end">
          <button class="btn" id="sendCmd" ${can("can_send_command") ? "" : "disabled"}>Send</button>
        </div>
      </div>
    </details>` : "";
  const canUseSharePanel = !!(
    state.me
    && (state.me.role === "superadmin" || (state.me.role === "admin" && can("can_manage_users")))
    && (!d.is_shared || state.me.role === "superadmin")
  );
  const sharePanel = canUseSharePanel ? `
    <details class="card device-drawer" id="sharePanel">
      <summary class="device-drawer__summary">
        <span class="device-drawer__title">Sharing</span>
        <span class="device-drawer__hint muted">Grant / revoke · expand</span>
      </summary>
      <div class="device-drawer__body">
        <div class="row" style="justify-content:flex-end;margin-bottom:8px">
          <button class="btn secondary btn-tap sm" type="button" id="shareRefresh">Refresh</button>
        </div>
        <p class="muted" style="margin:0 0 10px">Grant or revoke per-account access (admin: your users only).</p>
        <div class="inline-form" style="margin-top:4px">
          <label class="field grow"><span>Shared with</span>
            <input id="shareUser" placeholder="username" />
          </label>
          <label class="field"><span>View</span>
            <input id="shareCanView" type="checkbox" checked />
          </label>
          <label class="field"><span>Operate</span>
            <input id="shareCanOperate" type="checkbox" />
          </label>
          <div class="row wide" style="justify-content:flex-end">
            <button class="btn btn-tap" id="shareGrant">Grant / Update</button>
          </div>
        </div>
        <div id="shareList" style="margin-top:12px"></div>
      </div>
    </details>
  ` : "";
  const renderAlarmFeed = (items) => {
    const rows = Array.isArray(items) ? items : [];
    if (rows.length === 0) {
      return `<p class="muted audit-empty">No alarms in the last 24 hours.</p>`;
    }
    const dirLabel = (dir) => (String(dir || "") === "out" ? "Remote" : "On site");
    return `<div class="audit-feed device-alarm-feed-list">${rows.map((row) => {
      const fan =
        row.fanout_count != null && Number(row.fanout_count) > 0
          ? `<span class="muted"> · notified ${escapeHtml(String(row.fanout_count))}</span>`
          : "";
      return `<article class="audit-item device-alarm-feed-row">
        <div class="audit-item-top">
          <div class="audit-time">
            <span class="audit-ts mono">${escapeHtml(fmtTs(row.ts))}</span>
            <span class="muted audit-rel">${escapeHtml(fmtRel(row.ts))}</span>
          </div>
          <span class="chip">${escapeHtml(dirLabel(row.direction))}</span>
        </div>
        <div class="audit-extra">
          <div class="audit-extra-row"><span class="audit-k">Reason</span><span class="audit-v">${escapeHtml(row.reason || "—")}</span></div>
          <div class="audit-extra-row"><span class="audit-k">Operator</span><span class="audit-v mono">${escapeHtml(row.actor || "—")}</span></div>${fan}
        </div>
      </article>`;
    }).join("")}</div>`;
  };
  const loadAlarmFeed = async () => {
    const wrap = $("#devAlarmFeedWrap", view);
    if (!wrap) return;
    try {
      const pack = await api(buildDeviceAlarmFeedPath(id, 24));
      wrap.innerHTML = renderAlarmFeed(pack && pack.items);
    } catch (_e) {
      wrap.innerHTML = `<p class="muted">Could not load alarm feed.</p>`;
    }
  };
  const renderMsgFeed = (items) => {
    const msgItems = Array.isArray(items) ? items : [];
    if (msgItems.length === 0) return `<p class="muted audit-empty">No messages.</p>`;
    return `<div class="audit-feed">${msgItems.map((m) => {
      const plRows = messagePayloadRows(m.payload || {});
      const extra = plRows.length
        ? `<div class="audit-extra">${plRows.map((row) =>
            `<div class="audit-extra-row"><span class="audit-k">${escapeHtml(row.k)}</span><span class="audit-v mono">${escapeHtml(row.v)}</span></div>`,
        ).join("")}</div>`
        : "";
      return `<article class="audit-item">
        <div class="audit-item-top">
          <div class="audit-time">
            <span class="audit-ts mono">${escapeHtml(fmtTs(m.ts_received))}</span>
            <span class="muted audit-rel">${escapeHtml(fmtRel(m.ts_received))}</span>
          </div>
          <span class="chip">${escapeHtml(m.channel || "—")}</span>
        </div>
        ${extra}
      </article>`;
    }).join("")}</div>`;
  };
  const mqttMsgPanel = isSuperViewer ? `
    <div class="card">
      <details id="mqttMsgDetails">
        <summary style="cursor:pointer;font-weight:600">Board message log (staff · latest 25)</summary>
        <p class="muted" style="margin:8px 0 0">Recent uplink notes for troubleshooting. Hidden by default.</p>
        <div class="divider"></div>
        <div class="audit-feed-wrap" id="devMsgsList"><p class="muted">Expand to load…</p></div>
      </details>
    </div>` : "";
  mountView(view, `
    <section class="device-page-v2" aria-label="Board detail">
    <nav class="device-page-back-nav" aria-label="Board navigation">
      <a href="#/group/_all" class="btn secondary sm btn-tap device-page-back">← Back</a>
    </nav>
    <div class="card device-focus-layout">
      <div class="device-focus-left">
        <div class="row" style="align-items:flex-start;flex-wrap:wrap;gap:10px">
          <div class="device-page-head" style="flex:1;min-width:0">
            <div class="device-primary-name">${escapeHtml(d.display_label || id)}</div>
            ${d.display_label ? `<div class="device-id-sub mono">${escapeHtml(id)}</div>` : ""}
          </div>
          <span class="badge ${dm.pres.cls}" id="devOnlineBadge">${devicePresenceBadgeText(dm.pres)}</span>
          <span class="chip" id="devReasonChip">${escapeHtml(reasonEn[dm.reason] || dm.reason)}</span>
          ${d.zone ? `<span class="chip">${escapeHtml(d.zone)}</span>` : ""}
        </div>
        <div class="device-hero-card">
          <div class="device-thumb">${escapeHtml((d.display_label || id || "?").slice(0, 1).toUpperCase())}</div>
          <div class="device-hero-meta">
            <div class="device-hero-line device-hero-line--fw">
              <span class="muted">Firmware</span>
              <div class="device-hero-fw">
                <span class="mono" id="devFwVer">${escapeHtml(d.fw || "—")}</span>
                <span class="device-fw-state" id="devFwStatus" aria-live="polite">—</span>
                <button type="button" class="btn sm secondary fw-hint-cta" id="devFwHintBtn" style="display:${(d.firmware_hint && d.firmware_hint.update_available && firmwareHintStillValid(d.fw, d.firmware_hint)) ? "inline-flex" : "none"}" title="New firmware on server / 服务器有新固件">更新</button>
              </div>
            </div>
            <div class="device-hero-line"><span class="muted">Model</span><div class="mono">${crocFmtHwModelHtml(d)}</div></div>
            <div class="device-hero-line"><span class="muted">Network ID</span><span class="mono" id="devMac">${escapeHtml(crocFmtMacDisplay(d) || "—")}</span></div>
            <div class="device-hero-line"><span class="muted">Connection</span><span class="mono" id="devNetRow">${escapeHtml(d.net_type || "—")} · ${escapeHtml(dm.s.ip || "—")}</span></div>
            <div class="device-hero-line"><span class="muted">Wi‑Fi</span><span id="devWifiSsid">${dm.wifiSsidDd}</span></div>
            <div class="device-hero-line"><span class="muted">Output</span><span class="mono" id="devOutV">${escapeHtml(dm.outV)}</span></div>
            <div class="device-hero-line"><span class="muted">Traffic</span><span class="mono" id="devTxRx">${escapeHtml(bps(dm.s.tx_bps))} / ${escapeHtml(bps(dm.s.rx_bps))}</span></div>
            <div class="device-hero-line"><span class="muted">Signal</span><span class="mono" id="devRssi">${escapeHtml(dm.rssi)}</span></div>
            <div class="device-hero-line"><span class="muted">Wi‑Fi channel</span><span class="mono" id="devWifiCh">${dm.wifiChDd}</span></div>
            <div class="device-hero-line"><span class="muted">Uptime</span><span class="mono" id="devUptime">${escapeHtml((dm.s.uptime_s ? `${Math.floor(dm.s.uptime_s / 3600)}h ${Math.floor((dm.s.uptime_s % 3600) / 60)}m` : "—"))}</span></div>
            <div class="device-hero-line"><span class="muted">Free memory</span><span class="mono" id="devHeap">${escapeHtml(dm.s.free_heap ? `${dm.s.free_heap} B (min ${dm.s.min_free_heap || "?"} B)` : "—")}</span></div>
            <div class="device-hero-line"><span class="muted">Last link</span><span class="mono" id="devDisconnect">${escapeHtml(dm.reason)}</span></div>
            <div class="device-hero-line"><span class="muted">Updated</span><span id="devUpdated">${escapeHtml(fmtTs(d.updated_at))} (${escapeHtml(fmtRel(d.updated_at))})</span></div>
          </div>
        </div>
      </div>
      <aside class="device-focus-right">
        <details class="card device-drawer device-alarm-feed" id="alarms-24h" ${String(d.map_pulse || "") === "alert" ? "open" : ""} style="margin:0">
          <summary class="device-drawer__summary">
            <span class="device-drawer__title">Alarms · 24h</span>
            <span class="device-drawer__hint muted">Recent alarms</span>
          </summary>
          <div class="device-drawer__body" id="devAlarmFeedWrap">
            <p class="muted" style="margin:0">Loading…</p>
          </div>
        </details>
        <div class="card" style="margin:12px 0 0">
          <h3 style="margin:0 0 8px">Ownership</h3>
          <p class="muted" style="margin:0 0 8px">Current account binding for this board.</p>
          <div class="device-owner-kv">
            <div><span class="muted">Account</span><span class="mono">${escapeHtml(d.owner_admin || d.shared_by || "—")}</span></div>
            <div><span class="muted">Email</span><span class="mono">${escapeHtml(d.owner_email || "—")}</span></div>
            <div><span class="muted">Shared</span><span class="mono">${d.is_shared ? `Yes · by ${escapeHtml(d.shared_by || "?")}` : "No"}</span></div>
          </div>
        </div>
        <div class="card" style="margin:12px 0 0">
          <h3 style="margin:0 0 8px;font-size:13px;color:var(--text-muted)">Notifications</h3>
          ${d.is_shared ? `<p class="muted" style="margin:0 0 8px">This share is for this board only. You cannot see or edit the owner&rsquo;s notification group — use your own groups or single-board actions.</p>` : ""}
          <div class="row" style="gap:10px;align-items:flex-end;flex-wrap:wrap">
            <label class="field grow"><span>Display name ${d.is_shared ? "(owner)" : ""}</span>
              <input id="dispLabel" value="${escapeHtml(d.display_label || "")}" maxlength="80" ${d.is_shared ? "readonly title=\"Set by owner — read-only / 拥有者设定，只读\"" : ""} />
            </label>
            ${d.is_shared ? `<label class="field grow"><span>My note / 我的备注</span>
              <input id="granteeNote" value="${escapeHtml(d.grantee_display_note || "")}" maxlength="120" placeholder="Private label for you only" title="Saved on your share only; does not change owner display name" />
            </label>` : ""}
            <label class="field grow"><span>Group</span>
              <input id="notifGroup" value="${escapeHtml(d.notification_group || "")}" maxlength="80" placeholder="e.g. Warehouse A" ${d.is_shared ? "disabled title=\"Owner account only\"" : ""} />
            </label>
            <button class="btn secondary btn-tap" type="button" id="saveProfile">Save</button>
          </div>
          ${!d.is_shared ? `
          <div class="device-map-pin-panel" aria-labelledby="dev-map-pin-title">
            <h4 class="device-map-pin-panel__title" id="dev-map-pin-title">Map location</h4>
            <p class="device-map-pin-panel__lede muted">Address or place → <strong>Lookup</strong> fills coordinates and <strong>saves to server automatically</strong>. Then open map to <strong>drag</strong> the pin and tap <strong>Pin / 钉死</strong>. / 查找后自动保存坐标；到地图拖曳微调后点「钉死」。</p>
            <div class="device-map-pin-panel__row row" style="gap:8px">
              <label class="field grow"><span>Address / place / 地址或地名</span>
                <input id="mapPlaceQ" type="search" maxlength="120" placeholder="e.g. 1 Jalan PJ or Petaling Jaya" autocomplete="off" />
              </label>
              <button type="button" class="btn secondary sm btn-tap" id="mapGeocodeBtn">Lookup</button>
              <button type="button" class="btn ghost sm btn-tap" id="mapOpenMapBtn" title="Uses lat/lng fields only">Map preview</button>
            </div>
            <div class="device-map-pin-panel__row row" style="gap:10px">
              <label class="field"><span>Latitude</span>
                <input id="mapLat" type="number" step="any" inputmode="decimal" placeholder="3.157" value="${escapeHtml(mapLatStr)}" />
              </label>
              <label class="field"><span>Longitude</span>
                <input id="mapLng" type="number" step="any" inputmode="decimal" placeholder="101.712" value="${escapeHtml(mapLngStr)}" />
              </label>
              <label class="field grow"><span>Pin label</span>
                <input id="mapLabel" maxlength="160" value="${escapeHtml(String(d.map_label || ""))}" placeholder="Site or address name" />
              </label>
            </div>
            <div class="device-map-pin-panel__actions">
              <button type="button" class="btn ghost sm btn-tap" id="mapClearPin">Clear pin</button>
            </div>
          </div>` : ""}
        </div>
      </aside>
    </div>

    <div class="split device-quick-split">
      <div class="card">
        <h3>Quick actions</h3>
        ${d.is_shared ? `<p class="muted" style="margin:0 0 8px">Shared by <span class="mono">${escapeHtml(d.shared_by || "?")}</span>. Delete/Revoke actions are disabled for shared boards.</p>` : ""}
        <div class="row">
          <button class="btn" id="alertOn" ${canSirenThisDevice ? "" : "disabled"} title="${canSirenThisDevice ? "" : "Need Alert access or Operate on this board"}">Siren ON</button>
          <button class="btn secondary" id="alertOff" ${canSirenThisDevice ? "" : "disabled"} title="${canSirenThisDevice ? "" : "Need Alert access or Operate on this board"}">Siren OFF</button>
          <button class="btn secondary" id="selfTest" ${can("can_send_command") && canOperateThisDevice ? "" : "disabled"}>Self-test</button>
        </div>
        <div class="row" style="margin-top:10px">
          <input id="rebootDelay" placeholder="Delay seconds (e.g. 30)" style="max-width:200px" />
          <button class="btn secondary" id="doReboot" ${can("can_send_command") && canOperateThisDevice ? "" : "disabled"}>Schedule reboot</button>
        </div>
        <div class="row" style="margin-top:14px">
          <button class="btn secondary" id="unrevoke" ${can("can_send_command") && !d.is_shared ? "" : "disabled"}>Unrevoke</button>
        </div>
      </div>
      <details class="card device-drawer" id="devLocalSirenCard">
        <summary class="device-drawer__summary">
          <span class="device-drawer__title">Local siren default</span>
          <span class="device-drawer__hint muted">Panic · duration</span>
        </summary>
        <div class="device-drawer__body">
          <p class="muted device-local-siren-lede">Default panic siren length on this unit. Apply saves to the board (allow about 10 seconds when online).</p>
          ${can("can_send_command") && canOperateThisDevice ? `
          <div class="inline-form" style="gap:12px;flex-wrap:wrap;align-items:flex-end">
            <label class="field"><span>Duration (minutes)</span>
              <input id="devLocalSirenMin" type="number" min="0.5" max="5" step="0.5" value="${DEFAULT_LOCAL_SIREN_MS / 60000}" title="0.5–5 min" />
            </label>
            <button class="btn btn-tap" type="button" id="devLocalSirenApply">Apply to board</button>
          </div>
          <p class="muted" id="devLocalSirenStatus" style="margin-top:8px;min-height:1.3em"></p>` : `<p class="muted">Requires command permission and Operate access.</p>`}
        </div>
      </details>
    </div>
    ${sharePanel}

    ${devicePresenceDiagnosePanelHtml()}

    ${hasRole("admin") && catalogEnabled(state.me, "device_capabilities")
      ? deviceCapabilitiesPanelHtml({ canEdit: hasRole("admin") && (isSuperViewer || !d.is_shared) })
      : ""}

    ${deviceCamerasPanelHtml({ canEdit: canEditDeviceCameras })}

    ${deviceTfStoragePanelHtml({ canOperate: !!(can("can_send_command") && canOperateThisDevice) })}

    <details class="card device-drawer" id="wifiCtlCard">
      <summary class="device-drawer__summary">
        <span class="device-drawer__title">Wi‑Fi (board)</span>
        <span class="device-drawer__hint muted">Provision · expand</span>
      </summary>
      <div class="device-drawer__body">
        <p class="muted" style="margin:0 0 10px">Saves Wi‑Fi on the board and reboots it. Optional follow‑up checks run after it reconnects — no second click needed.</p>
        ${can("can_send_command") && canOperateThisDevice ? `
        <div class="inline-form" style="margin-top:4px">
          <label class="field grow"><span>New Wi‑Fi name</span><input id="wifiNewSsid" maxlength="32" autocomplete="off" placeholder="2.4 GHz network name" /></label>
          <label class="field grow"><span>Password</span><input id="wifiNewPass" type="password" maxlength="64" autocomplete="new-password" placeholder="empty if open network" /></label>
          <div class="field" style="margin-top:10px">
            <span>After reconnect</span>
            <div class="row" style="gap:14px;flex-wrap:wrap;margin-top:6px">
              <label><input type="checkbox" id="wifiChainGetInfo" checked /> Status check</label>
              <label><input type="checkbox" id="wifiChainPing" checked /> Ping</label>
              <label><input type="checkbox" id="wifiChainSelfTest" /> Self-test</label>
            </div>
          </div>
          <div class="row wide" style="justify-content:flex-end;flex-wrap:wrap;gap:8px">
            <button class="btn btn-tap" type="button" id="wifiApplyBtn">Start provision task</button>
          </div>
        </div>
        <div style="margin-top:8px">
          <progress id="wifiTaskProgress" value="0" max="100" style="width:100%;height:12px"></progress>
        </div>
        <p class="muted" id="wifiScanStatus" style="margin-top:8px;min-height:1.3em"></p>` : `<p class="muted">Requires command permission and Operate access on this board.</p>`}
      </div>
    </details>

    <details class="card danger-zone danger-zone--compact device-drawer">
      <summary class="device-drawer__summary">
        <span class="device-drawer__title">Danger zone</span>
        <span class="device-drawer__hint muted">Unbind &amp; reset</span>
      </summary>
      <div class="device-drawer__body danger-zone-body">
        <p class="muted danger-zone-compact-lead">Removes this board from your account and clears its claim when online. Re-add from Activate.</p>
        <div class="danger-zone-single-action">
          <button class="btn danger sm danger-zone-unbind-btn" type="button" id="deleteReset" ${can("can_send_command") && !d.is_shared ? "" : "disabled"}>Unbind &amp; reset</button>
        </div>
      </div>
    </details>

    ${d.is_shared ? `
    <details class="card device-drawer" id="triggerPolicyCard">
      <summary class="device-drawer__summary">
        <span class="device-drawer__title">Trigger policy</span>
        <span class="device-drawer__hint muted">Account owner only</span>
      </summary>
      <div class="device-drawer__body">
        <p class="muted" style="margin:0">Group trigger policy is managed by the <strong>account owner</strong> only. A shared board does not expose group policy.</p>
      </div>
    </details>` : `
    <details class="card device-drawer" id="triggerPolicyCard">
      <summary class="device-drawer__summary">
        <span class="device-drawer__title">Trigger policy</span>
        <span class="device-drawer__hint muted">Server · group scope · expand</span>
      </summary>
      <div class="device-drawer__body">
        <p class="muted" style="margin:0 0 10px">Applies to group <strong>${escapeHtml(d.notification_group || "Default")}</strong> on your account. Sibling units share that group. Remote #1 = silent alert; #2 = loud to siblings; panic = local siren plus optional sibling siren.</p>
        ${can("can_send_command") && canOperateThisDevice ? `
        <div class="inline-form" style="margin-top:4px;gap:12px;flex-wrap:wrap;align-items:flex-end">
          <label class="field"><span>Siren on this board</span><input type="checkbox" id="tpPanicLocal" title="Sound on board that pressed panic" /></label>
          <label class="field"><span>Panic → siblings</span><input type="checkbox" id="tpPanicLink" title="Siren to same-group boards" /></label>
          <label class="field"><span>Remote silent link</span><input type="checkbox" id="tpSilentLink" /></label>
          <label class="field"><span>Remote loud link</span><input type="checkbox" id="tpLoudLink" /></label>
          <label class="field"><span>Exclude self</span><input type="checkbox" id="tpExcludeSelf" /></label>
          <label class="field"><span>Loud (min)</span><input id="tpLoudMin" type="number" min="0.5" max="5" step="0.5" value="1" title="Remote / #2 sibling siren length" /></label>
          <label class="field"><span>Panic sibling (min)</span><input id="tpPanicMin" type="number" min="0.5" max="10" step="0.5" value="1" title="Panic sibling siren length" /></label>
          <div class="row wide" style="justify-content:flex-end;flex-basis:100%">
            <button class="btn secondary btn-tap" type="button" id="tpRefresh">Refresh</button>
            <button class="btn btn-tap" type="button" id="tpSave">Save</button>
          </div>
        </div>
        <p class="muted" id="tpStatus" style="margin-top:8px;min-height:1.3em"></p>` : `<p class="muted">Requires command permission and Operate access on this board.</p>`}
      </div>
    </details>`}

    ${rawCommandDrawer}

    ${mqttMsgPanel}
    </section>`);
  void loadAlarmFeed();
  wireDevicePresenceDiagnosePanel(view, {
    deviceId: id,
    routeSeq,
    isRouteCurrent,
    api,
    escapeHtml,
  });
  if (hasRole("admin") && catalogEnabled(state.me, "device_capabilities")) {
    wireDeviceCapabilitiesPanel(view, {
      deviceId: id,
      routeSeq,
      canEdit: hasRole("admin") && (isSuperViewer || !d.is_shared),
      isRouteCurrent,
      api,
      toast,
      escapeHtml,
    });
  }
  wireDeviceCamerasPanel(view, {
    deviceId: id,
    ownerAdmin: cameraOwnerAdmin,
    routeSeq,
    canEdit: canEditDeviceCameras,
    isRouteCurrent,
    api,
    toast,
    escapeHtml,
    timeoutMs: DEVICES_CONSOLE_TIMEOUT_MS,
  });
  const tfStoragePanel = wireDeviceTfStoragePanel(view, {
    deviceId: id,
    getDevice: () => d,
    canOperate: !!(can("can_send_command") && canOperateThisDevice),
    routeSeq,
    isRouteCurrent,
    api,
    toast,
    escapeHtml,
    buildPostDeviceCommandsPath,
    timeoutMs: DEVICES_CONSOLE_TIMEOUT_MS,
  });
  if (String(location.hash || "").includes("alarms-24h")) {
    requestAnimationFrame(() => {
      const el = document.getElementById("alarms-24h");
      if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }
  let _offlineLocked = false;
  const _offlineHinted = { shown: false };
  const setOfflineInteractionLock = (isOnlineNow) => {
    _offlineLocked = !isOnlineNow;
    const existing = $("#offlineLockMsg", view);
    if (_offlineLocked) {
      if (!existing) {
        const left = $(".device-focus-left", view);
        if (left) {
          const p = document.createElement("p");
          p.id = "offlineLockMsg";
          p.className = "badge offline";
          p.style.margin = "0 0 8px";
          p.textContent = "Board offline · view-only mode";
          left.insertBefore(p, left.firstChild || null);
        }
      } else {
        existing.style.display = "inline-flex";
      }
    } else if (existing) {
      existing.style.display = "none";
    }
    for (const ctl of $$("button, input, textarea, select", view)) {
      if (!(ctl instanceof HTMLElement)) continue;
      // Keep danger-zone unbind + share revoke available while offline:
      // operator may unlink a dead device or revoke ACL before group assign.
      if (ctl.id === "deleteReset") continue;
      if (ctl.id === "shareRefresh") continue;
      if (ctl.classList.contains("shareRevokeBtn")) continue;
      if (_offlineLocked) {
        // Persist original disabled state so unlock can restore exactly.
        if (ctl.dataset.offlinePrevDisabled === undefined) {
          ctl.dataset.offlinePrevDisabled = ctl.disabled ? "1" : "0";
        }
        ctl.disabled = true;
      } else if (ctl.dataset.offlinePrevDisabled !== undefined) {
        ctl.disabled = ctl.dataset.offlinePrevDisabled === "1";
        delete ctl.dataset.offlinePrevDisabled;
      }
    }
  };
  const refreshOfflineInteractionLock = () => setOfflineInteractionLock(!_offlineLocked);
  const patchDeviceLive = (dev) => {
    const m = deviceLiveModel(dev);
    const onlineBadge = $("#devOnlineBadge", view);
    if (onlineBadge) {
      onlineBadge.textContent = devicePresenceBadgeText(m.pres);
      onlineBadge.className = `badge ${m.pres.cls}`;
    }
    const reasonChip = $("#devReasonChip", view);
    if (reasonChip) reasonChip.textContent = String(reasonEn[m.reason] || m.reason);
    const setText = (idSel, txt) => { const el = $(idSel, view); if (el) el.textContent = String(txt); };
    const setHtml = (idSel, txt) => { const el = $(idSel, view); if (el) setChildMarkup(el, String(txt)); };
    setText("#devNetRow", `${dev.net_type || "—"} · ${m.s.ip || "—"}`);
    setHtml("#devWifiSsid", m.wifiSsidDd);
    setHtml("#devWifiCh", m.wifiChDd);
    setText("#devRssi", m.rssi);
    setText("#devOutV", m.outV);
    setText("#devTxRx", `${bps(m.s.tx_bps)} / ${bps(m.s.rx_bps)}`);
    setText("#devDisconnect", m.reason);
    setText("#devUptime", m.s.uptime_s ? `${Math.floor(m.s.uptime_s / 3600)}h ${Math.floor((m.s.uptime_s % 3600) / 60)}m` : "—");
    setText("#devHeap", m.s.free_heap ? `${m.s.free_heap} B (min ${m.s.min_free_heap || "?"} B)` : "—");
    setText("#devUpdated", `${fmtTs(dev.updated_at)} (${fmtRel(dev.updated_at)})`);
    syncDevicePageFirmwareHint(view, dev, id);
    if (tfStoragePanel && typeof tfStoragePanel.refresh === "function") tfStoragePanel.refresh(dev);
    setOfflineInteractionLock(m.on);
  };
  patchDeviceLive(d);
  // Interaction polish: keep drawer interactions tidy by treating device
  // panels as an accordion (except danger zone, which stays user-controlled).
  const deviceDrawers = $$("details.card.device-drawer", view);
  const isDangerDrawer = (el) => !!(el && el.classList && el.classList.contains("danger-zone"));
  for (const drawer of deviceDrawers) {
    drawer.addEventListener("toggle", () => {
      if (!drawer.open) return;
      if (isDangerDrawer(drawer)) return;
      for (const other of deviceDrawers) {
        if (other === drawer) continue;
        if (isDangerDrawer(other)) continue;
        if (!other.open) continue;
        other.open = false;
      }
    });
  }
  scheduleRouteTicker(routeSeq, `device-live-${id}`, async () => {
    if (!isRouteCurrent(routeSeq)) return;
    const latest = await api(buildGetDevicePath(id), { timeoutMs: DEVICES_CONSOLE_TIMEOUT_MS, retries: 0 });
    if (!isRouteCurrent(routeSeq) || !latest) return;
    d = latest;
    patchDeviceLive(latest);
  }, 6000);
  view.addEventListener("click", (ev) => {
    if (!_offlineLocked) return;
    const tgt = ev.target && ev.target.closest ? ev.target.closest("button") : null;
    if (!tgt) return;
    if (tgt.closest(".device-page-back-nav")) return;
    if (tgt.id === "deleteReset") return;
    if (tgt.id === "shareRefresh") return;
    if (tgt.classList.contains("shareRevokeBtn")) return;
    if (!_offlineHinted.shown) {
      _offlineHinted.shown = true;
      toast("Board offline: view-only mode; actions are disabled.", "warn");
      setTimeout(() => { _offlineHinted.shown = false; }, 2200);
    }
    ev.preventDefault();
    ev.stopPropagation();
  }, true);
  if (isSuperViewer) {
    const det = $("#mqttMsgDetails", view);
    const box = $("#devMsgsList", view);
    let loaded = false;
    const loadDebugMsgs = async () => {
      if (loaded || !box) return;
      loaded = true;
      setChildMarkup(box, `<p class="muted">Loading…</p>`);
      try {
        const msgs = await api(buildDeviceMessagesPath(id, 25), { timeoutMs: DEVICES_CONSOLE_TIMEOUT_MS });
        setChildMarkup(box, renderMsgFeed(msgs.items || []));
      } catch (e) {
        setChildMarkup(box, `<p class="badge offline">${escapeHtml(e.message || e)}</p>`);
      }
    };
    if (det) {
      det.addEventListener("toggle", () => {
        if (det.open) loadDebugMsgs();
      });
    }
  }

  $("#saveProfile").addEventListener("click", async () => {
    if (_offlineLocked) { toast("Board offline: actions are disabled.", "warn"); return; }
    try {
      let body;
      if (d.is_shared) {
        const gn = $("#granteeNote");
        body = { grantee_display_note: (gn && gn.value ? String(gn.value) : "").trim() };
      } else {
        body = { display_label: ($("#dispLabel").value || "").trim() };
        body.notification_group = canonicalGroupKey(($("#notifGroup") && $("#notifGroup").value) || "");
      }
      const res = await api(buildPatchDeviceProfilePath(id), {
        method: "PATCH",
        body,
      });
      if (d.is_shared && res && res.grantee_display_note != null) {
        d.grantee_display_note = res.grantee_display_note;
      }
      if (!d.is_shared) {
        reconcileGroupMetaForDevice(id, body.notification_group || "", d.owner_admin);
      }
      toast("Saved", "ok");
    } catch (e) { toast(softApiError(e), "err"); }
  });

  if (!d.is_shared) {
    const mapGeo = $("#mapGeocodeBtn", view);
    if (mapGeo) {
      mapGeo.addEventListener("click", async () => {
        if (_offlineLocked) { toast("Board offline: actions are disabled.", "warn"); return; }
        const qEl = $("#mapPlaceQ", view);
        const q = qEl ? String(qEl.value || "").trim() : "";
        if (!q) { toast("Enter a place name or lat, lng", "warn"); return; }
        try {
          const hit = parseLatLngPair(q) || await geocodeOpenMeteo(q);
          if (!hit) { toast("No results — try another spelling or paste lat,lng", "warn"); return; }
          const ilat = $("#mapLat", view);
          const ilng = $("#mapLng", view);
          const ilab = $("#mapLabel", view);
          if (ilat) ilat.value = String(hit.lat);
          if (ilng) ilng.value = String(hit.lng);
          if (ilab && !String(ilab.value || "").trim() && hit.label) ilab.value = hit.label;
          const lat = Number(hit.lat);
          const lng = Number(hit.lng);
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            toast("Invalid coordinates from lookup", "warn");
            return;
          }
          const labStr = (ilab && String(ilab.value || "").trim()) || (hit.label ? String(hit.label).trim() : "") || "";
          const body = { map_lat: lat, map_lng: lng };
          if (labStr) body.map_label = labStr;
          await api(buildPatchDeviceProfilePath(id), { method: "PATCH", body });
          bustApiGetCachedPrefix("/devices");
          d = await api(buildGetDevicePath(id));
          toast("Saved to server — open Map preview to drag & pin / 已保存，可地图预览拖钉", "ok");
        } catch (e) { toast(softApiError(e), "err"); }
      });
    }
    const mapOpen = $("#mapOpenMapBtn", view);
    if (mapOpen) {
      mapOpen.addEventListener("click", () => {
        const latStr = ($("#mapLat", view) && $("#mapLat", view).value || "").trim();
        const lngStr = ($("#mapLng", view) && $("#mapLng", view).value || "").trim();
        if (!latStr || !lngStr) {
          toast("Lookup or enter latitude and longitude first.", "warn");
          return;
        }
        const lat = Number(latStr);
        const lng = Number(lngStr);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
          toast("Invalid coordinates", "warn");
          return;
        }
        location.hash = crocHashOpsMapFlyTo(lat, lng, 16);
      });
    }
    const mapClr = $("#mapClearPin", view);
    if (mapClr) {
      mapClr.addEventListener("click", async () => {
        if (_offlineLocked) { toast("Board offline: actions are disabled.", "warn"); return; }
        try {
          await api(buildPatchDeviceProfilePath(id), {
            method: "PATCH",
            body: { clear_map_location: true },
          });
          const ilat = $("#mapLat", view);
          const ilng = $("#mapLng", view);
          const ilab = $("#mapLabel", view);
          if (ilat) ilat.value = "";
          if (ilng) ilng.value = "";
          if (ilab) ilab.value = "";
          bustApiGetCachedPrefix("/devices");
          toast("Map pin cleared", "ok");
        } catch (e) { toast(softApiError(e), "err"); }
      });
    }
  }

  const withDev = (fn) => async () => {
    if (_offlineLocked) { toast("Board offline: actions are disabled.", "warn"); return; }
    try { await fn(); toast("Sent", "ok"); }
    catch (e) { toast(softApiError(e), "err"); }
  };

  $("#alertOn").addEventListener("click", withDev(async () => {
    await api(buildPostDeviceAlertOnPath(id, DEFAULT_REMOTE_SIREN_MS), { method: "POST" });
    await loadAlarmFeed();
  }));
  $("#alertOff").addEventListener("click", withDev(async () => {
    await api(buildPostDeviceAlertOffPath(id), { method: "POST" });
    await loadAlarmFeed();
  }));
  $("#selfTest").addEventListener("click", withDev(() =>
    api(buildPostDeviceSelfTestPath(id), { method: "POST" })));
  $("#doReboot").addEventListener("click", withDev(() => {
    const v = parseInt($("#rebootDelay").value, 10);
    if (!Number.isFinite(v) || v < 5) throw new Error("delay must be >= 5 seconds");
    return api(buildPostDeviceScheduleRebootPath(id), { method: "POST", body: { delay_s: v } });
  }));
  $("#unrevoke").addEventListener("click", withDev(async () => {
    await api(buildPostDeviceUnrevokePath(id), { method: "POST" });
    bustDeviceListCaches();
  }));
  const devLocalSirenMin = $("#devLocalSirenMin", view);
  const devLocalSirenApply = $("#devLocalSirenApply", view);
  const devLocalSirenStatus = $("#devLocalSirenStatus", view);
  if (devLocalSirenApply && devLocalSirenMin) {
    devLocalSirenApply.addEventListener("click", async () => {
      if (_offlineLocked) { toast("Board offline: actions are disabled.", "warn"); return; }
      const m = parseFloat(String(devLocalSirenMin.value || ""), 10);
      if (!Number.isFinite(m) || m < 0.5 || m > 5) {
        toast("Local siren: use 0.5–5 minutes.", "err");
        return;
      }
      const siren_ms = Math.round(m * 60000);
      if (devLocalSirenStatus) devLocalSirenStatus.textContent = "Sending…";
      try {
        await api(buildPostDeviceCommandsPath(id), {
          method: "POST",
          body: { cmd: "set_param", params: { siren_ms } },
        });
        if (devLocalSirenStatus) devLocalSirenStatus.textContent = "Saved — device applies within about 10 seconds when online.";
        toast("Local siren duration queued on device", "ok");
      } catch (e) {
        if (devLocalSirenStatus) devLocalSirenStatus.textContent = String(e.message || e);
        toast(softApiError(e), "err");
      }
    });
  }
  const deleteResetBtn = $("#deleteReset", view);
  if (deleteResetBtn) {
    deleteResetBtn.addEventListener("click", async () => {
      if (!confirm("Delete this board from current account records? You can re-add and reconfigure later.")) return;
      const typed = String(prompt(`Type device ID to confirm delete/reset:\n${id}`) || "").trim();
      if (typed.toUpperCase() !== String(id).toUpperCase()) { toast("Confirmation mismatch", "err"); return; }
      try {
        const dr = await api(buildPostDeviceDeleteResetPath(id), {
          method: "POST",
          body: { confirm_text: typed },
        });
        removeDeviceIdFromAllGroupMeta(id);
        bustDeviceListCaches();
        const sentNv = dr && (dr.nvs_purge_sent === true);
        const ackNv = dr && (dr.nvs_purge_acked === true);
        toast(
          `Device removed from account.${ackNv ? " Device cleared Wi‑Fi claim and is rebooting." : (sentNv ? " Unlink ran, but the device did not confirm before disconnect." : " Device was offline or did not accept the clear command.")} Re-add from Activate.`,
          ackNv ? "ok" : "err",
        );
        location.hash = "#/group/_all";
      } catch (e) { toast(softApiError(e), "err"); }
    });
  }
  const sendCmdBtn = $("#sendCmd");
  if (sendCmdBtn) {
    sendCmdBtn.addEventListener("click", async () => {
      const name = ($("#cmdName").value || "").trim();
      if (!name) { toast("Enter a command name", "err"); return; }
      let params = {};
      const raw = ($("#cmdParams").value || "").trim();
      if (raw) {
        try { params = JSON.parse(raw); } catch { toast("Extra options look invalid — leave empty or ask platform staff", "err"); return; }
      }
      try {
        await api(buildPostDeviceCommandsPath(id), { method: "POST", body: { cmd: name, params } });
        toast("Command sent", "ok");
      } catch (e) { toast(softApiError(e), "err"); }
    });
  }

  const wifiApplyBtn = $("#wifiApplyBtn");
  try {
    const rawPf = sessionStorage.getItem("croc.deviceWifiPrefill.v1");
    if (rawPf) {
      const pf = JSON.parse(rawPf);
      const match = pf && String(pf.device_id || "").toUpperCase() === String(id).toUpperCase();
      if (match && pf.ssid) {
        const wS = $("#wifiNewSsid", view);
        const wP = $("#wifiNewPass", view);
        const card = $("#wifiCtlCard", view);
        if (wS) wS.value = String(pf.ssid);
        if (wP) wP.value = "";
        sessionStorage.removeItem("croc.deviceWifiPrefill.v1");
        if (card) card.open = true;
        toast("Prefilled Wi‑Fi from Activate page — start provision below when the device is online.", "ok");
      }
    }
  } catch (_) {}
  const wifiTaskProgress = $("#wifiTaskProgress");
  const setWifiProgress = (n) => {
    if (!wifiTaskProgress) return;
    const v = Math.max(0, Math.min(100, Number(n || 0)));
    wifiTaskProgress.value = v;
  };
  const pollWifiTask = async (taskId) => runPollDedup(`wifi-task:${id}:${String(taskId || "")}`, async () => {
    const st = $("#wifiScanStatus");
    for (let i = 0; i < 120; i++) {
      await new Promise((r) => setTimeout(r, 1000));
      try {
        const t = await api(buildGetDeviceProvisionWifiTaskPath(id, taskId), {
          timeoutMs: DEVICES_CONSOLE_TIMEOUT_MS,
        });
        setWifiProgress(t.progress || 0);
        if (st) st.textContent = String(t.message || t.status || "");
        if (t.status === "success") {
          toast("Provision success", "ok");
          return;
        }
        if (t.status === "failed") {
          toast(t.message || "Provision failed", "err");
          return;
        }
      } catch (e) {
        if (st) st.textContent = softApiError(e);
      }
    }
    if (st) st.textContent = "Timed out waiting task result";
    toast("Provision task timeout", "err");
  });
  if (wifiApplyBtn) {
    wifiApplyBtn.addEventListener("click", async () => {
      const ssid = ($("#wifiNewSsid", view).value || "").trim();
      const password = ($("#wifiNewPass", view).value || "");
      const st = $("#wifiScanStatus", view);
      if (!ssid) { toast("Enter Wi‑Fi name", "err"); return; }
      if (!confirm("Save Wi‑Fi on device and reboot? You may lose contact until it joins the new network.")) return;
      try {
        wifiApplyBtn.disabled = true;
        setWifiProgress(10);
        if (st) st.textContent = "Creating provision task…";
        const chain = [];
        const cGi = $("#wifiChainGetInfo", view);
        const cPi = $("#wifiChainPing", view);
        const cSt = $("#wifiChainSelfTest", view);
        if (cGi && cGi.checked) chain.push({ cmd: "get_info", params: {} });
        if (cPi && cPi.checked) chain.push({ cmd: "ping", params: {} });
        if (cSt && cSt.checked) chain.push({ cmd: "self_test", params: {} });
        const body = { ssid, password };
        if (chain.length) body.chain = chain;
        const r = await api(buildPostDeviceProvisionWifiTaskPath(id), {
          method: "POST",
          body,
        });
        setWifiProgress(r.progress || 35);
        if (st) st.textContent = `Task ${r.task_id} running…`;
        await pollWifiTask(r.task_id);
      } catch (e) { toast(softApiError(e), "err"); if (st) st.textContent = softApiError(e); }
      finally { wifiApplyBtn.disabled = false; }
    });
  }
  const tpPanicLocal = $("#tpPanicLocal");
  const tpPanicLink = $("#tpPanicLink");
  const tpSilentLink = $("#tpSilentLink");
  const tpLoudLink = $("#tpLoudLink");
  const tpExcludeSelf = $("#tpExcludeSelf");
  const tpLoudMin = $("#tpLoudMin");
  const tpPanicMin = $("#tpPanicMin");
  const tpStatus = $("#tpStatus");
  const loadTriggerPolicy = async () => {
    if (!tpPanicLocal || !tpPanicLink || !tpSilentLink || !tpLoudLink || !tpExcludeSelf || !tpLoudMin || !tpPanicMin) return;
    try {
      if (tpStatus) tpStatus.textContent = "Loading policy…";
      const r = await api(buildGetDeviceTriggerPolicyPath(id), { timeoutMs: DEVICES_CONSOLE_TIMEOUT_MS });
      const p = r.policy || {};
      tpPanicLocal.checked = !!p.panic_local_siren;
      if (tpPanicLink) tpPanicLink.checked = p.panic_link_enabled !== false;
      tpSilentLink.checked = !!p.remote_silent_link_enabled;
      tpLoudLink.checked = !!p.remote_loud_link_enabled;
      tpExcludeSelf.checked = !!p.fanout_exclude_self;
      const loudMs = Number(p.remote_loud_duration_ms || DEFAULT_REMOTE_SIREN_MS);
      const panicMs = Number(p.panic_fanout_duration_ms || DEFAULT_PANIC_FANOUT_MS);
      tpLoudMin.value = String(Math.round(Math.max(0.5, Math.min(5, loudMs / 60000)) * 10) / 10);
      tpPanicMin.value = String(Math.round(Math.max(0.5, Math.min(10, panicMs / 60000)) * 10) / 10);
      if (tpStatus) tpStatus.textContent = `Loaded for group ${r.scope_group || "(default)"}`;
    } catch (e) {
      if (tpStatus) tpStatus.textContent = String(e.message || e);
    }
  };
  const tpRefresh = $("#tpRefresh");
  if (tpRefresh) tpRefresh.addEventListener("click", () => loadTriggerPolicy());
  const tpSave = $("#tpSave");
  if (tpSave) {
    tpSave.addEventListener("click", async () => {
      try {
        const lm = parseFloat((tpLoudMin && tpLoudMin.value) || "1", 10);
        const pm = parseFloat((tpPanicMin && tpPanicMin.value) || "1", 10);
        if (!Number.isFinite(lm) || lm < 0.5 || lm > 5) throw new Error("Loud duration must be 0.5–5 minutes");
        if (!Number.isFinite(pm) || pm < 0.5 || pm > 10) throw new Error("Panic sibling duration must be 0.5–10 minutes");
        const remote_loud_duration_ms = Math.round(lm * 60000);
        const panic_fanout_duration_ms = Math.round(pm * 60000);
        if (tpStatus) tpStatus.textContent = "Saving policy…";
        await api(buildPutDeviceTriggerPolicyPath(id), {
          method: "PUT",
          body: {
            panic_local_siren: !!(tpPanicLocal && tpPanicLocal.checked),
            panic_link_enabled: !!(tpPanicLink && tpPanicLink.checked),
            remote_silent_link_enabled: !!(tpSilentLink && tpSilentLink.checked),
            remote_loud_link_enabled: !!(tpLoudLink && tpLoudLink.checked),
            fanout_exclude_self: !!(tpExcludeSelf && tpExcludeSelf.checked),
            remote_loud_duration_ms,
            panic_fanout_duration_ms,
          },
        });
        if (tpStatus) tpStatus.textContent = "Policy saved";
        toast("Trigger policy saved", "ok");
      } catch (e) {
        if (tpStatus) tpStatus.textContent = softApiError(e);
        toast(softApiError(e), "err");
      }
    });
    loadTriggerPolicy();
  }

  const shareListEl = $("#shareList");
  const renderShares = async () => {
    if (!shareListEl) return;
    setChildMarkup(shareListEl, `<p class="muted">Loading shares…</p>`);
    try {
      const r = await api(buildAdminDeviceSharesPath(id), { timeoutMs: ADMIN_CONSOLE_TIMEOUT_MS });
      const items = r.items || [];
      setChildMarkup(
        shareListEl,
        `
        <div class="table-wrap"><table class="t">
          <thead><tr><th>User</th><th>Role</th><th>View</th><th>Operate</th><th>Granted by</th><th>Granted at</th><th>Status</th><th></th></tr></thead>
          <tbody>${
            items.length === 0
              ? `<tr><td colspan="8" class="muted">No shares</td></tr>`
              : items.map((it) => `
                <tr>
                  <td class="mono">${escapeHtml(it.grantee_username || "")}</td>
                  <td>${escapeHtml(it.grantee_role || "—")}</td>
                  <td>${it.can_view ? "yes" : "no"}</td>
                  <td>${it.can_operate ? "yes" : "no"}</td>
                  <td class="mono">${escapeHtml(it.granted_by || "")}</td>
                  <td>${escapeHtml(fmtTs(it.granted_at))}</td>
                  <td>${it.revoked_at ? `<span class="badge offline">revoked</span>` : `<span class="badge online">active</span>`}</td>
                  <td>${it.revoked_at ? "" : `<button class="btn ghost shareRevokeBtn" data-user="${escapeHtml(it.grantee_username || "")}">Revoke</button>`}</td>
                </tr>
              `).join("")
          }</tbody>
        </table></div>
      `,
      );
      $$(".shareRevokeBtn", shareListEl).forEach((btn) => {
        btn.addEventListener("click", async () => {
          const u = btn.getAttribute("data-user") || "";
          if (!u) return;
          if (!confirm(`Revoke share for ${u}?`)) return;
          try {
            await api(buildAdminDeviceShareRevokePath(id, u), { method: "DELETE" });
            toast("Share revoked", "ok");
            await renderShares();
          } catch (e) { toast(softApiError(e), "err"); }
        });
      });
      refreshOfflineInteractionLock();
    } catch (e) {
      setChildMarkup(shareListEl, `<p class="badge revoked">${escapeHtml(e.message || e)}</p>`);
      refreshOfflineInteractionLock();
    }
  };
  const shareGrantBtn = $("#shareGrant");
  if (shareGrantBtn) {
    shareGrantBtn.addEventListener("click", async () => {
      if (_offlineLocked) { toast("Board offline: actions are disabled.", "warn"); return; }
      const grantee = ($("#shareUser").value || "").trim();
      const canView = !!$("#shareCanView").checked;
      const canOperate = !!$("#shareCanOperate").checked;
      if (!grantee) { toast("Enter username to share with", "err"); return; }
      if (!canView && !canOperate) { toast("Select view and/or operate", "err"); return; }
      try {
        await api(buildAdminDeviceSharePath(id), {
          method: "POST",
          body: { grantee_username: grantee, can_view: canView, can_operate: canOperate },
        });
        toast("Share granted", "ok");
        await renderShares();
      } catch (e) { toast(softApiError(e), "err"); }
    });
  }
  const shareRefreshBtn = $("#shareRefresh");
  if (shareRefreshBtn) {
    shareRefreshBtn.addEventListener("click", () => renderShares());
    renderShares();
  }
});


// ── fabric.route.js ─────────────────────────────────────
/**
 * Route: #/fabric — Spatial Compute Fabric (FAB-D1..D6 Soft).
 * ADR: 2026-07-27-cta-fabric-trust-engine-adr.md
 * KEEP leak contract: tip <b>543</b> (not operator paint).
 */

registerRoute("fabric", async (view) => {
  setCrumb("Fabric");
  const me = state.me || {};
  const role = String(me.role || "");
  const caps = new Set((me.sa_capabilities || []).map(String));
  const canRead =
    role === "superadmin" ||
    (role === "sa_staff" && (caps.has("sa_full_access") || caps.has("sa_device_admin")));
  if (!canRead) {
    mountView(
      view,
      `<div class="card glass-panel glass-panel--panel" style="padding:20px;margin:16px">
        <h2 style="margin:0 0 8px">Fabric Control</h2>
        <p class="muted" style="margin:0;line-height:1.45">
          Platform SA only. Distributed compute nodes and trust live here — not on the CTA Field shell.
        </p>
        <div class="row" style="gap:8px;margin-top:14px;flex-wrap:wrap">
          <button type="button" class="btn sm btn-tap" data-cc-go="overview">Home</button>
        </div>
      </div>`,
    );
    return;
  }

  const fabricChannelSay = (raw) => {
    const t = String(raw || "").trim().toLowerCase();
    if (t === "soft_mtls_standin") return "Soft stand-in · not production mTLS";
    if (t === "production_mtls") return "Production mTLS path · Soft until armed";
    if (t === "wg_overlay_soft") return "Overlay Soft · not live";
    if (t === "l9_pcr_soft") return "L9 Soft · not a live TPM";
    if (t === "cosign_publish_soft") return "Cosign Soft · Accept is not Publish";
    return "Soft ladder";
  };
  const fabricAttestSay = (raw) => {
    const t = String(raw || "").trim().toLowerCase();
    if (!t || t === "—") return "—";
    if (t === "strict_soft") return "Soft attest";
    if (t === "hard") return "Hard path · not a live TPM";
    if (t === "open") return "Open · no gate";
    return String(raw || "").trim();
  };
  const fabricTierSay = (raw) => {
    const t = String(raw || "").trim().toLowerCase();
    if (t === "accurate") return "Accurate";
    return "Fast";
  };
  const fabricModeSay = (raw) => {
    const t = String(raw || "").trim().toLowerCase();
    if (t === "partial") return "Partial";
    if (t === "max") return "Max";
    return "Auto";
  };
  const fabricStatusSay = (raw) => {
    const t = String(raw || "").trim().toLowerCase();
    if (t === "ready") return "Ready";
    if (t === "draining") return "Draining";
    return "Down";
  };
  const fabricWorkerSay = (raw) => {
    const s = String(raw || "").trim();
    if (!s) return "This worker";
    if (typeof looksLikeCode === "function" && looksLikeCode(s)) return "This worker";
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s)) return "This worker";
    if (/^[0-9a-f]{16,}$/i.test(s)) return "This worker";
    return s;
  };
  const fabricKindSay = (raw) => {
    const s = String(raw || "").trim();
    if (!s || s === "—") return "—";
    if (typeof looksLikeCode === "function" && looksLikeCode(s)) return "—";
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s)) return "—";
    if (/^[0-9a-f]{16,}$/i.test(s)) return "—";
    if (/[_/.:]/.test(s)) return "—";
    return s;
  };
  const fabricHeardSay = (raw) => {
    if (raw == null || raw === "") return "—";
    const t = Date.parse(raw);
    if (!Number.isFinite(t)) return "—";
    return typeof fmtRel === "function" ? fmtRel(raw) : "—";
  };
  const fabricJobSay = (raw) => {
    const s = String(raw || "").trim();
    if (!s || s === "—") return "—";
    if (typeof looksLikeCode === "function" && looksLikeCode(s)) return "This job";
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s)) return "This job";
    if (/^[0-9a-f]{16,}$/i.test(s)) return "This job";
    return s;
  };
  const fabricSpaceSay = (raw) => {
    const s = String(raw || "").trim();
    if (!s || s === "—") return "—";
    if (typeof looksLikeCode === "function" && looksLikeCode(s)) return "This Space";
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s)) return "This Space";
    if (/^[0-9a-f]{16,}$/i.test(s)) return "This Space";
    return s;
  };

  mountView(
    view,
    `<div class="croc-desk-stack">
      <div class="card glass-panel glass-panel--panel">
        <h2 style="margin:0 0 8px">Spatial Compute Fabric</h2>
        <p class="muted" style="margin:0 0 12px;line-height:1.45;max-width:70ch">
          Soft ladder stays closed. Cosign is not production signing. L9 is not a live TPM.
          Accept is not Publish — a node never publishes.
        </p>
        <p id="fabricReadiness" class="muted" style="margin:0 0 12px;font-size:12px;line-height:1.4">Soft readiness…</p>
        <div class="inline-form" style="gap:10px;flex-wrap:wrap;margin-bottom:12px">
          <label class="field grow"><span>Existing Space</span>
            <input id="fabricSpaceId" placeholder="Space for this pinned job" autocomplete="off" />
          </label>
          <label class="field"><span>Quality</span>
            <select id="fabricTier">
              <option value="fast">Fast</option>
              <option value="accurate">Accurate</option>
            </select>
          </label>
          <label class="field"><span>Schedule mode</span>
            <select id="fabricSchedMode">
              <option value="auto">Auto</option>
              <option value="partial">Partial</option>
              <option value="max">Max</option>
            </select>
          </label>
          <button type="button" class="btn secondary btn-tap" id="fabricSchedSave">Save schedule</button>
          <label class="field"><span>Channel Soft</span>
            <select id="fabricChannel">
              <option value="lab_soft">Soft ladder</option>
              <option value="soft_mtls_standin">Soft stand-in · not production mTLS</option>
              <option value="production_mtls">Production mTLS path · Soft until armed</option>
              <option value="wg_overlay_soft">Overlay Soft · not live</option>
              <option value="l9_pcr_soft">L9 Soft · not a live TPM</option>
              <option value="cosign_publish_soft">Cosign Soft · Accept is not Publish</option>
            </select>
          </label>
          <label class="field"><span>Attest Soft</span>
            <select id="fabricAttest">
              <option value="open">Open · no gate</option>
              <option value="strict_soft">Soft attest</option>
              <option value="hard">Hard path · not a live TPM</option>
            </select>
          </label>
          <button type="button" class="btn secondary btn-tap" id="fabricTrustSave">Save trust posture</button>
        </div>
        <div class="row" style="gap:8px;flex-wrap:wrap;margin-bottom:12px">
          <button type="button" class="btn btn-tap" id="fabricRefresh">Refresh nodes</button>
          <button type="button" class="btn secondary btn-tap" id="fabricSelectReady">Select Ready</button>
          <button type="button" class="btn secondary btn-tap" id="fabricClearSel">Clear selection</button>
          <button type="button" class="btn btn-tap" id="fabricDispatchMulti">Dispatch selected</button>
          <button type="button" class="btn secondary btn-tap" data-cc-go="croc-fde">CROC-FDE invite</button>
          <button type="button" class="btn secondary btn-tap" data-cc-go="overview">Home</button>
        </div>
        <p id="fabricMsg" class="muted" aria-live="polite">Loading…</p>
        <div id="fabricStats" class="row" style="gap:12px;flex-wrap:wrap;margin:8px 0"></div>
        <div id="fabricNodes" class="croc-cc-scroll" style="margin-top:8px"></div>
      </div>
      <div class="card glass-panel glass-panel--panel">
        <h3 style="margin:0 0 8px">Result Binding Soft</h3>
        <p class="muted" style="margin:0 0 10px;line-height:1.45;max-width:70ch">
          Accepted jobs with Soft Result Binding.
          <b>Accept is not Publish</b> — fencing is scrubbed on the board.
        </p>
        <div class="row" style="gap:8px;flex-wrap:wrap;margin-bottom:10px">
          <button type="button" class="btn btn-tap" id="fabricBindRefresh">Refresh bindings</button>
        </div>
        <p id="fabricBindMsg" class="muted" aria-live="polite">—</p>
        <div id="fabricBindings" class="croc-cc-scroll" style="margin-top:8px"></div>
      </div>
      <div class="card glass-panel glass-panel--inset" style="padding:14px">
        <p class="muted" style="margin:0;font-size:13px;line-height:1.45">
          <b>Shipped Soft:</b> Nodes · Score · Quality · Schedule · Result Binding · pin · Quarantine.
          <b>Honesty:</b> Soft HMAC is not production Cosign or a live TPM. Posture stays Soft. mTLS stays unclaimed.
        </p>
      </div>
    </div>`,
  );

  const msg = $("#fabricMsg", view);
  const stats = $("#fabricStats", view);
  const list = $("#fabricNodes", view);
  const readinessEl = $("#fabricReadiness", view);
  const bindMsg = $("#fabricBindMsg", view);
  const bindList = $("#fabricBindings", view);
  const selected = new Set();
  let lastSchedule = {};

  const readSpaceId = () => String(($("#fabricSpaceId", view) && $("#fabricSpaceId", view).value) || "").trim();
  const readTier = () => String(($("#fabricTier", view) && $("#fabricTier", view).value) || "fast").trim() || "fast";
  const selectedIds = () => Array.from(selected);

  const syncChecks = () => {
    if (!list) return;
    list.querySelectorAll("input[data-sel]").forEach((el) => {
      const id = String(el.getAttribute("data-sel") || "");
      el.checked = selected.has(id);
    });
    const multi = $("#fabricDispatchMulti", view);
    if (multi) multi.textContent = `Dispatch selected (${selected.size})`;
  };

  const applyScheduleUi = (sched) => {
    lastSchedule = sched || {};
    const modeEl = $("#fabricSchedMode", view);
    if (modeEl && lastSchedule.mode) modeEl.value = String(lastSchedule.mode);
  };

  const render = (pack) => {
    if (!list) return;
    const nodes = Array.isArray(pack && pack.nodes) ? pack.nodes : [];
    const fs = (pack && pack.fabric_schedule) || {};
    if (fs.mode) applyScheduleUi(fs);
    if (stats) {
      stats.innerHTML = `
        <span class="badge online">Ready ${escapeHtml(String(pack.ready ?? 0))}</span>
        <span class="badge neutral">Nodes ${escapeHtml(String(pack.count ?? nodes.length))}</span>
        <span class="badge warn">Draining ${escapeHtml(String(pack.draining ?? 0))}</span>
        <span class="badge offline">Ineligible ${escapeHtml(String(pack.ineligible ?? 0))}</span>
        <span class="badge neutral">Mode ${escapeHtml(fabricModeSay(fs.mode || lastSchedule.mode || "auto"))}</span>
        <span class="badge neutral">Min Score ${escapeHtml(String(fs.min_score ?? "—"))}</span>
        <span class="badge neutral">Selected ${escapeHtml(String(selected.size))}</span>`;
    }
    if (!nodes.length) {
      list.innerHTML = `<p class="muted">No nodes yet. Pair this worker, then refresh.</p>`;
      return;
    }
    const alive = new Set(nodes.map((n) => String(n.id || "").trim()).filter(Boolean));
    Array.from(selected).forEach((id) => {
      if (!alive.has(id)) selected.delete(id);
    });
    list.innerHTML = `<div class="croc-cc-tools__grid">${nodes
      .map((n) => {
        const id = String(n.id || "").trim();
        const st = String(n.status || "down");
        const cls = st === "ready" ? "online" : st === "draining" ? "warn" : "offline";
        const key = escapeHtml(fabricWorkerSay(n.worker_key || id || ""));
        const kind = escapeHtml(fabricKindSay(n.execution_kind || n.kind || ""));
        const use = Number(n.slots_in_use || 0);
        const cap = Number(n.concurrent_slots || 1);
        const hb = escapeHtml(fabricHeardSay(n.last_heartbeat_at));
        const eligible = n.scheduling_eligible !== false;
        const score = n.trust_score != null ? String(n.trust_score) : "—";
        const sok = n.schedule_ok !== false;
        const attSt = fabricAttestSay((n.attestation && n.attestation.status) || "");
        const behOk = !(n.behavior && n.behavior.anomaly);
        const ch = escapeHtml(fabricChannelSay(n.channel || "lab_soft"));
        const checked = selected.has(id) ? "checked" : "";
        return `<article class="croc-cc-tool-card glass-panel glass-panel--inset" data-worker-id="${escapeHtml(id)}">
          <div class="croc-cc-tool-card__top">
            <label class="row" style="gap:8px;align-items:center;margin:0">
              <input type="checkbox" data-sel="${escapeHtml(id)}" ${checked} />
              <strong>${key}</strong>
            </label>
            <span class="badge ${cls}">${escapeHtml(fabricStatusSay(st))}</span>
          </div>
          <p class="muted" style="margin:6px 0 0;font-size:12px;line-height:1.4">
            ${kind === "—" ? "" : `${kind} · `}Score <b>${escapeHtml(score)}</b>
            · ${sok ? "On schedule" : "Below schedule"} · ${escapeHtml(String(use))} of ${escapeHtml(String(cap))}
            · ${eligible ? "Eligible" : "Held Soft"}<br/>
            ${ch} · ${escapeHtml(attSt)} · ${behOk ? "Behavior ok" : "Behavior Soft"} · mTLS stays unclaimed<br/>
            ${hb === "—" ? "—" : hb}
          </p>
          <div class="croc-cc-tool-card__act row" style="gap:6px;flex-wrap:wrap;margin-top:8px">
            <button type="button" class="btn sm btn-tap" data-act="dispatch" data-id="${escapeHtml(id)}">Dispatch pin</button>
            <button type="button" class="btn sm secondary btn-tap" data-act="quarantine" data-id="${escapeHtml(id)}" data-on="${eligible ? "1" : "0"}">
              ${eligible ? "Quarantine" : "Release"}
            </button>
          </div>
        </article>`;
      })
      .join("")}</div>`;
    syncChecks();
  };

  const loadSchedule = async () => {
    try {
      const pack = await api("/twin-bridge/fabric/schedule", { timeoutMs: 20000 });
      applyScheduleUi((pack && pack.schedule) || {});
    } catch (_e) {
      /* nodes pack may still carry fabric_schedule */
    }
    try {
      const tp = await api("/twin-bridge/fabric/trust-posture", { timeoutMs: 20000 });
      const p = (tp && tp.posture) || {};
      const ch = $("#fabricChannel", view);
      const at = $("#fabricAttest", view);
      if (ch && p.channel) ch.value = String(p.channel);
      if (at && p.attestation_mode) at.value = String(p.attestation_mode);
    } catch (_e2) {
      /* optional Soft */
    }
    try {
      const rd = await api("/twin-bridge/fabric/readiness", { timeoutMs: 20000 });
      const r = (rd && rd.readiness) || {};
      if (readinessEl) {
        const ok = r.pass === true || r.ok === true;
        const ready = r.production_mtls_ready === true;
        const claimed = r.mtls_claimed === true;
        const hard = r.attestation_hard === true;
        const wgNeed = r.wg_required === true;
        const l9Need = r.l9_required === true;
        const cosNeed = r.cosign_required === true;
        const softSafe = r.soft_default_safe === true;
        const arms = [];
        if (ready) arms.push(claimed ? "mTLS is claimed" : "mTLS is ready · not claimed");
        if (wgNeed) arms.push("Overlay required · not live");
        if (l9Need) arms.push("L9 required · not a live TPM");
        if (cosNeed) arms.push("Cosign required · not production signing");
        if (hard && !l9Need) arms.push("Hard attest · not a live TPM");
        readinessEl.textContent = arms.length
          ? `${arms.join(" · ")}. Accept is not Publish.`
          : softSafe
            ? "Soft ladder stays closed. Accept is not Publish."
            : ok
              ? "Soft closeout. Accept is not Publish."
              : "Soft closeout is not ready.";
      }
    } catch (_e4) {
      if (readinessEl) readinessEl.textContent = "Soft readiness unreachable";
    }
  };

  const loadBindings = async () => {
    if (bindMsg) bindMsg.textContent = "Loading bindings…";
    try {
      const pack = await api("/twin-bridge/fabric/result-bindings?limit=40", { timeoutMs: 30000 });
      const items = Array.isArray(pack && pack.items) ? pack.items : [];
      if (bindMsg) {
        bindMsg.textContent = `Soft bindings ${pack.binding_ok_count ?? 0}/${pack.count ?? items.length} ok · Accept is not Publish`;
      }
      if (!bindList) return;
      if (!items.length) {
        bindList.innerHTML = `<p class="muted">No accepted Result Bindings yet. Accept a Worker result to mint a Soft capsule.</p>`;
        return;
      }
      bindList.innerHTML = `<div class="croc-cc-tools__grid">${items
        .map((b) => {
          const rawJid = String(b.job_id || b.task_job_id || "").trim();
          const jid = escapeHtml(rawJid || "—");
          const ok = b.binding_ok !== false;
          return `<article class="croc-cc-tool-card glass-panel glass-panel--inset" data-job-id="${jid}">
            <div class="croc-cc-tool-card__top">
              <strong>${escapeHtml(fabricJobSay(rawJid))}</strong>
              <span class="badge ${ok ? "online" : "offline"}">${ok ? "Binding ok" : "Not valid"}</span>
            </div>
            <p class="muted" style="margin:6px 0 0;font-size:12px;line-height:1.4">
              ${escapeHtml(fabricWorkerSay(b.worker_id || ""))} · Accept is not Publish · mTLS stays unclaimed
            </p>
            <div class="croc-cc-tool-card__act row" style="gap:6px;margin-top:8px">
              <button type="button" class="btn sm btn-tap" data-act="verify-bind" data-id="${jid}">Verify Soft</button>
            </div>
          </article>`;
        })
        .join("")}</div>`;
    } catch (e) {
      const err = typeof softApiError === "function" ? softApiError(e) : String(e.message || e);
      if (bindMsg) bindMsg.textContent = err;
      if (bindList) bindList.innerHTML = `<p class="muted">${escapeHtml(err)}</p>`;
    }
  };

  const load = async () => {
    if (msg) msg.textContent = "Loading…";
    try {
      await loadSchedule();
      const pack = await api("/twin-bridge/fabric/nodes?limit=100", { timeoutMs: 30000 });
      if (msg) {
        msg.textContent = pack && pack.ok
          ? "Soft · Score · Accept is not Publish"
          : "Loaded.";
      }
      render(pack || {});
      await loadBindings();
    } catch (e) {
      const err = typeof softApiError === "function" ? softApiError(e) : String(e.message || e);
      if (msg) msg.textContent = err;
      if (list) list.innerHTML = `<p class="muted">Could not load nodes. ${escapeHtml(err)}</p>`;
      toast(err, "err");
    }
  };

  const btn = $("#fabricRefresh", view);
  if (btn) btn.onclick = () => { load(); };
  const bindBtn = $("#fabricBindRefresh", view);
  if (bindBtn) bindBtn.onclick = () => { loadBindings(); };

  if (bindList) {
    bindList.addEventListener("click", async (ev) => {
      const t = ev.target && ev.target.closest ? ev.target.closest("[data-act=verify-bind]") : null;
      if (!t || !bindList.contains(t)) return;
      const jid = String(t.getAttribute("data-id") || "").trim();
      if (!jid) return;
      t.disabled = true;
      try {
        const pack = await api("/twin-bridge/fabric/result-bindings/verify", {
          method: "POST",
          body: { job_id: jid },
          timeoutMs: 20000,
        });
        toast(pack && pack.ok ? "Binding ok" : `Binding fail: ${pack.reason || "—"}`, pack && pack.ok ? "ok" : "warn");
        await loadBindings();
      } catch (e) {
        toast(typeof softApiError === "function" ? softApiError(e) : String(e.message || e), "err");
      } finally {
        t.disabled = false;
      }
    });
  }

  const saveSched = $("#fabricSchedSave", view);
  if (saveSched) {
    saveSched.onclick = async () => {
      const modeEl = $("#fabricSchedMode", view);
      const mode = String((modeEl && modeEl.value) || "auto").trim() || "auto";
      if (!(await softConfirm(`Set Fabric schedule mode to ${fabricModeSay(mode)}? (process Soft — resets on Twin restart)`))) return;
      saveSched.disabled = true;
      try {
        const pack = await api("/twin-bridge/fabric/schedule", {
          method: "PUT",
          body: { mode },
          timeoutMs: 20000,
        });
        applyScheduleUi((pack && pack.schedule) || { mode });
        toast(`Schedule Soft → ${fabricModeSay(mode)}`, "ok");
        await load();
      } catch (e) {
        toast(typeof softApiError === "function" ? softApiError(e) : String(e.message || e), "err");
      } finally {
        saveSched.disabled = false;
      }
    };
  }

  const saveTrust = $("#fabricTrustSave", view);
  if (saveTrust) {
    saveTrust.onclick = async () => {
      const ch = String(($("#fabricChannel", view) && $("#fabricChannel", view).value) || "lab_soft");
      const at = String(($("#fabricAttest", view) && $("#fabricAttest", view).value) || "open");
      if (!(await softConfirm(`Set Soft trust to ${fabricChannelSay(ch)} · ${fabricAttestSay(at)}? mTLS stays unclaimed.`))) return;
      saveTrust.disabled = true;
      try {
        await api("/twin-bridge/fabric/trust-posture", {
          method: "PUT",
          body: { channel: ch, attestation_mode: at },
          timeoutMs: 20000,
        });
        toast(`Trust Soft · ${fabricChannelSay(ch)} · ${fabricAttestSay(at)}`, "ok");
        await load();
      } catch (e) {
        toast(typeof softApiError === "function" ? softApiError(e) : String(e.message || e), "err");
      } finally {
        saveTrust.disabled = false;
      }
    };
  }

  const selReady = $("#fabricSelectReady", view);
  if (selReady) {
    selReady.onclick = () => {
      if (!list) return;
      list.querySelectorAll("article[data-worker-id]").forEach((card) => {
        const badge = card.querySelector(".badge");
        const id = String(card.getAttribute("data-worker-id") || "");
        if (id && badge && String(badge.textContent || "").trim() === "Ready") selected.add(id);
      });
      syncChecks();
    };
  }
  const clearSel = $("#fabricClearSel", view);
  if (clearSel) {
    clearSel.onclick = () => {
      selected.clear();
      syncChecks();
    };
  }

  const multiBtn = $("#fabricDispatchMulti", view);
  if (multiBtn) {
    multiBtn.onclick = async () => {
      const sid = readSpaceId();
      const ids = selectedIds();
      if (!sid) {
        toast("Enter Existing Space first.", "warn");
        return;
      }
      if (ids.length < 2) {
        toast("Select at least 2 Workers for multi pin.", "warn");
        return;
      }
      if (ids.length > 20) {
        toast("Multi pin max is 20 Workers.", "warn");
        return;
      }
      if (!(await softConfirm(`Pin Soft jobs on ${ids.length} Workers for ${fabricSpaceSay(sid)}? Accept is not Publish.`))) return;
      multiBtn.disabled = true;
      try {
        const pack = await api("/twin-bridge/fabric/dispatch-multi", {
          method: "POST",
          body: { space_id: sid, worker_ids: ids, quality_tier: readTier(), publish_mode: "manual" },
          timeoutMs: 60000,
        });
        const n = Number(pack.dispatched || 0);
        const fail = Number(pack.failed || 0);
        toast(fail ? `Multi pin partial ${n} ok / ${fail} fail` : `Multi pin ${n} jobs`, fail ? "warn" : "ok");
        if (msg) {
          msg.textContent = `Multi pin · dispatched ${n}/${pack.requested || ids.length}`
            + (fail ? ` · failed ${fail}` : "");
        }
      } catch (e) {
        toast(typeof softApiError === "function" ? softApiError(e) : String(e.message || e), "err");
      } finally {
        multiBtn.disabled = false;
      }
    };
  }

  if (list) {
    list.addEventListener("change", (ev) => {
      const t = ev.target;
      if (!t || !t.getAttribute || !t.getAttribute("data-sel")) return;
      const id = String(t.getAttribute("data-sel") || "");
      if (!id) return;
      if (t.checked) selected.add(id);
      else selected.delete(id);
      syncChecks();
    });
    list.addEventListener("click", async (ev) => {
      const t = ev.target && ev.target.closest ? ev.target.closest("[data-act]") : null;
      if (!t || !list.contains(t)) return;
      const act = t.getAttribute("data-act");
      const wid = String(t.getAttribute("data-id") || "").trim();
      if (!wid) return;
      if (act === "dispatch") {
        const sid = readSpaceId();
        if (!sid) {
          toast("Enter Existing Space first.", "warn");
          return;
        }
        if (!(await softConfirm(`Pin a Soft job on this Worker for ${fabricSpaceSay(sid)}? Accept is not Publish.`))) return;
        t.disabled = true;
        try {
          const pack = await api("/twin-bridge/fabric/dispatch", {
            method: "POST",
            body: { space_id: sid, worker_id: wid, quality_tier: readTier(), publish_mode: "manual" },
            timeoutMs: 30000,
          });
          toast(`Pinned ${pack.job_id ? fabricJobSay(pack.job_id) : "queued"}`, "ok");
          if (msg) {
            msg.textContent = `Dispatched pin · ${pack.job_id ? fabricJobSay(pack.job_id) : "—"} · ${fabricWorkerSay(wid)}`;
          }
        } catch (e) {
          toast(typeof softApiError === "function" ? softApiError(e) : String(e.message || e), "err");
        } finally {
          t.disabled = false;
        }
        return;
      }
      if (act === "quarantine") {
        const on = t.getAttribute("data-on") === "1";
        const next = on;
        if (!(await softConfirm(next ? "Quarantine this node (stop scheduling)?" : "Release node from quarantine?"))) return;
        t.disabled = true;
        try {
          await api(`/twin-bridge/fabric/nodes/${encodeURIComponent(wid)}/quarantine`, {
            method: "POST",
            body: {
              quarantine: next,
              trigger: "manual_sa",
              reason: next ? "manual_sa_console" : "released_console",
            },
            timeoutMs: 30000,
          });
          toast(next ? "Quarantined Soft" : "Released Soft", "ok");
          await load();
        } catch (e) {
          toast(typeof softApiError === "function" ? softApiError(e) : String(e.message || e), "err");
        } finally {
          t.disabled = false;
        }
      }
    });
  }

  await load();
});


// ── forgot-password.route.js ─────────────────────────────────────
/**
 * Route: #/forgot-password — username + email + SHA code reset flow.
 *
 * Build: concatenated by `scripts/build-dashboard.mjs`. Shares scope with
 * console.raw.js — see `account-activate.route.js` for the convention.
 *
 * Backend contracts (verified by `tests/test_spa_api_contract.py`):
 *   - GET  /auth/forgot/email/enabled   → feature gate (mailer configured?)
 *   - POST /auth/forgot/email/check     → username/email match check
 *   - POST /auth/forgot/email/start     → request SHA code
 *   - POST /auth/forgot/email/complete  → set new password
 *
 * Cooldown timer leak guard: any setInterval is also pinned on
 * `window.__fpCooldownTimer` so route navigation can clear it via
 * `clearRouteTickers()` in console.raw.js.
 */

registerRoute("forgot-password", async (view) => {
  setCrumb("Forgot password");
  document.body.dataset.auth = "none";
  activateCommandCenterAuthLayout();
  let enabled = true;
  try {
    const r = await fetch(apiBase() + buildGetAuthForgotEmailEnabledPath());
    const j = await r.json();
    enabled = !!j.enabled;
  } catch { enabled = false; }
  mountView(view, buildCommandCenterAuthSurfaceHtml("recovery", `
          <div class="auth-card auth-card--panel auth-card--auth-main auth-card--wide auth-card--prose" data-auth-card>
            <header class="auth-card__head">
              <h1 class="auth-card__title">Account recovery</h1>
              <p class="auth-card__lead">Reset via email verification code</p>
            </header>
            <div class="auth-card__body">
              <p class="muted auth-card__prose">
                Enter your username and the same email used at registration.
                The server sends a SHA-style verification code to that email.
                Enter the code to set a new password (saved permanently on server).
              </p>
              ${enabled ? "" : `<p class="badge revoked" style="margin:10px 0">Email sender is not configured on server.</p>`}
              <div id="fpStep1">
                <label class="field"><span>Username</span><input id="fp_user" autocomplete="username" /></label>
                <label class="field field--spaced"><span>Registered email</span><input id="fp_email" autocomplete="email" /></label>
                <div class="auth-card__submit">
                  <button class="btn btn-tap btn-block auth-btn-primary" type="button" id="fp_go" ${enabled ? "" : "disabled"}>Send SHA code</button>
                  <a class="auth-link auth-link--center" href="#/login">Back to sign in</a>
                </div>
                <p class="auth-card__msg muted" id="fp_msg1" aria-live="polite"></p>
              </div>
              <div id="fpStep2" style="display:none">
                <label class="field"><span>SHA code (from email)</span>
                  <input id="fp_sha_code" class="mono" maxlength="32" autocomplete="one-time-code" />
                </label>
                <p class="muted" id="fp_otp_expiry" style="margin:8px 0 0" aria-live="polite"></p>
                <label class="field field--spaced"><span>New password (≥12, upper, lower, digit, symbol)</span><input id="fp_p1" type="password" autocomplete="new-password" /></label>
                <label class="field field--spaced"><span>Confirm password</span><input id="fp_p2" type="password" autocomplete="new-password" /></label>
                <div class="auth-card__submit">
                  <button class="btn btn-tap btn-block auth-btn-primary" type="button" id="fp_done">Update password</button>
                  <button class="btn secondary btn-tap btn-block" type="button" id="fp_resend">Resend SHA code</button>
                  <button class="btn secondary btn-tap btn-block" type="button" id="fp_back">Back</button>
                </div>
                <p class="auth-card__msg muted" id="fp_msg2" aria-live="polite"></p>
              </div>
            </div>
          </div>`));
  const m1 = $("#fp_msg1"), m2 = $("#fp_msg2");
  let disposeFpCodeExpiry = () => {};
  let fpCooldown = 0;
  let fpCooldownTimer = 0;
  const fpGoBtn = $("#fp_go");
  const fpResendBtn = $("#fp_resend");
  const applyFpCooldownUi = () => {
    const left = Math.max(0, Number(fpCooldown || 0));
    if (fpGoBtn) {
      fpGoBtn.disabled = !enabled || left > 0;
      fpGoBtn.textContent = left > 0 ? `Resend in ${left}s` : "Send SHA code";
    }
    if (fpResendBtn) {
      fpResendBtn.disabled = left > 0;
      fpResendBtn.textContent = left > 0 ? `Resend in ${left}s` : "Resend SHA code";
    }
  };
  const startFpCooldown = (seconds) => {
    fpCooldown = Math.max(0, Number(seconds || 0));
    applyFpCooldownUi();
    if (fpCooldownTimer) clearInterval(fpCooldownTimer);
    // Also kill any leftover from an earlier visit so navigating away mid-cooldown
    // and coming back doesn't double-tick the UI.
    if (window.__fpCooldownTimer) {
      try { clearInterval(window.__fpCooldownTimer); } catch (_) {}
      window.__fpCooldownTimer = 0;
    }
    if (fpCooldown <= 0) return;
    fpCooldownTimer = setInterval(() => {
      fpCooldown = Math.max(0, fpCooldown - 1);
      applyFpCooldownUi();
      if (fpCooldown <= 0) {
        clearInterval(fpCooldownTimer);
        fpCooldownTimer = 0;
        window.__fpCooldownTimer = 0;
      }
    }, 1000);
    window.__fpCooldownTimer = fpCooldownTimer;
  };
  const parseCooldownFromMessage = (msg) => {
    const m = String(msg || "").match(/wait\s+(\d+)s/i);
    return m ? Math.max(1, Number(m[1])) : 0;
  };
  const doForgotSend = async () => {
    m1.textContent = "";
    const username = $("#fp_user").value.trim();
    const email = ($("#fp_email").value || "").trim().toLowerCase();
    if (!username || !email) { m1.textContent = "Enter username and email"; return null; }
    if (fpCooldown > 0) { m1.textContent = `Please wait ${fpCooldown}s before resending.`; return null; }
    const check = await fetch(apiBase() + buildPostAuthForgotEmailCheckPath(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, email }),
    });
    const cj = await check.json().catch(() => ({}));
    if (!check.ok) {
      const det = cj.detail;
      const msg = Array.isArray(det) ? det.map((x) => x.msg || JSON.stringify(x)).join("; ") : (det || check.statusText);
      throw new Error(msg);
    }
    if (!cj.matched) {
      m1.textContent = "Username and registered email do not match.";
      return null;
    }
    const preWait = Number(cj.resend_after_seconds || 0);
    if (preWait > 0) {
      startFpCooldown(preWait);
      m1.textContent = `Please wait ${preWait}s before sending again.`;
      return null;
    }
    const r = await fetch(apiBase() + buildPostAuthForgotEmailStartPath(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, email }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) {
      const det = d.detail;
      const msg = Array.isArray(det) ? det.map((x) => x.msg || JSON.stringify(x)).join("; ") : (det || r.statusText);
      const wait = parseCooldownFromMessage(msg);
      if (wait > 0) startFpCooldown(wait);
      throw new Error(msg);
    }
    const cd = Number(d.resend_after_seconds || 60);
    startFpCooldown(cd);
    m1.textContent = `Code sent. Valid for about ${Math.max(1, Math.round(Number(d.ttl_seconds || 0) / 60))} min — see countdown below.`;
    return { ttl_seconds: Number(d.ttl_seconds || 0), resend_after_seconds: cd };
  };
  applyFpCooldownUi();
  $("#fp_go").addEventListener("click", async () => {
    try {
      const sent = await doForgotSend();
      if (!sent) return;
      disposeFpCodeExpiry();
      disposeFpCodeExpiry = startEmailCodeExpiryCountdown($("#fp_otp_expiry"), sent.ttl_seconds);
      $("#fpStep1").style.display = "none";
      $("#fpStep2").style.display = "block";
    } catch (e) { m1.textContent = String(e.message || e); }
  });
  if (fpResendBtn) {
    fpResendBtn.addEventListener("click", async () => {
      try {
        const sent = await doForgotSend();
        if (!sent) return;
        disposeFpCodeExpiry();
        disposeFpCodeExpiry = startEmailCodeExpiryCountdown($("#fp_otp_expiry"), sent.ttl_seconds);
        m2.textContent = `Code resent. Wait ${fpCooldown}s before next resend.`;
      } catch (e) { m2.textContent = String(e.message || e); }
    });
  }
  $("#fp_back").addEventListener("click", () => {
    disposeFpCodeExpiry();
    disposeFpCodeExpiry = () => {};
    const ex = $("#fp_otp_expiry");
    if (ex) ex.textContent = "";
    $("#fpStep2").style.display = "none";
    $("#fpStep1").style.display = "block";
    m2.textContent = "";
  });
  $("#fp_done").addEventListener("click", async () => {
    m2.textContent = "";
    const username = $("#fp_user").value.trim();
    const email = ($("#fp_email").value || "").trim().toLowerCase();
    const sha_code = ($("#fp_sha_code").value || "").trim().toUpperCase();
    const password = $("#fp_p1").value;
    const password_confirm = $("#fp_p2").value;
    if (!email || !sha_code || !password) { m2.textContent = "Enter email, SHA code, and password"; return; }
    if (password !== password_confirm) { m2.textContent = "Passwords do not match"; return; }
    try {
      const r = await fetch(apiBase() + buildPostAuthForgotEmailCompletePath(), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, email, sha_code, password, password_confirm }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        const det = d.detail;
        const mapped = formatHttpApiDetail(det);
        const msg = mapped
          || (Array.isArray(det) ? det.map((x) => x.msg || JSON.stringify(x)).join("; ") : "")
          || (typeof det === "string" ? det : "")
          || r.statusText;
        throw new Error(msg);
      }
      setChildMarkup(m2, `<span class="badge online">Password updated</span> Redirecting to sign in…`);
      toast("Password updated", "ok");
      scheduleRouteRedirect(1500, "#/login");
    } catch (e) { m2.textContent = String(e.message || e); }
  });
});


// ── group.route.js ─────────────────────────────────────
/**
 * Route: #/group — Single group/site detail (deep link only).
 *
 * Build: split out of src/console.raw.js and concatenated as raw text by
 * scripts/build-dashboard.mjs after the monolith body. Shares scope with
 * helpers like $, mountView, api, registerRoute, state, toast, can, setCrumb
 * (defined in console.raw.js and the lib/ modules spliced at the top).
 */

registerRoute("group", async (view, args, routeSeq) => {
  // Two-key strategy (Phase 90): the URL carries whatever casing the
  // user typed/clicked — preserve that for display, but match devices
  // case-insensitively via the canonical key so siblings collide the
  // same way the backend's _sibling_group_norm does. Without this, a
  // route like "/group/Warehouse%20A" would not match a device whose
  // notification_group is "warehouse a", even though they're siblings
  // server-side.
  let rawArg = decodeURIComponent(args[0] || "");
  // ZD-PARTIAL-F18 expire-purge tip578: tip575 softs (#/group/demo · bare #/group → #/groups) deleted.
  // Bare / empty key → Home (no forever soft redirect). "#/group/demo" is a normal group key if present.
  const g = canonicalGroupKey(rawArg);
  if (!g) { location.hash = defaultHomeHash(); return; }
  const isGroupsHubDirectoryView = isGroupHubDirectoryView(g, canonicalGroupKey);
  const isAllDevicesView = isGroupFleetAllView(g, canonicalGroupKey);
  const useGroupModeB = true;
  const displayFromUrl = displayGroupName(rawArg);
  const tenantOwner = String((window.__routeQuery && window.__routeQuery.get("owner")) || "").trim();
  const metaKey = groupCardMetaKey(g, tenantOwner);
  const groupSlotCtx = { me: state.me, canonicalGroupKey, groupCardMetaKey };
  const groupScope = (state.me && state.me.username) ? state.me.username : "anon";
  const GROUP_META_LS_KEY = `croc.group.meta.v2.${groupScope}`;
  const GROUP_SETTINGS_LS_KEY = `croc.group.settings.v1.${groupScope}`;
  const GROUP_API_CAPS_LS_KEY = `croc.group.api.caps.v2.${groupScope}`;
  const loadGroupMeta = () => {
    try {
      const raw = localStorage.getItem(GROUP_META_LS_KEY);
      const obj = raw ? JSON.parse(raw) : {};
      return (obj && typeof obj === "object") ? obj : {};
    } catch { return {}; }
  };
  const loadGroupSettings = () => {
    try {
      const raw = localStorage.getItem(GROUP_SETTINGS_LS_KEY);
      const obj = raw ? JSON.parse(raw) : {};
      return (obj && typeof obj === "object") ? obj : {};
    } catch { return {}; }
  };
  const loadGroupApiCaps = () => {
    try {
      const raw = localStorage.getItem(GROUP_API_CAPS_LS_KEY);
      const obj = raw ? JSON.parse(raw) : {};
      return {
        apply: obj && obj.apply === false ? false : true,
        delete: obj && obj.delete === false ? false : true,
      };
    } catch { return { apply: true, delete: true }; }
  };
  const saveGroupApiCaps = (caps) => localStorage.setItem(GROUP_API_CAPS_LS_KEY, JSON.stringify({
    apply: !!(caps && caps.apply),
    delete: !!(caps && caps.delete),
  }));
  window.__groupDelayTimers = window.__groupDelayTimers || new Map();
  const meta = loadGroupMeta();
  const gsMap = loadGroupSettings();
  const groupApiCaps = loadGroupApiCaps();
  // Show *something* immediately on cold loads — /devices may take seconds.
  // Display priority: stored display_name > URL-supplied case > folded key.
  const coldName = isGroupsHubDirectoryView
    ? "Groups"
    : isAllDevicesView
      ? "All boards"
      : ((meta[metaKey] || meta[g] || {}).display_name || displayFromUrl || g);
  let grpMapCtrl = null;
  let disposeGroupDetailTabs = () => {};
  let disposeCreateGroupModal = () => {};
  let disposeSidebarShare = () => {};
  let groupSidebarSlots = [];
  let hubReadOnly = true;
  let list = { items: [] };
  let ov = null;
  let rows = [];
  let ids = [];
  let groupFleetPagesLoaded = GROUP_PAGE_DEVICES_INITIAL_PAGES;
  let groupFleetNextCursor = null;
  let groupFleetHasMore = false;
  const activeSlotCtx = (presenceOverride) => {
    const p = presenceOverride || { total: 0, online: 0, offline: 0 };
    return {
      groupKey: g,
      tenantOwner,
      metaKey,
      displayFromUrl,
      presence: p,
      canonicalGroupKey,
    };
  };
  const normalizeModeBSidebarSlots = (presenceOverride) => {
    if (isAllDevicesView || isGroupsHubDirectoryView) return;
    groupSidebarSlots = ensureActiveGroupInSidebarSlots(groupSidebarSlots, activeSlotCtx(presenceOverride));
  };
  const sidebarListCtx = () => ({
    escapeHtml,
    displayGroupName,
    resolveOverviewGroupCardTitle,
    meta,
    canonicalGroupKey,
    groupCardMetaKey,
    activeGroupKey: g,
    activeTenantOwner: tenantOwner,
    showRevokeCheckbox: !hubReadOnly,
  });
  const fleetCountsForAllDevicesCard = (presenceOverride) => {
    if (presenceOverride && presenceOverride.total != null) {
      return {
        total: Number(presenceOverride.total || 0),
        online: Number(presenceOverride.online || 0),
      };
    }
    if (isAllDevicesView) {
      const p = groupPagePresenceCounts(null, rows);
      return { total: p.total, online: p.online };
    }
    if (ov && Number.isFinite(Number(ov.total_devices))) {
      const total = Number(ov.total_devices);
      const online = Number(ov.online_devices != null ? ov.online_devices : 0);
      return { total, online };
    }
    let total = 0;
    let online = 0;
    for (const slot of groupSidebarSlots) {
      total += Number(slot.serverCounts?.total ?? 0);
      online += Number(slot.serverCounts?.online ?? 0);
    }
    return { total, online };
  };
  const allDevicesSidebarCtx = (presenceOverride) => {
    const fleet = fleetCountsForAllDevicesCard(presenceOverride);
    return {
      escapeHtml,
      allDevicesActive: isAllDevicesView,
      allDevicesTotal: fleet.total,
      allDevicesOnline: fleet.online,
      devices: rows,
      devicePresenceStatus,
      devicePresenceBadgeText,
    };
  };
  const paintGroupSidebarChrome = (presenceOverride) => {
    const shellRoot = view.querySelector(".grp-mode-b-page") || view;
    const sidebarList = shellRoot.querySelector("#grpSidebarList");
    if (sidebarList) {
      sidebarList.innerHTML = buildGroupSidebarListHtml(groupSidebarSlots, sidebarListCtx());
    }
    paintGroupSidebarAllDevicesCard(shellRoot, allDevicesSidebarCtx(presenceOverride));
  };
  const syncModeBShareUi = () => {
    paintGroupSidebarChrome();
    if (isAllDevicesView || isGroupsHubDirectoryView) return;
    const activeSlot = granteesForActiveSlot(groupSidebarSlots, g, tenantOwner, canonicalGroupKey);
    const ownerName = String(
      (activeSlot && activeSlot.tenantOwner)
      || (meta[metaKey] || meta[g] || {}).owner_name
      || (state.principal && state.principal.username)
      || "",
    ).trim();
    const granteesRich = activeSlot && Array.isArray(activeSlot.granteesRich) ? activeSlot.granteesRich : [];
    const granteeCount = granteesRich.length;
    const membersHtml = buildGranteesMembersHtml(granteesRich, escapeHtml, { ownerName });
    paintModeBMembersPanels(view, membersHtml, granteeCount);
  };
  const loadModeBHubAndMerge = async () => {
    if (!useGroupModeB || !isRouteCurrent(routeSeq)) return;
    const hubOwnerQ = String((window.__routeQuery && window.__routeQuery.get("owner")) || tenantOwner || "").trim();
    const hub = await loadModeBHubOperatorRows({
      m: state.me,
      api,
      ownerQ: hubOwnerQ,
      can,
      principalSeesCustodyDashboard,
      probeGroupsHubAdminHasOwnedDevices,
      buildGroupsHubGranteeRows,
      buildGroupsHubOperatorRows,
      buildGroupsHubOperatorRowsFromSummary,
      groupMetaStorageKey,
      filterNorm: "all",
    });
    if (!isRouteCurrent(routeSeq)) return;
    hubReadOnly = !!hub.readOnlyHub;
    normalizeModeBSidebarSlots();
    if (hub.rows && hub.rows.length) {
      groupSidebarSlots = mergeHubRowsIntoSidebarSlots(
        groupSidebarSlots,
        hub.rows,
        canonicalGroupKey,
        groupCardMetaKey,
      );
    }
    normalizeModeBSidebarSlots();
    syncModeBShareUi();
    try { disposeSidebarShare(); } catch (_) {}
    const shellRoot = view.querySelector(".grp-mode-b-page") || view;
    disposeSidebarShare = wireGroupSidebarShareActions(shellRoot, {
      $,
      $$,
      toast,
      readOnlyHub: hubReadOnly,
      onRevokeComplete: loadModeBHubAndMerge,
    });
  };
  const allDevicesBulkToolbarHtml = () => (
    isAllDevicesView ? buildGroupAllDevicesBulkToolbarHtml() : ""
  );
  const buildGroupShellHtml = useGroupModeB ? (opts = {}) => {
    const total = opts.total != null ? opts.total : 0;
    const on = opts.online != null ? opts.online : 0;
    const off = opts.offline != null ? opts.offline : 0;
    const title = escapeHtml(opts.title || coldName);
    const heroInitial = escapeHtml(String(opts.title || coldName).trim().charAt(0).toUpperCase() || "G");
    const statusLabel = on > 0 ? "Active" : (total > 0 ? "Critical" : "Inactive");
    const statusCls = on > 0 ? "online" : (total > 0 ? "offline" : "neutral");
    const ownerLine = escapeHtml(opts.ownerLine || (meta[metaKey] || meta[g] || {}).owner_name || "—");
    const createdBy = escapeHtml(
      (meta[metaKey] || meta[g] || {}).owner_name
      || (state.principal && state.principal.username)
      || "—",
    );
    const sidebarListHtml = opts.sidebarListHtml != null
      ? opts.sidebarListHtml
      : buildGroupSidebarListHtml(groupSidebarSlots, sidebarListCtx());
    const allDevicesSidebarHtml = opts.allDevicesSidebarHtml != null
      ? opts.allDevicesSidebarHtml
      : buildAllDevicesSidebarCardHtml({
        escapeHtml,
        allDevicesActive: isAllDevicesView,
        allDevicesTotal: 0,
        allDevicesOnline: 0,
      });
    const membersPreviewHtml = opts.membersPreviewHtml || (
      `<div class="grp-member-row"><span class="grp-member-row__avatar">${createdBy.charAt(0) || "O"}</span>` +
      `<div><strong>${createdBy}</strong><span class="muted"> · Owner</span></div></div>`
    );
    const rulesHtml = opts.rulesHtml || (
      `<div class="grp-rule-card"><span class="badge online">Active</span> Broadcast Rule</div>` +
      `<div class="grp-rule-card"><span class="badge online">Active</span> Escalation Rule</div>`
    );
    const actionButtonsHtml = opts.actionButtonsHtml || "";
    return buildGroupDetailShellHtml({
      groupTitle: title,
      heroInitial,
      statusLabel,
      statusCls,
      shareCtxLine: opts.shareCtxLine || "",
      legacyFilterBanner: opts.legacyFilterBanner || "",
      fleetTruncBanner: opts.fleetTruncBanner || "",
      total,
      online: on,
      offline: off,
      groupKeyChip: isAllDevicesView || isGroupsHubDirectoryView ? "—" : escapeHtml(g),
      ownerLine,
      createdByLine: createdBy,
      sidebarListHtml,
      allDevicesSidebarHtml,
      membersHtml: membersPreviewHtml,
      membersPreviewHtml,
      rulesHtml,
      rulesPreviewHtml: rulesHtml,
      memberCount: 1,
      ruleCount: 2,
      actionButtonsHtml,
      dangerZoneHtml: opts.dangerZoneHtml || "",
      isAllDevicesView: !!opts.isAllDevicesView,
      isHubDirectoryView: !!opts.isHubDirectoryView,
      hubCardsSectionHtml: opts.hubCardsSectionHtml || "",
      hubModalsHtml: opts.hubModalsHtml || "",
      allDevicesBulkToolbarHtml: opts.allDevicesBulkToolbarHtml != null
        ? opts.allDevicesBulkToolbarHtml
        : allDevicesBulkToolbarHtml(),
    });
  } : () => "";
  const refreshGroupDetailFeed = async () => {
    if (!useGroupModeB || isAllDevicesView || isGroupsHubDirectoryView) return;
    const actList = view.querySelector("#grp-activity-list");
    const actFull = view.querySelector("#grp-activity-full");
    const aiEl = view.querySelector("#grp-ai-body");
    try {
      const evts = await fetchGroupDetailEvents(api, rows.map((d) => d.device_id));
      if (!isRouteCurrent(routeSeq)) return;
      const html = renderGroupActivityListHtml(evts, escapeHtml, fmtRel);
      if (actList) actList.innerHTML = html;
      if (actFull) actFull.innerHTML = html;
      await renderGroupAiRecommendation(aiEl, api, evts, escapeHtml);
    } catch (_) {}
  };
  const initGroupDetailMap = async () => {
    if (!useGroupModeB || isAllDevicesView || isGroupsHubDirectoryView) return;
    const mapCanvas = view.querySelector("#grp-map-canvas");
    if (!mapCanvas) return;
    try {
      if (!grpMapCtrl) {
        grpMapCtrl = createGroupDetailMapController({
          container: mapCanvas,
          groupKey: g,
          canonicalGroupKey,
          isAlive: () => isRouteCurrent(routeSeq),
          api,
          // Same contract builder as Home (#/overview) — already in bundle HEADER.
          buildMapSnapshotPath: () => buildGetDevicesMapSnapshotPath({ skipCache: false }),
          setChildMarkup,
          escapeHtml,
        });
        await grpMapCtrl.init();
      }
      await grpMapCtrl.refresh();
    } catch (e) {
      console.warn("[grp-map] init/refresh failed", e);
    }
  };
  setCrumb(isGroupsHubDirectoryView ? "Groups" : (isAllDevicesView ? "All boards" : `Group · ${coldName}`));
  if (useGroupModeB) {
    ensureGroupDetailStyles();
    mountView(view, buildGroupShellHtml({
      title: coldName,
      isAllDevicesView,
      isHubDirectoryView: isGroupsHubDirectoryView,
      allDevicesBulkToolbarHtml: isAllDevicesView ? buildGroupAllDevicesBulkToolbarHtml() : "",
      sidebarListHtml: '<p class="muted grp-sidebar-empty">Loading groups…</p>',
      allDevicesSidebarHtml: buildAllDevicesSidebarCardHtml({
        escapeHtml,
        allDevicesActive: isAllDevicesView,
        allDevicesTotal: 0,
        allDevicesOnline: 0,
      }),
      hubCardsSectionHtml: isGroupsHubDirectoryView
        ? ""
        : `<p class="muted route-loading__label" style="padding:18px 8px" aria-busy="true">${isAllDevicesView ? "Loading devices…" : "Loading group…"}</p>`,
    }));
  }
  if (!isRouteCurrent(routeSeq)) return;
  const devPath0 = pickDevicesPollPath();
  const fleetLoadCtx = { me: state.me, canonicalGroupKey };
  const loadGroupFleet = async (pollOpts) => loadGroupPageFleet({
    groupKey: g,
    ownerAdmin: tenantOwner || undefined,
    live: pollOpts.live,
    timeoutMs: pollOpts.timeoutMs,
    retries: pollOpts.retries,
    maxPages: pollOpts.maxPages,
    searchQ: pollOpts.searchQ,
    ctx: fleetLoadCtx,
  });
  let hubCardsOv = null;
  let hubCardsList = { items: [] };
  let hubCardsSettingsItems = [];
  if (isGroupsHubDirectoryView) {
    const loadHubGroupSettingsCompat = async () => {
      try {
        const res = await fetchOverviewGroupSettingsList(api);
        return { items: Array.isArray(res && res.items) ? res.items : [] };
      } catch {
        return { items: [] };
      }
    };
    const [hubOvRes, hubSetRes] = await Promise.allSettled([
      fetchOverviewAndDevicesBundle(api),
      loadHubGroupSettingsCompat(),
    ]);
    if (!isRouteCurrent(routeSeq)) return;
    hubCardsOv = (hubOvRes.status === "fulfilled" && hubOvRes.value && hubOvRes.value.ov) ? hubOvRes.value.ov : null;
    hubCardsList = (hubOvRes.status === "fulfilled" && hubOvRes.value && hubOvRes.value.list)
      ? hubOvRes.value.list
      : { items: [] };
    hubCardsSettingsItems = (hubSetRes.status === "fulfilled" && hubSetRes.value && Array.isArray(hubSetRes.value.items))
      ? hubSetRes.value.items
      : [];
    list = { items: [] };
    ov = null;
  } else {
  const [listRes, ovRes] = await Promise.allSettled([
    loadGroupFleet({
      live: devPath0.includes("skip_cache=1"),
      timeoutMs: 16000,
      retries: 1,
      maxPages: isAllDevicesView ? FLEET_DEVICES_INITIAL_PAGES : GROUP_PAGE_DEVICES_INITIAL_PAGES,
    }),
    api(pickOverviewPollPath(), { timeoutMs: 12000, retries: 0 }),
  ]);
  if (!isRouteCurrent(routeSeq)) return;
  list = (listRes.status === "fulfilled" && listRes.value) ? listRes.value : { items: [] };
  ov = (ovRes.status === "fulfilled" && ovRes.value && typeof ovRes.value === "object") ? ovRes.value : null;
  }
  syncGroupMetaWithDevices(meta, list.items || []);
  try { localStorage.setItem(GROUP_META_LS_KEY, JSON.stringify(meta)); } catch (_) {}
  const gm = meta[metaKey] || meta[g] || { display_name: displayFromUrl || g, owner_name: "", phone: "", email: "", device_ids: [] };
  rows = list.items || [];
  ids = rows.map((d) => String(d.device_id || "")).filter(Boolean);
  groupFleetPagesLoaded = list.pages_fetched != null ? Number(list.pages_fetched) : GROUP_PAGE_DEVICES_INITIAL_PAGES;
  groupFleetNextCursor = list.page && list.page.next_cursor != null ? String(list.page.next_cursor) : null;
  groupFleetHasMore = !!(list.page && list.page.has_more) || !!list.truncated;
  const syncGroupPagePresenceBadges = () => {
    const slot = findGroupPageOverviewSlot(ov, g, tenantOwner, groupSlotCtx);
    const presence = groupPagePresenceCounts(slot, rows);
    const tEl = $("#grpTotal", view);
    const oEl = $("#grpOnline", view);
    const fEl = $("#grpOffline", view);
    if (tEl) tEl.textContent = String(presence.total);
    if (oEl) oEl.textContent = String(presence.online);
    if (fEl) fEl.textContent = String(presence.offline);
    const bt = $("#grpBottomTotal", view);
    const bo = $("#grpBottomOnline", view);
    const bf = $("#grpBottomOffline", view);
    const br = $("#grpBottomRate", view);
    if (bt) bt.textContent = String(presence.total);
    if (bo) bo.textContent = String(presence.online);
    if (bf) bf.textContent = String(presence.offline);
    if (br) {
      br.textContent = presence.total > 0
        ? `${Math.round((presence.online / presence.total) * 100)}%`
        : "0%";
    }
    const donut = $("#grpMapDonutPct", view);
    if (donut) {
      donut.textContent = presence.total > 0
        ? `${Math.round((presence.online / presence.total) * 100)}%`
        : "0%";
    }
  };
  const syncGroupPageLoadMoreUi = () => {
    const wrap = $("#grpFleetLoadMoreWrap", view);
    const btn = $("#grpFleetLoadMore", view);
    const hint = $("#grpFleetLoadMoreHint", view);
    if (wrap) wrap.style.display = groupFleetHasMore ? "" : "none";
    if (btn) btn.disabled = false;
    if (hint) {
      const vt = fleetVisibleTotalFrom(list);
      if (groupFleetHasMore) {
        hint.textContent = isAllDevicesView
          ? (vt != null
            ? `${rows.length} of ${vt} visible boards loaded — more available`
            : `${rows.length} loaded — more boards available`)
          : `${rows.length} loaded — more boards in this group`;
      } else {
        hint.textContent = (isAllDevicesView && vt != null)
          ? `${rows.length} of ${vt} visible boards loaded`
          : "";
      }
    }
  };
  /** Targets for ``/alerts`` when grantee: only devices with ``can_operate``; owners use full group list. */
  const sirenTargetIds = () => {
    if (can("can_alert")) return ids;
    return ids.filter((id) => {
      const d = rows.find((x) => String(x.device_id || "") === id);
      return !!(d && d.can_operate);
    });
  };
  /** Match server: role ``can_alert`` OR ACL ``can_operate`` on ≥1 device in this group (partial shares OK). */
  const canGroupSiren = can("can_alert") || sirenTargetIds().length > 0;
  const isSharedGroup = rows.some((d) => !!d.is_shared);
  const uniqueOwnerFromRows = () => {
    const s = new Set(rows.map((d) => String(d.owner_admin || "").trim()).filter(Boolean));
    if (s.size === 1) return [...s][0];
    return "";
  };
  const effectiveOwnerForGroupShareApi = () => String(tenantOwner || "").trim() || uniqueOwnerFromRows();
  const meName = String((state.me && state.me.username) || "").trim();
  const foreignOwnerGroup = !!(tenantOwner && meName && tenantOwner !== meName);
  const canOpenGroupShare = !isSharedGroup && !foreignOwnerGroup && state.me && (
    (state.me.role === "admin" && can("can_manage_users"))
    || (state.me.role === "superadmin" && !!effectiveOwnerForGroupShareApi())
  );
  const sharedByList = [...new Set(rows.filter((d) => d && d.is_shared).map((d) => String(d.shared_by || "").trim()).filter(Boolean))];
  const sharedByLine = sharedByList.length ? escapeHtml(sharedByList.join(", ")) : escapeHtml(tenantOwner || "");
  const shareCtxLine = buildGroupSharingContextLineHtml({
    isSharedGroup,
    canOpenGroupShare,
    sharedByLine,
    escapeHtml,
  });
  const postDeleteHub =
    state.me && (state.me.role === "superadmin" || (state.me.role === "admin" && can("can_manage_users")));
  const presence = isGroupsHubDirectoryView
    ? { total: 0, online: 0, offline: 0 }
    : isAllDevicesView
      ? groupPagePresenceCounts(null, rows)
      : groupPagePresenceCounts(findGroupPageOverviewSlot(ov, g, tenantOwner, groupSlotCtx), rows);
  const online = presence.online;
  const offline = presence.offline;
  const hubDevices = isGroupsHubDirectoryView ? (hubCardsList.items || []) : [];
  const hubHasOwnDeviceForShare = hubDevices.some((d) => d && !d.is_shared);
  const hubShowShareChrome = !!(isGroupsHubDirectoryView && state.me && (
    state.me.role === "superadmin"
    || (state.me.role === "admin" && can("can_manage_users") && hubHasOwnDeviceForShare)
  ));
  const hubShowGranteeLink = !!(isGroupsHubDirectoryView && state.me && state.me.role === "user" && hubDevices.some((d) => d && d.is_shared));
  const hubCardsSectionHtml = isGroupsHubDirectoryView
    ? buildGroupsHubCardsSectionHtml({
      me: state.me,
      ov: hubCardsOv,
      list: hubCardsList,
      showGranteeGroupsHubLink: hubShowGranteeLink,
      showShareChrome: hubShowShareChrome,
    })
    : "";
  const hubModalsHtml = isGroupsHubDirectoryView
    ? buildGroupsHubCardsModalsHtml()
    : ((!isAllDevicesView && useGroupModeB) ? buildGroupDetailEditModalHtml() : "");
  setCrumb(isGroupsHubDirectoryView ? "Groups" : (isAllDevicesView ? "All boards" : `Group · ${gm.display_name || g}`));
  if (useGroupModeB) {
    const inferOwnerForSlot = (gk) => overviewInferUniqueOwnerForGroup(rows, gk, canonicalGroupKey);
    const slotsFromApi = (!isGroupsHubDirectoryView && ov) ? buildOverviewSlotsFromApiResponse(ov, groupSlotCtx) : null;
    if (slotsFromApi && slotsFromApi.length) {
      groupSidebarSlots = coalesceOverviewGroupSlots(
        enrichOverviewSlotsForDisplay(slotsFromApi, rows, groupSlotCtx, inferOwnerForSlot),
        { me: state.principal },
      );
    }
    normalizeModeBSidebarSlots({ total: presence.total, online, offline });
    const actionButtonsHtml = (isAllDevicesView || isGroupsHubDirectoryView) ? "" : `
        <button class="btn sm danger grp-action-btn" id="grpAlarmOn" ${canGroupSiren ? "" : "disabled"} title="${canGroupSiren ? "" : "Need Alert access or Operate on at least one board in this group"}">Alarm ON</button>
        <button class="btn sm secondary grp-action-btn" id="grpAlarmOff" ${canGroupSiren ? "" : "disabled"} title="${canGroupSiren ? "" : "Need Alert access or Operate on at least one board in this group"}">Alarm OFF</button>
        <button type="button" class="btn ghost grp-action-btn" id="grpEditGroup">Edit Group</button>
        <button type="button" class="btn ghost grp-action-btn" id="grpAddDevices">Add boards</button>
        ${canOpenGroupShare ? `<button class="btn ghost grp-action-btn" id="grpOpenGroupShare" type="button">Add Members</button>` : ""}
        <button class="btn danger grp-action-btn" id="grpDelete" ${isSharedGroup ? "disabled title=\"Shared group cannot be deleted\"" : ""}>Delete Group</button>`;
    mountView(view, buildGroupShellHtml({
      title: isAllDevicesView ? "All boards" : (isGroupsHubDirectoryView ? "Groups" : (gm.display_name || g)),
      isAllDevicesView,
      isHubDirectoryView: isGroupsHubDirectoryView,
      allDevicesBulkToolbarHtml: isAllDevicesView ? buildGroupAllDevicesBulkToolbarHtml() : "",
      total: presence.total,
      online,
      offline,
      ownerLine: isAllDevicesView ? "—" : (gm.owner_name || "—"),
      shareCtxLine: isAllDevicesView ? "" : shareCtxLine,
      legacyFilterBanner: isAllDevicesView ? "" : groupPageLegacyFilterBannerHtml(list),
      fleetTruncBanner: isAllDevicesView ? fleetPollTruncatedHtml(list) : groupPageFleetTruncatedHtml(list, ov, g, tenantOwner, groupSlotCtx, "grpFleetTruncBanner"),
      actionButtonsHtml,
      sidebarListHtml: buildGroupSidebarListHtml(groupSidebarSlots, sidebarListCtx()),
      allDevicesSidebarHtml: buildAllDevicesSidebarCardHtml(allDevicesSidebarCtx()),
      hubCardsSectionHtml,
      hubModalsHtml,
    }));
    syncModeBShareUi();
    void loadModeBHubAndMerge();
  } else {
    mountView(view, `
    <section class="card">
      <div class="row" style="align-items:flex-start;justify-content:space-between;gap:12px;flex-wrap:wrap">
        <h2 style="margin:0;flex:1;min-width:0">${escapeHtml(gm.display_name || g)}</h2>
        <div class="nav-back-cluster">
          <button type="button" class="btn ghost btn-tap" data-cc-go="overview" title="Home">← Home</button>
          <button type="button" class="btn ghost btn-tap" data-cc-go="groups" title="Groups directory (sharing)">← Groups hub</button>
        </div>
      </div>
      ${shareCtxLine}
      ${groupPageLegacyFilterBannerHtml(list)}
      ${groupPageFleetTruncatedHtml(list, ov, g, tenantOwner, groupSlotCtx, "grpFleetTruncBanner")}
      <div class="divider"></div>
      <div class="row" style="gap:6px;flex-wrap:wrap">
        <span class="badge neutral">total <span id="grpTotal">${presence.total}</span></span>
        <span class="badge online">online <span id="grpOnline">${online}</span></span>
        <span class="badge offline">offline <span id="grpOffline">${offline}</span></span>
        <span class="chip">${escapeHtml(g)}</span>
      </div>
      <p class="muted" style="margin-top:8px">Owner: ${escapeHtml(gm.owner_name || "—")} · ${escapeHtml(gm.phone || "—")} · ${escapeHtml(gm.email || "—")}</p>
      <div class="row" style="margin-top:8px;gap:8px;justify-content:flex-end;flex-wrap:wrap">
        <button class="btn sm danger" id="grpAlarmOn" ${canGroupSiren ? "" : "disabled"} title="${canGroupSiren ? "" : "Need Alert access or Operate on at least one board in this group"}">Alarm ON</button>
        <button class="btn sm secondary" id="grpAlarmOff" ${canGroupSiren ? "" : "disabled"} title="${canGroupSiren ? "" : "Need Alert access or Operate on at least one board in this group"}">Alarm OFF</button>
        ${canOpenGroupShare ? `<button class="btn sm" id="grpOpenGroupShare" type="button">Share group</button>` : ""}
      </div>
    </section>
    <section class="card">
      <h3 style="margin:0">Boards</h3>
      <div class="divider"></div>
      <div class="device-grid" id="groupPageDevices"></div>
      <div id="grpFleetLoadMoreWrap" style="margin-top:12px;display:none">
        <button class="btn secondary btn-tap" id="grpFleetLoadMore" type="button">Load more boards</button>
        <p class="muted" id="grpFleetLoadMoreHint" style="margin:8px 0 0"></p>
      </div>
    </section>
    <details class="card danger-zone device-drawer">
      <summary class="device-drawer__summary">
        <span class="device-drawer__title">Danger zone</span>
        <span class="device-drawer__hint muted">Delete group · expand</span>
      </summary>
      <div class="device-drawer__body">
        <p class="muted" style="margin:0 0 10px">Delete group will clear the group assignment from all boards in this group.</p>
        <div class="row" style="justify-content:flex-end">
          <button class="btn danger btn-tap" id="grpDelete" ${isSharedGroup ? "disabled title=\"Shared group cannot be deleted\"" : ""}>Delete group</button>
        </div>
      </div>
    </details>
  `);
  }
  const devGrid = $("#groupPageDevices", view);
  let allDevicesPanel = null;
  let renderGroupDevices = () => {};
  let disposeAllDevicesPanel = () => {};
  if (isAllDevicesView) {
    try { disposeAllDevicesPanel(); } catch (_) {}
    allDevicesPanel = createGroupAllDevicesFleetPanel({
      view,
      routeSeq,
      $,
      setChildMarkup,
      mountDeviceGridVirtualList,
      virtualMin: DEVICES_GRID_VIRTUAL_MIN,
      api,
      toast,
      escapeHtml,
      fmtRel,
      canonicalGroupKey,
      isRouteCurrent,
      bustDeviceListCaches,
      buildPostDevicesBulkProfilePath,
      buildDevicesFirmwareHintsPath,
      DEVICES_FIRMWARE_HINTS_TIMEOUT_MS,
      pickDevicesPollPath,
      FLEET_DEVICES_INITIAL_PAGES,
      debounceDevicesSearch,
      devicesSearchQueryReady,
      devicesSearchTooShort,
      devicesServerSearchActive,
      syncDevicesSearchMinLenBanner,
      syncDevicesServerSearchBanner,
      fetchFleetDevicesList,
      fetchFleetDevicesPage,
      devicePresenceStatus,
      devicePresenceBadgeText,
      getMe: () => state.me,
      onFleetPageChange: ({ items, nextCursor, hasMore }) => {
        rows = items || [];
        ids = rows.map((d) => String(d.device_id || "")).filter(Boolean);
        groupFleetNextCursor = nextCursor;
        groupFleetHasMore = hasMore;
        syncGroupPageLoadMoreUi();
        syncGroupPagePresenceBadges();
        paintGroupSidebarChrome({ total: rows.length, online: groupPagePresenceCounts(null, rows).online });
      },
    });
    allDevicesPanel.wire();
    allDevicesPanel.hydrate({
      items: rows,
      page: list.page,
      hasMore: groupFleetHasMore,
      nextCursor: groupFleetNextCursor,
    });
    renderGroupDevices = () => allDevicesPanel.render();
    disposeAllDevicesPanel = () => allDevicesPanel.dispose();
    renderGroupDevices();
    void allDevicesPanel.loadFirmwareHints();
  } else if (!isGroupsHubDirectoryView) {
    const groupDeviceGrid = createGroupPageDeviceGrid({
      devGrid,
      view,
      getRows: () => rows,
      deviceReachable,
      mountDeviceGridVirtualList,
      virtualMin: DEVICES_GRID_VIRTUAL_MIN,
      virtualRowPx: GROUP_PAGE_GRID_VIRTUAL_ROW_PX,
      setChildMarkup,
      $,
      buildCardHtml: (d) => buildGroupPageDeviceCardHtml(d, {
        escapeHtml,
        devicePresenceStatus,
        devicePresenceBadgeText,
        crocFmtHwModelPrimary,
        BRAND_HARDWARE_MANUFACTURER,
      }),
    });
    renderGroupDevices = () => groupDeviceGrid.render();
    renderGroupDevices();
  }
  syncGroupPageLoadMoreUi();
  if (useGroupModeB) {
    try { disposeCreateGroupModal(); } catch (_) {}
    const shellRoot = view.querySelector(".grp-mode-b-page") || view;
    disposeCreateGroupModal = wireCreateGroupModal(shellRoot, {
      api,
      toast,
      escapeHtml,
      canonicalGroupKey,
      fetchFleetDevicesList,
      buildPatchDeviceProfilePath,
      isRouteCurrent,
      routeSeq,
    });
  }
  let disposeHubCardsSurface = () => {};
  if (isGroupsHubDirectoryView) {
    try { disposeHubCardsSurface(); } catch (_) {}
    const hubWire = wireGroupsHubCardsSurface(view, {
      routeSeq,
      api,
      toast,
      can,
      $,
      $$,
      setChildMarkup,
      isRouteCurrent,
      scheduleRouteTicker,
      bustDeviceListCaches,
      bustApiGetCachedPrefix,
      canonicalGroupKey,
      displayGroupName,
      groupCardMetaKey,
      parseGroupMetaKey,
      syncGroupMetaWithDevices,
      tryContractApiWithProbe,
      tryGroupDeleteWithProbe,
      fetchOverviewGroupSettingsList,
      putGroupCardSettingsV1,
      applyGroupCardSettingsV1,
      deleteGroupCardV1,
      buildPostGroupCardsApplyPath,
      buildPatchDeviceProfilePath,
      buildPostDevicesBulkProfilePath,
      buildPostAdminNotificationGroupShareLabelPath,
      buildPostAlertsPath,
      buildBulkAlertBody,
      buildPostDeviceSelfTestPath,
      buildPostDeviceCommandsPath,
      runGroupApplyOnAction,
      runGroupDeleteAction,
      buildAuthUsersPath,
      ADMIN_CONSOLE_TIMEOUT_MS,
      buildAdminNotificationGroupSharePreviewPath,
      HOME_CONSOLE_TIMEOUT_MS,
      buildAdminDeviceShareRevokePath,
      fetchGroupShareGrantees,
      granteePermissionSummary,
      syncGroupShareGrantees,
      fetchAllAdminShares,
      grantShareMatrix,
      mountDeviceGridVirtualList,
      DEVICES_GRID_VIRTUAL_MIN,
      OVERVIEW_GROUP_CARD_VIRTUAL_ROW_PX,
      DEFAULT_REMOTE_SIREN_MS,
      getMe: () => state.me,
      getState: () => state,
      groupScope,
      initialOv: hubCardsOv,
      initialList: hubCardsList,
      initialGroupSettingsItems: hubCardsSettingsItems,
      showShareChrome: hubShowShareChrome,
      buildGroupSlotsFromDeviceList,
      groupTriggerPayloadFromSettings,
    });
    disposeHubCardsSurface = hubWire.dispose;
  }
  if (useGroupModeB && !isAllDevicesView && !isGroupsHubDirectoryView) {
    try { disposeGroupDetailTabs(); } catch (_) {}
    const shellRoot = view.querySelector(".grp-mode-b-page") || view;
    disposeGroupDetailTabs = wireGroupDetailTabs(shellRoot, {
      initialPanel: "overview",
    });
    renderGroupDevices();
    void initGroupDetailMap();
    void refreshGroupDetailFeed();
  }
  const tryContractApiCall = (contractPath, opts) =>
    tryContractApiWithProbe(api, contractPath, opts, {});
  const tryDeleteNotificationGroupPost = (gk, oa) =>
    tryGroupDeleteWithProbe(tryContractApiCall, "post", gk, oa);
  const tryDeleteNotificationGroupHttp = (gk, oa) =>
    tryGroupDeleteWithProbe(tryContractApiCall, "delete", gk, oa);
  if (useGroupModeB && !isAllDevicesView && !isGroupsHubDirectoryView) {
    const mountRoot = (typeof window !== "undefined" && window.__ccPageContentSlot)
      || view.querySelector("#crocCcPageContent")
      || view;
    const grpModalEl = $("#grpModal", mountRoot) || $("#grpModal", view) || document.getElementById("grpModal");
    const grpSetModalEl = $("#grpSetModal", mountRoot) || $("#grpSetModal", view) || document.getElementById("grpSetModal");
    const editBtn = $("#grpEditGroup", mountRoot) || $("#grpEditGroup", view);
    const addDevBtn = $("#grpAddDevices", mountRoot) || $("#grpAddDevices", view);
    if (grpModalEl && grpSetModalEl && (editBtn || addDevBtn)) {
      let editDevices = Array.isArray(rows) ? rows.slice() : [];
      let editById = new Map(editDevices.map((d) => [String(d.device_id), d]));
      let groupModals = null;
      const saveGroupMetaFn = (obj) => {
        try { localStorage.setItem(GROUP_META_LS_KEY, JSON.stringify(obj || {})); } catch (_) {}
      };
      const loadLocalGroupSettings = () => {
        try {
          const raw = localStorage.getItem(GROUP_SETTINGS_LS_KEY);
          const obj = raw ? JSON.parse(raw) : {};
          return (obj && typeof obj === "object") ? obj : {};
        } catch { return {}; }
      };
      const saveLocalGroupSettings = (obj) => {
        try { localStorage.setItem(GROUP_SETTINGS_LS_KEY, JSON.stringify(obj || {})); } catch (_) {}
      };
      const groupSettingsMap = new Map();
      for (const [k, v] of Object.entries(gsMap || {})) groupSettingsMap.set(String(k), v || {});
      const modalApiCaps = {
        settings: true,
        apply: groupApiCaps.apply !== false,
        delete: groupApiCaps.delete !== false,
        prefix: "",
      };
      const saveModalApiCaps = (caps) => {
        if (!caps) return;
        groupApiCaps.apply = !!caps.apply;
        groupApiCaps.delete = !!caps.delete;
        saveGroupApiCaps(groupApiCaps);
        modalApiCaps.settings = caps.settings !== false;
        modalApiCaps.apply = !!caps.apply;
        modalApiCaps.delete = !!caps.delete;
        if (caps.prefix === "/api" || caps.prefix === "") modalApiCaps.prefix = caps.prefix;
      };
      const detailSlotCtx = { me: state.me, canonicalGroupKey };
      const groupHtmlKeys = { canonicalGroupKey, displayGroupName, escapeHtml };
      const openEditModalFallback = () => {
        // Last-resort open if controller failed — still better than a dead button.
        try {
          const m = meta[metaKey] || meta[g] || {};
          const keyEl = $("#gmKey", view) || document.getElementById("gmKey");
          const nameEl = $("#gmName", view) || document.getElementById("gmName");
          if (keyEl) keyEl.value = canonicalGroupKey(g) || "";
          if (nameEl) nameEl.value = m.display_name || gm.display_name || g || "";
          grpModalEl.hidden = false;
          grpModalEl.removeAttribute("aria-hidden");
          grpModalEl.classList.add("is-open");
          grpModalEl.style.display = "flex";
        } catch (e) {
          toast((e && e.message) || "Could not open Edit Group", "err");
        }
      };
      try {
        groupModals = createOverviewGroupModalsController({
          view,
          grpModalEl,
          grpSetModalEl,
          api,
          getMe: () => state.me,
          canFn: can,
          toast,
          escapeHtml,
          $,
          $$,
          setChildMarkup,
          getDevices: () => editDevices,
          devicesForGroups: () => editDevices,
          inferUniqueOwnerForGroup: (gk) => overviewInferUniqueOwnerForGroup(editDevices, gk, canonicalGroupKey),
          getById: () => editById,
          getMeta: () => meta,
          saveGroupMeta: saveGroupMetaFn,
          loadLocalGroupSettings,
          saveLocalGroupSettings,
          getGroupSettingsMap: () => groupSettingsMap,
          groupApiCaps: modalApiCaps,
          saveGroupApiCaps: saveModalApiCaps,
          groupDelayTimers: window.__groupDelayTimers,
          groupCardMetaKey,
          canonicalGroupKey,
          displayGroupName,
          parseGroupMetaKey,
          groupDeviceRow: (d, opts) => buildOverviewGroupDeviceRowHtml(d, opts, groupHtmlKeys),
          groupDeviceIdsFromList: (gk, oa) => overviewGroupDeviceIdsFromList(editDevices, gk, oa, detailSlotCtx),
          groupDeviceIdsFromSlot: (slot) => overviewGroupDeviceIdsFromSlot(slot, editDevices, editById, detailSlotCtx),
          groupSharedBySlot: (slot) => overviewGroupSharedBySlot(slot, editDevices, editById, detailSlotCtx),
          groupSharedByNotificationKey: (gk) => overviewGroupSharedByNotificationKey(editDevices, gk, canonicalGroupKey),
          sirenTargetIdsForSlot: (slot) => overviewSirenTargetIdsForSlot(slot, editDevices, editById, can("can_alert"), detailSlotCtx),
          canGroupSirenForSlot: (slot) => overviewCanGroupSirenForSlot(slot, editDevices, editById, can, detailSlotCtx),
          tryContractApiCall,
          tryGroupDeleteContractCall: (mode, gk, oa) => tryGroupDeleteWithProbe(tryContractApiCall, mode, gk, oa),
          putGroupCardSettingsV1,
          applyGroupCardSettingsV1,
          deleteGroupCardV1,
          buildPostGroupCardsApplyPath,
          buildPatchDeviceProfilePath,
          buildPostDevicesBulkProfilePath,
          buildPostAdminNotificationGroupShareLabelPath,
          buildPostAlertsPath,
          buildBulkAlertBody,
          buildPostDeviceSelfTestPath,
          buildPostDeviceCommandsPath,
          runGroupApplyOnAction,
          runGroupDeleteAction,
          bustDeviceListCaches,
          onGroupsChanged: () => {
            const hash = location.hash;
            location.hash = "#/groups";
            queueMicrotask(() => { location.hash = hash; });
          },
        });
        try { groupModals.wireHandlers(); } catch (wireErr) {
          console.warn("[grp-edit] wireHandlers", wireErr);
        }
      } catch (ctrlErr) {
        console.warn("[grp-edit] controller", ctrlErr);
        toast("Edit Group setup failed — using basic editor", "warn");
      }
      const openEditModal = async (focusDevices) => {
        // Open immediately so the click never feels dead; refresh picker after fleet load.
        try {
          if (groupModals && typeof groupModals.openGroupModal === "function") {
            groupModals.openGroupModal(metaKey);
          } else {
            openEditModalFallback();
          }
        } catch (e) {
          console.warn("[grp-edit] open", e);
          openEditModalFallback();
        }
        // Add Devices: retitle + jump to the tick list (add / untick to remove).
        try {
          const titleEl = view.querySelector(".grp-modal__title") || document.querySelector("#grpModal .grp-modal__title");
          const ledeEl = view.querySelector(".grp-modal__lede") || document.querySelector("#grpModal .grp-modal__lede");
          if (focusDevices) {
            if (titleEl) titleEl.textContent = "Add / remove boards";
            if (ledeEl) ledeEl.textContent = "Tick to add a board to this group; untick to remove. Save applies membership.";
          } else {
            if (titleEl) titleEl.textContent = "Edit group";
            if (ledeEl) ledeEl.textContent = "Name, contacts, then tick boards. Save updates membership for checked rows.";
          }
        } catch (_) {}
        if (focusDevices) {
          const pick = $("#gmDevices", view) || document.getElementById("gmDevices");
          if (pick) {
            try { pick.scrollIntoView({ block: "nearest", behavior: "smooth" }); } catch (_) {}
          }
        }
        try {
          // KILL-MERGE: one page sample for edit membership; Load more elsewhere for full fleet.
          const res = await fetchFleetDevicesList({ maxPages: FLEET_DEVICES_INITIAL_PAGES });
          if (!isRouteCurrent(routeSeq)) return;
          editDevices = (res && res.items) || editDevices;
          editById = new Map(editDevices.map((d) => [String(d.device_id), d]));
          syncGroupMetaWithDevices(meta, editDevices);
          saveGroupMetaFn(meta);
          if (groupModals && typeof groupModals.openGroupModal === "function") {
            groupModals.openGroupModal(metaKey);
          }
          try {
            const titleEl = view.querySelector(".grp-modal__title") || document.querySelector("#grpModal .grp-modal__title");
            const ledeEl = view.querySelector(".grp-modal__lede") || document.querySelector("#grpModal .grp-modal__lede");
            if (focusDevices) {
              if (titleEl) titleEl.textContent = "Add / remove boards";
              if (ledeEl) ledeEl.textContent = "Tick to add a board to this group; untick to remove. Save applies membership.";
            }
          } catch (_) {}
        } catch (e) {
          if (!isRouteCurrent(routeSeq)) return;
          toast((e && e.message) || "Failed to refresh boards for editor", "warn");
        }
      };
      const onGrpActionClick = (ev) => {
        const raw = ev.target;
        const el = raw && raw.nodeType === 3 ? raw.parentElement : raw;
        const t = el && el.closest ? el.closest("#grpEditGroup, #grpAddDevices") : null;
        if (!t) return;
        ev.preventDefault();
        void openEditModal(t.id === "grpAddDevices");
      };
      try {
        if (window.__crocGrpEditAbort) window.__crocGrpEditAbort.abort();
      } catch (_) {}
      window.__crocGrpEditAbort = typeof AbortController !== "undefined" ? new AbortController() : null;
      document.addEventListener(
        "click",
        onGrpActionClick,
        window.__crocGrpEditAbort
          ? { capture: true, signal: window.__crocGrpEditAbort.signal }
          : true,
      );
      if (editBtn) {
        editBtn.onclick = (ev) => {
          ev.preventDefault();
          void openEditModal(false);
        };
        editBtn.setAttribute("data-croc-edit-wired", "1");
      }
      if (addDevBtn) {
        addDevBtn.onclick = (ev) => {
          ev.preventDefault();
          void openEditModal(true);
        };
        addDevBtn.setAttribute("data-croc-edit-wired", "1");
      }
      try {
        window.__crocOpenGroupEdit = (focusDevices) => { void openEditModal(!!focusDevices); };
      } catch (_) {}
    } else {
      console.warn("[grp-edit] missing modal or buttons", {
        modal: !!grpModalEl, setModal: !!grpSetModalEl, edit: !!editBtn, add: !!addDevBtn,
      });
    }
  }
  const deleteGroupCardCompat = async (groupKey, ownerO) => runGroupDeleteAction({
    groupKey,
    ownerAdmin: ownerO,
    apiCaps: groupApiCaps,
    saveApiCaps: saveGroupApiCaps,
    tryDeleteV1: (gk, oa) => deleteGroupCardV1({
      group_key: gk,
      owner_admin: oa || undefined,
      timeoutMs: 20000,
    }),
    tryDeletePostRoute: tryDeleteNotificationGroupPost,
    tryDeleteRoute: tryDeleteNotificationGroupHttp,
  });
  const applyGroupSettingsFallbackCompat = async (_groupKey, _ownerO, payload) => {
    const durationMs = Number(payload.trigger_duration_ms || DEFAULT_REMOTE_SIREN_MS);
    const tkey = metaKey;
    const prev = window.__groupDelayTimers.get(tkey);
    if (prev) {
      clearTimeout(prev);
      window.__groupDelayTimers.delete(tkey);
    }
    const tids = sirenTargetIds();
    await api(buildPostAlertsPath(), {
      method: "POST",
      body: buildBulkAlertBody({
        action: "on",
        durationMs,
        notificationGroup: _groupKey,
        ownerAdmin: _ownerO,
        deviceIds: tids,
      }),
    });
    return { ok: true, fallback: true, device_count: tids.length };
  };
  const tryApplyGroupV1 = (gk, oa) => applyGroupCardSettingsV1({
    group_key: gk,
    owner_admin: oa || undefined,
    timeoutMs: 45000,
  });
  const tryApplyRouteCompat = (groupKey, ownerO) =>
    tryContractApiCall(buildPostGroupCardsApplyPath(groupKey, ownerO), { method: "POST" });
  const sendAlert = async (action) => {
    if (!canGroupSiren) {
      toast("No alarm permission — need Alert access or Operate on at least one board in this group", "err");
      return;
    }
    if (ids.length === 0) { toast("No boards in this group", "warn"); return; }
    const tids = sirenTargetIds();
    if (tids.length === 0) { toast("No boards you may operate in this group", "warn"); return; }
    const slotForAlert = findGroupPageOverviewSlot(ov, g, tenantOwner, groupSlotCtx);
    const serverTotal = slotForAlert && slotForAlert.serverCounts
      ? Number(slotForAlert.serverCounts.total || 0)
      : 0;
    const confirmLead = action === "on" ? "Open" : "Close";
    const confirmCount = serverTotal > 0 ? serverTotal : tids.length;
    const confirmBody = serverTotal > 0
      ? `${confirmLead} alarm for ${confirmCount} board(s) in ${g}?`
      : `${confirmLead} alarm for ${tids.length} board(s) in ${g}?`;
    if (!confirm(confirmBody)) return;
    if (action === "on") {
      const payload = groupTriggerPayloadFromSettings(gsMap[metaKey] || gsMap[g] || {});
      await runGroupApplyOnAction({
        groupKey: g,
        ownerAdmin: tenantOwner,
        payload,
        apiCaps: groupApiCaps,
        saveApiCaps: saveGroupApiCaps,
        tryApplyV1: tryApplyGroupV1,
        tryApplyRoute: tryApplyRouteCompat,
        applyFallback: applyGroupSettingsFallbackCompat,
      });
      toast(`Alarm ON · ${serverTotal > 0 ? serverTotal : tids.length}`, "ok");
      return;
    }
    const prev = window.__groupDelayTimers.get(metaKey);
    if (prev) {
      clearTimeout(prev);
      window.__groupDelayTimers.delete(metaKey);
    }
    const res = await api(buildPostAlertsPath(), {
      method: "POST",
      body: buildBulkAlertBody({
        action: "off",
        durationMs: DEFAULT_REMOTE_SIREN_MS,
        notificationGroup: g,
        ownerAdmin: tenantOwner,
        deviceIds: tids,
      }),
    });
    const n = Number(res && res.sent_count);
    if (n === 0 && tids.length) {
      toast("Alarm OFF: none sent. No boards resolved — try again shortly.", "warn");
      return;
    }
    toast(`Alarm OFF · ${n}`, "ok");
  };
  const alarmOnBtn = $("#grpAlarmOn", view);
  const alarmOffBtn = $("#grpAlarmOff", view);
  const delGroupBtn = $("#grpDelete", view);
  if (!isAllDevicesView) {
    if (alarmOnBtn) alarmOnBtn.addEventListener("click", () => sendAlert("on"));
    if (alarmOffBtn) alarmOffBtn.addEventListener("click", () => sendAlert("off"));
  }
  if (delGroupBtn && !isAllDevicesView) {
    delGroupBtn.addEventListener("click", async () => {
      if (isSharedGroup) { toast("Shared group cannot be deleted", "err"); return; }
      if (!confirm(`Delete group card "${g}"?`)) return;
      try {
        await deleteGroupCardCompat(g, tenantOwner);
        if (meta[metaKey]) delete meta[metaKey];
        else if (meta[g]) delete meta[g];
        localStorage.setItem(GROUP_META_LS_KEY, JSON.stringify(meta));
        try { bustDeviceListCaches(); } catch (_) {}
        toast("Group deleted", "ok");
        location.hash = postDeleteHub ? "#/groups" : defaultHomeHash();
      } catch (e) {
        toast(e.message || e, "err");
      }
    });
  }

  const groupDisplayForShareApi = () => String(
    (rows[0] && rows[0].notification_group) || gm.display_name || displayFromUrl || g,
  ).trim() || g;
  const groupShareChrome = isAllDevicesView ? { wireOpenButton: () => {} } : createGroupPageShareChrome({
    view,
    routeSeq,
    api,
    getMe: () => state.me,
    toast,
    escapeHtml,
    $,
    $$,
    setChildMarkup,
    isRouteCurrent,
    canOpenGroupShare: () => canOpenGroupShare,
    groupDisplayForShareApi,
    effectiveOwnerForGroupShareApi,
    buildAuthUsersPath,
    ADMIN_CONSOLE_TIMEOUT_MS,
    buildAdminNotificationGroupSharePreviewPath,
    HOME_CONSOLE_TIMEOUT_MS,
    fetchGroupShareGrantees,
    granteePermissionSummary,
    syncGroupShareGrantees,
    bustDeviceListCaches,
  });
  groupShareChrome.wireOpenButton();

  const loadMoreBtn = $("#grpFleetLoadMore", view);
  if (loadMoreBtn) {
    loadMoreBtn.addEventListener("click", async () => {
      if (!groupFleetHasMore || !groupFleetNextCursor) return;
      loadMoreBtn.disabled = true;
      try {
        if (isAllDevicesView && allDevicesPanel) {
          await allDevicesPanel.reload({ append: true });
          return;
        }
        const devPath = pickDevicesPollPath();
        const pg = await appendGroupPageFleetPage({
          groupKey: g,
          ownerAdmin: tenantOwner || undefined,
          cursor: groupFleetNextCursor,
          live: devPath.includes("skip_cache=1"),
          timeoutMs: 20000,
          retries: 1,
          ctx: fleetLoadCtx,
        });
        if (!isRouteCurrent(routeSeq)) return;
        const seen = new Set(rows.map((d) => String(d.device_id || "")));
        const extra = (pg.items || []).filter((d) => {
          const id = String(d.device_id || "");
          return id && !seen.has(id);
        });
        rows = rows.concat(extra);
        ids = rows.map((d) => String(d.device_id || "")).filter(Boolean);
        list = { ...list, items: rows, page: pg.page };
        groupFleetPagesLoaded += 1;
        const cur = groupPageFleetCursorState(list, pg.page);
        groupFleetNextCursor = cur.nextCursor;
        groupFleetHasMore = cur.hasMore;
        syncGroupPageFleetTruncBanner(view, list, ov, g, tenantOwner, groupSlotCtx, "grpFleetTruncBanner");
        syncGroupPagePresenceBadges();
        renderGroupDevices();
        syncGroupPageLoadMoreUi();
      } catch (e) {
        toast(String(e && e.message) || "Failed to load more boards", "err");
      } finally {
        if (isRouteCurrent(routeSeq)) loadMoreBtn.disabled = false;
      }
    });
  }

  const refreshGroupLive = async () => {
    if (!isRouteCurrent(routeSeq)) return;
    const devPath = pickDevicesPollPath();
    const searchQ = isAllDevicesView && allDevicesPanel ? allDevicesPanel.getSearchQ() : "";
    const [latestRes, ovLiveRes] = await Promise.allSettled([
      loadGroupFleet({
        live: devPath.includes("skip_cache=1"),
        timeoutMs: 16000,
        retries: 0,
        maxPages: isAllDevicesView
          ? Math.max(FLEET_DEVICES_INITIAL_PAGES, groupFleetPagesLoaded)
          : Math.max(GROUP_PAGE_DEVICES_INITIAL_PAGES, groupFleetPagesLoaded),
        searchQ,
      }),
      api(pickOverviewPollPath(), { timeoutMs: 12000, retries: 0 }),
    ]);
    if (!isRouteCurrent(routeSeq)) return;
    list = (latestRes.status === "fulfilled" && latestRes.value) ? latestRes.value : { items: [] };
    if (ovLiveRes.status === "fulfilled" && ovLiveRes.value && typeof ovLiveRes.value === "object") {
      ov = ovLiveRes.value;
    }
    syncGroupPageFleetTruncBanner(view, list, ov, g, tenantOwner, groupSlotCtx, "grpFleetTruncBanner");
    syncGroupMetaWithDevices(meta, list.items || []);
    try { localStorage.setItem(GROUP_META_LS_KEY, JSON.stringify(meta)); } catch (_) {}
    rows = list.items || [];
    ids = rows.map((d) => String(d.device_id || "")).filter(Boolean);
    groupFleetPagesLoaded = list.pages_fetched != null ? Number(list.pages_fetched) : groupFleetPagesLoaded;
    const cur = groupPageFleetCursorState(list, list.page);
    groupFleetNextCursor = cur.nextCursor;
    groupFleetHasMore = cur.hasMore;
    syncGroupPagePresenceBadges();
    if (isAllDevicesView && allDevicesPanel) {
      allDevicesPanel.hydrate({
        items: rows,
        page: list.page,
        hasMore: groupFleetHasMore,
        nextCursor: groupFleetNextCursor,
      });
    } else {
      renderGroupDevices();
    }
    syncGroupPageLoadMoreUi();
    if (useGroupModeB) {
      const inferOwnerForSlot = (gk) => overviewInferUniqueOwnerForGroup(rows, gk, canonicalGroupKey);
      const slotsFromLive = ov ? buildOverviewSlotsFromApiResponse(ov, groupSlotCtx) : null;
      if (slotsFromLive && slotsFromLive.length) {
        groupSidebarSlots = coalesceOverviewGroupSlots(
          enrichOverviewSlotsForDisplay(slotsFromLive, rows, groupSlotCtx, inferOwnerForSlot),
          { me: state.principal },
        );
      }
      normalizeModeBSidebarSlots(isAllDevicesView
        ? { total: groupPagePresenceCounts(null, rows).total, online: groupPagePresenceCounts(null, rows).online, offline: groupPagePresenceCounts(null, rows).offline }
        : groupPagePresenceCounts(findGroupPageOverviewSlot(ov, g, tenantOwner, groupSlotCtx), rows));
      paintGroupSidebarChrome(isAllDevicesView
        ? { total: rows.length, online: groupPagePresenceCounts(null, rows).online }
        : undefined);
      void loadModeBHubAndMerge();
      if (!isAllDevicesView) {
        void initGroupDetailMap();
        void refreshGroupDetailFeed();
      }
    }
  };
  scheduleRouteTicker(routeSeq, `group-live-${g}`, refreshGroupLive, 8000);
});


// ── groups.route.js ─────────────────────────────────────
/**
 * Route: #/groups — Group share hub (P1+P2): group-first table, filters, Selected-devices revoke (P1);
 * custody/via from ``/group-cards/settings`` (P2), ``sa_staff`` read-only + NGS list gate (P2),
 * audit deep link (P2). Grantee read-only slice for role ``user``.
 */
registerRoute("groups", async (view, _args, routeSeq) => {
  // Mode B groups directory: same shell as #/group but hub key (not demo trap).
  const groupHandler = routes["group"];
  if (groupHandler) {
    await groupHandler(view, [GROUP_HUB_DIRECTORY_KEY], routeSeq);
  } else {
    mountView(view, `<div class="card"><p class="muted">Route not ready.</p></div>`);
  }
});


// ── incidents.route.js ─────────────────────────────────────
/**
 * Route: #/incidents — Incident list + #/incidents/:eventId workspace.
 */

function humanizeIncidentRowTitle(ev) {
  try {
    if (typeof humanizeAlarmTitle === "function") {
      return humanizeAlarmTitle(ev.summary || ev.event_type || "Alarm");
    }
  } catch (_) {}
  return String(ev.summary || ev.event_type || "Alarm");
}

function buildIncidentDispatchRowsHtml(items) {
  const dispatches = (Array.isArray(items) ? items : []).filter(
    (it) => String(it.kind || "") === "dispatch",
  );
  if (!dispatches.length) {
    return '<tr><td colspan="4" class="muted">No responders assigned on this alarm yet.</td></tr>';
  }
  return dispatches
    .map((it) => {
      const detail = it.detail || {};
      const did = String(detail.dispatch_id || detail.id || "").trim();
      const statusRaw = String(detail.status || it.summary || "—");
      const statusMap = {
        pending: "Pending",
        accepted: "Accepted",
        en_route: "En route",
        on_scene: "On scene",
        resolved: "Resolved",
        cancelled: "Cancelled",
      };
      const status = escapeHtml(statusMap[statusRaw.toLowerCase()] || statusRaw.replace(/_/g, " "));
      const responder = escapeHtml(
        String(detail.assigned_responder || detail.responder_username || detail.responder || "—"),
      );
      const at = escapeHtml(fmtRel(it.at) !== "—" ? fmtRel(it.at) : String(it.at || ""));
      const link = did
        ? `<button type="button" class="btn sm ghost btn-tap" data-cc-go="response?dispatch_id=${encodeURIComponent(did)}">Open assignment</button>`
        : `<button type="button" class="btn sm ghost btn-tap" data-cc-go="response">Open assignment</button>`;
      return `<tr>
        <td>${status}</td>
        <td>${responder}</td>
        <td>${at}</td>
        <td>${link}</td>
      </tr>`;
    })
    .join("");
}

function buildPirActionItemsHtml(items) {
  const rows = Array.isArray(items) ? items : [];
  if (!rows.length) {
    return `<div class="pir-action-items" id="pirActionItems">
      <p class="muted" style="margin-bottom:8px">No action items yet.</p>
    </div>`;
  }
  return `<div class="pir-action-items" id="pirActionItems">${rows.map((row, idx) => {
    const text = String((row && row.text) || "");
    return `<div class="inline-form pir-action-row" data-idx="${idx}" style="margin-bottom:6px">
      <label class="field wide"><span>Action ${idx + 1}</span>
        <input class="pir-action-text" value="${escapeHtml(text)}" placeholder="Follow-up action" />
      </label>
      <button class="btn sm secondary pir-action-remove" type="button" aria-label="Remove action">Remove</button>
    </div>`;
  }).join("")}</div>`;
}

function readPirActionItems(view) {
  const out = [];
  $$(".pir-action-text", view).forEach((el) => {
    const text = String(el.value || "").trim();
    if (text) out.push({ text });
  });
  return out;
}

function wirePirActionItems(view) {
  const addBtn = $("#pirAddActionBtn", view);
  const container = $("#pirActionItems", view);
  if (addBtn && container) {
    addBtn.addEventListener("click", () => {
      const idx = $$(".pir-action-row", container).length;
      const row = document.createElement("div");
      row.className = "inline-form pir-action-row";
      row.setAttribute("data-idx", String(idx));
      row.style.marginBottom = "6px";
      row.innerHTML = `<label class="field wide"><span>Action ${idx + 1}</span>
        <input class="pir-action-text" value="" placeholder="Follow-up action" />
      </label>
      <button class="btn sm secondary pir-action-remove" type="button" aria-label="Remove action">Remove</button>`;
      container.appendChild(row);
    });
  }
  if (container) {
    container.addEventListener("click", (ev) => {
      const btn = ev.target.closest(".pir-action-remove");
      if (!btn) return;
      const row = btn.closest(".pir-action-row");
      if (row) row.remove();
    });
  }
}

function buildSopSuggestionCardsHtml(recommendations) {
  const items = Array.isArray(recommendations && recommendations.items)
    ? recommendations.items
    : (Array.isArray(recommendations) ? recommendations : []);
  if (!items.length) {
    return `<p class="muted">No playbook suggestions for this event type.</p>`;
  }
  return items.map((rec) => {
    const sopId = String(rec.sop_id || rec.id || "").trim();
    const solution = String(rec.solution || rec.summary || rec.recommendation || "—");
    const eventType = humanizeAlarmTitle(rec.event_type || "");
    return `<div class="glass-panel glass-panel--inset sop-suggestion-card" data-sop-id="${escapeHtml(sopId)}" style="padding:10px 12px;margin-bottom:8px">
      <p><strong>${escapeHtml(solution)}</strong></p>
      ${eventType ? `<p class="muted" style="font-size:12px">${escapeHtml(eventType)}</p>` : ""}
      ${sopId ? `<div class="row" style="gap:6px;margin-top:8px">
        <button class="btn sm btn-tap sop-feedback-accept" type="button" data-id="${escapeHtml(sopId)}" data-ok="1">Accept</button>
        <button class="btn sm secondary btn-tap sop-feedback-reject" type="button" data-id="${escapeHtml(sopId)}" data-ok="0">Reject</button>
      </div>` : ""}
    </div>`;
  }).join("");
}

function newIdempotencyKey() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch (_) {}
  return `inc-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

registerRoute("incidents", async (view, args, routeSeq) => {
  const eventId = String(args[0] || "").trim();
  setCrumb(eventId ? "Incident" : "Incidents");

  if (eventId) {
    mountView(view, `<div class="card glass-panel glass-panel--panel"><p class="muted route-loading__label" id="incidentLoad">Loading incident…</p></div>`);
    const isAdmin = hasRole("admin");
    const sopOn = catalogEnabled(state.me, "sop_knowledge");
    try {
      const [timeline, proof, sla, review] = await Promise.all([
        api(`/incidents/${encodeURIComponent(eventId)}/timeline`),
        api(`/incidents/${encodeURIComponent(eventId)}/proof-of-response`),
        api(`/incidents/${encodeURIComponent(eventId)}/sla`),
        api(`/incidents/${encodeURIComponent(eventId)}/review`),
      ]);
      if (!isRouteCurrent(routeSeq)) return;
      const items = Array.isArray(timeline.items) ? timeline.items : [];
      const event = timeline.event || {};
      const closure = (proof && proof.closure) || {};
      const commander = (proof && proof.commander) || {};
      const verified = !!closure.verified;
      const clocks = Array.isArray(sla && sla.clocks) ? sla.clocks : [];
      const pirStatus = String((review && review.status) || "draft");
      const pirLessons = String((review && review.lessons_learned) || "");
      const pirRoot = String((review && review.root_cause) || "");
      const pirActions = Array.isArray(review && review.action_items) ? review.action_items : [];
      const snapshotObj = (proof && proof.snapshot) || timeline.snapshot || null;
      const eventType = String(event.event_type || event.summary || "").trim();
      let sopHtml = "";
      if (sopOn && eventType) {
        try {
          const sopRec = await api(buildGetSopRecommendationsPath({ event_type: eventType }), {
            timeoutMs: PLATFORM_CONTROL_TIMEOUT_MS,
          });
          if (!isRouteCurrent(routeSeq)) return;
          sopHtml = `<h3 style="margin-top:16px">Suggested playbooks</h3>
            <p class="muted">Advisory steps for this kind of alarm</p>
            <div id="sopSuggestCards">${buildSopSuggestionCardsHtml(sopRec)}</div>
            <p id="sopSuggestMsg" class="muted" aria-live="polite"></p>`;
        } catch (e) {
          sopHtml = `<h3 style="margin-top:16px">Suggested playbooks</h3><p class="muted">${escapeHtml(String(e.message || e))}</p>`;
        }
      }
      setCrumb(eventId ? "Incident" : "Incidents");
      // Deep-link SoT (never paint): #/overview #/orchestration
      const title = humanizeAlarmTitle(event.summary || event.event_type || "Alarm");
      const boardSay = typeof softDeviceLabel === "function"
        ? softDeviceLabel(event.display_label || event.device_label || event.friendly_name || event.device_id || "")
        : String(event.device_id || "—");
      const overallSla = humanizeProofReason((sla && sla.overall_status) || "") || "—";
      const proofReason = humanizeProofReason(closure.reason || (verified ? "verified" : "pending"));
      mountView(view, `
        <div class="card glass-panel glass-panel--panel incident-workspace">
          <div class="row between" style="align-items:flex-start;gap:12px;flex-wrap:wrap">
            <div>
              <h2 style="margin:0 0 6px">Incident</h2>
              <p class="muted" style="margin:0;max-width:42rem;line-height:1.45">${escapeHtml(title)}</p>
            </div>
            <button type="button" class="btn ghost btn-tap sm" data-cc-go="incidents">← All incidents</button>
          </div>
          ${typeof buildTonightStageStripHtml === "function" ? buildTonightStageStripHtml({ stage: "during", active: "cases", eventId }) : ""}
          <p class="muted" style="margin:10px 0 0">${escapeHtml(boardSay && boardSay !== "—" ? `Board · ${boardSay}` : "Board · —")}</p>
          <h3 style="margin-top:18px">Responder assignments</h3>
          <table class="table">
            <thead><tr><th>Status</th><th>Responder</th><th>When</th><th></th></tr></thead>
            <tbody>
              ${buildIncidentDispatchRowsHtml(items)}
            </tbody>
          </table>
          <p class="muted"><button type="button" class="btn sm ghost btn-tap" data-cc-go="response">Open assignment</button></p>
          <h3 style="margin-top:18px">Response timing</h3>
          <p class="muted">Overall: ${escapeHtml(overallSla)}</p>
          <table class="table">
            <thead><tr><th>Checkpoint</th><th>Status</th><th>Remaining</th></tr></thead>
            <tbody>
              ${clocks.map((c) => {
                const st = humanizeProofReason(c.status || "") || String(c.status || "");
                const rem = c.met
                  ? "—"
                  : (c.remaining_seconds != null ? softDurationSeconds(c.remaining_seconds) || "—" : "—");
                return `<tr>
                <td>${escapeHtml(String(c.label || c.id || ""))}</td>
                <td>${escapeHtml(st)}</td>
                <td>${escapeHtml(rem)}</td>
              </tr>`;
              }).join("") || "<tr><td colspan='3' class='muted'>No timing data yet.</td></tr>"}
            </tbody>
          </table>
          <h3 style="margin-top:18px">Proof of response</h3>
          <p class="${verified ? "badge ok" : "badge offline"}">${verified ? "Verified" : "Not verified"} — ${escapeHtml(proofReason)}</p>
          <p class="muted">Lead: ${escapeHtml(String(commander.username || commander.assigned_responder || "—"))}</p>
          ${buildSnapshotEvidenceHtml(snapshotObj)}
          <h3 style="margin-top:18px">ONCR cognition</h3>
          <div id="oncrUxPanel"><p class="muted">Loading observation / hypothesis view…</p></div>
          ${isAdmin ? `
          <div class="inline-form glass-panel glass-panel--inset" style="margin-top:12px;padding:12px 14px">
            <p class="muted" style="margin-bottom:8px">Lead actions are available to admins and are recorded in the audit trail.</p>
            <label class="field"><span>Assign lead</span>
              <input id="cmdAssignUser" placeholder="Responder username" />
            </label>
            <button class="btn sm" id="cmdAssignBtn" type="button">Assign</button>
            <label class="field"><span>Hand off to</span>
              <input id="cmdHandoffUser" placeholder="Username" />
            </label>
            <button class="btn sm secondary" id="cmdHandoffBtn" type="button">Hand off</button>
            <label class="field wide"><span>Force-close reason</span>
              <input id="cmdOverrideReason" placeholder="Why closing early (min 4 characters)" />
            </label>
            <button class="btn sm danger" id="cmdOverrideBtn" type="button">Force close</button>
          </div>
          <p id="cmdMsg" class="muted" aria-live="polite"></p>` : ""}
          <h3 style="margin-top:18px">After-action review</h3>
          <p class="muted">Status: ${escapeHtml(pirStatus)}${review && review.reviewed_by ? ` · ${escapeHtml(String(review.reviewed_by))}` : ""}</p>
          ${isAdmin ? `
          <label class="field wide"><span>Root cause</span>
            <textarea id="pirRoot" rows="2">${escapeHtml(pirRoot)}</textarea>
          </label>
          <label class="field wide"><span>Lessons learned</span>
            <textarea id="pirLessons" rows="3">${escapeHtml(pirLessons)}</textarea>
          </label>
          <div style="margin-top:8px">
            <strong>Follow-ups</strong>
            ${buildPirActionItemsHtml(pirActions)}
            <button class="btn sm secondary" id="pirAddActionBtn" type="button" style="margin-top:6px">Add follow-up</button>
          </div>
          <div class="row" style="margin-top:8px">
            <button class="btn sm secondary" id="pirSaveBtn" type="button">Save draft</button>
            <button class="btn sm" id="pirSubmitBtn" type="button">Submit review</button>
          </div>
          <p id="pirMsg" class="muted" aria-live="polite"></p>` : `<p class="muted">${pirLessons ? escapeHtml(pirLessons) : "No review submitted yet."}</p>`}
          ${sopHtml}
          <h3 style="margin-top:18px">Timeline</h3>
          ${buildIncidentTimelineCardsHtml(items, { snapshot: snapshotObj, eventId })}
        </div>`);

      // ONCR O9 — best-effort cognition panel (ai-inc id when present, else event id).
      (async () => {
        const panel = $("#oncrUxPanel", view);
        if (!panel || typeof oncrIncidentUxHtml !== "function") return;
        const aiItem = (Array.isArray(items) ? items : []).find(
          (it) => String(it.kind || "") === "ai_incident",
        );
        const oncrId =
          String((aiItem && aiItem.detail && (aiItem.detail.incident_id || aiItem.detail.id)) || "").trim() ||
          String(eventId || "").trim();
        const deviceId = String(event.device_id || "").trim();
        try {
          const qs = deviceId ? `?device_id=${encodeURIComponent(deviceId)}` : "";
          const oncr = await api(`/ai/oncr/incidents/${encodeURIComponent(oncrId)}${qs}`, {
            timeoutMs: 8000,
          });
          if (!isRouteCurrent(routeSeq)) return;
          panel.innerHTML = oncrIncidentUxHtml(oncr);
        } catch (e) {
          if (!isRouteCurrent(routeSeq)) return;
          panel.innerHTML = `<p class="muted">ONCR view unavailable: ${escapeHtml(String(e.message || e))}</p>`;
        }
      })();

      if (isAdmin) {
        wirePirActionItems(view);
        const msg = $("#cmdMsg", view);
        const postCmd = async (path, body) => {
          if (msg) msg.textContent = "Working…";
          try {
            await api(path, {
              method: "POST",
              body,
              headers: { "Idempotency-Key": newIdempotencyKey() },
            });
            if (!isRouteCurrent(routeSeq)) return;
            if (msg) msg.textContent = "Updated — reloading…";
            location.hash = `#/incidents/${encodeURIComponent(eventId)}`;
          } catch (e) {
            if (!isRouteCurrent(routeSeq)) return;
            if (msg) msg.textContent = String(e.message || e);
            toast(String(e.message || e), "err");
          }
        };
        const assignBtn = $("#cmdAssignBtn", view);
        const handoffBtn = $("#cmdHandoffBtn", view);
        const overrideBtn = $("#cmdOverrideBtn", view);
        if (assignBtn) {
          assignBtn.addEventListener("click", () => {
            const u = String(($("#cmdAssignUser", view) && $("#cmdAssignUser", view).value) || "").trim();
            if (!u) { toast("Enter responder username", "err"); return; }
            postCmd(`/incidents/${encodeURIComponent(eventId)}/command/assign`, {
              commander: u,
              responder_username: u,
            });
          });
        }
        if (handoffBtn) {
          handoffBtn.addEventListener("click", () => {
            const u = String(($("#cmdHandoffUser", view) && $("#cmdHandoffUser", view).value) || "").trim();
            if (!u) { toast("Enter handoff target", "err"); return; }
            postCmd(`/incidents/${encodeURIComponent(eventId)}/command/handoff`, { to: u });
          });
        }
        if (overrideBtn) {
          overrideBtn.addEventListener("click", () => {
            const reason = String(($("#cmdOverrideReason", view) && $("#cmdOverrideReason", view).value) || "").trim();
            if (reason.length < 4) { toast("Override reason required", "err"); return; }
            if (!confirm("Break-glass override close this incident trace?")) return;
            postCmd(`/incidents/${encodeURIComponent(eventId)}/command/override`, {
              reason,
              action: "force_close",
            });
          });
        }
        const pirMsg = $("#pirMsg", view);
        const savePir = async (status) => {
          const body = {
            status,
            root_cause: String(($("#pirRoot", view) && $("#pirRoot", view).value) || "").trim(),
            lessons_learned: String(($("#pirLessons", view) && $("#pirLessons", view).value) || "").trim(),
            action_items: readPirActionItems(view),
          };
          if (pirMsg) pirMsg.textContent = "Saving…";
          try {
            await api(`/incidents/${encodeURIComponent(eventId)}/review`, { method: "PUT", body });
            if (!isRouteCurrent(routeSeq)) return;
            if (pirMsg) pirMsg.textContent = status === "submitted" ? "Review submitted." : "Draft saved.";
            toast(status === "submitted" ? "After-action review submitted" : "Review draft saved", "ok");
          } catch (e) {
            if (!isRouteCurrent(routeSeq)) return;
            if (pirMsg) pirMsg.textContent = String(e.message || e);
            toast(String(e.message || e), "err");
          }
        };
        const pirSaveBtn = $("#pirSaveBtn", view);
        const pirSubmitBtn = $("#pirSubmitBtn", view);
        if (pirSaveBtn) pirSaveBtn.addEventListener("click", () => savePir("draft"));
        if (pirSubmitBtn) pirSubmitBtn.addEventListener("click", () => savePir("submitted"));

        const sopMsg = $("#sopSuggestMsg", view);
        const wireSopFeedback = async (btn, accepted) => {
          const id = String(btn.getAttribute("data-id") || "").trim();
          if (!id) return;
          btn.disabled = true;
          if (sopMsg) sopMsg.textContent = "Sending feedback…";
          try {
            await api(buildPostSopFeedbackPath(id), {
              method: "POST",
              body: { accepted: !!accepted },
              timeoutMs: PLATFORM_CONTROL_TIMEOUT_MS,
            });
            if (!isRouteCurrent(routeSeq)) return;
            if (sopMsg) sopMsg.textContent = accepted ? "Playbook accepted." : "Playbook rejected.";
            toast(accepted ? "Playbook accepted" : "Playbook rejected", "ok");
          } catch (e) {
            if (!isRouteCurrent(routeSeq)) return;
            if (sopMsg) sopMsg.textContent = String(e.message || e);
            toast(String(e.message || e), "err");
            btn.disabled = false;
          }
        };
        $$(".sop-feedback-accept", view).forEach((btn) => {
          btn.addEventListener("click", () => wireSopFeedback(btn, true));
        });
        $$(".sop-feedback-reject", view).forEach((btn) => {
          btn.addEventListener("click", () => wireSopFeedback(btn, false));
        });
      }
    } catch (e) {
      if (!isRouteCurrent(routeSeq)) return;
      mountView(view, `<div class="card"><h2>Incident not found</h2><p class="muted">${escapeHtml(String(e.message || e))}</p></div>`);
    }
    return;
  }

  const listParams = new URLSearchParams((location.hash.split("?")[1] || ""));
  let listQ = String(listParams.get("q") || "").trim();
  let listLimit = Math.min(200, Math.max(10, Number(listParams.get("limit") || 40) || 40));
  let listSinceId = String(listParams.get("since_id") || "").trim();

  const buildListQuery = () => {
    const qs = new URLSearchParams();
    qs.set("limit", String(listLimit));
    if (listQ) qs.set("q", listQ);
    if (listSinceId && /^\d+$/.test(listSinceId)) qs.set("since_id", listSinceId);
    return qs.toString();
  };

  const renderListShell = (loading) => {
    mountView(view, `
      <div class="card glass-panel glass-panel--panel incident-list">
        <h2>Incidents</h2>
        ${typeof buildTonightStageStripHtml === "function" ? buildTonightStageStripHtml({ stage: "during", active: "cases" }) : ""}
        <p class="muted" style="max-width:40rem;line-height:1.45">Alarms and response activity for your account. Open one to review timing, assignments, and after-action notes.</p>
        <div class="inline-form" style="margin:14px 0 8px">
          <label class="field wide"><span>Search</span>
            <input id="incidentSearch" value="${escapeHtml(listQ)}" placeholder="Site, device, or alarm…" autocomplete="off" />
          </label>
          <label class="field"><span>Show</span>
            <input id="incidentLimit" type="number" min="10" max="200" value="${listLimit}" title="How many rows to load" />
          </label>
          <button class="btn btn-tap" id="incidentSearchBtn" type="button">Search</button>
        </div>
        <p id="incListStatus" class="muted" aria-live="polite">${loading ? "Loading incidents…" : ""}</p>
        <div id="incListBody" class="incident-list__body">${loading ? "" : ""}</div>
      </div>`);
  };

  const renderListRows = (items, errMsg) => {
    const body = $("#incListBody", view);
    const status = $("#incListStatus", view);
    if (status) status.textContent = errMsg || `${items.length} shown · updates automatically`;
    if (!body) return;
    if (errMsg) {
      body.innerHTML = `<p class="muted">${escapeHtml(errMsg)}</p>`;
      return;
    }
    if (!items.length) {
      body.innerHTML = `<p class="muted" style="padding:12px 0">No alarms match this search.</p>`;
      return;
    }
    body.innerHTML = `
      <div class="incident-list__cards" role="list">
          ${items.map((ev) => {
            const id = ev.incident_id != null ? ev.incident_id : (ev.event_id != null ? ev.event_id : ev.id);
            const whenRaw = ev.ts_malaysia || ev.ts || "";
            const whenRel = fmtRel(whenRaw);
            const whenFull = fmtTs(whenRaw);
            const title = humanizeIncidentRowTitle(ev);
            const device = String(ev.device_id || "—");
            return `<button type="button" class="incident-list__card glass-panel glass-panel--inset" role="listitem" data-cc-go="incidents" data-cc-id="${escapeHtml(String(id))}">
              <span class="incident-list__when" title="${escapeHtml(whenFull)}">${escapeHtml(whenRel !== "—" ? whenRel : whenFull)}</span>
              <span class="incident-list__title">${escapeHtml(title)}</span>
              <span class="incident-list__device muted">${escapeHtml(device)}</span>
              <span class="incident-list__go">Open</span>
            </button>`;
          }).join("")}
      </div>`;
  };

  const loadList = async (opts) => {
    const silent = !!(opts && opts.silent);
    if (!silent) renderListShell(true);
    if (window.__incListAbort) {
      try { window.__incListAbort.abort(); } catch (_) {}
    }
    const ac = new AbortController();
    window.__incListAbort = ac;
    try {
      const data = await api("/incidents?" + buildListQuery(), { signal: ac.signal });
      if (!isRouteCurrent(routeSeq)) return;
      const items = Array.isArray(data.items) ? data.items : [];
      if (silent) {
        renderListRows(items, "");
      } else {
        renderListShell(false);
        wireListControls();
        renderListRows(items, "");
      }
    } catch (e) {
      if (e && e.name === "AbortError") return;
      if (!isRouteCurrent(routeSeq)) return;
      if (silent) {
        renderListRows([], String(e.message || e));
      } else {
        renderListShell(false);
        wireListControls();
        renderListRows([], String(e.message || e));
      }
    }
  };

  const wireListControls = () => {
    const searchBtn = $("#incidentSearchBtn", view);
    const applySearch = () => {
      listQ = String(($("#incidentSearch", view) && $("#incidentSearch", view).value) || "").trim();
      listLimit = Math.min(200, Math.max(10, Number(($("#incidentLimit", view) && $("#incidentLimit", view).value) || 40) || 40));
      listSinceId = String(($("#incidentSinceId", view) && $("#incidentSinceId", view).value) || "").trim();
      const qs = new URLSearchParams();
      if (listQ) qs.set("q", listQ);
      if (listLimit !== 40) qs.set("limit", String(listLimit));
      if (listSinceId) qs.set("since_id", listSinceId);
      const suffix = qs.toString() ? `?${qs.toString()}` : "";
      location.hash = `#/incidents${suffix}`;
    };
    if (searchBtn) searchBtn.addEventListener("click", applySearch);
    const searchInput = $("#incidentSearch", view);
    if (searchInput) {
      searchInput.addEventListener("keydown", (ev) => {
        if (ev.key === "Enter") applySearch();
      });
    }
  };

  renderListShell(true);
  await loadList({ silent: false });

  scheduleRouteTicker(routeSeq, "incidents-list-poll", () => loadList({ silent: true }), 15000);
});


// ── login.route.js ─────────────────────────────────────
/**
 * Route: #/login — primary sign-in form (auth shell page).
 *
 * Build: concatenated by `scripts/build-dashboard.mjs`. Shares scope with
 * console.raw.js — see `account-activate.route.js` for the convention.
 *
 * Flow: posts to login() (defined in console.raw.js — wraps /auth/login,
 * stores CSRF token + JWT/cookie session, hydrates state.me), then loads
 * /me + /health and redirects to defaultHomeHash() (Ops if eligible, else Overview).
 *
 * Backend contracts: covered transitively by login() / loadMe() / loadHealth()
 * which are tested via `tests/test_spa_api_contract.py`.
 */

registerRoute("login", async (view) => {
  setCrumb("Sign in");
  document.body.dataset.auth = "none";
  activateCommandCenterAuthLayout();
  const cleanAuthMessage = (raw) => {
    const s = String(raw || "").trim();
    if (!s) return "Request failed. Please try again.";
    const l = s.toLowerCase();
    if (l.includes("401")) return "Username or password is incorrect.";
    if (l.includes("invalid credentials")) return "Username or password is incorrect.";
    if (l.includes("invalid or expired email code")) return "Invalid or expired email code.";
    if (l.includes("too many login attempts")) return s; /* 429: server already has seconds */
    if (l.includes("session expired")) return "Session expired. Please sign in again.";
    if (l.includes("networkerror") || l.includes("failed to fetch")) return "Network issue — check your connection and try again.";
    if (typeof softApiError === "function") return softApiError(s);
    return s.replace(/^error:\s*/i, "");
  };
  mountView(view, buildCommandCenterAuthSurfaceHtml("login", `
          <div class="auth-card auth-card--panel auth-card--auth-main" data-auth-card>
            <header class="auth-card__head">
              <h1 class="auth-card__title">Sign in</h1>
              <p class="auth-card__lead">Use the credentials your administrator provided.</p>
            </header>
            <form class="auth-card__body" id="loginForm" autocomplete="on">
              <div id="loginStep1">
                <label class="field">
                  <span>Username</span>
                  <input name="username" id="loginUserInp" autocomplete="username" required placeholder="e.g. dan" />
                </label>
                <label class="field field--spaced">
                  <span>Password</span>
                  <input name="password" type="password" autocomplete="current-password" required placeholder="••••••••" />
                </label>
                <div class="auth-card__submit">
                  <button class="btn btn-tap btn-block auth-btn-primary" type="submit" id="loginSubmit">Sign in</button>
                </div>
                <nav class="auth-card__links auth-card__links--grid" aria-label="Other sign-in options">
                  <a class="auth-link" href="#/register">Register admin</a>
                  <a class="auth-link" href="#/account-activate">Activate account</a>
                  <a class="auth-link" href="#/forgot-password">Forgot password</a>
                </nav>
              </div>
              <div id="loginStep2" style="display:none">
                <p class="muted" style="margin:0 0 12px">Staff accounts may require a second step: enter the <strong>6-digit code</strong> sent to your email.</p>
                <p class="muted" id="loginOtpExpiry" style="margin:0 0 10px" aria-live="polite"></p>
                <label class="field">
                  <span>Email code</span>
                  <input id="loginOtpInp" inputmode="numeric" autocomplete="one-time-code" maxlength="12" placeholder="000000" />
                </label>
                <div class="auth-card__submit">
                  <button class="btn btn-tap btn-block auth-btn-primary" type="button" id="loginOtpSubmit">Verify &amp; continue</button>
                </div>
                <div class="row wide" style="justify-content:space-between;flex-wrap:wrap;gap:10px;margin-top:10px">
                  <button type="button" class="btn secondary btn-tap" id="loginResendBtn">Resend code</button>
                  <button type="button" class="btn ghost btn-tap" id="loginBackBtn">Back</button>
                </div>
              </div>
              <p class="auth-card__msg auth-card__msg--fixed muted" id="loginMsg" aria-live="polite"></p>
            </form>
          </div>`));
  const form = $("#loginForm", view);
  const card = view.querySelector("[data-auth-card]");
  const step1 = $("#loginStep1", view);
  const step2 = $("#loginStep2", view);
  const otpInp = $("#loginOtpInp", view);
  const resendBtn = $("#loginResendBtn", view);
  let login2User = "";
  let disposeLogin2OtpUi = () => {};
  form.addEventListener("submit", async (ev) => {
    ev.preventDefault();
    const data = new FormData(form);
    const msg = $("#loginMsg", view);
    const btn = $("#loginSubmit", view);
    const label = btn ? btn.textContent : "Sign in";
    msg.textContent = "";
    if (btn) {
      btn.disabled = true;
      btn.textContent = "Signing in…";
    }
    try {
      const out = await login(data.get("username"), data.get("password"));
      if (out && out.requires_email_second) {
        login2User = String(out.username || data.get("username") || "").trim();
        if (step1) step1.style.display = "none";
        if (step2) step2.style.display = "";
        if (otpInp) otpInp.value = "";
        disposeLogin2OtpUi();
        const d1 = startEmailCodeExpiryCountdown($("#loginOtpExpiry", view), out.ttl_seconds);
        const d2 = startAuthResendCooldown(
          resendBtn,
          Number(out.resend_after_seconds || 60),
          "Resend code",
        );
        disposeLogin2OtpUi = () => {
          try { d1(); } catch (_) {}
          try { d2(); } catch (_) {}
        };
        msg.textContent = "Check your email for the login code.";
        return;
      }
      await loadMe();
      if (!state.me) {
        throw new Error(
          "Signed in but profile could not load (server busy or session cookie blocked). Wait a few seconds and try again.",
        );
      }
      await loadHealth();
      location.hash = defaultHomeHash();
    } catch (e) {
      msg.textContent = cleanAuthMessage(e.message || e);
      if (card) {
        card.classList.remove("auth-shake");
        void card.offsetWidth;
        card.classList.add("auth-shake");
        setTimeout(() => card.classList.remove("auth-shake"), 500);
      }
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.textContent = label;
      }
    }
  });
  const otpBtn = $("#loginOtpSubmit", view);
  const backBtn = $("#loginBackBtn", view);
  if (otpBtn) {
    otpBtn.addEventListener("click", async () => {
      const msg = $("#loginMsg", view);
      msg.textContent = "";
      otpBtn.disabled = true;
      try {
        await loginEmailSecond(login2User, otpInp ? otpInp.value : "");
        await loadMe();
        if (!state.me) {
          throw new Error(
            "Verification succeeded but profile could not load. Wait a few seconds and try again.",
          );
        }
        await loadHealth();
        location.hash = defaultHomeHash();
      } catch (e) {
        msg.textContent = cleanAuthMessage(e.message || e);
        if (card) {
          card.classList.remove("auth-shake");
          void card.offsetWidth;
          card.classList.add("auth-shake");
          setTimeout(() => card.classList.remove("auth-shake"), 500);
        }
      } finally {
        otpBtn.disabled = false;
      }
    });
  }
  if (resendBtn) {
    resendBtn.addEventListener("click", async () => {
      const msg = $("#loginMsg", view);
      msg.textContent = "";
      resendBtn.disabled = true;
      try {
        const j = await resendLoginSecondEmail(login2User);
        disposeLogin2OtpUi();
        const d1 = startEmailCodeExpiryCountdown($("#loginOtpExpiry", view), j.ttl_seconds);
        const d2 = startAuthResendCooldown(
          resendBtn,
          Number(j.resend_after_seconds || 60),
          "Resend code",
        );
        disposeLogin2OtpUi = () => {
          try { d1(); } catch (_) {}
          try { d2(); } catch (_) {}
        };
        msg.textContent = "If mail is configured, a new code was sent.";
      } catch (e) {
        msg.textContent = cleanAuthMessage(e.message || e);
        resendBtn.disabled = false;
      }
    });
  }
  if (backBtn) {
    backBtn.addEventListener("click", () => {
      login2User = "";
      disposeLogin2OtpUi();
      disposeLogin2OtpUi = () => {};
      const ex = $("#loginOtpExpiry", view);
      if (ex) ex.textContent = "";
      if (step2) step2.style.display = "none";
      if (step1) step1.style.display = "";
      if (otpInp) otpInp.value = "";
      $("#loginMsg", view).textContent = "";
    });
  }
});


// ── ops-live.route.js ─────────────────────────────────────
/**
 * Route: #/ops-live — Platform health metrics (SA ops UI, real metrics only).
 */

registerRoute("ops-live", async (view, _args, routeSeq) => {
  setCrumb("Health");
  const me = state.me || {};
  if (!me.ops_dashboard_eligible) {
    mountView(
      view,
      `<div class="card"><p class="muted">Platform health is limited to superadmin and delegated SA staff.</p></div>`,
    );
    return;
  }

  const sparkCellsHtml = (specs) =>
    (Array.isArray(specs) ? specs : []).map(
      (s) =>
        `<div class="live-dash__spark ops-spark-cell" data-spark="${escapeHtml(s.key)}">
        <div class="ops-spark-head">
          <div class="ops-spark-titlestack">
            <span class="ops-spark-label">${escapeHtml(s.label)}</span>
          </div>
          <div class="ops-spark-metrics">
            <div class="ops-spark-metric" title="Most recent sample">
              <span class="ops-spark-metric-k">Latest</span>
              <span class="ops-spark-value mono" aria-live="polite">—</span>
            </div>
            <div class="ops-spark-metric" title="Max in last ${OPS_HIST_LEN} samples">
              <span class="ops-spark-metric-k">Peak</span>
              <span class="ops-spark-peak mono">—</span>
            </div>
          </div>
        </div>
        <canvas class="ops-spark-canvas" width="320" height="90" aria-label="${escapeHtml(s.label)}"></canvas>
      </div>`,
    ).join("");

  let sparkSpecs = opsSparkSpecsFromAllowedKeys(OPS_SPARK_SPECS.map((s) => s.key));
  let metricsQueryScope = opsMetricsQueryScopeForSparkSpecs(sparkSpecs);
  let hist = createOpsHist(sparkSpecs);
  const sparksInner = sparkCellsHtml(sparkSpecs);

  /** SSE push cadence from worker (matches FastAPI `interval_ms` query). */
  const OPS_SSE_INTERVAL_MS = 1000;
  /** GET snapshot fallback when SSE unavailable — DOM refresh cadence. */
  const OPS_POLL_INTERVAL_MS = 2000;

  let pollTimer = 0;
  let streamAc = null;
  let chromeBuilt = false;
  let rafPending = false;
  let pendingPayload = null;
  /** Basis for `opsMetricsStale` — SSE tick vs poll interval. */
  let staleBasisMs = OPS_SSE_INTERVAL_MS;

  const stopAll = () => {
    try {
      const grid = $("#opsSparksGrid", view);
      if (grid && typeof grid._opsSparkHoverTeardown === "function") grid._opsSparkHoverTeardown();
    } catch (_) {}
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = 0;
    }
    try {
      if (window.__opsDashPoll) {
        clearInterval(window.__opsDashPoll);
        window.__opsDashPoll = 0;
      }
    } catch (_) {}
    if (streamAc) {
      try {
        streamAc.abort();
      } catch (_) {}
      streamAc = null;
    }
    try {
      window.__opsDashAbort = null;
      window.__opsDashPoll = 0;
    } catch (_) {}
  };

  mountView(
    view,
    `
    <div class="ops-workspace-shell" data-ops-live="1">
    <div class="live-dash ops-dash-wrap motion-fade-up" id="liveDashRoot">
      <header class="live-dash__top live-dash__top--ops ops-dash-head card">
        <div class="live-dash__title-block">
          <p class="live-dash__eyebrow mono">Live platform health</p>
          <h2 class="ops-dash-head__title live-dash__h2">Platform health</h2>
        </div>
        <div class="live-dash__meta-bar live-dash__meta-bar--ops ops-dash-head__meta mono">
          <span id="opsConnBadge" class="badge offline" title="Connection mode">
            <span id="opsLiveDot" class="ops-live-dot" aria-hidden="true"></span>
            <span id="opsConnText">Starting…</span>
          </span>
          <span class="live-dash__meta-sep" aria-hidden="true"></span>
          <span id="opsWallClock" class="live-dash__meta-clock">—</span>
          <span class="live-dash__meta-sep" aria-hidden="true"></span>
          <span id="opsStamp" class="live-dash__meta-stamp muted">—</span>
        </div>
      </header>

      <section class="live-dash__hero" aria-label="Headline KPIs">
        <div class="live-dash__hero-cell live-dash__panel" title="Signed-in console users active recently">
          <span class="live-dash__hero-k">Users online</span>
          <span class="live-dash__hero-v mono" id="opsHeroSessions">—</span>
        </div>
        <div class="live-dash__hero-cell live-dash__panel" title="Devices currently online">
          <span class="live-dash__hero-k">Devices online</span>
          <span class="live-dash__hero-v mono" id="opsHeroDevices">—</span>
        </div>
        <div class="live-dash__hero-cell live-dash__panel">
          <span class="live-dash__hero-k">Alerts waiting</span>
          <span class="live-dash__hero-v mono" id="opsHeroFcm">—</span>
        </div>
        <div class="live-dash__hero-cell live-dash__panel">
          <span class="live-dash__hero-k">Device channel</span>
          <span class="live-dash__hero-v mono" id="opsHeroMqtt">—</span>
        </div>
        <div class="live-dash__hero-cell live-dash__panel">
          <span class="live-dash__hero-k">Live updates</span>
          <span class="live-dash__hero-v mono" id="opsHeroEv">—</span>
        </div>
        <div class="live-dash__hero-cell live-dash__hero-cell--accent live-dash__panel">
          <span class="live-dash__hero-k">Status</span>
          <span class="live-dash__hero-v mono" id="opsHeroPid">—</span>
        </div>
      </section>

      <div class="live-dash__layout">
        <div class="live-dash__main-col">
          <div id="opsStaleBanner" class="ops-stale-banner live-dash__stale" hidden>
            <span class="ops-stale-banner__dot" aria-hidden="true"></span>
            Snapshot may be stale — live updates may have paused.
          </div>

          <section class="live-dash__panel live-dash__panel--pad ops-dash-sparks card" aria-label="Activity traces">
            <div class="ops-dash-sparks__head">
              <div class="ops-dash-sparks__title-row">
                <h3 class="ui-section-title live-dash__panel-title">Activity traces</h3>
                <span class="muted ops-sparks-hint">${sparkSpecs.length} series</span>
              </div>
            </div>
            <div class="ops-sparks-grid live-dash__spark-grid" id="opsSparksGrid">${sparksInner}</div>
          </section>

          <section class="ops-dash-grid live-dash__panel live-dash__panel--pad" id="opsTiles" aria-live="polite"></section>

          <section
            class="live-dash__panel live-dash__panel--pad card ops-fcm-migration"
            id="opsFcmMigrationPanel"
            aria-label="Alert routing metrics"
          >
            <div class="ops-dash-sparks__head">
              <div class="ops-dash-sparks__title-row">
                <h3 class="ui-section-title live-dash__panel-title">Alert routing</h3>
                <span class="muted ops-fcm-mig-hint">live</span>
              </div>
            </div>
            <div id="opsFcmMigrationBody" class="ops-fcm-mig-body"></div>
          </section>

          <section
            class="live-dash__panel live-dash__panel--pad card ops-fcm-migration"
            id="opsAlarmMergePanel"
            aria-label="Alarm grouping metrics"
          >
            <div class="ops-dash-sparks__head">
              <div class="ops-dash-sparks__title-row">
                <h3 class="ui-section-title live-dash__panel-title">Alarm grouping</h3>
                <span class="muted ops-fcm-mig-hint">live</span>
              </div>
            </div>
            <div id="opsAlarmMergeBody" class="ops-fcm-mig-body"></div>
          </section>

          <section
            class="live-dash__panel live-dash__panel--pad card ops-fcm-migration"
            id="opsAlarmRateLimitPanel"
            aria-label="Alarm throttle metrics"
          >
            <div class="ops-dash-sparks__head">
              <div class="ops-dash-sparks__title-row">
                <h3 class="ui-section-title live-dash__panel-title">Alarm throttle</h3>
                <span class="muted ops-fcm-mig-hint">live</span>
              </div>
            </div>
            <div id="opsAlarmRateLimitBody" class="ops-fcm-mig-body"></div>
          </section>

          <section
            class="live-dash__panel live-dash__panel--pad card ops-fcm-migration"
            id="opsAiIncidentPanel"
            aria-label="AI incident decision metrics"
          >
            <div class="ops-dash-sparks__head">
              <div class="ops-dash-sparks__title-row">
                <h3 class="ui-section-title live-dash__panel-title">AI decision loop</h3>
                <span class="muted ops-fcm-mig-hint">24h · read only</span>
              </div>
            </div>
            <div id="opsAiIncidentBody" class="ops-fcm-mig-body"></div>
          </section>

          <section
            class="live-dash__panel live-dash__panel--pad card ops-fcm-migration"
            id="opsFcmHealthPanel"
            aria-label="Alert delivery health"
          >
            <div class="ops-dash-sparks__head">
              <div class="ops-dash-sparks__title-row">
                <h3 class="ui-section-title live-dash__panel-title">Alert delivery health</h3>
                <span class="muted ops-fcm-mig-hint">detail</span>
              </div>
              <button class="btn sm secondary btn-tap" type="button" id="opsFcmHealthRefresh">Refresh</button>
            </div>
            <div id="opsFcmHealthBody" class="ops-fcm-mig-body"><p class="muted">Loading…</p></div>
          </section>

          <section
            class="live-dash__panel live-dash__panel--pad card ops-fcm-migration glass-panel glass-panel--panel"
            id="opsLogTailPanel"
            aria-label="Recent system notes"
          >
            <details class="ops-log-fold" id="opsLogFold">
              <summary class="ops-dash-sparks__title-row" style="cursor:pointer;list-style:none">
                <h3 class="ui-section-title live-dash__panel-title" style="margin:0">Recent system notes</h3>
                <span class="muted ops-fcm-mig-hint">staff · expand</span>
              </summary>
              <p class="muted" style="margin:8px 0">Security staff only. Recent lines for troubleshooting — not a live stream.</p>
              <div class="row" style="justify-content:flex-end;margin-bottom:8px">
                <button class="btn sm secondary btn-tap" type="button" id="opsLogTailRefresh">Refresh</button>
              </div>
              <div id="opsLogTailBody" class="ops-fcm-mig-body"><p class="muted">Expand and refresh to load…</p></div>
            </details>
          </section>
        </div>
      </div>
    </div>
    </div>
  `,
  );

  const pct = (num, den) => {
    const n = Number(num) || 0;
    const d = Number(den) || 0;
    if (d <= 0) return 0;
    return Math.min(100, Math.round((n / d) * 1000) / 10);
  };

  const gaugeClass = (p) => {
    if (p >= 90) return "ops-gauge__fill ops-gauge__fill--crit";
    if (p >= 70) return "ops-gauge__fill ops-gauge__fill--warn";
    return "ops-gauge__fill";
  };

  /** Primary tile / hero value is exactly zero — extra legibility vs O / dash. */
  const tileValueLooksLikeZero = (value) => {
    if (value === 0 || value === "0") return true;
    const s = String(value).trim();
    if (s === "0 ms") return true;
    if (/^0(\.0+)?%$/.test(s)) return true;
    if (/^0\s*\/\s*\d+/.test(s)) return true;
    return false;
  };

  const tileHtml = (id, label, value, meta, fillPct) => {
    const p = Math.max(0, Math.min(100, Number(fillPct) || 0));
    const gc = gaugeClass(p);
    const zeroCls = tileValueLooksLikeZero(value) ? " mono ops-num-zero" : " mono";
    return `
      <article class="ops-tile live-dash__tile" id="${escapeHtml(id)}">
        <p class="ops-tile__label">${escapeHtml(label)}</p>
        <p class="ops-tile__value${zeroCls}">${escapeHtml(String(value))}</p>
        <p class="ops-tile__meta">${escapeHtml(meta || "")}</p>
        <div class="ops-gauge" aria-hidden="true"><div class="${gc}" data-gauge-fill style="width:${p}%"></div></div>
      </article>`;
  };

  const patchHero = (data) => {
    const k = data.kpi || {};
    const fleet = data.fleet || {};
    const fcm = data.fcm || {};
    const mqtt = data.mqtt || {};
    const ev = data.events || {};

    if (data.metrics_scope === "admin") {
      const dropped = Number(fcm.dropped_count) || Number(k.fcm_dropped_total) || 0;
      const dOn = fleet.devices_online != null ? fleet.devices_online : k.devices_online;
      const dTot = fleet.devices_total != null ? fleet.devices_total : k.devices_total;
      const devLabel =
        dTot != null && Number(dTot) > 0 ? `${dOn ?? "—"} / ${dTot}` : String(dOn ?? "—");

      const elSess = $("#opsHeroSessions", view);
      setTextIfChanged(elSess, "—");
      if (elSess) elSess.classList.add("ops-num-zero");

      const elDev = $("#opsHeroDevices", view);
      setTextIfChanged(elDev, devLabel);
      if (elDev) {
        const onlineZero =
          dTot != null && Number(dTot) > 0 ? Number(dOn) === 0 : Number(dOn) === 0;
        elDev.classList.toggle("ops-num-zero", onlineZero);
      }

      const elFcm = $("#opsHeroFcm", view);
      setTextIfChanged(elFcm, String(dropped));
      if (elFcm) elFcm.classList.toggle("ops-num-zero", dropped === 0);

      const elMqtt = $("#opsHeroMqtt", view);
      setTextIfChanged(elMqtt, "—");
      if (elMqtt) elMqtt.classList.add("ops-num-zero");

      const elEv = $("#opsHeroEv", view);
      setTextIfChanged(elEv, "—");
      if (elEv) elEv.classList.add("ops-num-zero");

      const elPid = $("#opsHeroPid", view);
      setTextIfChanged(elPid, data.uptime_ready === false ? "Warming" : "Ready");
      if (elPid) elPid.classList.toggle("ops-num-zero", false);
      return;
    }

    const q = Number(fcm.queue_size) || 0;
    const subCap = Number(ev.subscriber_cap) || 1;
    const subN = Number(ev.subscriber_count) || 0;
    const consoleUsers =
      fleet.console_users_online != null
        ? fleet.console_users_online
        : k.console_users_online != null
          ? k.console_users_online
          : 0;
    const evPctHead = pct(subN, subCap);
    const dOn = fleet.devices_online != null ? fleet.devices_online : k.devices_online;
    const dTot = fleet.devices_total != null ? fleet.devices_total : k.devices_total;
    const devLabel =
      dTot != null && Number(dTot) > 0 ? `${dOn ?? "—"} / ${dTot}` : String(dOn ?? "—");
    const mqttDepth = mqtt.ingest_queue_depth;

    const elSess = $("#opsHeroSessions", view);
    setTextIfChanged(elSess, String(consoleUsers));
    if (elSess) elSess.classList.toggle("ops-num-zero", Number(consoleUsers) === 0);

    const elDev = $("#opsHeroDevices", view);
    setTextIfChanged(elDev, devLabel);
    if (elDev) {
      const onlineZero =
        dTot != null && Number(dTot) > 0 ? Number(dOn) === 0 : Number(dOn) === 0;
      elDev.classList.toggle("ops-num-zero", onlineZero);
    }

    const elFcm = $("#opsHeroFcm", view);
    setTextIfChanged(elFcm, String(q));
    if (elFcm) elFcm.classList.toggle("ops-num-zero", q === 0);

    const elMqtt = $("#opsHeroMqtt", view);
    setTextIfChanged(elMqtt, String(mqttDepth ?? "—"));
    if (elMqtt)
      elMqtt.classList.toggle("ops-num-zero", mqttDepth != null && Number(mqttDepth) === 0);

    const elEv = $("#opsHeroEv", view);
    setTextIfChanged(elEv, `${evPctHead.toFixed(1)}%`);
    if (elEv) elEv.classList.toggle("ops-num-zero", evPctHead === 0);

    const elPid = $("#opsHeroPid", view);
    setTextIfChanged(elPid, data.uptime_ready === false ? "Warming" : "Ready");
    if (elPid) elPid.classList.toggle("ops-num-zero", false);
  };

  /** Format KPI ratios from `collect_global_metrics().kpi.*` (0–1 floats). */
  const fmtRatioPct = (v) => {
    if (v == null || typeof v !== "number" || !Number.isFinite(v)) return "—";
    return `${(v * 100).toFixed(2)}%`;
  };

  /** `alarm_merge.inputs_folded_per_coalesced_flush` may exceed 1 — show as multiplier, not %. */
  const fmtFoldPerFlush = (v) => {
    if (v == null || typeof v !== "number" || !Number.isFinite(v)) return "—";
    const s = v.toFixed(4).replace(/\.?0+$/, "");
    return `${s || "0"}×`;
  };

  /** Flags, counters, and derived KPIs already embedded in global-metrics (no extra HTTP). */
  const patchFcmMigrationPanel = (data) => {
    const body = $("#opsFcmMigrationBody", view);
    if (!body) return;
    if (data.metrics_scope === "admin") {
      setHtmlIfChanged(
        body,
        `<p class="muted">Alert routing metrics are for platform staff only.</p>`,
      );
      return;
    }
    const mig = data.fcm_migration || {};
    const flags = mig.flags || {};
    const ctr = mig.counters || {};
    const kpi = (data.kpi && data.kpi.fcm_migration) || {};
    const err = mig.last_error ? String(mig.last_error) : "";

    const n = (key) => {
      const v = ctr[key];
      const x = Number(v);
      return Number.isFinite(x) ? x : 0;
    };

    const flagOnOff = (b) => (b === true ? "on" : b === false ? "off" : "—");
    const batch =
      flags.FCM_TOPIC_RECONCILE_BATCH != null && flags.FCM_TOPIC_RECONCILE_BATCH !== ""
        ? String(flags.FCM_TOPIC_RECONCILE_BATCH)
        : "—";

    const esc = escapeHtml;
    const rowsFlag = [
      ["Group alerts", flagOnOff(flags.FCM_TOPIC_SEND_ENABLED)],
      ["Direct to device", flagOnOff(flags.FCM_TOKEN_FANOUT_FALLBACK)],
      ["Device binding update", flagOnOff(flags.FCM_TOPIC_RECONCILE_ENABLED)],
      ["Update batch size", batch],
      ["Delivery paused", flagOnOff(flags.FCM_ENQUEUE_PAUSED)],
    ];
    const rowsKpi = [
      ["Delivery success", fmtRatioPct(kpi.http_send_success_rate)],
      ["Waiting vs sent", fmtRatioPct(kpi.queued_vs_dispatch)],
      ["Skipped (no route)", fmtRatioPct(kpi.skipped_no_transport_vs_dispatch)],
      ["Rejected (full)", fmtRatioPct(kpi.queue_full_reject_vs_dispatch)],
      ["Send skips", fmtRatioPct(kpi.worker_skip_vs_worker_outcomes)],
      ["Auth refresh fails", fmtRatioPct(kpi.oauth_refresh_fail_vs_worker_outcomes)],
      ["Binding update fails", fmtRatioPct(kpi.reconcile_iid_failure_vs_finished)],
    ];
    const rowsCtr = [
      ["Alarm via group", n("alarm_dispatch_topic_payloads")],
      ["Alarm via device", n("alarm_dispatch_token_payloads")],
      ["Waiting (group)", n("fcm_queue_enqueued_topic")],
      ["Waiting (device)", n("fcm_queue_enqueued_token")],
      ["Sent ok", n("fcm_send_ok_topic") + n("fcm_send_ok_token")],
      ["Send fail", n("fcm_send_fail_topic") + n("fcm_send_fail_token")],
      ["Retried", n("fcm_send_retry_requeued")],
      ["Dropped (full)", n("fcm_send_retry_drop_queue_full")],
      ["Skipped while paused (group)", n("fcm_enqueue_skipped_pause_gate_topic")],
      ["Skipped while paused (device)", n("fcm_enqueue_skipped_pause_gate_token")],
    ];

    const col = (title, pairs, valueIsHtml) => {
      const inner = pairs
        .map(
          ([lab, val]) =>
            `<div class="ops-fcm-mig-row"><dt>${esc(lab)}</dt><dd class="mono">${
              valueIsHtml ? val : esc(String(val))
            }</dd></div>`,
        )
        .join("");
      return `<div class="ops-fcm-mig-col">
        <p class="muted ops-fcm-mig-col-title">${esc(title)}</p>
        <dl class="ops-fcm-mig-dl">${inner}</dl>
      </div>`;
    };

    const softErr = typeof softOperatorNote === "function" ? softOperatorNote(err) : err;
    const errHtml = softErr
      ? `<p class="muted ops-fcm-mig-err" title="${esc(softErr)}">${esc(softErr.length > 180 ? `${softErr.slice(0, 180)}…` : softErr)}</p>`
      : "";

    const html = `<div class="ops-fcm-mig-cols">
      ${col("Status", rowsFlag, false)}
      ${col("Health", rowsKpi, true)}
      ${col("Counts", rowsCtr, false)}
    </div>${errHtml}`;

    setHtmlIfChanged(body, html);
  };

  const patchAlarmMergePanel = (data) => {
    const body = $("#opsAlarmMergeBody", view);
    if (!body) return;
    if (data.metrics_scope === "admin") {
      setHtmlIfChanged(
        body,
        `<p class="muted">Alarm grouping metrics are platform-staff diagnostics only.</p>`,
      );
      return;
    }
    const block = data.alarm_merge || {};
    const flags = block.flags || {};
    const ctr = block.counters || {};
    const kpi = (data.kpi && data.kpi.alarm_merge) || {};
    const err = block.last_error ? String(block.last_error) : "";

    const n = (key) => {
      const v = ctr[key];
      const x = Number(v);
      return Number.isFinite(x) ? x : 0;
    };
    const esc = escapeHtml;
    const flagOnOff = (b) => (b === true ? "on" : b === false ? "off" : "—");
    const rowsFlag = [
      ["Grouping on", flagOnOff(flags.ALARM_MERGE_ENABLED)],
      ["Group window", flags.ALARM_MERGE_WINDOW_MS != null
        ? (Number(flags.ALARM_MERGE_WINDOW_MS) % 1000 === 0
          ? `${Number(flags.ALARM_MERGE_WINDOW_MS) / 1000}s`
          : `${flags.ALARM_MERGE_WINDOW_MS} ms`)
        : "—"],
      ["Max alarms per group", flags.ALARM_MERGE_MAX_EVENTS_PER_BUCKET != null ? String(flags.ALARM_MERGE_MAX_EVENTS_PER_BUCKET) : "—"],
    ];
    const rowsKpi = [
      ["Grouped releases vs all releases", fmtRatioPct(kpi.coalesced_flush_vs_total_flush)],
      ["Overflow releases vs all", fmtRatioPct(kpi.overflow_vs_total_flush)],
      ["Alarms folded per group release", fmtFoldPerFlush(kpi.inputs_folded_per_coalesced_flush)],
    ];
    const rowsCtr = [
      ["Release (timer)", n("alarm_merge_flush_timer")],
      ["Release (overflow)", n("alarm_merge_flush_overflow")],
      ["Grouped releases", n("alarm_merge_coalesced_flush")],
      ["Alarms folded in", n("alarm_merge_inputs_folded")],
    ];
    const col = (title, pairs, valueIsHtml) => {
      const inner = pairs
        .map(
          ([lab, val]) =>
            `<div class="ops-fcm-mig-row"><dt>${esc(lab)}</dt><dd class="mono">${
              valueIsHtml ? val : esc(String(val))
            }</dd></div>`,
        )
        .join("");
      return `<div class="ops-fcm-mig-col">
        <p class="muted ops-fcm-mig-col-title">${esc(title)}</p>
        <dl class="ops-fcm-mig-dl">${inner}</dl>
      </div>`;
    };
    const softErrLine = err
      ? (typeof softOperatorNote === "function" ? softOperatorNote(err) : err)
      : "";
    const errHtml = softErrLine
      ? `<p class="muted ops-fcm-mig-err" title="${esc(softErrLine)}">${esc(softErrLine.length > 180 ? `${softErrLine.slice(0, 180)}…` : softErrLine)}</p>`
      : "";
    const html = `<div class="ops-fcm-mig-cols">
      ${col("Status", rowsFlag, false)}
      ${col("Health", rowsKpi, true)}
      ${col("Counts", rowsCtr, false)}
    </div>${errHtml}`;
    setHtmlIfChanged(body, html);
  };

  const patchAlarmRateLimitPanel = (data) => {
    const body = $("#opsAlarmRateLimitBody", view);
    if (!body) return;
    if (data.metrics_scope === "admin") {
      setHtmlIfChanged(
        body,
        `<p class="muted">Alarm throttle details are for platform staff only.</p>`,
      );
      return;
    }
    const block = data.alarm_rate_limit || {};
    const flags = block.flags || {};
    const ctr = block.counters || {};
    const kpi = (data.kpi && data.kpi.alarm_rate_limit) || {};
    const err = block.last_error ? String(block.last_error) : "";

    const n = (key) => {
      const v = ctr[key];
      const x = Number(v);
      return Number.isFinite(x) ? x : 0;
    };
    const esc = escapeHtml;
    const flagOnOff = (b) => (b === true ? "on" : b === false ? "off" : "—");
    const cool = flags.ALARM_RATE_LIMIT_AUDIT_COOLDOWN_SEC;
    const coolStr =
      cool != null && cool !== ""
        ? `${String(Number(cool))}s`
        : "—";
    const rowsFlag = [
      ["Throttle on", flagOnOff(flags.ALARM_RATE_LIMIT_ENABLED)],
      ["Max every 10s", flags.ALARM_RATE_LIMIT_MAX_PER_10S != null ? String(flags.ALARM_RATE_LIMIT_MAX_PER_10S) : "—"],
      ["Max every minute", flags.ALARM_RATE_LIMIT_MAX_PER_60S != null ? String(flags.ALARM_RATE_LIMIT_MAX_PER_60S) : "—"],
      ["Audit pause", coolStr],
    ];
    const rowsKpi = [["Blocked vs incoming", fmtRatioPct(kpi.block_vs_ingress)]];
    const rowsCtr = [
      ["Incoming", n("alarm_rate_limit_ingress")],
      ["Blocked", n("alarm_rate_limit_block")],
      ["Delayed total", n("alarm_rate_limit_delay_total")],
    ];
    const col = (title, pairs, valueIsHtml) => {
      const inner = pairs
        .map(
          ([lab, val]) =>
            `<div class="ops-fcm-mig-row"><dt>${esc(lab)}</dt><dd class="mono">${
              valueIsHtml ? val : esc(String(val))
            }</dd></div>`,
        )
        .join("");
      return `<div class="ops-fcm-mig-col">
        <p class="muted ops-fcm-mig-col-title">${esc(title)}</p>
        <dl class="ops-fcm-mig-dl">${inner}</dl>
      </div>`;
    };
    const softErrLine = err
      ? (typeof softOperatorNote === "function" ? softOperatorNote(err) : err)
      : "";
    const errHtml = softErrLine
      ? `<p class="muted ops-fcm-mig-err" title="${esc(softErrLine)}">${esc(softErrLine.length > 180 ? `${softErrLine.slice(0, 180)}…` : softErrLine)}</p>`
      : "";
    const html = `<div class="ops-fcm-mig-cols">
      ${col("Status", rowsFlag, false)}
      ${col("Health", rowsKpi, true)}
      ${col("Counts", rowsCtr, false)}
    </div>${errHtml}`;
    setHtmlIfChanged(body, html);
  };

  const patchAiIncidentPanel = (data) => {
    const body = $("#opsAiIncidentBody", view);
    if (!body) return;
    setHtmlIfChanged(
      body,
      aiIncidentObservabilityHtml(data.ai_incident || {}, data.metrics_scope || "sa"),
    );
  };

  const setConn = (mode) => {
    staleBasisMs =
      mode === "poll" ? OPS_POLL_INTERVAL_MS : mode === "sse" ? OPS_SSE_INTERVAL_MS : OPS_POLL_INTERVAL_MS;
    const dot = $("#opsLiveDot", view);
    const text = $("#opsConnText", view);
    const badge = $("#opsConnBadge", view);
    if (dot) {
      dot.className = "ops-live-dot";
      if (mode === "sse") dot.classList.add("ops-live-dot--live");
      else if (mode === "poll") dot.classList.add("ops-live-dot--poll");
    }
    if (text) {
      text.textContent =
        mode === "sse"
          ? `Live`
          : mode === "poll"
            ? `Refreshing every few seconds`
            : "Offline";
    }
    if (badge) {
      badge.className =
        mode === "sse" ? "badge online" : mode === "poll" ? "badge partial" : "badge offline";
    }
  };

  const updateStale = (data) => {
    const el = $("#opsStaleBanner", view);
    if (!el) return;
    el.hidden = !opsMetricsStale(data, staleBasisMs);
  };

  const pulseWrap = () => {
    const w = $("#liveDashRoot", view);
    if (!w) return;
    w.classList.remove("live-dash--pulse");
    void w.offsetWidth;
    w.classList.add("live-dash--pulse");
  };

  const setSaDiagnosticsPanelsHidden = (hidden) => {
    ["opsFcmMigrationPanel", "opsAlarmMergePanel", "opsAlarmRateLimitPanel", "opsFcmHealthPanel", "opsLogTailPanel"].forEach((id) => {
      const el = view.querySelector(`#${id}`);
      if (el) el.hidden = !!hidden;
    });
  };

  const buildChrome = (data) => {
    const adminScope = data.metrics_scope === "admin";
    const k = data.kpi || {};
    const fcm = data.fcm || {};
    const cap = Number(fcm.queue_capacity) || 500;
    const q = Number(fcm.queue_size) || 0;
    const mqtt = data.mqtt || {};
    const ev = data.events || {};
    const subCap = Number(ev.subscriber_cap) || 1;
    const tg = data.telegram || {};
    const smtp = data.smtp || {};
    const tgQ = Number(tg.queue_size);
    const tgMetaBits = [];
    tgMetaBits.push(`dropped ${tg.dropped_queue_full ?? 0}`);
    if (tg.last_error) {
      const soft =
        typeof softApiError === "function"
          ? softApiError(tg.last_error, String(tg.last_error).slice(0, 56))
          : String(tg.last_error).slice(0, 56);
      tgMetaBits.push(soft);
    }

    setSaDiagnosticsPanelsHidden(adminScope);

    let tilesHtml;
    if (adminScope) {
      const fleet = data.fleet || {};
      const dropped = Number(fcm.dropped_count) || 0;
      const proc = Number(fcm.processed_count) || 0;
      const dOn = fleet.devices_online != null ? fleet.devices_online : k.devices_online;
      const dTot = fleet.devices_total != null ? fleet.devices_total : k.devices_total;
      const devLabel =
        dTot != null && Number(dTot) > 0 ? `${dOn ?? "—"} / ${dTot}` : String(dOn ?? "—");
      const denom = Math.max(1, dropped + proc);
      tilesHtml = [
        tileHtml(
          "opsTileFcm",
          "Alert delivery failures",
          String(dropped),
          `processed ${proc}`,
          pct(dropped, denom),
        ),
        tileHtml(
          "opsTileFleet",
          "Devices online",
          devLabel,
          "across devices",
          pct(Number(dOn) || 0, Math.max(1, Number(dTot) || 1)),
        ),
        tileHtml(
          "opsTileTg",
          "Telegram",
          tg.enabled === false ? "off" : tg.worker_running ? "up" : "down",
          tg.enabled === false ? "disabled" : "service",
          tg.enabled === false ? 5 : tg.worker_running ? 75 : 35,
        ),
        tileHtml(
          "opsTileSmtp",
          "Email",
          smtp.configured ? "on" : "off",
          smtp.worker_running ? "sending" : "not sending",
          smtp.configured && smtp.worker_running ? 70 : smtp.configured ? 35 : 8,
        ),
      ].join("");
    } else {
      tilesHtml = [
        tileHtml(
          "opsTileFcm",
          "Alerts",
          q,
          `peak ${k.fcm_queue_high_watermark || 0} · dropped ${k.fcm_dropped_total || 0}`,
          pct(q, cap),
        ),
        tileHtml(
          "opsTileLag",
          "Alert wait",
          `${k.fcm_oldest_age_ms || 0} ms`,
          "longest wait",
          Math.min(100, (Number(k.fcm_oldest_age_ms) || 0) / 50),
        ),
        tileHtml(
          "opsTileMqtt",
          "Device channel",
          mqtt.ingest_queue_depth ?? "—",
          `dropped ${mqtt.ingest_dropped ?? 0}`,
          pct(mqtt.ingest_queue_depth, 1000),
        ),
        tileHtml(
          "opsTileEv",
          "Live updates",
          `${ev.subscriber_count ?? 0} / ${subCap}`,
          `dropped ${ev.subscriber_dropped_total ?? 0}`,
          pct(ev.subscriber_count, subCap),
        ),
        tileHtml(
          "opsTileTg",
          "Telegram",
          tg.enabled === false ? "off" : Number.isFinite(tgQ) ? tgQ : "—",
          tg.enabled === false ? "disabled" : tgMetaBits.join(" · ") || "ok",
          pct(Number.isFinite(tgQ) ? tgQ : 0, 400),
        ),
        tileHtml(
          "opsTileSmtp",
          "Email",
          smtp.configured ? "on" : "off",
          smtp.worker_running ? "sending" : "not sending",
          smtp.configured && smtp.worker_running ? 70 : smtp.configured ? 35 : 8,
        ),
      ].join("");
    }
    const grid = $("#opsTiles", view);
    if (grid) setHtmlIfChanged(grid, tilesHtml);

    patchHero(data);
    patchFcmMigrationPanel(data);
    patchAlarmMergePanel(data);
    patchAlarmRateLimitPanel(data);
    chromeBuilt = true;
  };

  const patchChrome = (data) => {
    const k = data.kpi || {};
    const fcm = data.fcm || {};
    const cap = Number(fcm.queue_capacity) || 500;
    const q = Number(fcm.queue_size) || 0;
    const mqtt = data.mqtt || {};
    const ev = data.events || {};
    const subCap = Number(ev.subscriber_cap) || 1;
    const tg = data.telegram || {};
    const smtp = data.smtp || {};

    const patchTileVal = (tileId, val) => {
      const art = document.getElementById(tileId);
      const v = art && art.querySelector(".ops-tile__value");
      if (v) {
        const s = String(val);
        setTextIfChanged(v, s);
        v.classList.toggle("ops-num-zero", tileValueLooksLikeZero(val));
      }
    };

    const patchGauge = (tileId, fillPct) => {
      const el = document.getElementById(tileId);
      if (!el) return;
      const p = Math.max(0, Math.min(100, Number(fillPct) || 0));
      const fill = el.querySelector("[data-gauge-fill]");
      if (fill) {
        fill.style.width = `${p}%`;
        fill.className = gaugeClass(p);
      }
    };

    const adminScope = data.metrics_scope === "admin";
    setSaDiagnosticsPanelsHidden(adminScope);

    if (adminScope) {
      const fleet = data.fleet || {};
      const dropped = Number(fcm.dropped_count) || Number(k.fcm_dropped_total) || 0;
      const proc = Number(fcm.processed_count) || 0;
      patchTileVal("opsTileFcm", String(dropped));
      const m0a = $("#opsTileFcm .ops-tile__meta", view);
      if (m0a) setTextIfChanged(m0a, `processed ${proc}`);
      patchGauge("opsTileFcm", pct(dropped, Math.max(1, dropped + proc)));

      const dOn = fleet.devices_online != null ? fleet.devices_online : k.devices_online;
      const dTot = fleet.devices_total != null ? fleet.devices_total : k.devices_total;
      const devLabel =
        dTot != null && Number(dTot) > 0 ? `${dOn ?? "—"} / ${dTot}` : String(dOn ?? "—");
      patchTileVal("opsTileFleet", devLabel);
      patchGauge("opsTileFleet", pct(Number(dOn) || 0, Math.max(1, Number(dTot) || 1)));

      patchTileVal("opsTileTg", tg.enabled === false ? "off" : tg.worker_running ? "up" : "down");
      const mta = $("#opsTileTg .ops-tile__meta", view);
      if (mta) setTextIfChanged(mta, tg.enabled === false ? "disabled" : "service");
      patchGauge("opsTileTg", tg.enabled === false ? 5 : tg.worker_running ? 75 : 35);

      patchTileVal("opsTileSmtp", smtp.configured ? "on" : "off");
      const msa = $("#opsTileSmtp .ops-tile__meta", view);
      if (msa) setTextIfChanged(msa, smtp.worker_running ? "sending" : "not sending");
      patchGauge(
        "opsTileSmtp",
        smtp.configured && smtp.worker_running ? 70 : smtp.configured ? 35 : 8,
      );
    } else {
    patchTileVal("opsTileFcm", q);
    const m0 = $("#opsTileFcm .ops-tile__meta", view);
    if (m0) setTextIfChanged(m0, `peak ${k.fcm_queue_high_watermark || 0} · dropped ${k.fcm_dropped_total || 0}`);
    patchGauge("opsTileFcm", pct(q, cap));

    patchTileVal("opsTileLag", `${k.fcm_oldest_age_ms || 0} ms`);
    const mLag = $("#opsTileLag .ops-tile__meta", view);
    if (mLag) setTextIfChanged(mLag, "longest wait");
    patchGauge("opsTileLag", Math.min(100, (Number(k.fcm_oldest_age_ms) || 0) / 50));

    patchTileVal("opsTileMqtt", mqtt.ingest_queue_depth ?? "—");
    const m2 = $("#opsTileMqtt .ops-tile__meta", view);
    if (m2) setTextIfChanged(m2, `dropped ${mqtt.ingest_dropped ?? 0}`);
    patchGauge("opsTileMqtt", pct(mqtt.ingest_queue_depth, 1000));

    patchTileVal("opsTileEv", `${ev.subscriber_count ?? 0} / ${subCap}`);
    const m3 = $("#opsTileEv .ops-tile__meta", view);
    if (m3) setTextIfChanged(m3, `dropped ${ev.subscriber_dropped_total ?? 0}`);
    patchGauge("opsTileEv", pct(ev.subscriber_count, subCap));

    const tgQ = Number(tg.queue_size);
    patchTileVal("opsTileTg", tg.enabled === false ? "off" : Number.isFinite(tgQ) ? tgQ : "—");
    const mt = $("#opsTileTg .ops-tile__meta", view);
    if (mt) {
      const bits = [`dropped ${tg.dropped_queue_full ?? 0}`];
      if (tg.last_error) {
        const soft =
          typeof softApiError === "function"
            ? softApiError(tg.last_error, String(tg.last_error).slice(0, 56))
            : String(tg.last_error).slice(0, 56);
        bits.push(soft);
      }
      setTextIfChanged(mt, tg.enabled === false ? "disabled" : bits.join(" · ") || "ok");
    }
    patchGauge("opsTileTg", pct(Number.isFinite(tgQ) ? tgQ : 0, 400));

    patchTileVal("opsTileSmtp", smtp.configured ? "on" : "off");
    const ms = $("#opsTileSmtp .ops-tile__meta", view);
    if (ms) setTextIfChanged(ms, smtp.worker_running ? "sending" : "not sending");
    patchGauge(
      "opsTileSmtp",
      smtp.configured && smtp.worker_running ? 70 : smtp.configured ? 35 : 8,
    );
    }

    const ts = Number(data.ts) || 0;
    const stamp = ts
      ? `Updated ${fmtTs(new Date(ts * 1000).toISOString())}${data.uptime_ready ? "" : " · warming up"}`
      : "—";
    const extra = data.bootstrap_error ? " · start warning" : "";
    setTextIfChanged($("#opsStamp", view), stamp + extra);

    patchHero(data);
    patchFcmMigrationPanel(data);
    patchAlarmMergePanel(data);
    patchAlarmRateLimitPanel(data);
    patchAiIncidentPanel(data);
  };

  const applyRender = (raw) => {
    if (!isRouteCurrent(routeSeq)) return;
    const pack = unwrapOpsMetricsEnvelope(raw || {});
    const data = pack.data;
    if (pack.allowed_specs && pack.allowed_specs.length) {
      const nextSpecs = opsSparkSpecsFromAllowedKeys(pack.allowed_specs);
      const same =
        nextSpecs.length === sparkSpecs.length &&
        nextSpecs.every((s, i) => sparkSpecs[i] && sparkSpecs[i].key === s.key);
      if (!same) {
        sparkSpecs = nextSpecs;
        hist = createOpsHist(sparkSpecs);
        const grid = $("#opsSparksGrid", view);
        if (grid) {
          if (typeof grid._opsSparkHoverTeardown === "function") grid._opsSparkHoverTeardown();
          setChildMarkup(grid, sparkCellsHtml(sparkSpecs));
        }
        const hint = view.querySelector(".ops-sparks-hint");
        if (hint) setTextIfChanged(hint, `${sparkSpecs.length} series`);
      }
    }
    if (pack.scope === "admin" || pack.scope === "sa") {
      metricsQueryScope = pack.scope;
    }
    pushOpsHistFromPayload(hist, data);
    if (!chromeBuilt) buildChrome(data);
    else patchChrome(data);
    redrawOpsSparkGrid($("#opsSparksGrid", view), hist, sparkSpecs);
    ensureOpsSparkHover($("#opsSparksGrid", view), () => hist, sparkSpecs);
    updateStale(data);
    pulseWrap();
  };

  const scheduleApply = (data) => {
    pendingPayload = data;
    if (rafPending) return;
    rafPending = true;
    window.requestAnimationFrame(() => {
      rafPending = false;
      const d = pendingPayload;
      pendingPayload = null;
      if (d) applyRender(d);
    });
  };

  const fetchOnce = async () => {
    const data = await api(buildAdminOpsGlobalMetricsPath(metricsQueryScope), {
      timeoutMs: OPS_METRICS_CONSOLE_TIMEOUT_MS,
    });
    if (!isRouteCurrent(routeSeq)) return;
    scheduleApply(data || {});
  };

  const startPolling = () => {
    stopAll();
    setConn("poll");
    void fetchOnce();
    pollTimer = setInterval(() => {
      if (!isRouteCurrent(routeSeq)) {
        stopAll();
        return;
      }
      void fetchOnce();
    }, OPS_POLL_INTERVAL_MS);
    try {
      window.__opsDashPoll = pollTimer;
    } catch (_) {}
  };

  const startStream = async () => {
    stopAll();
    setConn("offline");
    const tok = getToken();
    streamAc = new AbortController();
    const ac = streamAc;
    try {
      window.__opsDashAbort = ac;
    } catch (_) {}
    const url = `${apiBase()}${buildAdminOpsGlobalMetricsStreamPath({
      interval_ms: OPS_SSE_INTERVAL_MS,
      scope: metricsQueryScope,
    })}`;
    const hdrs = {
      Accept: "text/event-stream",
      "Cache-Control": "no-store",
    };
    if (tok) hdrs.Authorization = `Bearer ${tok}`;
    try {
      const r = await fetch(url, {
        method: "GET",
        credentials: "include",
        headers: hdrs,
        signal: ac.signal,
      });
      if (!isRouteCurrent(routeSeq)) return;
      if (!r.ok) {
        const t = await r.text().catch(() => "");
        throw new Error(t || String(r.status));
      }
      if (!r.body || typeof r.body.getReader !== "function") {
        throw new Error("stream unsupported");
      }
      setConn("sse");
      await pumpSseBody(r.body.getReader(), ac.signal, (kind, payload) => {
        if (!isRouteCurrent(routeSeq)) {
          try {
            ac.abort();
          } catch (_) {}
          return;
        }
        if (kind === "ping") return;
        try {
          const data = JSON.parse(payload);
          scheduleApply(data || {});
        } catch (_) {}
      });
      if (!isRouteCurrent(routeSeq)) return;
      if (!ac.signal.aborted) {
        setConn("offline");
        startPolling();
      }
    } catch (e) {
      if (e && e.name === "AbortError") return;
      if (!isRouteCurrent(routeSeq)) return;
      setConn("offline");
      try {
        if (typeof toast === "function") toast(String(e && e.message ? e.message : e), "err");
      } catch (_) {}
      startPolling();
    }
  };

  const OPS_LOG_TAIL = 200;

  const renderFcmHealthPack = (pack) => {
    const body = $("#opsFcmHealthBody", view);
    if (!body) return;
    const flags = (pack && pack.flags) || {};
    const bindings = (pack && pack.bindings) || {};
    const ctr = (pack && pack.counters) || {};
    const aux = (pack && pack.topic_aux_queue) || {};
    const esc = escapeHtml;
    const flagOnOff = (b) => (b === true ? "on" : b === false ? "off" : "—");
    const softBindState = (raw) => {
      const s = String(raw || "").trim().toLowerCase();
      if (s === "active" || s === "ok" || s === "ready") return "Active";
      if (s === "pending" || s === "stale") return "Updating";
      if (s === "error" || s === "failed") return "Needs attention";
      if (s === "disabled" || s === "revoked") return "Off";
      return s ? s.replace(/_/g, " ") : "—";
    };
    const byState = bindings.bindings_by_state || {};
    const statePairs = Object.keys(byState)
      .sort()
      .map((k) => [softBindState(k), String(byState[k])]);
    const rowsBind = [
      ["Device links", String(bindings.bindings_total != null ? bindings.bindings_total : "—")],
      ["With errors", String(bindings.bindings_with_last_error_nonnull != null ? bindings.bindings_with_last_error_nonnull : "—")],
      ["Link version (min)", String(bindings.binding_version_min != null ? bindings.binding_version_min : "—")],
      ["Link version (max)", String(bindings.binding_version_max != null ? bindings.binding_version_max : "—")],
      ...statePairs,
    ];
    const rowsFlag = [
      ["Group alerts", flagOnOff(flags.FCM_TOPIC_SEND_ENABLED)],
      ["Direct to device", flagOnOff(flags.FCM_TOKEN_FANOUT_FALLBACK)],
      ["Device binding update", flagOnOff(flags.FCM_TOPIC_RECONCILE_ENABLED)],
      ["Delivery paused", flagOnOff(flags.FCM_ENQUEUE_PAUSED)],
    ];
    const n = (key) => {
      const x = Number(ctr[key]);
      return Number.isFinite(x) ? x : 0;
    };
    const rowsCtr = [
      ["Sent ok", n("fcm_send_ok_topic") + n("fcm_send_ok_token")],
      ["Send fail", n("fcm_send_fail_topic") + n("fcm_send_fail_token")],
      ["Extra queue depth", aux.queue_depth != null ? String(aux.queue_depth) : "—"],
      ["Extra queue capacity", aux.queue_capacity != null ? String(aux.queue_capacity) : "—"],
      ["Background service", aux.worker_alive === true ? "up" : aux.worker_alive === false ? "down" : "—"],
    ];
    const col = (title, pairs) => {
      const inner = pairs
        .map(([lab, val]) => `<div class="ops-fcm-mig-row"><dt>${esc(lab)}</dt><dd class="mono">${esc(String(val))}</dd></div>`)
        .join("");
      return `<div class="ops-fcm-mig-col">
        <p class="muted ops-fcm-mig-col-title">${esc(title)}</p>
        <dl class="ops-fcm-mig-dl">${inner || `<div class="ops-fcm-mig-row"><dt>—</dt><dd class="mono">—</dd></div>`}</dl>
      </div>`;
    };
    body.innerHTML = `<div class="ops-fcm-mig-cols">
      ${col("Device links", rowsBind)}
      ${col("Status", rowsFlag)}
      ${col("Counts", rowsCtr)}
    </div>`;
  };

  const loadFcmHealth = async () => {
    const body = $("#opsFcmHealthBody", view);
    const btn = $("#opsFcmHealthRefresh", view);
    if (btn) btn.disabled = true;
    try {
      const pack = await api(buildGetAdminFcmTopicMigrationHealthPath(), {
        timeoutMs: OPS_METRICS_CONSOLE_TIMEOUT_MS,
      });
      if (!isRouteCurrent(routeSeq)) return;
      renderFcmHealthPack(pack || {});
    } catch (e) {
      if (!isRouteCurrent(routeSeq)) return;
      const msg = typeof softApiError === "function" ? softApiError(e) : String(e && e.message ? e.message : e);
      if (body) {
        body.innerHTML = `<p class="muted">${escapeHtml(msg)}</p>
          <p class="muted">Requires Platform health access (platform staff).</p>`;
      }
    } finally {
      if (btn) btn.disabled = false;
    }
  };

  const loadLogTail = async () => {
    const body = $("#opsLogTailBody", view);
    const btn = $("#opsLogTailRefresh", view);
    if (btn) btn.disabled = true;
    try {
      const pack = await api(buildGetLogsFilePath({ tail: OPS_LOG_TAIL }), {
        timeoutMs: OPS_METRICS_CONSOLE_TIMEOUT_MS,
      });
      if (!isRouteCurrent(routeSeq)) return;
      const lines = Array.isArray(pack && pack.lines) ? pack.lines : [];
      const text = lines.length
        ? lines.map((ln) => escapeHtml(String(ln))).join("\n")
        : "(empty — log file missing or no lines)";
      if (body) {
        body.innerHTML = `<p class="muted">${lines.length} recent line${lines.length === 1 ? "" : "s"}</p>
          <pre class="ops-log-pre" style="max-height:280px;overflow:auto;white-space:pre-wrap;font-size:11px;margin:0;padding:10px 12px;border-radius:12px;background:color-mix(in srgb, var(--bg-muted) 88%, transparent)">${text}</pre>`;
      }
    } catch (e) {
      if (!isRouteCurrent(routeSeq)) return;
      const msg = String(e && e.message ? e.message : e);
      const denied = /403|forbidden|sa_security|capability/i.test(msg);
      if (body) {
        body.innerHTML = `<p class="badge offline">${escapeHtml(denied ? "Not available for this account" : "Could not load notes")}</p>
          <p class="muted">${denied
            ? "Requires security staff access."
            : escapeHtml(msg)}</p>`;
      }
    } finally {
      if (btn) btn.disabled = false;
    }
  };

  const fcmHealthBtn = $("#opsFcmHealthRefresh", view);
  if (fcmHealthBtn) fcmHealthBtn.onclick = () => void loadFcmHealth();
  const logTailBtn = $("#opsLogTailRefresh", view);
  if (logTailBtn) logTailBtn.onclick = () => void loadLogTail();
  void loadFcmHealth();
  // Do not auto-fetch log dump — operator expands + Refresh when needed.
  const logFold = $("#opsLogFold", view);
  if (logFold) {
    logFold.addEventListener("toggle", () => {
      if (logFold.open) void loadLogTail();
    });
  }
  scheduleRouteTicker(routeSeq, "ops-wall-clock", () => {
    if (!isRouteCurrent(routeSeq)) return;
    const clk = $("#opsWallClock", view);
    if (!clk) return;
    try {
      clk.textContent = new Date().toLocaleTimeString(undefined, { hour12: false });
    } catch (_) {}
  }, 1000);
  try {
    const clk0 = $("#opsWallClock", view);
    if (clk0) clk0.textContent = new Date().toLocaleTimeString(undefined, { hour12: false });
  } catch (_) {}

  const restart = () => void startStream();

  window.addEventListener(
    "resize",
    () => {
      if (!isRouteCurrent(routeSeq)) return;
      redrawOpsSparkGrid($("#opsSparksGrid", view), hist, sparkSpecs);
    },
    { passive: true },
  );

  restart();
});


// ── orchestration.route.js ─────────────────────────────────────
/**
 * Route: #/orchestration — Incident cell / cluster / stigmergy + actuator registry.
 */

function softOrchMemberLabel(raw) {
  const rid = String(raw || "").trim();
  if (!rid || rid === "—" || rid === "unassigned") return rid === "unassigned" ? "Unassigned" : "—";
  try {
    if (typeof softDeviceLabel === "function") {
      const soft = softDeviceLabel(rid);
      if (soft && soft !== "—" && !/^Unit ·/i.test(soft)) return soft;
    }
  } catch (_) {}
  if (/^SN-/i.test(rid)) return "Device unit";
  if (rid.length > 28) return `${rid.slice(0, 24)}…`;
  return rid;
}

function softOrchRole(raw) {
  const r = String(raw || "member").trim().toLowerCase();
  const map = {
    member: "Member",
    lead: "Lead",
    responder: "Responder",
    device: "Device",
    actuator: "Equipment",
    robot: "Field robot",
  };
  if (map[r]) return map[r];
  return r.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function softOrchState(raw) {
  const s = String(raw || "active").trim().toLowerCase();
  const map = {
    active: "Active",
    vacated: "Left",
    pending: "Pending",
    ready: "Ready",
    offline: "Offline",
  };
  if (map[s]) return map[s];
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function softOrchCellState(raw) {
  const s = String(raw || "").trim().toLowerCase();
  const map = {
    forming: "Forming",
    active: "Active",
    ready: "Ready",
    resolved: "Closed",
    closed: "Closed",
    vacated: "Left",
    pending: "Pending",
  };
  if (map[s]) return map[s];
  if (!s || s === "—") return "—";
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Normalize AI advisory swarm_state into the cell/cluster card shape. */
function bundleFromAiSwarmState(swarmState, meta = {}) {
  const ss = swarmState && typeof swarmState === "object" ? swarmState : {};
  const membership = Array.isArray(ss.incident_cluster_membership)
    ? ss.incident_cluster_membership.map((m) => ({
      resource_id: m.resource_id,
      role: (Array.isArray(ss.incident_role_allocations)
        ? (ss.incident_role_allocations.find((a) => a.resource_id === m.resource_id) || {}).role
        : null) || "member",
      state: m.state || "joined",
    }))
    : [];
  const roleAllocs = Array.isArray(ss.incident_role_allocations)
    ? ss.incident_role_allocations
    : [];
  return {
    cell: {
      state: ss.incident_cell_state || meta.cell_state || "forming",
      status: ss.incident_cell_state || meta.cell_state || "forming",
      id: ss.incident_cell_id || "",
    },
    cluster: {
      state: ss.incident_cell_state || "forming",
      priority: meta.priority || ss.incident_cluster_profile || "—",
      membership,
      role_allocations: roleAllocs,
      profile: ss.incident_cluster_profile || "",
    },
    from_advisory: true,
  };
}

function buildSwarmMemberCardsHtml(bundle) {
  const cluster = (bundle && bundle.cluster) || {};
  const cell = (bundle && bundle.cell) || {};
  const members = Array.isArray(cluster.membership)
    ? cluster.membership
    : (Array.isArray(cluster.members) ? cluster.members : []);
  const roleAllocs = Array.isArray(cluster.role_allocations) ? cluster.role_allocations : [];
  const cards = [];
  if (members.length) {
    for (const m of members) {
      const rid = softOrchMemberLabel(m.resource_id || m.member_id || m.id || "—");
      const role = softOrchRole(m.role || m.assigned_role || "member");
      const state = softOrchState(m.state || m.status || "active");
      cards.push(`<article class="glass-panel glass-panel--inset swarm-member-card" style="padding:12px 16px;margin-bottom:12px">
        <div class="row" style="justify-content:space-between;gap:8px;flex-wrap:wrap">
          <strong>${escapeHtml(rid)}</strong>
          <span class="badge ok">${escapeHtml(role)}</span>
        </div>
        <p class="muted" style="font-size:12px;margin-top:4px">${escapeHtml(state)}</p>
      </article>`);
    }
  } else if (roleAllocs.length) {
    for (const a of roleAllocs) {
      cards.push(`<article class="glass-panel glass-panel--inset swarm-member-card" style="padding:12px 16px;margin-bottom:12px">
        <div class="row" style="justify-content:space-between;gap:8px;flex-wrap:wrap">
          <strong>${escapeHtml(softOrchRole(a.role || "role"))}</strong>
          <span class="badge ${String(a.state || "") === "vacated" ? "offline" : "ok"}">${escapeHtml(softOrchState(a.state || "active"))}</span>
        </div>
        <p class="muted" style="font-size:12px;margin-top:4px">${escapeHtml(softOrchMemberLabel(a.resource_id || "unassigned"))}</p>
      </article>`);
    }
  }
  const cellState = softOrchCellState(cell.state || cell.status || cluster.state || "—");
  const priorityRaw = String(cluster.priority || cell.priority || "—");
  const priority = priorityRaw === "intrusion" ? "Intrusion" : softOrchCellState(priorityRaw);
  const header = `<div class="stats orchestration-status-card" style="margin-bottom:12px">
    <div class="stat glass-panel glass-panel--inset"><span class="k">Response unit</span><strong class="v">${escapeHtml(cellState)}</strong></div>
    <div class="stat glass-panel glass-panel--inset"><span class="k">Priority</span><strong class="v">${escapeHtml(priority)}</strong></div>
    <div class="stat glass-panel glass-panel--inset"><span class="k">Members</span><strong class="v">${escapeHtml(String(members.length || roleAllocs.length))}</strong></div>
  </div>`;
  const advisoryNote = bundle && bundle.from_advisory
    ? `<p class="muted" style="font-size:12px;margin:0 0 12px">Loaded from the latest advisory team plan. You can still start or adjust the live response below.</p>`
    : "";
  if (!cards.length) {
    return `${header}${advisoryNote}<p class="muted">No team members loaded yet — pick an incident and press Load team, or open Auto Sentinel to form a plan.</p>`;
  }
  return `${header}${advisoryNote}<div class="swarm-member-cards">${cards.join("")}</div>`;
}

function buildActuatorSelectCardHtml(selected) {
  const s = selected || {};
  if (s.error) {
    return `<p class="muted">${escapeHtml(String(s.error))}</p>`;
  }
  const kind = softOrchRole(s.kind || s.resource_kind || s.selected_kind || "—");
  const status = softOrchState(s.status || s.result || "selected");
  const resourceId = softOrchMemberLabel(s.resource_id || s.selected_resource_id || "—");
  const dryRun = s.dry_run === true || s.mode === "dry_run";
  return `<div class="glass-panel glass-panel--inset orchestration-status-card" style="padding:16px;margin-top:12px">
    ${dryRun ? '<span class="badge neutral">Practice</span>' : '<span class="badge ok">Live equipment list</span>'}
    <p style="margin-top:8px"><strong>${escapeHtml(kind)}</strong> · ${escapeHtml(status)}</p>
    <p class="muted" style="font-size:12px">${escapeHtml(resourceId)}</p>
  </div>`;
}

registerRoute("orchestration", async (view, _args, routeSeq) => {
  setCrumb("Response team");
  if (!hasRole("admin")) {
    mountView(view, workspaceBlockedHtml({
      title: "Response team",
      detail: "Team coordination opens with an admin account. Your field steps stay on Assignments and Home — Accept → On the way → On site.",
      backHref: "#/response",
      backLabel: "Open my field queue",
      innovKind: "field",
      loopActiveId: "people",
      loopTitle: "Team desk closed",
      loopLede: "Assignments and Home still run your living response steps.",
      extraActions: [
        { href: "#/response", label: "My assignments" },
        { href: "#/incidents", label: "Incidents", ghost: true },
      ],
    }));
    return;
  }

  const swarmOn = catalogEnabled(state.me, "swarm");
  const actuatorsOn = catalogEnabled(state.me, "actuators");
  const robotsOn = catalogEnabled(state.me, "external_robots");
  // Always keep Response team open for verify — tabs soft-gate individually.
  const params = new URLSearchParams((location.hash.split("?")[1] || ""));
  const eventId = String(params.get("event_id") || "").trim();
  let tab = String(params.get("tab") || (swarmOn ? "cell" : robotsOn ? "robots" : actuatorsOn ? "actuators" : "robots")).trim();
  if (tab === "swarm") tab = "cell";
  if (!["cell", "cluster", "stigmergy", "actuators", "robots"].includes(tab)) tab = "cell";
  const facetQ = eventId ? `&event_id=${encodeURIComponent(eventId)}` : "";

  const tabsHtml = `
    <nav class="croc-stage-tabs croc-stage-tabs--facets orchestration-tabs" aria-label="Tonight's loop">
      <p class="croc-stage-tabs__k">This loop</p>
      <div class="croc-stage-tabs__row">
        <button type="button" class="btn sm ${tab === "cell" ? "" : "secondary"} btn-tap" data-cc-go="orchestration?tab=cell${facetQ}" aria-current="${tab === "cell" ? "page" : "false"}">Who</button>
        <button type="button" class="btn sm ${tab === "cluster" ? "" : "secondary"} btn-tap" data-cc-go="orchestration?tab=cluster${facetQ}" aria-current="${tab === "cluster" ? "page" : "false"}">Team</button>
        <button type="button" class="btn sm ${tab === "actuators" ? "" : "secondary"} btn-tap" data-cc-go="orchestration?tab=actuators${facetQ}" aria-current="${tab === "actuators" ? "page" : "false"}">Equipment</button>
        <button type="button" class="btn sm ${tab === "robots" ? "" : "secondary"} btn-tap" data-cc-go="orchestration?tab=robots${facetQ}" aria-current="${tab === "robots" ? "page" : "false"}">Field robots</button>
      </div>
    </nav>`;

  // One Layer-1 strip. Hop chrome lives in buildTonightStageStripHtml.
  const wrapOrch = (title, lede, bodyHtml) => `
    <div class="croc-desk-stack" data-orchestration="1">
      ${typeof buildTonightStageStripHtml === "function" ? buildTonightStageStripHtml({ stage: "during", active: "loop", eventId }) : ""}
      <div class="card glass-panel glass-panel--panel orchestration-workspace orchestration-page">
        <h2>${escapeHtml(title)}</h2>
        ${lede ? `<p class="muted orchestration-workspace__lede">${lede}</p>` : ""}
        ${tabsHtml}
        ${bodyHtml}
      </div>
    </div>`;

  mountView(view, `<div class="card glass-panel glass-panel--panel"><p class="muted">Loading response team…</p></div>`);

  try {
    if (tab === "robots") {
      const cmdsOn = catalogEnabled(state.me, "external_robot_commands");
      const role = String((state.me && state.me.role) || "");
      const isTenantAdmin = role === "admin";
      let units = [];
      let integrations = [];
      let catalogNote = "";
      let loadNote = "";
      if (isTenantAdmin && robotsOn) {
        try {
          const [uPack, iPack] = await Promise.all([
            api(buildGetExternalRobotsUnitsPath(), { timeoutMs: SRF_CONCEPTS_TIMEOUT_MS }),
            api(buildGetExternalRobotsIntegrationsPath(), { timeoutMs: SRF_CONCEPTS_TIMEOUT_MS }),
          ]);
          if (!isRouteCurrent(routeSeq)) return;
          units = Array.isArray(uPack && uPack.items) ? uPack.items : (Array.isArray(uPack) ? uPack : []);
          integrations = Array.isArray(iPack && iPack.items) ? iPack.items : (Array.isArray(iPack) ? iPack : []);
        } catch (e) {
          loadNote = softApiError(e, "Field robot registry could not be loaded yet.");
        }
        try {
          await api(buildGetExternalRobotsCatalogPath({}), { timeoutMs: SRF_CONCEPTS_TIMEOUT_MS });
        } catch (e) {
          catalogNote = softApiError(e, "");
        }
      } else if (!robotsOn) {
        loadNote = "Field robots stay visible for verification. The live equipment list opens when this capability is turned on.";
      } else {
        loadNote = "Field robot registry is managed by an organization admin account. Platform staff can verify the concept here; day-to-day registration uses admin.";
      }

      const unitCards = units.map((u) => {
        const name = String(u.label || u.name || u.unit_id || u.id || "Field unit");
        const vendor = softRobotVendorLabel(u.vendor || u.vendor_key || u.kind);
        const statusRaw = String(u.status || u.state || "unknown");
        const statusSoft = statusRaw === "unknown" ? "Status unknown" : statusRaw.replace(/_/g, " ");
        const statusCls = /ready|online|active/i.test(statusRaw) ? "ok" : "neutral";
        return `<article class="glass-panel glass-panel--inset" style="padding:12px">
          <div class="row" style="justify-content:space-between;gap:8px;flex-wrap:wrap">
            <strong>${escapeHtml(name)}</strong>
            <span class="badge ${statusCls}">${escapeHtml(statusSoft)}</span>
          </div>
          <p class="muted" style="margin:6px 0 0;font-size:12px">${escapeHtml(vendor)}</p>
        </article>`;
      }).join("");

      const integCards = integrations.map((it) => {
        const label = String(it.label || it.name || it.integration_id || it.id || "Connection");
        const vendor = softRobotVendorLabel(it.vendor || it.vendor_key);
        return `<article class="glass-panel glass-panel--inset" style="padding:12px">
          <strong>${escapeHtml(label)}</strong>
          <p class="muted" style="margin:6px 0 0;font-size:12px">${escapeHtml(vendor)}</p>
        </article>`;
      }).join("");

      mountView(view, wrapOrch(
        "Field robots",
        "Register and review field robots for coordinated response. Commands stay off until dual-control approvals are ready.",
        `${loadNote ? `<p class="muted" style="margin-top:8px">${escapeHtml(loadNote)}</p>` : ""}
        <div class="glass-panel glass-panel--inset" style="padding:16px;margin-top:8px">
          <p style="margin:0 0 8px"><strong>Registered units</strong></p>
          <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:10px">
            ${unitCards || `<p class="muted" style="margin:0;line-height:1.45">No field robots registered yet. When units are commissioned, they appear here with status and readiness.</p>`}
          </div>
        </div>
        <div class="glass-panel glass-panel--inset" style="padding:16px;margin-top:12px">
          <p style="margin:0 0 8px"><strong>Vendor connections</strong></p>
          <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:10px">
            ${integCards || `<p class="muted" style="margin:0">No vendor connections yet.</p>`}
          </div>
          ${isTenantAdmin && robotsOn ? `<div class="inline-form" style="margin-top:12px">
            <label class="field"><span>Connection name</span><input id="robotIntName" placeholder="Warehouse air unit link" autocomplete="off" /></label>
            <label class="field"><span>Kind</span>
              <select id="robotIntVendor"><option value="dji">Air unit</option><option value="unitree">Ground unit</option></select>
            </label>
          </div>
          <div class="row" style="margin-top:8px"><button class="btn btn-tap" type="button" id="robotIntBtn">Add connection</button></div>
          <p id="robotIntMsg" class="muted" aria-live="polite"></p>` : ""}
        </div>
        <div class="glass-panel glass-panel--inset" style="padding:16px;margin-top:12px">
          <p style="margin:0 0 8px"><strong>Commands</strong>
            <span class="badge ${cmdsOn ? "neutral" : "offline"}" style="margin-left:8px">${cmdsOn ? "Approvals open" : "Commands closed"}</span>
          </p>
          <p class="muted" style="margin:0;line-height:1.45">${cmdsOn
            ? "Approved robot actions still require dual-control confirmation before anything is sent."
            : "Robot commands stay closed until approvals and dual-control are opened for your organization."}</p>
        </div>
        ${catalogNote ? `<p class="muted" style="margin-top:10px;font-size:12px">${escapeHtml(catalogNote)}</p>` : ""}`,
      ));

      const intBtn = $("#robotIntBtn", view);
      if (intBtn) {
        intBtn.onclick = async () => {
          const name = String(($("#robotIntName", view) && $("#robotIntName", view).value) || "").trim();
          const vendor = String(($("#robotIntVendor", view) && $("#robotIntVendor", view).value) || "dji").trim();
          if (!name) {
            ($("#robotIntMsg", view) || {}).textContent = "Connection name is required.";
            return;
          }
          if (!(await softConfirm(`Add ${softRobotVendorLabel(vendor)} connection “${name}”?`))) return;
          intBtn.disabled = true;
          try {
            await api(buildPostExternalRobotsIntegrationsPath(), {
              method: "POST",
              body: { label: name, name, vendor },
              timeoutMs: SRF_CONCEPTS_TIMEOUT_MS,
            });
            toast("Connection added", "ok");
            location.hash = `#/orchestration?tab=robots&_=${Date.now()}`;
          } catch (e) {
            const soft = softApiError(e);
            ($("#robotIntMsg", view) || {}).textContent = soft;
            toast(soft, "err");
            intBtn.disabled = false;
          }
        };
      }
      return;
    }
    if (tab === "actuators") {
      if (!actuatorsOn) {
        mountView(view, wrapOrch(
          "Equipment",
          "",
          `<div class="glass-panel glass-panel--inset" style="padding:16px;margin-top:8px">
            <span class="badge neutral">Practice</span>
            <p style="margin:10px 0 0;line-height:1.45">Connected response equipment — sirens, cameras, and field gear — will list here when equipment registry is opened for verification.</p>
          </div>`,
        ));
        return;
      }
      const pack = await api(buildGetActuatorResourcesPath(), { timeoutMs: ORCHESTRATION_TIMEOUT_MS });
      if (!isRouteCurrent(routeSeq)) return;
      const items = Array.isArray(pack.items) ? pack.items : [];
      const rows = items.map((it) => {
        const kind = softAdapterKindLabel(it.kind || it.resource_kind);
        const status = String((it.status && it.status.status) || it.status || "—");
        const caps = Array.isArray(it.capabilities) ? it.capabilities.map((c) => String(c).replace(/_/g, " ")).join(", ") : String(it.capabilities || "—");
        return `<tr>
        <td>${escapeHtml(kind)}</td>
        <td>${escapeHtml(status)}</td>
        <td>${escapeHtml(caps)}</td>
      </tr>`;
      }).join("");
      mountView(view, wrapOrch(
        "Equipment",
        "Connected response equipment — type, status, and what each unit can do.",
        `<p class="muted">${escapeHtml(String(pack.count != null ? pack.count : items.length))} registered</p>
        <table class="table"><thead><tr><th>Type</th><th>Status</th><th>Capabilities</th></tr></thead>
          <tbody>${rows || `<tr><td colspan="3" class="muted">No response equipment registered yet.</td></tr>`}</tbody></table>
        <div class="glass-panel glass-panel--inset" style="padding:12px;margin-top:14px">
          <strong>Practice selection</strong>
          <p class="muted" style="margin-top:4px"><span class="badge neutral">Practice</span> — tests registry selection without commanding equipment.</p>
          <div class="inline-form" style="margin-top:8px">
            <label class="field"><span>Alarm reference</span><input id="actAlarmId" /></label>
            <label class="field"><span>Device code</span><input id="actDeviceId" /></label>
            <label class="field"><span>Risk score</span><input id="actRisk" type="number" value="50" /></label>
            <label class="field"><span>Reason</span><input id="actReason" value="operator practice" /></label>
          </div>
          <div class="row" style="margin-top:8px"><button class="btn btn-tap" type="button" id="actSelectBtn">Practice select</button></div>
          <div id="actSelectOut"></div>
        </div>`,
      ));
      const btn = $("#actSelectBtn", view);
      if (btn) {
        btn.onclick = async () => {
          const alarmId = String(($("#actAlarmId", view) && $("#actAlarmId", view).value) || "").trim();
          const deviceId = String(($("#actDeviceId", view) && $("#actDeviceId", view).value) || "").trim();
          const risk = Number(($("#actRisk", view) && $("#actRisk", view).value) || 0);
          const reason = String(($("#actReason", view) && $("#actReason", view).value) || "").trim();
          if (!(await softConfirm("Practice equipment selection against the live registry?"))) return;
          btn.disabled = true;
          try {
            const selected = await api(buildPostActuatorSelectPath(), {
              method: "POST",
              body: {
                alarm_id: alarmId || undefined,
                device_id: deviceId || undefined,
                risk_score: risk,
                reason: reason || undefined,
                dry_run: true,
              },
              timeoutMs: ORCHESTRATION_TIMEOUT_MS,
            });
            if (!isRouteCurrent(routeSeq)) return;
            const out = $("#actSelectOut", view);
            if (out) out.innerHTML = buildActuatorSelectCardHtml(Object.assign({ dry_run: true }, selected));
            toast("Practice complete", "ok");
          } catch (e) {
            toast(typeof softApiError === "function" ? softApiError(e) : String(e.message || e), "err");
          } finally {
            btn.disabled = false;
          }
        };
      }
      return;
    }

    if (!swarmOn) {
      mountView(view, wrapOrch(
        "Response team",
        "",
        `<div class="glass-panel glass-panel--inset" style="padding:16px;margin-top:12px">
          <span class="badge neutral">Practice</span>
          <p style="margin:10px 0 0;line-height:1.45">Coordinated response forms a live team around an incident — members, priority, and shared notes. Open Field robots or Equipment from the tabs above.</p>
          <div class="row" style="margin-top:12px;gap:8px;flex-wrap:wrap">
            <button type="button" class="btn secondary btn-tap" data-cc-go="orchestration?tab=robots${facetQ}">Field robots</button>
            <button type="button" class="btn secondary btn-tap" data-cc-go="orchestration?tab=actuators${facetQ}">Equipment</button>
            <button type="button" class="btn secondary btn-tap" data-cc-go="response">Assignments</button>
          </div>
        </div>`,
      ));
      return;
    }

    let recentInc = [];
    try {
      const pack = await api("/incidents?limit=12", { timeoutMs: ORCHESTRATION_TIMEOUT_MS });
      recentInc = Array.isArray(pack && pack.items) ? pack.items : [];
    } catch (_) { recentInc = []; }
    const pickOpts = (() => {
      // Prefer AI/swarm incident_id (ai-inc-…). Fall back to event id; loadBundle resolves digits.
      const swarmKey = (it) =>
        String((it && (it.incident_id || it.swarm_incident_id)) || "").trim();
      const displayKey = (it) => swarmKey(it) || String((it && it.event_id) || "").trim();
      const rows = recentInc.filter((it) => it && displayKey(it));
      const opts = rows.map((it) => {
        const eid = displayKey(it);
        const label = typeof softIncidentPickerLabel === "function"
          ? softIncidentPickerLabel(it)
          : softOperatorNote(String(it.summary || eid));
        const sel = String(eventId) === eid || String(eventId) === String(it.event_id || "") || String(eventId) === swarmKey(it) ? " selected" : "";
        return `<option value="${escapeHtml(eid)}"${sel}>${escapeHtml(label)}</option>`;
      });
      if (eventId && !rows.some((it) => displayKey(it) === String(eventId) || String(it.event_id || "") === String(eventId) || swarmKey(it) === String(eventId))) {
        if (/^ai-inc-/i.test(String(eventId)) || !/^\d+$/.test(String(eventId))) {
          opts.unshift(`<option value="${escapeHtml(eventId)}" selected>Selected incident</option>`);
        }
      }
      if (!opts.length) opts.push(`<option value="">Choose a recent alarm…</option>`);
      return opts.join("");
    })();
    mountView(view, wrapOrch(
      "Response team",
      "Coordinate members, priority, and shared notes for a live incident. People accept on Assignments.",
      `<div class="inline-form">
        <label class="field wide"><span>Incident</span>
          <select id="orchEventId">${pickOpts}</select>
        </label>
      </div>
      <div class="row" style="margin-top:12px;gap:8px;flex-wrap:wrap">
        <button class="btn btn-tap" type="button" id="orchLoadBtn">Load team</button>
        <button class="btn secondary btn-tap" type="button" id="orchActivateBtn">Start response</button>
        <button class="btn secondary btn-tap" type="button" id="orchResolveBtn">Close response</button>
        <button type="button" class="btn secondary btn-tap" id="orchIncidentLink" data-cc-go="incidents"${eventId ? ` data-cc-id="${escapeHtml(eventId)}"` : ""}>Open incident</button>
      </div>
      <div class="glass-panel glass-panel--inset" style="padding:16px;margin-top:16px">
        <strong>Team membership</strong>
        <div class="inline-form" style="margin-top:12px">
          <label class="field"><span>Unit or person</span><input id="orchResourceId" placeholder="Unit name or account" autocomplete="off" /></label>
          <label class="field"><span>Member type</span>
            <select id="orchResourceKind">
              <option value="device" selected>Device</option>
              <option value="responder">Responder</option>
              <option value="actuator">Equipment</option>
              <option value="robot">Field robot</option>
            </select>
          </label>
          <label class="field grow"><span>Capabilities</span><input id="orchCaps" placeholder="loud alert, photo" autocomplete="off" /></label>
          <label class="field"><span>Priority</span>
            <select id="orchPriority">
              <option value="">—</option>
              <option value="high">High</option>
              <option value="normal">Normal</option>
              <option value="low">Low</option>
            </select>
          </label>
          <label class="field grow"><span>Shared note</span><input id="orchStigType" placeholder="Status update" autocomplete="off" /></label>
        </div>
        <div class="row" style="margin-top:12px;gap:8px;flex-wrap:wrap">
          <button class="btn secondary btn-tap" type="button" id="orchJoinBtn">Join</button>
          <button class="btn secondary btn-tap" type="button" id="orchLeaveBtn">Leave</button>
          <button class="btn secondary btn-tap" type="button" id="orchReallocBtn">Reassign</button>
          <button class="btn secondary btn-tap" type="button" id="orchPriorityBtn">Set priority</button>
          <button class="btn secondary btn-tap" type="button" id="orchStigBtn">Add note</button>
        </div>
      </div>
      <p id="orchMsg" class="muted" aria-live="polite"></p>
      <div id="orchOut" style="margin-top:12px"></div>`,
    ));

    const swarmKeyFromRow = (it) =>
      String((it && (it.incident_id || it.swarm_incident_id)) || "").trim();

    const resolveSwarmIncidentId = async (rawId) => {
      const raw = String(rawId || "").trim();
      if (!raw) return "";
      if (/^ai-inc-/i.test(raw)) return raw;
      if (!/^\d+$/.test(raw)) return raw;
      try {
        if (typeof buildGetAiIncidentsPath !== "function") return "";
        const list = await api(buildGetAiIncidentsPath(), { timeoutMs: ORCHESTRATION_TIMEOUT_MS });
        const items = Array.isArray(list && list.items) ? list.items : [];
        const hit =
          items.find((it) => String(it.event_id || "") === raw && swarmKeyFromRow(it)) ||
          items.find((it) => swarmKeyFromRow(it) === raw);
        return hit ? swarmKeyFromRow(hit) : "";
      } catch (_) {
        return "";
      }
    };

    const loadBundle = async (id) => {
      let swarmId = String(id || "").trim();
      if (swarmId && /^\d+$/.test(swarmId)) {
        const resolved = await resolveSwarmIncidentId(swarmId);
        if (resolved) {
          swarmId = resolved;
          const sel = $("#orchEventId", view);
          if (sel && String(sel.value || "").trim() === String(id)) {
            // Prefer resolved swarm key so subsequent actions hit the right route.
            const hasOpt = Array.from(sel.options || []).some((o) => o.value === swarmId);
            if (!hasOpt) {
              const opt = document.createElement("option");
              opt.value = swarmId;
              opt.textContent = swarmId;
              sel.appendChild(opt);
            }
            sel.value = swarmId;
          }
        } else {
          // Soft skip — do not spam /cell|/cluster with raw event ids (404 noise).
          try {
            if (typeof buildGetAiIncidentsPath === "function") {
              const list = await api(buildGetAiIncidentsPath(), { timeoutMs: ORCHESTRATION_TIMEOUT_MS });
              const items = Array.isArray(list && list.items) ? list.items : [];
              const hit = items.find((it) => String(it.event_id || "") === String(id) && it.swarm_state);
              if (hit && hit.swarm_state) {
                return bundleFromAiSwarmState(hit.swarm_state, { priority: hit.category || "" });
              }
            }
          } catch (_) { /* advisory optional */ }
          return {
            cell: { error: "swarm_incident_id_required" },
            cluster: { error: "swarm_incident_id_required" },
            affinity: {},
            stigmergy: {},
            audit: {},
          };
        }
      }
      const [cell, cluster, affinity, stigmergy, audit] = await Promise.all([
        api(buildGetIncidentCellPath(swarmId), { timeoutMs: ORCHESTRATION_TIMEOUT_MS }).catch((e) => ({ error: typeof softApiError === "function" ? softApiError(e) : String(e.message || e) })),
        api(buildGetIncidentClusterPath(swarmId), { timeoutMs: ORCHESTRATION_TIMEOUT_MS }).catch((e) => ({ error: typeof softApiError === "function" ? softApiError(e) : String(e.message || e) })),
        api(buildGetIncidentClusterAffinityPath(swarmId), { timeoutMs: ORCHESTRATION_TIMEOUT_MS }).catch((e) => ({ error: typeof softApiError === "function" ? softApiError(e) : String(e.message || e) })),
        api(buildGetIncidentStigmergyPath(swarmId), { timeoutMs: ORCHESTRATION_TIMEOUT_MS }).catch((e) => ({ error: typeof softApiError === "function" ? softApiError(e) : String(e.message || e) })),
        api(buildGetIncidentSwarmAuditPath(swarmId), { timeoutMs: ORCHESTRATION_TIMEOUT_MS }).catch((e) => ({ error: typeof softApiError === "function" ? softApiError(e) : String(e.message || e) })),
      ]);
      const hasMembers =
        (cluster && Array.isArray(cluster.membership) && cluster.membership.length) ||
        (cluster && Array.isArray(cluster.role_allocations) && cluster.role_allocations.length) ||
        (cluster && Array.isArray(cluster.members) && cluster.members.length);
      if (hasMembers) return { cell, cluster, affinity, stigmergy, audit };
      // Soft fallback: advisory swarm plan when live cell/cluster is empty.
      try {
        if (typeof buildGetAiIncidentsPath === "function") {
          const list = await api(buildGetAiIncidentsPath(), { timeoutMs: ORCHESTRATION_TIMEOUT_MS });
          const items = Array.isArray(list && list.items) ? list.items : [];
          const hit = items.find((it) => String(it.event_id || "") === String(id) && it.swarm_state) ||
            items.find((it) => swarmKeyFromRow(it) === swarmId && it.swarm_state) ||
            items.find((it) => it.swarm_state && String(it.status || "") === "pending");
          if (hit && hit.swarm_state) {
            const fromAi = bundleFromAiSwarmState(hit.swarm_state, { priority: hit.category || "" });
            return Object.assign({}, fromAi, { affinity, stigmergy, audit, cell: fromAi.cell, cluster: fromAi.cluster });
          }
        }
      } catch (_) { /* advisory optional */ }
      return { cell, cluster, affinity, stigmergy, audit };
    };

    const renderBundle = (bundle) => {
      const out = $("#orchOut", view);
      if (!out) return;
      const err = (bundle.cell && bundle.cell.error) || (bundle.cluster && bundle.cluster.error);
      const hasMembers =
        (bundle.cluster && Array.isArray(bundle.cluster.membership) && bundle.cluster.membership.length) ||
        (bundle.cluster && Array.isArray(bundle.cluster.role_allocations) && bundle.cluster.role_allocations.length);
      if (err && !hasMembers) {
        out.innerHTML = `<p class="muted">No live team for this incident yet. Open Auto Sentinel to scan an alarm, or press Start response.</p>
          <div class="row" style="margin-top:12px;gap:8px;flex-wrap:wrap">
            <button type="button" class="btn secondary btn-tap" data-cc-go="overview">Home</button>
            <button type="button" class="btn secondary btn-tap" data-cc-go="incidents">Incidents</button>
          </div>`;
        return;
      }
      out.innerHTML = buildSwarmMemberCardsHtml(bundle);
    };

    const seedFromAdvisories = async () => {
      try {
        if (typeof buildGetAiIncidentsPath !== "function") return "";
        const list = await api(buildGetAiIncidentsPath(), { timeoutMs: ORCHESTRATION_TIMEOUT_MS });
        const items = Array.isArray(list && list.items) ? list.items : [];
        const hit = items.find((it) => it && it.swarm_state && swarmKeyFromRow(it)) ||
          items.find((it) => it && swarmKeyFromRow(it));
        return hit ? swarmKeyFromRow(hit) : "";
      } catch (_) {
        return "";
      }
    };

    const readEventId = () => String(($("#orchEventId", view) && $("#orchEventId", view).value) || "").trim();
    const msg = $("#orchMsg", view);

    const loadBtn = $("#orchLoadBtn", view);
    if (loadBtn) {
      loadBtn.onclick = async () => {
        const id = readEventId();
        if (!id) {
          if (msg) msg.textContent = "Enter an incident reference.";
          return;
        }
        location.hash = `#/orchestration?tab=cell&event_id=${encodeURIComponent(id)}`;
        loadBtn.disabled = true;
        if (msg) msg.textContent = "Loading…";
        try {
          const bundle = await loadBundle(id);
          if (!isRouteCurrent(routeSeq)) return;
          renderBundle(bundle);
          if (msg) msg.textContent = "Loaded.";
          const link = $("#orchIncidentLink", view);
          if (link) {
            link.setAttribute("data-cc-go", "incidents");
            link.setAttribute("data-cc-id", id);
          }
        } catch (e) {
          if (msg) msg.textContent = typeof softApiError === "function" ? softApiError(e) : String(e.message || e);
          toast(typeof softApiError === "function" ? softApiError(e) : String(e.message || e), "err");
        } finally {
          loadBtn.disabled = false;
        }
      };
    }

    const activateBtn = $("#orchActivateBtn", view);
    if (activateBtn) {
      activateBtn.onclick = async () => {
        const id = readEventId();
        if (!id) {
          if (msg) msg.textContent = "Enter an incident reference.";
          return;
        }
        if (!(await softConfirm("Start the response team for this incident?"))) return;
        activateBtn.disabled = true;
        try {
          await api(buildPostIncidentCellActivatePath(id), {
            method: "POST",
            body: {},
            timeoutMs: ORCHESTRATION_TIMEOUT_MS,
          });
          if (!isRouteCurrent(routeSeq)) return;
          const bundle = await loadBundle(id);
          renderBundle(bundle);
          toast("Response started", "ok");
        } catch (e) {
          toast(typeof softApiError === "function" ? softApiError(e) : String(e.message || e), "err");
          if (msg) msg.textContent = typeof softApiError === "function" ? softApiError(e) : String(e.message || e);
        } finally {
          activateBtn.disabled = false;
        }
      };
    }

    const resolveBtn = $("#orchResolveBtn", view);
    if (resolveBtn) {
      resolveBtn.onclick = async () => {
        const id = readEventId();
        if (!id) {
          if (msg) msg.textContent = "Enter an incident reference.";
          return;
        }
        if (!(await softConfirm("Close the response team for this incident?"))) return;
        resolveBtn.disabled = true;
        try {
          await api(buildPostIncidentCellResolvePath(id), {
            method: "POST",
            body: {},
            timeoutMs: ORCHESTRATION_TIMEOUT_MS,
          });
          if (!isRouteCurrent(routeSeq)) return;
          const bundle = await loadBundle(id);
          renderBundle(bundle);
          toast("Response closed", "ok");
        } catch (e) {
          toast(typeof softApiError === "function" ? softApiError(e) : String(e.message || e), "err");
          if (msg) msg.textContent = typeof softApiError === "function" ? softApiError(e) : String(e.message || e);
        } finally {
          resolveBtn.disabled = false;
        }
      };
    }

    const readResourceId = () => String(($("#orchResourceId", view) && $("#orchResourceId", view).value) || "").trim();
    const readResourceKind = () => String(($("#orchResourceKind", view) && $("#orchResourceKind", view).value) || "device").trim() || "device";
    const readCaps = () => String(($("#orchCaps", view) && $("#orchCaps", view).value) || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    const joinBtn = $("#orchJoinBtn", view);
    if (joinBtn) {
      joinBtn.onclick = async () => {
        const id = readEventId();
        const rid = readResourceId();
        if (!id || !rid) {
          if (msg) msg.textContent = "Enter an incident reference and a unit or device.";
          return;
        }
        if (!(await softConfirm("Add this unit or person to the response team?"))) return;
        joinBtn.disabled = true;
        try {
          const caps = readCaps();
          const pack = await api(buildPostIncidentClusterJoinPath(id), {
            method: "POST",
            body: {
              resource_id: rid,
              resource_kind: readResourceKind(),
              capabilities: caps.length ? caps : undefined,
            },
            timeoutMs: ORCHESTRATION_TIMEOUT_MS,
          });
          if (!isRouteCurrent(routeSeq)) return;
          renderBundle({ cell: {}, cluster: pack, affinity: {}, stigmergy: {}, audit: {} });
          toast("Joined the team", "ok");
        } catch (e) {
          toast(typeof softApiError === "function" ? softApiError(e) : String(e.message || e), "err");
          if (msg) msg.textContent = typeof softApiError === "function" ? softApiError(e) : String(e.message || e);
        } finally {
          joinBtn.disabled = false;
        }
      };
    }
    const leaveBtn = $("#orchLeaveBtn", view);
    if (leaveBtn) {
      leaveBtn.onclick = async () => {
        const id = readEventId();
        const rid = readResourceId();
        if (!id || !rid) {
          if (msg) msg.textContent = "Enter an incident reference and a unit or device.";
          return;
        }
        if (!(await softConfirm("Remove this unit from the response team?"))) return;
        leaveBtn.disabled = true;
        try {
          const pack = await api(buildPostIncidentClusterLeavePath(id), {
            method: "POST",
            body: { resource_id: rid },
            timeoutMs: ORCHESTRATION_TIMEOUT_MS,
          });
          if (!isRouteCurrent(routeSeq)) return;
          renderBundle({ cell: {}, cluster: pack, affinity: {}, stigmergy: {}, audit: {} });
          toast("Left the response team", "ok");
        } catch (e) {
          toast(typeof softApiError === "function" ? softApiError(e) : String(e.message || e), "err");
          if (msg) msg.textContent = typeof softApiError === "function" ? softApiError(e) : String(e.message || e);
        } finally {
          leaveBtn.disabled = false;
        }
      };
    }
    const reallocBtn = $("#orchReallocBtn", view);
    if (reallocBtn) {
      reallocBtn.onclick = async () => {
        const id = readEventId();
        if (!id) {
          if (msg) msg.textContent = "Enter an incident reference.";
          return;
        }
        if (!(await softConfirm("Reassign roles for this response team?"))) return;
        reallocBtn.disabled = true;
        try {
          const pack = await api(buildPostIncidentClusterReallocatePath(id), {
            method: "POST",
            body: {},
            timeoutMs: ORCHESTRATION_TIMEOUT_MS,
          });
          if (!isRouteCurrent(routeSeq)) return;
          renderBundle({ cell: {}, cluster: pack, affinity: {}, stigmergy: {}, audit: {} });
          toast("Reallocated", "ok");
        } catch (e) {
          toast(typeof softApiError === "function" ? softApiError(e) : String(e.message || e), "err");
          if (msg) msg.textContent = typeof softApiError === "function" ? softApiError(e) : String(e.message || e);
        } finally {
          reallocBtn.disabled = false;
        }
      };
    }
    const priorityBtn = $("#orchPriorityBtn", view);
    if (priorityBtn) {
      priorityBtn.onclick = async () => {
        const id = readEventId();
        const priority = String(($("#orchPriority", view) && $("#orchPriority", view).value) || "").trim();
        if (!id || !priority) {
          if (msg) msg.textContent = "Incident reference and priority required.";
          return;
        }
        if (!(await softConfirm(`Set response priority to ${priority}?`))) return;
        priorityBtn.disabled = true;
        try {
          const pack = await api(buildPatchIncidentClusterPriorityPath(id), {
            method: "PATCH",
            body: { priority },
            timeoutMs: ORCHESTRATION_TIMEOUT_MS,
          });
          if (!isRouteCurrent(routeSeq)) return;
          renderBundle({ cell: {}, cluster: pack, affinity: {}, stigmergy: {}, audit: {} });
          toast("Priority updated", "ok");
        } catch (e) {
          toast(typeof softApiError === "function" ? softApiError(e) : String(e.message || e), "err");
          if (msg) msg.textContent = typeof softApiError === "function" ? softApiError(e) : String(e.message || e);
        } finally {
          priorityBtn.disabled = false;
        }
      };
    }
    const stigBtn = $("#orchStigBtn", view);
    if (stigBtn) {
      stigBtn.onclick = async () => {
        const id = readEventId();
        const traceType = String(($("#orchStigType", view) && $("#orchStigType", view).value) || "").trim();
        if (!id || !traceType) {
          if (msg) msg.textContent = "Incident reference and trail note type required.";
          return;
        }
        if (!(await softConfirm("Add this note to the response trail?"))) return;
        stigBtn.disabled = true;
        try {
          await api(buildPostIncidentStigmergyPath(id), {
            method: "POST",
            body: { trace_type: traceType, detail: {} },
            timeoutMs: ORCHESTRATION_TIMEOUT_MS,
          });
          if (!isRouteCurrent(routeSeq)) return;
          toast("Trail note added", "ok");
        } catch (e) {
          toast(typeof softApiError === "function" ? softApiError(e) : String(e.message || e), "err");
          if (msg) msg.textContent = typeof softApiError === "function" ? softApiError(e) : String(e.message || e);
        } finally {
          stigBtn.disabled = false;
        }
      };
    }

    if (eventId && loadBtn) {
      loadBtn.click();
    } else if (loadBtn) {
      // Auto-open the latest advisory team plan so the desk is never an empty form.
      seedFromAdvisories().then((seedId) => {
        if (!isRouteCurrent(routeSeq) || !seedId) return;
        const inp = $("#orchEventId", view);
        if (inp) inp.value = seedId;
        if (msg) msg.textContent = "Loaded latest advisory team plan.";
        loadBtn.click();
      });
    }
  } catch (e) {
    if (!isRouteCurrent(routeSeq)) return;
    const text = String(e.message || e);
    if (/503|disabled|unavailable|pg_required/i.test(text)) {
      mountView(view, capabilityUnavailableHtml({
        title: "Response team",
        detail: "Orchestration is not opened for this account yet.",
      }));
      return;
    }
    mountView(view, `<div class="card glass-panel glass-panel--panel orchestration-page"><h2>Response team unavailable</h2><p class="muted">${escapeHtml(typeof softApiError === "function" ? softApiError(e) : text)}</p></div>`);
  }
});


// ── ota.route.js ─────────────────────────────────────
/**
 * Route: #/ota — OTA & firmware (superadmin staging + tenant-side upgrade hint).
 *
 * Build: split out of src/console.raw.js and concatenated as raw text by
 * scripts/build-dashboard.mjs after the monolith body. Shares scope with
 * helpers like $, mountView, api, registerRoute, state, toast, can, setCrumb
 * (defined in console.raw.js and the lib/ modules spliced at the top).
 */

registerRoute("ota", async (view, _args, routeSeq) => {
  await __renderOtaFirmwareRoute(view, routeSeq);
});

async function __renderOtaFirmwareRoute(view, routeSeq) {
  setCrumb("Firmware updates");
  const me = state.me || { username: "", role: "user" };
  const capSet = new Set((me.sa_capabilities || []).map(String));
  const isSuper =
    me.role === "superadmin" ||
    (me.role === "sa_staff" && (capSet.has("sa_full_access") || capSet.has("sa_ota_admin")));
  const otaUnlockKey = "croc.ota.super.unlock";
  const OTA_LOCK_LISTENER_KEY = "__crocOtaAutoLockListenerBound";

  if (!window[OTA_LOCK_LISTENER_KEY]) {
    const autoLockOnLeave = () => {
      const hash = String(location.hash || "");
      if (!hash.startsWith("#/ota")) {
        sessionStorage.removeItem(otaUnlockKey);
      }
    };
    window.addEventListener("hashchange", autoLockOnLeave);
    window[OTA_LOCK_LISTENER_KEY] = true;
  }

  if (!isSuper) {
    mountView(view, `
      <div class="card">
        <h2 class="ui-section-title" style="margin:0">OTA & firmware</h2>
        <p class="muted" style="margin:8px 0 0">Account operators do not use a separate firmware console. Open <a href="#/group/_all">All boards</a> and board detail for the <strong>↑ + red dot</strong> when an upgrade is available. Staging and campaigns are <strong>superadmin</strong> only (sidebar <strong>Firmware updates</strong>).</p>
      </div>`);
    return;
  }

  if (sessionStorage.getItem(otaUnlockKey) !== "1") {
    const unlocked = await new Promise((resolve) => {
      const dlg = document.createElement("dialog");
      dlg.className = "croc-fw-hint-dlg";
      dlg.setAttribute("aria-label", "OTA unlock");
      dlg.innerHTML = `
        <form method="dialog" class="croc-fw-hint-dlg__form croc-ota-unlock">
          <div class="croc-ota-unlock__head">
            <p class="croc-ota-unlock__eyebrow">${BRAND_OTA_ZONE_EYEBROW}</p>
            <h3 class="croc-fw-hint-dlg__title">Firmware security unlock</h3>
            <p class="muted croc-ota-unlock__lead">Superadmin only. Verify credentials to open firmware update operations.</p>
          </div>

          <div class="croc-ota-unlock__fields">
            <label class="field wide">
              <span class="croc-ota-unlock__label">USER</span>
              <input id="otaUnlockUserModal" type="text" autocomplete="username" placeholder="${BRAND_OTA_UNLOCK_PLACEHOLDER}" />
            </label>
            <label class="field wide">
              <span class="croc-ota-unlock__label">PASSWORD</span>
              <input id="otaUnlockPwdModal" type="password" autocomplete="current-password" placeholder="Enter unlock password" />
            </label>
          </div>

          <p id="otaUnlockErrModal" class="croc-ota-unlock__status" aria-live="polite"></p>
          <div class="row croc-ota-unlock__actions">
            <button type="button" class="btn secondary btn-tap" id="otaUnlockCancelModal">Cancel</button>
            <button type="button" class="btn btn-tap" id="otaUnlockBtnModal">Unlock</button>
          </div>
        </form>`;
      document.body.appendChild(dlg);

      let done = false;
      const finish = (ok) => {
        if (done) return;
        done = true;
        window.removeEventListener("hashchange", closeOnRouteLeave);
        try { dlg.close(); } catch (_) {}
        try { dlg.remove(); } catch (_) {}
        resolve(!!ok);
      };
      const closeOnRouteLeave = () => {
        if (!String(location.hash || "").startsWith("#/ota")) finish(false);
      };
      window.addEventListener("hashchange", closeOnRouteLeave);

      const formEl = dlg.querySelector("form");
      const cancelBtn = dlg.querySelector("#otaUnlockCancelModal");
      const unlockBtn = dlg.querySelector("#otaUnlockBtnModal");
      const userEl = dlg.querySelector("#otaUnlockUserModal");
      const pwdEl = dlg.querySelector("#otaUnlockPwdModal");
      const errEl = dlg.querySelector("#otaUnlockErrModal");
      if (cancelBtn) cancelBtn.onclick = () => finish(false);
      dlg.addEventListener("cancel", (ev) => { ev.preventDefault(); finish(false); }, { once: true });
      dlg.addEventListener("close", () => finish(false), { once: true });
      if (unlockBtn) {
        unlockBtn.onclick = async () => {
          const user = String(userEl?.value || "").trim();
          const pwd = String(pwdEl?.value || "");
          if (!user || !pwd) {
            if (errEl) errEl.textContent = "Username and password are required.";
            return;
          }
          unlockBtn.disabled = true;
          if (cancelBtn) cancelBtn.disabled = true;
          if (errEl) errEl.textContent = "Verifying...";
          try {
            await api(buildPostAuthMeVerifyOtaUnlockPath(), {
              method: "POST",
              body: { username: user, password: pwd },
              timeoutMs: OTA_VERIFY_UNLOCK_TIMEOUT_MS,
            });
            sessionStorage.setItem(otaUnlockKey, "1");
            toast("Firmware tools unlocked", "ok");
            finish(true);
          } catch (e) {
            if (errEl) errEl.textContent = softApiError(e, "Unlock failed");
            toast(softApiError(e, "Unlock failed"), "err");
            unlockBtn.disabled = false;
            if (cancelBtn) cancelBtn.disabled = false;
          }
        };
      }

      const submitByEnter = (ev) => {
        if (ev.key === "Enter") {
          ev.preventDefault();
          if (unlockBtn && !unlockBtn.disabled) unlockBtn.click();
        }
      };
      formEl?.addEventListener("keydown", submitByEnter);
      userEl?.addEventListener("keydown", submitByEnter);
      pwdEl?.addEventListener("keydown", submitByEnter);

      if (typeof dlg.showModal === "function") dlg.showModal();
      try { userEl && userEl.focus(); } catch (_) {}
    });
    if (!unlocked) {
      if (String(location.hash || "").startsWith("#/ota")) {
        location.hash = defaultHomeHash();
      }
      return;
    }
  }

  const ccCtx = {
    me: state.me,
    hasRole,
    can,
    currentHash: location.hash || "#/ota",
  };

  // The router shell already mounted the CC page shell for non-immersive routes
  // and will wire the chrome after this handler. Just fill the content slot.
  const slot = window.__ccPageContentSlot || view.querySelector("#crocCcPageContent");
  if (!slot) {
    mountView(view, `<div class="card"><p class="muted">Unable to load OTA command center layout.</p></div>`);
    return;
  }
  slot.innerHTML = buildOtaCommandCenterHtml(ccCtx);

  let refreshOtaCcLive = async () => {};

  /** Modals — reusable glass dialogs for upload and campaign creation. */
  const ensureModal = (id, buildHtml) => {
    let dlg = document.getElementById(id);
    if (dlg && dlg.parentNode) dlg.remove();
    dlg = document.createElement("dialog");
    dlg.id = id;
    dlg.className = "croc-cc-ota-modal";
    dlg.setAttribute("aria-modal", "true");
    dlg.innerHTML = buildHtml();
    slot.appendChild(dlg);
    return dlg;
  };

  const openUploadModal = () => {
    const dlg = ensureModal("otaCcUploadModal", () => `
      <form method="dialog" class="croc-cc-ota-modal__form">
        <div class="croc-cc-ota-modal__head">
          <h3 class="croc-cc-ota-modal__title">Upload firmware</h3>
          <button type="button" class="croc-cc-ota-modal__close" data-close-modal aria-label="Close">×</button>
        </div>
        <p class="croc-cc-ota-form__hint">Upload stages firmware on the server (upload password required). The server keeps at most <strong id="otaMaxBinsPerHwLbl">3</strong> staged builds <strong>per model</strong>. Active campaign files are never deleted.</p>
        <div class="croc-cc-ota-form__row">
          <label><span>Upload password *</span><input type="password" id="otaStUploadPwd" autocomplete="off" placeholder="Upload password" /></label>
          <label><span>Firmware file</span><input type="file" id="otaStFile" accept=".bin,application/octet-stream" /></label>
        </div>
        <div class="croc-cc-ota-form__row">
          <label><span>Version label *</span><input id="otaStFw" placeholder="6.6.8" maxlength="40" /></label>
          <label><span>Allowed models</span><input id="otaStHwCodes" placeholder="CS-M402, CS-M405" maxlength="500" autocomplete="off" /></label>
        </div>
        <div class="croc-cc-ota-form__row">
          <label><span>Model scope</span><select id="otaStHwScope"><option value="global">All accounts</option><option value="tenant">One account (custom)</option></select></label>
          <label><span>Account admin (only if one account)</span><input id="otaStHwTenant" placeholder="admin username" maxlength="64" autocomplete="off" /></label>
        </div>
        <p class="croc-cc-ota__retention-info" id="otaRetentionInfo"></p>
        <p class="croc-cc-ota__result" id="otaStResult"></p>
        <div class="croc-cc-ota-form__actions">
          <button type="button" class="croc-cc-ota-form__btn croc-cc-ota-form__btn--secondary" data-close-modal>Cancel</button>
          <button type="button" class="croc-cc-ota-form__btn croc-cc-ota-form__btn--primary" id="otaStBtn">Upload & verify</button>
        </div>
      </form>`);
    dlg.querySelectorAll("[data-close-modal]").forEach((b) => b.onclick = () => { try { dlg.close(); } catch (_) {} });
    dlg.addEventListener("cancel", (ev) => ev.preventDefault());
    if (typeof dlg.showModal === "function") dlg.showModal();
    void refreshFirmwareSelect();
    bindUploadHandler();
  };

  const openCampaignModal = () => {
    const dlg = ensureModal("otaCcCampaignModal", () => `
      <form method="dialog" class="croc-cc-ota-modal__form">
        <div class="croc-cc-ota-modal__head">
          <h3 class="croc-cc-ota-modal__title">Create campaign</h3>
          <button type="button" class="croc-cc-ota-modal__close" data-close-modal aria-label="Close">×</button>
        </div>
        <p class="croc-cc-ota-form__hint">Publish from server-staged firmware. List is <strong>newest first</strong>. Campaign version comes from the server catalog.</p>
        <div class="croc-cc-ota__grouped-list" id="otaFwGroupedList" aria-live="polite">Loading…</div>
        <div class="croc-cc-ota-form__row">
          <label style="flex:2"><span>Firmware on server *</span><select id="otaFromSel"><option value="">Loading…</option></select></label>
          <button type="button" class="croc-cc-ota-form__btn croc-cc-ota-form__btn--secondary" id="otaFwListRefresh" style="align-self:flex-end;margin-bottom:2px">Refresh list</button>
        </div>
        <label><span>Version (from server, read-only)</span><input type="text" id="otaFromResolvedVer" class="mono" readonly tabindex="-1" value="—" style="background:var(--cc-ota-inset-bg);cursor:default" aria-live="polite" /></label>
        <div class="croc-cc-ota__compat-summary" id="otaFwCompatSummary">Select a build to see <strong>version</strong> and <strong>HW scope</strong>.</div>
        <label><span>Notes</span><input id="otaFromNotes" maxlength="500" /></label>
        <div class="croc-cc-ota-form__group">
          <p class="croc-cc-ota-form__hint"><strong>Campaign targets</strong> — pick admin(s). Each block lists boards with model code, public model name, and board profile.</p>
          <div class="croc-cc-ota-form__radio-row">
            <label><input type="radio" name="otaFromTargetMode" id="otaFromModeAll" value="all" checked /> All admins</label>
            <label><input type="radio" name="otaFromTargetMode" id="otaFromModePick" value="pick" /> Select admin(s)</label>
          </div>
          <div id="otaAdminPickWrap" style="display:none">
            <label><span>Admins (hold Ctrl/Cmd to multi-select)</span>
              <select id="otaAdminMs" multiple class="croc-cc-ota-form__select-multiple" aria-label="Select admin accounts for firmware campaign"></select>
            </label>
            <p id="otaFleetHonestyBanner" class="badge offline" style="display:none;margin:8px 0" data-fleet-truncated="1"></p>
            <button type="button" class="croc-cc-ota-form__btn croc-cc-ota-form__btn--secondary" id="otaFleetLoadMore" style="display:none;margin:0 0 8px">Load more boards</button>
            <div id="otaTenantModelPanel" class="croc-cc-ota__tenant-panel" aria-live="polite">Select one or more admins to see every board.</div>
          </div>
          <label><span>Optional comma list (only if list above is empty)</span><input id="otaFromAdmTxt" placeholder="admin-a, admin-b" /></label>
        </div>
        <div class="croc-cc-ota-form__actions">
          <button type="button" class="croc-cc-ota-form__btn croc-cc-ota-form__btn--secondary" data-close-modal>Cancel</button>
          <button type="button" class="croc-cc-ota-form__btn croc-cc-ota-form__btn--primary" id="otaFromBtn">Create campaign</button>
        </div>
      </form>`);
    dlg.querySelectorAll("[data-close-modal]").forEach((b) => b.onclick = () => { try { dlg.close(); } catch (_) {} });
    dlg.addEventListener("cancel", (ev) => ev.preventDefault());
    if (typeof dlg.showModal === "function") dlg.showModal();
    void refreshFirmwareSelect();
    void initOtaCampaignTargets();
    bindCampaignHandler();
    bindRefreshButton();
  };

  wireOtaCommandCenter(slot, {
    onUpload: openUploadModal,
    onCreateCampaign: openCampaignModal,
    onRefresh: () => { void refreshOtaCcLive(); refreshAll(); toast("OTA refreshed", "ok"); },
    onTab: (tab) => {
      const mapView = slot.querySelector("#otaCcMapView");
      const depView = slot.querySelector("#otaCcDeploymentsView");
      const isDep = tab === "deployments";
      if (mapView) mapView.hidden = isDep;
      if (depView) depView.hidden = !isDep;
      if (isDep) void loadDeploymentsTable();
    },
  });

  const renderDeploymentsTable = (items) => {
    const el = slot.querySelector("#otaCcDeploymentsTable");
    if (!el) return;
    const rows = Array.isArray(items) ? items : [];
    if (!rows.length) {
      el.innerHTML = '<p class="muted">No campaigns yet.</p>';
      return;
    }
    el.innerHTML = `<div class="table-wrap"><table class="t">
      <thead><tr><th>ID</th><th>Version</th><th>State</th><th>Targets</th><th>Created</th><th></th></tr></thead>
      <tbody>${rows
        .map((c) => {
          const id = escapeHtml(String(c.id || ""));
          const fw = escapeHtml(String(c.fw_version || "—"));
          const st = escapeHtml(String(c.state || "—"));
          const targets = escapeHtml(
            Array.isArray(c.target_admins) ? c.target_admins.join(", ") : String(c.target_admins || "—"),
          );
          const created = escapeHtml(fmtTs(c.created_at));
          return `<tr data-campaign-id="${id}">
            <td class="mono">${id}</td><td>${fw}</td><td>${st}</td><td class="mono">${targets}</td><td>${created}</td>
            <td class="row" style="gap:6px;flex-wrap:wrap">
              <button type="button" class="btn sm btn-tap ota-camp-accept" data-id="${id}">Accept</button>
              <button type="button" class="btn sm secondary btn-tap ota-camp-decline" data-id="${id}">Decline</button>
              <button type="button" class="btn sm secondary btn-tap ota-camp-rollback" data-id="${id}">Rollback</button>
            </td>
          </tr>`;
        })
        .join("")}</tbody></table></div>`;
  };

  const loadDeploymentsTable = async () => {
    if (!isRouteCurrent(routeSeq)) return;
    const el = slot.querySelector("#otaCcDeploymentsTable");
    if (el) el.innerHTML = '<p class="muted">Loading campaigns…</p>';
    try {
      const data = await api(buildGetOtaCampaignsPath(), { timeoutMs: OTA_CONSOLE_TIMEOUT_MS });
      if (!isRouteCurrent(routeSeq)) return;
      const items = (data && data.items) || [];
      renderDeploymentsTable(items);
      patchOtaCommandCenterLive(slot, items);
    } catch (e) {
      if (!isRouteCurrent(routeSeq)) return;
      if (el) el.innerHTML = `<p class="muted">${escapeHtml(e.message || String(e))}</p>`;
      patchOtaCommandCenterLive(slot, []);
    }
  };

  refreshOtaCcLive = loadDeploymentsTable;
  void loadDeploymentsTable();

  slot.addEventListener(
    "click",
    (ev) => {
      const accept = ev.target && ev.target.closest && ev.target.closest(".ota-camp-accept");
      const decline = ev.target && ev.target.closest && ev.target.closest(".ota-camp-decline");
      const rollback = ev.target && ev.target.closest && ev.target.closest(".ota-camp-rollback");
      const btn = accept || decline || rollback;
      if (!btn || !slot.contains(btn)) return;
      const cid = btn.getAttribute("data-id") || "";
      if (!cid) return;
      const action = accept ? "accept" : decline ? "decline" : "rollback";
      const pathFn = accept
        ? buildPostOtaCampaignAcceptPath
        : decline
          ? buildPostOtaCampaignDeclinePath
          : buildPostOtaCampaignRollbackPath;
      if (!confirm(`${action} campaign ${cid}?`)) return;
      void (async () => {
        try {
          await api(pathFn(cid), { method: "POST", body: {}, timeoutMs: OTA_CONSOLE_TIMEOUT_MS });
          toast(`Campaign ${action} OK`, "ok");
          await loadDeploymentsTable();
        } catch (e) {
          toast(e.message || e, "err");
        }
      })();
    },
    true,
  );

  const depRefresh = slot.querySelector("#otaCcDeploymentsRefresh");
  if (depRefresh) depRefresh.addEventListener("click", () => void loadDeploymentsTable());

  const refreshAll = async () => {
    await refreshFirmwareSelect();
    await initOtaCampaignTargets();
  };

  /** Staged firmware rows from last `GET /ota/firmwares` (server-sorted newest first). Select value = row index. */
  let otaStagedFirmwareRows = [];

  const fmtOtaMtime = (ts) => {
    const t = Number(ts);
    if (!Number.isFinite(t) || t <= 0) return "";
    try {
      const d = new Date(t * 1000);
      return d.toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
    } catch {
      return "";
    }
  };

  const _otaGroupHeadingForRow = (row) => {
    const mode = String(row.hw_scope_mode || "global").trim().toLowerCase();
    const ten = String(row.hw_tenant_admin || "").trim();
    if (mode === "tenant" && ten) {
      return `Custom · account · ${ten}`;
    }
    const codes = Array.isArray(row.hw_required_codes)
      ? row.hw_required_codes.map((c) => String(c).trim()).filter(Boolean).sort((a, b) => a.localeCompare(b))
      : [];
    if (codes.length) {
      return codes.join(" | ");
    }
    return "任意机型（未限制 SKU）";
  };

  const renderOtaFirmwareGroupedList = () => {
    const el = $("#otaFwGroupedList", view);
    if (!el) return;
    if (!otaStagedFirmwareRows.length) {
      el.innerHTML = "<span class='muted'>(no staged builds)</span>";
      return;
    }
    const g = new Map();
    for (const row of otaStagedFirmwareRows) {
      const key = _otaGroupHeadingForRow(row);
      if (!g.has(key)) g.set(key, []);
      g.get(key).push(row);
    }
    const rank = (k) => {
      if (k.startsWith("任意机型")) return "\u0000";
      if (k.startsWith("Custom")) return "\u0002" + k;
      return "\u0001" + k;
    };
    const keys = [...g.keys()].sort((a, b) => {
      const c = rank(a).localeCompare(rank(b));
      return c !== 0 ? c : a.localeCompare(b);
    });
    const blocks = keys.map((key) => {
      const rows = (g.get(key) || []).slice().sort((a, b) => (Number(b.mtime) || 0) - (Number(a.mtime) || 0));
      const inner = rows.map((r) => {
        const mt = fmtOtaMtime(r.mtime);
        const ver = escapeHtml(String(r.fw_version || "").trim() || "—");
        const nm = escapeHtml(String(r.name || ""));
        return `<div class="ota-fw-line" style="padding:6px 0;border-bottom:1px solid var(--border,#2222);display:flex;flex-wrap:wrap;gap:6px 14px;align-items:baseline"><span class="mono" style="font-size:12.5px;word-break:break-all;flex:1;min-width:180px">${nm}</span><span><strong>v${ver}</strong></span><span class="muted">${escapeHtml(mt || "—")}</span></div>`;
      }).join("");
      return `<section style="margin-bottom:14px"><div style="font-weight:600;margin:0 0 8px" class="mono">${escapeHtml(key)}</div>${inner}</section>`;
    }).join("");
    el.innerHTML = blocks;
  };

  const otaProbeReachability = async (fname) => {
    const name = String(fname || "").trim();
    if (!name) return null;
    try {
      return await api(buildOtaFirmwareReachabilityPath(name), { timeoutMs: OTA_CONSOLE_TIMEOUT_MS });
    } catch (e) {
      return { ok: false, detail: e && e.message ? String(e.message) : String(e) };
    }
  };

  const otaSyncFirmwareSelectionUi = () => {
    const sel = $("#otaFromSel", view);
    const ro = $("#otaFromResolvedVer", view);
    const sum = $("#otaFwCompatSummary", view);
    const fromBtn = $("#otaFromBtn", view);
    if (!ro) return;
    const rawIdx = sel && sel.value !== "" && sel.value != null ? Number.parseInt(String(sel.value), 10) : NaN;
    const row = Number.isFinite(rawIdx) && rawIdx >= 0 ? otaStagedFirmwareRows[rawIdx] : null;
    if (!sel || !row) {
      ro.value = "—";
      if (fromBtn) fromBtn.disabled = true;
      if (sum) {
        sum.innerHTML = "<span class='muted'>Select a staged build to see <strong>version</strong> and <strong>HW scope</strong> (public SKU list).</span>";
      }
      return;
    }
    const ver = (row.fw_version && String(row.fw_version).trim()) || "";
    ro.value = ver || "—";
    if (fromBtn) fromBtn.disabled = !ver;

    const codeList = Array.isArray(row.hw_required_codes) ? row.hw_required_codes.map((c) => String(c).trim()).filter(Boolean) : [];
    const mode = String(row.hw_scope_mode || "global").trim() || "global";
    const ten = String(row.hw_tenant_admin || "").trim();
    const baseSummary = () => {
      const modelLine = codeList.length
        ? `Artifact public SKU: <strong class="mono">${codeList.map(escapeHtml).join(", ")}</strong>`
        : "HW: <strong>any model</strong> — no required SKU list on this artifact";
      const scopeLine =
        mode === "tenant" && ten
          ? `<br/>Scope: <strong>one account</strong> · admin <code class="mono">${escapeHtml(ten)}</code>`
          : `<br/>Scope: <strong>${escapeHtml(mode)}</strong>`;
      return `${modelLine}${scopeLine}`;
    };
    if (sum) {
      if (!ver) {
        sum.innerHTML = `${baseSummary()}<br/><span class="muted">No version on server — re-upload with a version label or add a <code>.version</code> sidecar before creating a campaign.</span>`;
        return;
      }
      sum.innerHTML = `${baseSummary()}<br/><span class="muted">Checking firmware URL reachability (HEAD)…</span>`;
      void (async () => {
        const probe = await otaProbeReachability(row.name);
        if (!isRouteCurrent(routeSeq)) return;
        const curIdx = sel && sel.value !== "" && sel.value != null ? Number.parseInt(String(sel.value), 10) : NaN;
        const curRow = Number.isFinite(curIdx) && curIdx >= 0 ? otaStagedFirmwareRows[curIdx] : null;
        if (!curRow || String(curRow.name || "") !== String(row.name || "")) return;
        const probeLine = probe && probe.ok
          ? `<span style="color:var(--ok,#2a7)">HTTP probe OK</span> · ${escapeHtml(String(probe.detail || "reachable"))}`
          : `<span style="color:var(--err,#c44)">HTTP probe failed</span> · ${escapeHtml(String((probe && probe.detail) || "unknown"))}. Ask platform staff to check firmware download settings on the server.`;
        if (sum) sum.innerHTML = `${baseSummary()}<br/>${probeLine}`;
        if (fromBtn) fromBtn.disabled = !(probe && probe.ok);
      })();
    }
  };

  const refreshFirmwareSelect = async () => {
    if (!isSuper) return;
    const sel = $("#otaFromSel", view);
    if (!sel) return;
    try {
      const r = await api(buildOtaFirmwaresPath(), { timeoutMs: OTA_CONSOLE_TIMEOUT_MS });
      if (!isRouteCurrent(routeSeq)) return;
      const items = r.items || [];
      otaStagedFirmwareRows = items.map((x) => ({ ...x }));
      const ret = r.retention;
      const perHw = $("#otaMaxBinsPerHwLbl", view);
      const perModel = ret && (ret.max_bins_per_hw_code ?? ret.max_bins_per_model);
      if (perHw && perModel != null) perHw.textContent = String(perModel);
      const inf = $("#otaRetentionInfo", view);
      if (inf) {
        if (ret && ret.per_hw_code_enabled !== false) {
          inf.textContent = `Staged: ${ret.stored_count || 0} total · max ${perModel ?? 3} per 自编码/SKU · upload password: ${ret.upload_password_configured ? "configured" : "not set on server"}.`;
        } else if (ret) {
          inf.textContent = `Staged: ${ret.stored_count || 0} / global max ${ret.max_bins}. Upload password: ${ret.upload_password_configured ? "configured" : "not set on server"}.`;
        } else {
          inf.textContent = "";
        }
      }
      sel.innerHTML = items.length
        ? items.map((it, idx) => {
          const vRaw = (it.fw_version && String(it.fw_version).trim()) || "";
          const mt = fmtOtaMtime(it.mtime);
          const nm = escapeHtml(String(it.name || ""));
          const verEsc = escapeHtml(vRaw || "—");
          const mtEsc = escapeHtml(mt || "");
          const label = mt
            ? `${nm} · v${verEsc} · ${mtEsc}`
            : `${nm} · v${verEsc}`;
          return `<option value="${idx}">${label}</option>`;
        }).join("")
        : "<option value=\"\">(no staged builds)</option>";
      renderOtaFirmwareGroupedList();
      otaSyncFirmwareSelectionUi();
      sel.onchange = otaSyncFirmwareSelectionUi;
    } catch (e) {
      otaStagedFirmwareRows = [];
      const inf = $("#otaRetentionInfo", view);
      if (inf) inf.textContent = "";
      sel.innerHTML = `<option value="">${escapeHtml(e.message || "list failed")}</option>`;
      renderOtaFirmwareGroupedList();
      otaSyncFirmwareSelectionUi();
      sel.onchange = otaSyncFirmwareSelectionUi;
    }
  };

  await refreshFirmwareSelect();

  const _otaDevicesForOwners = (devices, ownersOrdered) => {
    const want = new Set((ownersOrdered || []).map((u) => String(u || "").trim()).filter(Boolean));
    const out = [];
    for (const d of devices || []) {
      const ow = String(d.owner_admin || "").trim();
      if (!want.has(ow)) continue;
      out.push(d);
    }
    out.sort((a, b) => String(a.device_id || "").localeCompare(String(b.device_id || "")));
    return out;
  };

  const _otaRenderOwnerDevicePanel = (el, devices, ownersOrdered) => {
    if (!el) return;
    if (!ownersOrdered || ownersOrdered.length === 0) {
      el.innerHTML = "<span class='muted'>No admins selected. / 未选择用户</span>";
      return;
    }
    const all = _otaDevicesForOwners(devices, ownersOrdered);
    const byOwner = new Map();
    for (const d of all) {
      const ow = String(d.owner_admin || "").trim();
      if (!byOwner.has(ow)) byOwner.set(ow, []);
      byOwner.get(ow).push(d);
    }
    const thStyle = "text-align:left;padding:8px 10px;vertical-align:bottom;font-weight:600;border-bottom:2px solid var(--border,#4444);white-space:nowrap";
    const tdStyle = "text-align:left;padding:7px 10px;vertical-align:top;border-bottom:1px solid var(--border,#2a2a2a);word-break:break-word";
    const blocks = [];
    for (const ow of ownersOrdered) {
      const list = byOwner.get(ow) || [];
      const n = list.length;
      const head = `<div style="font-weight:600;margin:0 0 8px;font-size:13px;display:flex;flex-wrap:wrap;align-items:baseline;gap:8px 14px">
        <span class="mono">${escapeHtml(ow)}</span>
        <span class="muted" style="font-weight:500;font-size:12.5px">${n} board(s)</span>
      </div>`;
      if (!n) {
        blocks.push(`<section style="margin-bottom:14px">${head}<p class="muted" style="margin:0">No boards</p></section>`);
        continue;
      }
      const body = list.map((d) => {
        const did = escapeHtml(String(d.device_id || ""));
        const selfc = escapeHtml(String(d.product_hw_code || "").trim() || "—");
        const sku = escapeHtml(String(d.hw_code_resolved || "").trim() || "—");
        const chip = escapeHtml(String(d.chip_target || "").trim() || "—");
        const brd = escapeHtml(String(d.board_profile || "").trim() || "—");
        return `<tr>
          <td class="mono" style="${tdStyle};width:22%">${did}</td>
          <td class="mono" style="${tdStyle};width:13%">${selfc}</td>
          <td class="mono" style="${tdStyle};width:13%">${sku}</td>
          <td class="mono" style="${tdStyle};width:22%">${chip}</td>
          <td class="mono" style="${tdStyle};width:30%">${brd}</td>
        </tr>`;
      }).join("");
      blocks.push(`<section style="margin-bottom:18px">${head}
        <div style="overflow-x:auto;-webkit-overflow-scrolling:touch">
          <table style="width:100%;min-width:760px;border-collapse:collapse;font-size:12.5px;table-layout:fixed">
            <colgroup><col style="width:22%" /><col style="width:13%" /><col style="width:13%" /><col style="width:22%" /><col style="width:30%" /></colgroup>
            <thead><tr>
              <th class="mono" style="${thStyle}">Board serial</th>
              <th class="mono" style="${thStyle}">自编码</th>
              <th class="mono" style="${thStyle}">公示型号</th>
              <th class="mono" style="${thStyle}">原编码 chip</th>
              <th class="mono" style="${thStyle}">原编码 board</th>
            </tr></thead>
            <tbody>${body}</tbody>
          </table>
        </div>
      </section>`);
    }
    el.innerHTML = blocks.join("");
  };

  const initOtaCampaignTargets = async () => {
    const wrap = $("#otaAdminPickWrap", view);
    const ms = $("#otaAdminMs", view);
    const panel = $("#otaTenantModelPanel", view);
    const modeAll = $("#otaFromModeAll", view);
    const modePick = $("#otaFromModePick", view);
    const honesty = $("#otaFleetHonestyBanner", view);
    const loadMoreBtn = $("#otaFleetLoadMore", view);
    if (!ms || !panel) return;

    let devices = [];
    let otaNextCursor = null;
    let otaVisibleTotal = null;
    let otaPagesFetched = 0;

    const syncOtaHonestyUi = () => {
      const n = devices.length;
      const truncated = otaNextCursor != null || (otaVisibleTotal != null && n < otaVisibleTotal);
      if (honesty) {
        if (!truncated && otaVisibleTotal == null) {
          honesty.style.display = "none";
          honesty.innerHTML = "";
        } else {
          honesty.style.display = "";
          const vtNote = otaVisibleTotal != null
            ? `Boards on file: <strong>${otaVisibleTotal}</strong>. `
            : "";
          honesty.innerHTML =
            `Showing <strong>${n}</strong> loaded board(s)` +
            (otaPagesFetched ? ` · ${otaPagesFetched} page(s)` : "") +
            `. ${vtNote}` +
            (truncated ? "Preview sample — not the full fleet." : "");
        }
      }
      if (loadMoreBtn) {
        loadMoreBtn.style.display = otaNextCursor ? "" : "none";
        loadMoreBtn.disabled = !otaNextCursor;
      }
    };

    try {
      const dr = await fetchFleetDevicesList({
        maxPages: 1,
        otaPreview: true,
        timeoutMs: OTA_DEVICES_LIST_TIMEOUT_MS,
      });
      if (!isRouteCurrent(routeSeq)) return;
      devices = dr.items || [];
      otaPagesFetched = Number(dr.pages_fetched) || 1;
      otaVisibleTotal = fleetVisibleTotalFrom(dr);
      const page = dr.page && typeof dr.page === "object" ? dr.page : null;
      otaNextCursor = page && page.has_more && page.next_cursor != null
        ? String(page.next_cursor)
        : null;
    } catch (e) {
      if (panel) panel.innerHTML = `<span class="muted">Could not load boards: ${escapeHtml(e.message || String(e))}</span>`;
    }
    syncOtaHonestyUi();

    const normalizeAdminPickerItems = (raw) => {
      const arr = Array.isArray(raw) ? raw : [];
      return arr
        .map((a) =>
          typeof a === "string"
            ? { username: String(a || "").trim(), email: "" }
            : {
                username: String((a && a.username) || "").trim(),
                email: String((a && a.email) || "").trim(),
              },
        )
        .filter((x) => x.username);
    };

    let adminRows = [];
    try {
      const ar = await api(buildAuthAdminsPath(), { timeoutMs: ADMIN_CONSOLE_TIMEOUT_MS });
      if (!isRouteCurrent(routeSeq)) return;
      adminRows = normalizeAdminPickerItems(ar.items);
    } catch (_) {
      /* SA without SA_USER_ADMIN may get 403 — still list owners from devices */
    }
    const byUser = new Map(adminRows.map((r) => [r.username, r]));
    for (const d of devices) {
      const o = String(d.owner_admin || "").trim();
      if (o && !byUser.has(o)) byUser.set(o, { username: o, email: "" });
    }
    const adminList = [...byUser.values()].sort((a, b) =>
      a.username.localeCompare(b.username, undefined, { sensitivity: "base" }),
    );

    ms.innerHTML = adminList.length
      ? adminList
          .map((r) => {
            const u = escapeHtml(r.username);
            const lab = r.email ? `${u} · ${escapeHtml(r.email)}` : u;
            return `<option value="${u}">${lab}</option>`;
          })
          .join("")
      : "<option value=\"\">(no admin usernames found)</option>";

    const syncPanel = () => {
      if (!modePick || !modePick.checked) {
        if (wrap) wrap.style.display = "none";
        return;
      }
      if (wrap) wrap.style.display = "";
      const picked = [...ms.selectedOptions].map((o) => o.value).filter(Boolean);
      _otaRenderOwnerDevicePanel(panel, devices, picked);
    };

    const syncMode = () => {
      if (modeAll && modeAll.checked) {
        if (wrap) wrap.style.display = "none";
      } else {
        syncPanel();
      }
    };

    if (loadMoreBtn && !loadMoreBtn.dataset.otaBound) {
      loadMoreBtn.dataset.otaBound = "1";
      loadMoreBtn.addEventListener("click", async () => {
        if (!otaNextCursor) return;
        loadMoreBtn.disabled = true;
        try {
          const pagePack = await fetchFleetDevicesPage({
            cursor: otaNextCursor,
            otaPreview: true,
            timeoutMs: OTA_DEVICES_LIST_TIMEOUT_MS,
          });
          if (!isRouteCurrent(routeSeq)) return;
          const more = pagePack.items || [];
          for (const it of more) devices.push(it);
          otaPagesFetched += 1;
          const vt = fleetVisibleTotalFrom(pagePack);
          if (vt != null) otaVisibleTotal = vt;
          const page = pagePack.page && typeof pagePack.page === "object" ? pagePack.page : null;
          otaNextCursor = page && page.has_more && page.next_cursor != null
            ? String(page.next_cursor)
            : null;
          for (const d of more) {
            const o = String(d.owner_admin || "").trim();
            if (o && !byUser.has(o)) {
              byUser.set(o, { username: o, email: "" });
              const opt = document.createElement("option");
              opt.value = o;
              opt.textContent = o;
              ms.appendChild(opt);
            }
          }
          syncOtaHonestyUi();
          syncPanel();
        } catch (e) {
          toast(e.message || String(e), "err");
          syncOtaHonestyUi();
        }
      });
    }

    modeAll?.addEventListener("change", syncMode);
    modePick?.addEventListener("change", syncMode);
    ms.addEventListener("change", syncPanel);
    ms.addEventListener("click", syncPanel);
    syncMode();
  };

  await initOtaCampaignTargets();

  function bindRefreshButton() {
    const otaFwListRefresh = $("#otaFwListRefresh", view);
    if (otaFwListRefresh) {
      otaFwListRefresh.addEventListener("click", async () => {
        otaFwListRefresh.disabled = true;
        try {
          await refreshFirmwareSelect();
          toast("Firmware list refreshed from server", "ok");
        } catch (_) {}
        finally { otaFwListRefresh.disabled = false; }
      });
    }
  }

  function bindUploadHandler() {
    const stBtn = $("#otaStBtn", view);
    if (stBtn) {
      stBtn.addEventListener("click", async () => {
        const inp = $("#otaStFile", view);
        const f = inp && inp.files && inp.files[0];
        const fw = String($("#otaStFw", view)?.value || "").trim();
        const upw = String($("#otaStUploadPwd", view)?.value || "");
        if (!f || !fw) { toast("Choose file and version label", "err"); return; }
        if (!upw) { toast("Enter the upload password configured on the server.", "err"); return; }
        const hwCodes = String($("#otaStHwCodes", view)?.value || "").trim();
        if (!hwCodes) {
          toast("Enter model scope (e.g. CS-M401). Empty scope puts this build in the global bucket (wrong-model risk).", "err");
          return;
        }
        if (!confirm("Upload firmware to server (HEAD check against public /fw/ URL)?")) return;
        try {
          const fd = new FormData();
          fd.append("file", f);
          fd.append("fw_version", fw);
          fd.append("upload_password", upw);
          fd.append("hw_required_codes", hwCodes);
          fd.append("hw_scope", String($("#otaStHwScope", view)?.value || "global").trim() || "global");
          fd.append("hw_tenant_admin", String($("#otaStHwTenant", view)?.value || "").trim());
          const r = await api(buildPostOtaFirmwareUploadPath(), { method: "POST", body: fd, timeoutMs: OTA_FIRMWARE_UPLOAD_TIMEOUT_MS });
          if (!isRouteCurrent(routeSeq)) return;
          const resEl = $("#otaStResult", view);
          const headLabel = r.head_ok ? "download check OK" : "download check failed";
          const scopeNote = r.hw_scope && typeof r.hw_scope === "object"
            ? ` · model scope${r.hw_scope.written ? " saved" : r.hw_scope.cleared ? " cleared" : ""}`
            : "";
          const stored = r.stored_as ? escapeHtml(String(r.stored_as)) : "";
          if (resEl) {
            resEl.textContent = stored
              ? `Stored as ${stored} · label v${escapeHtml(fw)} · ${headLabel} · ${r.verify || ""}${scopeNote}`
              : `Staged · label v${escapeHtml(fw)} · ${headLabel} · ${r.verify || ""}${scopeNote}`;
          }
          toast("Upload finished", r.head_ok ? "ok" : "err");
          if (inp) inp.value = "";
          refreshFirmwareSelect();
        } catch (e) { toast(softApiError(e), "err"); }
      });
    }
  }

  function bindCampaignHandler() {
    const fromBtn = $("#otaFromBtn", view);
    if (fromBtn) {
      fromBtn.addEventListener("click", async () => {
        const selEl = $("#otaFromSel", view);
        const rawIdx = selEl && selEl.value !== "" && selEl.value != null ? Number.parseInt(String(selEl.value), 10) : NaN;
        const row = Number.isFinite(rawIdx) && rawIdx >= 0 ? otaStagedFirmwareRows[rawIdx] : null;
        const fn = row && row.name ? String(row.name).trim() : "";
        const notes = String($("#otaFromNotes", view)?.value || "").trim();
        const modeAll = $("#otaFromModeAll", view)?.checked;
        const ms = $("#otaAdminMs", view);
        const rawAdm = String($("#otaFromAdmTxt", view)?.value || "").trim();
        let target_admins;
        if (modeAll) {
          target_admins = ["*"];
        } else {
          const picked = ms ? [...ms.selectedOptions].map((o) => o.value).filter(Boolean) : [];
          if (picked.length) {
            target_admins = picked;
          } else if (rawAdm) {
            target_admins = rawAdm.split(/[\s,;]+/).filter(Boolean);
          } else {
            toast("Select one or more admins in the list, or enter optional comma names, or choose All admins", "err");
            return;
          }
        }
        if (!fn) { toast("Select a firmware package from the list", "err"); return; }
        const fwVer = row && row.fw_version ? String(row.fw_version).trim() : "";
        if (!fwVer) {
          toast("Selected build has no version on server — re-upload with a version label first", "err");
          return;
        }
        if (!confirm("Create firmware campaign from the selected staged build? Campaign version is taken from the server catalog, not the UI.")) return;
        fromBtn.disabled = true;
        try {
          const probe = await otaProbeReachability(fn);
          if (!probe || !probe.ok) {
            const det = probe && probe.detail ? String(probe.detail) : "firmware HTTP probe failed";
            toast(`Cannot create campaign: ${det}`, "err");
            return;
          }
          const out = await api(buildPostOtaCampaignsFromStoredPath(), {
            method: "POST",
            body: {
              filename: fn,
              fw_version: fwVer,
              notes: notes || undefined,
              target_admins,
            },
            timeoutMs: OTA_CONSOLE_TIMEOUT_MS,
          });
          const skipped = out && Array.isArray(out.skipped_admins) ? out.skipped_admins.filter(Boolean) : [];
          const baseMsg = (out && out.fw_version) ? `Campaign created · v${out.fw_version}` : "Campaign created";
          toast(
            skipped.length
              ? `${baseMsg} · skipped (SKU mismatch): ${skipped.join(", ")}`
              : baseMsg,
            "ok",
          );
          try { bustDeviceListCaches(); } catch (_) {}
        } catch (e) { toast(e.message || e, "err"); }
        finally { fromBtn.disabled = false; otaSyncFirmwareSelectionUi(); }
      });
    }
  }

  bindRefreshButton();
  bindUploadHandler();
  bindCampaignHandler();
}


function renderPolicyPanel(username, p) {
  const row = (k, label, locked) => `
    <label class="checkbox"><input type="checkbox" data-k="${k}" ${p[k] ? "checked" : ""} ${locked ? "disabled" : ""}/><span>${escapeHtml(label)}</span></label>`;
  return `
    <div class="stack">
      <p class="muted" style="margin:0">Capabilities for <strong>${escapeHtml(username)}</strong> (user role).</p>
      <div class="row">
        ${row("can_alert", "Siren & alarm actions")}
        ${row("can_send_command", "Send device commands")}
        ${row("can_claim_device", "Claim / provision devices")}
        ${row("can_manage_users", "Manage users (not for user role)", true)}
        ${row("can_backup_restore", "Backup / restore (not for user role)", true)}
      </div>
      <div class="row" style="justify-content:flex-end">
        <button class="btn js-save" type="button">Save</button>
      </div>
    </div>`;
}


// ── overview.route.js ─────────────────────────────────────
/**
 * Route: #/overview — Command Center (KPIs, map, feed). Group cards live on #/groups.
 *
 * Build: split out of src/console.raw.js and concatenated as raw text by
 * scripts/build-dashboard.mjs after the monolith body.
 */

registerRoute("overview", async (view, _args, routeSeq) => {
  setCrumb("Home");
  activateOverviewCommandCenterLayout();

  const [ovListRes, alarmEventsRes, mineDispatchesRes] = await Promise.allSettled([
    fetchOverviewAndDevicesBundle(api),
    api("/events?category=alarm&min_level=warn&limit=12"),
    api("/responder-dispatches/mine?limit=12"),
  ]);
  let ov = (ovListRes.status === "fulfilled" && ovListRes.value && ovListRes.value.ov) ? ovListRes.value.ov : null;
  let list = (ovListRes.status === "fulfilled" && ovListRes.value && ovListRes.value.list) ? ovListRes.value.list : null;
  if (!ov || !list) {
    const cached = state.overviewCache;
    if (cached && cached.ov && cached.list) {
      ov = ov || cached.ov;
      list = list || cached.list;
      toast("Showing last saved view — connection is slow. We'll refresh when it recovers.", "warn");
    }
  }
  if (!ov) ov = { mqtt_connected: false };
  if (!list) list = { items: [] };
  state.overviewCache = { ov, list, ts: Date.now() };
  const devices = list.items || [];
  try { if (typeof rememberDeviceLabels === "function") rememberDeviceLabels(devices); } catch (_) {}
  const isSuper = !!(state.me && (state.me.role === "superadmin" || state.me.role === "sa_staff"));
  const kpi0 = buildOverviewKpiPatch(ov, devices, { health: state.health, isSuper, me: state.me });

  const mapAlarmEventsToFeed = (alarmPayload, dispatchPayload) => {
    let cards = [];
    if (alarmPayload && Array.isArray(alarmPayload.items)) {
      cards = alarmPayload.items
        .filter((ev) => String(ev.event_type || "").startsWith("alarm."))
        .map((ev) => mapEventToIncidentCard(ev));
    }
    let resps = [];
    if (dispatchPayload && Array.isArray(dispatchPayload.items)) {
      resps = dispatchPayload.items.map((d) => {
        const st = String(d.status || "pending");
        const statusLabel = ({
          pending: "Waiting",
          accepted: "Accepted",
          en_route: "On the way",
          on_scene: "On site",
          resolved: "Done",
          closed: "Done",
          cancelled: "Cancelled",
        })[st] || softOperatorNote(st.replace(/_/g, " "));
        const nextMap = {
          pending: ["accepted", "Accept"],
          accepted: ["en_route", "On the way"],
          en_route: ["on_scene", "On site"],
          on_scene: ["resolved", "Done"],
        };
        const next = nextMap[st] || null;
        return {
          name: String(d.assigned_responder || softDeviceLabel(d.device_id) || "You"),
          status: statusLabel,
          dist: softDeviceLabel(d.device_id) || "—",
          eta: "—",
          active: ["accepted", "en_route", "on_scene"].includes(st),
          dispatchId: String(d.dispatch_id || ""),
          nextStatus: next ? next[0] : "",
          nextLabel: next ? next[1] : "",
        };
      });
    }
    const timeline = cards.slice(0, 6).map((c) => ({
      at: c.ago,
      summary: c.title,
    }));
    return { incidentCards: cards, responders: resps, timelineItems: timeline };
  };

  const alarmPayload = alarmEventsRes.status === "fulfilled" ? alarmEventsRes.value : null;
  const dispPayload = mineDispatchesRes.status === "fulfilled" ? mineDispatchesRes.value : null;
  const { incidentCards, responders, timelineItems } = mapAlarmEventsToFeed(alarmPayload, dispPayload);

  let lastOverviewHeaderSig = "";
  const patchOverviewHeader = (vals) => {
    const sig = JSON.stringify(vals || {});
    if (sig === lastOverviewHeaderSig) return;
    lastOverviewHeaderSig = sig;
    patchOverviewCommandCenterMetrics(view, {
      devices: vals.devices,
      online: vals.online,
      offline: vals.offline,
    });
  };

  const ccShellHtml = buildOverviewCommandCenterShellHtml({
    me: state.me,
    kpi: kpi0,
    devices,
    totalDevices: kpi0.devices,
    onlineDevices: kpi0.online,
    incidentCards,
    responders,
    timelineItems,
    mapEnabled: canUseOpsMap(state.me),
    hasRole,
    can,
    currentHash: location.hash || "#/overview",
  });

  mountView(view, ccShellHtml);
  patchOverviewHeader(kpi0);
  wireOverviewCommandCenterChrome(view, {
    devices,
    me: state.me,
    apiOr,
    buildAlarmsSummaryPath,
    alarmsSummaryTimeoutMs: TELEMETRY_ALARMS_SUMMARY_TIMEOUT_MS,
  });
  // tip597: after shell mount — force map big-screen when map is allowed.
  try {
    if (typeof canUseOpsMap === "function" && canUseOpsMap(state.me)
      && typeof setHomeLandingPreference === "function"
      && typeof applyHomeLandingChrome === "function") {
      setHomeLandingPreference("map");
      applyHomeLandingChrome(view.querySelector("#crocCcRoot") || view, state.me);
      const wbForce = view.querySelector("[data-home-wb]");
      if (wbForce) {
        wbForce.setAttribute("data-wb-open", "0");
        wbForce.setAttribute("data-wb-dock", "1");
        try {
          localStorage.setItem("croc_home_wb_open", "0");
          localStorage.setItem("croc_home_wb_dock", "1");
        } catch (_) {}
      }
    }
  } catch (_) {}

  // Home workbench: real assign (admin) + field-queue transitions (all roles).
  const homeWb = view.querySelector("[data-home-wb]");
  const assignRoot = view.querySelector("#ccHomeAssign");

  const resolveAlarmLatLng = async (deviceId) => {
    const want = String(deviceId || "").trim();
    if (!want || typeof buildGetDevicesMapSnapshotPath !== "function") return null;
    try {
      const snap = await api(buildGetDevicesMapSnapshotPath({ skipCache: false }), { timeoutMs: 12000 });
      const items = Array.isArray(snap && snap.items) ? snap.items : [];
      const row = items.find((it) => {
        const id = String((it && (it.device_id || it.id || it.marker_ref)) || "").trim();
        return id && id.toUpperCase() === want.toUpperCase();
      });
      if (!row) return null;
      const latitude = Number(row.map_lat != null ? row.map_lat : row.latitude);
      const longitude = Number(row.map_lng != null ? row.map_lng : row.longitude);
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
      if (latitude === 0 && longitude === 0) return null;
      return { latitude, longitude };
    } catch (_) {
      return null;
    }
  };

  if (assignRoot) {
    if (hasRole("admin") && typeof wireDispatchComposer === "function") {
      wireDispatchComposer(assignRoot, {
        api,
        toast,
        resolveAlarmLatLng,
        onCreated: () => {
          toast("Assigned — it appears in the field queue", "ok");
        },
      });
    } else {
      assignRoot.innerHTML = `<p class="muted" style="margin:0;line-height:1.45">Waiting for an admin to assign you. Your Accept → On the way → On site steps will appear in <strong>My field queue</strong> and under <button type="button" class="btn sm ghost btn-tap" data-cc-go="response">Assignments</button>.</p>`;
    }
  }
  if (homeWb && homeWb.dataset.alertWired !== "1") {
    homeWb.dataset.alertWired = "1";
    homeWb.addEventListener("click", async (ev) => {
      const pick = ev.target && ev.target.closest ? ev.target.closest("[data-home-alert-pick]") : null;
      if (!pick || !homeWb.contains(pick)) return;
      const did = String(pick.getAttribute("data-device-id") || "").trim();
      const aid = String(pick.getAttribute("data-alarm-id") || "").trim();
      const eid = String(pick.getAttribute("data-event-id") || "").trim();
      const title = String(pick.getAttribute("data-title") || "Alarm").trim();
      const deviceEl = homeWb.querySelector("#dcDeviceId");
      const alarmEl = homeWb.querySelector("#dcAlarmId");
      const reasonEl = homeWb.querySelector("#dcReason");
      const latEl = homeWb.querySelector("#dcLat");
      const lngEl = homeWb.querySelector("#dcLng");
      const out = homeWb.querySelector("#dcOut");
      if (deviceEl && did) deviceEl.value = did;
      if (alarmEl && aid) alarmEl.value = aid;
      if (reasonEl && !String(reasonEl.value || "").trim()) reasonEl.value = `From Home · ${title}`.slice(0, 200);
      if (out) out.textContent = did
        ? `Ready to assign for “${title}”. Resolving map position…`
        : "This alarm has no device label yet — open the case to continue.";
      if (did) toast("Assign form filled from live alert", "ok");
      try { deviceEl && deviceEl.focus(); } catch (_) {}
      if (did && latEl && lngEl) {
        const ll = await resolveAlarmLatLng(did);
        if (!isRouteCurrent(routeSeq)) return;
        if (ll) {
          latEl.value = String(ll.latitude);
          lngEl.value = String(ll.longitude);
          if (out) out.textContent = `Ready to assign for “${title}” · map position loaded for nearest match.`;
        } else if (out) {
          out.textContent = `Ready to assign for “${title}”. No map pin yet — Assign by name, or place the device on the map.`;
        }
      }
      if (eid && typeof applyHomeProofPanel === "function") {
        try {
          const [proof, sla] = await Promise.all([
            api(`/incidents/${encodeURIComponent(eid)}/proof-of-response`, { timeoutMs: 10000 }),
            api(`/incidents/${encodeURIComponent(eid)}/sla`, { timeoutMs: 10000 }),
          ]);
          if (!isRouteCurrent(routeSeq)) return;
          applyHomeProofPanel(view, { eventId: eid, proof, sla });
        } catch (_) {}
      }
    });
  }
  if (homeWb && homeWb.dataset.queueWired !== "1") {
    homeWb.dataset.queueWired = "1";
    homeWb.addEventListener("click", async (ev) => {
      const t = ev.target && ev.target.closest ? ev.target.closest("[data-home-dispatch][data-home-next]") : null;
      if (!t || !homeWb.contains(t)) return;
      const did = String(t.getAttribute("data-home-dispatch") || "").trim();
      const next = String(t.getAttribute("data-home-next") || "").trim();
      const label = String(t.textContent || "").trim() || "next step";
      if (!did || !next) return;
      if (!(await softConfirm(`Move this assignment to “${label}”?`))) return;
      t.disabled = true;
      try {
        await api(`/responder-dispatches/mine/${encodeURIComponent(did)}/transition`, {
          method: "POST",
          body: { status: next },
          timeoutMs: 16000,
        });
        if (!isRouteCurrent(routeSeq)) return;
        toast("Assignment updated", "ok");
        try {
          const again = await api("/responder-dispatches/mine?limit=12");
          if (!isRouteCurrent(routeSeq)) return;
          const mapped = mapAlarmEventsToFeed(null, again);
          patchOverviewIncidentFeed(view, {
            incidentCards,
            responders: mapped.responders,
            timelineItems,
            aiPack: lastAiPack,
          });
          const panel = view.querySelector(".croc-cc-auto-sentinel");
          if (panel) delete panel.dataset.wired;
          if (typeof wireHomeAutoSentinel === "function") wireHomeAutoSentinel();
        } catch (_) {}
      } catch (e) {
        toast(typeof softApiError === "function" ? softApiError(e) : "Could not update assignment", "err");
        t.disabled = false;
      }
    });
  }

  const aiOnHome = hasRole("admin") && catalogEnabled(state.me, "ai_incident");
  let lastAiPack = { aiOn: aiOnHome, items: [], settings: {} };
  /** @type {null | { init: Function; refresh: Function; teardown: Function; setAiWaitingPlans?: Function; focusAiPlan?: Function; openAiSheet?: Function }} */
  let overviewMapCtrl = null;
  if (!aiOnHome) {
    patchOverviewIncidentFeed(view, { incidentCards, responders, timelineItems, aiPack: { aiOn: false } });
  }

  const quietScanRecent = async (limit = 2, { force = false } = {}) => {
    if (!aiOnHome) return { ok: 0 };
    const key = "croc_auto_sentinel_home_scan_at";
    try {
      const last = Number(sessionStorage.getItem(key) || 0);
      if (!force && Date.now() - last < 5 * 60 * 1000) return { ok: 0, skipped: true };
    } catch (_) {}
    let recent = [];
    try {
      const pack = await api("/incidents?limit=8", { timeoutMs: INTELLIGENCE_TIMEOUT_MS });
      recent = (Array.isArray(pack && pack.items) ? pack.items : [])
        .filter((it) => it && (it.event_id || it.incident_id) && it.device_id)
        .slice(0, Math.max(1, Number(limit) || 2));
    } catch (_) {
      return { ok: 0, fail: 1 };
    }
    if (!recent.length) return { ok: 0, empty: true };
    try { sessionStorage.setItem(key, String(Date.now())); } catch (_) {}
    let ok = 0;
    for (const it of recent) {
      if (!isRouteCurrent(routeSeq)) return { ok, aborted: true };
      const eventId = Number(it.event_id != null ? it.event_id : it.incident_id);
      const deviceId = String(it.device_id || "").trim();
      if (!(eventId > 0) || !deviceId) continue;
      try {
        await api(buildPostAiAnalyzePath(), {
          method: "POST",
          body: {
            alarm_id: `alarm-${eventId}`,
            device_id: deviceId,
            event_id: eventId,
            event_type: String(it.event_type || "alarm.trigger"),
          },
          timeoutMs: INTELLIGENCE_TIMEOUT_MS,
        });
        ok += 1;
      } catch (_) {}
    }
    return { ok };
  };

  const resolveOverviewTeamRun = async (incidentId) => {
    const id = String(incidentId || "").trim();
    if (!id) return { teamRunId: "", controlGen: 1 };
    try {
      const pack = await api(`/ai/team/incidents/${encodeURIComponent(id)}/run`, {
        method: "GET",
        timeoutMs: INTELLIGENCE_TIMEOUT_MS,
      });
      const teamRunId = String((pack && pack.run && pack.run.team_run_id) || "").trim();
      const g = Number(
        (pack && pack.run && pack.run.control_generation)
        || (pack && pack.mission && pack.mission.control_generation)
        || 0,
      );
      return { teamRunId, controlGen: g >= 1 ? g : 1 };
    } catch (_) {
      return { teamRunId: "", controlGen: 1 };
    }
  };

  const wireHomeAutoSentinel = () => {
    if (view.dataset.aiHomeWired === "1") return;
    view.dataset.aiHomeWired = "1";
    view.addEventListener("click", async (ev) => {
      const goBtn = ev.target && ev.target.closest ? ev.target.closest("[data-cc-go]") : null;
      if (goBtn) {
        ev.preventDefault();
        const dest = String(goBtn.getAttribute("data-cc-go") || "");
        const extra = String(goBtn.getAttribute("data-cc-id") || "").trim();
        const hash = typeof hashFromCcGo === "function" ? hashFromCcGo(dest, extra) : "";
        if (hash) {
          try { location.hash = hash; } catch (_) {}
        }
        return;
      }
      const t = ev.target && ev.target.closest
        ? ev.target.closest("[data-ai-home-allow],[data-ai-home-hold],[data-ai-home-escalate],[data-ai-home-more-evidence],[data-ai-map-focus],#ccAutoScanBtn")
        : null;
      if (!t) return;
      const mapFocusId = t.getAttribute("data-ai-map-focus");
      if (mapFocusId) {
        ev.preventDefault();
        const tab = String(t.getAttribute("data-ai-map-tab") || "observation").trim() || "observation";
        // Prefer Map tab so Spatial Context sheet is visible.
        try {
          const toggles = view.querySelectorAll("button");
          for (const b of toggles) {
            if (String(b.textContent || "").trim() === "Map") {
              b.click();
              break;
            }
          }
        } catch (_) {}
        if (overviewMapCtrl && typeof overviewMapCtrl.focusAiPlan === "function") {
          overviewMapCtrl.focusAiPlan(mapFocusId, tab, null);
        } else if (overviewMapCtrl && typeof overviewMapCtrl.openAiSheet === "function") {
          overviewMapCtrl.openAiSheet({ incident_id: mapFocusId }, tab, { force: true });
        }
        return;
      }
      if (t.id === "ccAutoScanBtn") {
        t.disabled = true;
        const copy = $("#ccAutoWatchCopy", view);
        if (copy) copy.textContent = "Sentinel scanning recent alarms…";
        const res = await quietScanRecent(3, { force: true });
        if (!isRouteCurrent(routeSeq)) return;
        if (res && res.ok > 0) {
          toast(res.ok === 1 ? "Auto plan ready — interrupt if needed" : `${res.ok} auto plans ready`, "ok");
          await refreshHomeAutoSentinel({ forceScan: false });
        } else {
          if (copy) copy.textContent = res && res.empty
            ? "No recent alarms to scan yet."
            : "Scan finished — Sentinel keeps watching.";
          t.disabled = false;
        }
        return;
      }
      const allowId = t.getAttribute("data-ai-home-allow");
      const holdId = t.getAttribute("data-ai-home-hold");
      const escalateId = t.getAttribute("data-ai-home-escalate");
      const moreEvId = t.getAttribute("data-ai-home-more-evidence");
      if (moreEvId) {
        ev.preventDefault();
        if (!(await softConfirm("Ask for more proof — Hold this plan while you gather photos or a person?"))) return;
        t.disabled = true;
        try {
          const resolved = await resolveOverviewTeamRun(moreEvId);
          const teamRunId = resolved.teamRunId;
          const controlGen = resolved.controlGen;
          if (!teamRunId) {
            toast("More evidence needs an open Team Run — open Mission Room first", "warn");
            t.disabled = false;
            return;
          }
          // L6 leftover request-evidence → Team intervene (overview_more_evidence_intervene).
          await api(`/ai/team/runs/${encodeURIComponent(teamRunId)}/intervene`, {
            method: "POST",
            body: {
              control_generation: controlGen,
              note: "operator requested more evidence",
              facts: ["evidence_requested"],
            },
            timeoutMs: INTELLIGENCE_TIMEOUT_MS,
          });
          toast("Asked for more evidence · Team Runtime", "warn");
          if (overviewMapCtrl && typeof overviewMapCtrl.focusAiPlan === "function") {
            overviewMapCtrl.focusAiPlan(moreEvId, "evidence", null);
          }
          await refreshHomeAutoSentinel({ forceScan: false });
        } catch (e) {
          toast(typeof softApiError === "function" ? softApiError(e) : "Could not request evidence", "err");
          t.disabled = false;
        }
        return;
      }
      if (escalateId) {
        ev.preventDefault();
        if (!(await softConfirm("Escalate Soft — Hold this plan and take the wheel for Response / Assign?"))) return;
        t.disabled = true;
        try {
          const resolved = await resolveOverviewTeamRun(escalateId);
          const teamRunId = resolved.teamRunId;
          const controlGen = resolved.controlGen;
          if (!teamRunId) {
            toast("Takeover needs an open Team Run — open Mission Room first", "warn");
            t.disabled = false;
            return;
          }
          await api(`/ai/team/runs/${encodeURIComponent(teamRunId)}/takeover`, {
            method: "POST",
            body: { control_generation: controlGen, trigger: "overview_escalate_takeover" },
            timeoutMs: INTELLIGENCE_TIMEOUT_MS,
          });
          toast("Takeover · Team Runtime. Open Response.", "warn");
          await refreshHomeAutoSentinel({ forceScan: false });
        } catch (e) {
          toast(typeof softApiError === "function" ? softApiError(e) : "Could not escalate", "err");
          t.disabled = false;
        }
        return;
      }
      const planId = allowId || holdId;
      if (!planId) return;
      const isContinue = !!allowId;
      const isReject = t.getAttribute("data-ds-reject") === "1";
      if (!(await softConfirm(isContinue
        ? "Continue Team — stay on the loop?"
        : isReject
          ? "Reject — stop this auto plan?"
          : "Hold — take the wheel?"))) return;
      t.disabled = true;
      try {
        const resolved = await resolveOverviewTeamRun(planId);
        const teamRunId = resolved.teamRunId;
        const controlGen = resolved.controlGen;
        if (!teamRunId) {
          toast(
            isContinue
              ? "Continue needs an open Team Run — open Mission Room first"
              : "Hold needs an open Team Run — open Mission Room first",
            "warn",
          );
          t.disabled = false;
          return;
        }
        if (!isContinue) {
          await api(`/ai/team/runs/${encodeURIComponent(teamRunId)}/hold`, {
            method: "POST",
            body: {
              control_generation: controlGen,
              trigger: isReject ? "overview_home_reject" : "overview_home_hold",
            },
            timeoutMs: INTELLIGENCE_TIMEOUT_MS,
          });
          toast(isReject ? "Rejected · Team Runtime hold" : "Held — Team Runtime", "warn");
          await refreshHomeAutoSentinel({ forceScan: false });
          return;
        }
        const res = await api(`/ai/team/runs/${encodeURIComponent(teamRunId)}/continue`, {
          method: "POST",
          body: { control_generation: controlGen, trigger: "overview_home_continue" },
          timeoutMs: INTELLIGENCE_TIMEOUT_MS,
        });
        toast("Continue Team", "ok");
        const record =
          res && typeof res === "object" && res.run && typeof res.run === "object"
            ? res.run
            : null;
        if (overviewMapCtrl && typeof overviewMapCtrl.focusAiPlan === "function") {
          overviewMapCtrl.focusAiPlan(planId, "execution", record);
        }
        await refreshHomeAutoSentinel({ forceScan: false });
        if (isContinue) {
          const it =
            record ||
            (lastAiPack.items || []).find((x) => String(x.incident_id || x.id || "") === planId);
          const eid = it && it.event_id != null ? String(it.event_id) : "";
          const did = it && it.device_id != null ? String(it.device_id).trim() : "";
          const deviceEl = view.querySelector("#ccHomeAssign #dcDeviceId");
          const alarmEl = view.querySelector("#ccHomeAssign #dcAlarmId");
          if (deviceEl && did) deviceEl.value = did;
          if (alarmEl && eid) alarmEl.value = `alarm-${eid}`;
          const out = view.querySelector("#ccHomeAssign #dcOut");
          if (out) out.textContent = eid
            ? "On the loop — assign a person below, or follow Execute on the map."
            : "On the loop — follow Execute on the map.";
        }
      } catch (e) {
        toast(typeof softApiError === "function" ? softApiError(e) : "Could not update plan", "err");
        t.disabled = false;
      }
    });
  };

  const refreshHomeAutoSentinel = async ({ forceScan = false } = {}) => {
    if (!isRouteCurrent(routeSeq)) return;
    if (!aiOnHome) {
      const quiet = { aiOn: false, items: [], settings: {} };
      try {
        const home = await api("/ai/home-context", { timeoutMs: 8000 });
        if (home && home.ok) {
          quiet.why = home.why;
          quiet.pulse = home.pulse;
          quiet.tonight = home.tonight;
          quiet.lamps = (home.tonight && home.tonight.lamps) || home.lamps;
          quiet.needs_you = home.needs_you;
        }
      } catch (_) {}
      lastAiPack = quiet;
      patchOverviewIncidentFeed(view, { incidentCards, responders, timelineItems, aiPack: quiet });
      return;
    }
    let items = [];
    let settings = {};
    try {
      const [listRes, setRes] = await Promise.allSettled([
        api(buildGetAiIncidentsPath(), { timeoutMs: INTELLIGENCE_TIMEOUT_MS }),
        api(buildGetAiSettingsPath(), { timeoutMs: INTELLIGENCE_TIMEOUT_MS }),
      ]);
      if (!isRouteCurrent(routeSeq)) return;
      if (listRes.status === "fulfilled") {
        items = Array.isArray(listRes.value && listRes.value.items) ? listRes.value.items : [];
      }
      if (setRes.status === "fulfilled") settings = setRes.value || {};
    } catch (_) {}

    // Team continues server-side. Home never silently POSTs Continue (no SPA rule desk).
    const waiting = items.filter((it) => {
      const st = String(it.status || "").toLowerCase();
      return !st || st === "pending" || st === "open";
    });
    if (waiting.length === 0) {
      const scanned = await quietScanRecent(2, { force: !!forceScan });
      if (scanned && scanned.ok > 0) {
        try {
          const again = await api(buildGetAiIncidentsPath(), { timeoutMs: INTELLIGENCE_TIMEOUT_MS });
          items = Array.isArray(again && again.items) ? again.items : items;
        } catch (_) {}
      }
    }

    lastAiPack = { aiOn: true, items, settings };
    try {
      const home = await api("/ai/home-context", { timeoutMs: 8000 });
      if (home && home.ok) {
        lastAiPack.why = home.why;
        lastAiPack.pulse = home.pulse;
        lastAiPack.tonight = home.tonight;
        lastAiPack.lamps = (home.tonight && home.tonight.lamps) || home.lamps;
        lastAiPack.decision_actions = home.decision_actions;
        lastAiPack.needs_you = home.needs_you;
      }
    } catch (_) {}
    try {
      const leadId = String(
        (Array.isArray(items) && items[0] && (items[0].incident_id || items[0].id)) || "",
      ).trim();
      const qs = leadId ? `?incident_id=${encodeURIComponent(leadId)}` : "";
      const softLanes = await api(`/ai/soft-lanes${qs}`, { timeoutMs: 8000 });
      if (softLanes && softLanes.soft === true) {
        lastAiPack.softLanes = softLanes;
        try {
          if (typeof paintSoftLanesMapChip === "function") paintSoftLanesMapChip(view, softLanes);
        } catch (_) {}
      }
    } catch (_) {}
    patchOverviewIncidentFeed(view, {
      incidentCards,
      responders,
      timelineItems,
      aiPack: lastAiPack,
    });
    wireHomeAutoSentinel();
    try {
      if (overviewMapCtrl && typeof overviewMapCtrl.setAiWaitingPlans === "function") {
        overviewMapCtrl.setAiWaitingPlans(items);
      }
    } catch (_) {}
  };

  void refreshHomeAutoSentinel({ forceScan: false });

  // Home Proof & close — real proof-of-response + SLA for the lead case.
  void (async () => {
    if (typeof applyHomeProofPanel !== "function") return;
    const lead = Array.isArray(incidentCards) && incidentCards[0] ? incidentCards[0] : null;
    const eid = lead && lead.eventId != null ? String(lead.eventId).trim() : "";
    if (!eid) {
      applyHomeProofPanel(view, { emptyReason: "No open case yet — proof appears after an alarm lands." });
      return;
    }
    try {
      const [proof, sla] = await Promise.all([
        api(`/incidents/${encodeURIComponent(eid)}/proof-of-response`, { timeoutMs: 10000 }),
        api(`/incidents/${encodeURIComponent(eid)}/sla`, { timeoutMs: 10000 }),
      ]);
      if (!isRouteCurrent(routeSeq)) return;
      applyHomeProofPanel(view, { eventId: eid, proof, sla });
    } catch (_) {
      if (!isRouteCurrent(routeSeq)) return;
      applyHomeProofPanel(view, {
        eventId: eid,
        emptyReason: "Proof is slow right now — open the case to continue.",
      });
    }
  })();

  // Home people strip — Soft poll GET /locations/latest @ 12s (ZD-TRANSPORT · F13 freeze).
  // Not a second Live stream: map pins Live path remains SSE /locations/stream;
  // Soft UI refresh of the same phone_locations SoR — spa-transport-duals-freeze.md.
  void (async () => {
    const emptyEl = $("#ccHomePeopleEmpty", view);
    const bodyEl = $("#ccHomePeopleBody", view);
    if (!emptyEl || !bodyEl) return;
    const paintPeople = async () => {
      if (!isRouteCurrent(routeSeq)) return;
      try {
        const path = typeof buildLocationsLatestPath === "function"
          ? buildLocationsLatestPath({ limit: 12 })
          : "/locations/latest?limit=12";
        const pack = await api(path, { timeoutMs: typeof LOCATIONS_CONSOLE_TIMEOUT_MS === "number" ? LOCATIONS_CONSOLE_TIMEOUT_MS : 8000 });
        if (!isRouteCurrent(routeSeq)) return;
        const items = Array.isArray(pack && pack.items) ? pack.items : [];
        const painted = typeof paintHomePeopleStripHtml === "function"
          ? paintHomePeopleStripHtml(items)
          : { empty: true, html: "" };
        if (painted.empty) {
          emptyEl.hidden = false;
          emptyEl.textContent = painted.text
            || "No shared phone locations yet. When responders share GPS, they appear here and on the map — stale points stay labeled.";
          bodyEl.hidden = true;
          bodyEl.innerHTML = "";
        } else {
          emptyEl.hidden = true;
          emptyEl.textContent = "";
          bodyEl.hidden = false;
          bodyEl.innerHTML = painted.html;
        }
      } catch (_) {
        if (!isRouteCurrent(routeSeq)) return;
        emptyEl.hidden = false;
        emptyEl.textContent = "Location share is off or slow — open Map after responders share GPS.";
        bodyEl.hidden = true;
      }
    };
    await paintPeople();
    const peoplePoll = setInterval(() => {
      if (!isRouteCurrent(routeSeq) || !view.isConnected) {
        clearInterval(peoplePoll);
        return;
      }
      paintPeople();
    }, 12000);
  })();

  // Map tab shortcut + People strip → Assign
  if (homeWb && homeWb.dataset.mapTabWired !== "1") {
    homeWb.dataset.mapTabWired = "1";
    homeWb.addEventListener("click", (ev) => {
      const person = ev.target && ev.target.closest ? ev.target.closest("[data-home-person-pick]") : null;
      if (person && homeWb.contains(person)) {
        ev.preventDefault();
        const uid = String(person.getAttribute("data-user-id") || "").trim();
        const label = String(person.getAttribute("data-person-label") || uid || "Person").trim();
        const stale = person.getAttribute("data-stale") === "1";
        const lat = Number(person.getAttribute("data-lat"));
        const lng = Number(person.getAttribute("data-lng"));
        if (typeof openHomeAssignWorkbench === "function") {
          openHomeAssignWorkbench(view, {
            responder: uid,
            outText: stale
              ? `Person “${label}” is stale GPS — Soft nearest match prefers fresh pins.`
              : `Person “${label}” filled for Assign.`,
          });
        }
        if (overviewMapCtrl && Number.isFinite(lat) && Number.isFinite(lng) && typeof overviewMapCtrl.focusLngLat === "function") {
          overviewMapCtrl.focusLngLat(lng, lat, 14);
        } else if (overviewMapCtrl && uid && typeof overviewMapCtrl.focusPerson === "function") {
          overviewMapCtrl.focusPerson(uid);
        }
        toast(stale ? `${label} · stale · filled Assign` : `${label} · filled Assign`, stale ? "warn" : "ok");
        return;
      }
      const tab = ev.target && ev.target.closest ? ev.target.closest("[data-home-map-tab]") : null;
      if (!tab || !homeWb.contains(tab)) return;
      ev.preventDefault();
      const mapBtn = view.querySelector('[data-cc-home-tab="map"], button[aria-pressed][data-tab="map"], .croc-cc-tabs button');
      // Prefer explicit home map toggle if present
      const toggles = view.querySelectorAll("button");
      let hit = null;
      for (const b of toggles) {
        if (String(b.textContent || "").trim() === "Map") { hit = b; break; }
      }
      if (hit) hit.click();
      else if (mapBtn) mapBtn.click();
    });
  }

  // Home scene photos — real thumbs when camera snapshot domain is open for this admin.
  void (async () => {
    const emptyEl = $("#ccHomePhotosEmpty", view);
    const gridEl = $("#ccHomePhotosGrid", view);
    if (!emptyEl || !gridEl) return;
    const camOn = !!(state.me && state.me.camera_snapshot_enabled);
    const canAdminCam = hasRole("admin");
    if (!camOn || !canAdminCam) {
      emptyEl.hidden = false;
      emptyEl.textContent = camOn
        ? "Scene photos open when cameras are ready for this account."
        : "Scene photos open when cameras are ready for this account.";
      gridEl.hidden = true;
      return;
    }
    // Avoid hammering /admin/camera-snapshots when role policy lacks domain_cameras (403 spam).
    try {
      if (sessionStorage.getItem("croc_home_photos_forbidden") === "1") {
        emptyEl.hidden = false;
        emptyEl.textContent = "Scene photos stay on Photos when your account role allows camera review.";
        gridEl.hidden = true;
        return;
      }
    } catch (_) {}
    try {
      const path = typeof buildGetAdminCameraSnapshotsPath === "function"
        ? buildGetAdminCameraSnapshotsPath({ limit: 8 })
        : "/admin/camera-snapshots?limit=8";
      const scoped = typeof cameraApiPathWithOwner === "function"
        ? cameraApiPathWithOwner(path, "")
        : path;
      const pack = await api(scoped, { timeoutMs: 12000 });
      if (!isRouteCurrent(routeSeq)) return;
      try { sessionStorage.removeItem("croc_home_photos_forbidden"); } catch (_) {}
      const items = ((pack && pack.items) || [])
        .filter((it) => it && it.has_image)
        .map((it) => {
          const sid = String(it.snapshot_id || it.id || "").trim();
          let imgSrc = "";
          if (sid && typeof buildGetAdminCameraSnapshotImagePath === "function" && typeof apiBase === "function") {
            const rel = typeof cameraApiPathWithOwner === "function"
              ? cameraApiPathWithOwner(buildGetAdminCameraSnapshotImagePath(sid), "")
              : buildGetAdminCameraSnapshotImagePath(sid);
            imgSrc = apiBase() + rel;
          }
          return { ...it, _imgSrc: imgSrc };
        });
      const painted = typeof paintHomePhotoThumbsHtml === "function"
        ? paintHomePhotoThumbsHtml(items)
        : { empty: true, html: "" };
      if (painted.empty) {
        emptyEl.hidden = false;
        emptyEl.textContent = "No scene photos yet. When a camera captures an alarm, thumbs appear here.";
        gridEl.hidden = true;
        gridEl.innerHTML = "";
      } else {
        emptyEl.hidden = true;
        gridEl.hidden = false;
        gridEl.innerHTML = painted.html;
      }
    } catch (e) {
      if (!isRouteCurrent(routeSeq)) return;
      const msg = String((e && e.message) || e || "");
      const m = msg.match(/^(\d{3})\b/);
      const st = m
        ? Number(m[1])
        : Number((e && (e.status || e.statusCode || (e.response && e.response.status))) || 0);
      if (st === 403) {
        try { sessionStorage.setItem("croc_home_photos_forbidden", "1"); } catch (_) {}
        emptyEl.hidden = false;
        emptyEl.textContent = "Scene photos stay on Photos when your account role allows camera review.";
        gridEl.hidden = true;
        return;
      }
      if (st === 400 && /owner_admin/i.test(msg)) {
        emptyEl.hidden = false;
        emptyEl.textContent =
          "Scene photos: open Photos and choose an account owner first (platform scope).";
        gridEl.hidden = true;
        return;
      }
      emptyEl.hidden = false;
      emptyEl.textContent = "No scene photos yet — open Photos after the next camera capture.";
      gridEl.hidden = true;
    }
  })();

  if (canUseOpsMap(state.me)) {
    const mapCanvas = $("#cc-overview-map-canvas", view);
    if (mapCanvas) {
      overviewMapCtrl = createOverviewMapStageController({
        container: mapCanvas,
        isAlive: () => isRouteCurrent(routeSeq),
        api,
        buildMapSnapshotPath: () => buildGetDevicesMapSnapshotPath({ skipCache: false }),
        setChildMarkup,
        escapeHtml,
        locationsEnabled: true,
        toast,
        onSelectPerson: (pack) => {
          const uid = String((pack && pack.userId) || "").trim();
          const label = String((pack && pack.label) || uid || "Person").trim();
          const stale = !!(pack && pack.stale);
          if (typeof openHomeAssignWorkbench === "function") {
            openHomeAssignWorkbench(view, {
              responder: uid,
              outText: stale
                ? `Person “${label}” is stale GPS — Soft nearest match prefers fresh pins.`
                : `Person “${label}” filled for Assign.`,
            });
          }
          toast(stale
            ? `${label} · stale · Assign armed — open Home workbench chip (map stays large)`
            : `${label} · Assign armed — open Home workbench chip (map stays large)`, stale ? "warn" : "ok");
        },
        onSelectDevice: (pack) => {
          const did = String((pack && pack.deviceId) || "").trim();
          const label = String((pack && pack.label) || did || "Unit").trim();
          if (typeof openHomeAssignWorkbench === "function") {
            openHomeAssignWorkbench(view, {
              deviceId: did,
              outText: did
                ? `Device “${label}” filled for Assign — pick a person or Find nearest.`
                : `Unit “${label}” has no device id — open All boards if needed.`,
            });
          }
          toast(did
            ? `${label} · Assign armed — open Home workbench chip (map stays large)`
            : `${label} · no device id`, did ? "ok" : "warn");
        },
      });
      try {
        await overviewMapCtrl.init();
        await overviewMapCtrl.refresh();
      } catch (_) {}
    }
  }

  if (typeof window.__overviewLandingPickDispose === "function") {
    try { window.__overviewLandingPickDispose(); } catch (_) {}
    window.__overviewLandingPickDispose = null;
  }
  const ovLandingPick = $("#croc-overview-landing-pick", view);
  if (ovLandingPick) {
    window.__overviewLandingPickDispose = wireHomeLandingPick(ovLandingPick, { toast, me: state.me });
  }

  let lastOverviewLivePollToastAt = 0;

  const refreshOverviewLive = async () => {
    if (!isRouteCurrent(routeSeq)) return;
    try {
      const [tickRes, alarmRes, dispRes] = await Promise.allSettled([
        fetchOverviewLivePair(api, {
          beforeFetch: () => {
            bustApiGetCachedPrefix("/dashboard/overview");
            bustApiGetCachedPrefix("/devices");
            bustApiGetCachedPrefix("/events");
          },
        }),
        api("/events?category=alarm&min_level=warn&limit=12"),
        api("/responder-dispatches/mine?limit=12"),
      ]);
      if (!isRouteCurrent(routeSeq)) return;
      const tick = tickRes.status === "fulfilled" ? tickRes.value : { ov: null, list: null };
      const liveStatus = assessOverviewLiveTick(tick);
      syncOverviewLivePollDegradedBanner(view, liveStatus);
      const toastGate = shouldToastOverviewLivePollFail(lastOverviewLivePollToastAt, liveStatus);
      if (toastGate.toast) {
        toast(liveStatus.message, "warn");
        lastOverviewLivePollToastAt = toastGate.at;
      }
      if (tick.ov) ov = tick.ov;
      if (tick.list) list = tick.list;
      state.overviewCache = { ov, list, ts: Date.now() };
      const liveDevices = Array.isArray(list && list.items) ? list.items : devices;
      patchOverviewHeader(buildOverviewKpiPatch(ov, liveDevices, { health: state.health, isSuper, me: state.me }));
      // Do NOT re-wire CC chrome on live poll — stacking click handlers on
      // Home/Operations triggers makes open+close cancel (dead clicks).
      const alarmPayload = alarmRes.status === "fulfilled" ? alarmRes.value : null;
      const dispPayload = dispRes.status === "fulfilled" ? dispRes.value : null;
      const feed = mapAlarmEventsToFeed(alarmPayload, dispPayload);
      patchOverviewIncidentFeed(view, { ...feed, aiPack: lastAiPack });
      if (aiOnHome) void refreshHomeAutoSentinel({ forceScan: false });
      if (overviewMapCtrl) {
        try {
          await overviewMapCtrl.refresh();
          if (typeof overviewMapCtrl.setAiWaitingPlans === "function" && lastAiPack && lastAiPack.items) {
            overviewMapCtrl.setAiWaitingPlans(lastAiPack.items);
          }
        } catch (_) {}
      }
    } catch (e) {
      if (!isRouteCurrent(routeSeq)) return;
      const liveStatus = assessOverviewLiveTick({ ov: null, list: null });
      syncOverviewLivePollDegradedBanner(view, liveStatus);
      const toastGate = shouldToastOverviewLivePollFail(lastOverviewLivePollToastAt, liveStatus);
      if (toastGate.toast) {
        toast((typeof softApiError === "function" ? softApiError(e, liveStatus.message) : null) || liveStatus.message, "warn");
        lastOverviewLivePollToastAt = toastGate.at;
      }
    }
  };
  scheduleRouteTicker(routeSeq, "overview-live", refreshOverviewLive, OVERVIEW_LIVE_MS);
});


// ── platform-control.route.js ─────────────────────────────────────
/**
 * Route: #/platform-control — Federation, SA platform/custody, cross-tenant shares, security ops.
 * Tenant admin may open ?tab=shares when cross_tenant_share is enabled; SA tabs stay SA-only.
 */

registerRoute("platform-control", async (view, _args, routeSeq) => {
  setCrumb("Platform");
  const me = state.me || {};
  const role = String(me.role || "");
  const isSaOp = hasRole("superadmin") || role === "sa_staff";
  const shareCapOn = catalogEnabled(me, "cross_tenant_share");
  const canTenantShares = hasRole("admin") && shareCapOn;
  if (!isSaOp && !canTenantShares) {
    mountView(view, capabilityUnavailableHtml({
      title: "Platform",
      detail: "Platform operator or organization share access required.",
      backHref: "#/settings",
    }));
    return;
  }

  const params = new URLSearchParams((location.hash.split("?")[1] || ""));
  let tab = String(params.get("tab") || (isSaOp ? "domains" : "shares")).trim();
  if (!isSaOp) {
    if (tab !== "shares") {
      mountView(view, capabilityUnavailableHtml({
        title: "Platform operator tab",
        detail: "Account admins may use Organization shares only. Account domains, board custody, group shares, multi-site HQ, security and backup require platform staff.",
        backHref: "#/platform-control?tab=shares",
        backLabel: "Open shares",
      }));
      return;
    }
  }
  const fedOn = catalogEnabled(me, "federation");
  const legacySecOn = catalogEnabled(me, "admin_security") || catalogEnabled(me, "mac_acl_audit");
  const conceptSecOn =
    catalogEnabled(me, "physical_inhibit") ||
    catalogEnabled(me, "adapter_certification") ||
    catalogEnabled(me, "adapter_cert") ||
    catalogEnabled(me, "delivery_ledger") ||
    catalogEnabled(me, "site_authority");
  // Always expose Security for SA so concepts stay verifiable (Ready or Verify).
  const secOn = isSaOp || legacySecOn || conceptSecOn;
  const _backupOn = catalogEnabled(me, "backup_worker") || catalogEnabled(me, "manual_backup") || isSaOp;
  const showShares = isSaOp || canTenantShares;
  const showFed = isSaOp; // openable for verify even when federation flag closed

  // FE-S1 source token stays: href="#/platform-control?tab=ng-shares"
  const tabBtn = (id, label, show) =>
    show
      ? `<button type="button" class="btn sm ${tab === id ? "" : "secondary"} btn-tap" data-cc-go="platform-control?tab=${id}">${label}</button>`
      : "";
  const tabsHtml = `
    <div class="row" style="gap:8px;flex-wrap:wrap;margin:10px 0 14px">
      ${tabBtn("domains", "Account domains", isSaOp)}
      ${tabBtn("custody", "Board custody", isSaOp)}
      ${tabBtn("ng-shares", "Group shares", isSaOp)}
      ${tabBtn("shares", "Organization shares", showShares)}
      ${tabBtn("federation", "Multi-site HQ", showFed)}
      ${tabBtn("security", "Security", secOn)}
      ${tabBtn("backup", "Backup schedule", isSaOp)}
    </div>`;

  mountView(view, `<div class="card"><p class="muted">Loading platform control…</p></div>`);

  try {
    if (tab === "federation") {
      if (!isSaOp) {
        mountView(view, capabilityUnavailableHtml({
          title: "Multi-site HQ",
          detail: "Multi-site HQ is for platform staff.",
        }));
        return;
      }
      if (!fedOn) {
        mountView(view, `<div class="card glass-panel glass-panel--panel">
          <h2>Platform · Multi-site HQ</h2>
          ${tabsHtml}
          <div class="glass-panel glass-panel--inset" style="padding:16px;margin-top:8px">
            <span class="badge neutral">Verify</span>
            <p style="margin:10px 0 0;line-height:1.45">Multi-site headquarters links partner organizations under one view. The live consent tools open when this capability is turned on for the deployment.</p>
          </div>
        </div>`);
        return;
      }
      const [metrics, tenants] = await Promise.all([
        api(buildGetFederationMetricsPath(), { timeoutMs: PLATFORM_CONTROL_TIMEOUT_MS }),
        api(buildGetFederationTenantsPath(), { timeoutMs: PLATFORM_CONTROL_TIMEOUT_MS }),
      ]);
      if (!isRouteCurrent(routeSeq)) return;
      const fedTablesHtml = buildFederationTablesHtml(metrics || {}, tenants || {}, { escapeHtml });
      mountView(view, `<div class="card glass-panel glass-panel--panel">
        <h2>Platform · Multi-site HQ</h2>
        ${tabsHtml}
        ${fedTablesHtml}
        <div class="glass-panel glass-panel--inset" style="padding:12px;margin-top:14px">
          <strong>Consent</strong>
          <div class="inline-form" style="margin-top:8px">
            <label class="field"><span>Action</span>
              <select id="fedAction"><option value="join">join</option><option value="leave">leave</option></select>
            </label>
            <label class="field"><span>HQ network ID</span><input id="fedId" class="mono" /></label>
          </div>
          <div class="row" style="margin-top:8px"><button class="btn btn-tap" type="button" id="fedConsentBtn">Submit consent</button></div>
          <p id="fedMsg" class="muted" aria-live="polite"></p>
        </div>
      </div>`);
      const btn = $("#fedConsentBtn", view);
      if (btn) {
        btn.onclick = async () => {
          const action = String(($("#fedAction", view) && $("#fedAction", view).value) || "join");
          const federationId = String(($("#fedId", view) && $("#fedId", view).value) || "").trim();
          if (!window.confirm(`Submit federation ${action}${federationId ? ` for ${federationId}` : ""}?`)) return;
          btn.disabled = true;
          try {
            await api(buildPostFederationConsentPath(), {
              method: "POST",
              body: { action, federation_id: federationId || undefined },
              timeoutMs: PLATFORM_CONTROL_TIMEOUT_MS,
            });
            toast("Consent submitted", "ok");
            location.hash = "#/platform-control?tab=federation";
          } catch (e) {
            const m = $("#fedMsg", view);
            if (m) m.textContent = String(e.message || e);
            toast(String(e.message || e), "err");
            btn.disabled = false;
          }
        };
      }
      return;
    }

    if (tab === "custody") {
      if (!isSaOp) {
        mountView(view, capabilityUnavailableHtml({
          title: "Board custody",
          detail: "SA / superadmin only.",
          backHref: "#/platform-control?tab=shares",
          backLabel: "Open shares",
        }));
        return;
      }
      mountView(view, `<div class="card glass-panel glass-panel--panel">
        <h2>Platform · Board custody</h2>
        ${tabsHtml}
        <p class="muted">Move a board to another account owner, or reclaim it to platform custody. Platform staff only.</p>
        <div class="inline-form">
          <label class="field"><span>Action</span>
            <select id="custodyAction"><option value="transfer">Transfer</option><option value="reclaim">Reclaim</option></select>
          </label>
          <label class="field"><span>Board serial</span><input id="custodyDeviceId" class="mono" autocomplete="off" /></label>
          <label class="field"><span>New account owner</span><input id="custodyToOwner" class="mono" placeholder="username — required for transfer" autocomplete="off" /></label>
        </div>
        <div class="row" style="margin-top:10px"><button class="btn btn-tap" type="button" id="custodyBtn">Apply</button></div>
        <p id="custodyMsg" class="muted" aria-live="polite"></p>
      </div>`);
      const btn = $("#custodyBtn", view);
      if (btn) {
        btn.onclick = async () => {
          const action = String(($("#custodyAction", view) && $("#custodyAction", view).value) || "transfer");
          const deviceId = String(($("#custodyDeviceId", view) && $("#custodyDeviceId", view).value) || "").trim();
          const toOwner = String(($("#custodyToOwner", view) && $("#custodyToOwner", view).value) || "").trim();
          if (!deviceId) {
            ($("#custodyMsg", view) || {}).textContent = "Board serial is required.";
            return;
          }
          if (action === "transfer" && !toOwner) {
            ($("#custodyMsg", view) || {}).textContent = "New account owner is required for transfer.";
            return;
          }
          if (!window.confirm(action === "transfer"
            ? `Transfer board ${deviceId} to ${toOwner}?`
            : `Reclaim board ${deviceId} to platform custody?`)) return;
          btn.disabled = true;
          try {
            await api(buildPostSaDevicesCustodyPath(), {
              method: "POST",
              body: {
                action,
                device_id: deviceId,
                to_owner_admin: toOwner || undefined,
              },
              timeoutMs: PLATFORM_CONTROL_TIMEOUT_MS,
            });
            if (!isRouteCurrent(routeSeq)) return;
            const msgEl = $("#custodyMsg", view);
            if (msgEl) {
              msgEl.textContent = action === "transfer"
                ? `Transferred ${deviceId} to ${toOwner}`
                : `Reclaimed ${deviceId}`;
            }
            toast("Custody updated", "ok");
          } catch (e) {
            const soft = softApiError(e);
            ($("#custodyMsg", view) || {}).textContent = soft;
            toast(soft, "err");
          } finally {
            btn.disabled = false;
          }
        };
      }
      return;
    }

    if (tab === "ng-shares") {
      if (!isSaOp) {
        mountView(view, capabilityUnavailableHtml({
          title: "Group shares",
          detail: "Platform staff only.",
          backHref: "#/platform-control?tab=shares",
          backLabel: "Open shares",
        }));
        return;
      }
      const qOwner = String(params.get("owner_admin") || "").trim();
      const qGrantee = String(params.get("grantee") || "").trim();
      const qGroup = String(params.get("group") || "").trim();
      const qRevoked = params.get("include_revoked") === "1" || params.get("include_revoked") === "true";
      const qCursor = String(params.get("cursor") || "").trim();
      const query = { limit: 100 };
      if (qOwner) query.owner_admin = qOwner;
      if (qGrantee) query.grantee_username = qGrantee;
      if (qGroup) query.group_display = qGroup;
      if (qRevoked) query.include_revoked = true;
      if (qCursor) query.cursor_owner_admin = qCursor;
      let pack = { items: [], count: 0, has_more: false, next_cursor_owner_admin: null };
      let loadError = "";
      try {
        pack = await api(buildGetSaNotificationGroupSharesPath(query), {
          timeoutMs: PLATFORM_CONTROL_TIMEOUT_MS,
        });
      } catch (e) {
        loadError = softApiError(e);
      }
      if (!isRouteCurrent(routeSeq)) return;
      const items = Array.isArray(pack.items) ? pack.items : [];
      const nextCursor = String(pack.next_cursor_owner_admin || "").trim();
      const hasMore = Boolean(pack.has_more) && Boolean(nextCursor);
      const nextHref = (() => {
        if (!hasMore) return "";
        const p = new URLSearchParams();
        p.set("tab", "ng-shares");
        if (qOwner) p.set("owner_admin", qOwner);
        if (qGrantee) p.set("grantee", qGrantee);
        if (qGroup) p.set("group", qGroup);
        if (qRevoked) p.set("include_revoked", "1");
        p.set("cursor", nextCursor);
        return `#/platform-control?${p.toString()}`;
      })();
      mountView(view, `<div class="card glass-panel glass-panel--panel">
        <h2>Platform · Group shares</h2>
        ${tabsHtml}
        <p class="muted">Account-wide group shares. Requires board-admin staff access.</p>
        ${loadError ? `<p class="muted">${escapeHtml(loadError)}</p>` : ""}
        <div class="glass-panel glass-panel--inset" style="padding:12px;margin-bottom:12px">
          <strong>Filters</strong>
          <div class="inline-form" style="margin-top:8px">
            <label class="field"><span>Account owner</span><input id="ngShareOwner" class="mono" value="${escapeHtml(qOwner)}" placeholder="exact username" /></label>
            <label class="field"><span>Shared with</span><input id="ngShareGrantee" class="mono" value="${escapeHtml(qGrantee)}" /></label>
            <label class="field"><span>Group</span><input id="ngShareGroup" class="mono" value="${escapeHtml(qGroup)}" /></label>
            <label class="field"><span>Include revoked</span>
              <select id="ngShareRevoked"><option value="0"${qRevoked ? "" : " selected"}>No</option><option value="1"${qRevoked ? " selected" : ""}>Yes</option></select>
            </label>
          </div>
          <div class="row" style="margin-top:8px;gap:8px">
            <button class="btn btn-tap" type="button" id="ngShareLoadBtn">Apply filters</button>
            ${qCursor ? `<button type="button" class="btn secondary btn-tap" data-cc-go="platform-control?tab=ng-shares${qOwner ? `&owner_admin=${encodeURIComponent(qOwner)}` : ""}${qGrantee ? `&grantee=${encodeURIComponent(qGrantee)}` : ""}${qGroup ? `&group=${encodeURIComponent(qGroup)}` : ""}${qRevoked ? "&include_revoked=1" : ""}">Back to first page</button>` : ""}
          </div>
        </div>
        <p class="muted">${escapeHtml(String(pack.count != null ? pack.count : items.length))} share(s)${hasMore ? " · more available" : ""}</p>
        <table class="table"><thead><tr>
          <th>Account owner</th><th>Group</th><th>Shared with</th><th>Can view</th><th>Can operate</th><th>Label</th><th>Granted</th><th>Revoked</th>
        </tr></thead>
          <tbody>${items.map((it) => {
            const granted = it.granted_at != null ? fmtTs(it.granted_at) : "—";
            const revoked = it.revoked_at != null ? fmtTs(it.revoked_at) : "—";
            return `<tr>
              <td class="mono">${escapeHtml(String(it.owner_admin || "—"))}</td>
              <td>${escapeHtml(String(it.group_display || it.group_key || "—"))}</td>
              <td class="mono">${escapeHtml(String(it.grantee_username || "—"))}</td>
              <td>${it.can_view ? "yes" : "no"}</td>
              <td>${it.can_operate ? "yes" : "no"}</td>
              <td>${escapeHtml(String(it.label || "—"))}</td>
              <td class="mono">${escapeHtml(String(granted))}</td>
              <td class="mono">${escapeHtml(String(revoked))}</td>
            </tr>`;
          }).join("") || `<tr><td colspan="8" class="muted">No group shares yet.</td></tr>`}</tbody></table>
        ${hasMore ? `<div class="row" style="margin-top:10px"><a class="btn secondary btn-tap" href="${escapeHtml(nextHref)}">Next page</a></div>` : ""}
      </div>`);
      const loadBtn = $("#ngShareLoadBtn", view);
      if (loadBtn) {
        loadBtn.onclick = () => {
          const owner = String(($("#ngShareOwner", view) && $("#ngShareOwner", view).value) || "").trim();
          const grantee = String(($("#ngShareGrantee", view) && $("#ngShareGrantee", view).value) || "").trim();
          const group = String(($("#ngShareGroup", view) && $("#ngShareGroup", view).value) || "").trim();
          const revoked = String(($("#ngShareRevoked", view) && $("#ngShareRevoked", view).value) || "0") === "1";
          const p = new URLSearchParams();
          p.set("tab", "ng-shares");
          if (owner) p.set("owner_admin", owner);
          if (grantee) p.set("grantee", grantee);
          if (group) p.set("group", group);
          if (revoked) p.set("include_revoked", "1");
          location.hash = `#/platform-control?${p.toString()}`;
        };
      }
      return;
    }

    if (tab === "shares") {
      if (!shareCapOn) {
        mountView(view, capabilityUnavailableHtml({
          title: "Organization shares",
          detail: "Organization sharing is not opened for this account yet.",
        }));
        return;
      }
      let pack = { items: [] };
      let loadError = "";
      try {
        pack = await api(buildGetSharesPath(), { timeoutMs: PLATFORM_CONTROL_TIMEOUT_MS });
      } catch (e) {
        loadError = softApiError(e);
      }
      if (!isRouteCurrent(routeSeq)) return;
      const items = Array.isArray(pack.items) ? pack.items : [];
      const softShareScope = (type, target) => {
        const t = String(type || "").toLowerCase();
        const label = t === "device" ? "Board" : t === "group" ? "Group" : (t.replace(/_/g, " ") || "Scope");
        const tgt = String(target || "").trim();
        return tgt ? `${label}: ${tgt}` : label;
      };
      mountView(view, `<div class="card glass-panel glass-panel--panel">
        <h2>Platform · Organization shares</h2>
        ${tabsHtml}
        <p class="muted">Issue a one-time share key so another account can view a board or group for a limited time.</p>
        ${loadError ? `<p class="muted">${escapeHtml(loadError)}</p>` : ""}
        <div class="glass-panel glass-panel--inset" style="padding:12px;margin-bottom:12px">
          <strong>Create share key</strong>
          <div class="inline-form" style="margin-top:8px">
            <label class="field"><span>Share what</span>
              <select id="shareScopeType">
                <option value="device" selected>Board</option>
                <option value="group">Group</option>
              </select>
            </label>
            <label class="field"><span>Board serial or group</span><input id="shareScopeTarget" class="mono" autocomplete="off" /></label>
            <label class="field"><span>Expires in (hours)</span><input id="shareTtl" type="number" value="24" min="1" /></label>
          </div>
          <div class="row" style="margin-top:8px;gap:8px">
            <button class="btn btn-tap" type="button" id="shareCreateBtn">Create key</button>
            <button class="btn secondary btn-tap" type="button" id="shareRedeemBtn">Redeem key</button>
          </div>
          <label class="field wide" style="margin-top:8px"><span>Redeem key</span><input id="shareRedeemKey" class="mono" autocomplete="off" /></label>
          <p id="shareMsg" class="muted" aria-live="polite"></p>
        </div>
        <table class="table"><thead><tr><th>Share</th><th>Target</th><th>Status</th><th></th></tr></thead>
          <tbody>${items.map((it) => {
            const id = String(it.id || it.share_id || "");
            return `<tr>
              <td class="mono">${escapeHtml(id || "—")}</td>
              <td>${escapeHtml(softShareScope(it.scope_type, it.scope_target))}</td>
              <td>${escapeHtml(String(it.status || "—"))}</td>
              <td>${id ? `<button class="btn sm secondary btn-tap share-revoke" type="button" data-id="${escapeHtml(id)}">Revoke</button>` : ""}</td>
            </tr>`;
          }).join("") || `<tr><td colspan="4" class="muted">No cross-account shares yet.</td></tr>`}</tbody></table>
      </div>`);
      const createBtn = $("#shareCreateBtn", view);
      if (createBtn) {
        createBtn.onclick = async () => {
          const scopeType = String(($("#shareScopeType", view) && $("#shareScopeType", view).value) || "").trim();
          const scopeTarget = String(($("#shareScopeTarget", view) && $("#shareScopeTarget", view).value) || "").trim();
          const ttl = Number(($("#shareTtl", view) && $("#shareTtl", view).value) || 24);
          if (!scopeType || !scopeTarget) {
            ($("#shareMsg", view) || {}).textContent = "Choose what to share and enter the board serial or group.";
            return;
          }
          if (!window.confirm(`Create a share key for ${softShareScope(scopeType, scopeTarget)} (expires in ${ttl}h)?`)) return;
          createBtn.disabled = true;
          try {
            const created = await api(buildPostSharesKeysPath(), {
              method: "POST",
              body: { scope_type: scopeType, scope_target: scopeTarget, ttl_hours: ttl, can_view: true, can_operate: false },
              timeoutMs: PLATFORM_CONTROL_TIMEOUT_MS,
            });
            const m = $("#shareMsg", view);
            if (m) m.textContent = `Key created: ${String(created.key || created.id || "ok")} — copy now; it is shown once.`;
            toast("Share key created", "ok");
          } catch (e) {
            const soft = softApiError(e);
            ($("#shareMsg", view) || {}).textContent = soft;
            toast(soft, "err");
          } finally {
            createBtn.disabled = false;
          }
        };
      }
      const redeemBtn = $("#shareRedeemBtn", view);
      if (redeemBtn) {
        redeemBtn.onclick = async () => {
          const key = String(($("#shareRedeemKey", view) && $("#shareRedeemKey", view).value) || "").trim();
          if (!key) return;
          if (!window.confirm("Redeem this share key into your account?")) return;
          redeemBtn.disabled = true;
          try {
            await api(buildPostSharesRedeemPath(), { method: "POST", body: { key }, timeoutMs: PLATFORM_CONTROL_TIMEOUT_MS });
            toast("Share redeemed", "ok");
            location.hash = "#/platform-control?tab=shares";
          } catch (e) {
            toast(softApiError(e), "err");
            redeemBtn.disabled = false;
          }
        };
      }
      view.querySelectorAll(".share-revoke").forEach((btn) => {
        btn.onclick = async () => {
          const id = String(btn.getAttribute("data-id") || "");
          if (!id || !window.confirm("Revoke this share?")) return;
          btn.disabled = true;
          try {
            await api(buildDeleteSharePath(id), { method: "DELETE", timeoutMs: PLATFORM_CONTROL_TIMEOUT_MS });
            toast("Share revoked", "ok");
            location.hash = "#/platform-control?tab=shares";
          } catch (e) {
            toast(softApiError(e), "err");
            btn.disabled = false;
          }
        };
      });
      return;
    }

    if (tab === "security") {
      if (!isSaOp) {
        mountView(view, capabilityUnavailableHtml({
          title: "Security",
          detail: "Security controls are for platform staff.",
        }));
        return;
      }
      const tenantId = String(me.username || me.tenant_id || me.account_id || "").trim() || "esa-daniel";
      const inhibitOn = catalogEnabled(me, "physical_inhibit") || catalogEnabled(me, "site_authority");
      const certOn = catalogEnabled(me, "adapter_certification") || catalogEnabled(me, "adapter_cert");
      const ledgerOn = catalogEnabled(me, "delivery_ledger");
      const rotOn = catalogEnabled(me, "admin_security");
      const macOn = catalogEnabled(me, "mac_acl_audit");

      let inhibitPack = { inhibited: false, active_inhibit: null };
      let certPack = { items: [] };
      let inhibitNote = "";
      let certNote = "";
      try {
        inhibitPack = await api(buildGetPhysicalInhibitStatusPath({ tenant_id: tenantId }), {
          timeoutMs: SRF_CONCEPTS_TIMEOUT_MS,
        });
      } catch (e) {
        inhibitNote = softApiError(e, "Emergency stop status could not be loaded yet.");
      }
      try {
        certPack = await api(buildGetAdapterCertificationsPath({ limit: 40 }), {
          timeoutMs: SRF_CONCEPTS_TIMEOUT_MS,
        });
      } catch (e) {
        certNote = softApiError(e, "Equipment approvals could not be loaded yet.");
      }
      if (!isRouteCurrent(routeSeq)) return;

      const [audit, drift] = await Promise.all([
        rotOn
          ? api(buildGetAdminSecurityAuditPath({ limit: 20 }), { timeoutMs: PLATFORM_CONTROL_TIMEOUT_MS }).catch((e) => ({ error: softApiError(e) }))
          : Promise.resolve({ skipped: true }),
        macOn
          ? api(buildGetSaMacDriftPath({ limit: 20 }), { timeoutMs: PLATFORM_CONTROL_TIMEOUT_MS }).catch((e) => ({ error: softApiError(e) }))
          : Promise.resolve({ skipped: true }),
      ]);
      if (!isRouteCurrent(routeSeq)) return;

      const inhibited = !!(inhibitPack && inhibitPack.inhibited);
      const active = (inhibitPack && inhibitPack.active_inhibit) || {};
      const inhibitId = String(active.inhibit_id || active.id || "").trim();
      const certItems = Array.isArray(certPack && certPack.items) ? certPack.items : [];
      const certCards = certItems.slice(0, 24).map((it) => {
        const kind = softAdapterKindLabel(it.resource_kind || it.adapter_id || it.capability);
        const state = softCertStateLabel(it.state || it.status || (it.approved_at ? "approved" : "pending"));
        const action = softAdapterKindLabel(it.action_name || it.capability || "");
        const badgeCls = /approved|active/i.test(state) ? "online" : /pending/i.test(state) ? "neutral" : "offline";
        return `<article class="glass-panel glass-panel--inset" style="padding:12px">
          <div class="row" style="justify-content:space-between;gap:8px;flex-wrap:wrap;align-items:flex-start">
            <strong>${escapeHtml(kind)}</strong>
            <span class="badge ${badgeCls}">${escapeHtml(state)}</span>
          </div>
          <p class="muted" style="margin:8px 0 0;font-size:12px;line-height:1.4">${escapeHtml(action)}</p>
        </article>`;
      }).join("");

      const securityTablesHtml = (rotOn || macOn)
        ? buildSecurityTablesHtml(audit || {}, drift || {}, { escapeHtml })
        : "";

      mountView(view, `<div class="card glass-panel glass-panel--panel">
        <h2>Platform · Security</h2>
        <p class="muted">Site safety controls — emergency stop, equipment approvals, and delivery proof. No raw technical dumps.</p>
        ${tabsHtml}

        <div class="glass-panel glass-panel--inset" style="padding:16px;margin-bottom:14px">
          <div class="row" style="justify-content:space-between;gap:8px;flex-wrap:wrap;align-items:center">
            <strong>Site emergency stop</strong>
            <span class="badge ${inhibited ? "offline" : "online"}">${inhibited ? "Stopped" : "Clear"}</span>
          </div>
          <p class="muted" style="margin:8px 0 0;line-height:1.45">${inhibitNote
            ? escapeHtml(inhibitNote)
            : inhibited
              ? "Physical actions are blocked for this organization until cleared."
              : "Organization is clear — emergency stop is available when you need it."}</p>
          <div class="inline-form" style="margin-top:12px">
            <label class="field"><span>Organization</span><input id="inhTenant" value="${escapeHtml(tenantId)}" autocomplete="off" /></label>
            <label class="field"><span>Site (optional)</span><input id="inhSite" placeholder="Leave blank for whole organization" autocomplete="off" /></label>
            <label class="field"><span>Reason</span><input id="inhReason" placeholder="Maintenance / safety hold" autocomplete="off" /></label>
          </div>
          <div class="row" style="margin-top:10px;gap:8px;flex-wrap:wrap">
            <button class="btn btn-tap" type="button" id="inhEngageBtn" ${inhibitOn ? "" : "disabled"}>${inhibited ? "Refresh status" : "Engage emergency stop"}</button>
            ${inhibitId ? `<button class="btn secondary btn-tap" type="button" id="inhClearBtn">Clear stop</button>` : ""}
          </div>
          <p id="inhMsg" class="muted" aria-live="polite" style="margin-top:8px"></p>
          ${inhibitOn ? "" : `<p class="muted" style="margin-top:8px;font-size:12px">Status is shown for verification. Engage controls unlock when emergency stop is opened on this deployment.</p>`}
        </div>

        <div class="glass-panel glass-panel--inset" style="padding:16px;margin-bottom:14px">
          <div class="row" style="justify-content:space-between;gap:8px;flex-wrap:wrap;align-items:center">
            <strong>Equipment approvals</strong>
            <span class="badge ${certOn ? "online" : "neutral"}">${certOn ? "Ready" : "Verify"}</span>
          </div>
          <p class="muted" style="margin:8px 0 12px;line-height:1.45">Approve response equipment before it can be commanded in the field.</p>
          ${certNote ? `<p class="muted">${escapeHtml(certNote)}</p>` : ""}
          <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:10px">
            ${certCards || `<p class="muted" style="margin:0">No equipment approvals on file yet.</p>`}
          </div>
        </div>

        <div class="glass-panel glass-panel--inset" style="padding:16px;margin-bottom:14px">
          <div class="row" style="justify-content:space-between;gap:8px;flex-wrap:wrap;align-items:center">
            <strong>Command delivery proof</strong>
            <span class="badge ${ledgerOn ? "online" : "neutral"}">${ledgerOn ? "Ready" : "Verify"}</span>
          </div>
          <p class="muted" style="margin:8px 0 0;line-height:1.45">${ledgerOn
            ? "Signed delivery and acknowledgment records appear here as commands complete."
            : "Signed delivery proof will list command hand-offs and acknowledgments here once opened for verification."}</p>
        </div>

        ${rotOn ? `<div class="glass-panel glass-panel--inset" style="padding:12px;margin-bottom:12px">
          <strong>Rotate board command key</strong>
          <p class="muted">Destructive. Type the board serial again to confirm. Platform security staff only.</p>
          <div class="inline-form">
            <label class="field"><span>Board serial</span><input id="rotDeviceId" autocomplete="off" /></label>
            <label class="field"><span>Type serial to confirm</span><input id="rotConfirm" autocomplete="off" /></label>
          </div>
          <div class="row" style="margin-top:8px"><button class="btn btn-tap" type="button" id="rotBtn">Rotate</button></div>
          <p id="rotMsg" class="muted" aria-live="polite"></p>
        </div>` : ""}
        ${securityTablesHtml}
      </div>`);

      const inhEngage = $("#inhEngageBtn", view);
      if (inhEngage) {
        inhEngage.onclick = async () => {
          if (!inhibitOn) return;
          if (inhibited) {
            location.hash = `#/platform-control?tab=security&_=${Date.now()}`;
            return;
          }
          const tid = String(($("#inhTenant", view) && $("#inhTenant", view).value) || "").trim();
          const site = String(($("#inhSite", view) && $("#inhSite", view).value) || "").trim();
          const reason = String(($("#inhReason", view) && $("#inhReason", view).value) || "").trim();
          if (!tid) {
            ($("#inhMsg", view) || {}).textContent = "Organization is required.";
            return;
          }
          if (!window.confirm(`Engage emergency stop for ${tid}${site ? ` · site ${site}` : ""}? Physical actions will pause.`)) return;
          inhEngage.disabled = true;
          try {
            const key = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `inh-${Date.now()}`;
            await api(buildPostPhysicalInhibitPath(), {
              method: "POST",
              body: {
                tenant_id: tid,
                site_id: site || undefined,
                reason: reason || "operator emergency stop",
              },
              headers: { "Idempotency-Key": key },
              timeoutMs: SRF_CONCEPTS_TIMEOUT_MS,
            });
            toast("Emergency stop engaged", "ok");
            location.hash = `#/platform-control?tab=security&_=${Date.now()}`;
          } catch (e) {
            const soft = softApiError(e);
            ($("#inhMsg", view) || {}).textContent = soft;
            toast(soft, "err");
            inhEngage.disabled = false;
          }
        };
      }
      const inhClear = $("#inhClearBtn", view);
      if (inhClear && inhibitId) {
        inhClear.onclick = async () => {
          if (!window.confirm("Clear the emergency stop and allow physical actions again?")) return;
          inhClear.disabled = true;
          try {
            const key = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `inhc-${Date.now()}`;
            await api(buildPostPhysicalInhibitClearPath(inhibitId), {
              method: "POST",
              body: { reason: "operator clear" },
              headers: { "Idempotency-Key": key },
              timeoutMs: SRF_CONCEPTS_TIMEOUT_MS,
            });
            toast("Emergency stop cleared", "ok");
            location.hash = `#/platform-control?tab=security&_=${Date.now()}`;
          } catch (e) {
            const soft = softApiError(e);
            ($("#inhMsg", view) || {}).textContent = soft;
            toast(soft, "err");
            inhClear.disabled = false;
          }
        };
      }

      const rotBtn = $("#rotBtn", view);
      if (rotBtn) {
        rotBtn.onclick = async () => {
          const deviceId = String(($("#rotDeviceId", view) && $("#rotDeviceId", view).value) || "").trim();
          const typed = String(($("#rotConfirm", view) && $("#rotConfirm", view).value) || "").trim();
          if (!deviceId) return;
          if (typed !== deviceId) {
            ($("#rotMsg", view) || {}).textContent = "Confirmation does not match board serial.";
            return;
          }
          if (!window.confirm(`Rotate the command key for ${deviceId}? This cannot be undone from the console.`)) return;
          rotBtn.disabled = true;
          try {
            await api(buildPostAdminSecurityRotateCmdKeyPath(), {
              method: "POST",
              body: { device_id: deviceId },
              timeoutMs: PLATFORM_CONTROL_TIMEOUT_MS,
            });
            ($("#rotMsg", view) || {}).textContent = "Command key rotated.";
            toast("Rotated", "ok");
          } catch (e) {
            const soft = softApiError(e);
            ($("#rotMsg", view) || {}).textContent = soft;
            toast(soft, "err");
            rotBtn.disabled = false;
          }
        };
      }
      return;
    }

    if (tab === "backup") {
      if (!isSaOp) {
        mountView(view, capabilityUnavailableHtml({
          title: "Backup schedule",
          detail: "Backup schedule is for platform staff.",
        }));
        return;
      }
      if (!catalogEnabled(me, "backup_worker") && !catalogEnabled(me, "manual_backup")) {
        mountView(view, `<div class="card glass-panel glass-panel--panel">
          <h2>Platform · Backup schedule</h2>
          ${tabsHtml}
          <div class="glass-panel glass-panel--inset" style="padding:16px;margin-top:8px">
            <span class="badge neutral">Verify</span>
            <p style="margin:10px 0 0;line-height:1.45">Scheduled backups and restore drills keep a durable copy of platform data. Manual export stays in Settings. Live schedule tools open when backup is turned on for this deployment.</p>
            <p style="margin-top:12px"><button type="button" class="btn secondary btn-tap" data-cc-go="settings">Open Settings backup</button></p>
          </div>
        </div>`);
        return;
      }
      try {
        const schedule = await api(buildGetAdminBackupSchedulePath(), { timeoutMs: PLATFORM_CONTROL_TIMEOUT_MS });
        if (!isRouteCurrent(routeSeq)) return;
        const backupTablesHtml = buildBackupScheduleTablesHtml(schedule || {}, { escapeHtml });
        mountView(view, `<div class="card glass-panel glass-panel--panel">
          <h2>Platform · Backup schedule</h2>
          ${tabsHtml}
          <p class="muted">Automated backup cadence and readiness. Manual export/import stays in Settings. Restore drill tests a file in isolation — live data is not overwritten.</p>
          ${backupTablesHtml}
          <div class="glass-panel glass-panel--inset" style="padding:12px;margin-top:12px">
            <strong>Restore drill</strong>
            <p class="muted">Upload an encrypted backup and passphrase. Platform security staff only.</p>
            <div class="inline-form" style="margin-top:8px">
              <label class="field"><span>Encrypted backup file</span><input id="restoreDrillFile" type="file" /></label>
              <label class="field"><span>Backup passphrase</span><input id="restoreDrillKey" type="password" autocomplete="off" /></label>
            </div>
            <div class="row" style="margin-top:8px;gap:8px">
              <button class="btn secondary btn-tap" type="button" id="restoreDrillBtn">Run restore drill</button>
              <button type="button" class="btn secondary btn-tap" data-cc-go="settings">Open Settings backup</button>
            </div>
            <p id="backupMsg" class="muted" aria-live="polite"></p>
          </div>
        </div>`);
        const drill = $("#restoreDrillBtn", view);
        if (drill) {
          drill.onclick = async () => {
            const fileEl = $("#restoreDrillFile", view);
            const keyEl = $("#restoreDrillKey", view);
            const file = fileEl && fileEl.files && fileEl.files[0];
            const key = String((keyEl && keyEl.value) || "").trim();
            const msg = $("#backupMsg", view);
            if (!file) {
              if (msg) msg.textContent = "Select an encrypted backup file.";
              return;
            }
            if (!key) {
              if (msg) msg.textContent = "Backup passphrase is required.";
              return;
            }
            if (!window.confirm(`Run isolated restore drill on ${file.name}? Production DB will not be written.`)) return;
            drill.disabled = true;
            if (msg) msg.textContent = "Running restore drill…";
            try {
              const form = new FormData();
              form.append("file", file, file.name || "sentinel-backup.enc");
              const pack = await api(buildPostAdminBackupRestoreDrillPath(), {
                method: "POST",
                body: form,
                headers: { "X-Backup-Encryption-Key": key },
                timeoutMs: Math.max(PLATFORM_CONTROL_TIMEOUT_MS, 120000),
              });
              if (msg) {
                const ok = pack && (pack.ok === true || pack.status === "ok" || pack.drill_ok === true);
                msg.textContent = ok
                  ? "Restore drill finished — production was not written."
                  : "Restore drill finished. Check Settings backup if you need to export again.";
              }
              if (keyEl) keyEl.value = "";
              toast("Restore drill complete", "ok");
            } catch (e) {
              if (msg) msg.textContent = String(e.message || e);
              toast(String(e.message || e), "err");
              drill.disabled = false;
            }
          };
        }
      } catch (e) {
        mountView(view, `<div class="card glass-panel glass-panel--panel">
          <h2>Platform · Backup schedule</h2>
          ${tabsHtml}
          <p class="muted">${escapeHtml(String(e.message || e))}</p>
          <button type="button" class="btn secondary btn-tap" data-cc-go="settings">Open Settings backup</button>
        </div>`);
      }
      return;
    }

    // domains default
    if (!isSaOp) {
      mountView(view, capabilityUnavailableHtml({
        title: "Platform domains",
        detail: "SA / superadmin only.",
        backHref: "#/platform-control?tab=shares",
        backLabel: "Open shares",
      }));
      return;
    }
    const domains = await api(buildGetSaPlatformDomainsPath(), { timeoutMs: PLATFORM_CONTROL_TIMEOUT_MS });
    if (!isRouteCurrent(routeSeq)) return;
    const tablesHtml = buildPlatformDomainsTablesHtml(domains || {}, { escapeHtml });
    mountView(view, `<div class="card glass-panel glass-panel--panel">
      <h2>Platform · Account domains</h2>
      <p class="muted">Where each platform capability stores data — no secrets shown.</p>
      ${tabsHtml}
      ${tablesHtml}
    </div>`);
  } catch (e) {
    if (!isRouteCurrent(routeSeq)) return;
    mountView(view, `<div class="card"><h2>Platform unavailable</h2><p class="muted">${typeof softApiError === "function" ? softApiError(e) : escapeHtml(String(e.message || e))}</p></div>`);
  }
});


// ── readiness.route.js ─────────────────────────────────────
/**
 * Route: #/readiness — Response readiness score + optional health engine tab.
 */

registerRoute("readiness", async (view, _args, routeSeq) => {
  setCrumb("Readiness");
  if (!hasRole("admin")) {
    mountView(view, `<div class="card glass-panel glass-panel--panel"><p class="muted">Admin access required.</p></div>`);
    return;
  }

  const softLabel = (raw) => softOperatorNote(raw);
  const softGap = (raw) => softOperatorNote(raw);

  const params = new URLSearchParams((location.hash.split("?")[1] || ""));
  const tab = String(params.get("tab") || "score").trim();
  const healthOn = catalogEnabled(state.me, "health_engine");
  // Device health · Before tonight — strip SoT in buildTonightStageStripHtml (never paint #/).
  const tabsHtml = typeof buildTonightStageStripHtml === "function"
    ? buildTonightStageStripHtml({ stage: "before", active: tab === "health" ? "health" : "score" })
    : "";

  if (tab === "health") {
    if (!healthOn) {
      mountView(view, `<div class="card glass-panel glass-panel--panel">
        <h2>Device health</h2>
        ${tabsHtml}
        <div class="glass-panel glass-panel--inset" style="padding:16px;margin-top:8px">
          <span class="badge neutral">Verify</span>
          <p style="margin:10px 0 0;line-height:1.45">Deeper board health scoring and alerts appear here when health checks are opened for this deployment.</p>
        </div>
      </div>`);
      return;
    }
    mountView(view, `<div class="card glass-panel glass-panel--panel"><p class="muted">Loading board health…</p></div>`);
    try {
      const [status, alerts] = await Promise.all([
        api(buildGetHealthStatusPath(), { timeoutMs: PLATFORM_CONTROL_TIMEOUT_MS }),
        api(buildGetHealthAlertsPath({ refresh: false }), { timeoutMs: PLATFORM_CONTROL_TIMEOUT_MS }),
      ]);
      if (!isRouteCurrent(routeSeq)) return;
      mountView(view, `<div class="card glass-panel glass-panel--panel">
        <h2>Device health</h2>
        <p class="muted">Operator view — health scoring is separate from security alarms.</p>
        ${tabsHtml}
        <h3>Status</h3>
        ${buildHealthStatusCardsHtml(status)}
        <h3>Alerts</h3>
        ${buildHealthAlertsTableHtml(alerts)}
        <p class="muted" style="margin-top:12px">
          <button type="button" class="btn sm secondary btn-tap" id="healthRefreshBtn">Refresh alerts</button>
        </p>
      </div>`);
      const refreshBtn = $("#healthRefreshBtn", view);
      if (refreshBtn) {
        refreshBtn.onclick = () => {
          location.hash = "#/readiness?tab=health&_=" + Date.now();
        };
      }
    } catch (e) {
      if (!isRouteCurrent(routeSeq)) return;
      mountView(view, `<div class="card glass-panel glass-panel--panel"><h2>Device health unavailable</h2>${tabsHtml}<p class="muted">${escapeHtml(softApiError(e))}</p></div>`);
    }
    return;
  }

  mountView(view, `<div class="card glass-panel glass-panel--panel"><p class="muted">Loading readiness score…</p></div>`);
  try {
    const [score, gaps] = await Promise.all([
      api("/readiness/score"),
      api("/readiness/gaps"),
    ]);
    if (!isRouteCurrent(routeSeq)) return;
    const grade = String(score.grade || "—");
    const pct = Number(score.score || 0);
    const checks = Array.isArray(score.checks) ? score.checks : [];
    const gapItems = Array.isArray(gaps.items) ? gaps.items : [];
    mountView(view, `
      <div class="card glass-panel glass-panel--panel">
        <h2>Response readiness</h2>
        <p class="muted">Before tonight: plans, practice, alerts, cameras, and who can go.
          <button type="button" class="btn sm ghost btn-tap" data-cc-go="overview">Home</button></p>
        ${tabsHtml}
        <div class="stats" style="margin:12px 0">
          <div class="stat glass-panel glass-panel--inset">
            <span class="k">Score</span><strong class="v">${escapeHtml(String(pct))}%</strong>
          </div>
          <div class="stat glass-panel glass-panel--inset">
            <span class="k">Grade</span><strong class="v">${escapeHtml(grade)}</strong>
          </div>
          <div class="stat glass-panel glass-panel--inset">
            <span class="k">Gaps</span><strong class="v">${escapeHtml(String(gapItems.length))}</strong>
          </div>
        </div>
        <h3>Checks</h3>
        <table class="table" aria-label="Readiness checks">
          <thead><tr><th>Area</th><th>Status</th><th>Weight</th><th>Fix</th></tr></thead>
          <tbody>
            ${checks.map((c) => `<tr>
              <td>${escapeHtml(softLabel(c.label || c.id || ""))}</td>
              <td>${c.ok ? '<span class="badge ok">OK</span>' : '<span class="badge offline">Gap</span>'}</td>
              <td>${escapeHtml(String(c.earned || 0))}/${escapeHtml(String(c.weight || 0))}</td>
              <td>${(() => {
                const rem = String(c.remediation || "").trim();
                if (!rem.startsWith("#/")) return "—";
                return `<button type="button" class="btn sm secondary btn-tap" data-cc-go="${escapeHtml(rem.replace(/^#\/?/, ""))}">Open</button>`;
              })()}</td>
            </tr>`).join("") || "<tr><td colspan='4' class='muted'>No checks.</td></tr>"}
          </tbody>
        </table>
        <h3 style="margin-top:16px">What to fix</h3>
        <ul>
          ${gapItems.map((g) => {
            const text = softGap(g.gap || g.label || "");
            const href = g.remediation ? String(g.remediation) : "";
            const isHash = href.startsWith("#/");
            return `<li>${escapeHtml(text)}${isHash ? ` — <button type="button" class="btn sm ghost btn-tap" data-cc-go="${escapeHtml(href.replace(/^#\/?/, ""))}">Fix</button>` : ""}</li>`;
          }).join("") || "<li class='muted'>No gaps — account is response-ready.</li>"}
        </ul>
      </div>`);
  } catch (e) {
    if (!isRouteCurrent(routeSeq)) return;
    mountView(view, `<div class="card glass-panel glass-panel--panel"><h2>Readiness unavailable</h2><p class="muted">${escapeHtml(softApiError(e))}</p></div>`);
  }
});


// ── register.route.js ─────────────────────────────────────
/**
 * Route: #/register — public admin signup (start → email-code → verify).
 *
 * Build: concatenated by `scripts/build-dashboard.mjs`. Shares scope with
 * console.raw.js — see `account-activate.route.js` for the convention.
 *
 * Backend contracts:
 *   - POST /auth/signup/start    → request OTP
 *   - POST /auth/signup/verify   → finish signup
 *   - POST /auth/code/resend     → re-issue OTP (purpose=signup)
 * Verified by `tests/test_spa_api_contract.py`.
 */

registerRoute("register", async (view) => {
  setCrumb("Register admin");
  document.body.dataset.auth = "none";
  activateCommandCenterAuthLayout();
  const cleanSignupMessage = (raw) => {
    const s = String(raw || "").trim();
    if (!s) return "Request failed. Please try again.";
    const l = s.toLowerCase();
    if (l.includes("already exists")) return "Username or email already exists.";
    if (l.includes("email already registered")) return "This email is already registered.";
    if (l.includes("password must")) return "Password: at least 12 characters with upper, lower, a number, and a symbol.";
    if (l.includes("password_policy_violation")) return "Password does not meet requirements.";
    if (l.includes("invalid") && l.includes("email")) return "Email format is invalid.";
    if (l.includes("networkerror") || l.includes("failed to fetch")) return "Network error. Please check server/API.";
    return s.replace(/^error:\s*/i, "");
  };
  mountView(view, buildCommandCenterAuthSurfaceHtml("register", `
          <div class="auth-card auth-card--panel auth-card--auth-main auth-card--wide" data-auth-card>
            <header class="auth-card__head">
              <h1 class="auth-card__title">Create admin</h1>
              <p class="auth-card__lead">Email verification, then sign in.</p>
            </header>
            <div class="auth-card__body">
              <p class="auth-card__note muted">After verification, you can sign in immediately.</p>
              <ol class="auth-steps" aria-label="Steps">
                <li id="r_step_ind1" class="is-active"><span class="auth-steps__n">1</span><span class="auth-steps__t">Your details</span></li>
                <li id="r_step_ind2"><span class="auth-steps__n">2</span><span class="auth-steps__t">Email code</span></li>
              </ol>
              <div id="rStep1">
                <label class="field"><span>Username</span><input id="r_user" autocomplete="username" placeholder="2–64 chars, letters · digits · ._-"/></label>
                <label class="field field--spaced"><span>Password</span><input id="r_pass" type="password" autocomplete="new-password" placeholder="≥12 chars: upper, lower, digit, symbol"/></label>
                <label class="field field--spaced"><span>Email</span><input id="r_email" type="email" autocomplete="email" placeholder="you@company.com"/></label>
                <div class="auth-card__submit">
                  <button class="btn btn-tap btn-block auth-btn-primary" type="button" id="r_start">Send verification code</button>
                  <a class="auth-link auth-link--center" href="#/login">Already have an account</a>
                </div>
                <p class="auth-card__msg muted" id="r_msg1" aria-live="polite"></p>
              </div>
              <div id="rStep2" style="display:none">
                <p class="auth-card__note">We sent a code to <strong class="mono" id="r_shown_email"></strong>. Check inbox and spam.</p>
                <p class="muted" id="r_otp_expiry" style="margin:0 0 8px" aria-live="polite"></p>
                <label class="field field--spaced"><span>Verification code</span><input id="r_email_code" inputmode="numeric" maxlength="12" autocomplete="one-time-code" placeholder="6–12 digits"/></label>
                <div class="auth-card__submit">
                  <button class="btn btn-tap btn-block auth-btn-primary" type="button" id="r_verify">Complete signup</button>
                  <button class="btn secondary btn-tap btn-block" type="button" id="r_resend">Resend code</button>
                  <button class="btn ghost btn-tap btn-block" type="button" id="r_back_step">Edit details</button>
                </div>
                <p class="auth-card__msg muted" id="r_msg2" aria-live="polite"></p>
              </div>
            </div>
          </div>`));
  const m1 = $("#r_msg1"), m2 = $("#r_msg2");
  let disposeOtpUi = () => {};
  $("#r_start").addEventListener("click", async () => {
    m1.textContent = "";
    const body = {
      username: $("#r_user").value.trim(),
      password: $("#r_pass").value,
      email: $("#r_email").value.trim(),
    };
    if (!body.username || !body.password || !body.email) { m1.textContent = "Username, password, and email required"; return; }
    try {
      const r = await fetch(apiBase() + buildPostAuthSignupStartPath(), {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        const msg = formatHttpApiDetail(j.detail) || (typeof j.detail === "string" ? j.detail : "") || `${r.status}`;
        throw new Error(msg);
      }
      disposeOtpUi();
      const d1 = startEmailCodeExpiryCountdown($("#r_otp_expiry"), j.ttl_seconds);
      const d2 = startAuthResendCooldown($("#r_resend"), Number(j.resend_after_seconds || 60), "Resend code");
      disposeOtpUi = () => {
        try { d1(); } catch (_) {}
        try { d2(); } catch (_) {}
      };
      sessionStorage.setItem("croc.signup_user", body.username);
      $("#r_shown_email").textContent = body.email;
      $("#r_step_ind1").classList.remove("is-active");
      $("#r_step_ind2").classList.add("is-active");
      $("#rStep1").style.display = "none";
      $("#rStep2").style.display = "";
    } catch (e) { m1.textContent = cleanSignupMessage(e.message || e); }
  });
  $("#r_verify").addEventListener("click", async () => {
    m2.textContent = "";
    const body = {
      username: sessionStorage.getItem("croc.signup_user") || "",
      email_code: $("#r_email_code").value.trim(),
    };
    try {
      const r = await fetch(apiBase() + buildPostAuthSignupVerifyPath(), {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        const msg = formatHttpApiDetail(j.detail) || (typeof j.detail === "string" ? j.detail : "") || `${r.status}`;
        throw new Error(msg);
      }
      setChildMarkup(m2, `<span class="badge online">OK</span> Redirecting to sign in…`);
      scheduleRouteRedirect(1500, "#/login");
    } catch (e) { m2.textContent = cleanSignupMessage(e.message || e); }
  });
  $("#r_back_step").addEventListener("click", () => {
    m2.textContent = "";
    disposeOtpUi();
    disposeOtpUi = () => {};
    const ex = $("#r_otp_expiry");
    if (ex) ex.textContent = "";
    $("#r_step_ind2").classList.remove("is-active");
    $("#r_step_ind1").classList.add("is-active");
    $("#rStep2").style.display = "none";
    $("#rStep1").style.display = "";
  });
  $("#r_resend").addEventListener("click", async () => {
    const username = sessionStorage.getItem("croc.signup_user") || "";
    if (!username) { m2.textContent = "Complete step 1 first"; return; }
    try {
      const r = await fetch(apiBase() + buildPostAuthCodeResendPath(), {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, channel: "email", purpose: "signup" }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        const msg = formatHttpApiDetail(j.detail) || (typeof j.detail === "string" ? j.detail : "") || `${r.status}`;
        throw new Error(msg);
      }
      disposeOtpUi();
      const d1 = startEmailCodeExpiryCountdown($("#r_otp_expiry"), j.ttl_seconds);
      const d2 = startAuthResendCooldown($("#r_resend"), Number(j.resend_after_seconds || 60), "Resend code");
      disposeOtpUi = () => {
        try { d1(); } catch (_) {}
        try { d2(); } catch (_) {}
      };
      m2.textContent = "Code resent";
    } catch (e) { m2.textContent = cleanSignupMessage(e.message || e); }
  });
});


// ── response-drill.route.js ─────────────────────────────────────
/**
 * Route: #/response-drill — Response drill UI (INC-OS-DRILL-001).
 * Isolated practice run — production stores untouched.
 */

registerRoute("response-drill", async (view, _args, routeSeq) => {
  setCrumb("Practice drill");
  if (!hasRole("admin")) {
    mountView(view, `<div class="card glass-panel glass-panel--panel"><p class="muted">Admin access required.</p></div>`);
    return;
  }
  if (!catalogEnabled(state.me, "response_drill")) {
    mountView(view, `<div class="card glass-panel glass-panel--panel">
      <h2>Practice drill</h2>
      <span class="badge neutral">Verify</span>
      <p class="muted" style="margin-top:10px;line-height:1.45">Run isolated practice scenarios without live alarms, dispatches, or notifications. Live drill controls open when practice is turned on for this deployment.</p>
      <p style="margin-top:12px"><button type="button" class="btn secondary btn-tap" data-cc-go="readiness">Back to Readiness</button></p>
    </div>`);
    return;
  }

  mountView(view, `<div class="card glass-panel glass-panel--panel"><p class="muted">Loading drill console…</p></div>`);

  const render = (state) => {
    if (!isRouteCurrent(routeSeq)) return;
    const disabled = !!state.disabled;
    mountView(view, `
      <div class="card glass-panel glass-panel--panel">
        <h2>Response drill</h2>
        <p class="muted">Isolated practice — no live alarms, dispatches, or notifications.
          <button type="button" class="btn sm ghost btn-tap" data-cc-go="readiness">← Readiness</button></p>
        ${disabled ? `<p class="muted">Practice drills are turned off on this deployment.</p>` : ""}
        <div class="inline-form" style="margin-top:12px">
          <label class="field"><span>Drill name</span>
            <input id="drillName" value="Quarterly alarm drill" autocomplete="off" />
          </label>
          <label class="field"><span>Scenario</span>
            <select id="drillEventType">
              <option value="simulation.drill" selected>Practice drill</option>
              <option value="warehouse.fire_drill">Warehouse fire drill</option>
            </select>
          </label>
        </div>
        <div class="row" style="margin-top:10px">
          <button class="btn" id="drillRunBtn" type="button" ${disabled ? "disabled" : ""}>Run practice drill</button>
        </div>
        <p id="drillMsg" class="muted" aria-live="polite"></p>
        ${state.reportHtml || ""}
      </div>`);

    const btn = $("#drillRunBtn", view);
    const msg = $("#drillMsg", view);
    if (!btn || disabled) return;
    btn.addEventListener("click", async () => {
      const name = String(($("#drillName", view) && $("#drillName", view).value) || "").trim();
      const eventType = String(($("#drillEventType", view) && $("#drillEventType", view).value) || "").trim();
      if (!name || !eventType) {
        if (msg) msg.textContent = "Name and scenario are required.";
        return;
      }
      // Production alarm/panic types are rejected by the sandbox (isolation guard).
      if (/^alarm\./i.test(eventType) || /panic/i.test(eventType)) {
        if (msg) msg.textContent = "Choose a practice scenario — live alarm types are blocked.";
        return;
      }
      if (msg) msg.textContent = "Creating scenario…";
      try {
        // Sandbox requires ≥1 device id; dry-run stubs never touch production devices.
        const created = await api("/simulations", {
          method: "POST",
          body: {
            name,
            simulated_event_type: eventType,
            device_ids: ["practice-drill"],
            user_behaviors: ["acknowledge"],
          },
        });
        if (!isRouteCurrent(routeSeq)) return;
        const simId = String((created.item && created.item.simulation_id) || "");
        if (!simId) throw new Error("Could not start drill");
        if (msg) msg.textContent = "Running practice drill…";
        const run = await api(`/simulations/${encodeURIComponent(simId)}/run`, {
          method: "POST",
          body: { mode: "dry_run" },
        });
        if (!isRouteCurrent(routeSeq)) return;
        const report = await api(`/simulations/${encodeURIComponent(simId)}/report`);
        if (!isRouteCurrent(routeSeq)) return;
        const steps = (report.report && report.report.steps) || (run.run && run.run.steps) || [];
        const reportHtml = `
          <h3 style="margin-top:16px">Last drill report</h3>
          <p class="muted">Practice run complete</p>
          <table class="table">
            <thead><tr><th>Step</th><th>Handler</th><th>OK</th><th>Duration (ms)</th></tr></thead>
            <tbody>
              ${steps.map((s) => `<tr>
                <td>${escapeHtml(softOperatorNote(s.step || ""))}</td>
                <td>${escapeHtml(softOperatorNote(s.executor || ""))}</td>
                <td>${s.ok ? "yes" : "no"}</td>
                <td>${escapeHtml(String(s.duration_ms != null ? s.duration_ms : ""))}</td>
              </tr>`).join("") || "<tr><td colspan='4' class='muted'>No steps recorded.</td></tr>"}
            </tbody>
          </table>`;
        if (msg) msg.textContent = "Practice drill complete — readiness will refresh on next score load.";
        toast("Response drill complete", "ok");
        render({ disabled: false, reportHtml });
      } catch (e) {
        if (!isRouteCurrent(routeSeq)) return;
        const text = softApiError(e);
        if (msg) msg.textContent = text;
        const raw = String(e.message || e);
        if (raw.includes("503") || raw.toLowerCase().includes("simulation_disabled")) {
          render({ disabled: true, reportHtml: "" });
        }
        toast(text, "err");
      }
    });
  };

  render({ disabled: false, reportHtml: "" });
});


// ── response-plans.route.js ─────────────────────────────────────
/**
 * Route: #/response-plans — Response plan list + import (INC-OS-FE-006).
 */

registerRoute("response-plans", async (view, _args, routeSeq) => {
  setCrumb("Response plans");
  if (!hasRole("admin")) {
    mountView(view, `<div class="card glass-panel glass-panel--panel"><p class="muted">Admin access required.</p></div>`);
    return;
  }

  const softEventType = (raw) => {
    const s = String(raw || "").trim();
    if (!s) return "—";
    if (s === "alarm.trigger") return "Alarm trigger";
    if (s === "alarm.clear") return "Alarm clear";
    if (s === "panic") return "Panic";
    return s.replace(/[._]/g, " ");
  };

  const softActionType = (raw) => {
    const s = String(raw || "").trim();
    if (!s) return "—";
    const map = {
      notify: "Notify",
      siren: "Siren",
      camera_snapshot: "Camera snapshot",
      dispatch: "Dispatch",
      escalate: "Escalate",
    };
    if (map[s]) return map[s];
    return s.replace(/[._]/g, " ");
  };

  const params = new URLSearchParams((location.hash.split("?")[1] || ""));
  const tab = String(params.get("tab") || "plans").trim();
  const sopOn = catalogEnabled(state.me, "sop_knowledge");
  if (tab === "sop") {
    if (!sopOn) {
      mountView(view, capabilityUnavailableHtml({
        title: "Playbook knowledge",
        detail: "Playbook knowledge is not opened for this account yet.",
        backHref: "#/response-plans",
      }));
      return;
    }
    mountView(view, `<div class="card glass-panel glass-panel--panel"><p class="muted">Loading playbook knowledge…</p></div>`);
    try {
      const pack = await api(buildGetSopPath(), { timeoutMs: PLATFORM_CONTROL_TIMEOUT_MS });
      if (!isRouteCurrent(routeSeq)) return;
      const items = Array.isArray(pack.items) ? pack.items : [];
      mountView(view, `<div class="card glass-panel glass-panel--panel">
        <h2>Playbook knowledge</h2>
        <p class="muted">Advisory response tips for operators · <button type="button" class="btn sm ghost btn-tap" data-cc-go="response-plans">← Plans</button></p>
        <div class="glass-panel glass-panel--inset" style="padding:12px;margin:12px 0">
          <strong>Add or update tip</strong>
          <div class="inline-form" style="margin-top:8px">
            <label class="field"><span>When this happens</span>
              <select id="sopEventType">
                <option value="alarm.trigger" selected>Alarm trigger</option>
                <option value="alarm.clear">Alarm clear</option>
                <option value="panic">Panic</option>
              </select>
            </label>
            <label class="field wide"><span>Suggested response</span><input id="sopSolution" autocomplete="off" /></label>
          </div>
          <div class="row" style="margin-top:8px"><button class="btn btn-tap" type="button" id="sopSaveBtn">Save</button></div>
          <p id="sopMsg" class="muted" aria-live="polite"></p>
        </div>
        <div class="glass-panel glass-panel--inset" style="padding:12px;margin:12px 0">
          <strong>Suggested tips</strong>
          <div class="inline-form" style="margin-top:8px">
            <label class="field"><span>When this happens</span>
              <select id="sopRecEvent">
                <option value="alarm.trigger" selected>Alarm trigger</option>
                <option value="alarm.clear">Alarm clear</option>
                <option value="panic">Panic</option>
              </select>
            </label>
            <button class="btn secondary btn-tap" type="button" id="sopRecBtn">Show tips</button>
          </div>
          <div id="sopRecOut" class="muted" style="margin-top:8px;font-size:13px;line-height:1.45"></div>
        </div>
        <table class="table"><thead><tr><th>ID</th><th>When</th><th>Suggested response</th><th></th></tr></thead>
          <tbody>${items.map((it) => {
            const id = String(it.sop_id || it.id || "");
            return `<tr>
              <td class="mono">${escapeHtml(id || "—")}</td>
              <td>${escapeHtml(softEventType(it.event_type))}</td>
              <td>${escapeHtml(String(it.solution || "—"))}</td>
              <td>${id ? `<button class="btn sm secondary btn-tap sop-feedback" type="button" data-id="${escapeHtml(id)}" data-ok="1">Mark useful</button>` : ""}</td>
            </tr>`;
          }).join("") || `<tr><td colspan="4" class="muted">No playbook tips yet.</td></tr>`}</tbody></table>
      </div>`);
      const saveBtn = $("#sopSaveBtn", view);
      if (saveBtn) {
        saveBtn.onclick = async () => {
          const eventType = String(($("#sopEventType", view) && $("#sopEventType", view).value) || "").trim();
          const solution = String(($("#sopSolution", view) && $("#sopSolution", view).value) || "").trim();
          if (!eventType || !solution) return;
          saveBtn.disabled = true;
          try {
            await api(buildPostSopPath(), {
              method: "POST",
              body: { event_type: eventType, solution },
              timeoutMs: PLATFORM_CONTROL_TIMEOUT_MS,
            });
            toast("Tip saved", "ok");
            location.hash = "#/response-plans?tab=sop";
          } catch (e) {
            const soft = softApiError(e);
            ($("#sopMsg", view) || {}).textContent = soft;
            toast(soft, "err");
            saveBtn.disabled = false;
          }
        };
      }
      const recBtn = $("#sopRecBtn", view);
      if (recBtn) {
        recBtn.onclick = async () => {
          const eventType = String(($("#sopRecEvent", view) && $("#sopRecEvent", view).value) || "").trim();
          if (!eventType) return;
          try {
            const rec = await api(buildGetSopRecommendationsPath({ event_type: eventType }), { timeoutMs: PLATFORM_CONTROL_TIMEOUT_MS });
            const out = $("#sopRecOut", view);
            if (out) {
              const items = Array.isArray(rec && rec.items) ? rec.items
                : Array.isArray(rec && rec.recommendations) ? rec.recommendations
                : Array.isArray(rec) ? rec : [];
              if (!items.length) {
                out.textContent = "No tips for this situation yet.";
              } else {
                out.innerHTML = `<ul style="margin:0;padding-left:1.2em">${items.map((it) => {
                  const text = typeof it === "string"
                    ? it
                    : (it.solution || it.text || it.summary || it.title || "");
                  return `<li>${escapeHtml(String(text || "—"))}</li>`;
                }).join("")}</ul>`;
              }
            }
          } catch (e) {
            toast(softApiError(e), "err");
          }
        };
      }
      view.querySelectorAll(".sop-feedback").forEach((btn) => {
        btn.onclick = async () => {
          const id = String(btn.getAttribute("data-id") || "");
          if (!id) return;
          btn.disabled = true;
          try {
            await api(buildPostSopFeedbackPath(id), {
              method: "POST",
              body: { success: true },
              timeoutMs: PLATFORM_CONTROL_TIMEOUT_MS,
            });
            toast("Thanks — tip marked useful", "ok");
          } catch (e) {
            toast(softApiError(e), "err");
            btn.disabled = false;
          }
        };
      });
    } catch (e) {
      if (!isRouteCurrent(routeSeq)) return;
      mountView(view, `<div class="card glass-panel glass-panel--panel"><h2>Playbook unavailable</h2><p class="muted">${escapeHtml(softApiError(e))}</p></div>`);
    }
    return;
  }

  mountView(view, `<div class="card glass-panel glass-panel--panel"><p class="muted">Loading response plans…</p></div>`);

  const render = (pack, errMsg) => {
    if (!isRouteCurrent(routeSeq)) return;
    const items = (pack && pack.items) || (Array.isArray(pack) ? pack : []);
    const certifiedCount = pack && pack.certified_count != null
      ? Number(pack.certified_count)
      : items.filter((r) => r && r.certified_at).length;
    mountView(view, `
      <div class="card glass-panel glass-panel--panel">
        <h2>Response plans</h2>
        <p class="muted">${"" /* dispatch_rules responder_dispatches */}Channel and playbook scripts — not the person queue. Certified ${certifiedCount} of ${items.length}.
          <button type="button" class="btn sm ghost btn-tap" data-cc-go="settings">← Settings</button>
          ${sopOn ? ` · <button type="button" class="btn sm ghost btn-tap" data-cc-go="response-plans?tab=sop">Playbook knowledge</button>` : ""}</p>
        ${errMsg ? `<p class="muted">${escapeHtml(errMsg)}</p>` : ""}
        <table class="table">
          <thead><tr><th>Name</th><th>Response</th><th>Version</th><th>Certified</th><th>Enabled</th><th></th></tr></thead>
          <tbody>
            ${(items || []).map((r) => `<tr>
              <td>${escapeHtml(String(r.name || r.rule_id || ""))}</td>
              <td>${escapeHtml(softActionType(r.action_type))}</td>
              <td>${escapeHtml(String(r.revision != null ? r.revision : 1))}</td>
              <td>${r.certified_at ? escapeHtml(String(r.certified_by || "yes")) : '<span class="muted">—</span>'}</td>
              <td>${r.enabled ? "yes" : "no"}</td>
              <td>${r.certified_at ? "" : `<button class="btn sm secondary rp-certify" data-rule-id="${escapeHtml(String(r.rule_id || ""))}" type="button">Certify</button>`}</td>
            </tr>`).join("") || "<tr><td colspan='6' class='muted'>No plans yet — import a plan pack below.</td></tr>"}
          </tbody>
        </table>
        <h3 style="margin-top:20px">Import from runbook</h3>
        <p class="muted" style="font-size:12px">Staff only. Paste the plan pack your runbook provides. Everyday operators can ignore this box.</p>
        <textarea id="rpImportJson" class="wide" rows="8" placeholder="Paste plan pack from your runbook…" spellcheck="false"></textarea>
        <div class="row" style="margin-top:10px">
          <button class="btn" id="rpImportBtn">Import</button>
        </div>
        <p id="rpImportMsg" class="muted" aria-live="polite"></p>
      </div>`);

    const btn = $("#rpImportBtn", view);
    const msg = $("#rpImportMsg", view);
    const ta = $("#rpImportJson", view);
    view.querySelectorAll(".rp-certify").forEach((el) => {
      el.addEventListener("click", async () => {
        const ruleId = String(el.getAttribute("data-rule-id") || "").trim();
        if (!ruleId) return;
        if (msg) msg.textContent = "Certifying…";
        try {
          await api(`/dispatch-rules/${encodeURIComponent(ruleId)}/certify`, { method: "POST", body: {} });
          if (!isRouteCurrent(routeSeq)) return;
          toast("Plan certified", "ok");
          const data = await api("/dispatch-rules?limit=100");
          if (!isRouteCurrent(routeSeq)) return;
          render(data || {}, "");
        } catch (e) {
          if (!isRouteCurrent(routeSeq)) return;
          const soft = softApiError(e);
          if (msg) msg.textContent = soft;
          toast(soft, "err");
        }
      });
    });
    if (!btn || !ta) return;
    btn.addEventListener("click", async () => {
      let body;
      try {
        body = JSON.parse(ta.value || "{}");
      } catch (_e) {
        if (msg) msg.textContent = "Plan pack format looks invalid — paste the full pack from your runbook.";
        return;
      }
      if (msg) msg.textContent = "Importing…";
      try {
        const r = await api("/dispatch-rules/import", { method: "POST", body });
        if (!isRouteCurrent(routeSeq)) return;
        if (msg) msg.textContent = `Imported ${r.imported || 0} plan(s).`;
        toast(`Imported ${r.imported || 0} plan(s)`, "ok");
        const data = await api("/dispatch-rules?limit=100");
        if (!isRouteCurrent(routeSeq)) return;
        render(data || {}, "");
      } catch (e) {
        if (!isRouteCurrent(routeSeq)) return;
        const soft = softApiError(e);
        if (msg) msg.textContent = soft;
        toast(soft, "err");
      }
    });
  };

  try {
    const data = await api("/dispatch-rules?limit=100");
    if (!isRouteCurrent(routeSeq)) return;
    render(data || {}, "");
  } catch (e) {
    if (!isRouteCurrent(routeSeq)) return;
    render({ items: [], certified_count: 0 }, softApiError(e));
  }
});


// ── response.route.js ─────────────────────────────────────
/**
 * Route: #/response — Response queue + siren channel (?tab=siren).
 * ZD-DEAD: `#/alerts` registry deleted tip559 — bookmarks must use `#/response?tab=siren` directly.
 */

registerRoute("response", async (view, _args, routeSeq) => {
  const tabParam = String((window.__routeQuery && window.__routeQuery.get("tab")) || "").toLowerCase();
  const canManageTeam = hasRole("admin");
  const tab = tabParam === "siren"
    ? "siren"
    : (tabParam === "team" && canManageTeam ? "team" : "queue");

  if (tab === "siren") {
    setCrumb("Siren");
    const enabled = can("can_alert");
    mountView(view, `
      <div class="card glass-panel glass-panel--panel">
        <h2>Siren control</h2>
        ${typeof buildTonightStageStripHtml === "function" ? buildTonightStageStripHtml({ stage: "during", active: "assign" }) : ""}
        <p class="muted"><button type="button" class="btn sm ghost btn-tap" data-cc-go="response">← Assignments</button></p>
        ${enabled ? "" : `<p class="badge revoked">Siren control is not available on this account — ask an admin.</p>`}
        <p class="muted" style="margin:6px 0 10px;font-size:12px;line-height:1.45">
          Turn the siren on or off for one board, a selection, or all boards you can see.
          Leave the list empty to target <strong>all boards you can see</strong>.
          For a single unit, the board page is usually clearer.
        </p>
        <p id="alertsLoadMsg" class="muted" aria-live="polite">Loading boards…</p>
        <div class="inline-form inline-form--bulk-siren" style="margin-top:12px">
          <label class="field"><span>Siren</span>
            <select id="action"><option value="on">On</option><option value="off">Off</option></select>
          </label>
          <label class="field"><span>How long (minutes)</span>
            <input id="durMin" type="number" value="${DEFAULT_REMOTE_SIREN_MS / 60000}" min="0.5" max="5" step="0.5" />
          </label>
          <label class="field wide"><span>Boards (empty = all you can see)</span>
            <select id="targets" multiple size="6" disabled></select>
          </label>
          <div class="row wide" style="justify-content:flex-end">
            <button class="btn danger" id="fire" disabled>Apply</button>
          </div>
        </div>
      </div>`);

    const sel = $("#targets");
    const fireBtn = $("#fire");
    const loadMsg = $("#alertsLoadMsg");

    let list;
    try {
      list = await apiGetCached(DEVICES_API_LIVE, { timeoutMs: 16000 }, 3000);
      if (!isRouteCurrent(routeSeq)) return;
      if (loadMsg) loadMsg.remove();
    } catch (e) {
      if (!isRouteCurrent(routeSeq)) return;
      if (loadMsg) {
        loadMsg.className = "muted";
        loadMsg.textContent = softApiError(e, "Could not load boards — you can still try Apply, or open a board page.");
      }
      list = { items: [] };
    }
    const devices = list.items || [];

    setChildMarkup(sel, devices.map((d) => {
      const lab = d.display_label ? `${escapeHtml(d.display_label)}` : escapeHtml(d.device_id);
      const serial = d.display_label ? ` · ${escapeHtml(d.device_id)}` : "";
      const grp = d.notification_group ? `[${escapeHtml(d.notification_group)}] ` : "";
      const z = d.zone ? ` · ${escapeHtml(d.zone)}` : "";
      return `<option value="${escapeHtml(d.device_id)}">${grp}${lab}${serial}${z}</option>`;
    }).join(""));
    sel.disabled = false;
    if (enabled) fireBtn.disabled = false;

    fireBtn.addEventListener("click", async () => {
      const action = $("#action").value;
      const durMin = parseFloat(($("#durMin") && $("#durMin").value) || "") || (DEFAULT_REMOTE_SIREN_MS / 60000);
      const dur = Math.round(Math.min(5, Math.max(0.5, durMin)) * 60000);
      const ids = Array.from(sel.selectedOptions).map((o) => o.value);
      if (action === "on" && !confirm(`Turn siren on for ${ids.length === 0 ? "all boards you can see" : `${ids.length} selected board(s)`}?`)) return;
      try {
        const r = await api(buildPostAlertsPath(), { method: "POST", body: { action, duration_ms: dur, device_ids: ids } });
        toast(`Siren ${action === "on" ? "on" : "off"} · ${r.sent_count} board(s)`, "ok");
      } catch (e) { toast(softApiError(e), "err"); }
    });
    return;
  }

  const teamMode = tab === "team";
  setCrumb(teamMode ? "Team assignments" : "Response");
  if (!catalogEnabled(state.me, "responder_dispatch")) {
    mountView(view, capabilityUnavailableHtml({
      title: "Response team",
      detail: "Responder assignments are not opened for this account yet. Siren control remains available.",
      backHref: "#/response?tab=siren",
      backLabel: "Open siren",
    }));
    return;
  }
  const focusDispatchId = String((window.__routeQuery && window.__routeQuery.get("dispatch_id")) || "").trim();
  const humanDispatchStatus = (status) => {
    const s = String(status || "").toLowerCase();
    return ({
      pending: "Pending",
      accepted: "Accepted",
      en_route: "En route",
      on_scene: "On scene",
      resolved: "Resolved",
      cancelled: "Cancelled",
    })[s] || String(status || "—").replace(/_/g, " ");
  };
  mountView(view, `<div class="card glass-panel glass-panel--panel route-loading" aria-busy="true"><span class="route-loading__label muted">Loading ${teamMode ? "team assignments" : "your assignments"}…</span></div>`);
  try {
    const data = await api(teamMode ? "/responder-dispatches?limit=60" : "/responder-dispatches/mine?limit=40");
    if (!isRouteCurrent(routeSeq)) return;
    const items = data.items || [];
    const incidentGo = (d) => {
      const aid = String(d.alarm_id || "").trim();
      if (aid && /^\d+$/.test(aid)) {
        return `<button type="button" class="btn sm ghost btn-tap" data-cc-go="incidents" data-cc-id="${escapeHtml(aid)}">Open</button>`;
      }
      const did = String(d.dispatch_id || "").trim();
      if (did) {
        return `<button type="button" class="btn sm ghost btn-tap" data-cc-go="response?dispatch_id=${encodeURIComponent(did)}">Open</button>`;
      }
      return `<button type="button" class="btn sm ghost btn-tap" data-cc-go="response">Open</button>`;
    };
    const transitionActionsHtml = (d) => {
      const status = String(d.status || "pending");
      const did = escapeHtml(String(d.dispatch_id || ""));
      const next = {
        pending: ["accepted", "Accept"],
        accepted: ["en_route", "En route"],
        en_route: ["on_scene", "On scene"],
        on_scene: ["resolved", "Resolve"],
      }[status];
      const primary = next
        ? `<button class="btn btn-tap sm dispatch-transition" type="button" data-dispatch-id="${did}" data-next-status="${next[0]}">${next[1]}</button>`
        : "";
      const cancel = !["resolved", "cancelled"].includes(status)
        ? `<button class="btn secondary btn-tap sm dispatch-transition" type="button" data-dispatch-id="${did}" data-next-status="cancelled">Cancel</button>`
        : "";
      return `<div class="row" style="gap:6px;justify-content:flex-end;flex-wrap:wrap">${primary}${cancel}</div>`;
    };
    mountView(view, `
      <div class="card glass-panel glass-panel--panel">
        <h2>${teamMode ? "Team assignments" : "My assignments"}</h2>
        ${typeof buildTonightStageStripHtml === "function" ? buildTonightStageStripHtml({
          stage: "during",
          active: "assign",
          extra: [
            { dest: "response", label: "My queue", current: !teamMode },
            ...(canManageTeam ? [{ dest: "response?tab=team", label: "Team", current: teamMode }] : []),
            { dest: "response?tab=siren", label: "Siren" },
          ],
        }) : ""}
        <p class="muted">${teamMode ? "All responder assignments for this account." : "Alarms assigned to you."}</p>
        ${canManageTeam ? dispatchComposerHtml() : ""}
        ${focusDispatchId ? `<p class="badge ok">Showing one focused assignment</p>` : ""}
        <table class="table">
          <thead><tr><th>Status</th><th>Device</th><th>Responder</th><th>Note</th><th></th></tr></thead>
          <tbody id="dispatchQueueBody">
            ${items.map((d) => {
              const did = String(d.dispatch_id || "");
              const focused = focusDispatchId && did === focusDispatchId;
              return `<tr class="${focused ? "row-focus" : ""}" data-dispatch-row="${escapeHtml(did)}">
              <td class="dispatch-status">${escapeHtml(humanDispatchStatus(d.status))}</td>
              <td class="mono">${escapeHtml(String(d.device_id || ""))}</td>
              <td>${escapeHtml(String(d.assigned_responder || "—"))}</td>
              <td>${escapeHtml(String(d.dispatch_reason || ""))}</td>
              <td>
                <div style="text-align:right">${incidentGo(d)}</div>
                <div class="dispatch-actions" style="margin-top:6px">${transitionActionsHtml(d)}</div>
              </td>
            </tr>`;
            }).join("") || `<tr><td colspan="5" class="muted">${teamMode ? "No team assignments yet." : "Nothing assigned to you yet."}</td></tr>`}
          </tbody>
        </table>
      </div>`);
    const bindTransitionButtons = () => {
      view.querySelectorAll(".dispatch-transition").forEach((button) => {
        if (button.dataset.bound === "1") return;
        button.dataset.bound = "1";
        button.addEventListener("click", async () => {
          const dispatchId = String(button.dataset.dispatchId || "");
          const nextStatus = String(button.dataset.nextStatus || "");
          if (!dispatchId || !nextStatus) return;
          let resolution = "";
          if (nextStatus === "resolved") {
            const entered = prompt("Resolution summary");
            if (entered === null) return;
            resolution = String(entered).trim();
          }
          if (nextStatus === "cancelled" && !confirm("Cancel this dispatch?")) return;
          button.disabled = true;
          try {
            const result = await api(
              teamMode
                ? `/responder-dispatches/${encodeURIComponent(dispatchId)}/transition`
                : `/responder-dispatches/mine/${encodeURIComponent(dispatchId)}/transition`,
              { method: "POST", body: { status: nextStatus, resolution } },
            );
            const updated = (result && result.record) || { dispatch_id: dispatchId, status: nextStatus };
            const row = button.closest("tr");
            const statusCell = row && row.querySelector(".dispatch-status");
            const actions = row && row.querySelector(".dispatch-actions");
            if (statusCell) statusCell.textContent = humanDispatchStatus(updated.status || nextStatus);
            if (actions) actions.innerHTML = transitionActionsHtml(updated);
            bindTransitionButtons();
            toast(`Status → ${humanDispatchStatus(updated.status || nextStatus)}`, "ok");
          } catch (e) {
            button.disabled = false;
            toast(softApiError(e), "err");
          }
        });
      });
    };
    if (canManageTeam) {
      wireDispatchComposer(view, {
        api,
        toast,
        onCreated: (record) => {
          const tbody = view.querySelector("#dispatchQueueBody");
          if (!tbody || !record) return;
          const empty = tbody.querySelector("td[colspan]");
          if (empty) tbody.innerHTML = "";
          const did = String(record.dispatch_id || "");
          const tr = document.createElement("tr");
          tr.setAttribute("data-dispatch-row", did);
          tr.innerHTML = `
              <td class="dispatch-status">${escapeHtml(humanDispatchStatus(record.status || "pending"))}</td>
              <td class="mono">${escapeHtml(String(record.device_id || ""))}</td>
              <td>${escapeHtml(String(record.assigned_responder || "—"))}</td>
              <td>${escapeHtml(String(record.dispatch_reason || ""))}</td>
              <td>
                <div style="text-align:right">${incidentGo(record)}</div>
                <div class="dispatch-actions" style="margin-top:6px">${transitionActionsHtml(record)}</div>
              </td>`;
          tbody.prepend(tr);
          bindTransitionButtons();
        },
      });
    }
    bindTransitionButtons();
  } catch (e) {
    if (!isRouteCurrent(routeSeq)) return;
    const msg = String(e.message || e);
    mountView(view, `
      <div class="card glass-panel glass-panel--panel">
        <h2>Response team</h2>
        <p class="muted">${escapeHtml(msg)}</p>
        <p><button type="button" class="btn sm ghost btn-tap" data-cc-go="response?tab=siren">Use siren control</button> if assignments are unavailable.</p>
      </div>`);
  }
});


// ── sa-team.route.js ─────────────────────────────────────
/**
 * Route: #/sa-team — Root SA only: provision delegated sa_staff accounts.
 */

registerRoute("sa-team", async (view) => {
  setCrumb("SA team");
  const me = state.me || {};
  if (!me.can_provision_sa) {
    const isRootSa = me.role === "superadmin";
    mountView(
      view,
      `<div class="card glass-panel glass-panel--panel">
        <h2 style="margin:0 0 8px">SA team</h2>
        <p class="muted" style="margin:0;font-size:13px;line-height:1.45">
          ${isRootSa
    ? "Staff accounts are not opened for this login yet. Ask platform ops to enable staff provisioning for your root account, then refresh this page."
    : "Root platform account only. Ask platform ops if you need staff provisioning."}
        </p>
      </div>`,
    );
    return;
  }

  const isDbBusyMsg = (e) => {
    const msg = String((e && e.message) || e || "");
    return msg.includes("503")
      || msg.includes("database contended")
      || msg.includes("System busy")
      || msg.includes("account store temporarily unavailable");
  };

  const CAPS = [
    { id: "sa_device_admin", label: "Device / factory", hint: "Factory ops & device tools" },
    { id: "sa_ota_admin", label: "Firmware updates", hint: "Firmware rollouts" },
    { id: "sa_audit_read", label: "Audit log", hint: "Read audit trail" },
    { id: "sa_security_admin", label: "Security", hint: "Backup & server logs" },
    { id: "sa_user_admin", label: "Accounts / users", hint: "Admin & user management" },
    { id: "sa_billing_admin", label: "Billing", hint: "Future billing ops" },
  ];

  let lastUpdatedUser = "";
  const ttlOpsInFlight = new Set();

  const ttlInputParts = (unixSec) => {
    if (!unixSec) return { date: "", time: "" };
    const d = new Date(Number(unixSec) * 1000);
    if (!Number.isFinite(d.getTime())) return { date: "", time: "" };
    const pad = (n) => String(n).padStart(2, "0");
    return {
      date: `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`,
      time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
    };
  };

  const ttlRemainingText = (unixSec) => {
    if (!unixSec) return "—";
    const remain = Number(unixSec) - Math.floor(Date.now() / 1000);
    if (!Number.isFinite(remain) || remain <= 0) return "expired";
    const d = Math.floor(remain / 86400);
    const h = Math.floor((remain % 86400) / 3600);
    const m = Math.floor((remain % 3600) / 60);
    if (d > 0) return `${d}d ${h}h left`;
    if (h > 0) return `${h}h ${m}m left`;
    return `${Math.max(1, m)}m left`;
  };

  const parseDdMmYyyy = (str) => {
    const s = String(str || "").trim();
    if (!s) return null;
    const m = /^(\d{1,2})-(\d{1,2})-(\d{4})$/.exec(s);
    if (!m) return "bad";
    const day = Number(m[1]);
    const month = Number(m[2]);
    const year = Number(m[3]);
    if (month < 1 || month > 12 || day < 1 || day > 31) return "bad";
    const dt = new Date(year, month - 1, day);
    if (dt.getFullYear() !== year || dt.getMonth() !== month - 1 || dt.getDate() !== day) return "bad";
    return { year, month, day };
  };

  const expiryUnixFromDdMmYyyy = (parts, timeValue) => {
    let hh = 23;
    let mm = 59;
    const ts = String(timeValue || "").trim();
    if (ts) {
      const p = ts.split(":");
      const h = Number(p[0]);
      const mi = Number(p[1] || 0);
      if (!Number.isFinite(h) || !Number.isFinite(mi) || h < 0 || h > 23 || mi < 0 || mi > 59) {
        return "bad-time";
      }
      hh = h;
      mm = mi;
    }
    const dt = new Date(parts.year, parts.month - 1, parts.day, hh, mm, 0, 0);
    return Math.floor(dt.getTime() / 1000);
  };

  const capFieldset = CAPS.map(
    (c) => `
    <label class="checkbox tenant-deleg-cap-line">
      <input type="checkbox" data-cap="${escapeHtml(c.id)}" />
      <span>
        <strong>${escapeHtml(c.label)}</strong>
        <span class="muted tenant-deleg-cap-hint">${escapeHtml(c.hint)}</span>
      </span>
    </label>`,
  ).join("");

  mountView(view, `
    <div class="tenant-tool-layout">
      <aside class="card tenant-tool-aside">
        <h2 class="tenant-tool-aside__title">Create SA staff</h2>
        <p class="muted tenant-tool-aside__lede">Delegated operators with the boxes you tick. They finish at <a href="#/account-activate">Activate account</a> (phone call + email code).</p>
        <div class="divider"></div>
        <label class="field"><span>Username</span><input id="sa_u" autocomplete="off" /></label>
        <label class="field"><span>Password (≥12, upper, lower, digit, symbol)</span><input id="sa_p" type="password" autocomplete="new-password" /></label>
        <label class="field"><span>Email</span><input id="sa_e" type="email" autocomplete="off" /></label>
        <fieldset class="tenant-cap-fieldset">
          <legend>Powers (capabilities)</legend>
          ${capFieldset}
        </fieldset>
        <label class="field"><span>Access expires — date (optional)</span>
          <input id="sa_until_date" type="text" inputmode="numeric" placeholder="dd-mm-yyyy" autocomplete="off" spellcheck="false" />
        </label>
        <label class="field"><span>End time (optional)</span>
          <input id="sa_until_time" type="time" />
        </label>
        <p class="muted" style="font-size:11px;margin:8px 0 0;line-height:1.45">Blank date = no auto-expiry. Local time; empty time → <span class="mono">23:59</span>.</p>
        <div class="tenant-tool-aside__actions">
          <button class="btn btn-tap" id="sa_go" type="button">Create &amp; send activation</button>
        </div>
        <p class="muted" id="sa_msg" style="margin:10px 0 0;font-size:12px;line-height:1.45"></p>
        <p class="muted" id="sa_email_otp_expiry" style="margin:6px 0 0;font-size:12px;line-height:1.45" aria-live="polite"></p>
      </aside>
      <div class="card tenant-tool-main">
        <div class="row wide sa-team-ttl-head" style="justify-content:space-between;align-items:center;flex-wrap:wrap">
          <h3 class="tenant-tool-main__title" style="margin:0">SA team</h3>
          <button class="btn secondary btn-tap sm" id="sa_list_reload" type="button">Refresh</button>
        </div>
        <p class="muted" style="margin:8px 0 0;font-size:12px;line-height:1.45">Click a row to show permissions, access expiry, and extend or clear access window (<span class="mono">dd-mm-yyyy</span> + optional time).</p>
        <div style="overflow-x:auto;margin-top:8px">
          <table class="table compact" style="width:100%;border-collapse:collapse">
            <thead>
              <tr>
                <th>Username</th><th>Email</th><th>Status</th>
              </tr>
            </thead>
            <tbody id="sa_list_body"><tr><td colspan="3" class="muted">Loading…</td></tr></tbody>
          </table>
        </div>
      </div>
    </div>`);

  const renderSaListRows = (items) => {
    if (!items.length) return `<tr><td colspan="3" class="muted">No delegated SA staff.</td></tr>`;
    return items.map((row) => {
      const u = escapeHtml(String(row.username || ""));
      const rawUser = String(row.username || "");
      const em = escapeHtml(String(row.email || "").trim() || "—");
      const st = escapeHtml(String(row.status || "—"));
      const caps = escapeHtml(String(row.sa_capabilities_csv || "—"));
      const until = row.sa_capabilities_granted_until ? fmtTs(row.sa_capabilities_granted_until) : "—";
      const remain = escapeHtml(ttlRemainingText(row.sa_capabilities_granted_until));
      const _ttlState = escapeHtml(String(row.sa_capabilities_ttl_state || "no-ttl"));
      const parts = ttlInputParts(row.sa_capabilities_granted_until);
      const ttlDateVal = escapeHtml(parts.date);
      const ttlTimeVal = escapeHtml(parts.time);
      const sumStyle = lastUpdatedUser && rawUser === lastUpdatedUser
        ? ' style="background:rgba(34,197,94,0.08)"'
        : "";
      return `<tr class="sa-ttl-sum" tabindex="0" data-sa-user="${escapeHtml(rawUser)}" title="Show access window &amp; permissions"${sumStyle}>
        <td style="cursor:pointer"><span class="mono">${u}</span> <span class="muted" style="font-size:11px">▸</span></td>
        <td class="mono" style="font-size:11px;cursor:pointer">${em}</td>
        <td style="cursor:pointer">${st}</td>
      </tr>
      <tr class="sa-ttl-detail" hidden data-sa-detail="${escapeHtml(rawUser)}">
        <td colspan="3" style="padding:0;border-top:0">
          <div style="padding:10px 12px 14px 14px;border-top:1px solid rgba(127,127,127,0.2)">
            <div class="row" style="flex-wrap:wrap;gap:14px;align-items:flex-start">
              <div style="flex:1;min-width:200px">
                <div class="muted" style="font-size:11px;letter-spacing:0.02em;text-transform:uppercase">Permissions</div>
                <div class="mono" style="font-size:12px;margin-top:4px;white-space:normal;word-break:break-word">${caps}</div>
              </div>
              <div class="muted" style="font-size:12px;min-width:170px">
                <div class="muted" style="font-size:11px;letter-spacing:0.02em;text-transform:uppercase">Access until</div>
                <div style="margin-top:4px">${until}</div>
                <div style="font-size:11px">${remain}</div>
              </div>
              <div style="flex:1;min-width:220px">
                <div class="muted" style="font-size:11px;font-weight:600;margin-bottom:6px">Adjust access window</div>
                <div class="row" style="gap:6px;flex-wrap:wrap">
                  <button class="btn secondary btn-tap sm sa-ttl-7d" data-user="${u}" type="button">Extend +7d</button>
                  <button class="btn secondary btn-tap sm sa-ttl-30d" data-user="${u}" type="button">Extend +30d</button>
                  <button class="btn secondary btn-tap sm sa-ttl-clear" data-user="${u}" type="button">Clear</button>
                </div>
                <div class="row" style="gap:8px;margin-top:8px;align-items:flex-end;flex-wrap:wrap">
                  <label class="field" style="margin:0;min-width:130px;flex:1"><span class="muted" style="font-size:11px">dd-mm-yyyy</span>
                    <input class="sa-ttl-date" data-user="${u}" type="text" inputmode="numeric" placeholder="dd-mm-yyyy" value="${ttlDateVal}" autocomplete="off" spellcheck="false" />
                  </label>
                  <label class="field" style="margin:0;min-width:100px"><span class="muted" style="font-size:11px">Time</span>
                    <input class="sa-ttl-time" data-user="${u}" type="time" value="${ttlTimeVal}" />
                  </label>
                  <button class="btn secondary btn-tap sm sa-ttl-set" data-user="${u}" type="button">Set</button>
                </div>
              </div>
            </div>
          </div>
        </td>
      </tr>`;
    }).join("");
  };

  const loadSaList = async () => {
    try {
      const data = await api(buildGetSaTeamPath(), { timeoutMs: SA_CONSOLE_TIMEOUT_MS });
      const body = $("#sa_list_body");
      if (body) body.innerHTML = renderSaListRows(Array.isArray(data.items) ? data.items : []);
    } catch (err) {
      const body = $("#sa_list_body");
      const text = escapeHtml(String(err && err.message ? err.message : err));
      if (body) body.innerHTML = `<tr><td colspan="3" class="muted">${text}</td></tr>`;
    }
  };

  const currentTtlForUser = (user) => {
    const dateEl = $(`.sa-ttl-date[data-user="${user}"]`, view);
    const timeEl = $(`.sa-ttl-time[data-user="${user}"]`, view);
    const dateRaw = String((dateEl && dateEl.value) || "").trim();
    const timeRaw = String((timeEl && timeEl.value) || "").trim();
    if (!dateRaw) return null;
    const parsed = parseDdMmYyyy(dateRaw);
    if (parsed === "bad" || parsed === null) return null;
    const unix = expiryUnixFromDdMmYyyy(parsed, timeRaw);
    if (unix === "bad-time") return null;
    return unix;
  };

  const ttlLabel = (unixSec) => (unixSec ? fmtTs(unixSec) : "none");

  const setRowTtlControlsDisabled = (user, disabled) => {
    $$(`.sa-ttl-date[data-user="${user}"], .sa-ttl-time[data-user="${user}"], .sa-ttl-7d[data-user="${user}"], .sa-ttl-30d[data-user="${user}"], .sa-ttl-clear[data-user="${user}"], .sa-ttl-set[data-user="${user}"]`, view).forEach((n) => {
      n.disabled = !!disabled;
    });
  };

  const go = $("#sa_go");
  if (go) {
    go.addEventListener("click", async () => {
      const msg = $("#sa_msg");
      const u = String(($("#sa_u") && $("#sa_u").value) || "").trim();
      const p = String(($("#sa_p") && $("#sa_p").value) || "");
      const e = String(($("#sa_e") && $("#sa_e").value) || "").trim();
      const untilDateRaw = String(($("#sa_until_date") && $("#sa_until_date").value) || "").trim();
      const untilTimeRaw = String(($("#sa_until_time") && $("#sa_until_time").value) || "").trim();
      const caps = [];
      $$('.tenant-tool-aside input[type="checkbox"][data-cap]', view).forEach((x) => {
        if (x.checked) caps.push(x.getAttribute("data-cap"));
      });
      let grantedUntil;
      if (untilDateRaw) {
        const parsed = parseDdMmYyyy(untilDateRaw);
        if (parsed === "bad") {
          if (msg) msg.textContent = "Use expiry date as dd-mm-yyyy (example: 31-12-2026), or leave blank.";
          return;
        }
        const unix = expiryUnixFromDdMmYyyy(parsed, untilTimeRaw);
        if (unix === "bad-time") {
          if (msg) msg.textContent = "Invalid end time; use the time picker or leave it blank for 23:59.";
          return;
        }
        grantedUntil = unix;
        if (grantedUntil <= Math.floor(Date.now() / 1000)) {
          if (msg) msg.textContent = "Expiry must be in the future.";
          return;
        }
      } else if (untilTimeRaw) {
        if (msg) msg.textContent = "Set an expiry date before choosing an end time, or clear the time field.";
        return;
      }
      if (!u || !p || !e) {
        if (msg) msg.textContent = "Fill username, password, email.";
        return;
      }
      if (!caps.length) {
        if (msg) msg.textContent = "Select at least one capability.";
        return;
      }
      go.disabled = true;
      if (msg) msg.textContent = "Sending…";
      try {
        const resp = await api(buildPostSaTeamProvisionPath(), {
          method: "POST",
          body: { username: u, password: p, email: e, capabilities: caps, granted_until: grantedUntil },
          timeoutMs: SA_CONSOLE_TIMEOUT_MS,
        });
        if (msg) {
          const got = resp && Object.prototype.hasOwnProperty.call(resp, "granted_until")
            ? resp.granted_until
            : grantedUntil;
          const suffix = got ? ` TTL until ${fmtTs(got)}.` : " No TTL (manual revoke required).";
          msg.textContent = `Created pending user — check email for activation code.${suffix}`;
        }
        const ex = $("#sa_email_otp_expiry");
        if (ex) {
          const ttl = Number(resp && resp.ttl_seconds) || 0;
          if (ttl > 0) startEmailCodeExpiryCountdown(ex, ttl);
        }
        if ($("#sa_p")) $("#sa_p").value = "";
        if ($("#sa_until_date")) $("#sa_until_date").value = "";
        if ($("#sa_until_time")) $("#sa_until_time").value = "";
        await loadSaList();
      } catch (err) {
        if (msg) {
          msg.textContent = isDbBusyMsg(err)
            ? "Account store busy — retry shortly or ask an operator to finish the account-store cutover."
            : String(err && err.message ? err.message : err);
        }
      } finally {
        go.disabled = false;
      }
    });
  }

  const reload = $("#sa_list_reload");
  if (reload) reload.addEventListener("click", () => loadSaList());

  const toggleSaDetailForSumRow = (sumRow) => {
    if (!sumRow || !sumRow.classList || !sumRow.classList.contains("sa-ttl-sum")) return;
    const next = sumRow.nextElementSibling;
    if (!next || !next.classList.contains("sa-ttl-detail")) return;
    next.hidden = !next.hidden;
    const mark = sumRow.querySelector("td .muted");
    if (mark && (mark.textContent === "▸" || mark.textContent === "▾")) {
      mark.textContent = next.hidden ? "▸" : "▾";
    }
  };

  view.addEventListener("click", (ev) => {
    const sum = ev.target && ev.target.closest && ev.target.closest(".sa-ttl-sum");
    if (sum && !(ev.target.closest && ev.target.closest("a,button"))) {
      toggleSaDetailForSumRow(sum);
    }
  });
  view.addEventListener("keydown", (ev) => {
    if (ev.key !== "Enter" && ev.key !== " ") return;
    const t = ev.target;
    if (!t || !t.classList || !t.classList.contains("sa-ttl-sum")) return;
    ev.preventDefault();
    toggleSaDetailForSumRow(t);
  });

  view.addEventListener("click", async (ev) => {
    const t = ev.target;
    if (!t || !t.classList) return;
    const user = String(t.getAttribute("data-user") || "").trim();
    if (!user) return;
    if (ttlOpsInFlight.has(user)) return;
    const beforeUntil = currentTtlForUser(user);
    let grantedUntil;
    if (t.classList.contains("sa-ttl-clear")) {
      if (!confirm(
        `[Warning] Clear TTL for ${user}?\nBefore: ${ttlLabel(beforeUntil)}\nAfter: none\nThis makes capability access non-expiring until manually changed.`,
      )) {
        return;
      }
      grantedUntil = null;
    } else if (t.classList.contains("sa-ttl-7d")) {
      const base = Math.max(Math.floor(Date.now() / 1000), Number(beforeUntil || 0));
      grantedUntil = base + 7 * 24 * 3600;
      if (!confirm(
        `[Confirm] Extend TTL for ${user} by +7d?\nBefore: ${ttlLabel(beforeUntil)}\nAfter: ${ttlLabel(grantedUntil)}`,
      )) {
        return;
      }
    } else if (t.classList.contains("sa-ttl-30d")) {
      const base = Math.max(Math.floor(Date.now() / 1000), Number(beforeUntil || 0));
      grantedUntil = base + 30 * 24 * 3600;
      if (!confirm(
        `[Confirm] Extend TTL for ${user} by +30d?\nBefore: ${ttlLabel(beforeUntil)}\nAfter: ${ttlLabel(grantedUntil)}`,
      )) {
        return;
      }
    } else if (t.classList.contains("sa-ttl-set")) {
      const dateEl = $(`.sa-ttl-date[data-user="${user}"]`, view);
      const timeEl = $(`.sa-ttl-time[data-user="${user}"]`, view);
      const dateRaw = String((dateEl && dateEl.value) || "").trim();
      const timeRaw = String((timeEl && timeEl.value) || "").trim();
      if (!dateRaw) {
        toast("Enter dd-mm-yyyy (and optional time), or use Extend/Clear.", "err");
        return;
      }
      const parsed = parseDdMmYyyy(dateRaw);
      if (parsed === "bad") {
        toast("Invalid date — use dd-mm-yyyy.", "err");
        return;
      }
      const unix = expiryUnixFromDdMmYyyy(parsed, timeRaw);
      if (unix === "bad-time") {
        toast("Invalid time.", "err");
        return;
      }
      grantedUntil = unix;
      if (grantedUntil <= Math.floor(Date.now() / 1000)) {
        toast("Expiry must be in the future.", "err");
        return;
      }
      if (!confirm(
        `[Confirm] Set exact TTL for ${user}?\nBefore: ${ttlLabel(beforeUntil)}\nAfter: ${ttlLabel(grantedUntil)}`,
      )) {
        return;
      }
    } else {
      return;
    }
    ttlOpsInFlight.add(user);
    setRowTtlControlsDisabled(user, true);
    const originalText = t.textContent;
    t.textContent = "Applying…";
    try {
      await api(buildPostSaTeamTtlPath(user), {
        method: "POST",
        body: { granted_until: grantedUntil },
        timeoutMs: SA_CONSOLE_TIMEOUT_MS,
      });
      lastUpdatedUser = user;
      const beforeText = beforeUntil ? fmtTs(beforeUntil) : "none";
      const afterText = grantedUntil ? fmtTs(grantedUntil) : "none";
      toast(`Access window updated for ${user}: ${beforeText} -> ${afterText}`, "ok");
      await loadSaList();
    } catch (err) {
      toast(String(err && err.message ? err.message : err), "err");
    } finally {
      if (originalText != null) t.textContent = originalText;
      setRowTtlControlsDisabled(user, false);
      ttlOpsInFlight.delete(user);
    }
  });
  await loadSaList();
});


// ── settings.route.js ─────────────────────────────────────
/**
 * Route: #/settings — Settings overview (admin, billing, telegram on one page).
 */

registerRoute("settings", async (view, _args, routeSeq) => {
  setCrumb("Settings");
  await renderSettingsOverviewPage(view, {
    activeNavId: "hub",
    routeSeq,
    me: state.me,
    hasRole,
    can,
  });
});


// ── site.route.js ─────────────────────────────────────
/**
 * Route: #/site — Tenant site overview (superadmin).
 *
 * Build: split out of src/console.raw.js and concatenated as raw text by
 * scripts/build-dashboard.mjs after the monolith body. Shares scope with
 * helpers like $, mountView, api, registerRoute, state, toast, can, setCrumb
 * (defined in console.raw.js and the lib/ modules spliced at the top).
 */

registerRoute("site", async (view, _args, routeSeq) => {
  setCrumb("Site · owners & groups");
  if (!(state.me && state.me.role === "superadmin")) {
    mountView(view, `<div class="card glass-panel glass-panel--panel"><p class="muted">Superadmin only.</p></div>`);
    return;
  }
  let ownerQ = String((window.__routeQuery && window.__routeQuery.get("owner")) || "").trim();
  let ov = null;
  let siteFleetList = { items: [] };
  let allDevs = [];
  try {
    const bundle = await fetchSiteOverviewBundle(api, {
      timeoutMs: 22000,
      retries: 2,
      ownerFilterQ: ownerQ,
    });
    if (!isRouteCurrent(routeSeq)) return;
    ov = bundle && bundle.ov;
    siteFleetList = (bundle && bundle.list) || { items: [] };
    allDevs = Array.isArray(siteFleetList.items) ? siteFleetList.items.slice() : [];
  } catch (e) {
    if (!isRouteCurrent(routeSeq)) return;
    mountView(view, `<div class="card glass-panel glass-panel--panel"><p class="muted">${escapeHtml(softApiError(e))}</p></div>`);
    return;
  }
  const slotsCtx = {
    me: state.me,
    canonicalGroupKey,
    groupCardMetaKey,
    buildGroupSlotsFromDeviceList,
  };
  const filtered = () => filterSiteDevicesByOwner(allDevs, ownerQ);
  const ownerPack = collectSiteOwnerUsernames(ov, allDevs, slotsCtx);
  const ownersU = ownerPack.owners;
  const ownersNote = siteOwnersIndexNote(ownerPack, siteFleetList, ov);
  const ownerFilterBanner = siteOwnerServerFilterActive(ownerQ)
    ? siteOwnerServerFilterBannerHtml()
    : "";
  const slots = collectSiteGroupSlots(ov, allDevs, ownerQ, slotsCtx);
  let siteFleetNextCursor = siteFleetList.page && siteFleetList.page.next_cursor != null
    ? String(siteFleetList.page.next_cursor)
    : null;
  let siteFleetHasMore = !!(siteFleetList.page && siteFleetList.page.has_more) || !!siteFleetList.truncated;
  const grpRows = slots.map((s) => {
    const owq = s.tenantOwner ? `?owner=${encodeURIComponent(s.tenantOwner)}` : "";
    return `<tr><td><a href="#/group/${encodeURIComponent(s.groupKey)}${owq}">${escapeHtml(s.groupKey)}</a></td><td class="mono">${escapeHtml(s.tenantOwner || "—")}</td></tr>`;
  }).join("");
  mountView(view, `
    <section class="card glass-panel glass-panel--panel">
      <h2 class="ui-section-title" style="margin:0">Site</h2>
      <p class="muted" style="margin:8px 0 0">Search by <strong>account owner</strong> username. Lists boards and <strong>notification groups</strong> under that filter.</p>
      <div class="inline-form" style="margin-top:12px;flex-wrap:wrap;gap:10px;align-items:flex-end">
        <label class="field grow"><span>Owner contains</span>
          <input type="search" id="siteOwnerQ" value="${escapeHtml(ownerQ)}" placeholder="e.g. dan" list="siteOwnerDl" autocomplete="off" />
          <datalist id="siteOwnerDl">${ownersU.map((o) => `<option value="${escapeHtml(o)}"></option>`).join("")}</datalist>
        </label>
        <button type="button" class="btn sm secondary btn-tap" id="siteOwnerClear">Clear</button>
        <button type="button" class="btn btn-tap" id="siteOwnerApply">Apply</button>
      </div>
    </section>
    <div id="siteFleetBannerHost">${ownerFilterBanner}${siteFleetBannerHtml(siteFleetList, ov)}</div>
    <section class="card glass-panel glass-panel--panel">
      <h3 style="margin:0 0 8px">Owners in fleet (${ownersU.length})</h3>
      <p class="muted" style="margin:0;font-size:12px">${escapeHtml(ownersNote)}</p>
      <div class="row" style="gap:6px;flex-wrap:wrap;margin-top:10px">
        ${ownersU.map((o) => `<button type="button" class="chip js-site-owner-chip" data-o="${escapeHtml(o)}" style="cursor:pointer">${escapeHtml(o)}</button>`).join("")}
      </div>
    </section>
    <section class="card glass-panel glass-panel--panel">
      <h3 style="margin:0 0 8px">Boards (<span id="siteDeviceCount">${filtered().length}</span>)</h3>
      <div class="table-wrap"><table class="t">
        <thead><tr><th>Board</th><th>Owner</th><th>Notification group</th><th>Zone</th><th>Presence</th></tr></thead>
        <tbody id="siteDeviceTbody"></tbody>
      </table></div>
      <div id="siteFleetLoadMoreWrap" style="margin-top:12px;display:none">
        <button class="btn secondary btn-tap" id="siteFleetLoadMore" type="button">Load more boards</button>
        <p class="muted" id="siteFleetLoadMoreHint" style="margin:8px 0 0"></p>
      </div>
    </section>
    <section class="card glass-panel glass-panel--panel">
      <h3 style="margin:0 0 8px">Groups (${slots.length})</h3>
      <div class="table-wrap"><table class="t">
        <thead><tr><th>Group</th><th>Account owner</th></tr></thead>
        <tbody>${grpRows || `<tr><td colspan="2" class="muted">No groups in filter.</td></tr>`}</tbody>
      </table></div>
    </section>
  `);
  const renderDeviceTable = () => {
    const fd = filtered();
    const tbody = $("#siteDeviceTbody", view);
    const countEl = $("#siteDeviceCount", view);
    const rows = fd.map((d) => buildSiteDeviceTableRowHtml(
      d,
      devicePresenceStatus,
      devicePresenceBadgeText,
      escapeHtml,
    )).join("");
    if (tbody) {
      tbody.innerHTML = rows || `<tr><td colspan="5" class="muted">No boards match.</td></tr>`;
    }
    if (countEl) countEl.textContent = String(fd.length);
    const wrap = $("#siteFleetLoadMoreWrap", view);
    const btn = $("#siteFleetLoadMore", view);
    const hint = $("#siteFleetLoadMoreHint", view);
    if (wrap) wrap.style.display = siteFleetHasMore ? "" : "none";
    if (btn) btn.disabled = false;
    if (hint) {
      hint.textContent = siteOwnerServerFilterActive(ownerQ)
        ? (siteFleetHasMore
          ? `${allDevs.length} matches loaded — load more below`
          : `${allDevs.length} match(es)`)
        : (siteFleetHasMore
          ? `${allDevs.length} loaded — more boards available`
          : "");
    }
  };
  renderDeviceTable();
  const loadMoreBtn = $("#siteFleetLoadMore", view);
  if (loadMoreBtn) {
    loadMoreBtn.addEventListener("click", async () => {
      if (!siteFleetHasMore || !siteFleetNextCursor) return;
      loadMoreBtn.disabled = true;
      try {
        const devPath = pickDevicesPollPath();
        const pg = await appendSiteFleetPage(api, {
          cursor: siteFleetNextCursor,
          live: devPath.includes("skip_cache=1"),
          ownerFilterQ: ownerQ,
          timeoutMs: 20000,
          retries: 1,
        });
        if (!isRouteCurrent(routeSeq)) return;
        const seen = new Set(allDevs.map((d) => String(d.device_id || "")));
        const extra = (pg.items || []).filter((d) => {
          const id = String(d.device_id || "");
          return id && !seen.has(id);
        });
        allDevs = allDevs.concat(extra);
        siteFleetList = { ...siteFleetList, items: allDevs, page: pg.page };
        const cur = siteFleetCursorState(siteFleetList, pg.page);
        siteFleetNextCursor = cur.nextCursor;
        siteFleetHasMore = cur.hasMore;
        const bannerHost = $("#siteFleetBannerHost", view);
        if (bannerHost) bannerHost.innerHTML = siteFleetBannerHtml(siteFleetList, ov);
        renderDeviceTable();
      } catch (e) {
        toast(String(e && e.message) || "Failed to load more boards", "err");
      } finally {
        if (isRouteCurrent(routeSeq)) loadMoreBtn.disabled = false;
      }
    });
  }
  $("#siteOwnerApply", view).addEventListener("click", () => {
    ownerQ = String($("#siteOwnerQ", view)?.value || "").trim();
    location.hash = ownerQ ? `#/site?owner=${encodeURIComponent(ownerQ)}` : "#/site";
  });
  $("#siteOwnerClear", view).addEventListener("click", () => { location.hash = "#/site"; });
  $("#siteOwnerQ", view).addEventListener("keydown", (ev) => {
    if (ev.key === "Enter") { ev.preventDefault(); $("#siteOwnerApply", view).click(); }
  });
  $$(".js-site-owner-chip", view).forEach((btn) => {
    btn.addEventListener("click", () => {
      const o = btn.getAttribute("data-o") || "";
      location.hash = o ? `#/site?owner=${encodeURIComponent(o)}` : "#/site";
    });
  });
});


// ── snapshots.route.js ─────────────────────────────────────
/**
 * Route: #/snapshots — Evidence / camera snapshots (Assets · CAM-S4a).
 *
 * FE-U1 honesty: only Snapshot is product-backed (/admin/camera-snapshots).
 * Video / Audio / Sensor / GIS / Summary stay hidden until real backends exist.
 * Deep-link ?tab=video|audio|… shows a one-line unavailable notice, then Snapshot.
 */

registerRoute("snapshots", async (view, _args, routeSeq) => {
  setCrumb("Evidence");
  if (!state.me) {
    location.hash = "#/login";
    return;
  }
  if (!state.me.camera_snapshot_enabled) {
    mountView(view, capabilityUnavailableHtml({
      title: "Camera snapshots",
      detail: "Evidence snapshots are not opened for this account yet.",
      backHref: "#/settings",
    }));
    return;
  }
  if (!isRouteCurrent(routeSeq)) return;
  ensureCityObservationStyles();

  const isSuper = !!(state.me && state.me.role === "superadmin");
  const canEmail = hasRole("admin");

  const LIVE_TABS = [{ id: "snapshot", label: "Snapshot" }];
  const DEFERRED_TAB_IDS = new Set(["video", "audio", "sensor", "gis", "ai", "summary"]);
  const tabParam = String((window.__routeQuery && window.__routeQuery.get("tab")) || "")
    .trim()
    .toLowerCase();
  let deferredTabNotice = "";
  const deferredKey = tabParam === "summary" ? "ai" : tabParam;
  if (tabParam && tabParam !== "snapshot" && DEFERRED_TAB_IDS.has(deferredKey)) {
    const label =
      deferredKey === "ai" ? "Summary" : deferredKey.charAt(0).toUpperCase() + deferredKey.slice(1);
    deferredTabNotice = `${label} observation is not available yet (no live API). Showing Snapshot.`;
  }
  let activeTab = "snapshot";

  /** @type {Array<any>} */
  let items = [];
  let loadError = false;
  let ownerAdmin = "";
  const filters = { device: "", zone: "", rangeHours: 0 };
  const DONUT_COLORS = ["#38bdf8", "#6366f1", "#f97316", "#22c55e", "#eab308", "#ec4899", "#14b8a6", "#94a3b8"];

  // -------------------------------------------------------------- helpers
  const relFromSec = (sec) => {
    const t = Number(sec) * 1000;
    if (!Number.isFinite(t) || t <= 0) return "—";
    const diff = Date.now() - t;
    if (diff < 60000) return "just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return `${Math.floor(diff / 86400000)}d ago`;
  };
  const imgUrl = (sid) =>
    apiBase() + cameraApiPathWithOwner(buildGetAdminCameraSnapshotImagePath(String(sid)), ownerAdmin);
  const zoneOf = (it) => String((it && (it.zone || it.zone_name)) || "").trim();
  const remainText = (it) =>
    typeof it.remaining_label === "string" && it.remaining_label
      ? it.remaining_label
      : Number(it.days_remaining || 0) > 0
      ? `~${Number(it.days_remaining).toFixed(1)}d left`
      : "";

  const getFiltered = () => {
    const cutoff = filters.rangeHours > 0 ? Date.now() / 1000 - filters.rangeHours * 3600 : 0;
    return items.filter((it) => {
      if (filters.device && String(it.device_id || "") !== filters.device) return false;
      if (filters.zone && zoneOf(it) !== filters.zone) return false;
      if (cutoff && Number(it.created_at || 0) < cutoff) return false;
      return true;
    });
  };

  // -------------------------------------------------------------- shell
  const tabsHtml = LIVE_TABS.map(
    (t) => `<button type="button" class="cobs-tab${t.id === activeTab ? " cobs-tab--on" : ""}" data-cobs-tab="${t.id}">${escapeHtml(t.label)}</button>`,
  ).join("");
  const deferredBanner = deferredTabNotice
    ? `<p class="cobs-muted" id="cobsDeferredNotice" style="margin:0 0 12px;font-size:13px;line-height:1.5" role="status">${escapeHtml(deferredTabNotice)}</p>`
    : "";

  mountView(
    view,
    `
    <section class="cobs-page" aria-label="Evidence">
      <div class="cobs-head glass-panel glass-panel--panel">
        <div class="cobs-head__title">
          <span class="cobs-head__ico" aria-hidden="true">◉</span>
          <div>
            <h2>Evidence</h2>
            <p class="cobs-head__sub">Event-linked camera snapshots</p>
          </div>
        </div>
        <nav class="cobs-tabs" id="cobsTabs" aria-label="Evidence views">${tabsHtml}</nav>
      </div>
      ${deferredBanner}
      <div id="cobsBody"></div>
    </section>`,
  );

  // -------------------------------------------------------------- snapshot tab
  const buildSnapshotSkeleton = () => {
    const devices = [...new Set(items.map((it) => String(it.device_id || "")).filter(Boolean))].sort();
    const zones = [...new Set(items.map(zoneOf).filter(Boolean))].sort();
    const deviceOpts = `<option value="">All cameras</option>` + devices.map((d) => `<option value="${escapeHtml(d)}">${escapeHtml(d)}</option>`).join("");
    const zoneOpts = `<option value="">All zones</option>` + zones.map((z) => `<option value="${escapeHtml(z)}">${escapeHtml(z)}</option>`).join("");
    const ownerField = isSuper
      ? `<label class="cobs-field"><span>Account owner (optional)</span><input id="cobsOwner" placeholder="filter one account · blank = all" maxlength="64" autocomplete="off" value="${escapeHtml(ownerAdmin)}" /></label>
         <button type="button" class="btn sm secondary btn-tap cobs-apply" id="cobsOwnerApply" style="margin-bottom:12px">Apply filter</button>`
      : "";
    const zoneField = zones.length ? `<label class="cobs-field"><span>Zone</span><select id="cobsZone">${zoneOpts}</select></label>` : "";

    return `
      <div class="cobs-snap">
        <aside class="cobs-side glass-panel glass-panel--panel cobs-panel" aria-label="Snapshot filters">
          <div class="cobs-panel__head"><h3 class="cobs-title">Snapshot Filters</h3></div>
          ${ownerField}
          <label class="cobs-field"><span>Camera</span><select id="cobsDevice">${deviceOpts}</select></label>
          ${zoneField}
          <p class="cobs-eyebrow" style="margin:4px 0 8px">Quick filters</p>
          <div class="cobs-quick" id="cobsQuick">
            <button type="button" class="cobs-quick__pill${filters.rangeHours === 1 ? " cobs-quick__pill--on" : ""}" data-h="1">Last 1h</button>
            <button type="button" class="cobs-quick__pill${filters.rangeHours === 24 ? " cobs-quick__pill--on" : ""}" data-h="24">Today</button>
            <button type="button" class="cobs-quick__pill${filters.rangeHours === 168 ? " cobs-quick__pill--on" : ""}" data-h="168">7 days</button>
            <button type="button" class="cobs-quick__pill${filters.rangeHours === 0 ? " cobs-quick__pill--on" : ""}" data-h="0">All</button>
          </div>
        </aside>

        <section class="cobs-gallery glass-panel glass-panel--panel cobs-panel" aria-label="Snapshot gallery">
          <div class="cobs-panel__head">
            <div><p class="cobs-eyebrow">Gallery</p><h3 class="cobs-title">Snapshot Gallery</h3></div>
            <span class="cobs-muted mono" id="cobsGalleryCount">—</span>
          </div>
          <div id="cobsGallery"><p class="cobs-empty">Loading…</p></div>
        </section>

        <section class="cobs-timeline glass-panel glass-panel--panel cobs-panel" aria-label="Snapshot timeline">
          <div class="cobs-panel__head"><div><p class="cobs-eyebrow">Recent</p><h3 class="cobs-title">Snapshot Timeline</h3></div></div>
          <div class="cobs-tl" id="cobsTimeline"></div>
        </section>

        <div class="cobs-right">
          <section class="glass-panel glass-panel--panel cobs-panel">
            <div class="cobs-panel__head"><h3 class="cobs-title">AI Snapshot Analysis</h3></div>
            <div id="cobsAnalysis"></div>
          </section>
          <section class="glass-panel glass-panel--panel cobs-panel">
            <div class="cobs-panel__head"><h3 class="cobs-title">Captures by Camera</h3></div>
            <div class="cobs-donut-wrap">
              <div class="cobs-donut" id="cobsDonut"><div class="cobs-donut__c"><span class="cobs-donut__t" id="cobsDonutTotal">0</span><span class="cobs-donut__l">Total</span></div></div>
              <div class="cobs-legend" id="cobsLegend"></div>
            </div>
          </section>
          <section class="glass-panel glass-panel--panel cobs-panel">
            <div class="cobs-panel__head"><h3 class="cobs-title">Similar Snapshots</h3></div>
            <div class="cobs-similar" id="cobsSimilar"></div>
          </section>
        </div>
      </div>

      <div class="cobs-bottom">
        <section class="glass-panel glass-panel--panel cobs-panel">
          <div class="cobs-panel__head"><h3 class="cobs-title">Snapshot stats</h3></div>
          <div class="cobs-kpis" id="cobsSensorKpis"></div>
        </section>
        <section class="glass-panel glass-panel--panel cobs-panel">
          <div class="cobs-panel__head"><h3 class="cobs-title">Local observation summary</h3></div>
          <p class="cobs-muted" style="font-size:12.5px;line-height:1.5;margin:0" id="cobsInsight">—</p>
          <p class="cobs-muted" style="font-size:12px;margin-top:8px">Counts from loaded snapshots · local summary only.</p>
        </section>
      </div>`;
  };

  const paintGallery = (list) => {
    const el = $("#cobsGallery", view);
    const countEl = $("#cobsGalleryCount", view);
    if (countEl) setTextIfChanged(countEl, `${list.length} snapshots`);
    if (!el) return;
    if (isSuper && !ownerAdmin) {
      setHtmlIfChanged(el, `<p class="cobs-empty">Enter an account owner above and Load account to view snapshots.</p>`);
      return;
    }
    if (loadError) {
      setHtmlIfChanged(el, `<p class="cobs-empty">Could not load snapshots. Snapshots may not be opened on this deployment yet.</p>`);
      return;
    }
    if (!list.length) {
      setHtmlIfChanged(el, `<p class="cobs-empty">No snapshots match the current filters.</p>`);
      return;
    }
    const html = `<div class="cobs-grid">${list
      .map((it) => {
        const dev = escapeHtml(String(it.device_id || ""));
        const sid = String(it.snapshot_id || "");
        const zone = zoneOf(it);
        const url = imgUrl(sid);
        const dl = `<a class="btn sm secondary" href="${url}" download="snapshot-${escapeHtml(sid)}.jpg">Save</a>`;
        const mail = canEmail ? `<button type="button" class="btn sm secondary js-snap-email" data-sid="${escapeHtml(sid)}">Email</button>` : "";
        const badge = zone ? `<span class="cobs-card__badge">${escapeHtml(zone)}</span>` : `<span class="cobs-card__badge">CAM</span>`;
        const rt = remainText(it);
        return `<article class="cobs-card">
          <div class="cobs-card__media">
            ${badge}
            <span class="cobs-card__pin" aria-hidden="true">⚲</span>
            <span class="cobs-card__time">${escapeHtml(relFromSec(it.created_at))}</span>
            <img class="js-snap-thumb" data-snap-id="${escapeHtml(sid)}" src="${url}" alt="" loading="lazy" />
          </div>
          <div class="cobs-card__body">
            <div class="cobs-card__dev">${dev}</div>
            <div class="cobs-card__meta"><span>${escapeHtml(fmtTs(it.created_at))}</span></div>
            ${rt ? `<div class="cobs-card__meta cobs-muted">${escapeHtml(rt)}</div>` : ""}
            <div class="cobs-card__actions">${dl}${mail}</div>
          </div>
        </article>`;
      })
      .join("")}</div>`;
    setHtmlIfChanged(el, html);
    el.querySelectorAll(".js-snap-thumb").forEach((imgEl) => {
      imgEl.onerror = () => {
        const card = imgEl.closest(".cobs-card");
        if (card) card.remove();
        if (!el.querySelector(".cobs-grid .cobs-card")) {
          setHtmlIfChanged(el, `<p class="cobs-empty">No snapshots with a stored image yet.</p>`);
        }
      };
    });
    el.querySelectorAll(".js-snap-email").forEach((btn) => {
      btn.addEventListener("click", async () => {
        const sid = btn.getAttribute("data-sid");
        if (!sid) return;
        btn.disabled = true;
        try {
          const mailPath = cameraApiPathWithOwner(buildPostAdminCameraSnapshotSendEmailPath(sid), ownerAdmin);
          await api(mailPath, { method: "POST", timeoutMs: 30000 });
          toast("Email queued to account address", "ok");
        } catch (e) {
          toast((e && e.message) || "Email failed", "err");
        } finally {
          btn.disabled = false;
        }
      });
    });
  };

  const paintTimeline = (list) => {
    const el = $("#cobsTimeline", view);
    if (!el) return;
    const recent = list.slice(0, 8);
    if (!recent.length) {
      setHtmlIfChanged(el, `<p class="cobs-muted" style="font-size:12px;margin:0">No recent captures.</p>`);
      return;
    }
    setHtmlIfChanged(
      el,
      recent
        .map(
          (it) => `<div class="cobs-tl__node">
            <span class="cobs-tl__ts">${escapeHtml(fmtTs(it.created_at).slice(11, 16))}</span>
            <span class="cobs-tl__dot"></span>
            <span class="cobs-tl__lbl">${escapeHtml(String(it.device_id || "—"))}</span>
          </div>`,
        )
        .join(""),
    );
  };

  const paintAnalysis = (list) => {
    const el = $("#cobsAnalysis", view);
    if (!el) return;
    if (!list.length) {
      setHtmlIfChanged(el, `<p class="cobs-empty">No snapshot selected.</p>`);
      return;
    }
    const top = list[0];
    const devCount = new Set(list.map((it) => String(it.device_id || "")).filter(Boolean)).size;
    const rt = remainText(top) || "—";
    setHtmlIfChanged(
      el,
      `<img class="cobs-hero-img" src="${imgUrl(top.snapshot_id)}" alt="Latest snapshot" loading="lazy" onerror="this.style.display='none'" />
       <div class="cobs-stat-row">
         <div class="cobs-stat"><div class="cobs-stat__k">Snapshots</div><div class="cobs-stat__v">${list.length}</div></div>
         <div class="cobs-stat"><div class="cobs-stat__k">Cameras</div><div class="cobs-stat__v">${devCount}</div></div>
         <div class="cobs-stat"><div class="cobs-stat__k">Latest</div><div class="cobs-stat__v" style="font-size:0.95rem">${escapeHtml(relFromSec(top.created_at))}</div></div>
         <div class="cobs-stat"><div class="cobs-stat__k">Retention</div><div class="cobs-stat__v" style="font-size:0.95rem">${escapeHtml(rt)}</div></div>
       </div>`,
    );
  };

  const paintDonut = (list) => {
    const counts = new Map();
    for (const it of list) {
      const k = String(it.device_id || "—");
      counts.set(k, (counts.get(k) || 0) + 1);
    }
    const total = list.length;
    const totalEl = $("#cobsDonutTotal", view);
    if (totalEl) setTextIfChanged(totalEl, String(total));
    const donut = $("#cobsDonut", view);
    const legend = $("#cobsLegend", view);
    if (!donut || !legend) return;
    if (!total) {
      donut.style.background = "conic-gradient(rgba(148,163,184,0.25) 0 100%)";
      setHtmlIfChanged(legend, `<p class="cobs-muted" style="font-size:12px;margin:0">No data.</p>`);
      return;
    }
    const sorted = [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 7);
    let acc = 0;
    const stops = [];
    const rows = [];
    sorted.forEach(([name, c], i) => {
      const col = DONUT_COLORS[i % DONUT_COLORS.length];
      const from = (acc / total) * 100;
      acc += c;
      const to = (acc / total) * 100;
      stops.push(`${col} ${from.toFixed(2)}% ${to.toFixed(2)}%`);
      rows.push(`<div class="cobs-legend__row"><span class="cobs-legend__sw" style="background:${col}"></span><span class="cobs-legend__n">${escapeHtml(name)}</span><span class="cobs-legend__v">${c}</span></div>`);
    });
    donut.style.background = `conic-gradient(${stops.join(", ")})`;
    setHtmlIfChanged(legend, rows.join(""));
  };

  const paintSimilar = (list) => {
    const el = $("#cobsSimilar", view);
    if (!el) return;
    const rest = list.slice(1, 5);
    if (!rest.length) {
      setHtmlIfChanged(el, `<p class="cobs-muted" style="font-size:12px;margin:0">No related snapshots.</p>`);
      return;
    }
    setHtmlIfChanged(
      el,
      rest
        .map(
          (it) => `<div class="cobs-similar__row">
            <img class="cobs-similar__thumb" src="${imgUrl(it.snapshot_id)}" alt="" loading="lazy" onerror="this.style.visibility='hidden'" />
            <div class="cobs-similar__meta">
              <div class="cobs-similar__dev">${escapeHtml(String(it.device_id || "—"))}</div>
              <div class="cobs-similar__ts">${escapeHtml(relFromSec(it.created_at))}</div>
            </div>
          </div>`,
        )
        .join(""),
    );
  };

  const paintBottom = (list) => {
    const sensorEl = $("#cobsSensorKpis", view);
    if (sensorEl) {
      const devCount = new Set(list.map((it) => String(it.device_id || "")).filter(Boolean)).size;
      const latest = list.length ? relFromSec(list[0].created_at) : "—";
      setHtmlIfChanged(
        sensorEl,
        `<div class="cobs-kpi"><div class="cobs-kpi__v">${devCount}</div><div class="cobs-kpi__k">Active cameras</div></div>
         <div class="cobs-kpi"><div class="cobs-kpi__v">${list.length}</div><div class="cobs-kpi__k">Snapshots</div></div>
         <div class="cobs-kpi"><div class="cobs-kpi__v" style="font-size:0.95rem">${escapeHtml(latest)}</div><div class="cobs-kpi__k">Latest capture</div></div>
         <div class="cobs-kpi"><div class="cobs-kpi__v" style="font-size:0.95rem">${escapeHtml(list.length ? remainText(list[0]) || "—" : "—")}</div><div class="cobs-kpi__k">Retention</div></div>`,
      );
    }
    const insightEl = $("#cobsInsight", view);
    if (insightEl) {
      const devCount = new Set(list.map((it) => String(it.device_id || "")).filter(Boolean)).size;
      const txt = list.length
        ? `${list.length} snapshots captured across ${devCount} camera${devCount === 1 ? "" : "s"}. Most recent activity ${relFromSec(list[0].created_at)}.`
        : "No observation activity in the selected window.";
      setTextIfChanged(insightEl, txt);
    }
  };

  const repaintSnapshot = () => {
    const list = getFiltered();
    paintGallery(list);
    paintTimeline(list);
    paintAnalysis(list);
    paintDonut(list);
    paintSimilar(list);
    paintBottom(list);
  };

  const wireSnapshotControls = () => {
    $("#cobsDevice", view)?.addEventListener("change", (e) => {
      filters.device = e.target.value || "";
      repaintSnapshot();
    });
    $("#cobsZone", view)?.addEventListener("change", (e) => {
      filters.zone = e.target.value || "";
      repaintSnapshot();
    });
    const quick = $("#cobsQuick", view);
    if (quick) {
      quick.addEventListener("click", (ev) => {
        const btn = ev.target.closest("[data-h]");
        if (!btn) return;
        filters.rangeHours = parseInt(btn.getAttribute("data-h"), 10) || 0;
        for (const b of quick.querySelectorAll(".cobs-quick__pill")) b.classList.toggle("cobs-quick__pill--on", b === btn);
        repaintSnapshot();
      });
    }
    if (isSuper) {
      $("#cobsOwnerApply", view)?.addEventListener("click", () => {
        ownerAdmin = String($("#cobsOwner", view)?.value || "").trim();
        void loadSnapshots();
      });
    }
  };

  // -------------------------------------------------------------- tab paint
  const renderTab = () => {
    const body = $("#cobsBody", view);
    if (!body) return;
    activeTab = "snapshot";
    setHtmlIfChanged(body, buildSnapshotSkeleton());
    wireSnapshotControls();
    repaintSnapshot();
  };

  // -------------------------------------------------------------- load
  const loadSnapshots = async () => {
    loadError = false;
    try {
      const path = cameraApiPathWithOwner(buildGetAdminCameraSnapshotsPath({ limit: 60 }), ownerAdmin);
      const pack = await api(path);
      if (!isRouteCurrent(routeSeq)) return;
      items = ((pack && pack.items) || []).filter((it) => it && it.has_image);
      items.sort((a, b) => Number(b.created_at || 0) - Number(a.created_at || 0));
    } catch (_err) {
      if (!isRouteCurrent(routeSeq)) return;
      loadError = true;
      items = [];
    }
    renderTab();
  };

  renderTab();
  await loadSnapshots();
});


// ── telegram.route.js ─────────────────────────────────────
/**
 * Route: #/telegram — deep link → Settings overview (telegram section).
 */

registerRoute("telegram", async (view, _args, routeSeq) => {
  setCrumb("Telegram");
  await renderSettingsOverviewPage(view, {
    activeNavId: "telegram",
    routeSeq,
    me: state.me,
    hasRole,
    can,
  });
});


// ── tenant-delegations.route.js ─────────────────────────────────────
/**
 * Route: #/tenant-delegations — Tenant admin CRUD for staff capability delegations.
 *
 * API: GET|POST|DELETE /admin/tenant-delegations (mounted when TENANT_ACL_ENABLED).
 */

registerRoute("tenant-delegations", async (view, _args, routeSeq) => {
  setCrumb("Staff delegations");
  if (!state.me) {
    location.hash = "#/login";
    return;
  }
  const me = state.me;
  const roleNorm = String(me.role || "").trim().toLowerCase();
  if (roleNorm !== "admin" || !can("can_manage_users")) {
    mountView(
      view,
      `<div class="card glass-panel glass-panel--panel"><p class="muted">Account admin with user-management access required.</p><p><button type="button" class="btn secondary btn-tap" data-cc-go="account">← Account</button></p></div>`,
    );
    return;
  }
  if (!me.tenant_acl_enabled) {
    mountView(
      view,
      `<div class="card glass-panel glass-panel--panel"><p class="muted">Staff delegations are not opened on this server yet.</p><p><button type="button" class="btn secondary btn-tap" data-cc-go="account">← Account</button></p></div>`,
    );
    return;
  }

  const CAP_OPTS = [
    { id: "tenant_user_admin", label: "User admin" },
    { id: "tenant_share_admin", label: "Sharing" },
    { id: "tenant_audit_read", label: "Audit read" },
    { id: "tenant_full_access", label: "Full access" },
  ];
  const CAP_LABEL = Object.fromEntries(CAP_OPTS.map((o) => [o.id, o.label]));

  const capsToBadges = (csv) => {
    const ids = String(csv || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (!ids.length) return `<span class="muted">—</span>`;
    return `<span class="tenant-cap-badges">${ids
      .map((id) => {
        const lab = CAP_LABEL[id] || id;
        return `<span class="tenant-cap-badge">${escapeHtml(lab)}</span>`;
      })
      .join("")}</span>`;
  };

  let lastItems = [];
  /** @type {Map<string,string>} */
  let granteeEmailByUser = new Map();

  const parseDdMmYyyy = (str) => {
    const s = String(str || "").trim();
    if (!s) return null;
    const m = /^(\d{1,2})-(\d{1,2})-(\d{4})$/.exec(s);
    if (!m) return "bad";
    const day = Number(m[1]);
    const month = Number(m[2]);
    const year = Number(m[3]);
    if (month < 1 || month > 12 || day < 1 || day > 31) return "bad";
    const dt = new Date(year, month - 1, day);
    if (dt.getFullYear() !== year || dt.getMonth() !== month - 1 || dt.getDate() !== day) return "bad";
    return { year, month, day };
  };

  const expiryUnixFromDdMmYyyy = (parts, timeValue) => {
    let hh = 23;
    let mm = 59;
    const ts = String(timeValue || "").trim();
    if (ts) {
      const p = ts.split(":");
      const h = Number(p[0]);
      const mi = Number(p[1] || 0);
      if (!Number.isFinite(h) || !Number.isFinite(mi) || h < 0 || h > 23 || mi < 0 || mi > 59) {
        return "bad-time";
      }
      hh = h;
      mm = mi;
    }
    const dt = new Date(parts.year, parts.month - 1, parts.day, hh, mm, 0, 0);
    return Math.floor(dt.getTime() / 1000);
  };

  const capsFromChecks = () => {
    const ids = [];
    for (const o of CAP_OPTS) {
      const el = $(`#td_cap_${o.id}`, view);
      if (el && el.checked) ids.push(o.id);
    }
    return ids.join(", ");
  };

  const setChecksFromCsv = (csv) => {
    const set = new Set(
      String(csv || "")
        .split(",")
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean),
    );
    for (const o of CAP_OPTS) {
      const el = $(`#td_cap_${o.id}`, view);
      if (el) el.checked = set.has(o.id);
    }
  };

  const prefillExpiry = (unix) => {
    const dateEl = $("#td_until_date", view);
    const timeEl = $("#td_until_time", view);
    if (!dateEl || !timeEl) return;
    if (!unix) {
      dateEl.value = "";
      timeEl.value = "";
      return;
    }
    const d = new Date(Number(unix) * 1000);
    if (!Number.isFinite(d.getTime())) return;
    const pad = (n) => String(n).padStart(2, "0");
    dateEl.value = `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
    timeEl.value = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const highlightTableRow = (grantee) => {
    const body = $("#delegTable", view);
    if (!body) return;
    const g = String(grantee || "").trim();
    for (const tr of body.querySelectorAll("tr[data-grantee]")) {
      tr.classList.toggle("is-selected", g && tr.getAttribute("data-grantee") === g);
    }
  };

  const syncFormFromGrantee = () => {
    const sel = $("#td_grantee", view);
    const g = sel ? String(sel.value || "").trim() : "";
    highlightTableRow(g);
    if (!g) {
      setChecksFromCsv("");
      prefillExpiry(null);
      return;
    }
    const row = lastItems.find((x) => String(x.grantee_username || "") === g);
    if (row) {
      setChecksFromCsv(row.capabilities_csv);
      prefillExpiry(row.granted_until);
    } else {
      setChecksFromCsv("");
      prefillExpiry(null);
    }
  };

  const applyPreset = (capIds) => {
    setChecksFromCsv(capIds.join(", "));
  };

  const delegEmptyRow = (msg) =>
    `<tr class="tenant-deleg-table__row-empty"><td colspan="4"><div class="tenant-deleg-table__empty-state">${escapeHtml(msg)}</div></td></tr>`;

  const renderRows = (items) => {
    if (!items.length) {
      return delegEmptyRow("No delegations yet.");
    }
    return items
      .map((row) => {
        const g = escapeHtml(String(row.grantee_username || ""));
        const capsHtml = capsToBadges(row.capabilities_csv);
        const until =
          row.granted_until != null && row.granted_until !== "" ? fmtTs(row.granted_until) : "—";
        return `<tr data-grantee="${g}">
          <td class="tenant-deleg-table__cell-staff"><span class="mono">${g}</span></td>
          <td class="tenant-deleg-table__cell-caps">${capsHtml}</td>
          <td class="muted tenant-deleg-table__cell-until">${until}</td>
          <td class="tenant-deleg-table__cell-actions">
            <button type="button" class="btn secondary btn-tap deleg-revoke" data-grantee="${g}">Revoke</button>
          </td>
        </tr>`;
      })
      .join("");
  };

  const loadGranteeOptions = async () => {
    const sel = $("#td_grantee", view);
    if (!sel) return;
    const prev = String(sel.value || "").trim();
    const names = new Set();
    const selfName = String(me.username || "").trim();
    granteeEmailByUser = new Map();
    try {
      const d = await api(buildAuthUsersPath(), { timeoutMs: TENANT_CONSOLE_TIMEOUT_MS });
      for (const x of Array.isArray(d.items) ? d.items : []) {
        const u = String(x.username || "").trim();
        if (u && u !== selfName) {
          names.add(u);
          granteeEmailByUser.set(u, String(x.email || "").trim());
        }
      }
    } catch (_) {}
    const childAdminSet = new Set();
    try {
      const ch = await api(buildGetAdminUserScopesChildrenPath(), { timeoutMs: TENANT_CONSOLE_TIMEOUT_MS });
      for (const x of Array.isArray(ch.items) ? ch.items : []) {
        const u = String(x.sub_admin_username || "").trim();
        if (u && u !== selfName) {
          names.add(u);
          childAdminSet.add(u);
        }
      }
    } catch (_) {}
    const sorted = Array.from(names).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
    const opts = [`<option value="">— Choose staff —</option>`].concat(
      sorted.map((u) => {
        const em = granteeEmailByUser.get(u) || "";
        const tag = childAdminSet.has(u) ? " · child admin" : "";
        const lab = em ? `${u} · ${em}${tag}` : `${u}${tag}`;
        return `<option value="${escapeHtml(u)}">${escapeHtml(lab)}</option>`;
      }),
    );
    sel.innerHTML = opts.join("");
    if (prev && sorted.includes(prev)) sel.value = prev;
  };

  const load = async () => {
    let d;
    try {
      d = await api(buildGetAdminTenantDelegationsPath(), { timeoutMs: TENANT_CONSOLE_TIMEOUT_MS });
    } catch (e) {
      if (!isRouteCurrent(routeSeq)) return;
      toast(e.message || e, "err");
      return;
    }
    if (!isRouteCurrent(routeSeq)) return;
    const items = Array.isArray(d.items) ? d.items : [];
    lastItems = items;
    const body = $("#delegTable", view);
    if (body) setHtmlIfChanged(body, renderRows(items));
    syncFormFromGrantee();
  };

  const capFieldset = CAP_OPTS.map(
    (o) => `
    <label class="checkbox tenant-deleg-cap-line">
      <input type="checkbox" id="td_cap_${o.id}" data-cap="${escapeHtml(o.id)}" />
      <span><strong>${escapeHtml(o.label)}</strong></span>
    </label>`,
  ).join("");

  mountView(
    view,
    `<div class="tenant-admin-page">
      <header class="card tenant-admin-page__top">
        <div>
          <h2>Staff delegations</h2>
          <p class="muted">Grant extra permissions to staff. For who they can see, use <button type="button" class="tenant-admin-page__link" data-cc-go="admin-user-scopes">Child admin access</button>.</p>
        </div>
        <button type="button" class="tenant-admin-page__link" data-cc-go="admin-user-scopes">Child admin access →</button>
      </header>

      <section class="card tenant-admin-page__card">
        <h3 class="tenant-admin-page__card-title">Edit delegation</h3>
        <p class="tenant-admin-page__hint">Child admins: only <strong>User admin</strong> is allowed. Click a table row to edit.</p>
        <label class="field"><span>Staff member</span>
          <select id="td_grantee" autocomplete="off"></select>
        </label>
        <p class="tenant-admin-page__card-title" style="margin-top:14px;margin-bottom:8px;font-size:12px">Capabilities</p>
        <div class="tenant-admin-page__caps">${capFieldset}</div>
        <div class="tenant-admin-page__form-row tenant-admin-page__form-row--2" style="margin-top:14px">
          <label class="field"><span>Expires (date)</span>
            <input id="td_until_date" type="text" inputmode="numeric" placeholder="dd-mm-yyyy" autocomplete="off" spellcheck="false" />
          </label>
          <label class="field"><span>Time</span>
            <input id="td_until_time" type="time" />
          </label>
        </div>
        <div class="tenant-admin-page__form-actions tenant-admin-page__form-actions--between">
          <div class="tenant-admin-page__form-actions-group">
            <button type="button" class="btn ghost btn-tap" id="td_preset_user">User admin</button>
            <button type="button" class="btn ghost btn-tap" id="td_preset_clear">Clear all</button>
          </div>
          <div class="tenant-admin-page__form-actions-group">
            <button type="button" class="btn secondary btn-tap" id="delegReload">Refresh</button>
            <button type="button" class="btn btn-tap" id="td_submit">Save</button>
          </div>
        </div>
      </section>

      <section class="card tenant-admin-page__card">
        <h3 class="tenant-admin-page__card-title">Active delegations</h3>
        <div class="tenant-admin-page__table-wrap" style="margin-top:12px">
          <table class="table compact tenant-deleg-table">
            <colgroup>
              <col class="tenant-deleg-table__w-staff" />
              <col class="tenant-deleg-table__w-caps" />
              <col class="tenant-deleg-table__w-until" />
              <col class="tenant-deleg-table__w-actions" />
            </colgroup>
            <thead>
              <tr>
                <th scope="col">Staff</th>
                <th scope="col">Capabilities</th>
                <th scope="col">Until</th>
                <th scope="col" class="tenant-deleg-table__th-actions">Actions</th>
              </tr>
            </thead>
            <tbody id="delegTable">${delegEmptyRow("Loading…")}</tbody>
          </table>
        </div>
      </section>
    </div>`,
  );

  const granteeSel = $("#td_grantee", view);
  if (granteeSel) granteeSel.addEventListener("change", () => syncFormFromGrantee());

  const selectGranteeInForm = (grantee) => {
    const g = String(grantee || "").trim();
    const sel = $("#td_grantee", view);
    if (!sel || !g) return;
    sel.value = g;
    syncFormFromGrantee();
    const aside = view.querySelector(".tenant-admin-page__card");
    if (aside && aside.scrollIntoView) aside.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const onRevoke = async (grantee) => {
    const g = String(grantee || "").trim();
    if (!g) return;
    if (!confirm(`Revoke all delegated capabilities for ${g}?`)) return;
    try {
      await api(buildDeleteAdminTenantDelegationPath(g), { method: "DELETE", timeoutMs: TENANT_CONSOLE_TIMEOUT_MS });
      toast("Revoked " + g, "ok");
      await load();
      await loadGranteeOptions();
    } catch (e) {
      toast(e.message || e, "err");
    }
  };

  view.addEventListener("click", (ev) => {
    const t = ev.target;
    if (!t || !t.classList) return;
    if (t.classList.contains("deleg-revoke")) {
      const g = t.getAttribute("data-grantee");
      if (g) onRevoke(g);
      return;
    }
    const tr = t.closest && t.closest("tr[data-grantee]");
    if (tr && !t.classList.contains("deleg-revoke")) {
      const g = tr.getAttribute("data-grantee");
      if (g) selectGranteeInForm(g);
    }
  });

  const presetUser = $("#td_preset_user", view);
  if (presetUser) presetUser.addEventListener("click", () => applyPreset(["tenant_user_admin"]));
  const presetClear = $("#td_preset_clear", view);
  if (presetClear) presetClear.addEventListener("click", () => applyPreset([]));

  const btnR = $("#delegReload", view);
  if (btnR) btnR.addEventListener("click", async () => { await load(); await loadGranteeOptions(); });

  const btnS = $("#td_submit", view);
  if (btnS) {
    btnS.addEventListener("click", async () => {
      const ge = $("#td_grantee", view);
      const grantee_username = ge && ge.value ? String(ge.value).trim() : "";
      const capabilities_csv = capsFromChecks();
      const untilDateRaw = String(($("#td_until_date", view) && $("#td_until_date", view).value) || "").trim();
      const untilTimeRaw = String(($("#td_until_time", view) && $("#td_until_time", view).value) || "").trim();
      let granted_until;
      if (untilDateRaw) {
        const parsed = parseDdMmYyyy(untilDateRaw);
        if (parsed === "bad") {
          toast("Use dd-mm-yyyy for expiry date, or leave blank.", "err");
          return;
        }
        const unix = expiryUnixFromDdMmYyyy(parsed, untilTimeRaw);
        if (unix === "bad-time") {
          toast("Invalid end time.", "err");
          return;
        }
        granted_until = unix;
        if (granted_until <= Math.floor(Date.now() / 1000)) {
          toast("Expiry must be in the future.", "err");
          return;
        }
      } else if (untilTimeRaw) {
        toast("Set a date before choosing end time.", "err");
        return;
      }
      if (!grantee_username) {
        toast("Choose a staff member.", "err");
        return;
      }
      if (!capabilities_csv.trim()) {
        toast("Select at least one capability.", "err");
        return;
      }
      try {
        await api(buildPostAdminTenantDelegationsPath(), {
          method: "POST",
          body: { grantee_username, capabilities_csv, granted_until },
          timeoutMs: TENANT_CONSOLE_TIMEOUT_MS,
        });
        toast("Delegation saved", "ok");
        await load();
      } catch (e) {
        toast(e.message || e, "err");
      }
    });
  }

  await loadGranteeOptions();
  await load();
});


// ── voice.route.js ─────────────────────────────────────
/**
 * Route: #/voice — deep link → Settings overview (voice section).
 */

registerRoute("voice", async (view, _args, routeSeq) => {
  setCrumb("Voice");
  if (!state.me) {
    location.hash = "#/login";
    return;
  }
  if (!state.me.voice_enabled) {
    mountView(view, capabilityUnavailableHtml({
      title: "Voice alerts",
      detail: "Voice calling is not opened for this account yet.",
      backHref: "#/settings",
    }));
    return;
  }
  await renderSettingsOverviewPage(view, {
    activeNavId: "voice",
    routeSeq,
    me: state.me,
    hasRole,
    can,
  });
});

// ── 99-boot.shell.js ─────────────────────────────────────
/**
 * Last slice (build moves this after routes/) — start console once state + routes exist.
 * console.raw.js defines boot() but must not invoke it (raw is concatenated first).
 */
(function startConsoleBoot() {
  const paintBootError = (err) => {
    try {
      const view = document.getElementById("view");
      if (!view) return;
      const msg = err && (err.stack || err.message || String(err));
      view.innerHTML =
        `<div class="card glass-panel glass-panel--panel" style="padding:24px">` +
        `<h2 style="margin:0 0 8px">Workspace failed to start</h2>` +
        `<p class="muted" style="margin:0;white-space:pre-wrap;font-size:13px">${String(msg || "Unknown error")
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")}</p>` +
        `<p class="muted" style="margin:12px 0 0">Refresh the page. If it persists, contact platform staff.</p>` +
        `</div>`;
    } catch (_) {}
  };
  const run = () => {
    try {
      if (typeof boot !== "function") {
        paintBootError(new Error("boot() is not available in this bundle"));
        return;
      }
      const ret = boot();
      if (ret && typeof ret.then === "function") {
        ret.catch((err) => {
          try {
            console.error("[croc] boot failed", err);
          } catch (_) {}
          paintBootError(err);
        });
      }
    } catch (err) {
      try {
        console.error("[croc] boot failed", err);
      } catch (_) {}
      paintBootError(err);
    }
  };
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", run);
  } else {
    run();
  }
})();

